/**
 * API Client for GRC Platform
 */

// Task type definition for the Tasks module
interface TaskType {
  id: string
  tenant_id: string
  title: string
  description?: string
  type: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
  assigned_to?: string
  assigned_to_username?: string
  assigned_to_email?: string
  created_by: string
  created_by_username?: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'open' | 'in-progress' | 'completed' | 'cancelled'
  due_date?: number
  reminder_sent: boolean
  related_control_id?: string
  control_number?: string
  control_name?: string
  related_document_id?: string
  document_title?: string
  related_risk_id?: string
  risk_title?: string
  related_vendor_id?: string
  vendor_name?: string
  evidence_count?: number
  created_at: number
  updated_at: number
  completed_at?: number
}

// Control Exception type definition (Issue #72)
interface ControlException {
  id: string
  tenant_id: string
  control_id: string
  control_number?: string
  control_name?: string
  framework_name?: string
  exception_type: 'waiver' | 'exception' | 'compensating_control'
  reason: string
  risk_acceptance?: string
  compensating_control_description?: string
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'revoked'
  requested_by: string
  requested_by_name?: string
  approved_by?: string
  approved_by_name?: string
  approved_at?: number
  rejection_reason?: string
  valid_from: number
  valid_until?: number | null
  review_frequency?: 'monthly' | 'quarterly' | 'semi_annually' | 'annually' | null
  next_review_date?: number
  last_reviewed_at?: number
  last_reviewed_by?: string
  created_at: number
  updated_at: number
}

// Exception history entry (Issue #72)
interface ExceptionHistoryEntry {
  id: string
  tenant_id: string
  exception_id: string
  action: 'created' | 'submitted' | 'approved' | 'rejected' | 'revoked' | 'expired' | 'reviewed' | 'updated'
  actor_id?: string
  actor_name?: string
  previous_status?: string
  new_status?: string
  comments?: string
  created_at: number
}

// Central admin URL for tenant lookup
const CENTRAL_ADMIN_URL = import.meta.env.PROD
  ? 'https://admin.clarantis.com'
  : 'http://localhost:8788' // Local dev proxy

// Storage key for selected tenant
const SELECTED_TENANT_KEY = 'grc_selected_tenant'

// Storage key for available tenants (stored on login for tenant switching)
const AVAILABLE_TENANTS_KEY = 'grc_available_tenants'

/**
 * Retrieves the currently selected tenant from browser localStorage.
 *
 * This function is used during the multi-tenant login flow to maintain
 * tenant context across page refreshes and API calls. The selected tenant
 * determines which tenant's API endpoint will be used for all subsequent requests.
 *
 * @returns {Object | null} The selected tenant object containing:
 *   - `slug` {string} - The unique tenant identifier used in subdomain URLs (e.g., "acme" for acme.app.clarantis.com)
 *   - `name` {string | null} - The human-readable tenant display name, or null if not set
 *   Returns null if no tenant is selected or if localStorage access fails.
 *
 * @example
 * // Check if a tenant is currently selected
 * const tenant = getSelectedTenant();
 * if (tenant) {
 *   console.log(`Current tenant: ${tenant.name} (${tenant.slug})`);
 * } else {
 *   console.log('No tenant selected - redirect to tenant selection');
 * }
 *
 * @see {@link setSelectedTenant} for setting the tenant
 * @see {@link clearSelectedTenant} for clearing tenant on logout
 * @see {@link getApiBase} for how this affects API routing
 */
function getSelectedTenant(): { slug: string; name: string | null } | null {
  try {
    const stored = localStorage.getItem(SELECTED_TENANT_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

/**
 * Persists the selected tenant to browser localStorage for API routing.
 *
 * This function is called after a user selects their tenant during the
 * multi-tenant login flow. Once set, all subsequent API calls will be
 * routed to the appropriate tenant-specific endpoint (e.g., {slug}.app.clarantis.com).
 *
 * Passing `null` will clear the stored tenant, which is equivalent to calling
 * {@link clearSelectedTenant}.
 *
 * @param {Object | null} tenant - The tenant to set, or null to clear.
 * @param {string} tenant.slug - The unique tenant identifier used in subdomain URLs.
 *   This value is used by {@link getApiBase} to construct the API URL.
 * @param {string | null} tenant.name - The human-readable tenant display name for UI purposes.
 *
 * @returns {void}
 *
 * @example
 * // Set tenant after user selection from lookupTenants response
 * const tenants = await api.lookupTenants(email);
 * if (tenants.count === 1) {
 *   // Auto-select if only one tenant
 *   setSelectedTenant({ slug: tenants.tenants[0].slug, name: tenants.tenants[0].name });
 * }
 *
 * @example
 * // Clear tenant (equivalent to clearSelectedTenant())
 * setSelectedTenant(null);
 *
 * @see {@link getSelectedTenant} for retrieving the current tenant
 * @see {@link clearSelectedTenant} for explicitly clearing tenant on logout
 * @see {@link getApiBase} for how the tenant affects API URL routing
 */
function setSelectedTenant(tenant: { slug: string; name: string | null } | null): void {
  if (tenant) {
    localStorage.setItem(SELECTED_TENANT_KEY, JSON.stringify(tenant))
  } else {
    localStorage.removeItem(SELECTED_TENANT_KEY)
  }
}

/**
 * Clears the selected tenant from browser localStorage.
 *
 * This function should be called during the logout process to ensure
 * the user is required to re-select their tenant on the next login.
 * After clearing, {@link getApiBase} will fall back to deriving the
 * tenant from the current hostname or environment variables.
 *
 * This is the preferred method for logout cleanup compared to
 * `setSelectedTenant(null)` as it makes the intent explicit.
 *
 * @returns {void}
 *
 * @example
 * // Call during logout to clear tenant context
 * async function handleLogout() {
 *   await api.logout();
 *   api.setToken(null);
 *   clearSelectedTenant();
 *   navigate('/login');
 * }
 *
 * @see {@link setSelectedTenant} for setting the tenant
 * @see {@link getSelectedTenant} for retrieving the current tenant
 */
function clearSelectedTenant(): void {
  localStorage.removeItem(SELECTED_TENANT_KEY)
}

/**
 * Interface for available tenant info stored during login
 */
interface AvailableTenant {
  id: string
  slug: string
  name: string | null
  role: string
}

/**
 * Stores the list of available tenants for the current user.
 *
 * This is called during login when a user has access to multiple tenants.
 * The stored list enables the tenant switcher UI to show available options
 * without requiring another API call.
 *
 * @param {AvailableTenant[]} tenants - Array of tenants the user can access
 */
function setAvailableTenants(tenants: AvailableTenant[]): void {
  localStorage.setItem(AVAILABLE_TENANTS_KEY, JSON.stringify(tenants))
}

/**
 * Retrieves the list of available tenants for the current user.
 *
 * Returns the tenants stored during login. This is used by the tenant
 * switcher UI to display available options without an API call.
 *
 * @returns {AvailableTenant[]} Array of available tenants, or empty array if none stored
 */
function getAvailableTenants(): AvailableTenant[] {
  try {
    const stored = localStorage.getItem(AVAILABLE_TENANTS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

/**
 * Clears the list of available tenants from localStorage.
 *
 * Should be called during logout to ensure tenant list is refreshed on next login.
 */
function clearAvailableTenants(): void {
  localStorage.removeItem(AVAILABLE_TENANTS_KEY)
}

/**
 * Dynamically determines the API base URL for routing requests to the correct tenant.
 *
 * This function implements the multi-tenant URL resolution strategy, ensuring
 * API requests are routed to the appropriate tenant-specific backend. It is called
 * on every API request to support dynamic tenant switching during the login flow.
 *
 * **Resolution Priority (in order):**
 *
 * 1. **Development mode** (`!import.meta.env.PROD`):
 *    - Returns empty string to use Vite's proxy configuration
 *    - Proxy is configured in vite.config.ts to route /api/* requests
 *
 * 2. **Selected tenant from localStorage** ({@link getSelectedTenant}):
 *    - Used during login flow when user has selected a tenant but hasn't
 *      navigated to the tenant subdomain yet
 *    - Returns: `https://{slug}.app.clarantis.com`
 *
 * 3. **Hostname subdomain pattern** (`{tenant}.app.clarantis.com`):
 *    - Extracts tenant from current URL subdomain
 *    - Used when user is already on a tenant-specific subdomain
 *    - Returns: `https://{tenant}.app.clarantis.com`
 *
 * 4. **Cloudflare Pages preview deployments** (`*.pages.dev`):
 *    - Falls back to environment variable or workers.dev default
 *    - Used for PR preview deployments and branch previews
 *
 * 5. **Fallback**:
 *    - Uses `VITE_API_BASE_URL` environment variable if set
 *    - Otherwise defaults to dev workers.dev endpoint
 *
 * @returns {string} The base URL for API requests (without trailing slash).
 *   Examples:
 *   - `""` (empty string for development with Vite proxy)
 *   - `"https://acme.app.clarantis.com"` (production tenant URL)
 *   - `"https://grc-tenant-dev.port-technology-account.workers.dev"` (fallback)
 *
 * @example
 * // In development mode
 * getApiBase(); // Returns "" - uses Vite proxy
 *
 * @example
 * // With selected tenant in storage
 * setSelectedTenant({ slug: 'acme', name: 'ACME Corp' });
 * getApiBase(); // Returns "https://acme.app.clarantis.com"
 *
 * @example
 * // On tenant subdomain (https://acme.app.clarantis.com)
 * getApiBase(); // Returns "https://acme.app.clarantis.com"
 *
 * @see {@link getSelectedTenant} for the localStorage tenant lookup
 * @see {@link setSelectedTenant} for setting tenant during login
 */
function getApiBase(): string {
  if (!import.meta.env.PROD) {
    return '' // Use Vite proxy in development
  }

  // Check for selected tenant (used during login flow)
  const selectedTenant = getSelectedTenant()
  if (selectedTenant?.slug) {
    return `https://${selectedTenant.slug}.app.clarantis.com`
  }

  const hostname = window.location.hostname

  // Check for tenant subdomain pattern: {tenant}.app.clarantis.com
  const clarantisMatch = hostname.match(/^([^.]+)\.app\.clarantis\.com$/)
  if (clarantisMatch) {
    const tenant = clarantisMatch[1]
    return `https://${tenant}.app.clarantis.com`
  }

  // Check for Pages preview/branch deployments: *.grc-web-ui.pages.dev
  // These should use the workers.dev fallback or env var
  if (hostname.endsWith('.pages.dev')) {
    return import.meta.env.VITE_API_BASE_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'
  }

  // Fallback to env var or default
  return import.meta.env.VITE_API_BASE_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'
}

// Note: API_BASE is now dynamically computed on each request
// to support tenant switching during login flow

/**
 * Central API client for all GRC platform backend interactions.
 *
 * This class provides a unified interface for making authenticated HTTP requests
 * to the GRC backend API. It handles authentication token management, multi-tenant
 * context, and provides typed methods for all API endpoints including controls,
 * evidence, risks, vendors, documents, and more.
 *
 * **Key Features:**
 * - Automatic JWT token management with localStorage persistence
 * - Multi-tenant context handling for subdomain-based routing
 * - Consistent error handling across all endpoints
 * - TypeScript-first with fully typed request/response interfaces
 *
 * **Usage Pattern:**
 * A singleton instance (`api`) is exported from this module and should be
 * used throughout the application. Do not instantiate this class directly.
 *
 * @example
 * // Import the singleton instance
 * import { api } from '@/lib/api';
 *
 * // Set authentication token after login
 * api.setToken(authResponse.token);
 *
 * // Make authenticated API calls
 * const controls = await api.getControls({ framework: 'SOC2' });
 *
 * @example
 * // Handle multi-tenant context
 * api.setTenant({ slug: 'acme', name: 'ACME Corp' });
 * const dashboard = await api.getDashboardOverview(); // Routes to acme.app.clarantis.com
 *
 * @see {@link getApiBase} for understanding multi-tenant URL routing
 * @see {@link setSelectedTenant} for the underlying tenant storage mechanism
 */
class ApiClient {
  /**
   * The current JWT authentication token.
   * @private
   */
  private token: string | null = null

  /**
   * Creates a new ApiClient instance and restores any persisted authentication token.
   *
   * The constructor automatically loads any existing JWT token from localStorage,
   * allowing users to maintain their authenticated session across page refreshes.
   * The token is stored under the key `'grc_token'`.
   *
   * **Note:** You should use the exported singleton `api` instance rather than
   * creating new instances of this class.
   *
   * @example
   * // The singleton is automatically instantiated
   * import { api } from '@/lib/api';
   *
   * // Token is auto-loaded if user was previously logged in
   * if (api.getToken()) {
   *   console.log('User has existing session');
   * }
   */
  constructor() {
    this.token = localStorage.getItem('grc_token')
  }

  /**
   * Sets or clears the JWT authentication token for API requests.
   *
   * This method updates both the in-memory token and persists it to localStorage.
   * All subsequent API requests will include this token in the Authorization header.
   * Passing `null` clears the token, effectively logging the user out.
   *
   * @param {string | null} token - The JWT token to set, or null to clear authentication.
   *   The token should be the raw JWT string (without 'Bearer ' prefix).
   *
   * @returns {void}
   *
   * @example
   * // Set token after successful login
   * const response = await api.verifyCode(email, code);
   * api.setToken(response.token);
   *
   * @example
   * // Clear token during logout
   * async function handleLogout() {
   *   await api.logout();
   *   api.setToken(null);
   *   api.clearTenant();
   *   navigate('/login');
   * }
   *
   * @see {@link getToken} for retrieving the current token
   * @see {@link logout} for the full logout flow
   */
  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('grc_token', token)
    } else {
      localStorage.removeItem('grc_token')
    }
  }

  /**
   * Retrieves the current JWT authentication token.
   *
   * Returns the in-memory token that was either loaded from localStorage
   * on construction or set via {@link setToken}. This can be used to check
   * if the user has an active session or to manually inspect the token.
   *
   * @returns {string | null} The current JWT token, or null if not authenticated.
   *
   * @example
   * // Check if user is authenticated
   * if (api.getToken()) {
   *   // User has a session - fetch their data
   *   const user = await api.getMe();
   * } else {
   *   // Redirect to login
   *   navigate('/login');
   * }
   *
   * @example
   * // Pass token to external services (e.g., WebSocket connection)
   * const ws = new WebSocket(`wss://api.example.com?token=${api.getToken()}`);
   *
   * @see {@link setToken} for setting the authentication token
   */
  getToken(): string | null {
    return this.token
  }

  /**
   * Sets the current tenant context for multi-tenant API routing.
   *
   * This method stores the tenant information in localStorage, which is then
   * used by {@link getApiBase} to route API requests to the correct tenant-specific
   * backend URL (e.g., `https://{slug}.app.clarantis.com`).
   *
   * **When to use:**
   * - After user selects a tenant from the login flow (when they belong to multiple tenants)
   * - When programmatically switching between tenants
   *
   * Passing `null` clears the tenant context, which is equivalent to calling {@link clearTenant}.
   *
   * @param {Object | null} tenant - The tenant to set, or null to clear.
   * @param {string} tenant.slug - The unique tenant identifier used in subdomain URLs.
   *   Example: `"acme"` for `acme.app.clarantis.com`
   * @param {string | null} tenant.name - The human-readable display name for UI purposes.
   *
   * @returns {void}
   *
   * @example
   * // Set tenant after user selection during login
   * const tenantResponse = await api.lookupTenants(email);
   * if (tenantResponse.count === 1) {
   *   // Auto-select single tenant
   *   const t = tenantResponse.tenants[0];
   *   api.setTenant({ slug: t.slug, name: t.name });
   * }
   *
   * @example
   * // Switch tenants (for users with access to multiple organizations)
   * api.setTenant({ slug: 'newcorp', name: 'NewCorp Inc.' });
   * // Subsequent API calls will now route to newcorp.app.clarantis.com
   *
   * @see {@link getTenant} for retrieving the current tenant
   * @see {@link clearTenant} for clearing tenant during logout
   * @see {@link getApiBase} for how tenant affects API routing
   */
  setTenant(tenant: { slug: string; name: string | null } | null) {
    setSelectedTenant(tenant)
  }

  /**
   * Retrieves the currently selected tenant context.
   *
   * Returns the tenant information stored in localStorage that determines
   * which tenant-specific API endpoint is used for requests. This is useful
   * for displaying the current tenant in the UI or for conditional logic
   * based on tenant context.
   *
   * @returns {Object | null} The current tenant object, or null if no tenant is selected.
   *   - `slug` {string} - The tenant identifier used in subdomain URLs
   *   - `name` {string | null} - The human-readable tenant display name
   *
   * @example
   * // Display current tenant in header
   * const tenant = api.getTenant();
   * if (tenant) {
   *   headerElement.textContent = `Organization: ${tenant.name || tenant.slug}`;
   * }
   *
   * @example
   * // Check if tenant context is set before making API calls
   * if (!api.getTenant()) {
   *   navigate('/select-tenant');
   *   return;
   * }
   *
   * @see {@link setTenant} for setting the tenant context
   * @see {@link clearTenant} for clearing tenant during logout
   */
  getTenant() {
    return getSelectedTenant()
  }

  /**
   * Clears the current tenant context from localStorage.
   *
   * This should be called as part of the logout flow to ensure the user
   * must re-select their tenant on the next login. After clearing, subsequent
   * API calls will fall back to determining the tenant from the current
   * hostname or environment variables.
   *
   * @returns {void}
   *
   * @example
   * // Complete logout flow
   * async function handleLogout() {
   *   try {
   *     await api.logout(); // Invalidate session on server
   *   } catch (e) {
   *     // Continue logout even if server call fails
   *   }
   *   api.setToken(null);     // Clear authentication token
   *   api.clearTenant();      // Clear tenant context
   *   navigate('/login');
   * }
   *
   * @see {@link setTenant} for setting the tenant context
   * @see {@link getTenant} for retrieving the current tenant
   */
  clearTenant() {
    clearSelectedTenant()
  }

  /**
   * Stores the list of available tenants for the current user.
   *
   * This is called during login when validating a magic link that returns
   * the list of tenants the user has access to. Storing this list enables
   * the tenant switcher UI without requiring additional API calls.
   *
   * @param {AvailableTenant[]} tenants - Array of tenants the user can access
   */
  setAvailableTenants(tenants: AvailableTenant[]) {
    setAvailableTenants(tenants)
  }

  /**
   * Retrieves the list of available tenants for the current user.
   *
   * Returns the tenants that were stored during login. Used by the
   * tenant switcher component to display available options.
   *
   * @returns {AvailableTenant[]} Array of available tenants
   */
  getAvailableTenants(): AvailableTenant[] {
    return getAvailableTenants()
  }

  /**
   * Clears the list of available tenants.
   *
   * Should be called during logout to ensure the tenant list is
   * refreshed on the next login.
   */
  clearAvailableTenants() {
    clearAvailableTenants()
  }

  /**
   * Looks up which tenants a user has access to based on their email address.
   *
   * This method calls the central-admin service to query the multi-tenant database
   * and retrieve all organizations (tenants) associated with the given email address.
   * This is typically the first step in the multi-tenant login flow, allowing users
   * who belong to multiple organizations to select which one they want to access.
   *
   * **Flow Overview:**
   * 1. User enters their email on the login page
   * 2. This method is called to check tenant membership
   * 3. If user belongs to one tenant, auto-select it
   * 4. If user belongs to multiple tenants, show a selection UI
   * 5. After selection, proceed with {@link requestMagicLink} or {@link requestCode}
   *
   * **Note:** This method does NOT require authentication and calls the central-admin
   * service directly (not tenant-specific endpoints).
   *
   * @param {string} email - The user's email address to look up.
   *   Must be a valid email format. The lookup is case-insensitive.
   *
   * @returns {Promise<Object>} A promise resolving to the tenant lookup result:
   *   - `success` {boolean} - Whether the lookup was successful
   *   - `email` {string} - The email address that was queried (normalized)
   *   - `tenants` {Array<Object>} - List of tenants the user has access to:
   *     - `id` {string} - The unique tenant identifier (UUID)
   *     - `slug` {string} - The tenant subdomain slug (e.g., "acme" for acme.app.clarantis.com)
   *     - `name` {string | null} - The human-readable organization name
   *     - `role` {string} - The user's role in this tenant (e.g., "admin", "user", "auditor")
   *   - `count` {number} - The total number of tenants found
   *
   * @throws {Error} When the tenant lookup fails. Common error scenarios:
   *   - Invalid email format
   *   - Network connectivity issues
   *   - Central-admin service unavailable
   *
   * @example
   * // Check tenant membership during login
   * try {
   *   const result = await api.lookupTenants('user@example.com');
   *
   *   if (result.count === 0) {
   *     showError('No account found for this email');
   *   } else if (result.count === 1) {
   *     // Auto-select single tenant and proceed to authentication
   *     api.setTenant({ slug: result.tenants[0].slug, name: result.tenants[0].name });
   *     await api.requestMagicLink(result.email);
   *   } else {
   *     // Show tenant selection UI
   *     showTenantSelector(result.tenants);
   *   }
   * } catch (error) {
   *   showError('Failed to look up account');
   * }
   *
   * @see {@link requestMagicLink} for the next step in passwordless authentication
   * @see {@link setTenant} for setting the selected tenant context
   * @see {@link validateMagicLink} for validating the magic link token
   */
  async lookupTenants(email: string): Promise<{
    success: boolean
    email: string
    tenants: Array<{
      id: string
      slug: string
      name: string | null
      role: string
    }>
    count: number
  }> {
    const response = await fetch(
      `${CENTRAL_ADMIN_URL}/api/tenant-lookup?email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Tenant lookup failed')
    }

    return data
  }

  /**
   * Requests a tenant switch token from central-admin.
   *
   * This is step 1 of the tenant switch flow. The user must be authenticated
   * to their current tenant. Central-admin will verify the user has access
   * to the target tenant and issue a short-lived switch token.
   *
   * @param currentToken - The current JWT token
   * @param targetTenantSlug - The slug of the tenant to switch to
   * @param email - The user's email address
   * @returns A switch token that can be exchanged for a new JWT
   */
  async requestTenantSwitch(
    currentToken: string,
    targetTenantSlug: string,
    email: string
  ): Promise<{
    success: boolean
    switch_token?: string
    target_tenant?: {
      id: string
      slug: string
      name: string | null
      role: string
    }
    expires_in?: number
    message?: string
    error?: string
  }> {
    const response = await fetch(`${CENTRAL_ADMIN_URL}/api/tenant-auth/switch-tenant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_token: currentToken,
        target_tenant_slug: targetTenantSlug,
        email: email,
      }),
    })

    // Handle non-JSON responses (e.g., Cloudflare error pages)
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      const text = await response.text()
      // Check for Cloudflare error codes
      if (text.includes('error code:') || text.includes('522') || text.includes('524')) {
        return {
          success: false,
          error: 'Service temporarily unavailable',
          message: 'Unable to reach authentication service. Please try again.',
        }
      }
      return {
        success: false,
        error: 'Unexpected response',
        message: 'Server returned an unexpected response. Please try again.',
      }
    }

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to initiate tenant switch',
        message: data.message || 'Unable to switch tenants',
      }
    }

    return data
  }

  /**
   * Completes a tenant switch by exchanging the switch token for a new JWT.
   *
   * This is step 2 of the tenant switch flow. After getting a switch token
   * from central-admin via requestTenantSwitch(), this method calls the
   * target tenant's API to get a new JWT.
   *
   * Note: The tenant context must be set to the target tenant before calling this.
   *
   * @param switchToken - The switch token from requestTenantSwitch()
   * @returns The new JWT token and user info for the target tenant
   */
  async completeTenantSwitch(switchToken: string): Promise<{
    success: boolean
    token?: string
    sessionId?: string
    user?: {
      id: string
      username: string
      email: string
      role: string
    }
    expiresIn?: number
    message?: string
    error?: string
  }> {
    const response = await fetch(`${getApiBase()}/auth/switch-tenant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        switch_token: switchToken,
      }),
    })

    // Handle non-JSON responses (e.g., Cloudflare error pages)
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      const text = await response.text()
      // Check for Cloudflare error codes
      if (text.includes('error code:') || text.includes('522') || text.includes('524')) {
        return {
          success: false,
          error: 'Service temporarily unavailable',
          message: 'Unable to reach organization service. Please try again.',
        }
      }
      return {
        success: false,
        error: 'Unexpected response',
        message: 'Server returned an unexpected response. Please try again.',
      }
    }

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to complete tenant switch',
        message: data.message || 'Unable to switch tenants',
      }
    }

    return data
  }

  /**
   * Executes an authenticated HTTP request to the tenant-specific API endpoint.
   *
   * This is the core request method used by all public API methods in this class.
   * It handles authentication, content-type headers, multi-tenant URL routing,
   * and error handling in a consistent way.
   *
   * **Request Flow:**
   * 1. Builds headers with Authorization token (if available)
   * 2. Sets Content-Type to JSON (unless body is FormData for file uploads)
   * 3. Determines the correct API base URL via {@link getApiBase}
   * 4. Executes the fetch request
   * 5. Parses JSON response and throws on non-OK status
   *
   * **Header Handling:**
   * - Authorization header is automatically added if a token exists
   * - Content-Type defaults to 'application/json' unless body is FormData
   * - Additional headers from options.headers are merged in
   *
   * @template T - The expected response type
   *
   * @param {string} endpoint - The API endpoint path (e.g., '/api/controls').
   *   Should start with '/' and not include the base URL.
   * @param {RequestInit} [options={}] - Standard fetch options (method, body, headers, etc.).
   *   Body should be JSON.stringify'd for JSON requests.
   *
   * @returns {Promise<T>} The parsed JSON response body, typed as T.
   *
   * @throws {Error} When the response status is not OK (non-2xx). The error message
   *   is extracted from the response body's `message` or `error` field, or defaults
   *   to 'Request failed'.
   *
   * @example
   * // GET request (used internally by getControls)
   * const controls = await this.request<ControlsResponse>('/api/controls');
   *
   * @example
   * // POST request with JSON body (used internally by createControl)
   * const newControl = await this.request<Control>('/api/controls', {
   *   method: 'POST',
   *   body: JSON.stringify({ name: 'New Control', framework: 'SOC2' })
   * });
   *
   * @example
   * // File upload with FormData (used internally by uploadEvidence)
   * const formData = new FormData();
   * formData.append('file', file);
   * const evidence = await this.request<Evidence>('/api/evidence/upload', {
   *   method: 'POST',
   *   body: formData // Content-Type will NOT be set (browser handles multipart boundary)
   * });
   *
   * @private
   * @see {@link getApiBase} for multi-tenant URL routing logic
   * @see {@link setToken} for authentication token management
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {}

    // Always get fresh token from localStorage to handle cases where
    // the token was set after the ApiClient was instantiated
    const token = this.token || localStorage.getItem('grc_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    // Merge with existing headers if any
    if (options.headers) {
      Object.assign(headers, options.headers)
    }

    // Get API base dynamically for each request
    const apiBase = getApiBase()

    const response = await fetch(`${apiBase}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed')
    }

    return data
  }

  /**
   * Requests a magic link to be sent to the user's email for passwordless authentication.
   *
   * This method initiates the passwordless login flow by sending a one-time magic link
   * to the user's email address. The link contains a secure token that, when clicked,
   * validates the user's identity and allows them to complete the login process.
   *
   * **Authentication Flow:**
   * 1. User enters email → {@link lookupTenants} identifies their tenant(s)
   * 2. User selects tenant (if multiple) → {@link setTenant} stores the selection
   * 3. **This method sends the magic link email**
   * 4. User clicks link → Redirects to app with token in URL
   * 5. App calls {@link validateMagicLink} to verify token and get session
   *
   * **Note:** This method calls the central-admin service directly. The `web_ui_url`
   * parameter is automatically determined based on the environment (production origin
   * or localhost for development).
   *
   * @param {string} email - The user's email address to send the magic link to.
   *   Must be an email associated with a valid user account.
   *
   * @returns {Promise<Object>} A promise resolving to the request result:
   *   - `success` {boolean} - Whether the magic link was sent successfully
   *   - `message` {string} - A human-readable status message (e.g., "Magic link sent to your email")
   *
   * @throws {Error} When the magic link request fails. Common error scenarios:
   *   - Email not found in any tenant
   *   - Rate limiting (too many requests)
   *   - Email delivery failure
   *   - Central-admin service unavailable
   *
   * @example
   * // Request magic link after tenant selection
   * async function handleLoginSubmit(email: string) {
   *   try {
   *     const result = await api.requestMagicLink(email);
   *     if (result.success) {
   *       showMessage('Check your email for a login link');
   *       navigateToCheckEmailPage();
   *     }
   *   } catch (error) {
   *     showError('Failed to send login link. Please try again.');
   *   }
   * }
   *
   * @example
   * // Complete login flow with tenant lookup
   * async function initiateLogin(email: string) {
   *   // First, look up tenants
   *   const lookup = await api.lookupTenants(email);
   *
   *   if (lookup.count === 1) {
   *     // Set tenant and request magic link
   *     api.setTenant({ slug: lookup.tenants[0].slug, name: lookup.tenants[0].name });
   *     await api.requestMagicLink(email);
   *     showMessage('Magic link sent!');
   *   } else if (lookup.count > 1) {
   *     // Show tenant selector first
   *     showTenantSelector(lookup.tenants, email);
   *   }
   * }
   *
   * @see {@link lookupTenants} for the preceding step in the login flow
   * @see {@link validateMagicLink} for validating the magic link after user clicks it
   * @see {@link setTenant} for setting tenant context before requesting the link
   */
  async requestMagicLink(email: string): Promise<{
    success: boolean
    message: string
  }> {
    const webUiUrl = import.meta.env.PROD
      ? window.location.origin
      : 'http://localhost:5173'

    const response = await fetch(
      `${CENTRAL_ADMIN_URL}/api/tenant-auth/request-magic-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, web_ui_url: webUiUrl }),
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to send magic link')
    }

    return data
  }

  /**
   * Validates a magic link token and retrieves the associated user's tenant information.
   *
   * This method is called when a user clicks the magic link in their email. It verifies
   * that the token is valid and not expired, then returns the user's email and their
   * available tenants. This is the final step before the user can select a tenant and
   * complete their authentication.
   *
   * **Magic Link Validation Flow:**
   * 1. User clicks magic link in email → App receives token in URL
   * 2. **This method validates the token**
   * 3. If valid, display tenant selection (if multiple) or auto-authenticate
   * 4. User proceeds with tenant-specific authentication via {@link requestCode}
   *
   * **Token Characteristics:**
   * - One-time use: Token is invalidated after successful validation
   * - Time-limited: Typically expires after 15-30 minutes
   * - Secure: Contains cryptographic signature to prevent tampering
   *
   * **Note:** This method calls the central-admin service directly and does not
   * require prior authentication.
   *
   * @param {string} token - The magic link token from the URL.
   *   This is the unique token appended to the magic link URL that the user clicked.
   *
   * @returns {Promise<Object>} A promise resolving to the validation result:
   *   - `success` {boolean} - Whether the token was valid
   *   - `email` {string} - The email address associated with this magic link
   *   - `tenants` {Array<Object>} - List of tenants the user has access to:
   *     - `id` {string} - The unique tenant identifier (UUID)
   *     - `slug` {string} - The tenant subdomain slug (e.g., "acme" for acme.app.clarantis.com)
   *     - `name` {string | null} - The human-readable organization name
   *     - `role` {string} - The user's role in this tenant (e.g., "admin", "user", "auditor")
   *   - `magic_link_id` {string} - The unique identifier for this magic link session,
   *     used for audit logging and rate limiting
   *
   * @throws {Error} When the magic link validation fails. Common error scenarios:
   *   - "Invalid or expired link" - Token has expired or was already used
   *   - "Link not found" - Token doesn't exist or was tampered with
   *   - Network connectivity issues
   *   - Central-admin service unavailable
   *
   * @example
   * // Handle magic link callback in the app
   * async function handleMagicLinkCallback() {
   *   // Extract token from URL (e.g., /auth/magic?token=abc123)
   *   const urlParams = new URLSearchParams(window.location.search);
   *   const token = urlParams.get('token');
   *
   *   if (!token) {
   *     showError('Invalid login link');
   *     return;
   *   }
   *
   *   try {
   *     const result = await api.validateMagicLink(token);
   *
   *     if (result.tenants.length === 1) {
   *       // Auto-select single tenant and proceed to code verification
   *       const tenant = result.tenants[0];
   *       api.setTenant({ slug: tenant.slug, name: tenant.name });
   *       await api.requestCode(result.email);
   *       navigate('/verify-code');
   *     } else {
   *       // Show tenant selection
   *       showTenantSelector(result.tenants, result.email);
   *     }
   *   } catch (error) {
   *     showError('This link is invalid or has expired. Please request a new one.');
   *     navigate('/login');
   *   }
   * }
   *
   * @see {@link requestMagicLink} for requesting the magic link email
   * @see {@link requestCode} for the next step after tenant selection
   * @see {@link setTenant} for setting the selected tenant context
   */
  async validateMagicLink(token: string): Promise<{
    success: boolean
    email: string
    tenants: Array<{
      id: string
      slug: string
      name: string | null
      role: string
    }>
    magic_link_id: string
  }> {
    const response = await fetch(
      `${CENTRAL_ADMIN_URL}/api/tenant-auth/validate-magic-link/${token}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Invalid or expired link')
    }

    return data
  }

  /**
   * Requests a one-time verification code to be sent to the user's email.
   *
   * This method initiates the 2-step email verification process by sending a
   * numeric code to the user's email address. The code must be verified using
   * {@link verifyCode} to complete authentication and receive a JWT session token.
   *
   * **Authentication Flow:**
   * 1. User enters email → {@link lookupTenants} identifies their tenant(s)
   * 2. User selects tenant (if multiple) → {@link setTenant} stores the selection
   * 3. Optionally, user clicks magic link → {@link validateMagicLink} validates token
   * 4. **This method sends the verification code email**
   * 5. User receives code → {@link verifyCode} validates code and returns JWT token
   * 6. App calls {@link setToken} to store the session
   *
   * **Note:** This method calls the tenant-specific API endpoint (not central-admin).
   * Ensure the tenant context is set via {@link setTenant} before calling this method.
   *
   * The returned `session_token` is a temporary token that must be passed to
   * {@link verifyCode} along with the code. It expires after a short period (typically 10-15 minutes).
   *
   * @param {string} email - The user's email address to send the verification code to.
   *   Must be an email associated with a valid user account in the selected tenant.
   *
   * @returns {Promise<Object>} A promise resolving to the request result:
   *   - `success` {boolean} - Whether the code was sent successfully
   *   - `message` {string} - A human-readable status message (e.g., "Verification code sent")
   *   - `session_token` {string} - A temporary token to pass to {@link verifyCode}
   *
   * @throws {Error} When the code request fails. Common error scenarios:
   *   - Email not found in tenant's user database
   *   - User account is disabled or locked
   *   - Rate limiting (too many code requests)
   *   - Tenant context not set (API endpoint cannot be determined)
   *   - Network connectivity issues
   *
   * @example
   * // Request verification code after tenant selection
   * async function handleEmailSubmit(email: string) {
   *   try {
   *     const result = await api.requestCode(email);
   *     // Store session token for verification step
   *     setSessionToken(result.session_token);
   *     showMessage('Check your email for a verification code');
   *     navigateToVerifyCodePage();
   *   } catch (error) {
   *     showError('Failed to send verification code. Please try again.');
   *   }
   * }
   *
   * @example
   * // Complete flow from magic link validation to code request
   * async function handleMagicLinkCallback(token: string) {
   *   const validated = await api.validateMagicLink(token);
   *   if (validated.tenants.length === 1) {
   *     api.setTenant({
   *       slug: validated.tenants[0].slug,
   *       name: validated.tenants[0].name
   *     });
   *     const codeResult = await api.requestCode(validated.email);
   *     setSessionToken(codeResult.session_token);
   *     navigate('/verify-code');
   *   }
   * }
   *
   * @see {@link verifyCode} for the next step to verify the code
   * @see {@link resendCode} for requesting a new code if the original expires
   * @see {@link validateMagicLink} for the preceding magic link validation step
   * @see {@link setTenant} for setting the tenant context before requesting code
   */
  async requestCode(email: string) {
    const data = await this.request<{
      success: boolean
      message: string
      session_token: string
    }>('/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })

    if (!data.success) {
      throw new Error(data.message || 'Failed to send code')
    }

    return data
  }

  /**
   * Verifies the one-time code sent to the user's email and completes authentication.
   *
   * This method is the final step in the 2-step email verification process. It validates
   * the code entered by the user against the session token received from {@link requestCode}.
   * On successful verification, it returns a JWT token and user information, and automatically
   * stores the token for subsequent API calls via {@link setToken}.
   *
   * **Authentication Flow:**
   * 1. User enters email → {@link lookupTenants} identifies their tenant(s)
   * 2. User selects tenant → {@link setTenant} stores the selection
   * 3. {@link requestCode} sends verification code email and returns session token
   * 4. User enters the code received via email
   * 5. **This method validates the code and returns JWT + user data**
   * 6. JWT token is automatically stored via {@link setToken}
   *
   * **Security Notes:**
   * - The verification code is typically 6 digits
   * - Codes expire after a short period (typically 10-15 minutes)
   * - Multiple failed attempts may trigger rate limiting or account lockout
   * - The session token and code are both required for verification
   *
   * @param {string} sessionToken - The temporary session token returned from {@link requestCode}.
   *   This token associates the verification attempt with the original code request.
   * @param {string} code - The verification code entered by the user.
   *   Typically a 6-digit numeric code sent to the user's email.
   *
   * @returns {Promise<Object>} A promise resolving to the verification result:
   *   - `success` {boolean} - Whether the code was verified successfully
   *   - `token` {string} - The JWT authentication token for API requests (auto-stored)
   *   - `sessionId` {string} - The server-side session identifier
   *   - `user` {Object} - The authenticated user's information:
   *     - `id` {string} - The unique user identifier (UUID)
   *     - `username` {string} - The user's display name
   *     - `email` {string} - The user's email address
   *     - `role` {string} - The user's role (e.g., "admin", "user", "auditor")
   *   - `message` {string | undefined} - Optional status message
   *
   * @throws {Error} When code verification fails. Common error scenarios:
   *   - "Invalid or expired code" - Code doesn't match or has expired
   *   - "Session expired" - The session token has expired (request a new code)
   *   - "Too many attempts" - Rate limiting due to multiple failed attempts
   *   - "Account locked" - Account temporarily locked after too many failures
   *   - Network connectivity issues
   *
   * @example
   * // Verify code entered by user
   * async function handleCodeSubmit(code: string) {
   *   const sessionToken = getStoredSessionToken();
   *   try {
   *     const result = await api.verifyCode(sessionToken, code);
   *     if (result.success) {
   *       // Token is auto-stored, navigate to app
   *       localStorage.setItem('grc_session', result.sessionId);
   *       showMessage(`Welcome, ${result.user.username}!`);
   *       navigate('/dashboard');
   *     }
   *   } catch (error) {
   *     if (error.message.includes('expired')) {
   *       showError('Code expired. Please request a new one.');
   *     } else {
   *       showError('Invalid code. Please try again.');
   *     }
   *   }
   * }
   *
   * @example
   * // Complete login flow with error handling
   * async function completeLogin(sessionToken: string, code: string) {
   *   try {
   *     const result = await api.verifyCode(sessionToken, code);
   *     // Store session ID for logout
   *     localStorage.setItem('grc_session', result.sessionId);
   *     return { success: true, user: result.user };
   *   } catch (error) {
   *     return { success: false, error: error.message };
   *   }
   * }
   *
   * @see {@link requestCode} for requesting the verification code
   * @see {@link resendCode} for requesting a new code if the original expires
   * @see {@link setToken} for how the JWT token is stored (called automatically)
   * @see {@link getMe} for fetching updated user information after login
   */
  async verifyCode(sessionToken: string, code: string) {
    const data = await this.request<{
      success: boolean
      token?: string
      sessionId?: string
      user?: {
        id: string
        username: string
        email: string
        role: string
      }
      message?: string
      // 2FA fields - returned when user has 2FA enabled
      requires_2fa?: boolean
      mfa_session_token?: string
    }>('/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ session_token: sessionToken, code }),
    })

    // Only set token if this is a non-2FA login (token is present)
    if (data.success && data.token) {
      this.setToken(data.token)
    }

    return data
  }

  /**
   * Requests a new verification code to be sent to the user's email.
   *
   * This method allows users to request a new verification code if their original
   * code has expired or was not received. It uses the same session token from the
   * original {@link requestCode} call to maintain the authentication context.
   *
   * **Common Use Cases:**
   * - User didn't receive the original email (spam folder, delivery delay)
   * - Original code has expired (typically after 10-15 minutes)
   * - User accidentally deleted the email before noting the code
   *
   * **Rate Limiting:**
   * This method may be rate-limited to prevent abuse. Typical limits are:
   * - Maximum 3-5 resend attempts per session
   * - Minimum 30-60 seconds between resend requests
   *
   * The session token remains valid across resend requests, so you should
   * continue using the same session token when calling {@link verifyCode}.
   *
   * @param {string} sessionToken - The temporary session token returned from the
   *   original {@link requestCode} call. This maintains the verification context.
   *
   * @returns {Promise<Object>} A promise resolving to the resend result:
   *   - `success` {boolean} - Whether a new code was sent successfully
   *   - `message` {string} - A human-readable status message (e.g., "New code sent to your email")
   *
   * @throws {Error} When the resend request fails. Common error scenarios:
   *   - "Session expired" - The session token has expired; must restart with {@link requestCode}
   *   - "Rate limit exceeded" - Too many resend attempts; wait before trying again
   *   - "Maximum resend attempts reached" - Must restart the login flow
   *   - "Invalid session" - Session token is malformed or tampered with
   *   - Network connectivity issues
   *
   * @example
   * // Allow user to resend code with rate limit feedback
   * async function handleResendCode() {
   *   const sessionToken = getStoredSessionToken();
   *   setResendLoading(true);
   *
   *   try {
   *     const result = await api.resendCode(sessionToken);
   *     showMessage(result.message);
   *     // Start cooldown timer before allowing another resend
   *     startResendCooldown(60); // 60 seconds
   *   } catch (error) {
   *     if (error.message.includes('expired')) {
   *       showError('Session expired. Please start over.');
   *       navigate('/login');
   *     } else if (error.message.includes('Rate limit')) {
   *       showError('Please wait before requesting another code.');
   *     } else {
   *       showError('Failed to resend code. Please try again.');
   *     }
   *   } finally {
   *     setResendLoading(false);
   *   }
   * }
   *
   * @example
   * // Resend code with retry logic
   * async function resendWithRetry(sessionToken: string, maxRetries = 2) {
   *   for (let attempt = 0; attempt <= maxRetries; attempt++) {
   *     try {
   *       return await api.resendCode(sessionToken);
   *     } catch (error) {
   *       if (attempt === maxRetries) throw error;
   *       await sleep(1000 * (attempt + 1)); // Exponential backoff
   *     }
   *   }
   * }
   *
   * @see {@link requestCode} for the initial code request
   * @see {@link verifyCode} for verifying the received code
   */
  async resendCode(sessionToken: string) {
    const data = await this.request<{
      success: boolean
      message: string
    }>('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ session_token: sessionToken }),
    })

    if (!data.success) {
      throw new Error(data.message || 'Failed to resend code')
    }

    return data
  }

  /**
   * Resends a magic link email to the user (rate-limited: max 3 per 5 minutes).
   *
   * This method allows users to request a new magic link if they didn't receive
   * the original email or if it expired. It invalidates any pending magic links
   * for the email and generates a new one.
   *
   * **Use Cases:**
   * - User didn't receive the original magic link email
   * - Magic link expired before user clicked it
   * - Email was caught by spam filter
   * - User accidentally deleted the email
   *
   * **Rate Limiting:**
   * - Maximum 3 magic link requests per 5 minutes per email
   * - Returns 429 status with retry_after_minutes if limit exceeded
   *
   * @param {string} email - The user's email address
   * @returns {Promise<Object>} A promise resolving to the result:
   *   - `success` {boolean} - Whether the magic link was sent
   *   - `message` {string} - Status message
   *
   * @throws {Error} When rate limited or request fails
   *
   * @example
   * // Handle resend magic link
   * async function handleResendLink(email: string) {
   *   try {
   *     const result = await api.resendMagicLink(email);
   *     showMessage(result.message);
   *   } catch (error) {
   *     if (error.message.includes('Rate limited')) {
   *       showError(error.message);
   *     } else {
   *       showError('Failed to send sign-in link');
   *     }
   *   }
   * }
   *
   * @see {@link requestMagicLink} for the initial magic link request
   */
  async resendMagicLink(email: string): Promise<{
    success: boolean
    message: string
    retry_after_minutes?: number
  }> {
    const webUiUrl = import.meta.env.PROD
      ? window.location.origin
      : 'http://localhost:5173'

    const response = await fetch(
      `${CENTRAL_ADMIN_URL}/api/tenant-auth/resend-magic-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, web_ui_url: webUiUrl }),
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to send magic link')
    }

    return data
  }

  /**
   * Checks if an account is currently locked due to failed login attempts.
   *
   * This is a public endpoint that can be called before authentication to check
   * if an account is in a lockout state. This allows showing a clear message
   * to users before they attempt to enter a PIN code.
   *
   * **Important:** This method does not reveal whether an account exists.
   * If the account doesn't exist, it returns locked: false to prevent
   * account enumeration attacks.
   *
   * **Lockout Policy:**
   * - Accounts are locked after 5 failed PIN attempts (configurable)
   * - Default lockout duration is 30 minutes (configurable)
   * - Auto-unlocks when lockout period expires
   *
   * @param {string} email - The user's email address to check
   * @returns {Promise<Object>} A promise resolving to the lockout status:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `locked` {boolean} - Whether the account is currently locked
   *   - `locked_until` {number} - Unix timestamp when lockout expires (if locked)
   *   - `remaining_minutes` {number} - Minutes until lockout expires (if locked)
   *
   * @example
   * // Check lockout before showing PIN entry
   * async function checkBeforePinEntry(email: string) {
   *   const status = await api.checkLockoutStatus(email);
   *   if (status.locked) {
   *     showError(`Account locked. Try again in ${status.remaining_minutes} minutes.`);
   *     return false;
   *   }
   *   return true;
   * }
   */
  async checkLockoutStatus(email: string): Promise<{
    success: boolean
    locked: boolean
    locked_until?: number
    remaining_minutes?: number
  }> {
    const data = await this.request<{
      success: boolean
      locked: boolean
      locked_until?: number
      remaining_minutes?: number
    }>('/auth/check-lockout', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })

    return data
  }

  /**
   * Verifies a user's email address using a verification token.
   *
   * This endpoint is called when a user clicks the verification link in their email.
   * The token is a UUID that was generated when the user was registered.
   *
   * **Flow:**
   * 1. Admin creates user account via /auth/register
   * 2. User receives verification email with link containing token
   * 3. User clicks link, frontend extracts token and calls this method
   * 4. On success, email_verified is set to true and user can log in
   *
   * **Token Expiration:**
   * Verification tokens expire after 24 hours. If expired, user must request
   * a new verification email via {@link resendVerificationEmail}.
   *
   * @param {string} token - The verification token from the email link
   * @returns {Promise<Object>} A promise resolving to the verification result:
   *   - `success` {boolean} - Whether verification succeeded
   *   - `message` {string} - Status message
   *   - `already_verified` {boolean} - True if email was already verified
   *   - `expired` {boolean} - True if token has expired (only on failure)
   *
   * @throws {Error} When token is invalid, expired, or request fails
   *
   * @example
   * // Handle email verification from URL
   * async function handleEmailVerification(token: string) {
   *   try {
   *     const result = await api.verifyEmail(token);
   *     if (result.success) {
   *       showSuccess(result.message);
   *       navigate('/login');
   *     }
   *   } catch (error) {
   *     if (error.message.includes('expired')) {
   *       showError('Verification link expired. Request a new one.');
   *     } else {
   *       showError('Invalid verification link.');
   *     }
   *   }
   * }
   *
   * @see {@link resendVerificationEmail} for requesting a new verification email
   */
  async verifyEmail(token: string, tenant?: string): Promise<{
    success: boolean
    message: string
    already_verified?: boolean
    expired?: boolean
  }> {
    // When tenant is provided in production, use tenant subdomain directly
    // This ensures the request goes to the correct worker, not the fallback
    if (tenant && import.meta.env.PROD) {
      const response = await fetch(`https://${tenant}.app.clarantis.com/auth/verify-email/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await response.json()
      if (!data.success) {
        throw new Error(data.message || data.error || 'Email verification failed')
      }
      return data
    }

    const headers: Record<string, string> = {}
    if (tenant) {
      headers['X-Tenant-ID'] = tenant
    }

    const data = await this.request<{
      success: boolean
      message: string
      already_verified?: boolean
      expired?: boolean
      error?: string
    }>(`/auth/verify-email/${token}`, {
      method: 'POST',
      headers,
    })

    if (!data.success) {
      throw new Error(data.message || data.error || 'Email verification failed')
    }

    return data
  }

  /**
   * Resends the email verification link to a user.
   *
   * This is used when:
   * - The original verification email wasn't received
   * - The verification link expired (24 hours)
   * - User wants to receive a new verification email
   *
   * **Security:**
   * - Rate limited to 5 requests per 15 minutes per IP
   * - Does not reveal if account exists (always returns success message)
   * - Only sends email if account exists and is not yet verified
   *
   * @param {string} email - The user's email address
   * @returns {Promise<Object>} A promise resolving to the result:
   *   - `success` {boolean} - Whether request was processed
   *   - `message` {string} - Status message
   *   - `already_verified` {boolean} - True if email is already verified
   *
   * @throws {Error} When rate limited or request fails
   *
   * @example
   * // Request new verification email
   * async function handleResendVerification(email: string) {
   *   try {
   *     const result = await api.resendVerificationEmail(email);
   *     if (result.already_verified) {
   *       showInfo('Your email is already verified. You can log in.');
   *     } else {
   *       showSuccess(result.message);
   *     }
   *   } catch (error) {
   *     showError(error.message);
   *   }
   * }
   *
   * @see {@link verifyEmail} for verifying with the token
   */
  async resendVerificationEmail(email: string, tenant?: string): Promise<{
    success: boolean
    message: string
    already_verified?: boolean
    retry_after?: number
  }> {
    // When tenant is provided in production, use tenant subdomain directly
    // This ensures the request goes to the correct worker, not the fallback
    if (tenant && import.meta.env.PROD) {
      const response = await fetch(`https://${tenant}.app.clarantis.com/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await response.json()
      if (!data.success) {
        throw new Error(data.message || data.error || 'Failed to resend verification email')
      }
      return data
    }

    const headers: Record<string, string> = {}
    if (tenant) {
      headers['X-Tenant-ID'] = tenant
    }

    const data = await this.request<{
      success: boolean
      message: string
      already_verified?: boolean
      retry_after?: number
      error?: string
    }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers,
    })

    if (!data.success) {
      throw new Error(data.message || data.error || 'Failed to resend verification email')
    }

    return data
  }

  /**
   * Logs out the current user by invalidating the session on the server.
   *
   * This method performs a complete logout by:
   * 1. Sending a logout request to the server to invalidate the session
   * 2. Clearing the local JWT token via {@link setToken}(null)
   * 3. Removing the session ID from localStorage
   *
   * **Important:** This method clears local authentication state even if the
   * server request fails. This ensures users can always log out locally,
   * preventing them from being stuck in an authenticated state due to network issues.
   *
   * **Cleanup Recommendations:**
   * After calling logout, you should also:
   * - Call {@link clearTenant} to clear the tenant context
   * - Navigate the user to the login page
   * - Clear any cached user data in your application state
   *
   * **Session ID Header:**
   * The logout request includes an `X-Session-ID` header if a session ID is stored
   * in localStorage. This allows the server to invalidate the specific session,
   * which is important for audit logging and security.
   *
   * @returns {Promise<void>} A promise that resolves when logout is complete.
   *   The promise always resolves (never rejects) since local cleanup is performed
   *   in the finally block regardless of server response.
   *
   * @example
   * // Complete logout flow
   * async function handleLogout() {
   *   try {
   *     await api.logout();
   *   } catch (error) {
   *     // Log error but continue - local state is already cleared
   *     console.warn('Server logout failed:', error);
   *   }
   *   // Clear tenant context
   *   api.clearTenant();
   *   // Clear any application state
   *   clearUserState();
   *   // Navigate to login
   *   navigate('/login');
   * }
   *
   * @example
   * // Logout with confirmation dialog
   * async function confirmLogout() {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Sign Out',
   *     message: 'Are you sure you want to sign out?',
   *   });
   *
   *   if (confirmed) {
   *     await api.logout();
   *     api.clearTenant();
   *     navigate('/login');
   *   }
   * }
   *
   * @example
   * // Forced logout on session expiry
   * function handleSessionExpiry() {
   *   api.logout().then(() => {
   *     api.clearTenant();
   *     showMessage('Your session has expired. Please log in again.');
   *     navigate('/login');
   *   });
   * }
   *
   * @see {@link setToken} for how the JWT token is cleared
   * @see {@link clearTenant} for clearing tenant context (should be called after logout)
   * @see {@link getMe} for checking current authentication status
   */
  async logout() {
    const sessionId = localStorage.getItem('grc_session')
    try {
      await this.request('/auth/logout', {
        method: 'POST',
        headers: sessionId ? { 'X-Session-ID': sessionId } : {},
      })
    } finally {
      this.setToken(null)
      localStorage.removeItem('grc_session')
    }
  }

  // ===========================================================================
  // SESSION MANAGEMENT
  // ===========================================================================

  /**
   * Retrieves all active sessions for the current user.
   *
   * This method fetches a list of all active sessions, allowing users to see
   * which devices/browsers are currently logged into their account. Each session
   * includes device information, IP address, and timestamps.
   *
   * The response includes an `isCurrent` flag indicating which session is the
   * one making the request, helping users identify their current session.
   *
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `sessions` {Array} - List of active sessions with:
   *     - `sessionId` {string} - Unique session identifier
   *     - `deviceInfo` {string} - Parsed device/browser information
   *     - `createdAt` {number} - When the session was created (Unix timestamp)
   *     - `lastActivityAt` {number} - Last activity timestamp
   *     - `expiresAt` {number} - When the session expires
   *     - `ipAddress` {string} - IP address of the session
   *     - `userAgent` {string} - Raw user agent string
   *     - `isCurrent` {boolean} - Whether this is the current session
   *   - `count` {number} - Total number of active sessions
   *
   * @example
   * const { sessions, count } = await api.getSessions();
   * console.log(`You have ${count} active sessions`);
   * sessions.forEach(s => {
   *   console.log(`${s.deviceInfo} - ${s.isCurrent ? '(Current)' : ''}`);
   * });
   */
  async getSessions() {
    const sessionId = localStorage.getItem('grc_session')
    return this.request<{
      success: boolean
      sessions: Array<{
        sessionId: string
        deviceInfo: string
        createdAt: number
        lastActivityAt: number
        expiresAt: number
        ipAddress: string | null
        userAgent: string | null
        isCurrent: boolean
      }>
      count: number
    }>('/auth/sessions', {
      headers: sessionId ? { 'X-Session-ID': sessionId } : {},
    })
  }

  /**
   * Revokes a specific session, logging out that device/browser.
   *
   * This allows users to terminate sessions from other devices, useful for
   * security purposes (e.g., if they suspect unauthorized access).
   *
   * If the revoked session is the current session, the response will include
   * `wasCurrentSession: true`, indicating the user will be logged out.
   *
   * @param {string} sessionId - The ID of the session to revoke
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the revocation was successful
   *   - `message` {string} - Success/error message
   *   - `wasCurrentSession` {boolean} - Whether the revoked session was current
   *
   * @example
   * // Revoke a suspicious session
   * const result = await api.revokeSession(suspiciousSessionId);
   * if (result.wasCurrentSession) {
   *   // User revoked their own session, log them out
   *   api.setToken(null);
   *   navigate('/login');
   * }
   */
  async revokeSession(sessionId: string) {
    const currentSessionId = localStorage.getItem('grc_session')
    return this.request<{
      success: boolean
      message: string
      wasCurrentSession: boolean
    }>(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: currentSessionId ? { 'X-Session-ID': currentSessionId } : {},
    })
  }

  /**
   * Revokes all sessions except optionally the current one.
   *
   * This is a "log out all devices" feature, useful when users suspect their
   * account has been compromised or when they want to reset all sessions.
   *
   * By default, keeps the current session active. Pass `exceptCurrent: false`
   * to also revoke the current session (full logout everywhere).
   *
   * @param {boolean} exceptCurrent - If true (default), keep current session
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the operation was successful
   *   - `message` {string} - Success message
   *   - `revokedCount` {number} - Number of sessions revoked
   *   - `keptCurrent` {boolean} - Whether current session was kept
   *
   * @example
   * // Log out all other devices
   * const result = await api.revokeAllSessions();
   * toast(`Logged out from ${result.revokedCount} other devices`);
   *
   * @example
   * // Complete logout everywhere (including current)
   * const result = await api.revokeAllSessions(false);
   * api.setToken(null);
   * navigate('/login');
   */
  async revokeAllSessions(exceptCurrent = true) {
    const currentSessionId = localStorage.getItem('grc_session')
    return this.request<{
      success: boolean
      message: string
      revokedCount: number
      keptCurrent: boolean
    }>('/auth/sessions/revoke-all', {
      method: 'POST',
      headers: currentSessionId ? { 'X-Session-ID': currentSessionId } : {},
      body: JSON.stringify({ exceptCurrent }),
    })
  }

  /**
   * Retrieves the current authenticated user's profile information.
   *
   * This method fetches the user's profile data from the server using the stored
   * JWT token. It's commonly used to verify authentication status, display user
   * information in the UI, or refresh user data after profile updates.
   *
   * **Common Use Cases:**
   * - Verify if user is still authenticated on app load
   * - Display user name/email in the navigation header
   * - Check user role for permission-based UI rendering
   * - Refresh user data after profile updates
   *
   * **Authentication Requirement:**
   * This method requires a valid JWT token to be set via {@link setToken}.
   * If no token is set or the token is invalid/expired, the request will fail.
   *
   * **Response Caching:**
   * This method does not cache results. For performance, consider caching the
   * user data in your application state and only re-fetching when needed.
   *
   * @returns {Promise<Object>} A promise resolving to the user data:
   *   - `success` {boolean} - Whether the request was successful
   *   - `user` {Object} - The authenticated user's information:
   *     - `id` {string} - The unique user identifier (UUID)
   *     - `username` {string} - The user's display name
   *     - `email` {string} - The user's email address
   *     - `role` {string} - The user's role (e.g., "admin", "user", "auditor")
   *     - `first_name` {string | undefined} - The user's first name (optional)
   *     - `last_name` {string | undefined} - The user's last name (optional)
   *
   * @throws {Error} When the request fails. Common error scenarios:
   *   - "Unauthorized" or 401 - No token set or token is invalid/expired
   *   - "User not found" - Token is valid but user no longer exists
   *   - Network connectivity issues
   *
   * @example
   * // Check authentication on app load
   * async function initializeApp() {
   *   if (!api.getToken()) {
   *     navigate('/login');
   *     return;
   *   }
   *
   *   try {
   *     const result = await api.getMe();
   *     setCurrentUser(result.user);
   *     navigate('/dashboard');
   *   } catch (error) {
   *     // Token is invalid or expired
   *     api.setToken(null);
   *     api.clearTenant();
   *     navigate('/login');
   *   }
   * }
   *
   * @example
   * // Display user info in header
   * async function loadUserProfile() {
   *   try {
   *     const { user } = await api.getMe();
   *     const displayName = user.first_name
   *       ? `${user.first_name} ${user.last_name || ''}`
   *       : user.username;
   *     headerElement.textContent = displayName;
   *     roleElement.textContent = user.role;
   *   } catch (error) {
   *     handleAuthError(error);
   *   }
   * }
   *
   * @example
   * // Role-based UI rendering
   * async function checkAdminAccess() {
   *   const { user } = await api.getMe();
   *   if (user.role !== 'admin') {
   *     showError('Admin access required');
   *     navigate('/dashboard');
   *     return false;
   *   }
   *   return true;
   * }
   *
   * @see {@link verifyCode} for how the user data is first retrieved during login
   * @see {@link updateProfile} for updating user profile information
   * @see {@link logout} for ending the user session
   */
  async getMe() {
    return this.request<{
      success: boolean
      user: {
        id: string
        username: string
        email: string
        role: string
        first_name?: string
        last_name?: string
        phone?: string
      }
    }>('/auth/me')
  }

  // Two-Factor Authentication (2FA) endpoints

  /**
   * Get the current 2FA status for the authenticated user.
   *
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `enabled` {boolean} - Whether 2FA is enabled
   *   - `backup_codes_remaining` {number} - Number of unused backup codes
   */
  async get2FAStatus() {
    return this.request<{
      success: boolean
      enabled: boolean
      backup_codes_remaining: number
    }>('/auth/2fa/status')
  }

  /**
   * Start 2FA setup - generates TOTP secret and returns QR code data.
   *
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `secret` {string} - The TOTP secret in base32 format
   *   - `otpauth_url` {string} - The otpauth URL for QR code generation
   *   - `issuer` {string} - The issuer name (Clarantis GRC)
   *   - `account` {string} - The account (user email)
   */
  async setup2FA() {
    return this.request<{
      success: boolean
      secret: string
      otpauth_url: string
      issuer: string
      account: string
    }>('/auth/2fa/setup', {
      method: 'POST',
    })
  }

  /**
   * Complete 2FA setup by verifying a TOTP code.
   *
   * @param {string} code - The 6-digit TOTP code from the authenticator app
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `message` {string} - Success message
   *   - `backup_codes` {string[]} - Array of backup codes (only shown once!)
   */
  async verify2FASetup(code: string) {
    return this.request<{
      success: boolean
      message: string
      backup_codes: string[]
    }>('/auth/2fa/verify-setup', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  }

  /**
   * Disable 2FA on the account.
   *
   * @param {string} code - A valid TOTP code or backup code
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `message` {string} - Success message
   */
  async disable2FA(code: string) {
    return this.request<{
      success: boolean
      message: string
    }>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  }

  /**
   * Generate new backup codes (invalidates old ones).
   *
   * @param {string} code - A valid TOTP code
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `message` {string} - Success message
   *   - `backup_codes` {string[]} - Array of new backup codes
   */
  async regenerate2FABackupCodes(code: string) {
    return this.request<{
      success: boolean
      message: string
      backup_codes: string[]
    }>('/auth/2fa/backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  }

  /**
   * Verify a 2FA code during login flow.
   * This is called after email verification when user has 2FA enabled.
   *
   * @param {string} mfaSessionToken - The MFA session token from verify-code response
   * @param {string} code - The 6-digit TOTP code or backup code
   * @returns {Promise<Object>} A promise resolving to:
   *   - `success` {boolean} - Whether the request was successful
   *   - `token` {string} - The JWT auth token
   *   - `sessionId` {string} - The session ID
   *   - `user` {Object} - The authenticated user data
   *   - `expiresIn` {number} - Token expiry in seconds
   *   - `used_backup_code` {boolean} - Whether a backup code was used
   */
  async verify2FALogin(mfaSessionToken: string, code: string) {
    return this.request<{
      success: boolean
      token?: string
      sessionId?: string
      user?: {
        id: string
        username: string
        email: string
        role: string
      }
      expiresIn?: number
      used_backup_code?: boolean
      error?: string
      message?: string
    }>('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ mfa_session_token: mfaSessionToken, code }),
    })
  }

  // Dashboard endpoints

  /**
   * Retrieves the main dashboard overview with high-level compliance metrics and status summaries.
   *
   * This method fetches aggregated data for the dashboard's primary view, providing
   * a snapshot of the organization's overall GRC (Governance, Risk, and Compliance) posture.
   * The overview includes metrics across controls, risks, evidence, and compliance frameworks.
   *
   * **Overview Data Typically Includes:**
   * - Control compliance statistics (total, compliant, non-compliant, in-progress)
   * - Risk register summary (total risks by severity/status)
   * - Evidence collection status (total evidence, pending reviews)
   * - Framework compliance percentages
   * - Task and assessment counts
   *
   * **Performance Note:** This endpoint aggregates data from multiple sources and may
   * take longer than simple CRUD operations. Consider caching the response or using
   * optimistic UI updates for frequently accessed dashboards.
   *
   * @returns {Promise<Object>} A promise resolving to the dashboard overview:
   *   - `success` {boolean} - Whether the request was successful
   *   - `overview` {Object} - Aggregated dashboard metrics and status summaries
   *     - May include: controlStats, riskStats, evidenceStats, frameworkCompliance,
   *       taskCounts, recentUpdates, and other tenant-specific metrics
   *
   * @throws {Error} When the request fails. Common scenarios:
   *   - User not authenticated (401)
   *   - Tenant context not set
   *   - Backend service unavailable
   *
   * @example
   * // Fetch dashboard overview on page load
   * async function loadDashboard() {
   *   try {
   *     const { overview } = await api.getDashboardOverview();
   *
   *     setControlStats({
   *       total: overview.controls.total,
   *       compliant: overview.controls.compliant,
   *       percentage: overview.controls.complianceRate
   *     });
   *
   *     setRiskSummary(overview.risks);
   *     setFrameworkProgress(overview.frameworks);
   *   } catch (error) {
   *     showError('Failed to load dashboard data');
   *   }
   * }
   *
   * @example
   * // Refresh dashboard with loading state
   * async function refreshDashboard() {
   *   setLoading(true);
   *   try {
   *     const { overview } = await api.getDashboardOverview();
   *     updateDashboardState(overview);
   *   } finally {
   *     setLoading(false);
   *   }
   * }
   *
   * @see {@link getDashboardActivity} for recent activity feed
   * @see {@link getDashboardAlerts} for compliance alerts and warnings
   * @see {@link getComplianceSummary} for detailed compliance reporting
   */
  async getDashboardOverview(startDate?: number, endDate?: number) {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate.toString())
    if (endDate) params.append('end_date', endDate.toString())
    const queryString = params.toString()
    return this.request<{
      success: boolean
      overview: any
    }>(`/api/dashboard/overview${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Retrieves a feed of recent activity across the organization's GRC platform.
   *
   * This method fetches the most recent actions and events that have occurred
   * within the tenant, providing an audit trail and activity timeline for the dashboard.
   * Activity items typically include user actions, system events, and compliance updates.
   *
   * **Activity Types May Include:**
   * - Control status changes (e.g., "Control AC-1 marked as compliant")
   * - Evidence uploads and approvals
   * - Risk register updates (new risks, status changes)
   * - Document modifications
   * - User actions (logins, permission changes)
   * - Integration sync events
   * - Assessment submissions and reviews
   *
   * **Pagination:** This endpoint uses a simple limit-based approach. For infinite
   * scroll implementations, track the oldest item's timestamp and use offset-based
   * pagination in subsequent requests.
   *
   * @param {number} [limit=20] - Maximum number of activity items to retrieve.
   *   Defaults to 20. Higher values may increase response time.
   *   Recommended range: 10-50 for optimal performance.
   *
   * @returns {Promise<Object>} A promise resolving to the activity feed:
   *   - `success` {boolean} - Whether the request was successful
   *   - `activity` {Array<Object>} - Array of activity items, sorted by recency (newest first)
   *     - Each item typically includes: id, type, description, timestamp, user, entityType, entityId
   *
   * @throws {Error} When the request fails. Common scenarios:
   *   - User not authenticated (401)
   *   - Tenant context not set
   *   - Invalid limit parameter
   *
   * @example
   * // Load initial activity feed
   * async function loadActivityFeed() {
   *   const { activity } = await api.getDashboardActivity(10);
   *   setActivityItems(activity);
   * }
   *
   * @example
   * // Implement "load more" functionality
   * async function loadMoreActivity(currentCount: number) {
   *   const { activity } = await api.getDashboardActivity(currentCount + 20);
   *   // Note: This returns all items up to the limit, not just new ones
   *   setActivityItems(activity);
   * }
   *
   * @example
   * // Auto-refresh activity feed
   * useEffect(() => {
   *   const fetchActivity = async () => {
   *     const { activity } = await api.getDashboardActivity(15);
   *     setRecentActivity(activity);
   *   };
   *
   *   fetchActivity();
   *   const interval = setInterval(fetchActivity, 30000); // Refresh every 30s
   *   return () => clearInterval(interval);
   * }, []);
   *
   * @see {@link getDashboardOverview} for high-level metrics
   * @see {@link getAuditLogs} for detailed audit trail with filtering
   * @see {@link getNotifications} for user-specific notifications
   */
  async getDashboardActivity(limit = 20, startDate?: number, endDate?: number) {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    if (startDate) params.append('start_date', startDate.toString())
    if (endDate) params.append('end_date', endDate.toString())
    return this.request<{
      success: boolean
      activity: any[]
    }>(`/api/dashboard/activity?${params.toString()}`)
  }

  /**
   * Retrieves active compliance alerts and warnings requiring attention.
   *
   * This method fetches priority alerts that indicate compliance issues, overdue tasks,
   * or system warnings that require action. Alerts are typically displayed prominently
   * on the dashboard to draw attention to items needing immediate review.
   *
   * **Alert Types May Include:**
   * - Overdue control assessments
   * - Expiring evidence (approaching review deadlines)
   * - High or critical unmitigated risks
   * - Failed integration syncs
   * - Incomplete assessment campaigns
   * - Framework compliance threshold breaches
   * - Vendor risk assessment warnings
   * - Dark web monitoring alerts
   *
   * **Severity Levels:**
   * - `critical`: Requires immediate attention (compliance violations, security issues)
   * - `high`: Important items that should be addressed soon
   * - `medium`: Items to review when possible (included in total, not in counts breakdown)
   * - `low`: Informational alerts (included in total, not in counts breakdown)
   *
   * @returns {Promise<Object>} A promise resolving to the alerts data:
   *   - `success` {boolean} - Whether the request was successful
   *   - `alerts` {Array<Object>} - Array of active alert objects, sorted by severity
   *     - Each alert typically includes: id, type, severity, title, description,
   *       createdAt, entityType, entityId, actionUrl
   *   - `counts` {Object} - Summary counts by severity level:
   *     - `critical` {number} - Number of critical severity alerts
   *     - `high` {number} - Number of high severity alerts
   *     - `total` {number} - Total number of active alerts across all severities
   *
   * @throws {Error} When the request fails. Common scenarios:
   *   - User not authenticated (401)
   *   - Tenant context not set
   *   - Backend service unavailable
   *
   * @example
   * // Display alert counts in dashboard header
   * async function loadAlertCounts() {
   *   const { counts } = await api.getDashboardAlerts();
   *
   *   if (counts.critical > 0) {
   *     showCriticalBanner(`${counts.critical} critical alerts require attention`);
   *   }
   *
   *   setAlertBadge(counts.total);
   * }
   *
   * @example
   * // Render alert list with severity indicators
   * async function loadAlerts() {
   *   const { alerts, counts } = await api.getDashboardAlerts();
   *
   *   return (
   *     <div>
   *       <AlertSummary critical={counts.critical} high={counts.high} total={counts.total} />
   *       {alerts.map(alert => (
   *         <AlertCard
   *           key={alert.id}
   *           severity={alert.severity}
   *           title={alert.title}
   *           description={alert.description}
   *           onAction={() => navigateTo(alert.actionUrl)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Poll for new critical alerts
   * useEffect(() => {
   *   const checkAlerts = async () => {
   *     const { counts } = await api.getDashboardAlerts();
   *     if (counts.critical > previousCritical) {
   *       showNotification('New critical alert requires attention');
   *     }
   *     setPreviousCritical(counts.critical);
   *   };
   *
   *   const interval = setInterval(checkAlerts, 60000); // Check every minute
   *   return () => clearInterval(interval);
   * }, [previousCritical]);
   *
   * @see {@link getDashboardOverview} for general compliance metrics
   * @see {@link getDashboardActivity} for recent activity feed
   * @see {@link getNotifications} for user-specific notifications
   * @see {@link getDarkWebAlerts} for vendor-specific dark web monitoring alerts
   */
  async getDashboardAlerts() {
    return this.request<{
      success: boolean
      alerts: any[]
      counts: { critical: number; high: number; total: number }
    }>('/api/dashboard/alerts')
  }

  /**
   * Retrieves compliance and evidence trends over time.
   *
   * This method fetches historical trend data for visualization in
   * dashboard charts, showing how compliance metrics have changed
   * over the specified time period.
   *
   * @param {number} [days=30] - Number of days of history to retrieve.
   *   Maximum is 90 days.
   * @param {number} [startDate] - Optional start date timestamp (ms).
   * @param {number} [endDate] - Optional end date timestamp (ms).
   *
   * @returns {Promise<Object>} Response containing trend data arrays.
   */
  async getDashboardTrends(days = 30, startDate?: number, endDate?: number) {
    const params = new URLSearchParams()
    params.append('days', days.toString())
    if (startDate) params.append('start_date', startDate.toString())
    if (endDate) params.append('end_date', endDate.toString())
    return this.request<{
      success: boolean
      trends: {
        evidence_collection: Array<{ date: string; count: number }>
        period_days: number
      }
    }>(`/api/dashboard/trends?${params.toString()}`)
  }

  /**
   * Retrieves industry benchmark comparisons for compliance scores.
   *
   * @returns {Promise<Object>} Response containing benchmark comparisons per framework.
   */
  async getDashboardBenchmarks() {
    return this.request<{
      success: boolean
      benchmarks: Array<{
        framework: string
        display_name: string
        your_score: number
        industry_average: number
        top_quartile: number
        vs_average: number
        vs_top: number
      }>
      note: string
    }>('/api/dashboard/benchmarks')
  }

  // ============================================================================
  // RISK INSIGHTS DASHBOARD (Issue #272)
  // ============================================================================

  /**
   * Get risk posture - distribution by severity level
   */
  async getRiskPosture() {
    return this.request<{
      success: boolean
      posture: {
        by_severity: Array<{
          severity: 'critical' | 'high' | 'medium' | 'low'
          count: number
          open_count: number
          mitigating_count: number
          resolved_count: number
        }>
        totals: {
          critical: number
          high: number
          medium: number
          low: number
          total: number
        }
        risk_score: number
      }
    }>('/api/dashboard/risk-posture')
  }

  /**
   * Get 5x5 risk heatmap for dashboard (Impact vs Likelihood matrix)
   */
  async getDashboardRiskHeatmap() {
    return this.request<{
      success: boolean
      heatmap: {
        matrix: Array<Array<{
          likelihood: number
          impact: number
          count: number
          risk_ids: string[]
        }>>
        labels: {
          likelihood: string[]
          impact: string[]
        }
      }
    }>('/api/dashboard/risk-heatmap')
  }

  /**
   * Get risks grouped by category with appetite data
   */
  async getRisksByCategory() {
    return this.request<{
      success: boolean
      by_category: Array<{
        category_id: string
        category_name: string
        color: string
        appetite_level: number | null
        tolerance_threshold: number | null
        max_acceptable_score: number | null
        appetite_statement: string | null
        risk_count: number
        open_risks: number
        avg_risk_score: number | null
        max_risk_score: number | null
        above_tolerance: number
      }>
      uncategorized: {
        risk_count: number
        open_risks: number
        avg_risk_score: number | null
        max_risk_score: number | null
      }
    }>('/api/dashboard/risk-by-category')
  }

  /**
   * Compare risk appetite settings against actual exposure
   */
  async getAppetiteVsActual() {
    return this.request<{
      success: boolean
      comparison: Array<{
        appetite_id: string
        category: string
        display_name: string
        appetite_level: number
        tolerance_threshold: number
        max_acceptable_score: number
        appetite_statement: string | null
        category_id: string | null
        category_name: string | null
        color: string | null
        total_risks: number
        avg_exposure: number | null
        max_exposure: number | null
        tolerance_breaches: number
        critical_breaches: number
      }>
      summary: {
        total_categories: number
        compliant_categories: number
        compliance_percentage: number
      }
    }>('/api/dashboard/appetite-vs-actual')
  }

  /**
   * Get KRI summary with traffic light indicators
   */
  async getKRISummary() {
    return this.request<{
      success: boolean
      summary: {
        total: number
        green: number
        yellow: number
        red: number
        no_data: number
      }
      kris: Array<{
        id: string
        name: string
        description: string | null
        metric_type: string
        unit: string | null
        green_threshold: number
        yellow_threshold: number
        direction: string
        category_id: string | null
        category_name: string | null
        category_color: string | null
        current_value: number | null
        current_status: 'green' | 'yellow' | 'red' | null
        last_recorded_at: number | null
        linked_risks_count: number
      }>
      by_category: Record<string, any[]>
      unacknowledged_alerts: number
    }>('/api/dashboard/kri-summary')
  }

  /**
   * Get KRI trends over time for sparklines
   */
  async getKRITrends(days?: number, kriId?: string) {
    const params = new URLSearchParams()
    if (days) params.append('days', days.toString())
    if (kriId) params.append('kri_id', kriId)
    const queryString = params.toString()
    return this.request<{
      success: boolean
      trends: Record<string, {
        kri_name: string
        values: Array<{
          value: number
          status: string
          recorded_at: number
        }>
      }>
      period_days: number
      start_date: number
    }>(`/api/dashboard/kri-trends${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Retrieves detailed compliance score breakdown for transparency.
   *
   * This method provides complete visibility into how the compliance score is calculated,
   * including per-framework breakdowns, individual control contributions, and
   * prioritized recommendations for improving the score.
   *
   * The response includes:
   * - Overall score formula and methodology explanation
   * - Per-framework compliance breakdown with control-level details
   * - Impact scores for each control to prioritize remediation
   * - Top recommendations for highest-impact improvements
   * - Status definitions for understanding control states
   *
   * @returns {Promise<Object>} Response containing score breakdown data:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `score_breakdown` {Object} - Complete score breakdown containing:
   *     - `overall` {Object} - Overall score summary with formula and methodology
   *     - `frameworks` {Array} - Per-framework breakdown with controls
   *     - `recommendations` {Object} - Improvement recommendations
   *     - `status_definitions` {Object} - Definitions for each status type
   *     - `generated_at` {number} - Timestamp when breakdown was generated
   *
   * @example
   * // Fetch score breakdown
   * const { score_breakdown } = await api.getScoreBreakdown();
   * console.log(`Overall Score: ${score_breakdown.overall.compliance_score}%`);
   * console.log(`Formula: ${score_breakdown.overall.formula}`);
   *
   * @example
   * // Show top recommendations
   * const recs = score_breakdown.recommendations.items;
   * recs.forEach(r => console.log(`${r.priority}: ${r.control_number} - ${r.action}`));
   *
   * @see {@link getDashboardOverview} for general dashboard metrics
   * @see {@link getDashboardBenchmarks} for industry benchmark comparisons
   */
  async getScoreBreakdown() {
    return this.request<{
      success: boolean
      score_breakdown: {
        overall: {
          compliance_score: number
          formula: string
          methodology: string
          total_controls: number
          applicable_controls: number
          compliant_controls: number
          non_compliant_controls: number
          in_progress_controls: number
          pending_controls: number
          not_applicable_controls: number
          overdue_controls: number
        }
        frameworks: Array<{
          id: string
          name: string
          code: string
          total_controls: number
          compliant_controls: number
          non_compliant_controls: number
          in_progress_controls: number
          pending_controls: number
          not_applicable_controls: number
          overdue_controls: number
          compliance_score: number
          controls: Array<{
            id: string
            control_number: string
            name: string
            status: string
            status_reason: string
            frequency: string
            is_overdue: boolean
            days_until_due: number | null
            owner_username: string | null
            impact_score: number
            contribution: number
          }>
        }>
        recommendations: {
          summary: string
          items: Array<{
            control_id: string
            control_number: string
            control_name: string
            framework_name: string
            current_status: string
            priority: 'critical' | 'high' | 'medium' | 'low'
            action: string
            impact: string
            days_until_due: number | null
            owner: string
          }>
        }
        status_definitions: {
          compliant: string
          non_compliant: string
          in_progress: string
          pending: string
          not_applicable: string
        }
        generated_at: number
      }
    }>('/api/dashboard/score-breakdown')
  }

  /**
   * Retrieves executive dashboard data with high-level KPIs, trends, and top risks.
   *
   * This method fetches aggregated data specifically designed for executive-level
   * reporting, board presentations, and CISO briefings. The data includes:
   * - Overall security posture score with trend direction
   * - Key performance indicators (compliance, risk, vendor, activity)
   * - Top risks requiring attention
   * - Overdue controls list
   * - Recent achievements
   * - Framework compliance summary
   * - 30-day activity trend data
   *
   * @param {string} [comparePeriod='month'] - Period for trend comparison.
   *   Options: 'week', 'month', 'quarter', 'year'
   *
   * @returns {Promise<Object>} Response containing executive dashboard data:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `executive` {Object} - Executive dashboard data containing:
   *     - `organization` {Object} - Organization name and website
   *     - `generated_at` {number} - Timestamp when report was generated
   *     - `compare_period` {string} - The comparison period used
   *     - `posture` {Object} - Overall security posture (score, trend, level)
   *     - `kpis` {Object} - Key performance indicators for compliance, risk, vendor, activity
   *     - `top_risks` {Array} - Top 5 risks requiring attention
   *     - `overdue_controls` {Array} - Top 5 overdue controls
   *     - `achievements` {Array} - Notable achievements this period
   *     - `frameworks` {Array} - Framework compliance summary (sorted by lowest compliance)
   *     - `trend` {Array} - 30-day activity trend data for sparkline charts
   *
   * @example
   * // Fetch monthly executive dashboard
   * const { executive } = await api.getExecutiveDashboard('month');
   * console.log(`Security Posture: ${executive.posture.score}/100 (${executive.posture.trend})`);
   *
   * @example
   * // Quarterly board report data
   * const { executive } = await api.getExecutiveDashboard('quarter');
   * const criticalRisks = executive.top_risks.filter(r => r.severity === 'critical');
   *
   * @see {@link getDashboardOverview} for practitioner-level dashboard data
   * @see {@link getDashboardBenchmarks} for industry benchmark comparisons
   */
  async getExecutiveDashboard(comparePeriod: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    return this.request<{
      success: boolean
      executive: {
        organization: {
          name: string
          website: string | null
        }
        generated_at: number
        compare_period: string
        posture: {
          score: number
          trend: 'improving' | 'stable' | 'declining'
          level: 'strong' | 'moderate' | 'needs_attention' | 'critical'
        }
        kpis: {
          compliance: {
            score: number
            total_controls: number
            compliant_controls: number
            trend: number
            trend_direction: 'up' | 'down' | 'stable'
          }
          risk: {
            posture_score: number
            total: number
            open: number
            critical: number
            high: number
            resolved_this_period: number
          }
          vendor: {
            health_score: number
            total: number
            assessed: number
            critical_risk: number
            high_risk: number
          }
          activity: {
            evidence_this_period: number
            evidence_trend: number
          }
        }
        top_risks: Array<{
          id: string
          title: string
          description: string
          severity: 'critical' | 'high' | 'medium' | 'low'
          risk_score: number
          category: string
          owner: string | null
          days_overdue: number | null
        }>
        overdue_controls: Array<{
          id: string
          control_number: string
          name: string
          framework: string
          days_overdue: number
          owner: string | null
        }>
        achievements: Array<{
          type: string
          title: string
          description: string
          icon: string
        }>
        frameworks: Array<{
          id: string
          name: string
          code: string
          compliance_percentage: number
          compliant_controls: number
          total_controls: number
          overdue_controls: number
          status: 'on_track' | 'needs_attention' | 'at_risk'
        }>
        trend: Array<{
          date: string
          evidence_count: number
        }>
      }
    }>(`/api/dashboard/executive?compare=${comparePeriod}`)
  }

  /**
   * Retrieves framework-specific compliance dashboard data.
   *
   * @param {string} frameworkId - The framework ID to get dashboard for
   * @returns {Promise<Object>} Response containing framework-specific dashboard data
   */
  async getFrameworkDashboard(frameworkId: string) {
    return this.request<{
      success: boolean
      dashboard: {
        framework: {
          id: string
          name: string
          display_name: string
          version: string | null
          description: string | null
          enabled: number
        }
        compliance_score: number
        controls: {
          total: number
          applicable: number
          compliant: number
          non_compliant: number
          in_progress: number
          pending: number
          not_applicable: number
          overdue: number
        }
        categories: Array<{
          name: string
          total: number
          compliant: number
          non_compliant: number
          in_progress: number
          pending: number
          not_applicable: number
          overdue: number
          compliance_percentage: number
        }>
        evidence: {
          total: number
          controls_with_evidence: number
          coverage_percentage: number
          last_30_days: number
          last_7_days: number
        }
        documents: {
          controls_with_policies: number
          total_policies: number
          approved_policies: number
        }
        overdue_controls: Array<{
          id: string
          control_number: string
          name: string
          days_overdue: number
          next_due_date: number
        }>
        upcoming_due: Array<{
          id: string
          control_number: string
          name: string
          status: string
          days_until_due: number
          next_due_date: number
        }>
      }
    }>(`/api/dashboard/framework/${frameworkId}`)
  }

  /**
   * Retrieves historical compliance trends for a specific framework.
   *
   * @param {string} frameworkId - The framework ID to get trends for
   * @param {number} [days=90] - Number of days of history (max 365)
   * @returns {Promise<Object>} Response containing trend data
   */
  async getFrameworkTrends(frameworkId: string, days = 90) {
    return this.request<{
      success: boolean
      trends: {
        framework_id: string
        framework_name: string
        period_days: number
        start_date: number
        end_date: number
        evidence_collection: Array<{ date: string; evidence_count: number }>
        status_changes: Array<{ date: string; became_compliant: number; became_non_compliant: number }>
        compliance_timeline: Array<{ date: string; compliance_percentage: number; compliant: number }>
        current_stats: {
          total_controls: number
          applicable_controls: number
          compliant_controls: number
          compliance_percentage: number
        }
      }
    }>(`/api/dashboard/framework/${frameworkId}/trends?days=${days}`)
  }

  /**
   * Retrieves gap analysis for a specific framework.
   *
   * @param {string} frameworkId - The framework ID to get gap analysis for
   * @returns {Promise<Object>} Response containing gap analysis data
   */
  async getFrameworkGaps(frameworkId: string) {
    return this.request<{
      success: boolean
      gaps: {
        framework_id: string
        framework_name: string
        summary: {
          total_controls: number
          applicable_controls: number
          compliant_controls: number
          gap_count: number
          gap_percentage: number
          not_started_count: number
          in_progress_count: number
          non_compliant_count: number
          unassigned_count: number
          missing_policies_count: number
        }
        not_started: Array<{
          id: string
          control_number: string
          name: string
          category: string
          frequency: string
          owner: string
          evidence_type: string
        }>
        in_progress: Array<{
          id: string
          control_number: string
          name: string
          category: string
          frequency: string
          owner: string
          days_until_due: number | null
          next_due_date: number | null
          last_evidence_date: number | null
        }>
        non_compliant: Array<{
          id: string
          control_number: string
          name: string
          category: string
          frequency: string
          owner: string
          is_overdue: boolean
          days_overdue: number | null
          status_reason: string | null
          last_evidence_date: number | null
        }>
        unassigned: Array<{
          id: string
          control_number: string
          name: string
          category: string
          status: string
        }>
        missing_policies: Array<{
          id: string
          control_number: string
          name: string
          category: string
          status: string
          evidence_type: string
        }>
      }
    }>(`/api/dashboard/framework/${frameworkId}/gaps`)
  }

  /**
   * Retrieves recent activity for a specific framework.
   *
   * This method fetches recent evidence uploads and control status changes
   * for a framework, combined and sorted by timestamp.
   *
   * @param {string} frameworkId - The framework ID to get activity for
   * @param {number} [limit=20] - Maximum number of activity items to return (max 50)
   * @returns {Promise<Object>} Response containing activity data
   */
  async getFrameworkActivity(frameworkId: string, limit = 20) {
    return this.request<{
      success: boolean
      activity: Array<{
        type: 'evidence_uploaded' | 'control_updated'
        id: string
        title: string
        control_number?: string
        control_name?: string
        username?: string
        status?: string
        timestamp: number
      }>
      framework_id: string
      framework_name: string
    }>(`/api/dashboard/framework/${frameworkId}/activity?limit=${limit}`)
  }

  /**
   * Compares multiple frameworks side by side.
   *
   * This method retrieves comprehensive metrics for multiple frameworks
   * including compliance scores, control counts, evidence coverage,
   * policy coverage, and risk exposure.
   *
   * @param {string[]} frameworkIds - Array of framework IDs to compare (2-5 required)
   * @returns {Promise<Object>} Response containing comparison data with insights
   */
  async getFrameworkComparison(frameworkIds: string[]) {
    const ids = frameworkIds.join(',')
    return this.request<{
      success: boolean
      comparison: {
        frameworks: Array<{
          framework: {
            id: string
            name: string
            display_name: string
            version: string | null
          }
          metrics: {
            compliance_score: number
            total_controls: number
            applicable_controls: number
            compliant_controls: number
            non_compliant_controls: number
            in_progress_controls: number
            pending_controls: number
            overdue_controls: number
          }
          evidence: {
            total: number
            coverage_percentage: number
            last_30_days: number
          }
          documents: {
            total_policies: number
            approved_policies: number
            coverage_percentage: number
          }
          risk_exposure: {
            linked_risks: number
            open_risks: number
            high_critical_risks: number
          }
        }>
        insights: {
          average_compliance: number
          average_evidence_coverage: number
          best_performing: {
            framework_id: string
            framework_name: string
            score: number
          }
          worst_performing: {
            framework_id: string
            framework_name: string
            score: number
          }
          total_overdue_controls: number
          total_open_risks: number
        }
      }
    }>(`/api/dashboard/frameworks/compare?ids=${ids}`)
  }

  /**
   * Retrieves list of all enabled frameworks with summary stats.
   *
   * This method is optimized for populating framework selector dropdowns,
   * returning just the essential information and compliance scores.
   *
   * @returns {Promise<Object>} Response containing framework list with stats
   */
  async getFrameworkList() {
    return this.request<{
      success: boolean
      frameworks: Array<{
        id: string
        name: string
        display_name: string
        version: string | null
        description: string | null
        total_controls: number
        compliance_score: number
        overdue_controls: number
      }>
    }>('/api/dashboard/frameworks/list')
  }

  // Controls endpoints

  /**
   * Retrieves a paginated list of controls with optional filtering.
   *
   * Controls are the core compliance artifacts in the GRC platform, representing
   * specific security or compliance requirements from frameworks like SOC 2, ISO 27001,
   * or GDPR. This method supports flexible filtering by framework, status, owner,
   * and text search to help users manage large control libraries efficiently.
   *
   * @param {Object} params - Optional filter and pagination parameters.
   * @param {string} [params.framework_id] - Filter controls by framework ID (e.g., SOC 2, ISO 27001).
   *   Only controls belonging to the specified framework will be returned.
   * @param {string} [params.status] - Filter by control implementation status.
   *   Common values: 'implemented', 'partial', 'not_implemented', 'not_applicable'.
   * @param {string} [params.owner_id] - Filter by control owner's user ID.
   *   Useful for showing a user's assigned controls.
   * @param {number} [params.limit=50] - Maximum number of controls to return per page.
   * @param {number} [params.offset=0] - Number of controls to skip for pagination.
   * @param {string} [params.search] - Full-text search query matching control number,
   *   title, description, or guidance text.
   * @param {boolean} [params.include_inactive=false] - Include inactive/disabled controls
   *   in results. By default, only active controls are returned.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `controls` {Array<Object>} - Array of control objects, each containing:
   *     - `id` {string} - Unique control identifier
   *     - `control_number` {string} - Framework-specific control ID (e.g., "CC6.1", "A.9.1.1")
   *     - `title` {string} - Control title/name
   *     - `description` {string} - Full control description
   *     - `status` {string} - Implementation status
   *     - `owner_id` {string|null} - Assigned owner's user ID
   *     - `framework_id` {string} - Parent framework ID
   *     - `evidence_count` {number} - Number of linked evidence items
   *     - `created_at` {number} - Unix timestamp of creation
   *     - `updated_at` {number} - Unix timestamp of last update
   *   - `pagination` {Object} - Pagination metadata:
   *     - `total` {number} - Total number of matching controls
   *     - `limit` {number} - Current page size limit
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether more results exist beyond current page
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view controls for the current tenant.
   *
   * @example
   * // Fetch first page of SOC 2 controls
   * const { controls, pagination } = await api.getControls({
   *   framework_id: 'soc2-framework-id',
   *   limit: 25
   * });
   *
   * @example
   * // Search for access-related controls
   * const { controls } = await api.getControls({
   *   search: 'access control',
   *   status: 'implemented'
   * });
   *
   * @example
   * // Paginate through all controls
   * let offset = 0;
   * const limit = 50;
   * let allControls = [];
   *
   * while (true) {
   *   const { controls, pagination } = await api.getControls({ limit, offset });
   *   allControls = [...allControls, ...controls];
   *   if (!pagination.hasMore) break;
   *   offset += limit;
   * }
   *
   * @see {@link getControl} for fetching a single control by ID
   * @see {@link createControl} for creating new controls
   * @see {@link getControlStats} for aggregate control statistics
   * @see {@link getFrameworks} for listing available frameworks
   */
  async getControls(params: {
    framework_id?: string
    status?: string
    owner_id?: string
    tag?: string
    limit?: number
    offset?: number
    search?: string
    include_inactive?: boolean
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      controls: any[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/controls?${searchParams}`)
  }

  /**
   * Retrieves all unique tags used across controls.
   *
   * This method returns a deduplicated, sorted list of all tags currently in use
   * across all controls in the tenant. Useful for populating filter dropdowns
   * and tag suggestion lists.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `tags` {string[]} - Array of unique tag strings, sorted alphabetically
   *   - `count` {number} - Total number of unique tags
   *
   * @example
   * // Populate tag filter dropdown
   * const { tags } = await api.getControlTags();
   * setAvailableTags(tags);
   */
  async getControlTags() {
    return this.request<{
      success: boolean
      tags: string[]
      count: number
    }>('/api/controls/tags')
  }

  /**
   * Retrieves a single control by its unique identifier.
   *
   * Fetches the complete control record including all metadata, linked policies,
   * evidence statistics, and implementation details. This method is typically used
   * when navigating to a control detail page or when preparing to edit a control.
   *
   * @param {string} id - The unique identifier of the control to retrieve.
   *   This is the UUID assigned when the control was created.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `control` {Object} - The complete control object containing:
   *     - `id` {string} - Unique control identifier
   *     - `control_number` {string} - Framework-specific control ID (e.g., "CC6.1")
   *     - `title` {string} - Control title/name
   *     - `description` {string} - Full control description
   *     - `guidance` {string} - Implementation guidance notes
   *     - `status` {string} - Implementation status ('implemented', 'partial', etc.)
   *     - `owner_id` {string|null} - Assigned owner's user ID
   *     - `owner` {Object|null} - Owner user object with name and email
   *     - `framework_id` {string} - Parent framework ID
   *     - `framework` {Object} - Parent framework details
   *     - `evidence_count` {number} - Number of linked evidence items
   *     - `policy_count` {number} - Number of linked policy documents
   *     - `test_frequency` {string} - How often control should be tested
   *     - `last_tested_at` {number|null} - Unix timestamp of last test
   *     - `next_test_due` {number|null} - Unix timestamp of next scheduled test
   *     - `created_at` {number} - Unix timestamp of creation
   *     - `updated_at` {number} - Unix timestamp of last update
   *
   * @throws {Error} Throws if the control is not found (404) or the user lacks
   *   permission to view it.
   *
   * @example
   * // Fetch control details for display
   * async function loadControlDetails(controlId: string) {
   *   try {
   *     const { control } = await api.getControl(controlId);
   *     setControlData(control);
   *     setPageTitle(`${control.control_number}: ${control.title}`);
   *   } catch (error) {
   *     if (error.message.includes('404')) {
   *       navigate('/controls'); // Control not found
   *     }
   *     throw error;
   *   }
   * }
   *
   * @example
   * // Prepare control for editing
   * const { control } = await api.getControl(id);
   * setFormValues({
   *   title: control.title,
   *   description: control.description,
   *   status: control.status,
   *   owner_id: control.owner_id
   * });
   *
   * @see {@link getControls} for listing multiple controls
   * @see {@link updateControl} for modifying a control
   * @see {@link getControlPolicies} for fetching linked policies
   */
  async getControl(id: string) {
    return this.request<{
      success: boolean
      control: any
    }>(`/api/controls/${id}`)
  }

  /**
   * Retrieves shared service inheritance information for a control.
   *
   * This method fetches information about how a control is inherited across
   * shared services or frameworks. Used for Issue #154 to display inheritance
   * relationships in the control detail view.
   *
   * @param {string} id - The ID of the control to get inheritance info for
   * @returns {Promise<Object>} Response containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `inheritance` {Array} - Array of inheritance relationships
   */
  async getControlInheritance(id: string) {
    return this.request<{
      success: boolean
      inheritance: any[]
    }>(`/api/controls/${id}/inheritance`)
  }

  /**
   * Retrieves a list of shared services that can be inherited by controls.
   *
   * @param {Object} [params] - Optional filter parameters
   * @param {string} [params.status] - Filter by service status (e.g., 'active')
   * @returns {Promise<Object>} Response containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `shared_services` {Array} - Array of available shared services
   */
  async getSharedServices(params?: { status?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.append('status', params.status)
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      shared_services: any[]
    }>(`/api/shared-services${query ? `?${query}` : ''}`)
  }

  /**
   * Adds an inheritance relationship from a shared service to a control.
   *
   * @param {string} controlId - The ID of the control
   * @param {Object} data - The inheritance data
   * @param {string} data.shared_service_id - The ID of the shared service to inherit from
   * @param {string} data.inheritance_type - Type: 'full', 'partial', or 'complementary'
   * @param {string} [data.user_responsibility] - User's responsibility description
   * @param {string} [data.notes] - Additional notes
   * @returns {Promise<Object>} Response containing success status
   */
  async addControlInheritance(controlId: string, data: {
    shared_service_id: string
    inheritance_type: string
    user_responsibility?: string
    notes?: string
  }) {
    return this.request<{
      success: boolean
      message?: string
    }>(`/api/controls/${controlId}/inheritance`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Removes an inheritance relationship from a control.
   *
   * @param {string} controlId - The ID of the control
   * @param {string} sharedServiceId - The ID of the shared service to remove
   * @returns {Promise<Object>} Response containing success status
   */
  async removeControlInheritance(controlId: string, sharedServiceId: string) {
    return this.request<{
      success: boolean
      message?: string
    }>(`/api/controls/${controlId}/inheritance/${sharedServiceId}`, {
      method: 'DELETE',
    })
  }

  // Control Dependencies (Issue #142)
  async getControlDependencies(controlId: string) {
    return this.request<{
      success: boolean
      control: { id: string; control_number: string; name: string }
      dependencies: Array<{ id: string; source_control_id: string; target_control_id: string; relationship_type: 'blocks' | 'supports'; notes?: string; target_control_number: string; target_control_name: string; target_framework_name: string; target_status: string; created_at: number; created_by_username?: string }>
      blocking_status: { canBeCompliant: boolean; blockingDependencies: Array<{ id: string; control_number: string; name: string; status: string }> }
    }>(`/api/controls/${controlId}/dependencies`)
  }

  async getControlDependents(controlId: string) {
    return this.request<{
      success: boolean
      control: { id: string; control_number: string; name: string; status: string }
      dependents: Array<{ id: string; source_control_id: string; target_control_id: string; relationship_type: 'blocks' | 'supports'; notes?: string; source_control_number: string; source_control_name: string; source_framework_name: string; source_status: string; created_at: number }>
      counts: { total: number; blocking: number; supporting: number }
    }>(`/api/controls/${controlId}/dependents`)
  }

  async getControlImpactAnalysis(controlId: string) {
    return this.request<{
      success: boolean
      control: { id: string; control_number: string; name: string; status: string; framework_name: string }
      upstream: { dependencies: Array<{ id: string; control_number: string; name: string; framework_name: string; status: string; relationship_type: 'blocks' | 'supports'; depth: number }>; blocking_count: number; non_compliant_blocking: Array<any>; can_be_compliant: boolean }
      downstream: { dependents: Array<{ id: string; control_number: string; name: string; framework_name: string; status: string; relationship_type: 'blocks' | 'supports'; depth: number }>; blocking_count: number; total_impacted: number }
      summary: { total_upstream: number; total_downstream: number; max_upstream_depth: number; max_downstream_depth: number; is_blocked: boolean; blocks_others: boolean }
    }>(`/api/controls/${controlId}/impact-analysis`)
  }

  async addControlDependency(controlId: string, data: { target_control_id: string; relationship_type: 'blocks' | 'supports'; notes?: string }) {
    return this.request<{ success: boolean; dependency: { id: string; source_control_id: string; target_control_id: string; relationship_type: 'blocks' | 'supports'; notes?: string; target_control_number: string; target_control_name: string; created_at: number } }>(`/api/controls/${controlId}/dependencies`, { method: 'POST', body: JSON.stringify(data) })
  }

  async updateControlDependency(controlId: string, dependencyId: string, data: { relationship_type?: 'blocks' | 'supports'; notes?: string }) {
    return this.request<{ success: boolean; message?: string }>(`/api/controls/${controlId}/dependencies/${dependencyId}`, { method: 'PATCH', body: JSON.stringify(data) })
  }

  async removeControlDependency(controlId: string, dependencyId: string) {
    return this.request<{ success: boolean; message?: string }>(`/api/controls/${controlId}/dependencies/${dependencyId}`, { method: 'DELETE' })
  }

  // ============================================================================
  // CONTROL HISTORY (Issue #94)
  // ============================================================================

  /**
   * Retrieves the change history for a control.
   *
   * This method fetches the complete audit trail of all changes made to a control,
   * including creation, updates, status changes, and deletion. Each history entry
   * includes who made the change, when, what fields were modified, and the before/after
   * values for changed fields.
   *
   * **Use cases:**
   * - Compliance auditing: Demonstrate control changes over time
   * - Troubleshooting: Identify when and why a control changed status
   * - Accountability: Track who made specific changes
   *
   * @param {string} controlId - The ID of the control to get history for
   * @param {Object} [options] - Optional query parameters
   * @param {number} [options.limit=50] - Maximum number of history entries to return (max 200)
   * @param {number} [options.offset=0] - Number of entries to skip for pagination
   * @param {string} [options.change_type] - Filter by change type: 'created', 'updated', 'status_change', 'deleted'
   *
   * @returns {Promise<ControlHistoryResponse>} Response containing history entries and stats
   */
  async getControlHistory(
    controlId: string,
    options?: { limit?: number; offset?: number; change_type?: 'created' | 'updated' | 'status_change' | 'deleted' }
  ): Promise<ControlHistoryResponse> {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))
    if (options?.change_type) params.set('change_type', options.change_type)

    const query = params.toString()
    return this.request<ControlHistoryResponse>(`/api/controls/${controlId}/history${query ? `?${query}` : ''}`)
  }

  /**
   * Creates a new control within a compliance framework.
   *
   * This method allows administrators to add custom controls to existing frameworks
   * or create controls for custom frameworks. The control will be assigned a unique
   * identifier and timestamps automatically. After creation, evidence and policies
   * can be linked to the control using separate API methods.
   *
   * **Required permissions:** User must have 'admin' or 'editor' role for the tenant.
   *
   * @param {Object} data - The control data to create.
   * @param {string} data.framework_id - ID of the parent framework this control belongs to.
   *   Required - every control must be associated with a framework.
   * @param {string} data.control_number - Framework-specific control identifier
   *   (e.g., "CC6.1", "A.9.1.1", "CUSTOM-001"). Should be unique within the framework.
   * @param {string} data.title - Short, descriptive title for the control.
   * @param {string} [data.description] - Detailed description of the control requirement
   *   and what it aims to achieve.
   * @param {string} [data.guidance] - Implementation guidance and notes for control owners.
   * @param {string} [data.status='not_implemented'] - Initial implementation status.
   *   Values: 'implemented', 'partial', 'not_implemented', 'not_applicable'.
   * @param {string} [data.owner_id] - User ID of the person responsible for this control.
   * @param {string} [data.test_frequency] - How often the control should be tested.
   *   Values: 'daily', 'weekly', 'monthly', 'quarterly', 'annually'.
   * @param {string} [data.evidence_type] - Type of evidence expected for this control.
   *   Values: 'evidence', 'policy', 'both'.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the control was created successfully
   *   - `control` {Object} - The newly created control object with all fields
   *     including generated `id`, `created_at`, and `updated_at` timestamps.
   *
   * @throws {Error} Throws if:
   *   - User lacks permission to create controls
   *   - Required fields (framework_id, control_number, title) are missing
   *   - Control number already exists within the framework
   *   - Framework ID is invalid
   *
   * @example
   * // Create a custom control for SOC 2
   * const { control } = await api.createControl({
   *   framework_id: 'soc2-framework-id',
   *   control_number: 'CC6.8-CUSTOM',
   *   title: 'Custom Access Review Process',
   *   description: 'Quarterly review of user access privileges',
   *   status: 'not_implemented',
   *   owner_id: currentUser.id,
   *   test_frequency: 'quarterly'
   * });
   *
   * console.log(`Created control: ${control.id}`);
   *
   * @example
   * // Create control with form data
   * async function handleSubmit(formData: ControlFormData) {
   *   try {
   *     const { control } = await api.createControl({
   *       framework_id: formData.frameworkId,
   *       control_number: formData.controlNumber,
   *       title: formData.title,
   *       description: formData.description,
   *       guidance: formData.guidance,
   *       owner_id: formData.ownerId
   *     });
   *     toast.success('Control created successfully');
   *     navigate(`/controls/${control.id}`);
   *   } catch (error) {
   *     toast.error(`Failed to create control: ${error.message}`);
   *   }
   * }
   *
   * @see {@link getControl} for retrieving the created control
   * @see {@link updateControl} for modifying control after creation
   * @see {@link linkPolicyToControl} for attaching policies to the control
   */
  async createControl(data: any) {
    return this.request<{
      success: boolean
      control: any
    }>('/api/controls', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing control with partial data.
   *
   * This method uses PATCH semantics, meaning only the fields provided in the
   * data object will be updated. Omitted fields will retain their current values.
   * This is ideal for inline editing, status updates, or reassigning ownership
   * without needing to send the complete control object.
   *
   * **Required permissions:** User must have 'admin' or 'editor' role, or be the
   * control owner to update implementation status.
   *
   * @param {string} id - The unique identifier of the control to update.
   * @param {Object} data - Partial control data to update. Only include fields that should change.
   * @param {string} [data.control_number] - Updated control identifier (must remain unique within framework).
   * @param {string} [data.title] - Updated control title.
   * @param {string} [data.description] - Updated control description.
   * @param {string} [data.guidance] - Updated implementation guidance.
   * @param {string} [data.status] - Updated implementation status.
   *   Values: 'implemented', 'partial', 'not_implemented', 'not_applicable'.
   * @param {string} [data.owner_id] - Updated owner user ID. Set to null to unassign.
   * @param {string} [data.test_frequency] - Updated test frequency.
   *   Values: 'daily', 'weekly', 'monthly', 'quarterly', 'annually'.
   * @param {string} [data.evidence_type] - Updated evidence type requirement.
   *   Values: 'evidence', 'policy', 'both'.
   * @param {boolean} [data.is_active] - Whether the control is active.
   *   Setting to false effectively archives the control.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the update was applied successfully.
   *   Note: The updated control object is not returned. Use {@link getControl}
   *   to fetch the updated record if needed.
   *
   * @throws {Error} Throws if:
   *   - Control is not found (404)
   *   - User lacks permission to update the control
   *   - Validation fails (e.g., duplicate control_number)
   *
   * @example
   * // Update control status
   * await api.updateControl(controlId, {
   *   status: 'implemented'
   * });
   *
   * @example
   * // Reassign control ownership
   * await api.updateControl(controlId, {
   *   owner_id: newOwnerId
   * });
   *
   * @example
   * // Update multiple fields from edit form
   * async function handleSave(controlId: string, formData: Partial<Control>) {
   *   try {
   *     await api.updateControl(controlId, {
   *       title: formData.title,
   *       description: formData.description,
   *       guidance: formData.guidance,
   *       status: formData.status,
   *       owner_id: formData.ownerId
   *     });
   *     toast.success('Control updated successfully');
   *     // Refresh control data
   *     const { control } = await api.getControl(controlId);
   *     setControlData(control);
   *   } catch (error) {
   *     toast.error(`Failed to update control: ${error.message}`);
   *   }
   * }
   *
   * @see {@link getControl} for fetching the updated control
   * @see {@link createControl} for creating new controls
   * @see {@link deleteControl} for removing controls
   */
  async updateControl(id: string, data: any) {
    return this.request<{
      success: boolean
    }>(`/api/controls/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a control from the system.
   *
   * This action is irreversible and will also remove:
   * - All evidence-to-control links (evidence items themselves are preserved)
   * - All policy-to-control links (policy documents themselves are preserved)
   * - Control mapping associations with other frameworks
   * - Audit log entries will reference the deleted control ID for historical purposes
   *
   * **Recommendation:** Consider using {@link updateControl} with `is_active: false`
   * to archive controls instead of deleting, which preserves historical data and
   * allows for recovery.
   *
   * **Required permissions:** User must have 'admin' role for the tenant.
   *
   * @param {string} id - The unique identifier of the control to delete.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the control was deleted successfully.
   *
   * @throws {Error} Throws if:
   *   - Control is not found (404)
   *   - User lacks admin permission to delete controls
   *   - Control is part of an active compliance framework that cannot be modified
   *
   * @example
   * // Delete with confirmation dialog
   * async function handleDelete(controlId: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete Control?',
   *     message: 'This action cannot be undone. All linked evidence and policies will be unlinked.',
   *     confirmLabel: 'Delete',
   *     destructive: true
   *   });
   *
   *   if (!confirmed) return;
   *
   *   try {
   *     await api.deleteControl(controlId);
   *     toast.success('Control deleted');
   *     navigate('/controls');
   *   } catch (error) {
   *     toast.error(`Failed to delete control: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Bulk delete multiple controls
   * async function deleteSelectedControls(controlIds: string[]) {
   *   const results = await Promise.allSettled(
   *     controlIds.map(id => api.deleteControl(id))
   *   );
   *
   *   const failed = results.filter(r => r.status === 'rejected');
   *   if (failed.length > 0) {
   *     toast.warning(`${failed.length} controls failed to delete`);
   *   }
   *   toast.success(`${controlIds.length - failed.length} controls deleted`);
   * }
   *
   * @see {@link updateControl} for archiving controls (preferred over deletion)
   * @see {@link getControl} for retrieving control before deletion
   */
  async deleteControl(id: string) {
    return this.request<{
      success: boolean
    }>(`/api/controls/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Bulk update multiple controls at once (#48)
   * Supports: assign owner, change frequency, mark as N/A, change status
   */
  async bulkUpdateControls(
    controlIds: string[],
    updates: {
      owner_id?: string | null
      frequency?: string
      is_not_applicable?: boolean
      status?: string
      effectiveness_rating?: number | null
      tags?: string[]
    }
  ) {
    return this.request<{
      success: boolean
      message: string
      updated_count: number
      requested_count: number
    }>('/api/controls/bulk', {
      method: 'PATCH',
      body: JSON.stringify({ control_ids: controlIds, updates }),
    })
  }

  /**
   * Bulk delete multiple controls (admin only) (#48)
   */
  async bulkDeleteControls(controlIds: string[]) {
    return this.request<{
      success: boolean
      message: string
      deleted_count: number
      requested_count: number
      controls_with_evidence?: string[]
    }>('/api/controls/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ control_ids: controlIds }),
    })
  }

  // ============================================
  // Control Exceptions/Waivers (Issue #72)
  // ============================================

  /**
   * Get exceptions for a specific control
   */
  async getControlExceptions(
    controlId: string,
    options?: { status?: string }
  ) {
    const params = new URLSearchParams()
    if (options?.status) params.set('status', options.status)
    const query = params.toString() ? `?${params.toString()}` : ''
    return this.request<{
      success: boolean
      exceptions: ControlException[]
    }>(`/api/controls/${controlId}/exceptions${query}`)
  }

  /**
   * Request a new exception/waiver for a control
   */
  async requestControlException(
    controlId: string,
    data: {
      exception_type: 'waiver' | 'exception' | 'compensating_control'
      reason: string
      risk_acceptance?: string
      compensating_control_description?: string
      valid_from: number
      valid_until?: number | null
      review_frequency?: 'monthly' | 'quarterly' | 'semi_annually' | 'annually' | null
    }
  ) {
    return this.request<{
      success: boolean
      message: string
      exception: {
        id: string
        status: string
        exception_type: string
      }
    }>(`/api/controls/${controlId}/exceptions`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an exception (approve, reject, revoke, review)
   */
  async updateControlException(
    controlId: string,
    exceptionId: string,
    data: {
      action: 'approve' | 'reject' | 'revoke' | 'review'
      comments?: string
      rejection_reason?: string
    }
  ) {
    return this.request<{
      success: boolean
      message: string
      status: string
    }>(`/api/controls/${controlId}/exceptions/${exceptionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get exception history/audit trail
   */
  async getExceptionHistory(controlId: string, exceptionId: string) {
    return this.request<{
      success: boolean
      history: ExceptionHistoryEntry[]
    }>(`/api/controls/${controlId}/exceptions/${exceptionId}/history`)
  }

  /**
   * Delete a pending exception
   */
  async deleteControlException(controlId: string, exceptionId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/controls/${controlId}/exceptions/${exceptionId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get all pending exceptions (admin only)
   */
  async getPendingExceptions() {
    return this.request<{
      success: boolean
      exceptions: ControlException[]
    }>('/api/controls/exceptions/pending')
  }

  // Control-Policy linking endpoints

  /**
   * Retrieves all policy documents linked to a specific control.
   *
   * Controls can be supported by policy documents that outline the procedures,
   * standards, and guidelines that help satisfy the control requirements. This
   * method returns all policies currently linked to a control, along with their
   * status and review dates. The relationship between controls and policies is
   * many-to-many: a control can have multiple policies, and a policy can support
   * multiple controls.
   *
   * @param {string} controlId - The unique identifier of the control whose
   *   linked policies should be retrieved.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `control_id` {string} - The control ID (echoed back for reference)
   *   - `evidence_type` {'evidence' | 'policy' | 'both'} - What type of evidence
   *     this control accepts
   *   - `policies` {Array<Object>} - Array of linked policy documents:
   *     - `id` {string} - Unique policy document identifier
   *     - `title` {string} - Policy document title
   *     - `status` {string} - Document status ('published', 'draft', 'review')
   *     - `last_review_date` {number} - Unix timestamp of last review (optional)
   *     - `next_review_date` {number} - Unix timestamp of next scheduled review (optional)
   *     - `doc_type` {string} - Document type classification (optional)
   *     - `linked_at` {number} - Unix timestamp when policy was linked to control
   *
   * @throws {Error} Throws if the control is not found (404) or the user lacks
   *   permission to view the control's policies.
   *
   * @example
   * // Display policies linked to a control
   * async function loadControlPolicies(controlId: string) {
   *   const { policies, evidence_type } = await api.getControlPolicies(controlId);
   *
   *   if (policies.length === 0) {
   *     showMessage('No policies linked to this control');
   *   } else {
   *     setPolicies(policies);
   *     // Check if any policies need review
   *     const overdueReviews = policies.filter(
   *       p => p.next_review_date && p.next_review_date < Date.now()
   *     );
   *     if (overdueReviews.length > 0) {
   *       showWarning(`${overdueReviews.length} policies need review`);
   *     }
   *   }
   * }
   *
   * @see {@link linkPolicyToControl} for linking a new policy to a control
   * @see {@link unlinkPolicyFromControl} for removing a policy link
   * @see {@link getControl} for fetching the control details
   * @see {@link getDocuments} for listing available policy documents
   */
  async getControlPolicies(controlId: string) {
    return this.request<{
      success: boolean
      control_id: string
      evidence_type: 'evidence' | 'policy' | 'both'
      policies: Array<{
        id: string
        title: string
        status: string
        last_review_date?: number
        next_review_date?: number
        doc_type?: string
        linked_at: number
      }>
    }>(`/api/controls/${controlId}/policies`)
  }

  /**
   * Links a policy document to a control as supporting documentation.
   *
   * Creates a relationship between a control and a policy document, indicating
   * that the policy supports or implements aspects of the control requirement.
   * This is a key operation for demonstrating compliance, as auditors often
   * require evidence that policies exist to address specific control objectives.
   *
   * The linking is idempotent - if the policy is already linked to the control,
   * the operation succeeds without creating a duplicate link.
   *
   * **Required permissions:** User must have 'admin' or 'editor' role for the tenant.
   *
   * @param {string} controlId - The unique identifier of the control to link the policy to.
   * @param {string} documentId - The unique identifier of the policy document to link.
   *   The document must be of a policy type (e.g., 'policy', 'standard', 'procedure').
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the linking operation succeeded
   *   - `message` {string} - Human-readable confirmation message
   *
   * @throws {Error} Throws if:
   *   - The control is not found (404)
   *   - The document is not found (404)
   *   - The user lacks permission to modify control links
   *   - The document type is not a valid policy type
   *
   * @example
   * // Link a policy to a control from a policy selector
   * async function handlePolicySelect(controlId: string, selectedPolicyId: string) {
   *   try {
   *     await api.linkPolicyToControl(controlId, selectedPolicyId);
   *     toast.success('Policy linked successfully');
   *     // Refresh the policies list
   *     const { policies } = await api.getControlPolicies(controlId);
   *     setLinkedPolicies(policies);
   *   } catch (error) {
   *     toast.error('Failed to link policy: ' + error.message);
   *   }
   * }
   *
   * @example
   * // Bulk link multiple policies to a control
   * async function linkMultiplePolicies(controlId: string, policyIds: string[]) {
   *   const results = await Promise.allSettled(
   *     policyIds.map(id => api.linkPolicyToControl(controlId, id))
   *   );
   *   const failed = results.filter(r => r.status === 'rejected');
   *   if (failed.length > 0) {
   *     toast.warning(`${failed.length} policies failed to link`);
   *   }
   * }
   *
   * @see {@link getControlPolicies} for listing linked policies
   * @see {@link unlinkPolicyFromControl} for removing a policy link
   * @see {@link getDocuments} for listing available policy documents
   */
  async linkPolicyToControl(controlId: string, documentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/controls/${controlId}/policies`, {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId }),
    })
  }

  /**
   * Removes a policy document link from a control.
   *
   * Deletes the relationship between a control and a policy document. This does
   * not delete either the control or the policy document itself - it only removes
   * the association between them. Use this when a policy no longer applies to a
   * control or when reorganizing policy mappings.
   *
   * **Required permissions:** User must have 'admin' or 'editor' role for the tenant.
   *
   * @param {string} controlId - The unique identifier of the control to unlink from.
   * @param {string} documentId - The unique identifier of the policy document to unlink.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the unlinking operation succeeded
   *   - `message` {string} - Human-readable confirmation message
   *
   * @throws {Error} Throws if:
   *   - The control is not found (404)
   *   - The document is not found (404)
   *   - The link between control and document doesn't exist
   *   - The user lacks permission to modify control links
   *
   * @example
   * // Remove a policy link with confirmation
   * async function handleRemovePolicy(controlId: string, policyId: string) {
   *   const confirmed = await confirm(
   *     'Are you sure you want to unlink this policy from the control?'
   *   );
   *   if (!confirmed) return;
   *
   *   try {
   *     await api.unlinkPolicyFromControl(controlId, policyId);
   *     toast.success('Policy unlinked successfully');
   *     // Refresh the policies list
   *     const { policies } = await api.getControlPolicies(controlId);
   *     setLinkedPolicies(policies);
   *   } catch (error) {
   *     toast.error('Failed to unlink policy: ' + error.message);
   *   }
   * }
   *
   * @see {@link getControlPolicies} for listing linked policies
   * @see {@link linkPolicyToControl} for adding a policy link
   */
  async unlinkPolicyFromControl(controlId: string, documentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/controls/${controlId}/policies/${documentId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves aggregate statistics about controls across all frameworks.
   *
   * Returns summary statistics about control implementation status, compliance
   * rates, and other metrics. This data is typically used for executive dashboards,
   * compliance scorecards, and trend analysis. The statistics are calculated
   * across all enabled frameworks for the current tenant.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `stats` {Object} - Aggregate control statistics including:
   *     - Total control counts by status (implemented, partial, not_implemented)
   *     - Compliance percentages by framework
   *     - Controls requiring attention (overdue, missing evidence)
   *     - Trend data (improvement/regression over time)
   *     - Owner assignment coverage
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view control statistics for the current tenant.
   *
   * @example
   * // Display control statistics on dashboard
   * async function loadControlStats() {
   *   const { stats } = await api.getControlStats();
   *
   *   setComplianceScore(stats.overallCompliancePercent);
   *   setImplementedCount(stats.implemented);
   *   setPartialCount(stats.partial);
   *   setNotImplementedCount(stats.not_implemented);
   *
   *   // Show warning if compliance is low
   *   if (stats.overallCompliancePercent < 70) {
   *     showWarning('Compliance score below target');
   *   }
   * }
   *
   * @example
   * // Create a compliance trend chart
   * async function loadComplianceTrend() {
   *   const { stats } = await api.getControlStats();
   *   const chartData = stats.trendData.map(point => ({
   *     date: formatDate(point.date),
   *     compliance: point.compliancePercent
   *   }));
   *   setChartData(chartData);
   * }
   *
   * @see {@link getControls} for listing individual controls
   * @see {@link getDashboardOverview} for high-level dashboard data
   */
  async getControlStats() {
    return this.request<{
      success: boolean
      stats: any
    }>('/api/controls/stats/summary')
  }

  // ============================================================================
  // ISSUE #56: CONTROL TESTING SCHEDULES
  // ============================================================================

  /**
   * Get test history for a control
   */
  async getControlTests(controlId: string, params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      control: {
        id: string
        control_number: string
        name: string
        testing_frequency: string | null
        test_procedure: string | null
        test_owner_id: string | null
        last_test_date: number | null
        next_test_date: number | null
      }
      tests: Array<{
        id: string
        control_id: string
        test_date: number
        result: 'pass' | 'fail' | 'partial' | 'not_applicable'
        tester_id: string
        tester_name?: string
        notes: string | null
        findings: string | null
        evidence_ids: string | null
        remediation_required: number
        remediation_due_date: number | null
        remediation_completed_at: number | null
        remediation_notes: string | null
        created_at: number
        updated_at: number
      }>
      total: number
      limit: number
      offset: number
    }>(`/api/controls/${controlId}/tests${query ? `?${query}` : ''}`)
  }

  /**
   * Record a test result for a control
   */
  async createControlTest(controlId: string, data: {
    result: 'pass' | 'fail' | 'partial' | 'not_applicable'
    test_date?: number
    notes?: string
    findings?: string[]
    evidence_ids?: string[]
    remediation_required?: boolean
    remediation_due_date?: number
    remediation_notes?: string
  }) {
    return this.request<{
      success: boolean
      test: {
        id: string
        control_id: string
        test_date: number
        result: string
        tester_id: string
        notes: string | null
        findings: string[] | null
        evidence_ids: string[] | null
        remediation_required: boolean
        remediation_due_date: number | null
        remediation_notes: string | null
        created_at: number
      }
      control: {
        id: string
        last_test_date: number
        next_test_date: number | null
      }
    }>(`/api/controls/${controlId}/tests`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a test result (e.g., mark remediation complete)
   */
  async updateControlTest(controlId: string, testId: string, data: {
    notes?: string
    findings?: string[]
    evidence_ids?: string[]
    remediation_required?: boolean
    remediation_due_date?: number
    remediation_completed_at?: number
    remediation_notes?: string
  }) {
    return this.request<{
      success: boolean
      test: any
    }>(`/api/controls/${controlId}/tests/${testId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get upcoming control tests across all controls
   */
  async getUpcomingControlTests(params?: { days?: number; limit?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.days) searchParams.set('days', params.days.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      summary: {
        overdue: number
        urgent: number
        upcoming: number
        total: number
      }
      tests: Array<{
        id: string
        control_number: string
        name: string
        testing_frequency: string
        test_procedure: string | null
        last_test_date: number | null
        next_test_date: number
        test_owner_id: string | null
        test_owner_name: string | null
        framework_name: string
        urgency: 'overdue' | 'urgent' | 'upcoming'
        days_until: number
      }>
    }>(`/api/controls/tests/upcoming${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a list of compliance tasks derived from control requirements.
   *
   * Tasks represent ongoing compliance obligations tied to controls that require
   * periodic evidence collection or review. Each task tracks when evidence was
   * last collected, when the next collection is due, and the current compliance
   * status. This method is the primary way to build task lists, compliance
   * calendars, and workload views for compliance teams.
   *
   * Tasks are automatically generated from controls based on their configured
   * test frequency (e.g., monthly, quarterly, annually). The task status is
   * calculated based on the time since last evidence and the required frequency.
   *
   * @param {Object} [params={}] - Query parameters to filter and paginate tasks.
   * @param {'all' | 'overdue' | 'due_soon' | 'missing_evidence' | 'current'} [params.filter='all'] -
   *   Filter tasks by status:
   *   - `'all'` - Return all tasks regardless of status
   *   - `'overdue'` - Tasks past their due date
   *   - `'due_soon'` - Tasks due within the next 7-14 days
   *   - `'missing_evidence'` - Tasks that have never had evidence collected
   *   - `'current'` - Tasks that are up to date with evidence
   * @param {string} [params.framework_id] - Filter to tasks for a specific framework.
   * @param {string} [params.owner_id] - Filter to tasks assigned to a specific user.
   * @param {string} [params.search] - Search term to filter tasks by control name or number.
   * @param {number} [params.limit=25] - Maximum number of tasks to return per page.
   * @param {number} [params.offset=0] - Number of tasks to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `tasks` {Array<Object>} - Array of task objects:
   *     - `id` {string} - Unique task/control identifier
   *     - `control_number` {string} - Framework control ID (e.g., "CC6.1")
   *     - `name` {string} - Control/task name
   *     - `description` {string} - Control description (optional)
   *     - `framework_name` {string} - Name of the parent framework
   *     - `frequency` {string} - Required evidence collection frequency
   *     - `task_status` {'overdue' | 'due_soon' | 'missing_evidence' | 'current'} -
   *       Current task status based on evidence timing
   *     - `compliance_status` {string} - Overall compliance status of the control
   *     - `status_reason` {string} - Human-readable explanation of status (optional)
   *     - `days_until_due` {number} - Days until next evidence is due (optional)
   *     - `next_due_date` {number} - Unix timestamp of next due date (optional)
   *     - `last_evidence_date` {number} - Unix timestamp of last evidence (optional)
   *     - `evidence_count` {number} - Total number of evidence items collected
   *     - `owner_id` {string} - Assigned owner's user ID (optional)
   *     - `owner_username` {string} - Assigned owner's username (optional)
   *     - `owner_email` {string} - Assigned owner's email (optional)
   *   - `counts` {Object} - Summary counts by status:
   *     - `total` {number} - Total number of tasks
   *     - `overdue` {number} - Number of overdue tasks
   *     - `due_soon` {number} - Number of tasks due soon
   *     - `missing_evidence` {number} - Number of tasks missing evidence
   *     - `current` {number} - Number of current/up-to-date tasks
   *   - `pagination` {Object} - Pagination metadata:
   *     - `limit` {number} - Current page size limit
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether more results exist
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view tasks for the current tenant.
   *
   * @example
   * // Display overdue tasks on dashboard
   * async function loadOverdueTasks() {
   *   const { tasks, counts } = await api.getTasks({
   *     filter: 'overdue',
   *     limit: 10
   *   });
   *
   *   setOverdueCount(counts.overdue);
   *   setOverdueTasks(tasks);
   * }
   *
   * @example
   * // Build a compliance calendar view
   * async function loadTaskCalendar(ownerId: string) {
   *   const { tasks } = await api.getTasks({
   *     owner_id: ownerId,
   *     limit: 100
   *   });
   *
   *   const calendarEvents = tasks
   *     .filter(t => t.next_due_date)
   *     .map(task => ({
   *       title: `${task.control_number}: ${task.name}`,
   *       date: new Date(task.next_due_date!),
   *       status: task.task_status
   *     }));
   *   setCalendarEvents(calendarEvents);
   * }
   *
   * @example
   * // Task list with filtering
   * async function loadFilteredTasks(filter: string, frameworkId?: string) {
   *   const { tasks, counts, pagination } = await api.getTasks({
   *     filter: filter as any,
   *     framework_id: frameworkId,
   *     limit: 25,
   *     offset: currentPage * 25
   *   });
   *
   *   setTasks(tasks);
   *   setStatusCounts(counts);
   *   setHasMorePages(pagination.hasMore);
   * }
   *
   * @see {@link getControls} for listing controls without task context
   * @see {@link getControlStats} for aggregate control statistics
   * @see {@link getDashboardOverview} for high-level dashboard metrics
   */
  async getTasks(params: {
    filter?: 'all' | 'overdue' | 'due_soon' | 'missing_evidence' | 'current'
    framework_id?: string
    owner_id?: string
    search?: string
    limit?: number
    offset?: number
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      tasks: Array<{
        id: string
        control_number: string
        name: string
        description?: string
        framework_name: string
        frequency: string
        task_status: 'overdue' | 'due_soon' | 'missing_evidence' | 'current'
        compliance_status: string
        status_reason?: string
        days_until_due?: number
        next_due_date?: number
        last_evidence_date?: number
        evidence_count: number
        owner_id?: string
        owner_username?: string
        owner_email?: string
      }>
      counts: {
        total: number
        overdue: number
        due_soon: number
        missing_evidence: number
        current: number
      }
      pagination: { limit: number; offset: number; hasMore: boolean }
    }>(`/api/controls/tasks?${searchParams}`)
  }

  // Evidence endpoints

  /**
   * Retrieves a paginated list of evidence items with optional filtering.
   *
   * Evidence represents proof of control implementation - documents, screenshots,
   * logs, or files that demonstrate compliance activities. This method supports
   * filtering by control and source type, making it useful for evidence libraries,
   * control detail pages, and compliance audit preparation.
   *
   * Evidence items are stored in R2 cloud storage and can be downloaded using
   * {@link getEvidenceDownloadUrl}.
   *
   * @param {Object} [params={}] - Optional filtering and pagination parameters.
   * @param {string} [params.control_id] - Filter evidence linked to a specific control.
   *   When specified, only returns evidence attached to that control. Useful for
   *   control detail pages showing related evidence.
   * @param {string} [params.source] - Filter by evidence source type.
   *   Common values: 'upload', 'integration', 'screenshot', 'api'.
   * @param {number} [params.limit=25] - Maximum number of evidence items to return per page.
   *   Range: 1-100. Default: 25.
   * @param {number} [params.offset=0] - Number of items to skip for pagination.
   *   Use with `limit` to implement page navigation.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `evidence` {Array<Object>} - Array of evidence items, each containing:
   *     - `id` {string} - Unique evidence identifier
   *     - `filename` {string} - Original file name
   *     - `file_type` {string} - MIME type (e.g., 'application/pdf', 'image/png')
   *     - `file_size` {number} - File size in bytes
   *     - `source` {string} - How the evidence was collected ('upload', 'integration', etc.)
   *     - `note` {string|null} - Optional description or context for the evidence
   *     - `tags` {string[]} - Categorization tags for organization
   *     - `control_ids` {string[]} - IDs of controls this evidence is linked to
   *     - `uploaded_by` {Object} - User who uploaded the evidence
   *     - `created_at` {number} - Unix timestamp of when evidence was uploaded
   *   - `pagination` {Object} - Pagination metadata:
   *     - `total` {number} - Total count of matching evidence items
   *     - `limit` {number} - Current page size limit
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether more results exist beyond current page
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view evidence for the current tenant.
   *
   * @example
   * // Fetch all evidence for the evidence library page
   * const { evidence, pagination } = await api.getEvidence({ limit: 50 });
   * console.log(`Showing ${evidence.length} of ${pagination.total} evidence items`);
   *
   * @example
   * // Fetch evidence linked to a specific control
   * const { evidence } = await api.getEvidence({
   *   control_id: 'control-uuid-here',
   *   limit: 10
   * });
   * setControlEvidence(evidence);
   *
   * @example
   * // Filter by source type and paginate
   * const { evidence, pagination } = await api.getEvidence({
   *   source: 'upload',
   *   limit: 25,
   *   offset: 50  // Page 3
   * });
   *
   * @see {@link getEvidenceItem} for fetching a single evidence item
   * @see {@link uploadEvidence} for uploading new evidence files
   * @see {@link createEvidence} for creating evidence metadata without file
   * @see {@link getEvidenceDownloadUrl} for downloading evidence files
   */
  async getEvidence(params: {
    control_id?: string
    source?: string
    limit?: number
    offset?: number
    // Archive filter support (#182)
    include_archived?: boolean
    archived_only?: boolean
    // Tag filter support (#151) - comma-separated tag slugs
    tags?: string
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      evidence: any[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/evidence?${searchParams}`)
  }

  /**
   * Retrieves a single evidence item by its unique identifier.
   *
   * Fetches the complete evidence record including all metadata, linked controls,
   * file information, and user attribution. This method is typically used when
   * displaying evidence details, preparing for download, or editing evidence metadata.
   *
   * The actual file content is not returned - use {@link getEvidenceDownloadUrl}
   * to generate a download URL for the file.
   *
   * @param {string} id - The unique identifier of the evidence to retrieve.
   *   This is the UUID assigned when the evidence was created or uploaded.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `evidence` {Object} - The complete evidence object containing:
   *     - `id` {string} - Unique evidence identifier
   *     - `filename` {string} - Original file name as uploaded
   *     - `file_type` {string} - MIME type (e.g., 'application/pdf', 'image/png')
   *     - `file_size` {number} - File size in bytes
   *     - `source` {string} - How the evidence was collected ('upload', 'integration', 'screenshot')
   *     - `note` {string|null} - Optional description or context for the evidence
   *     - `tags` {string[]} - Categorization tags for organization
   *     - `control_ids` {string[]} - IDs of controls this evidence is linked to
   *     - `controls` {Array<Object>} - Expanded control objects with basic info
   *     - `uploaded_by` {Object} - User who uploaded the evidence (id, name, email)
   *     - `storage_key` {string} - Internal R2 storage path (not for public use)
   *     - `created_at` {number} - Unix timestamp of when evidence was uploaded
   *     - `updated_at` {number} - Unix timestamp of last modification
   *
   * @throws {Error} Throws if the evidence is not found (404) or the user lacks
   *   permission to view it.
   *
   * @example
   * // Fetch evidence details for display
   * async function loadEvidenceDetails(evidenceId: string) {
   *   try {
   *     const { evidence } = await api.getEvidenceItem(evidenceId);
   *     setEvidenceData(evidence);
   *     setLinkedControls(evidence.controls);
   *   } catch (error) {
   *     if (error.message.includes('404')) {
   *       toast.error('Evidence not found');
   *       navigate('/evidence');
   *     }
   *     throw error;
   *   }
   * }
   *
   * @example
   * // Show evidence in a modal with download link
   * const { evidence } = await api.getEvidenceItem(id);
   * const downloadUrl = api.getEvidenceDownloadUrl(id);
   * showModal({
   *   title: evidence.filename,
   *   fileSize: formatBytes(evidence.file_size),
   *   uploadedBy: evidence.uploaded_by.name,
   *   downloadUrl
   * });
   *
   * @see {@link getEvidence} for listing multiple evidence items
   * @see {@link getEvidenceDownloadUrl} for downloading the evidence file
   * @see {@link deleteEvidence} for removing evidence
   */
  async getEvidenceItem(id: string) {
    return this.request<{
      success: boolean
      evidence: any
    }>(`/api/evidence/${id}`)
  }

  /**
   * Creates an evidence record without a file attachment.
   *
   * This method creates evidence metadata that can represent notes, observations,
   * or references to external evidence that cannot be uploaded. For evidence with
   * actual file attachments, use {@link uploadEvidence} instead, which handles
   * both the file upload and metadata creation in a single request.
   *
   * Use cases for this method include:
   * - Recording verbal attestations or observations
   * - Referencing evidence stored in external systems
   * - Creating placeholder records to be updated later
   * - Documenting manual verification activities
   *
   * **Required permissions:** User must have 'admin', 'editor', or 'contributor' role.
   *
   * @param {Object} data - The evidence data to create.
   * @param {string} data.control_id - ID of the control this evidence supports.
   *   Required - evidence must be linked to at least one control.
   * @param {string} [data.note] - Description or context for the evidence.
   *   Useful for explaining what the evidence demonstrates or where it came from.
   * @param {string[]} [data.tags] - Array of tags for categorization and filtering.
   *   Examples: ['annual-review', 'access-control', 'soc2'].
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the evidence was created successfully
   *   - `evidence` {Object} - The newly created evidence object with:
   *     - `id` {string} - Generated unique identifier
   *     - `control_id` {string} - Linked control ID
   *     - `note` {string|null} - Provided description
   *     - `tags` {string[]} - Provided tags (empty array if not specified)
   *     - `source` {string} - Set to 'manual' for created evidence
   *     - `created_at` {number} - Unix timestamp of creation
   *     - `uploaded_by` {Object} - Current user's information
   *
   * @throws {Error} Throws if:
   *   - User lacks permission to create evidence
   *   - Required control_id is missing or invalid
   *   - Control does not exist or user cannot access it
   *
   * @example
   * // Record a manual verification observation
   * const { evidence } = await api.createEvidence({
   *   control_id: 'access-control-id',
   *   note: 'Verified quarterly access review was completed on 2024-03-15. ' +
   *         'Review documentation stored in SharePoint at: [link]',
   *   tags: ['quarterly-review', 'access-control', 'Q1-2024']
   * });
   *
   * @example
   * // Create placeholder evidence for a control
   * async function handleAddNote(controlId: string, noteText: string) {
   *   try {
   *     const { evidence } = await api.createEvidence({
   *       control_id: controlId,
   *       note: noteText
   *     });
   *     toast.success('Evidence note added');
   *     refreshEvidenceList();
   *   } catch (error) {
   *     toast.error(`Failed to add evidence: ${error.message}`);
   *   }
   * }
   *
   * @see {@link uploadEvidence} for uploading evidence with file attachments
   * @see {@link getEvidence} for listing evidence items
   * @see {@link deleteEvidence} for removing evidence
   */
  async createEvidence(data: { control_id: string; note?: string; tags?: string[] }) {
    return this.request<{
      success: boolean
      evidence: any
    }>('/api/evidence', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Uploads an evidence file with associated metadata.
   *
   * This method handles file upload to R2 cloud storage along with evidence
   * metadata creation in a single request. It accepts a FormData object containing
   * the file and optional metadata fields. The file is stored securely and can
   * be downloaded later using {@link getEvidenceDownloadUrl}.
   *
   * **Note:** This method uses FormData (multipart/form-data) for the request body,
   * which is handled specially by the internal request method - no JSON serialization
   * or Content-Type header is applied.
   *
   * **File size limits:** Maximum file size is determined by server configuration,
   * typically 10-50MB depending on deployment.
   *
   * **Supported file types:** Common document formats (PDF, Word, Excel), images
   * (PNG, JPEG, GIF), and compressed archives (ZIP). Check server configuration
   * for the complete list of allowed MIME types.
   *
   * **Required permissions:** User must have 'admin', 'editor', or 'contributor' role.
   *
   * @param {FormData} formData - FormData object containing the file and metadata.
   *   Expected fields:
   *   - `file` {File} - Required. The file to upload.
   *   - `control_id` {string} - Required. ID of the control this evidence supports.
   *   - `note` {string} - Optional. Description or context for the evidence.
   *   - `tags` {string} - Optional. Comma-separated tags for categorization.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the upload and creation succeeded
   *   - `evidence` {Object} - The newly created evidence object with:
   *     - `id` {string} - Generated unique identifier
   *     - `filename` {string} - Original file name (sanitized)
   *     - `file_type` {string} - Detected MIME type
   *     - `file_size` {number} - File size in bytes
   *     - `storage_key` {string} - Internal R2 storage reference
   *     - `control_id` {string} - Linked control ID
   *     - `note` {string|null} - Provided description
   *     - `tags` {string[]} - Parsed tags array
   *     - `source` {string} - Set to 'upload' for uploaded files
   *     - `created_at` {number} - Unix timestamp of upload
   *     - `uploaded_by` {Object} - Current user's information
   *
   * @throws {Error} Throws if:
   *   - User lacks permission to upload evidence
   *   - Required fields (file, control_id) are missing
   *   - File exceeds size limit
   *   - File type is not allowed
   *   - Control does not exist or user cannot access it
   *
   * @example
   * // Upload evidence from a file input element
   * async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>, controlId: string) {
   *   const file = event.target.files?.[0];
   *   if (!file) return;
   *
   *   const formData = new FormData();
   *   formData.append('file', file);
   *   formData.append('control_id', controlId);
   *   formData.append('note', 'Quarterly access review report');
   *   formData.append('tags', 'quarterly,access-review,Q1-2024');
   *
   *   try {
   *     const { evidence } = await api.uploadEvidence(formData);
   *     toast.success(`Uploaded: ${evidence.filename}`);
   *     refreshEvidenceList();
   *   } catch (error) {
   *     toast.error(`Upload failed: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Upload with drag-and-drop
   * async function handleDrop(files: FileList, controlId: string) {
   *   for (const file of files) {
   *     const formData = new FormData();
   *     formData.append('file', file);
   *     formData.append('control_id', controlId);
   *
   *     await api.uploadEvidence(formData);
   *   }
   *   toast.success(`Uploaded ${files.length} files`);
   * }
   *
   * @see {@link createEvidence} for creating evidence without file attachment
   * @see {@link getEvidenceDownloadUrl} for downloading uploaded files
   * @see {@link deleteEvidence} for removing evidence
   */
  async uploadEvidence(formData: FormData) {
    return this.request<{
      success: boolean
      evidence: any
      count?: number
    }>('/api/evidence/upload', {
      method: 'POST',
      body: formData,
    })
  }

  /**
   * Permanently deletes an evidence item and its associated file.
   *
   * This method removes both the evidence metadata record from the database and
   * the actual file from R2 cloud storage. This action is irreversible - the file
   * and all metadata will be permanently deleted.
   *
   * When evidence is deleted, it is automatically unlinked from all controls it
   * was attached to. The deletion is logged in the audit trail for compliance
   * tracking purposes.
   *
   * **Warning:** This is a destructive operation. Consider implementing a
   * confirmation dialog in the UI before calling this method.
   *
   * **Required permissions:** User must have 'admin' or 'editor' role.
   * Contributors can only delete evidence they uploaded.
   *
   * @param {string} id - The unique identifier of the evidence to delete.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the deletion was successful
   *
   * @throws {Error} Throws if:
   *   - Evidence is not found (404)
   *   - User lacks permission to delete the evidence
   *   - Evidence is referenced by active assessments (may be protected)
   *
   * @example
   * // Delete evidence with confirmation
   * async function handleDeleteEvidence(evidenceId: string, filename: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete Evidence',
   *     message: `Are you sure you want to permanently delete "${filename}"? This action cannot be undone.`,
   *     confirmText: 'Delete',
   *     destructive: true
   *   });
   *
   *   if (confirmed) {
   *     try {
   *       await api.deleteEvidence(evidenceId);
   *       toast.success('Evidence deleted');
   *       refreshEvidenceList();
   *     } catch (error) {
   *       toast.error(`Failed to delete: ${error.message}`);
   *     }
   *   }
   * }
   *
   * @example
   * // Bulk delete (with caution)
   * async function handleBulkDelete(evidenceIds: string[]) {
   *   const results = await Promise.allSettled(
   *     evidenceIds.map(id => api.deleteEvidence(id))
   *   );
   *
   *   const succeeded = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   toast.info(`Deleted ${succeeded} items, ${failed} failed`);
   * }
   *
   * @see {@link getEvidence} for listing evidence items
   * @see {@link getEvidenceItem} for fetching evidence details before deletion
   */
  async deleteEvidence(id: string) {
    return this.request<{
      success: boolean
    }>(`/api/evidence/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves version history for an evidence item.
   */
  async getEvidenceVersions(evidenceId: string) {
    return this.request<{
      success: boolean
      current_version: number
      version_count: number
      versions: Array<{
        id: string
        version: number
        file_name: string
        file_size: number
        file_type?: string
        note?: string
        change_reason?: string
        created_at: number
        created_by_username?: string
      }>
    }>(`/api/evidence/${evidenceId}/versions`)
  }

  /**
   * Retrieves audit trail for an evidence item.
   */
  async getEvidenceAudit(evidenceId: string) {
    return this.request<{
      success: boolean
      audit_logs: Array<{
        id: string
        action: string
        user_id: string
        username?: string
        user_username?: string
        details?: Record<string, any>
        ip_address?: string
        created_at: number
      }>
    }>(`/api/evidence/${evidenceId}/audit`)
  }

  /**
   * Uploads a new version of an evidence item.
   */
  async uploadEvidenceVersion(evidenceId: string, formData: FormData) {
    return this.request<{
      success: boolean
      version: number
    }>(`/api/evidence/${evidenceId}/versions`, {
      method: 'POST',
      body: formData,
    })
  }

  /**
   * Links controls to an evidence item.
   */
  async linkEvidenceControls(evidenceId: string, controlIds: string[]) {
    return this.request<{
      success: boolean
      linked_count: number
    }>(`/api/evidence/${evidenceId}/controls`, {
      method: 'POST',
      body: JSON.stringify({ control_ids: controlIds }),
    })
  }

  /**
   * Unlinks a control from an evidence item.
   */
  async unlinkEvidenceControl(evidenceId: string, controlId: string) {
    return this.request<{
      success: boolean
    }>(`/api/evidence/${evidenceId}/controls/${controlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves aggregate statistics about the evidence library.
   *
   * Returns summary metrics about evidence items in the tenant, useful for
   * dashboard widgets, progress tracking, and compliance reporting. Statistics
   * include counts by source type, file type distribution, and coverage metrics.
   *
   * This endpoint is optimized for dashboard use and returns pre-computed
   * aggregates rather than calculating on each request.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `stats` {Object} - Aggregate statistics containing:
   *     - `total_count` {number} - Total number of evidence items
   *     - `total_size_bytes` {number} - Sum of all file sizes in bytes
   *     - `by_source` {Object} - Count breakdown by source type:
   *       - `upload` {number} - Manually uploaded files
   *       - `integration` {number} - Synced from integrations
   *       - `screenshot` {number} - Screen captures
   *       - `manual` {number} - Text-only evidence notes
   *     - `by_file_type` {Object} - Count breakdown by MIME type category:
   *       - `pdf` {number} - PDF documents
   *       - `image` {number} - Images (PNG, JPEG, etc.)
   *       - `document` {number} - Word/text documents
   *       - `spreadsheet` {number} - Excel/CSV files
   *       - `other` {number} - Other file types
   *     - `controls_with_evidence` {number} - Controls that have linked evidence
   *     - `controls_missing_evidence` {number} - Controls without evidence
   *     - `recent_uploads` {number} - Uploads in the last 30 days
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view evidence statistics.
   *
   * @example
   * // Display evidence statistics on dashboard
   * async function loadEvidenceStats() {
   *   const { stats } = await api.getEvidenceStats();
   *
   *   setMetrics({
   *     totalEvidence: stats.total_count,
   *     storageUsed: formatBytes(stats.total_size_bytes),
   *     coveragePercent: Math.round(
   *       (stats.controls_with_evidence /
   *         (stats.controls_with_evidence + stats.controls_missing_evidence)) * 100
   *     )
   *   });
   * }
   *
   * @example
   * // Show evidence coverage warning
   * const { stats } = await api.getEvidenceStats();
   * if (stats.controls_missing_evidence > 0) {
   *   showWarning(`${stats.controls_missing_evidence} controls are missing evidence`);
   * }
   *
   * @see {@link getEvidence} for listing individual evidence items
   * @see {@link getControlStats} for control-focused statistics
   * @see {@link getDashboardOverview} for combined dashboard metrics
   */
  async getEvidenceStats() {
    return this.request<{
      success: boolean
      stats: any
    }>('/api/evidence/stats/summary')
  }

  /**
   * Generates a direct download URL for an evidence file.
   *
   * This is a synchronous method that constructs the download URL for an evidence
   * file. Unlike other API methods, this does not make an HTTP request - it simply
   * builds the URL that can be used in `<a href>` tags, `window.open()`, or for
   * programmatic downloads.
   *
   * **Important:** The download endpoint requires authentication. The user must
   * have an active session (via {@link setToken}) for the download to succeed.
   * The browser will automatically include the auth cookie for same-origin requests.
   *
   * The server responds with:
   * - `Content-Type` matching the file's MIME type
   * - `Content-Disposition: attachment` header to trigger download
   * - Appropriate cache headers
   *
   * @param {string} id - The unique identifier of the evidence to download.
   *
   * @returns {string} The fully-qualified download URL for the evidence file.
   *   Format: `{API_BASE}/api/evidence/{id}/download`
   *   Examples:
   *   - Development: `/api/evidence/abc-123/download` (uses Vite proxy)
   *   - Production: `https://acme.app.clarantis.com/api/evidence/abc-123/download`
   *
   * @example
   * // Create a download link in React
   * function DownloadButton({ evidenceId, filename }) {
   *   const downloadUrl = api.getEvidenceDownloadUrl(evidenceId);
   *
   *   return (
   *     <a href={downloadUrl} download={filename} className="btn">
   *       Download
   *     </a>
   *   );
   * }
   *
   * @example
   * // Trigger download programmatically
   * function handleDownload(evidenceId: string) {
   *   const url = api.getEvidenceDownloadUrl(evidenceId);
   *   window.open(url, '_blank');
   * }
   *
   * @example
   * // Preview in new tab (for images/PDFs)
   * function handlePreview(evidenceId: string) {
   *   const url = api.getEvidenceDownloadUrl(evidenceId);
   *   // For inline preview, server should set Content-Disposition: inline
   *   window.open(url, '_blank', 'noopener,noreferrer');
   * }
   *
   * @see {@link getEvidenceItem} for fetching evidence metadata
   * @see {@link uploadEvidence} for uploading new evidence files
   * @see {@link getApiBase} for URL resolution logic
   */
  getEvidenceDownloadUrl(id: string): string {
    return `${getApiBase()}/api/evidence/${id}/download`
  }

  /**
   * Generates a URL for previewing an evidence file inline.
   *
   * Unlike {@link getEvidenceDownloadUrl}, this URL serves files with
   * `Content-Disposition: inline` headers, allowing browsers to display
   * supported file types (images, PDFs) directly without prompting for download.
   *
   * For text files (TXT, JSON, XML, CSV, Markdown), this endpoint returns
   * JSON with the file content for safe rendering in the browser.
   *
   * @param {string} id - The unique identifier of the evidence item.
   *
   * @returns {string} The complete URL for previewing the evidence file inline.
   *
   * @example
   * // Preview an image in a modal
   * const previewUrl = api.getEvidencePreviewUrl(evidenceId);
   * <img src={previewUrl} alt="Evidence preview" />
   *
   * @example
   * // Preview a PDF in an iframe
   * const previewUrl = api.getEvidencePreviewUrl(evidenceId);
   * <iframe src={previewUrl} />
   *
   * @see {@link getEvidenceDownloadUrl} for download with Content-Disposition: attachment
   * @see {@link getEvidenceItem} for fetching evidence metadata
   *
   * Issue #163: Evidence Preview and Inline Viewing
   */
  getEvidencePreviewUrl(id: string): string {
    return `${getApiBase()}/api/evidence/${id}/preview`
  }

  // ============================================================================
  // Evidence Tags API (Issue #151)
  // ============================================================================

  /**
   * Get all evidence tags with usage counts.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async getEvidenceTags(params: {
    category?: string
    include_unused?: boolean
    limit?: number
    offset?: number
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      tags: EvidenceTag[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/evidence/tags?${searchParams}`)
  }

  /**
   * Get tag usage statistics.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async getEvidenceTagStats() {
    return this.request<{
      success: boolean
      stats: {
        total_tags: number
        system_tags: number
        custom_tags: number
        total_uses: number
        by_category: Array<{ category: string; count: number; uses: number }>
        top_tags: Array<{ name: string; slug: string; color: string; use_count: number }>
      }
    }>('/api/evidence/tags/stats')
  }

  /**
   * Create a new evidence tag.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async createEvidenceTag(data: {
    name: string
    category?: string
    color?: string
  }) {
    return this.request<{
      success: boolean
      tag: EvidenceTag
    }>('/api/evidence/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an existing evidence tag.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async updateEvidenceTag(tagId: string, data: {
    name?: string
    category?: string
    color?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/evidence/tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete an evidence tag.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async deleteEvidenceTag(tagId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/evidence/tags/${tagId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get tags for a specific evidence item.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async getEvidenceItemTags(evidenceId: string) {
    return this.request<{
      success: boolean
      tags: EvidenceTag[]
    }>(`/api/evidence/${evidenceId}/tags`)
  }

  /**
   * Add tags to an evidence item.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async addEvidenceTags(evidenceId: string, tagIds: string[]) {
    return this.request<{
      success: boolean
      applied_count: number
    }>(`/api/evidence/${evidenceId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tag_ids: tagIds }),
    })
  }

  /**
   * Remove a tag from an evidence item.
   * Issue #151: Evidence Categorization and Smart Tagging
   */
  async removeEvidenceTag(evidenceId: string, tagId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/evidence/${evidenceId}/tags/${tagId}`, {
      method: 'DELETE',
    })
  }

  // Frameworks endpoints

  /**
   * Retrieves all compliance frameworks available to the organization.
   *
   * This method fetches the complete list of compliance frameworks (e.g., SOC 2,
   * ISO 27001, HIPAA, PCI-DSS, NIST) that the organization can enable or disable.
   * Each framework contains its controls, requirements, and current enablement status.
   *
   * **Framework Structure:**
   * Frameworks are pre-defined sets of compliance controls that organizations can
   * adopt based on their regulatory requirements and industry standards. Enabling
   * a framework imports its controls into the organization's control library.
   *
   * **Common Framework Types:**
   * - **SOC 2** - Service organization controls for security, availability, integrity
   * - **ISO 27001** - International information security management standard
   * - **HIPAA** - Healthcare data protection requirements
   * - **PCI-DSS** - Payment card industry data security standards
   * - **NIST CSF** - Cybersecurity framework for critical infrastructure
   * - **GDPR** - European data protection regulation
   *
   * @returns {Promise<{success: boolean, frameworks: any[], count: number}>}
   *   - `success` - Whether the request completed successfully
   *   - `frameworks` - Array of framework objects, each containing:
   *     - `id` - Unique framework identifier
   *     - `name` - Display name (e.g., "SOC 2 Type II")
   *     - `description` - Framework overview and purpose
   *     - `enabled` - Whether the organization has enabled this framework
   *     - `control_count` - Number of controls in this framework
   *     - `version` - Framework version (e.g., "2017", "2022")
   *   - `count` - Total number of available frameworks
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If the user is not authenticated (401)
   * @throws {Error} If the user lacks permission to view frameworks (403)
   *
   * @example
   * // Load and display available frameworks
   * async function loadFrameworks() {
   *   const { frameworks, count } = await api.getFrameworks();
   *
   *   console.log(`${count} frameworks available`);
   *   frameworks.forEach(fw => {
   *     console.log(`${fw.name}: ${fw.enabled ? 'Enabled' : 'Disabled'}`);
   *   });
   * }
   *
   * @example
   * // Filter to show only enabled frameworks
   * async function getActiveFrameworks() {
   *   const { frameworks } = await api.getFrameworks();
   *   return frameworks.filter(fw => fw.enabled);
   * }
   *
   * @example
   * // Framework selection component
   * function FrameworkSelector() {
   *   const [frameworks, setFrameworks] = useState([]);
   *
   *   useEffect(() => {
   *     api.getFrameworks()
   *       .then(({ frameworks }) => setFrameworks(frameworks))
   *       .catch(err => console.error('Failed to load frameworks:', err));
   *   }, []);
   *
   *   return (
   *     <div>
   *       {frameworks.map(fw => (
   *         <FrameworkCard
   *           key={fw.id}
   *           framework={fw}
   *           onToggle={(enabled) => api.toggleFramework(fw.id, enabled)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @see {@link toggleFramework} for enabling/disabling a framework
   * @see {@link getControls} for listing controls (which may be filtered by framework)
   */
  async getFrameworks() {
    return this.request<{
      success: boolean
      frameworks: any[]
      count: number
    }>('/api/frameworks')
  }

  /**
   * Enables or disables a compliance framework for the organization.
   *
   * This method toggles a framework's enabled status. When a framework is enabled,
   * its controls become active in the organization's compliance program. When disabled,
   * the controls are deactivated but not deleted, preserving any existing evidence
   * and assessment data.
   *
   * **Enabling a Framework:**
   * - Imports all framework controls into the organization's control library
   * - Controls appear in the Controls page and compliance dashboards
   * - Evidence can be mapped to the framework's controls
   * - Framework appears in compliance reports and assessments
   *
   * **Disabling a Framework:**
   * - Hides framework controls from active views (but data is preserved)
   * - Removes framework from compliance reports
   * - Existing evidence links and assessment data are retained
   * - Framework can be re-enabled later without data loss
   *
   * **Important:** Enabling or disabling frameworks affects compliance reporting
   * and should be done carefully. Changes are immediately reflected across the
   * application.
   *
   * @param {string} id - The unique identifier of the framework to toggle.
   * @param {boolean} enabled - `true` to enable the framework, `false` to disable it.
   *
   * @returns {Promise<{success: boolean, message: string}>}
   *   - `success` - Whether the operation completed successfully
   *   - `message` - Confirmation message (e.g., "Framework enabled successfully")
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If the user is not authenticated (401)
   * @throws {Error} If the user lacks admin permission to modify frameworks (403)
   * @throws {Error} If the framework ID is not found (404)
   *
   * @example
   * // Enable a framework
   * async function enableSOC2(frameworkId: string) {
   *   try {
   *     const { message } = await api.toggleFramework(frameworkId, true);
   *     toast.success(message);
   *   } catch (error) {
   *     toast.error('Failed to enable framework');
   *   }
   * }
   *
   * @example
   * // Toggle switch handler in React
   * function FrameworkToggle({ framework, onUpdate }) {
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleToggle = async () => {
   *     setLoading(true);
   *     try {
   *       await api.toggleFramework(framework.id, !framework.enabled);
   *       onUpdate(); // Refresh frameworks list
   *     } catch (error) {
   *       console.error('Toggle failed:', error);
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <Switch
   *       checked={framework.enabled}
   *       onChange={handleToggle}
   *       disabled={loading}
   *       label={framework.name}
   *     />
   *   );
   * }
   *
   * @example
   * // Bulk enable multiple frameworks
   * async function enableFrameworks(frameworkIds: string[]) {
   *   const results = await Promise.allSettled(
   *     frameworkIds.map(id => api.toggleFramework(id, true))
   *   );
   *
   *   const succeeded = results.filter(r => r.status === 'fulfilled').length;
   *   console.log(`Enabled ${succeeded}/${frameworkIds.length} frameworks`);
   * }
   *
   * @see {@link getFrameworks} for listing available frameworks
   * @see {@link getControls} for viewing controls after framework changes
   */
  async toggleFramework(id: string, enabled: boolean) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/frameworks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    })
  }

  /**
   * Gets a single framework with full details including version information.
   */
  async getFramework(id: string) {
    return this.request<{
      success: boolean
      framework: any
    }>(`/api/frameworks/${id}`)
  }

  /**
   * Gets all versions of a framework (grouped by base_framework_name).
   */
  async getFrameworkVersions(id: string) {
    return this.request<{
      success: boolean
      base_framework_name: string
      versions: any[]
      count: number
    }>(`/api/frameworks/${id}/versions`)
  }

  /**
   * Creates a new version of a framework.
   */
  async createFrameworkVersion(id: string, data: {
    new_version: string
    new_display_name: string
    new_name?: string
    effective_date?: number
    migration_notes?: string
    copy_controls?: boolean
  }) {
    return this.request<{
      success: boolean
      message: string
      framework: any
      controls_copied: number
    }>(`/api/frameworks/${id}/upgrade`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Migrates controls and evidence from one framework version to another.
   */
  async migrateFramework(sourceId: string, data: {
    target_framework_id: string
    copy_evidence?: boolean
    copy_policies?: boolean
  }) {
    return this.request<{
      success: boolean
      message: string
      migration_id: string
      controls_migrated: number
      controls_skipped: number
      evidence_linked: number
    }>(`/api/frameworks/${sourceId}/migrate`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Gets all framework migrations.
   */
  async getFrameworkMigrations() {
    return this.request<{
      success: boolean
      migrations: any[]
      count: number
    }>('/api/frameworks/migrations')
  }

  /**
   * Gets details of a specific framework migration.
   */
  async getFrameworkMigration(id: string) {
    return this.request<{
      success: boolean
      migration: any
      control_mappings: any[]
    }>(`/api/frameworks/migrations/${id}`)
  }

  /**
   * Updates framework metadata including version information.
   */
  async updateFramework(id: string, data: {
    enabled?: boolean
    description?: string
    effective_date?: number | null
    sunset_date?: number | null
    base_framework_name?: string | null
    is_latest?: number
    migration_notes?: string | null
  }) {
    return this.request<{
      success: boolean
      message: string
      framework: any
    }>(`/api/frameworks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Exports a framework to JSON format for backup or sharing.
   *
   * This method exports a complete framework including all controls,
   * framework metadata, and control mappings. The export can be imported
   * into another tenant or used as a backup.
   *
   * @param id - The framework ID to export
   * @returns Export data containing framework, controls, and mappings
   *
   * @example
   * // Export a framework and download as JSON
   * const { export: data } = await api.exportFramework(frameworkId);
   * const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
   * const url = URL.createObjectURL(blob);
   * const a = document.createElement('a');
   * a.href = url;
   * a.download = `${data.framework.name}-export.json`;
   * a.click();
   */
  async exportFramework(id: string) {
    return this.request<{
      success: boolean
      message: string
      export: {
        export_format_version: string
        exported_at: string
        exported_from_tenant: string
        framework: {
          name: string
          display_name: string
          description: string | null
          version: string | null
          base_framework_name: string | null
          effective_date: number | null
          migration_notes: string | null
          settings: string
        }
        controls: Array<{
          control_number: string
          name: string
          description: string | null
          frequency: string
          evidence_type: string
          is_not_applicable: number
          category: string | null
          tags: string | null
          custom_fields: string
        }>
        framework_control_mappings: any[]
        control_mappings: any[]
        statistics: {
          total_controls: number
          total_framework_mappings: number
          total_control_mappings: number
        }
      }
    }>(`/api/frameworks/${id}/export`, {
      method: 'POST',
    })
  }

  /**
   * Imports a framework from JSON export data.
   *
   * This method imports a framework that was previously exported. It supports
   * three import modes:
   * - **create_new**: Always creates a new framework (default)
   * - **merge**: Adds new controls to existing framework, skips duplicates
   * - **replace**: Updates existing controls and adds new ones
   *
   * @param exportData - The exported framework data
   * @param importMode - How to handle existing frameworks ('create_new' | 'merge' | 'replace')
   * @param newFrameworkName - Optional: override the framework name
   * @returns Import results including statistics
   *
   * @example
   * // Import a framework from file
   * const file = event.target.files[0];
   * const text = await file.text();
   * const exportData = JSON.parse(text);
   * const result = await api.importFramework(exportData, 'create_new');
   * console.log(`Imported ${result.statistics.controls_created} controls`);
   */
  async importFramework(
    exportData: any,
    importMode: 'create_new' | 'merge' | 'replace' = 'create_new',
    newFrameworkName?: string
  ) {
    return this.request<{
      success: boolean
      message: string
      framework_id: string
      framework_name: string
      import_mode: string
      statistics: {
        controls_created: number
        controls_updated: number
        controls_skipped: number
      }
    }>('/api/frameworks/import', {
      method: 'POST',
      body: JSON.stringify({
        export: exportData,
        import_mode: importMode,
        new_framework_name: newFrameworkName,
      }),
    })
  }

  // ============================================================================
  // User Management Endpoints
  // ============================================================================

  /**
   * Creates a new user account within the organization.
   *
   * This method registers a new user with the specified role and credentials.
   * The user will receive an email invitation to set their password and complete
   * their account setup. Only administrators can create new user accounts.
   *
   * **Role Hierarchy & Permissions:**
   * - **admin** - Full access: manage users, settings, integrations, and all data
   * - **compliance_manager** - Manage controls, evidence, risks, and compliance workflows
   * - **auditor** - Read-only access to compliance data for audit review
   * - **viewer** - Limited read-only access to dashboards and reports
   *
   * **Registration Flow:**
   * 1. Admin calls createUser with user details
   * 2. System creates account in pending state
   * 3. User receives email invitation
   * 4. User sets password and activates account
   *
   * @param {Object} data - The user registration data
   * @param {string} data.username - Unique username for login (lowercase, alphanumeric)
   * @param {string} data.email - User's email address for notifications and recovery
   * @param {string} data.role - User's role: 'admin', 'compliance_manager', 'auditor', or 'viewer'
   * @param {string} [data.firstName] - User's first name for display purposes
   * @param {string} [data.lastName] - User's last name for display purposes
   *
   * @returns {Promise<{success: boolean, user: any}>}
   *   - `success` - Whether the user was created successfully
   *   - `user` - The created user object containing:
   *     - `id` - Unique user identifier (UUID)
   *     - `username` - The assigned username
   *     - `email` - User's email address
   *     - `role` - Assigned role
   *     - `firstName` / `lastName` - Display name components
   *     - `active` - Account status (initially pending)
   *     - `created_at` - Account creation timestamp
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If current user lacks admin permissions (403)
   * @throws {Error} If username or email already exists (409)
   * @throws {Error} If required fields are missing or invalid (400)
   *
   * @example
   * // Create a new compliance manager
   * async function inviteComplianceManager(email: string, name: string) {
   *   const [firstName, lastName] = name.split(' ');
   *   const username = email.split('@')[0].toLowerCase();
   *
   *   try {
   *     const { user } = await api.createUser({
   *       username,
   *       email,
   *       role: 'compliance_manager',
   *       firstName,
   *       lastName,
   *     });
   *     console.log(`Invited ${user.email} as ${user.role}`);
   *     return user;
   *   } catch (err) {
   *     if (err.message.includes('409')) {
   *       throw new Error('A user with this email already exists');
   *     }
   *     throw err;
   *   }
   * }
   *
   * @example
   * // User invitation form handler
   * function InviteUserForm() {
   *   const handleSubmit = async (formData: FormData) => {
   *     const { user } = await api.createUser({
   *       username: formData.get('username') as string,
   *       email: formData.get('email') as string,
   *       role: formData.get('role') as string,
   *       firstName: formData.get('firstName') as string,
   *       lastName: formData.get('lastName') as string,
   *     });
   *     toast.success(`Invitation sent to ${user.email}`);
   *   };
   *
   *   return <form onSubmit={handleSubmit}>...</form>;
   * }
   *
   * @see {@link getUsers} for listing all organization users
   * @see {@link updateUser} for modifying user details or role
   * @see {@link deleteUser} for removing user accounts
   */
  async createUser(data: {
    username: string
    email: string
    role: string
    firstName?: string
    lastName?: string
    department?: string
    title?: string
  }) {
    return this.request<{
      success: boolean
      user: any
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Retrieves all users in the organization.
   *
   * This method fetches the complete list of users who have access to the
   * organization's GRC platform. Results include user details, roles, and
   * activity status. Only administrators can access the full user list.
   *
   * **User Attributes:**
   * - **Active users** - Can log in and access the platform based on their role
   * - **Inactive users** - Account disabled, cannot log in (e.g., former employees)
   * - **Pending users** - Invitation sent but not yet accepted
   *
   * **Role Distribution:**
   * Use this method to audit role assignments and ensure proper access control.
   * Consider reviewing user roles periodically for security compliance.
   *
   * @returns {Promise<{success: boolean, users: any[], count: number}>}
   *   - `success` - Whether the request completed successfully
   *   - `users` - Array of user objects, each containing:
   *     - `id` - Unique user identifier (UUID)
   *     - `username` - User's login name
   *     - `email` - Email address
   *     - `role` - Assigned role ('admin', 'compliance_manager', 'auditor', 'viewer')
   *     - `firstName` / `lastName` - Display name components
   *     - `active` - Whether the account is enabled
   *     - `last_login` - Timestamp of most recent login (null if never logged in)
   *     - `created_at` - Account creation timestamp
   *   - `count` - Total number of users in the organization
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If current user lacks permission to view users (403)
   *
   * @example
   * // Load and display all users
   * async function loadUsers() {
   *   const { users, count } = await api.getUsers();
   *   console.log(`Organization has ${count} users`);
   *   return users;
   * }
   *
   * @example
   * // User management table component
   * function UserManagementTable() {
   *   const [users, setUsers] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getUsers()
   *       .then(({ users }) => setUsers(users))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   return (
   *     <table>
   *       <thead>
   *         <tr>
   *           <th>Name</th>
   *           <th>Email</th>
   *           <th>Role</th>
   *           <th>Status</th>
   *         </tr>
   *       </thead>
   *       <tbody>
   *         {users.map(user => (
   *           <tr key={user.id}>
   *             <td>{user.firstName} {user.lastName}</td>
   *             <td>{user.email}</td>
   *             <td>{user.role}</td>
   *             <td>{user.active ? 'Active' : 'Inactive'}</td>
   *           </tr>
   *         ))}
   *       </tbody>
   *     </table>
   *   );
   * }
   *
   * @example
   * // Audit role distribution
   * async function auditRoleDistribution() {
   *   const { users } = await api.getUsers();
   *   const roleCounts = users.reduce((acc, user) => {
   *     acc[user.role] = (acc[user.role] || 0) + 1;
   *     return acc;
   *   }, {} as Record<string, number>);
   *
   *   console.log('Role distribution:', roleCounts);
   *   // { admin: 2, compliance_manager: 5, auditor: 3, viewer: 10 }
   * }
   *
   * @see {@link createUser} for inviting new users
   * @see {@link updateUser} for modifying user details
   * @see {@link deleteUser} for removing users
   */
  async getUsers() {
    return this.request<{
      success: boolean
      users: any[]
      count: number
    }>('/api/users')
  }

  /**
   * Updates an existing user's profile or permissions.
   *
   * This method allows administrators to modify user details including their
   * role, active status, and display name. Role changes take effect immediately
   * on the user's next request. Only administrators can modify other users.
   *
   * **Common Update Operations:**
   * - **Role change** - Promote/demote user permissions (e.g., viewer → auditor)
   * - **Deactivation** - Disable account without deleting (set active: false)
   * - **Reactivation** - Re-enable a previously disabled account
   * - **Profile update** - Change display name
   *
   * **Role Change Implications:**
   * - Promoting to admin grants full system access immediately
   * - Demoting from admin removes elevated permissions immediately
   * - Role changes are logged in the audit trail
   *
   * **Deactivation vs. Deletion:**
   * - Prefer deactivation (active: false) over deletion for audit compliance
   * - Deactivated users retain their history but cannot log in
   * - Use deletion only when required for data privacy (e.g., GDPR requests)
   *
   * @param {string} userId - The unique identifier of the user to update
   * @param {Object} data - The fields to update (only specified fields are changed)
   * @param {string} [data.role] - New role: 'admin', 'compliance_manager', 'auditor', or 'viewer'
   * @param {boolean} [data.active] - Account status (true = enabled, false = disabled)
   * @param {string} [data.firstName] - Updated first name
   * @param {string} [data.lastName] - Updated last name
   *
   * @returns {Promise<{success: boolean, user: any}>}
   *   - `success` - Whether the update completed successfully
   *   - `user` - The updated user object with all current fields
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If current user lacks admin permissions (403)
   * @throws {Error} If the user ID does not exist (404)
   * @throws {Error} If trying to demote the last admin (400)
   *
   * @example
   * // Promote a user to compliance manager
   * async function promoteUser(userId: string) {
   *   const { user } = await api.updateUser(userId, {
   *     role: 'compliance_manager',
   *   });
   *   console.log(`${user.email} is now a ${user.role}`);
   * }
   *
   * @example
   * // Deactivate a user account (preferred over deletion)
   * async function deactivateUser(userId: string) {
   *   const { user } = await api.updateUser(userId, { active: false });
   *   console.log(`Deactivated user: ${user.email}`);
   * }
   *
   * @example
   * // User edit form handler
   * function EditUserForm({ userId, currentUser }) {
   *   const handleSubmit = async (formData: FormData) => {
   *     try {
   *       const { user } = await api.updateUser(userId, {
   *         role: formData.get('role') as string,
   *         firstName: formData.get('firstName') as string,
   *         lastName: formData.get('lastName') as string,
   *       });
   *       toast.success(`Updated ${user.firstName} ${user.lastName}`);
   *     } catch (err) {
   *       if (err.message.includes('403')) {
   *         toast.error('You do not have permission to edit users');
   *       } else {
   *         toast.error('Failed to update user');
   *       }
   *     }
   *   };
   *
   *   return <form onSubmit={handleSubmit}>...</form>;
   * }
   *
   * @example
   * // Toggle user active status
   * async function toggleUserStatus(userId: string, currentlyActive: boolean) {
   *   const { user } = await api.updateUser(userId, {
   *     active: !currentlyActive,
   *   });
   *   return user.active;
   * }
   *
   * @see {@link getUsers} for listing all users
   * @see {@link createUser} for adding new users
   * @see {@link deleteUser} for permanently removing users
   */
  async updateUser(userId: string, data: {
    role?: string
    active?: boolean
    firstName?: string
    lastName?: string
    department?: string
    title?: string
  }) {
    return this.request<{
      success: boolean
      user: any
    }>(`/api/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a user from the organization.
   *
   * This method removes a user account and associated data from the system.
   * This action is irreversible. For audit compliance, consider deactivating
   * users instead of deleting them (use updateUser with active: false).
   *
   * **Deletion Considerations:**
   * - Cannot delete your own account
   * - Cannot delete the last admin user
   * - All user-specific data is removed or anonymized
   * - Audit log entries are preserved but anonymized
   * - Tasks assigned to deleted users become unassigned
   *
   * **When to Delete vs. Deactivate:**
   * - **Delete** - GDPR/CCPA data deletion requests, test accounts
   * - **Deactivate** - Former employees, suspended accounts, audit retention
   *
   * **Data Handling:**
   * - Evidence uploads remain but lose attribution
   * - Comments and notes are preserved with "[Deleted User]" attribution
   * - Activity history is retained for compliance
   *
   * @param {string} userId - The unique identifier of the user to delete
   *
   * @returns {Promise<{success: boolean}>}
   *   - `success` - Whether the user was deleted successfully
   *
   * @throws {Error} If the request fails due to network issues
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If current user lacks admin permissions (403)
   * @throws {Error} If the user ID does not exist (404)
   * @throws {Error} If trying to delete own account (400)
   * @throws {Error} If trying to delete the last admin (400)
   *
   * @example
   * // Delete a user with confirmation
   * async function deleteUserWithConfirmation(userId: string, userName: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete User',
   *     message: `Are you sure you want to permanently delete ${userName}? This cannot be undone.`,
   *     confirmLabel: 'Delete',
   *     destructive: true,
   *   });
   *
   *   if (confirmed) {
   *     await api.deleteUser(userId);
   *     toast.success(`User ${userName} has been deleted`);
   *   }
   * }
   *
   * @example
   * // User row actions in a table
   * function UserRow({ user, onDeleted }) {
   *   const handleDelete = async () => {
   *     try {
   *       await api.deleteUser(user.id);
   *       onDeleted(user.id);
   *     } catch (err) {
   *       if (err.message.includes('400')) {
   *         toast.error('Cannot delete this user');
   *       } else {
   *         toast.error('Failed to delete user');
   *       }
   *     }
   *   };
   *
   *   return (
   *     <tr>
   *       <td>{user.email}</td>
   *       <td>
   *         <button onClick={handleDelete} className="danger">
   *           Delete
   *         </button>
   *       </td>
   *     </tr>
   *   );
   * }
   *
   * @example
   * // GDPR deletion request handler
   * async function handleGDPRDeletionRequest(email: string) {
   *   const { users } = await api.getUsers();
   *   const user = users.find(u => u.email === email);
   *
   *   if (user) {
   *     await api.deleteUser(user.id);
   *     console.log(`GDPR deletion completed for ${email}`);
   *   } else {
   *     console.log(`No user found with email ${email}`);
   *   }
   * }
   *
   * @see {@link getUsers} for listing all users
   * @see {@link updateUser} for deactivating users (preferred alternative)
   * @see {@link createUser} for adding new users
   */
  async deleteUser(userId: string) {
    return this.request<{
      success: boolean
    }>(`/api/users/${userId}`, {
      method: 'DELETE',
    })
  }

  // ==================== Audit Log Endpoints ====================

  /**
   * Retrieves a paginated list of audit log entries for compliance and security tracking.
   *
   * This method provides a comprehensive audit trail of all significant actions taken
   * within the organization. Audit logs are essential for:
   * - **Compliance Requirements**: Meeting regulatory mandates (SOC 2, ISO 27001, HIPAA)
   *   that require tracking of user activities and system changes
   * - **Security Investigations**: Tracing unauthorized access attempts or data changes
   * - **Accountability**: Documenting who did what and when for internal governance
   *
   * **Audit Log Entry Types:**
   * Common `resource_type` values include:
   * - `'user'` - User account changes (creation, role updates, deactivation)
   * - `'control'` - Control modifications (status changes, policy linking)
   * - `'evidence'` - Evidence uploads, deletions, and reviews
   * - `'risk'` - Risk register changes (scoring, mitigation updates)
   * - `'document'` - Document creation, approval, and version changes
   * - `'integration'` - Integration syncs and configuration changes
   * - `'settings'` - Organization and user preference modifications
   *
   * **Common Actions:**
   * Typical `action` values include:
   * - `'create'` - New resource created
   * - `'update'` - Resource modified
   * - `'delete'` - Resource removed
   * - `'login'` / `'logout'` - Authentication events
   * - `'export'` - Data exported from the system
   * - `'approve'` / `'reject'` - Workflow state changes
   *
   * @param {Object} [params={}] - Query parameters to filter and paginate logs.
   * @param {string} [params.resource_type] - Filter by resource type (e.g., 'user', 'control', 'evidence').
   *   Use this to focus on a specific category of changes.
   * @param {string} [params.user_id] - Filter to logs created by a specific user (UUID).
   *   Useful for investigating a particular user's activities.
   * @param {string} [params.action] - Filter by action type (e.g., 'create', 'update', 'delete').
   *   Helps narrow down to specific types of changes.
   * @param {number} [params.start_date] - Unix timestamp (milliseconds) for the start of the date range.
   *   Only logs created on or after this time are returned.
   * @param {number} [params.end_date] - Unix timestamp (milliseconds) for the end of the date range.
   *   Only logs created before this time are returned.
   * @param {string} [params.search] - Free-text search term to filter logs by description,
   *   resource name, or other searchable fields.
   * @param {number} [params.limit=25] - Maximum number of log entries to return per page.
   * @param {number} [params.offset=0] - Number of log entries to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request completed successfully
   *   - `logs` {Array<Object>} - Array of audit log entries, each containing:
   *     - `id` {string} - Unique log entry identifier (UUID)
   *     - `tenant_id` {string} - Organization tenant ID
   *     - `user_id` {string} - ID of the user who performed the action
   *     - `user_email` {string} - Email of the user who performed the action
   *     - `user_name` {string} - Display name of the user (optional)
   *     - `action` {string} - The type of action performed
   *     - `resource_type` {string} - Category of resource affected
   *     - `resource_id` {string} - ID of the affected resource (optional)
   *     - `resource_name` {string} - Human-readable name of the resource (optional)
   *     - `description` {string} - Human-readable description of the action
   *     - `details` {Object} - Additional context about the change (JSON, optional)
   *     - `ip_address` {string} - IP address of the client (optional)
   *     - `user_agent` {string} - Browser/client user agent string (optional)
   *     - `created_at` {number} - Unix timestamp when the action occurred
   *   - `pagination` {Object} - Pagination metadata:
   *     - `total` {number} - Total number of matching log entries
   *     - `limit` {number} - Current page size limit
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether additional pages exist
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to view audit logs (403)
   *   (typically requires admin or compliance_manager role)
   *
   * @example
   * // Load recent audit activity for the dashboard
   * async function loadRecentActivity() {
   *   const { logs } = await api.getAuditLogs({
   *     limit: 10
   *   });
   *
   *   return logs.map(log => ({
   *     id: log.id,
   *     description: log.description,
   *     user: log.user_email,
   *     time: new Date(log.created_at)
   *   }));
   * }
   *
   * @example
   * // Investigate a specific user's activities over the past week
   * async function investigateUser(userId: string) {
   *   const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
   *
   *   const { logs, pagination } = await api.getAuditLogs({
   *     user_id: userId,
   *     start_date: oneWeekAgo,
   *     limit: 100
   *   });
   *
   *   console.log(`Found ${pagination.total} activities for user in past week`);
   *   return logs;
   * }
   *
   * @example
   * // Audit log viewer component with filtering and pagination
   * function AuditLogViewer() {
   *   const [logs, setLogs] = useState([]);
   *   const [filters, setFilters] = useState({});
   *   const [page, setPage] = useState(0);
   *   const [hasMore, setHasMore] = useState(false);
   *   const pageSize = 25;
   *
   *   const loadLogs = async () => {
   *     const { logs, pagination } = await api.getAuditLogs({
   *       ...filters,
   *       limit: pageSize,
   *       offset: page * pageSize
   *     });
   *     setLogs(logs);
   *     setHasMore(pagination.hasMore);
   *   };
   *
   *   useEffect(() => { loadLogs(); }, [filters, page]);
   *
   *   return (
   *     <div>
   *       <AuditFilters onChange={setFilters} />
   *       <table>
   *         <thead>
   *           <tr>
   *             <th>Time</th>
   *             <th>User</th>
   *             <th>Action</th>
   *             <th>Description</th>
   *           </tr>
   *         </thead>
   *         <tbody>
   *           {logs.map(log => (
   *             <tr key={log.id}>
   *               <td>{new Date(log.created_at).toLocaleString()}</td>
   *               <td>{log.user_email}</td>
   *               <td>{log.action}</td>
   *               <td>{log.description}</td>
   *             </tr>
   *           ))}
   *         </tbody>
   *       </table>
   *       <Pagination page={page} hasMore={hasMore} onPageChange={setPage} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Generate compliance report for specific resource type
   * async function generateControlChangeReport(startDate: Date, endDate: Date) {
   *   const allLogs = [];
   *   let offset = 0;
   *   let hasMore = true;
   *
   *   while (hasMore) {
   *     const { logs, pagination } = await api.getAuditLogs({
   *       resource_type: 'control',
   *       start_date: startDate.getTime(),
   *       end_date: endDate.getTime(),
   *       limit: 100,
   *       offset
   *     });
   *     allLogs.push(...logs);
   *     hasMore = pagination.hasMore;
   *     offset += 100;
   *   }
   *
   *   console.log(`Total control changes: ${allLogs.length}`);
   *   return allLogs;
   * }
   *
   * @see {@link getAuditStats} for aggregate audit statistics
   * @see {@link getUsers} for user information referenced in logs
   * @see {@link getDashboardActivity} for simplified recent activity
   */
  async getAuditLogs(params: {
    resource_type?: string
    user_id?: string
    action?: string
    start_date?: number
    end_date?: number
    search?: string
    limit?: number
    offset?: number
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      logs: any[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/audit?${searchParams}`)
  }

  /**
   * Retrieves aggregate statistics about audit log activity for dashboards and reports.
   *
   * This method provides high-level metrics about system activity, useful for:
   * - **Dashboard Widgets**: Displaying activity summaries at a glance
   * - **Trend Analysis**: Identifying patterns in user behavior over time
   * - **Compliance Monitoring**: Tracking audit coverage and activity levels
   * - **Security Dashboards**: Monitoring for unusual activity patterns
   *
   * **Typical Statistics Returned:**
   * The stats object commonly includes:
   * - Total log count over various time periods (today, this week, this month)
   * - Activity breakdown by resource type (controls, evidence, users, etc.)
   * - Activity breakdown by action type (creates, updates, deletes)
   * - Most active users and their activity counts
   * - Activity trends over time (daily/weekly/monthly)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request completed successfully
   *   - `stats` {Object} - Aggregate audit statistics:
   *     - `total_logs` {number} - Total number of audit log entries
   *     - `logs_today` {number} - Number of logs created today
   *     - `logs_this_week` {number} - Number of logs created this week
   *     - `logs_this_month` {number} - Number of logs created this month
   *     - `by_resource_type` {Object} - Count of logs per resource type
   *     - `by_action` {Object} - Count of logs per action type
   *     - `top_users` {Array<{user_id: string, user_email: string, count: number}>} -
   *       Most active users ranked by log count
   *     - `activity_trend` {Array<{date: string, count: number}>} -
   *       Daily activity counts for trend visualization
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to view audit stats (403)
   *   (typically requires admin or compliance_manager role)
   *
   * @example
   * // Display audit activity summary on dashboard
   * async function loadAuditSummary() {
   *   const { stats } = await api.getAuditStats();
   *
   *   return {
   *     todayActivity: stats.logs_today,
   *     weekActivity: stats.logs_this_week,
   *     totalLogs: stats.total_logs
   *   };
   * }
   *
   * @example
   * // Audit activity dashboard widget
   * function AuditActivityWidget() {
   *   const [stats, setStats] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getAuditStats()
   *       .then(({ stats }) => setStats(stats))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div className="audit-widget">
   *       <h3>System Activity</h3>
   *       <div className="stat-grid">
   *         <StatCard label="Today" value={stats.logs_today} />
   *         <StatCard label="This Week" value={stats.logs_this_week} />
   *         <StatCard label="This Month" value={stats.logs_this_month} />
   *       </div>
   *       <ActivityChart data={stats.activity_trend} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Analyze activity distribution by resource type
   * async function analyzeActivityDistribution() {
   *   const { stats } = await api.getAuditStats();
   *
   *   const distribution = Object.entries(stats.by_resource_type)
   *     .map(([type, count]) => ({
   *       type,
   *       count,
   *       percentage: ((count / stats.total_logs) * 100).toFixed(1)
   *     }))
   *     .sort((a, b) => b.count - a.count);
   *
   *   console.log('Activity by resource type:');
   *   distribution.forEach(d =>
   *     console.log(`  ${d.type}: ${d.count} (${d.percentage}%)`)
   *   );
   *
   *   return distribution;
   * }
   *
   * @see {@link getAuditLogs} for detailed audit trail with filtering
   * @see {@link getDashboardOverview} for general dashboard metrics
   * @see {@link getDashboardActivity} for recent activity feed
   */
  async getAuditStats() {
    return this.request<{
      success: boolean
      stats: any
    }>('/api/audit/stats')
  }

  /**
   * Exports audit logs as a CSV file for compliance reporting and archival.
   *
   * This method triggers a download of audit log entries in CSV format.
   * Filters can be applied to export a specific date range or subset of logs.
   * Admin-only endpoint for security and compliance purposes.
   *
   * @param {Object} [params={}] - Optional filter parameters for the export
   * @param {number} [params.start_date] - Start timestamp (epoch milliseconds)
   * @param {number} [params.end_date] - End timestamp (epoch milliseconds)
   *
   * @returns {Promise<Blob>} The CSV file content as a Blob
   *
   * @throws {Error} If the export fails or user lacks admin permissions
   */
  async exportAuditLogs(params: {
    start_date?: number
    end_date?: number
  } = {}): Promise<Blob> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/audit/export?${searchParams}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new Error('Failed to export audit logs')
    }

    return response.blob()
  }

  // Risk endpoints

  /**
   * Retrieves the risk register with optional filtering, searching, and pagination.
   *
   * The risk register is a core GRC component for identifying, assessing, and tracking
   * organizational risks. Each risk entry includes likelihood and impact scores that
   * calculate an overall risk score (typically likelihood × impact on a 1-5 scale).
   *
   * **Common Use Cases:**
   * - **Risk Dashboard**: Display all tracked risks sorted by score or status
   * - **Risk Heat Maps**: Visualize risks by likelihood vs impact matrix
   * - **Owner Assignment**: Filter risks by owner for responsibility tracking
   * - **Category Management**: Group risks by operational, financial, compliance, etc.
   * - **Risk Prioritization**: Filter by minimum score to focus on critical risks
   *
   * **Risk Categories (Common):**
   * - `operational` - Day-to-day operational failures
   * - `financial` - Financial losses, fraud, budget issues
   * - `compliance` - Regulatory and legal compliance failures
   * - `reputational` - Brand damage, customer trust issues
   * - `strategic` - Business strategy and competitive risks
   * - `technology` - IT systems, cybersecurity, data risks
   * - `third_party` - Vendor and supply chain risks
   *
   * **Risk Statuses:**
   * - `identified` - Newly identified, pending assessment
   * - `assessed` - Likelihood and impact evaluated
   * - `mitigating` - Controls being implemented
   * - `accepted` - Risk accepted by management
   * - `closed` - Risk no longer applicable
   *
   * @param {Object} [params={}] - Optional filter and pagination parameters
   * @param {string} [params.status] - Filter by risk status (identified, assessed, mitigating, accepted, closed)
   * @param {string} [params.category] - Filter by risk category (operational, financial, compliance, etc.)
   * @param {string} [params.owner_id] - Filter by risk owner's user ID
   * @param {number} [params.min_score] - Filter to show only risks with score >= this value (1-25 range)
   * @param {string} [params.search] - Search term to filter by title or description
   * @param {number} [params.limit] - Maximum number of risks to return (default: 50)
   * @param {number} [params.offset] - Number of risks to skip for pagination (default: 0)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request completed successfully
   *   - `risks` {Array<Object>} - Array of risk entries, each containing:
   *     - `id` {string} - Unique risk identifier
   *     - `title` {string} - Brief risk title/name
   *     - `description` {string} - Detailed risk description
   *     - `category` {string} - Risk category classification
   *     - `likelihood` {number} - Inherent likelihood score (1-5)
   *     - `impact` {number} - Inherent impact score (1-5)
   *     - `inherent_score` {number} - Calculated inherent risk score (likelihood × impact)
   *     - `residual_likelihood` {number} - Likelihood after controls (1-5)
   *     - `residual_impact` {number} - Impact after controls (1-5)
   *     - `residual_score` {number} - Calculated residual risk score
   *     - `status` {string} - Current risk status
   *     - `owner_id` {string} - User ID of risk owner
   *     - `owner_name` {string} - Display name of risk owner
   *     - `mitigation_plan` {string} - Description of mitigation strategy
   *     - `due_date` {number} - Target date for mitigation (Unix timestamp ms)
   *     - `linked_controls` {number} - Count of controls linked to this risk
   *     - `created_at` {string} - ISO timestamp of creation
   *     - `updated_at` {string} - ISO timestamp of last update
   *   - `pagination` {Object} - Pagination metadata:
   *     - `total` {number} - Total number of risks matching filters
   *     - `limit` {number} - Page size used
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether more pages exist
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to view risks (403)
   *
   * @example
   * // Load all risks for risk register view
   * const { risks, pagination } = await api.getRisks();
   * console.log(`Displaying ${risks.length} of ${pagination.total} risks`);
   *
   * @example
   * // Filter high-priority risks for executive dashboard
   * async function loadCriticalRisks() {
   *   const { risks } = await api.getRisks({
   *     min_score: 15,  // High risk (likelihood × impact >= 15)
   *     status: 'mitigating'
   *   });
   *   return risks.sort((a, b) => b.inherent_score - a.inherent_score);
   * }
   *
   * @example
   * // Risk register component with filtering and pagination
   * function RiskRegister() {
   *   const [risks, setRisks] = useState([]);
   *   const [filters, setFilters] = useState({});
   *   const [page, setPage] = useState(0);
   *   const pageSize = 25;
   *
   *   const loadRisks = async () => {
   *     const { risks, pagination } = await api.getRisks({
   *       ...filters,
   *       limit: pageSize,
   *       offset: page * pageSize
   *     });
   *     setRisks(risks);
   *   };
   *
   *   return (
   *     <div>
   *       <RiskFilters onChange={setFilters} />
   *       <RiskTable risks={risks} />
   *       <Pagination page={page} onPageChange={setPage} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Build risk heat map data (likelihood x impact matrix)
   * async function buildHeatMapData() {
   *   const { risks } = await api.getRisks({ limit: 1000 });
   *
   *   // Create 5x5 matrix
   *   const heatMap = Array(5).fill(null).map(() =>
   *     Array(5).fill(null).map(() => [])
   *   );
   *
   *   risks.forEach(risk => {
   *     const row = risk.likelihood - 1;  // 0-indexed
   *     const col = risk.impact - 1;
   *     heatMap[row][col].push(risk);
   *   });
   *
   *   return heatMap;
   * }
   *
   * @see {@link getRisk} for retrieving a single risk with full details
   * @see {@link getRiskStats} for aggregate risk statistics
   * @see {@link createRisk} for adding new risks
   * @see {@link linkControlsToRisk} for associating controls with risks
   */
  async getRisks(params: {
    status?: string
    category?: string
    owner_id?: string
    min_score?: number
    severity?: string // 'critical' or 'high' for impact-based filtering
    search?: string
    limit?: number
    offset?: number
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      risks: any[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/risks?${searchParams}`)
  }

  /**
   * Retrieves a single risk entry by its unique identifier with full details.
   *
   * This method returns comprehensive information about a specific risk, including
   * its inherent and residual risk scores, linked controls, mitigation plans, and
   * audit history. Use this for risk detail views and editing forms.
   *
   * **Risk Scoring Model:**
   * - **Inherent Risk**: Raw risk before controls (likelihood × impact)
   * - **Residual Risk**: Risk after controls applied (residual_likelihood × residual_impact)
   * - **Score Range**: 1-25 (1-5 × 1-5 matrix)
   * - **Risk Levels**: Low (1-5), Medium (6-10), High (11-15), Critical (16-25)
   *
   * **Linked Controls:**
   * The risk response includes controls that have been linked to mitigate this risk.
   * These control associations help track how risks are being addressed and
   * contribute to the residual risk calculation.
   *
   * @param {string} id - The unique identifier of the risk to retrieve
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request completed successfully
   *   - `risk` {Object} - Complete risk details:
   *     - `id` {string} - Unique risk identifier
   *     - `title` {string} - Brief risk title/name
   *     - `description` {string} - Detailed risk description
   *     - `category` {string} - Risk category (operational, financial, compliance, etc.)
   *     - `likelihood` {number} - Inherent likelihood score (1-5)
   *     - `impact` {number} - Inherent impact score (1-5)
   *     - `inherent_score` {number} - Calculated inherent risk score
   *     - `residual_likelihood` {number} - Likelihood after controls (1-5)
   *     - `residual_impact` {number} - Impact after controls (1-5)
   *     - `residual_score` {number} - Calculated residual risk score
   *     - `status` {string} - Current status (identified, assessed, mitigating, accepted, closed)
   *     - `owner_id` {string} - User ID of risk owner
   *     - `owner` {Object} - Risk owner details (id, name, email)
   *     - `mitigation_plan` {string} - Description of mitigation strategy
   *     - `due_date` {number} - Target date for mitigation (Unix timestamp ms)
   *     - `linked_controls` {Array<Object>} - Controls linked to this risk:
   *       - `id` {string} - Control ID
   *       - `title` {string} - Control title
   *       - `status` {string} - Control implementation status
   *     - `created_at` {string} - ISO timestamp of creation
   *     - `updated_at` {string} - ISO timestamp of last update
   *     - `created_by` {string} - User ID who created the risk
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to view this risk (403)
   * @throws {Error} Throws if risk with given ID does not exist (404)
   *
   * @example
   * // Load risk details for view page
   * async function loadRiskDetails(riskId: string) {
   *   const { risk } = await api.getRisk(riskId);
   *
   *   console.log(`Risk: ${risk.title}`);
   *   console.log(`Inherent Score: ${risk.inherent_score}/25`);
   *   console.log(`Residual Score: ${risk.residual_score}/25`);
   *   console.log(`Linked Controls: ${risk.linked_controls.length}`);
   *
   *   return risk;
   * }
   *
   * @example
   * // Risk detail page with edit functionality
   * function RiskDetailPage({ riskId }) {
   *   const [risk, setRisk] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getRisk(riskId)
   *       .then(({ risk }) => setRisk(risk))
   *       .catch(err => {
   *         if (err.status === 404) {
   *           navigate('/risks');  // Risk not found
   *         }
   *       })
   *       .finally(() => setLoading(false));
   *   }, [riskId]);
   *
   *   if (loading) return <Spinner />;
   *   if (!risk) return null;
   *
   *   return (
   *     <div>
   *       <h1>{risk.title}</h1>
   *       <RiskScoreMatrix
   *         inherent={{ likelihood: risk.likelihood, impact: risk.impact }}
   *         residual={{ likelihood: risk.residual_likelihood, impact: risk.residual_impact }}
   *       />
   *       <RiskDetails risk={risk} />
   *       <LinkedControlsList controls={risk.linked_controls} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Pre-populate edit form with risk data
   * async function openRiskEditForm(riskId: string) {
   *   const { risk } = await api.getRisk(riskId);
   *
   *   return {
   *     title: risk.title,
   *     description: risk.description,
   *     category: risk.category,
   *     likelihood: risk.likelihood,
   *     impact: risk.impact,
   *     residual_likelihood: risk.residual_likelihood,
   *     residual_impact: risk.residual_impact,
   *     status: risk.status,
   *     owner_id: risk.owner_id,
   *     mitigation_plan: risk.mitigation_plan,
   *     due_date: risk.due_date
   *   };
   * }
   *
   * @see {@link getRisks} for listing all risks with filtering
   * @see {@link updateRisk} for modifying risk details
   * @see {@link linkControlsToRisk} for associating controls with this risk
   */
  async getRisk(id: string) {
    return this.request<{
      success: boolean
      risk: any
    }>(`/api/risks/${id}`)
  }

  /**
   * Retrieves aggregate statistics about risks for dashboards and executive reporting.
   *
   * This method provides high-level metrics about the organization's risk posture,
   * useful for dashboards, trend analysis, and compliance reporting. The statistics
   * include breakdowns by risk level, category, status, and trend data over time.
   *
   * **Common Use Cases:**
   * - **Executive Dashboard**: Display risk posture at a glance
   * - **Risk Heat Map Summary**: Show distribution across risk levels
   * - **Trend Analysis**: Track risk evolution over time
   * - **Compliance Reports**: Demonstrate risk management effectiveness
   * - **Board Presentations**: High-level risk overview
   *
   * **Risk Level Classification:**
   * - `critical` - Score 16-25 (requires immediate attention)
   * - `high` - Score 11-15 (requires prompt action)
   * - `medium` - Score 6-10 (monitor and plan mitigation)
   * - `low` - Score 1-5 (acceptable risk level)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request completed successfully
   *   - `stats` {Object} - Aggregate risk statistics:
   *     - `total_risks` {number} - Total number of risks in register
   *     - `by_level` {Object} - Count of risks by risk level:
   *       - `critical` {number} - Count of critical risks (score 16-25)
   *       - `high` {number} - Count of high risks (score 11-15)
   *       - `medium` {number} - Count of medium risks (score 6-10)
   *       - `low` {number} - Count of low risks (score 1-5)
   *     - `by_category` {Object} - Count of risks per category:
   *       - `operational` {number}, `financial` {number}, `compliance` {number}, etc.
   *     - `by_status` {Object} - Count of risks per status:
   *       - `identified` {number}, `assessed` {number}, `mitigating` {number},
   *         `accepted` {number}, `closed` {number}
   *     - `average_inherent_score` {number} - Mean inherent risk score
   *     - `average_residual_score` {number} - Mean residual risk score
   *     - `risk_reduction` {number} - Percentage reduction from inherent to residual
   *     - `overdue_mitigations` {number} - Risks past their mitigation due date
   *     - `unassigned_risks` {number} - Risks without an owner
   *     - `trend` {Array<{date: string, total: number, average_score: number}>} -
   *       Historical risk data for trend visualization
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to view risk stats (403)
   *
   * @example
   * // Load risk stats for dashboard overview
   * async function loadRiskOverview() {
   *   const { stats } = await api.getRiskStats();
   *
   *   return {
   *     totalRisks: stats.total_risks,
   *     criticalCount: stats.by_level.critical,
   *     highCount: stats.by_level.high,
   *     riskReduction: `${stats.risk_reduction}%`
   *   };
   * }
   *
   * @example
   * // Risk dashboard widget with level breakdown
   * function RiskOverviewWidget() {
   *   const [stats, setStats] = useState(null);
   *
   *   useEffect(() => {
   *     api.getRiskStats()
   *       .then(({ stats }) => setStats(stats));
   *   }, []);
   *
   *   if (!stats) return <Spinner />;
   *
   *   return (
   *     <div className="risk-overview">
   *       <h3>Risk Posture</h3>
   *       <div className="risk-levels">
   *         <RiskBadge level="critical" count={stats.by_level.critical} />
   *         <RiskBadge level="high" count={stats.by_level.high} />
   *         <RiskBadge level="medium" count={stats.by_level.medium} />
   *         <RiskBadge level="low" count={stats.by_level.low} />
   *       </div>
   *       <div className="risk-reduction">
   *         <ProgressBar
   *           label="Risk Reduction"
   *           value={stats.risk_reduction}
   *           max={100}
   *         />
   *         <span>Avg Inherent: {stats.average_inherent_score.toFixed(1)}</span>
   *         <span>Avg Residual: {stats.average_residual_score.toFixed(1)}</span>
   *       </div>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Risk trend chart component
   * function RiskTrendChart() {
   *   const [trendData, setTrendData] = useState([]);
   *
   *   useEffect(() => {
   *     api.getRiskStats().then(({ stats }) => {
   *       setTrendData(stats.trend.map(point => ({
   *         date: new Date(point.date),
   *         total: point.total,
   *         avgScore: point.average_score
   *       })));
   *     });
   *   }, []);
   *
   *   return <LineChart data={trendData} xKey="date" yKeys={['total', 'avgScore']} />;
   * }
   *
   * @example
   * // Risk distribution by category for pie chart
   * async function getRiskDistributionByCategory() {
   *   const { stats } = await api.getRiskStats();
   *
   *   return Object.entries(stats.by_category).map(([category, count]) => ({
   *     name: category.charAt(0).toUpperCase() + category.slice(1),
   *     value: count,
   *     percentage: ((count / stats.total_risks) * 100).toFixed(1)
   *   }));
   * }
   *
   * @see {@link getRisks} for detailed risk listing with filtering
   * @see {@link getDashboardOverview} for general dashboard metrics
   * @see {@link getComplianceSummary} for compliance-focused reporting
   */
  async getRiskStats() {
    return this.request<{
      success: boolean
      stats: any
    }>('/api/risks/stats')
  }

  /**
   * Creates a new risk entry in the risk register.
   *
   * This method adds a new risk to the organization's risk register with its
   * initial assessment. The inherent risk score is calculated automatically
   * from the provided likelihood and impact values (score = likelihood × impact).
   *
   * **Risk Assessment Process:**
   * 1. Identify the risk and provide a clear title/description
   * 2. Assess inherent likelihood (1-5): How likely is this to occur?
   * 3. Assess inherent impact (1-5): How severe would the impact be?
   * 4. Assign an owner responsible for managing the risk
   * 5. Document mitigation plans and set target completion dates
   * 6. Link controls that will reduce the residual risk
   *
   * **Likelihood Scale (1-5):**
   * - 1: Rare - May occur only in exceptional circumstances
   * - 2: Unlikely - Could occur at some time
   * - 3: Possible - Might occur at some time
   * - 4: Likely - Will probably occur in most circumstances
   * - 5: Almost Certain - Expected to occur in most circumstances
   *
   * **Impact Scale (1-5):**
   * - 1: Insignificant - Minimal impact, easily managed
   * - 2: Minor - Some impact, manageable with normal processes
   * - 3: Moderate - Significant impact, requires management attention
   * - 4: Major - Serious impact, significant resources needed
   * - 5: Catastrophic - Critical impact, threatens organizational viability
   *
   * @param {Object} data - Risk data for the new entry
   * @param {string} data.title - Brief, descriptive title for the risk (required)
   * @param {string} [data.description] - Detailed description of the risk scenario
   * @param {string} [data.category] - Risk category (operational, financial, compliance,
   *   reputational, strategic, technology, third_party)
   * @param {number} data.likelihood - Inherent likelihood score 1-5 (required)
   * @param {number} data.impact - Inherent impact score 1-5 (required)
   * @param {string} [data.status] - Initial status (defaults to 'identified')
   * @param {string} [data.owner_id] - User ID of the risk owner
   * @param {string} [data.mitigation_plan] - Description of planned mitigation actions
   * @param {number} [data.due_date] - Target date for mitigation completion (Unix timestamp ms)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the risk was created successfully
   *   - `risk` {Object} - The newly created risk entry with all fields including:
   *     - `id` {string} - Unique identifier for the new risk
   *     - `inherent_score` {number} - Calculated score (likelihood × impact)
   *     - `residual_likelihood` {number} - Initially equals inherent likelihood
   *     - `residual_impact` {number} - Initially equals inherent impact
   *     - `residual_score` {number} - Initially equals inherent score
   *     - `created_at` {string} - ISO timestamp of creation
   *     - All input fields are returned
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to create risks (403)
   * @throws {Error} Throws if validation fails (400) - e.g., missing required fields,
   *   likelihood/impact out of range, invalid category or status
   *
   * @example
   * // Create a basic risk entry
   * const { risk } = await api.createRisk({
   *   title: 'Data Breach via Phishing Attack',
   *   description: 'Employees may fall victim to phishing emails leading to credential theft',
   *   category: 'technology',
   *   likelihood: 4,  // Likely
   *   impact: 5       // Catastrophic
   * });
   * console.log(`Created risk with score: ${risk.inherent_score}/25`);
   *
   * @example
   * // Risk creation form submission handler
   * async function handleRiskFormSubmit(formData) {
   *   try {
   *     const { risk } = await api.createRisk({
   *       title: formData.title,
   *       description: formData.description,
   *       category: formData.category,
   *       likelihood: parseInt(formData.likelihood),
   *       impact: parseInt(formData.impact),
   *       owner_id: formData.ownerId,
   *       mitigation_plan: formData.mitigationPlan,
   *       due_date: formData.dueDate ? new Date(formData.dueDate).getTime() : undefined
   *     });
   *
   *     toast.success(`Risk "${risk.title}" created successfully`);
   *     navigate(`/risks/${risk.id}`);
   *   } catch (error) {
   *     if (error.status === 400) {
   *       toast.error('Please fill in all required fields');
   *     } else {
   *       toast.error('Failed to create risk');
   *     }
   *   }
   * }
   *
   * @example
   * // Create risk with full details including mitigation plan
   * async function createComprehensiveRisk() {
   *   const oneMonthFromNow = Date.now() + 30 * 24 * 60 * 60 * 1000;
   *
   *   const { risk } = await api.createRisk({
   *     title: 'Third-party Vendor Data Handling',
   *     description: 'Vendors handling customer data may not comply with our security policies, leading to potential data exposure or regulatory violations.',
   *     category: 'third_party',
   *     likelihood: 3,  // Possible
   *     impact: 4,      // Major
   *     status: 'assessed',
   *     owner_id: 'user-security-manager-id',
   *     mitigation_plan: '1. Implement vendor security assessment program\n2. Require SOC 2 compliance\n3. Add data handling clauses to contracts\n4. Conduct quarterly vendor reviews',
   *     due_date: oneMonthFromNow
   *   });
   *
   *   // Link relevant controls to the risk
   *   await api.linkControlsToRisk(risk.id, [
   *     'control-vendor-assessment-id',
   *     'control-data-classification-id'
   *   ]);
   *
   *   return risk;
   * }
   *
   * @see {@link getRisks} for listing all risks
   * @see {@link updateRisk} for modifying risk details and residual scores
   * @see {@link linkControlsToRisk} for associating controls with the new risk
   * @see {@link getUsers} for retrieving potential risk owners
   */
  async createRisk(data: {
    title: string
    description?: string
    category?: string
    category_id?: string // New: FK to risk_categories (Issue #100)
    likelihood: number
    impact: number
    residual_likelihood?: number
    residual_impact?: number
    control_effectiveness_rating?: number
    status?: string
    owner_id?: string
    mitigation_plan?: string
    due_date?: number
    business_unit?: string // Issue #153: Risk aggregation
    department?: string // Issue #153: Risk aggregation
    custom_fields?: Record<string, any>
  }) {
    return this.request<{
      success: boolean
      risk: any
    }>('/api/risks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing risk entry in the risk register.
   *
   * This method allows partial updates to any risk field, including updating
   * the residual risk scores after controls have been implemented. The residual
   * score reflects the risk level after mitigation measures are in place.
   *
   * **Key Update Scenarios:**
   * - **Reassessment**: Update likelihood/impact after new information
   * - **Residual Update**: Set residual_likelihood/residual_impact after controls are implemented
   * - **Status Change**: Move risk through lifecycle (identified → assessed → mitigating → accepted/closed)
   * - **Owner Change**: Reassign responsibility to different team member
   * - **Mitigation Progress**: Update mitigation plan as actions are completed
   *
   * **Residual Risk Assessment:**
   * After linking controls to a risk, update the residual scores to reflect
   * the expected risk reduction. The residual_score is recalculated automatically
   * (residual_likelihood × residual_impact).
   *
   * **Example Risk Reduction:**
   * - Inherent: likelihood=4, impact=5 → score=20 (Critical)
   * - After controls: residual_likelihood=2, residual_impact=3 → residual_score=6 (Medium)
   * - Risk reduction: 70% (from 20 to 6)
   *
   * @param {string} id - The unique identifier of the risk to update
   * @param {Object} data - Partial risk data to update (only provided fields are changed)
   * @param {string} [data.title] - Updated risk title
   * @param {string} [data.description] - Updated risk description
   * @param {string} [data.category] - Updated category (operational, financial, etc.)
   * @param {number} [data.likelihood] - Updated inherent likelihood (1-5)
   * @param {number} [data.impact] - Updated inherent impact (1-5)
   * @param {number} [data.residual_likelihood] - Residual likelihood after controls (1-5)
   * @param {number} [data.residual_impact] - Residual impact after controls (1-5)
   * @param {string} [data.status] - Updated status (identified, assessed, mitigating, accepted, closed)
   * @param {string} [data.owner_id] - Updated owner user ID
   * @param {string} [data.mitigation_plan] - Updated mitigation plan description
   * @param {number} [data.due_date] - Updated target date (Unix timestamp ms)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the update was successful
   *   - `message` {string} - Confirmation message (e.g., "Risk updated successfully")
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to update risks (403)
   * @throws {Error} Throws if risk with given ID does not exist (404)
   * @throws {Error} Throws if validation fails (400) - e.g., invalid score values
   *
   * @example
   * // Update residual risk after implementing controls
   * async function updateResidualRisk(riskId: string, residualLikelihood: number, residualImpact: number) {
   *   await api.updateRisk(riskId, {
   *     residual_likelihood: residualLikelihood,
   *     residual_impact: residualImpact,
   *     status: 'mitigating'
   *   });
   * }
   *
   * @example
   * // Risk edit form submission handler
   * async function handleRiskEditSubmit(riskId: string, formData: RiskFormData) {
   *   try {
   *     await api.updateRisk(riskId, {
   *       title: formData.title,
   *       description: formData.description,
   *       category: formData.category,
   *       likelihood: formData.likelihood,
   *       impact: formData.impact,
   *       residual_likelihood: formData.residualLikelihood,
   *       residual_impact: formData.residualImpact,
   *       status: formData.status,
   *       owner_id: formData.ownerId,
   *       mitigation_plan: formData.mitigationPlan,
   *       due_date: formData.dueDate?.getTime()
   *     });
   *
   *     toast.success('Risk updated successfully');
   *     navigate(`/risks/${riskId}`);
   *   } catch (error) {
   *     toast.error('Failed to update risk');
   *   }
   * }
   *
   * @example
   * // Accept a risk (management decision to accept without further mitigation)
   * async function acceptRisk(riskId: string, acceptanceRationale: string) {
   *   await api.updateRisk(riskId, {
   *     status: 'accepted',
   *     mitigation_plan: `ACCEPTED: ${acceptanceRationale}`
   *   });
   * }
   *
   * @example
   * // Close a risk that is no longer applicable
   * async function closeRisk(riskId: string, closureReason: string) {
   *   await api.updateRisk(riskId, {
   *     status: 'closed',
   *     mitigation_plan: `CLOSED: ${closureReason}`
   *   });
   * }
   *
   * @example
   * // Bulk update residual scores after control implementation project
   * async function updateRisksAfterControlImplementation(
   *   riskIds: string[],
   *   residualLikelihood: number,
   *   residualImpact: number
   * ) {
   *   await Promise.all(
   *     riskIds.map(id =>
   *       api.updateRisk(id, {
   *         residual_likelihood: residualLikelihood,
   *         residual_impact: residualImpact,
   *         status: 'mitigating'
   *       })
   *     )
   *   );
   * }
   *
   * @see {@link getRisk} for retrieving current risk details before editing
   * @see {@link createRisk} for adding new risks
   * @see {@link linkControlsToRisk} for associating controls that reduce residual risk
   * @see {@link deleteRisk} for removing risks
   */
  async updateRisk(id: string, data: Partial<{
    title: string
    description: string
    category: string
    category_id: string | null // New: FK to risk_categories (Issue #100)
    likelihood: number
    impact: number
    residual_likelihood: number
    residual_impact: number
    control_effectiveness_rating: number | null
    status: string
    owner_id: string
    mitigation_plan: string
    due_date: number
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // Risk Review Methods (Issue #70)
  // ============================================================================

  /**
   * Get risks due for review within the next 7 days or overdue.
   */
  async getRisksDueForReview() {
    return this.request<{
      success: boolean
      risks: Array<{
        id: string
        title: string
        status: string
        risk_score: number
        residual_risk_score: number
        review_frequency: string
        last_review_date: number | null
        next_review_date: number
        owner_id: string | null
        owner_username: string | null
        review_status: 'overdue' | 'due_soon'
      }>
    }>('/api/risks/due-for-review')
  }

  /**
   * Record a risk review with before/after snapshots.
   */
  async recordRiskReview(riskId: string, data: {
    likelihood?: number
    impact?: number
    residual_likelihood?: number
    residual_impact?: number
    status?: string
    notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      review: {
        id: string
        outcome: string
        next_review_date: number | null
      }
    }>(`/api/risks/${riskId}/review`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get review history for a specific risk.
   */
  async getRiskReviews(riskId: string) {
    return this.request<{
      success: boolean
      reviews: Array<{
        id: string
        reviewed_at: number
        reviewed_by: string
        reviewed_by_username: string
        reviewed_by_email: string
        reviewed_by_name: string
        likelihood_before: number | null
        impact_before: number | null
        residual_likelihood_before: number | null
        residual_impact_before: number | null
        status_before: string | null
        likelihood_after: number | null
        impact_after: number | null
        residual_likelihood_after: number | null
        residual_impact_after: number | null
        status_after: string | null
        outcome: string
        notes: string | null
        changes: Record<string, { old: any; new: any }> | null
      }>
      total: number
    }>(`/api/risks/${riskId}/reviews`)
  }

  /**
   * Retrieves risk reduction summary metrics for the dashboard.
   *
   * This method provides aggregate statistics on how effectively controls
   * and mitigations have reduced organizational risk. It calculates reduction
   * percentages comparing inherent (before controls) vs residual (after controls)
   * risk scores.
   *
   * **Reduction Calculation:**
   * - `reduction_percentage = ((inherent_score - residual_score) / inherent_score) * 100`
   * - Example: inherent=20, residual=6 -> 70% reduction
   *
   * **Reduction Categories:**
   * - Significant: >50% reduction (green indicator)
   * - Moderate: 25-50% reduction (yellow indicator)
   * - Low: <25% reduction (red indicator)
   *
   * @returns {Promise<Object>} Response containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `summary` {Object} - Aggregate risk reduction metrics:
   *     - `total_risks` {number} - Total risks in register
   *     - `active_risks` {number} - Risks not closed/accepted
   *     - `risks_with_reduction_data` {number} - Risks with residual scores set
   *     - `total_inherent_score` {number} - Sum of all inherent risk scores
   *     - `total_residual_score` {number} - Sum of all residual risk scores
   *     - `total_risk_reduced` {number} - Total points reduced
   *     - `overall_reduction_percentage` {number} - Overall reduction percentage
   *     - `average_effectiveness_rating` {number|null} - Average control effectiveness (1-5)
   *     - `reduction_distribution` {Object} - Count of risks by reduction level
   *   - `risks` {Array} - Individual risk reduction details
   *
   * @example
   * // Display risk reduction dashboard card
   * const { summary, risks } = await api.getRiskReductionSummary();
   * console.log(`Overall risk reduced by ${summary.overall_reduction_percentage}%`);
   *
   * @see {@link getRiskStats} for general risk statistics
   * @see {@link updateRisk} for updating residual scores
   */
  async getRiskReductionSummary() {
    return this.request<{
      success: boolean
      summary: {
        total_risks: number
        active_risks: number
        risks_with_reduction_data: number
        total_inherent_score: number
        total_residual_score: number
        total_risk_reduced: number
        overall_reduction_percentage: number
        average_effectiveness_rating: number | null
        reduction_distribution: {
          significant: number
          moderate: number
          low: number
        }
      }
      risks: Array<{
        id: string
        title: string
        inherent_score: number
        residual_score: number
        reduction_percentage: number
        reduction_points: number
        control_effectiveness_rating: number | null
        status: string
        category: string
      }>
    }>('/api/risks/reduction-summary')
  }

  /**
   * Gets risk data aggregated by category, business unit, or control family.
   *
   * This method provides roll-up reporting for risks grouped by various dimensions,
   * including summary statistics and severity distributions for each group.
   *
   * @param {Object} params - Aggregation parameters
   * @param {string} [params.groupBy='category'] - Dimension to group by: 'category', 'business_unit', 'control_family', or 'status'
   * @param {string} [params.status] - Optional status filter
   *
   * @returns {Promise<Object>} Aggregation data including:
   *   - `group_by` {string} - The dimension used for grouping
   *   - `summary` {Object} - Overall statistics
   *   - `aggregations` {Array} - Per-group statistics
   *
   * @example
   * // Get risks grouped by category
   * const { summary, aggregations } = await api.getRiskAggregation({ groupBy: 'category' });
   *
   * @see {@link getRiskStats} for overall risk statistics
   * @see {@link getRiskHeatmap} for heat map visualization data
   */
  async getRiskAggregation(params: {
    groupBy?: 'category' | 'owner' | 'status' | 'treatment' | 'control_family' | 'business_unit' | 'department'
    status?: string
  } = {}) {
    const searchParams = new URLSearchParams()
    if (params.groupBy) searchParams.set('groupBy', params.groupBy)
    if (params.status) searchParams.set('status', params.status)

    const queryString = searchParams.toString()
    const url = `/api/risks/aggregation${queryString ? `?${queryString}` : ''}`

    return this.request<{
      success: boolean
      group_by: string
      summary: {
        total_risks: number
        total_inherent_score: number
        total_residual_score: number
        total_exposure: number
        reduction_percentage: number
        avg_inherent_score: number
        avg_residual_score: number
        max_inherent_score: number
        max_residual_score: number
        active_count: number
        overdue_count: number
        severity_distribution: {
          critical: number
          high: number
          medium: number
          low: number
        }
      }
      aggregations: Array<{
        group_name: string
        group_id?: string
        risk_count: number
        total_inherent_score: number
        total_residual_score: number
        avg_inherent_score: number
        avg_residual_score: number
        max_inherent_score: number
        max_residual_score: number
        reduction_percentage: number
        critical_count: number
        high_count: number
        medium_count: number
        low_count: number
        open_count: number
        mitigating_count: number
        mitigated_count: number
        accepted_count: number
        closed_count: number
      }>
    }>(url)
  }

  /**
   * Gets heat map data for risks, optionally grouped by category or control family.
   *
   * This method returns matrix data suitable for rendering risk heat maps,
   * showing the distribution of risks across likelihood and impact dimensions.
   *
   * @param {Object} params - Heat map parameters
   * @param {string} [params.groupBy='category'] - Dimension to group by: 'category' or 'control_family'
   * @param {boolean} [params.activeOnly=true] - Whether to include only active risks (open/mitigating)
   *
   * @returns {Promise<Object>} Heat map data including:
   *   - `matrix` {Array} - Overall 5x5 matrix data with risk counts
   *   - `grouped_heatmap` {Object} - Per-group matrix data
   *
   * @example
   * // Get heat map for active risks by category
   * const { matrix, grouped_heatmap } = await api.getRiskHeatmap({ groupBy: 'category' });
   *
   * @see {@link getRiskAggregation} for roll-up reporting
   * @see {@link getRiskStats} for overall statistics
   */
  async getRiskHeatmap(params: {
    groupBy?: 'category' | 'control_family'
    activeOnly?: boolean
  } = {}) {
    const searchParams = new URLSearchParams()
    if (params.groupBy) searchParams.set('groupBy', params.groupBy)
    if (params.activeOnly !== undefined) searchParams.set('activeOnly', String(params.activeOnly))

    const queryString = searchParams.toString()
    const url = `/api/risks/heatmap${queryString ? `?${queryString}` : ''}`

    return this.request<{
      success: boolean
      group_by: string
      active_only: boolean
      matrix: Array<{
        likelihood: number
        impact: number
        count: number
        score: number
        risks: Array<{ id: string; title: string }>
      }>
      grouped_heatmap: Record<string, Array<{
        likelihood: number
        impact: number
        count: number
        score: number
      }>>
    }>(url)
  }

  /**
   * Gets a simplified roll-up summary of all risks with totals (Issue #153).
   *
   * This method provides high-level executive summary metrics suitable for
   * dashboards and reports. It includes comprehensive totals, severity
   * distributions, treatment status, and category breakdowns.
   *
   * @param {Object} params - Roll-up parameters
   * @param {boolean} [params.includeDetails=false] - Include top 10 highest-risk items
   *
   * @returns {Promise<Object>} Roll-up summary including:
   *   - `rollup` {Object} - Comprehensive metrics:
   *     - `total_risks`, `active_risks`, `mitigated_risks`, etc.
   *     - `total_exposure` - Current residual risk score
   *     - `reduction_percentage` - Overall risk reduction
   *     - `severity_distribution` - Count by severity level
   *     - `active_severity` - Active risks only by severity
   *     - `treatment` - Overdue, with/without plan counts
   *     - `by_category` - Breakdown per category
   *   - `top_risks` {Array} - Optional top 10 risks (if includeDetails=true)
   *
   * @example
   * // Get executive risk summary
   * const { rollup } = await api.getRiskRollup();
   * console.log(`Total exposure: ${rollup.total_exposure}`);
   * console.log(`Risk reduction: ${rollup.reduction_percentage}%`);
   *
   * @see {@link getRiskAggregation} for detailed group-by analytics
   * @see {@link getRiskStats} for basic statistics
   */
  async getRiskRollup(params: {
    includeDetails?: boolean
  } = {}) {
    const searchParams = new URLSearchParams()
    if (params.includeDetails) searchParams.set('includeDetails', 'true')

    const queryString = searchParams.toString()
    const url = `/api/risks/rollup${queryString ? `?${queryString}` : ''}`

    return this.request<{
      success: boolean
      rollup: {
        // Totals
        total_risks: number
        active_risks: number
        mitigated_risks: number
        accepted_risks: number
        closed_risks: number
        // Exposure metrics
        total_inherent_score: number
        total_residual_score: number
        total_exposure: number
        reduction_points: number
        reduction_percentage: number
        // Score stats
        avg_inherent_score: number
        avg_residual_score: number
        max_inherent_score: number
        max_residual_score: number
        // Severity distribution
        severity_distribution: {
          critical: number
          high: number
          medium: number
          low: number
        }
        // Active severity
        active_severity: {
          critical: number
          high: number
          medium: number
          low: number
        }
        // Treatment status
        treatment: {
          overdue: number
          with_plan: number
          without_plan: number
          unassigned: number
        }
        // Organization stats
        unique_owners: number
        unique_categories: number
        // Category breakdown
        by_category: Array<{
          category: string
          count: number
          total_inherent: number
          total_residual: number
          reduction_percentage: number
        }>
      }
      top_risks?: Array<{
        id: string
        title: string
        category: string
        inherent_score: number
        residual_score: number
        status: string
        due_date: number | null
        owner_name: string | null
      }>
    }>(url)
  }

  /**
   * Permanently deletes a risk entry from the risk register.
   *
   * This method removes a risk and all its associations (linked controls, comments,
   * history) from the system. For compliance and audit trail purposes, consider
   * using {@link updateRisk} to set status to 'closed' instead of deleting.
   *
   * **When to Delete vs. Close:**
   * - **Delete**: Risk was created in error, duplicate, or test data
   * - **Close**: Risk is no longer applicable but should be retained for audit history
   *
   * **Important Considerations:**
   * - Deletion is permanent and cannot be undone
   * - Linked control associations are removed (controls themselves are not deleted)
   * - Audit logs recording the deletion are preserved
   * - Consider exporting risk data before deletion for compliance records
   *
   * @param {string} id - The unique identifier of the risk to delete
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the deletion was successful
   *   - `message` {string} - Confirmation message (e.g., "Risk deleted successfully")
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to delete risks (403)
   *   (typically requires admin or compliance_manager role)
   * @throws {Error} Throws if risk with given ID does not exist (404)
   *
   * @example
   * // Delete a risk with confirmation
   * async function handleDeleteRisk(riskId: string, riskTitle: string) {
   *   const confirmed = window.confirm(
   *     `Are you sure you want to permanently delete "${riskTitle}"?\n\n` +
   *     `This action cannot be undone. Consider closing the risk instead to preserve audit history.`
   *   );
   *
   *   if (confirmed) {
   *     await api.deleteRisk(riskId);
   *     toast.success('Risk deleted');
   *     navigate('/risks');
   *   }
   * }
   *
   * @example
   * // Delete risk with error handling
   * async function deleteRiskSafely(riskId: string) {
   *   try {
   *     await api.deleteRisk(riskId);
   *     return { success: true };
   *   } catch (error) {
   *     if (error.status === 403) {
   *       return { success: false, error: 'You do not have permission to delete risks' };
   *     }
   *     if (error.status === 404) {
   *       return { success: false, error: 'Risk not found - it may have already been deleted' };
   *     }
   *     return { success: false, error: 'Failed to delete risk' };
   *   }
   * }
   *
   * @example
   * // Cleanup duplicate/test risks in bulk
   * async function cleanupTestRisks(riskIds: string[]) {
   *   const results = await Promise.allSettled(
   *     riskIds.map(id => api.deleteRisk(id))
   *   );
   *
   *   const deleted = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   console.log(`Deleted ${deleted} risks, ${failed} failed`);
   * }
   *
   * @see {@link updateRisk} for closing risks instead of deleting (preserves audit trail)
   * @see {@link getRisk} for retrieving risk details before deletion
   * @see {@link getRisks} for listing risks to identify candidates for deletion
   */
  async deleteRisk(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Links one or more controls to a risk for mitigation tracking.
   *
   * This method establishes a many-to-many relationship between risks and controls,
   * documenting which controls are being used to mitigate specific risks. This is
   * a fundamental GRC concept that enables:
   *
   * **Purpose of Risk-Control Linking:**
   * - **Mitigation Tracking**: Document which controls address which risks
   * - **Control Effectiveness**: Understand which risks each control helps mitigate
   * - **Gap Analysis**: Identify risks without adequate control coverage
   * - **Residual Risk**: Justify residual risk scores based on linked controls
   * - **Audit Evidence**: Demonstrate risk treatment to auditors
   *
   * **Common Workflow:**
   * 1. Identify a risk and assess its inherent score
   * 2. Determine which controls can reduce the likelihood or impact
   * 3. Link those controls to the risk using this method
   * 4. Update the residual risk score using {@link updateRisk}
   * 5. Monitor control effectiveness over time
   *
   * **Note:** This method replaces all existing control links for the risk.
   * To add controls without removing existing ones, first retrieve current
   * linked controls via {@link getRisk} and include them in the new list.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @param {string[]} controlIds - Array of control IDs to link to the risk
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the linking was successful
   *   - `message` {string} - Confirmation message (e.g., "Controls linked successfully")
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if current user lacks permission to modify risk links (403)
   * @throws {Error} Throws if risk or any control ID does not exist (404)
   * @throws {Error} Throws if validation fails (400) - e.g., empty controlIds array
   *
   * @example
   * // Link controls to a newly created risk
   * async function createRiskWithControls(riskData: CreateRiskData, controlIds: string[]) {
   *   // Create the risk
   *   const { risk } = await api.createRisk(riskData);
   *
   *   // Link related controls
   *   if (controlIds.length > 0) {
   *     await api.linkControlsToRisk(risk.id, controlIds);
   *   }
   *
   *   return risk;
   * }
   *
   * @example
   * // Control selection component for risk detail page
   * function RiskControlLinker({ riskId, currentControlIds }) {
   *   const [controlIds, setControlIds] = useState(currentControlIds);
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSave = async () => {
   *     setSaving(true);
   *     try {
   *       await api.linkControlsToRisk(riskId, controlIds);
   *       toast.success('Controls linked successfully');
   *     } catch (error) {
   *       toast.error('Failed to link controls');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <ControlMultiSelect
   *         value={controlIds}
   *         onChange={setControlIds}
   *       />
   *       <Button onClick={handleSave} loading={saving}>
   *         Save Control Links
   *       </Button>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Add a new control to existing linked controls (without removing existing)
   * async function addControlToRisk(riskId: string, newControlId: string) {
   *   // Get current linked controls
   *   const { risk } = await api.getRisk(riskId);
   *   const currentControlIds = risk.linked_controls.map(c => c.id);
   *
   *   // Add new control if not already linked
   *   if (!currentControlIds.includes(newControlId)) {
   *     await api.linkControlsToRisk(riskId, [...currentControlIds, newControlId]);
   *   }
   * }
   *
   * @example
   * // Remove a specific control from a risk
   * async function unlinkControlFromRisk(riskId: string, controlIdToRemove: string) {
   *   const { risk } = await api.getRisk(riskId);
   *   const remainingControlIds = risk.linked_controls
   *     .map(c => c.id)
   *     .filter(id => id !== controlIdToRemove);
   *
   *   await api.linkControlsToRisk(riskId, remainingControlIds);
   * }
   *
   * @example
   * // Bulk link controls to multiple risks (e.g., for a security control)
   * async function linkControlToMultipleRisks(controlId: string, riskIds: string[]) {
   *   for (const riskId of riskIds) {
   *     const { risk } = await api.getRisk(riskId);
   *     const currentControlIds = risk.linked_controls.map(c => c.id);
   *
   *     if (!currentControlIds.includes(controlId)) {
   *       await api.linkControlsToRisk(riskId, [...currentControlIds, controlId]);
   *     }
   *   }
   * }
   *
   * @see {@link getRisk} for retrieving current linked controls
   * @see {@link getControls} for listing available controls to link
   * @see {@link updateRisk} for updating residual risk after linking controls
   * @see {@link createRisk} for creating new risks to link controls to
   */
  async linkControlsToRisk(riskId: string, controlIds: string[]) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/${riskId}/controls`, {
      method: 'POST',
      body: JSON.stringify({ control_ids: controlIds }),
    })
  }

  // Risk-Evidence endpoints

  /**
   * Retrieves all evidence items linked to a specific risk.
   *
   * Returns the list of evidence records that have been associated with the risk,
   * along with relationship metadata such as the relationship type and notes.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @returns {Promise<{success: boolean, evidence: Array<{id: string, title: string, relationship_type: string, notes: string | null, created_at: number, evidence_type: string, status: string}>}>}
   *
   * @example
   * const { evidence } = await api.getRiskEvidence('risk-123');
   * console.log(`Found ${evidence.length} linked evidence items`);
   */
  async getRiskEvidence(riskId: string) {
    return this.request<{
      success: boolean
      evidence: Array<{
        id: string
        title: string
        relationship_type: string
        notes: string | null
        linked_at: number
        linked_by: string | null
        evidence_type: string
        status: string
        description: string | null
        control_id: string | null
      }>
    }>(`/api/risks/${riskId}/evidence`)
  }

  /**
   * Links one or more evidence items to a risk.
   *
   * Creates associations between evidence records and a risk, with an optional
   * relationship type to describe how the evidence relates to the risk.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @param {Object} data - The link data
   * @param {string[]} data.evidence_ids - Array of evidence IDs to link
   * @param {string} [data.relationship_type='supporting'] - Type of relationship: 'supporting', 'mitigation', or 'assessment'
   * @param {string} [data.notes] - Optional notes about the relationship
   * @returns {Promise<{success: boolean, message: string, linked_count: number}>}
   *
   * @example
   * await api.linkRiskEvidence('risk-123', {
   *   evidence_ids: ['evidence-1', 'evidence-2'],
   *   relationship_type: 'mitigation',
   *   notes: 'Evidence of control implementation'
   * });
   */
  async linkRiskEvidence(
    riskId: string,
    data: {
      evidence_ids: string[]
      relationship_type?: 'supporting' | 'mitigation' | 'assessment'
      notes?: string
    }
  ) {
    return this.request<{
      success: boolean
      message: string
      linked_count: number
    }>(`/api/risks/${riskId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Removes the link between an evidence item and a risk.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @param {string} evidenceId - The unique identifier of the evidence to unlink
   * @returns {Promise<{success: boolean, message: string}>}
   *
   * @example
   * await api.unlinkRiskEvidence('risk-123', 'evidence-456');
   */
  async unlinkRiskEvidence(riskId: string, evidenceId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/${riskId}/evidence/${evidenceId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Updates the relationship between a risk and an evidence item.
   *
   * Allows changing the relationship type or notes for an existing link.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @param {string} evidenceId - The unique identifier of the linked evidence
   * @param {Object} data - The update data
   * @param {string} [data.relationship_type] - New relationship type
   * @param {string} [data.notes] - Updated notes
   * @returns {Promise<{success: boolean, message: string}>}
   *
   * @example
   * await api.updateRiskEvidence('risk-123', 'evidence-456', {
   *   relationship_type: 'assessment',
   *   notes: 'Updated after annual review'
   * });
   */
  async updateRiskEvidence(
    riskId: string,
    evidenceId: string,
    data: {
      relationship_type?: 'supporting' | 'mitigation' | 'assessment'
      notes?: string
    }
  ) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/${riskId}/evidence/${evidenceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Gets the effectiveness of linked controls for a specific risk.
   * Returns analysis with average effectiveness rating and suggested residual values.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @returns {Promise<{success: boolean, analysis: Object}>}
   */
  async getRiskControlEffectiveness(riskId: string) {
    return this.request<{
      success: boolean
      risk_id: string
      risk_title: string
      analysis: {
        average_effectiveness_rating: number | null
        effectiveness_percentage: number
        suggested_residual_likelihood: number | null
        suggested_residual_impact: number | null
        suggested_residual_score: number | null
        controls_with_rating: number
        controls_without_rating: number
        total_linked_controls: number
        control_breakdown: Array<{
          id: string
          control_number: string
          name: string
          status: string
          effectiveness_rating: number | null
          effectiveness_label: string
          risk_reduction_percentage: number
          has_gap: boolean
        }>
        warnings: string[]
      }
      comparison: {
        current: {
          likelihood: number | null
          impact: number | null
          score: number | null
          effectiveness_rating: number | null
        }
        suggested: {
          likelihood: number | null
          impact: number | null
          score: number | null
          effectiveness_rating: number | null
        }
        inherent: {
          likelihood: number
          impact: number
          score: number
        }
      }
    }>(`/api/risks/${riskId}/control-effectiveness`)
  }

  /**
   * Applies the AI-suggested residual risk score to a risk.
   * Uses linked controls' effectiveness to calculate suggested residual values.
   *
   * @param {string} riskId - The unique identifier of the risk
   * @returns {Promise<{success: boolean, message: string, applied: Object}>}
   */
  async applyRiskSuggestedResidual(riskId: string) {
    return this.request<{
      success: boolean
      message: string
      applied?: {
        residual_likelihood: number
        residual_impact: number
        residual_risk_score: number
        control_effectiveness_rating: number | null
      }
      based_on?: {
        controls_analyzed: number
        controls_with_rating: number
        average_effectiveness: number | null
        effectiveness_percentage: number
      }
    }>(`/api/risks/${riskId}/apply-suggested-residual`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // RISK CATEGORIES (Issue #100)
  // ============================================================================
  // Hierarchical risk category taxonomy with predefined and custom categories

  /**
   * Retrieves all risk categories for the organization.
   * Returns hierarchical tree structure by default, or flat list if requested.
   *
   * @param {Object} params - Query parameters
   * @param {boolean} [params.flat] - Return flat list instead of hierarchical tree
   * @param {boolean} [params.include_inactive] - Include inactive categories
   * @returns {Promise<{success: boolean, categories: RiskCategory[], flat_count?: number}>}
   */
  async getRiskCategories(params?: {
    flat?: boolean
    include_inactive?: boolean
  }) {
    const query = new URLSearchParams()
    if (params?.flat) query.set('flat', 'true')
    if (params?.include_inactive) query.set('include_inactive', 'true')
    const queryString = query.toString()

    return this.request<{
      success: boolean
      categories: RiskCategory[]
      flat_count?: number
      count?: number
    }>(`/api/risks/risk-categories${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Retrieves a single risk category by ID with its children and parent.
   *
   * @param {string} id - The category ID
   * @returns {Promise<{success: boolean, category: RiskCategory & {children, parent, risk_count}}>}
   */
  async getRiskCategory(id: string) {
    return this.request<{
      success: boolean
      category: RiskCategory & {
        children: RiskCategory[]
        parent: { id: string; name: string; color: string; icon: string } | null
        risk_count: number
      }
    }>(`/api/risks/risk-categories/${id}`)
  }

  /**
   * Creates a new risk category.
   * Admin role required.
   *
   * @param {Object} data - Category data
   * @param {string} data.name - Category name (required)
   * @param {string} [data.description] - Category description
   * @param {string} [data.parent_id] - Parent category ID for sub-categories
   * @param {string} [data.color] - Hex color code (default: #6B7280)
   * @param {string} [data.icon] - Lucide icon name (default: folder)
   * @param {number} [data.sort_order] - Sort order (default: 0)
   * @returns {Promise<{success: boolean, category: RiskCategory}>}
   */
  async createRiskCategory(data: {
    name: string
    description?: string
    parent_id?: string
    color?: string
    icon?: string
    sort_order?: number
    appetite_id?: string  // FK to risk_appetite.id
    appetite_category?: string  // Deprecated
  }) {
    return this.request<{
      success: boolean
      category: RiskCategory
    }>('/api/risks/risk-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing risk category.
   * Admin role required.
   *
   * @param {string} id - Category ID to update
   * @param {Object} data - Fields to update
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateRiskCategory(id: string, data: Partial<{
    name: string
    description: string
    parent_id: string | null
    color: string
    icon: string
    sort_order: number
    is_active: boolean
    appetite_id: string | null  // FK to risk_appetite.id
    appetite_category: string | null  // Deprecated
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/risk-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Deletes a risk category.
   * Admin role required. Cannot delete system categories or categories with risks assigned.
   *
   * @param {string} id - Category ID to delete
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteRiskCategory(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/risks/risk-categories/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Seeds the default risk category taxonomy.
   * Admin role required. Only works if no categories exist yet.
   *
   * @returns {Promise<{success: boolean, message: string, count: number}>}
   */
  async seedRiskCategories() {
    return this.request<{
      success: boolean
      message: string
      count: number
    }>('/api/risks/risk-categories/seed', {
      method: 'POST',
    })
  }

  // ============================================================================
  // RISK APPETITE AND TOLERANCE (Issue #139)
  // ============================================================================

  /**
   * Retrieves all risk appetite settings for the organization.
   *
   * Risk appetite defines the amount and type of risk an organization is willing
   * to accept in pursuit of its objectives. Each category (strategic, operational,
   * financial, compliance, etc.) can have its own appetite level and tolerance thresholds.
   *
   * @returns {Promise<Object>} Response containing:
   *   - `appetites` {Array} - List of risk appetite settings per category
   *   - `appetite_levels` {Object} - Metadata for appetite levels (1-5)
   *
   * @example
   * const { appetites, appetite_levels } = await api.getRiskAppetite();
   * appetites.forEach(a => console.log(`${a.category}: ${a.appetite_level_label}`));
   */
  async getRiskAppetite() {
    return this.request<{
      success: boolean
      appetites: RiskAppetite[]
      appetite_levels: Record<number, { label: string; description: string; color: string }>
    }>('/api/risks/appetite')
  }

  /**
   * Retrieves risk appetite settings for a specific category.
   *
   * @param {string} category - The category name (e.g., 'compliance', 'operational')
   * @returns {Promise<Object>} Response containing the appetite settings for that category
   *
   * @example
   * const { appetite } = await api.getRiskAppetiteByCategory('compliance');
   * console.log(`Compliance tolerance: ${appetite.tolerance_threshold}`);
   */
  async getRiskAppetiteByCategory(category: string) {
    return this.request<{
      success: boolean
      appetite: RiskAppetite
    }>(`/api/risks/appetite/${encodeURIComponent(category)}`)
  }

  /**
   * Updates risk appetite settings for one or more categories.
   * Admin role required.
   *
   * @param {Object} data - The appetite settings to update
   * @param {Array} data.appetites - Array of appetite settings per category
   * @param {string} [data.change_reason] - Reason for the changes (for audit)
   * @returns {Promise<Object>} Response with updated category names
   *
   * @example
   * await api.updateRiskAppetite({
   *   appetites: [
   *     { category: 'compliance', appetite_level: 1, tolerance_threshold: 6, max_acceptable_score: 10 },
   *     { category: 'strategic', appetite_level: 3, tolerance_threshold: 12, max_acceptable_score: 16 }
   *   ],
   *   change_reason: 'Updated per board review Q1 2026'
   * });
   */
  async updateRiskAppetite(data: {
    appetites: Array<{
      category: string
      display_name?: string
      appetite_level: number
      tolerance_threshold: number
      max_acceptable_score: number
      description?: string
      appetite_statement?: string
    }>
    change_reason?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      updated_categories: string[]
    }>('/api/risks/appetite', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Seeds the default risk appetite categories.
   * Admin role required. Only creates categories that don't already exist.
   *
   * Default categories include:
   * - Strategic (Moderate appetite)
   * - Operational (Low appetite)
   * - Financial (Low appetite)
   * - Compliance (Very Low appetite)
   * - Reputational (Very Low appetite)
   * - Technology (Low appetite)
   * - Legal (Very Low appetite)
   *
   * @returns {Promise<Object>} Response with count of created categories
   *
   * @example
   * const { count } = await api.seedRiskAppetite();
   * console.log(`Created ${count} appetite categories`);
   */
  async seedRiskAppetite() {
    return this.request<{
      success: boolean
      message: string
      count: number
    }>('/api/risks/appetite/seed', {
      method: 'POST',
    })
  }

  /**
   * Retrieves risks that exceed their category's tolerance thresholds.
   *
   * This endpoint identifies risks where the residual (or inherent if no residual)
   * score exceeds the defined tolerance threshold for that risk category.
   * Critical breaches (exceeding max_acceptable_score) are flagged separately.
   *
   * @param {Object} [params] - Filter parameters
   * @param {string} [params.category] - Filter by specific category
   * @param {boolean} [params.include_accepted] - Include accepted risks (default: false)
   * @returns {Promise<Object>} Response containing:
   *   - `breaches` {Array} - List of risks exceeding tolerance
   *   - `summary` {Object} - Summary statistics
   *
   * @example
   * const { breaches, summary } = await api.getToleranceBreaches();
   * if (summary.critical_breaches > 0) {
   *   alert(`${summary.critical_breaches} risks exceed maximum acceptable levels!`);
   * }
   */
  async getToleranceBreaches(params: {
    category?: string
    include_accepted?: boolean
  } = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.set('category', params.category)
    if (params.include_accepted) searchParams.set('include_accepted', 'true')

    const queryString = searchParams.toString()
    const url = `/api/risks/tolerance-breaches${queryString ? `?${queryString}` : ''}`

    return this.request<{
      success: boolean
      breaches: ToleranceBreach[]
      summary: {
        total_breaches: number
        by_category: Record<string, number>
        critical_breaches: number
        above_tolerance: number
        above_max: number
      }
      message?: string
    }>(url)
  }

  /**
   * Retrieves the change history for a specific risk appetite category.
   *
   * @param {string} category - The category name
   * @returns {Promise<Object>} Response containing history records
   *
   * @example
   * const { history } = await api.getRiskAppetiteHistory('compliance');
   * console.log(`Last updated: ${new Date(history[0].changed_at).toLocaleString()}`);
   */
  async getRiskAppetiteHistory(category: string) {
    return this.request<{
      success: boolean
      history: RiskAppetiteHistory[]
    }>(`/api/risks/appetite/history/${encodeURIComponent(category)}`)
  }

  // Reports endpoints

  /**
   * Retrieves a compliance summary report for the organization.
   *
   * Generates a comprehensive compliance overview including control implementation
   * status, evidence coverage, compliance scores, and gap analysis. This report
   * is typically used for executive dashboards, board presentations, and auditor
   * preparation.
   *
   * The report includes:
   * - Overall compliance score by framework
   * - Control implementation status breakdown (implemented, in-progress, not-started)
   * - Evidence coverage metrics (controls with/without evidence)
   * - Gap analysis highlighting areas needing attention
   * - Trend data showing compliance progress over time
   *
   * @param {string} [frameworkId] - Optional framework ID to filter the report. When provided,
   *   the report only includes controls and compliance data for the specified framework.
   *   If omitted, returns an aggregated summary across all enabled frameworks.
   * @param {'json' | 'csv'} [format='json'] - Response format. 'json' returns structured
   *   data suitable for UI rendering, 'csv' returns a downloadable file format.
   *
   * @returns {Promise<{success: boolean, report: any}>} Response object containing:
   *   - `success` {boolean} - Whether the report was generated successfully
   *   - `report` {Object} - The compliance summary data:
   *     - `overall_score` {number} - Aggregated compliance percentage (0-100)
   *     - `framework_scores` {Array<{framework_id: string, name: string, score: number}>} -
   *       Per-framework compliance scores
   *     - `control_status` {Object} - Control counts by status:
   *       - `implemented` {number} - Fully implemented controls
   *       - `in_progress` {number} - Partially implemented controls
   *       - `not_started` {number} - Controls not yet addressed
   *     - `evidence_coverage` {Object} - Evidence metrics:
   *       - `with_evidence` {number} - Controls with at least one evidence item
   *       - `without_evidence` {number} - Controls lacking evidence
   *       - `coverage_percentage` {number} - Percentage of controls with evidence
   *     - `gaps` {Array<{control_id: string, title: string, issue: string}>} -
   *       Identified compliance gaps
   *     - `trend` {Array<{date: string, score: number}>} - Historical compliance scores
   *     - `generated_at` {string} - ISO timestamp of report generation
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if framework ID is invalid (404)
   *
   * @example
   * // Fetch overall compliance summary for executive dashboard
   * async function loadComplianceDashboard() {
   *   const { report } = await api.getComplianceSummary();
   *   setOverallScore(report.overall_score);
   *   setControlStatus(report.control_status);
   *   setTrendData(report.trend);
   * }
   *
   * @example
   * // Fetch SOC 2-specific compliance report
   * async function loadFrameworkReport(frameworkId: string) {
   *   const { report } = await api.getComplianceSummary(frameworkId);
   *   // Display framework-specific metrics
   *   setFrameworkScore(report.overall_score);
   *   setGaps(report.gaps);
   * }
   *
   * @example
   * // Export compliance summary as CSV for auditor
   * async function exportForAuditor() {
   *   const { report } = await api.getComplianceSummary(undefined, 'csv');
   *   // Note: When format is 'csv', report contains the raw CSV data
   *   downloadFile(report, 'compliance-summary.csv', 'text/csv');
   * }
   *
   * @see {@link exportControls} for detailed control export
   * @see {@link exportEvidence} for evidence export
   * @see {@link exportRiskRegister} for risk register export
   * @see {@link getDashboardOverview} for real-time dashboard metrics
   */
  async getComplianceSummary(frameworkId?: string, format: 'json' | 'csv' = 'json') {
    const params = new URLSearchParams()
    if (frameworkId) params.set('framework_id', frameworkId)
    params.set('format', format)
    return this.request<{
      success: boolean
      report: any
    }>(`/api/reports/compliance-summary?${params}`)
  }

  /**
   * Exports the controls register as a downloadable file.
   *
   * Generates a comprehensive export of all controls including their implementation
   * status, linked evidence, framework mappings, and compliance metadata. This export
   * is used for auditor requests, compliance reporting, and backup purposes.
   *
   * The export includes for each control:
   * - Control identifier and title
   * - Framework reference (e.g., SOC 2 CC6.1)
   * - Implementation status and owner
   * - Linked evidence items and their upload dates
   * - Last review date and review history
   * - Control testing results
   *
   * **Note:** This method bypasses the standard `request()` method to return raw
   * binary data (Blob) instead of JSON. The response is suitable for direct
   * download or file creation.
   *
   * @param {string} [frameworkId] - Optional framework ID to filter controls.
   *   When provided, only exports controls belonging to the specified framework.
   *   If omitted, exports controls across all enabled frameworks.
   * @param {string} [status] - Optional status filter to limit the export.
   *   Valid values: 'implemented', 'in_progress', 'not_started', 'not_applicable'.
   *   If omitted, exports controls of all statuses.
   *
   * @returns {Promise<Blob>} A Blob containing the export file (CSV format).
   *   Use with URL.createObjectURL() for download or FileReader for processing.
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401) - returns error Blob
   * @throws {Error} Throws if framework ID is invalid (404)
   *
   * @example
   * // Download controls for a specific framework
   * async function downloadControlsReport(frameworkId: string) {
   *   try {
   *     const blob = await api.exportControls(frameworkId);
   *     const url = URL.createObjectURL(blob);
   *     const a = document.createElement('a');
   *     a.href = url;
   *     a.download = `controls-${frameworkId}-${Date.now()}.csv`;
   *     a.click();
   *     URL.revokeObjectURL(url);
   *   } catch (error) {
   *     showError('Failed to export controls');
   *   }
   * }
   *
   * @example
   * // Export only implemented controls for audit
   * async function exportImplementedControls() {
   *   const blob = await api.exportControls(undefined, 'implemented');
   *   downloadBlob(blob, 'implemented-controls.csv');
   * }
   *
   * @example
   * // Export controls with framework and status filter
   * async function exportFilteredControls(frameworkId: string, status: string) {
   *   const blob = await api.exportControls(frameworkId, status);
   *   // Create download link
   *   const downloadUrl = window.URL.createObjectURL(blob);
   *   window.location.href = downloadUrl;
   * }
   *
   * @see {@link getControls} for fetching controls with pagination
   * @see {@link getComplianceSummary} for aggregated compliance metrics
   * @see {@link exportEvidenceReport} for evidence export
   */
  async exportControlsReport(frameworkId?: string, status?: string) {
    const params = new URLSearchParams()
    if (frameworkId) params.set('framework_id', frameworkId)
    if (status) params.set('status', status)

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/reports/controls?${params}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
    return response.blob()
  }

  /**
   * Exports evidence records as a downloadable file.
   *
   * Generates a comprehensive export of evidence items including file metadata,
   * linked controls, upload information, and compliance context. This export
   * supports auditor evidence requests and evidence retention requirements.
   *
   * The export includes for each evidence item:
   * - Evidence identifier and description
   * - File name, size, and content type
   * - Upload date and uploader information
   * - Linked control references
   * - Evidence validity period
   * - Review status and notes
   *
   * **Note:** This method bypasses the standard `request()` method to return raw
   * binary data (Blob) instead of JSON. The export contains metadata only; actual
   * evidence files must be downloaded separately via {@link getEvidenceDownloadUrl}.
   *
   * @param {string} [controlId] - Optional control ID to filter evidence.
   *   When provided, only exports evidence items linked to the specified control.
   *   If omitted, exports all evidence across all controls.
   * @param {number} [startDate] - Optional Unix timestamp (milliseconds) for date range start.
   *   Only includes evidence uploaded on or after this date. Useful for periodic reports.
   * @param {number} [endDate] - Optional Unix timestamp (milliseconds) for date range end.
   *   Only includes evidence uploaded on or before this date.
   *
   * @returns {Promise<Blob>} A Blob containing the export file (CSV format).
   *   Use with URL.createObjectURL() for download or FileReader for processing.
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401) - returns error Blob
   * @throws {Error} Throws if control ID is invalid (404)
   *
   * @example
   * // Export all evidence for compliance archive
   * async function archiveAllEvidence() {
   *   const blob = await api.exportEvidence();
   *   const url = URL.createObjectURL(blob);
   *   const link = document.createElement('a');
   *   link.href = url;
   *   link.download = `evidence-archive-${new Date().toISOString().split('T')[0]}.csv`;
   *   link.click();
   *   URL.revokeObjectURL(url);
   * }
   *
   * @example
   * // Export evidence for a specific control (for auditor request)
   * async function exportControlEvidence(controlId: string) {
   *   const blob = await api.exportEvidence(controlId);
   *   downloadBlob(blob, `evidence-${controlId}.csv`);
   * }
   *
   * @example
   * // Export evidence for a date range (quarterly report)
   * async function exportQuarterlyEvidence(quarter: number, year: number) {
   *   const startDate = new Date(year, (quarter - 1) * 3, 1).getTime();
   *   const endDate = new Date(year, quarter * 3, 0, 23, 59, 59).getTime();
   *   const blob = await api.exportEvidence(undefined, startDate, endDate);
   *   downloadBlob(blob, `evidence-Q${quarter}-${year}.csv`);
   * }
   *
   * @example
   * // Export evidence for control within date range
   * async function exportFilteredEvidence(controlId: string, startDate: Date, endDate: Date) {
   *   const blob = await api.exportEvidence(
   *     controlId,
   *     startDate.getTime(),
   *     endDate.getTime()
   *   );
   *   downloadBlob(blob, `evidence-${controlId}-filtered.csv`);
   * }
   *
   * @see {@link getEvidence} for fetching evidence with pagination
   * @see {@link getEvidenceDownloadUrl} for downloading actual evidence files
   * @see {@link exportControlsReport} for controls export
   * @see {@link getComplianceSummary} for compliance overview
   */
  async exportEvidenceReport(controlId?: string, startDate?: number, endDate?: number) {
    const params = new URLSearchParams()
    if (controlId) params.set('control_id', controlId)
    if (startDate) params.set('start_date', String(startDate))
    if (endDate) params.set('end_date', String(endDate))

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/reports/evidence?${params}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
    return response.blob()
  }

  /**
   * Exports the risk register as a downloadable file.
   *
   * Generates a comprehensive export of all risks including their assessment scores,
   * mitigation status, linked controls, and ownership information. This export
   * supports risk committee reviews, board reporting, and audit requirements.
   *
   * The export includes for each risk:
   * - Risk identifier, title, and description
   * - Category (operational, financial, compliance, strategic, reputational, security)
   * - Inherent risk assessment (likelihood, impact, score)
   * - Residual risk assessment (after controls)
   * - Current status (identified, assessed, mitigating, accepted, closed)
   * - Risk level classification (critical, high, medium, low)
   * - Owner assignment and mitigation plan
   * - Due date for mitigation activities
   * - Linked control references
   * - Creation and last update timestamps
   *
   * **Risk Scoring Reference:**
   * - Score = Likelihood (1-5) × Impact (1-5)
   * - Critical: 16-25 | High: 11-15 | Medium: 6-10 | Low: 1-5
   *
   * **Note:** This method bypasses the standard `request()` method to return raw
   * binary data (Blob) instead of JSON.
   *
   * @param {'json' | 'csv'} [format='csv'] - Export file format.
   *   - 'csv': Comma-separated values, suitable for spreadsheets (default)
   *   - 'json': Structured JSON, suitable for programmatic processing
   *
   * @returns {Promise<Blob>} A Blob containing the export file in the specified format.
   *   Use with URL.createObjectURL() for download or FileReader for processing.
   *
   * @throws {Error} Throws if the request fails due to network issues
   * @throws {Error} Throws if not authenticated (401) - returns error Blob
   * @throws {Error} Throws if insufficient permissions (403)
   *
   * @example
   * // Download risk register as CSV for board presentation
   * async function downloadRiskRegister() {
   *   const blob = await api.exportRiskRegister('csv');
   *   const url = URL.createObjectURL(blob);
   *   const link = document.createElement('a');
   *   link.href = url;
   *   link.download = `risk-register-${new Date().toISOString().split('T')[0]}.csv`;
   *   link.click();
   *   URL.revokeObjectURL(url);
   * }
   *
   * @example
   * // Export as JSON for data processing/analysis
   * async function processRiskData() {
   *   const blob = await api.exportRiskRegister('json');
   *   const text = await blob.text();
   *   const risks = JSON.parse(text);
   *   // Perform analysis on risk data
   *   const criticalRisks = risks.filter(r => r.residual_score >= 16);
   *   return { total: risks.length, critical: criticalRisks.length };
   * }
   *
   * @example
   * // React component with download button
   * function RiskExportButton() {
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleExport = async () => {
   *     setLoading(true);
   *     try {
   *       const blob = await api.exportRiskRegister();
   *       const url = URL.createObjectURL(blob);
   *       window.open(url, '_blank');
   *       URL.revokeObjectURL(url);
   *     } catch (error) {
   *       toast.error('Failed to export risk register');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <Button onClick={handleExport} disabled={loading}>
   *       {loading ? 'Exporting...' : 'Export Risk Register'}
   *     </Button>
   *   );
   * }
   *
   * @see {@link getRisks} for fetching risks with pagination and filtering
   * @see {@link getRiskStats} for risk statistics and metrics
   * @see {@link getComplianceSummary} for overall compliance reporting
   * @see {@link exportControls} for controls export
   */
  async exportRiskRegister(
    format: 'json' | 'csv' = 'csv',
    filters?: {
      status?: string
      category?: string
      min_score?: number
    }
  ): Promise<Blob> {
    const params = new URLSearchParams()
    params.set('format', format)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.category) params.set('category', filters.category)
    if (filters?.min_score) params.set('min_score', String(filters.min_score))

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/risks/export?${params}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`)
    }

    return response.blob()
  }

  // ============================================================================
  // GAP ANALYSIS ENDPOINTS
  // ============================================================================

  /**
   * Retrieves cross-framework gap analysis summary.
   */
  async getGapAnalysisSummary() {
    return this.request<{
      success: boolean
      summary: {
        generated_at: string
        generated_by: string
        totals: {
          total_controls: number
          controls_with_gaps: number
          gap_rate: number
          critical_gaps: number
          high_gaps: number
          medium_gaps: number
          low_gaps: number
        }
        frameworks: Array<{
          framework_id: string
          framework_name: string
          total_controls: number
          controls_with_gaps: number
          gap_rate: number
          critical_gaps: number
          high_gaps: number
          medium_gaps: number
          low_gaps: number
          gaps_by_type: {
            no_evidence: number
            overdue_evidence: number
            missing_policy: number
            overdue_task: number
            non_compliant: number
          }
        }>
      }
    }>('/api/reports/gap-analysis/summary')
  }

  /**
   * Retrieves detailed gap analysis for a specific framework.
   */
  async getGapAnalysis(
    frameworkId: string,
    options?: {
      severity?: string
      gap_type?: string
    }
  ) {
    const params = new URLSearchParams()
    if (options?.severity) params.set('severity', options.severity)
    if (options?.gap_type) params.set('gap_type', options.gap_type)

    const queryString = params.toString()
    const url = `/api/reports/gap-analysis/${frameworkId}${queryString ? `?${queryString}` : ''}`

    return this.request<{
      success: boolean
      report: {
        generated_at: string
        generated_by: string
        summary: {
          framework_id: string
          framework_name: string
          total_controls: number
          controls_with_gaps: number
          gap_rate: number
          critical_gaps: number
          high_gaps: number
          medium_gaps: number
          low_gaps: number
          gaps_by_type: {
            no_evidence: number
            overdue_evidence: number
            missing_policy: number
            overdue_task: number
            non_compliant: number
          }
        }
        gaps: Array<{
          id: string
          control_id: string
          control_number: string
          control_name: string
          framework_id: string
          framework_name: string
          gap_type: 'no_evidence' | 'overdue_evidence' | 'missing_policy' | 'overdue_task' | 'non_compliant'
          gap_description: string
          severity: 'critical' | 'high' | 'medium' | 'low'
          remediation_effort: 'high' | 'medium' | 'low'
          remediation_suggestion: string
          owner_id?: string
          owner_name?: string
          days_overdue?: number
          last_evidence_date?: number
          next_due_date?: number
        }>
      }
    }>(url)
  }

  /**
   * Exports gap analysis report as a CSV file.
   */
  async exportGapAnalysis(frameworkId: string): Promise<Blob> {
    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/reports/gap-analysis/${frameworkId}?format=csv`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`)
    }

    return response.blob()
  }

  // ============================================================================
  // SETTINGS ENDPOINTS
  // ============================================================================
  // Organization-level and user-level configuration methods.
  //
  // Settings are organized into three levels:
  // 1. **Organization Settings** - Tenant-wide configuration (name, logo, timezone)
  // 2. **User Preferences** - Individual user UI preferences (theme, language, defaults)
  // 3. **User Profile** - Personal information (name, phone, avatar)
  // 4. **Notification Settings** - Email/in-app notification preferences per category
  // ============================================================================

  /**
   * Retrieves the current organization's settings and configuration.
   *
   * Returns tenant-wide settings including organization name, branding, contact
   * information, timezone, and enabled features. These settings apply to all
   * users within the organization.
   *
   * **Settings Categories:**
   * - **Identity**: Organization name, logo URL, primary contact
   * - **Regional**: Timezone, date format, currency
   * - **Features**: Enabled modules (TPRM, Scanner, Assessments)
   * - **Branding**: Custom colors, logo, email templates
   * - **Compliance**: Default frameworks, reporting periods
   *
   * @returns {Promise<{success: boolean, organization: any}>} Organization settings.
   *   - `success` - Whether the request succeeded
   *   - `organization` - Organization settings object containing:
   *     - `id` - Organization UUID
   *     - `name` - Display name
   *     - `slug` - URL-safe identifier
   *     - `timezone` - IANA timezone (e.g., 'America/New_York')
   *     - `logo_url` - Custom logo URL (if set)
   *     - `primary_contact_email` - Main contact email
   *     - `settings` - Additional configuration object
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if user lacks admin permissions (403)
   *
   * @example
   * // Load organization settings for settings page
   * async function loadOrgSettings() {
   *   const response = await api.getOrganization();
   *   if (response.success) {
   *     setOrgName(response.organization.name);
   *     setTimezone(response.organization.timezone);
   *     setLogoUrl(response.organization.logo_url);
   *   }
   * }
   *
   * @example
   * // React settings component
   * function OrganizationSettings() {
   *   const [org, setOrg] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getOrganization()
   *       .then(res => setOrg(res.organization))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <Card>
   *       <CardHeader>Organization Settings</CardHeader>
   *       <CardContent>
   *         <p>Name: {org.name}</p>
   *         <p>Timezone: {org.timezone}</p>
   *       </CardContent>
   *     </Card>
   *   );
   * }
   *
   * @see {@link updateOrganization} for updating organization settings
   * @see {@link getPreferences} for user-specific preferences
   */
  async getOrganization() {
    return this.request<{
      success: boolean
      organization: any
    }>('/api/settings/organization')
  }

  /**
   * Updates the organization's settings and configuration.
   *
   * Allows administrators to modify tenant-wide settings. Changes apply
   * immediately to all users in the organization. Only users with admin
   * role can update organization settings.
   *
   * **Updateable Fields:**
   * - `name` - Organization display name
   * - `timezone` - IANA timezone identifier
   * - `logo_url` - Custom logo URL
   * - `primary_contact_email` - Main contact email
   * - `settings` - Feature flags and configuration
   *
   * @param {Object} data - Organization settings to update (partial update supported).
   * @param {string} [data.name] - Organization display name
   * @param {string} [data.timezone] - IANA timezone (e.g., 'America/New_York', 'Europe/London')
   * @param {string} [data.logo_url] - URL to organization logo image
   * @param {string} [data.primary_contact_email] - Primary contact email address
   * @param {Object} [data.settings] - Additional configuration settings
   *
   * @returns {Promise<{success: boolean, message: string}>} Update confirmation.
   *   - `success` - Whether the update succeeded
   *   - `message` - Confirmation or error message
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if user lacks admin permissions (403)
   * @throws {Error} Throws if validation fails (400) - invalid timezone, etc.
   *
   * @example
   * // Update organization name and timezone
   * async function updateOrgSettings(name, timezone) {
   *   try {
   *     const response = await api.updateOrganization({
   *       name,
   *       timezone
   *     });
   *     if (response.success) {
   *       toast.success('Organization settings updated');
   *     }
   *   } catch (error) {
   *     toast.error('Failed to update settings');
   *   }
   * }
   *
   * @example
   * // React form component for organization settings
   * function OrgSettingsForm({ initialData }) {
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSubmit = async (formData) => {
   *     setSaving(true);
   *     try {
   *       await api.updateOrganization({
   *         name: formData.name,
   *         timezone: formData.timezone,
   *         primary_contact_email: formData.contactEmail
   *       });
   *       toast.success('Settings saved');
   *     } catch (error) {
   *       toast.error('Failed to save settings');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <Input name="name" label="Organization Name" />
   *       <TimezoneSelect name="timezone" />
   *       <Input name="contactEmail" label="Contact Email" type="email" />
   *       <Button type="submit" disabled={saving}>
   *         {saving ? 'Saving...' : 'Save Settings'}
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @see {@link getOrganization} for retrieving current settings
   * @see {@link getMe} for current user context
   */
  async updateOrganization(data: any) {
    return this.request<{
      success: boolean
      message: string
    }>('/api/settings/organization', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Retrieves the current user's UI preferences.
   *
   * Returns user-specific settings that customize the application experience.
   * These preferences are stored per-user and persist across sessions and devices.
   *
   * **Preference Categories:**
   * - **Display**: Theme (light/dark/system), compact mode, sidebar collapsed
   * - **Regional**: Preferred language, date format, number format
   * - **Defaults**: Default views, items per page, sort preferences
   * - **Dashboard**: Widget layout, pinned items, favorite reports
   *
   * @returns {Promise<{success: boolean, preferences: any}>} User preferences.
   *   - `success` - Whether the request succeeded
   *   - `preferences` - User preference object containing:
   *     - `theme` - UI theme ('light', 'dark', 'system')
   *     - `language` - Preferred language code (e.g., 'en', 'es')
   *     - `items_per_page` - Default pagination size
   *     - `sidebar_collapsed` - Whether sidebar starts collapsed
   *     - `compact_mode` - Whether to use compact UI density
   *     - `default_dashboard` - Default dashboard view
   *
   * @throws {Error} Throws if not authenticated (401)
   *
   * @example
   * // Load user preferences on app initialization
   * async function initializeApp() {
   *   const { preferences } = await api.getPreferences();
   *
   *   // Apply theme
   *   document.documentElement.setAttribute('data-theme', preferences.theme);
   *
   *   // Set language
   *   i18n.changeLanguage(preferences.language);
   *
   *   // Configure pagination defaults
   *   setDefaultPageSize(preferences.items_per_page || 20);
   * }
   *
   * @example
   * // React preferences context provider
   * function PreferencesProvider({ children }) {
   *   const [prefs, setPrefs] = useState(null);
   *
   *   useEffect(() => {
   *     api.getPreferences()
   *       .then(res => setPrefs(res.preferences))
   *       .catch(() => setPrefs(getDefaultPreferences()));
   *   }, []);
   *
   *   if (!prefs) return <AppLoader />;
   *
   *   return (
   *     <PreferencesContext.Provider value={{ prefs, setPrefs }}>
   *       {children}
   *     </PreferencesContext.Provider>
   *   );
   * }
   *
   * @see {@link updatePreferences} for updating preferences
   * @see {@link getMe} for user profile information
   */
  async getPreferences() {
    return this.request<{
      success: boolean
      preferences: any
    }>('/api/settings/preferences')
  }

  /**
   * Updates the current user's UI preferences.
   *
   * Allows users to customize their application experience. Supports partial
   * updates - only specified fields are modified. Changes take effect immediately
   * and persist across sessions.
   *
   * **Common Use Cases:**
   * - Theme switching (light/dark mode)
   * - Language/locale changes
   * - Pagination size preferences
   * - Dashboard widget customization
   * - Accessibility settings
   *
   * @param {Object} data - Preferences to update (partial update supported).
   * @param {string} [data.theme] - UI theme: 'light', 'dark', or 'system'
   * @param {string} [data.language] - Language code (e.g., 'en', 'es', 'fr')
   * @param {number} [data.items_per_page] - Default pagination size (10, 20, 50, 100)
   * @param {boolean} [data.sidebar_collapsed] - Start with sidebar collapsed
   * @param {boolean} [data.compact_mode] - Use compact UI density
   * @param {Object} [data.dashboard_layout] - Custom dashboard widget arrangement
   *
   * @returns {Promise<{success: boolean, message: string, preferences: any}>} Update result.
   *   - `success` - Whether the update succeeded
   *   - `message` - Confirmation message
   *   - `preferences` - Updated preferences object
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if validation fails (400) - invalid theme, etc.
   *
   * @example
   * // Toggle dark mode
   * async function toggleDarkMode(enabled) {
   *   const theme = enabled ? 'dark' : 'light';
   *   await api.updatePreferences({ theme });
   *   document.documentElement.setAttribute('data-theme', theme);
   * }
   *
   * @example
   * // React theme toggle component
   * function ThemeToggle() {
   *   const { prefs, setPrefs } = usePreferences();
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleToggle = async () => {
   *     const newTheme = prefs.theme === 'dark' ? 'light' : 'dark';
   *     setSaving(true);
   *     try {
   *       const { preferences } = await api.updatePreferences({
   *         theme: newTheme
   *       });
   *       setPrefs(preferences);
   *       toast.success(`Switched to ${newTheme} mode`);
   *     } catch (error) {
   *       toast.error('Failed to update theme');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <Switch
   *       checked={prefs.theme === 'dark'}
   *       onChange={handleToggle}
   *       disabled={saving}
   *       label="Dark Mode"
   *     />
   *   );
   * }
   *
   * @example
   * // Update multiple preferences at once
   * async function applyUserSettings(settings) {
   *   const response = await api.updatePreferences({
   *     theme: settings.theme,
   *     language: settings.language,
   *     items_per_page: settings.pageSize,
   *     compact_mode: settings.compactMode
   *   });
   *
   *   // Apply changes to UI
   *   applyTheme(response.preferences.theme);
   *   i18n.changeLanguage(response.preferences.language);
   * }
   *
   * @see {@link getPreferences} for retrieving current preferences
   * @see {@link updateProfile} for updating user profile information
   */
  async updatePreferences(data: any) {
    return this.request<{
      success: boolean
      message: string
      preferences: any
    }>('/api/settings/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates the current user's profile information.
   *
   * Allows users to update their personal information displayed throughout
   * the application. This is separate from preferences (UI settings) and
   * authentication credentials (password).
   *
   * **Updatable Fields:**
   * - First name - Used in greetings and display
   * - Last name - Used in full name displays and mentions
   * - Phone number - Contact information for notifications
   *
   * **Note:** Email changes require separate verification flow through
   * the authentication system. Avatar/photo changes may require a
   * separate upload endpoint depending on implementation.
   *
   * @param {Object} data - Profile fields to update (partial update supported).
   * @param {string} [data.first_name] - User's first name
   * @param {string} [data.last_name] - User's last name
   * @param {string} [data.phone] - Phone number (E.164 format recommended)
   *
   * @returns {Promise<{success: boolean, message: string}>} Update confirmation.
   *   - `success` - Whether the update succeeded
   *   - `message` - Confirmation or error message
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if validation fails (400) - invalid phone format, etc.
   *
   * @example
   * // Update user profile from form
   * async function handleProfileSubmit(formData) {
   *   try {
   *     const response = await api.updateProfile({
   *       first_name: formData.firstName,
   *       last_name: formData.lastName,
   *       phone: formData.phone
   *     });
   *
   *     if (response.success) {
   *       toast.success('Profile updated successfully');
   *       // Refresh user context
   *       await refreshUser();
   *     }
   *   } catch (error) {
   *     toast.error('Failed to update profile');
   *   }
   * }
   *
   * @example
   * // React profile edit form
   * function ProfileForm() {
   *   const { user, refreshUser } = useAuth();
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSubmit = async (e) => {
   *     e.preventDefault();
   *     const formData = new FormData(e.target);
   *
   *     setSaving(true);
   *     try {
   *       await api.updateProfile({
   *         first_name: formData.get('firstName'),
   *         last_name: formData.get('lastName'),
   *         phone: formData.get('phone')
   *       });
   *       await refreshUser();
   *       toast.success('Profile saved');
   *     } catch (error) {
   *       toast.error('Save failed');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <Input name="firstName" defaultValue={user.first_name} label="First Name" />
   *       <Input name="lastName" defaultValue={user.last_name} label="Last Name" />
   *       <Input name="phone" defaultValue={user.phone} label="Phone" type="tel" />
   *       <Button type="submit" disabled={saving}>Save Profile</Button>
   *     </form>
   *   );
   * }
   *
   * @see {@link getMe} for retrieving current user profile
   * @see {@link updatePreferences} for UI preference updates
   */
  async updateProfile(data: { first_name?: string; last_name?: string; phone?: string }) {
    return this.request<{
      success: boolean
      message: string
    }>('/api/settings/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Initiates an email address change request.
   *
   * This method starts the email change verification flow by sending a verification
   * code to the new email address. The user must verify the code using
   * {@link verifyEmailChange} to complete the change.
   *
   * **Email Change Flow:**
   * 1. User requests email change with new address
   * 2. System sends 6-digit verification code to NEW email address
   * 3. User enters the code via {@link verifyEmailChange}
   * 4. On success, email is updated and notification sent to OLD address
   *
   * **Security Features:**
   * - Code expires after 15 minutes
   * - Maximum 3 verification attempts per request
   * - New email must be unique (not used by another account)
   * - Audit log entry created for change request
   *
   * @param {string} new_email - The new email address to change to.
   *
   * @returns {Promise<{success: boolean, message: string, request_id: string}>}
   *   - `success` - Whether the request was initiated successfully
   *   - `message` - Status message (e.g., "Verification code sent")
   *   - `request_id` - ID to use when verifying the code
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if new email is invalid format (400)
   * @throws {Error} Throws if new email same as current (400)
   * @throws {Error} Throws if new email already in use (409)
   *
   * @example
   * async function handleEmailChange(newEmail: string) {
   *   try {
   *     const result = await api.requestEmailChange(newEmail);
   *     setRequestId(result.request_id);
   *     setStep('verify');
   *     toast.success('Check your new email for a verification code');
   *   } catch (error) {
   *     toast.error(error.message);
   *   }
   * }
   *
   * @see {@link verifyEmailChange} for completing the email change
   */
  async requestEmailChange(new_email: string) {
    return this.request<{
      success: boolean
      message: string
      request_id: string
    }>('/api/auth/request-email-change', {
      method: 'POST',
      body: JSON.stringify({ new_email }),
    })
  }

  /**
   * Verifies the email change code and updates the user's email address.
   *
   * This method completes the email change flow by verifying the code that
   * was sent to the new email address. On success, the user's email is updated
   * and a notification is sent to the old email address.
   *
   * **Post-Change Effects:**
   * - User's email is updated in the database
   * - Notification sent to old email address
   * - Central admin registry updated (if configured)
   * - Audit log entry created
   *
   * **Note:** The user may need to re-authenticate with their new email
   * after this change takes effect, depending on session configuration.
   *
   * @param {string} request_id - The request ID from {@link requestEmailChange}.
   * @param {string} code - The 6-digit verification code sent to the new email.
   *
   * @returns {Promise<{success: boolean, message: string, new_email: string}>}
   *   - `success` - Whether the email was changed successfully
   *   - `message` - Status message (e.g., "Email address updated successfully")
   *   - `new_email` - The new email address that was set
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if request_id not found or expired (404)
   * @throws {Error} Throws if code is incorrect (401) - includes remaining attempts
   * @throws {Error} Throws if request has expired (410)
   * @throws {Error} Throws if too many failed attempts (429)
   *
   * @example
   * async function handleVerifyCode(code: string) {
   *   try {
   *     const result = await api.verifyEmailChange(requestId, code);
   *     toast.success(`Email changed to ${result.new_email}`);
   *     // Refresh user data to reflect new email
   *     await refreshUser();
   *     closeModal();
   *   } catch (error) {
   *     if (error.remaining_attempts !== undefined) {
   *       toast.error(`Invalid code. ${error.remaining_attempts} attempts remaining`);
   *     } else {
   *       toast.error(error.message);
   *     }
   *   }
   * }
   *
   * @see {@link requestEmailChange} for initiating the email change
   */
  async verifyEmailChange(request_id: string, code: string) {
    return this.request<{
      success: boolean
      message: string
      new_email: string
      remaining_attempts?: number
    }>('/api/auth/verify-email-change', {
      method: 'POST',
      body: JSON.stringify({ request_id, code }),
    })
  }

  /**
   * Retrieves the current user's notification settings.
   *
   * Returns configuration for how and when the user receives notifications.
   * Settings are organized by notification category and delivery channel
   * (email, in-app, push).
   *
   * **Notification Categories:**
   * - **Tasks**: Due date reminders, assignment notifications
   * - **Controls**: Status changes, evidence requests
   * - **Risks**: New risks, score changes, mitigation updates
   * - **Compliance**: Framework updates, assessment reminders
   * - **Security**: Login alerts, password changes, integration events
   * - **System**: Maintenance notices, feature announcements
   *
   * **Delivery Channels:**
   * - **Email**: Sent to registered email address
   * - **In-App**: Shown in notification center
   * - **Push**: Browser/mobile push notifications (if enabled)
   *
   * @returns {Promise<{success: boolean, notification_settings: any}>} Notification settings.
   *   - `success` - Whether the request succeeded
   *   - `notification_settings` - Settings object organized by category:
   *     - `tasks` - Task notification preferences
   *     - `controls` - Control notification preferences
   *     - `risks` - Risk notification preferences
   *     - `compliance` - Compliance notification preferences
   *     - `security` - Security alert preferences
   *     - Each category has: `email`, `in_app`, `push` (boolean)
   *
   * @throws {Error} Throws if not authenticated (401)
   *
   * @example
   * // Load notification settings for settings page
   * async function loadNotificationSettings() {
   *   const response = await api.getNotificationSettings();
   *   return response.notification_settings;
   * }
   *
   * @example
   * // React notification settings component
   * function NotificationSettings() {
   *   const [settings, setSettings] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getNotificationSettings()
   *       .then(res => setSettings(res.notification_settings))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   const categories = [
   *     { key: 'tasks', label: 'Task Notifications' },
   *     { key: 'controls', label: 'Control Updates' },
   *     { key: 'risks', label: 'Risk Alerts' },
   *     { key: 'security', label: 'Security Alerts' }
   *   ];
   *
   *   return (
   *     <div>
   *       {categories.map(cat => (
   *         <NotificationCategory
   *           key={cat.key}
   *           label={cat.label}
   *           settings={settings[cat.key]}
   *           onChange={(newSettings) => handleCategoryChange(cat.key, newSettings)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @see {@link updateNotificationSettings} for updating settings
   * @see {@link getNotifications} for retrieving notifications
   * @see {@link getUnreadCount} for notification badge count
   */
  async getNotificationSettings() {
    return this.request<{
      success: boolean
      notification_settings: any
    }>('/api/settings/notification-settings')
  }

  /**
   * Updates the current user's notification settings.
   *
   * Allows users to customize how and when they receive notifications.
   * Supports partial updates - only specified categories/channels are modified.
   * Changes take effect immediately for future notifications.
   *
   * **Common Patterns:**
   * - Disable email for frequent notifications (tasks due)
   * - Enable email for critical alerts (security events)
   * - Mute all notifications during vacation
   * - Set up digest preferences (daily/weekly summary)
   *
   * @param {Object} data - Notification settings to update (partial update supported).
   * @param {Object} [data.tasks] - Task notification preferences
   * @param {boolean} [data.tasks.email] - Send task notifications via email
   * @param {boolean} [data.tasks.in_app] - Show task notifications in-app
   * @param {boolean} [data.tasks.push] - Send task push notifications
   * @param {Object} [data.controls] - Control notification preferences
   * @param {Object} [data.risks] - Risk notification preferences
   * @param {Object} [data.compliance] - Compliance notification preferences
   * @param {Object} [data.security] - Security alert preferences (email recommended always on)
   * @param {string} [data.digest_frequency] - Email digest frequency: 'none', 'daily', 'weekly'
   * @param {boolean} [data.mute_all] - Temporarily mute all notifications
   *
   * @returns {Promise<{success: boolean, message: string, notification_settings: any}>} Update result.
   *   - `success` - Whether the update succeeded
   *   - `message` - Confirmation message
   *   - `notification_settings` - Updated notification settings
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if validation fails (400)
   *
   * @example
   * // Toggle email notifications for a category
   * async function toggleEmailNotifications(category, enabled) {
   *   await api.updateNotificationSettings({
   *     [category]: { email: enabled }
   *   });
   * }
   *
   * @example
   * // Enable all security notifications
   * async function enableSecurityAlerts() {
   *   await api.updateNotificationSettings({
   *     security: {
   *       email: true,
   *       in_app: true,
   *       push: true
   *     }
   *   });
   * }
   *
   * @example
   * // React notification toggle component
   * function NotificationToggle({ category, channel, initialValue }) {
   *   const [enabled, setEnabled] = useState(initialValue);
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleToggle = async () => {
   *     const newValue = !enabled;
   *     setSaving(true);
   *     try {
   *       await api.updateNotificationSettings({
   *         [category]: { [channel]: newValue }
   *       });
   *       setEnabled(newValue);
   *       toast.success(`${channel} notifications ${newValue ? 'enabled' : 'disabled'}`);
   *     } catch (error) {
   *       toast.error('Failed to update notification settings');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <Switch
   *       checked={enabled}
   *       onChange={handleToggle}
   *       disabled={saving}
   *       label={`${channel} notifications`}
   *     />
   *   );
   * }
   *
   * @example
   * // Mute all notifications (vacation mode)
   * async function enableVacationMode() {
   *   await api.updateNotificationSettings({
   *     mute_all: true,
   *     digest_frequency: 'weekly'
   *   });
   *   toast.success('Vacation mode enabled. You will receive a weekly digest.');
   * }
   *
   * @see {@link getNotificationSettings} for retrieving current settings
   * @see {@link getNotifications} for notification list
   * @see {@link markAllNotificationsRead} for clearing notification badges
   */
  async updateNotificationSettings(data: any) {
    return this.request<{
      success: boolean
      message: string
      notification_settings: any
    }>('/api/settings/notification-settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Notifications endpoints

  /**
   * Retrieves the current user's notifications with optional filtering and pagination.
   *
   * Returns a paginated list of notifications including system alerts, task reminders,
   * compliance updates, security events, and other activity relevant to the user.
   * Notifications are ordered by creation time (newest first).
   *
   * **Notification Types:**
   * - `task_due` - Upcoming or overdue task reminders
   * - `task_assigned` - New task assignments
   * - `control_updated` - Control status or assignment changes
   * - `risk_created` - New risks requiring attention
   * - `compliance_alert` - Framework compliance status changes
   * - `security_event` - Security-related alerts (login attempts, integrations)
   * - `system` - System announcements and maintenance notices
   *
   * **Common Patterns:**
   * - Poll periodically for real-time notification feel
   * - Filter unread only for notification dropdown
   * - Load all with pagination for notification center
   *
   * @param {Object} [params={}] - Query parameters for filtering notifications.
   * @param {boolean} [params.unread] - Filter to only unread notifications (default: all notifications)
   * @param {number} [params.limit] - Maximum notifications to return (default: 20, max: 100)
   * @param {number} [params.offset] - Number of notifications to skip for pagination (default: 0)
   *
   * @returns {Promise<{success: boolean, notifications: any[], pagination: {total: number, limit: number, offset: number, hasMore: boolean}}>} Notification list.
   *   - `success` - Whether the request succeeded
   *   - `notifications` - Array of notification objects containing:
   *     - `id` - Unique notification identifier
   *     - `type` - Notification type (task_due, control_updated, etc.)
   *     - `title` - Short notification title
   *     - `message` - Full notification message
   *     - `read` - Whether notification has been read
   *     - `created_at` - ISO timestamp when notification was created
   *     - `data` - Type-specific data (task_id, control_id, etc.)
   *   - `pagination` - Pagination metadata:
   *     - `total` - Total notification count
   *     - `limit` - Applied limit
   *     - `offset` - Applied offset
   *     - `hasMore` - Whether more notifications exist
   *
   * @throws {Error} Throws if not authenticated (401)
   *
   * @example
   * // Get unread notifications for dropdown badge
   * async function loadUnreadNotifications() {
   *   const { notifications, pagination } = await api.getNotifications({
   *     unread: true,
   *     limit: 5
   *   });
   *   return { notifications, totalUnread: pagination.total };
   * }
   *
   * @example
   * // Notification dropdown component
   * function NotificationDropdown() {
   *   const [notifications, setNotifications] = useState([]);
   *   const [unreadCount, setUnreadCount] = useState(0);
   *
   *   useEffect(() => {
   *     async function fetchNotifications() {
   *       const { notifications, pagination } = await api.getNotifications({
   *         unread: true,
   *         limit: 10
   *       });
   *       setNotifications(notifications);
   *       setUnreadCount(pagination.total);
   *     }
   *     fetchNotifications();
   *     // Poll every 30 seconds
   *     const interval = setInterval(fetchNotifications, 30000);
   *     return () => clearInterval(interval);
   *   }, []);
   *
   *   return (
   *     <div className="notification-dropdown">
   *       <span className="badge">{unreadCount}</span>
   *       {notifications.map(n => (
   *         <NotificationItem
   *           key={n.id}
   *           notification={n}
   *           onRead={() => handleMarkRead(n.id)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Paginated notification center
   * async function loadNotificationPage(page, pageSize = 20) {
   *   const offset = page * pageSize;
   *   const { notifications, pagination } = await api.getNotifications({
   *     limit: pageSize,
   *     offset
   *   });
   *   return {
   *     notifications,
   *     hasNext: pagination.hasMore,
   *     hasPrev: offset > 0,
   *     totalPages: Math.ceil(pagination.total / pageSize)
   *   };
   * }
   *
   * @see {@link getUnreadCount} for badge count only (lighter weight)
   * @see {@link markNotificationRead} for marking single notification read
   * @see {@link markAllNotificationsRead} for clearing all notifications
   * @see {@link getNotificationSettings} for notification preferences
   */
  async getNotifications(params: { unread?: boolean; limit?: number; offset?: number } = {}) {
    const searchParams = new URLSearchParams()
    if (params.unread) searchParams.set('unread', 'true')
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.offset) searchParams.set('offset', String(params.offset))

    return this.request<{
      success: boolean
      notifications: any[]
      pagination: { total: number; limit: number; offset: number; hasMore: boolean }
    }>(`/api/notifications?${searchParams}`)
  }

  /**
   * Gets the count of unread notifications for the current user.
   *
   * Returns a simple count of unread notifications, optimized for frequent
   * polling to update notification badges. This is more lightweight than
   * calling getNotifications() when only the count is needed.
   *
   * **Use Cases:**
   * - Notification bell badge in header
   * - Real-time unread count updates
   * - Mobile app badge numbers
   *
   * **Polling Recommendations:**
   * - Poll every 30-60 seconds for reasonable real-time feel
   * - Use WebSocket for true real-time if available
   * - Combine with getNotifications on dropdown open
   *
   * @returns {Promise<{success: boolean, unread_count: number}>} Unread notification count.
   *   - `success` - Whether the request succeeded
   *   - `unread_count` - Number of unread notifications (0 if all read)
   *
   * @throws {Error} Throws if not authenticated (401)
   *
   * @example
   * // Simple badge count fetch
   * async function getNotificationBadge() {
   *   const { unread_count } = await api.getUnreadCount();
   *   return unread_count > 99 ? '99+' : String(unread_count);
   * }
   *
   * @example
   * // Notification bell with polling
   * function NotificationBell() {
   *   const [unreadCount, setUnreadCount] = useState(0);
   *
   *   useEffect(() => {
   *     async function pollCount() {
   *       const { unread_count } = await api.getUnreadCount();
   *       setUnreadCount(unread_count);
   *     }
   *     pollCount();
   *     const interval = setInterval(pollCount, 30000);
   *     return () => clearInterval(interval);
   *   }, []);
   *
   *   return (
   *     <button className="notification-bell">
   *       <BellIcon />
   *       {unreadCount > 0 && (
   *         <span className="badge">
   *           {unreadCount > 99 ? '99+' : unreadCount}
   *         </span>
   *       )}
   *     </button>
   *   );
   * }
   *
   * @example
   * // Conditional content based on unread status
   * async function hasUnreadNotifications() {
   *   const { unread_count } = await api.getUnreadCount();
   *   return unread_count > 0;
   * }
   *
   * @see {@link getNotifications} for full notification list
   * @see {@link markNotificationRead} for marking single notification read
   * @see {@link markAllNotificationsRead} for clearing all notifications
   */
  async getUnreadCount() {
    return this.request<{
      success: boolean
      unread_count: number
    }>('/api/notifications/unread-count')
  }

  /**
   * Marks a single notification as read.
   *
   * Updates the specified notification's read status to true. This is typically
   * called when a user clicks on or views a notification. Already-read notifications
   * can be marked again without error (idempotent operation).
   *
   * **Common Triggers:**
   * - User clicks on notification in dropdown
   * - User navigates to linked content (task, control, etc.)
   * - Auto-mark when notification detail view is opened
   *
   * @param {string} id - The unique identifier of the notification to mark as read.
   *
   * @returns {Promise<{success: boolean, message: string}>} Mark read result.
   *   - `success` - Whether the operation succeeded
   *   - `message` - Confirmation message (e.g., "Notification marked as read")
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if notification not found (404)
   * @throws {Error} Throws if notification belongs to another user (403)
   *
   * @example
   * // Mark notification read on click
   * async function handleNotificationClick(notification) {
   *   if (!notification.read) {
   *     await api.markNotificationRead(notification.id);
   *   }
   *   // Navigate to linked content
   *   navigateToNotificationTarget(notification);
   * }
   *
   * @example
   * // Notification item component with optimistic update
   * function NotificationItem({ notification, onRead }) {
   *   const [isRead, setIsRead] = useState(notification.read);
   *
   *   const handleClick = async () => {
   *     if (!isRead) {
   *       // Optimistic update
   *       setIsRead(true);
   *       onRead();
   *       try {
   *         await api.markNotificationRead(notification.id);
   *       } catch (error) {
   *         // Revert on failure
   *         setIsRead(false);
   *         console.error('Failed to mark notification read:', error);
   *       }
   *     }
   *     // Handle navigation
   *     window.location.href = notification.data.link;
   *   };
   *
   *   return (
   *     <div
   *       className={`notification ${isRead ? '' : 'unread'}`}
   *       onClick={handleClick}
   *     >
   *       <h4>{notification.title}</h4>
   *       <p>{notification.message}</p>
   *       <time>{formatDate(notification.created_at)}</time>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Mark read and refresh count
   * async function markReadAndRefresh(notificationId) {
   *   await api.markNotificationRead(notificationId);
   *   const { unread_count } = await api.getUnreadCount();
   *   updateBadge(unread_count);
   * }
   *
   * @see {@link getNotifications} for fetching notifications
   * @see {@link getUnreadCount} for badge count
   * @see {@link markAllNotificationsRead} for clearing all notifications
   */
  async markNotificationRead(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/notifications/${id}/read`, {
      method: 'PATCH',
    })
  }

  /**
   * Marks all of the current user's notifications as read.
   *
   * Bulk operation that sets all unread notifications to read status in a single
   * request. Commonly used for "Mark all as read" buttons in notification centers.
   * This is idempotent - calling when all are already read has no side effects.
   *
   * **Use Cases:**
   * - "Mark all as read" button in notification dropdown
   * - "Clear all" action in notification center
   * - Automatic cleanup on notification panel close
   *
   * **After Calling:**
   * - getUnreadCount() will return 0
   * - getNotifications({ unread: true }) will return empty array
   * - All notification badges should be cleared
   *
   * @returns {Promise<{success: boolean, message: string}>} Bulk mark read result.
   *   - `success` - Whether the operation succeeded
   *   - `message` - Confirmation message (e.g., "All notifications marked as read")
   *
   * @throws {Error} Throws if not authenticated (401)
   *
   * @example
   * // Simple mark all read handler
   * async function handleMarkAllRead() {
   *   await api.markAllNotificationsRead();
   *   setUnreadCount(0);
   *   setNotifications(prev => prev.map(n => ({ ...n, read: true })));
   * }
   *
   * @example
   * // Notification header with mark all button
   * function NotificationHeader({ unreadCount, onClear }) {
   *   const [clearing, setClearing] = useState(false);
   *
   *   const handleClearAll = async () => {
   *     if (unreadCount === 0) return;
   *
   *     setClearing(true);
   *     try {
   *       await api.markAllNotificationsRead();
   *       onClear();
   *     } catch (error) {
   *       console.error('Failed to clear notifications:', error);
   *     } finally {
   *       setClearing(false);
   *     }
   *   };
   *
   *   return (
   *     <div className="notification-header">
   *       <h3>Notifications ({unreadCount})</h3>
   *       <button
   *         onClick={handleClearAll}
   *         disabled={clearing || unreadCount === 0}
   *       >
   *         {clearing ? 'Clearing...' : 'Mark all as read'}
   *       </button>
   *     </div>
   *   );
   * }
   *
   * @example
   * // With confirmation dialog
   * async function confirmAndClearAll() {
   *   const confirmed = window.confirm(
   *     'Mark all notifications as read? This cannot be undone.'
   *   );
   *   if (confirmed) {
   *     await api.markAllNotificationsRead();
   *     refreshNotifications();
   *   }
   * }
   *
   * @see {@link getNotifications} for fetching notifications
   * @see {@link getUnreadCount} for badge count
   * @see {@link markNotificationRead} for marking individual notifications
   */
  async markAllNotificationsRead() {
    return this.request<{
      success: boolean
      message: string
    }>('/api/notifications/mark-all-read', {
      method: 'POST',
    })
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INTEGRATIONS ENDPOINTS
  // Third-party service integrations for automated evidence collection and sync
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves all configured third-party integrations for the current tenant.
   *
   * Integrations connect external services (cloud providers, SaaS tools, security platforms)
   * to automate evidence collection and control compliance monitoring. This method returns
   * all integrations regardless of status (active, disabled, error state).
   *
   * **Supported Integration Types:**
   * - `aws` - Amazon Web Services (CloudTrail, Config, Security Hub)
   * - `azure` - Microsoft Azure (Activity Log, Security Center, Defender)
   * - `gcp` - Google Cloud Platform (Cloud Audit Logs, Security Command Center)
   * - `github` - GitHub (repository security, code scanning, dependabot)
   * - `gitlab` - GitLab (security scanning, compliance reports)
   * - `jira` - Atlassian Jira (issue tracking, audit trails)
   * - `slack` - Slack (activity logs, DLP events)
   * - `okta` - Okta (identity events, authentication logs)
   * - `crowdstrike` - CrowdStrike (endpoint security events)
   * - `qualys` - Qualys (vulnerability scans)
   * - `custom` - Custom webhook-based integrations
   *
   * **Integration Status Values:**
   * - `active` - Integration is enabled and syncing
   * - `disabled` - Integration is paused (manual or auto-disabled)
   * - `error` - Last sync failed, requires attention
   * - `pending` - Newly created, awaiting first sync
   *
   * @returns {Promise<{success: boolean, integrations: any[], count: number}>} List of integrations.
   *   - `success` - Whether the request succeeded
   *   - `integrations` - Array of integration objects with:
   *     - `id` - Unique integration identifier
   *     - `type` - Integration type (aws, azure, github, etc.)
   *     - `name` - User-defined display name
   *     - `description` - Optional description
   *     - `status` - Current status (active, disabled, error, pending)
   *     - `config` - Integration-specific configuration (credentials masked)
   *     - `sync_frequency` - How often to sync (hourly, daily, weekly, manual)
   *     - `auto_create_evidence` - Whether to auto-create evidence from synced data
   *     - `last_sync_at` - Timestamp of last successful sync
   *     - `last_sync_status` - Result of last sync (success, error, partial)
   *     - `created_at` - When integration was created
   *   - `count` - Total number of integrations
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized to view integrations (403) - requires admin role
   *
   * @example
   * // Load integrations list for settings page
   * async function loadIntegrations() {
   *   const { integrations, count } = await api.getIntegrations();
   *   console.log(`Found ${count} integrations`);
   *   return integrations;
   * }
   *
   * @example
   * // Integrations list component
   * function IntegrationsList() {
   *   const [integrations, setIntegrations] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     async function fetchIntegrations() {
   *       try {
   *         const { integrations } = await api.getIntegrations();
   *         setIntegrations(integrations);
   *       } catch (error) {
   *         console.error('Failed to load integrations:', error);
   *       } finally {
   *         setLoading(false);
   *       }
   *     }
   *     fetchIntegrations();
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div className="integrations-grid">
   *       {integrations.map(integration => (
   *         <IntegrationCard
   *           key={integration.id}
   *           integration={integration}
   *           onSync={() => handleSync(integration.id)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Filter integrations by status
   * async function getActiveIntegrations() {
   *   const { integrations } = await api.getIntegrations();
   *   return integrations.filter(i => i.status === 'active');
   * }
   *
   * @example
   * // Group integrations by type
   * async function getIntegrationsByType() {
   *   const { integrations } = await api.getIntegrations();
   *   return integrations.reduce((acc, integration) => {
   *     const type = integration.type;
   *     if (!acc[type]) acc[type] = [];
   *     acc[type].push(integration);
   *     return acc;
   *   }, {});
   * }
   *
   * @see {@link getIntegration} for fetching a single integration with sync history
   * @see {@link createIntegration} for adding new integrations
   * @see {@link syncIntegration} for triggering manual sync
   */
  async getIntegrations() {
    return this.request<{
      success: boolean
      integrations: any[]
      count: number
    }>('/api/integrations')
  }

  /**
   * Retrieves a single integration by ID with its sync history.
   *
   * Fetches detailed information about a specific integration including configuration
   * (with sensitive fields masked), current status, and a history of recent sync
   * operations. Use this for integration detail pages and troubleshooting sync issues.
   *
   * **Sync History Entry Fields:**
   * - `id` - Unique sync identifier
   * - `status` - Sync result (success, error, partial)
   * - `started_at` - When sync started
   * - `completed_at` - When sync finished
   * - `records_synced` - Number of records processed
   * - `evidence_created` - Number of evidence items created
   * - `error_message` - Error details if sync failed
   * - `duration_ms` - Sync duration in milliseconds
   *
   * @param {string} id - The unique identifier of the integration to retrieve.
   *
   * @returns {Promise<{success: boolean, integration: any, sync_history: any[]}>} Integration details.
   *   - `success` - Whether the request succeeded
   *   - `integration` - Full integration object with:
   *     - `id` - Integration identifier
   *     - `type` - Integration type
   *     - `name` - Display name
   *     - `description` - Optional description
   *     - `status` - Current status
   *     - `config` - Configuration with sensitive values masked
   *     - `sync_frequency` - Sync schedule
   *     - `auto_create_evidence` - Auto-evidence setting
   *     - `last_sync_at` - Last sync timestamp
   *     - `last_sync_status` - Last sync result
   *     - `created_at` - Creation timestamp
   *     - `updated_at` - Last update timestamp
   *   - `sync_history` - Array of recent sync records (last 50)
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403)
   * @throws {Error} Throws if integration not found (404)
   *
   * @example
   * // Load integration detail page
   * async function loadIntegrationDetails(integrationId) {
   *   const { integration, sync_history } = await api.getIntegration(integrationId);
   *   return {
   *     integration,
   *     recentSyncs: sync_history.slice(0, 10),
   *     hasErrors: sync_history.some(s => s.status === 'error')
   *   };
   * }
   *
   * @example
   * // Integration detail component
   * function IntegrationDetail({ integrationId }) {
   *   const [data, setData] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     async function fetchDetails() {
   *       try {
   *         const result = await api.getIntegration(integrationId);
   *         setData(result);
   *       } catch (error) {
   *         if (error.message.includes('404')) {
   *           // Integration was deleted
   *           navigate('/settings/integrations');
   *         }
   *       } finally {
   *         setLoading(false);
   *       }
   *     }
   *     fetchDetails();
   *   }, [integrationId]);
   *
   *   if (loading) return <Spinner />;
   *   if (!data) return null;
   *
   *   return (
   *     <div>
   *       <IntegrationHeader integration={data.integration} />
   *       <SyncHistoryTable history={data.sync_history} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate sync success rate
   * async function getSyncSuccessRate(integrationId) {
   *   const { sync_history } = await api.getIntegration(integrationId);
   *   if (sync_history.length === 0) return null;
   *
   *   const successful = sync_history.filter(s => s.status === 'success').length;
   *   return Math.round((successful / sync_history.length) * 100);
   * }
   *
   * @see {@link getIntegrations} for listing all integrations
   * @see {@link updateIntegration} for modifying integration settings
   * @see {@link syncIntegration} for triggering manual sync
   */
  async getIntegration(id: string) {
    return this.request<{
      success: boolean
      integration: any
      sync_history: any[]
    }>(`/api/integrations/${id}`)
  }

  /**
   * Creates a new third-party integration for automated evidence collection.
   *
   * Configures a connection to an external service for automated data synchronization.
   * The integration will be validated before creation, and initial sync can be triggered
   * immediately after. Credentials are encrypted at rest and never returned in API responses.
   *
   * **Integration Type Configurations:**
   *
   * AWS (type: 'aws'):
   * ```json
   * {
   *   "access_key_id": "AKIAIOSFODNN7EXAMPLE",
   *   "secret_access_key": "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY",
   *   "region": "us-east-1",
   *   "services": ["cloudtrail", "config", "securityhub"]
   * }
   * ```
   *
   * GitHub (type: 'github'):
   * ```json
   * {
   *   "access_token": "ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
   *   "organization": "my-org",
   *   "repositories": ["repo1", "repo2"] // or ["*"] for all
   * }
   * ```
   *
   * Jira (type: 'jira'):
   * ```json
   * {
   *   "host": "company.atlassian.net",
   *   "email": "admin@company.com",
   *   "api_token": "ATATT3xFfGF0...",
   *   "project_keys": ["SEC", "COMP"]
   * }
   * ```
   *
   * **Sync Frequency Options:**
   * - `hourly` - Every hour (best for security-sensitive integrations)
   * - `daily` - Once per day at midnight UTC
   * - `weekly` - Every Sunday at midnight UTC
   * - `manual` - Only sync when explicitly triggered
   *
   * @param {Object} data - Integration configuration.
   * @param {string} data.type - Integration type (aws, azure, gcp, github, gitlab, jira, slack, okta, crowdstrike, qualys, custom).
   * @param {string} data.name - Display name for the integration (e.g., "Production AWS Account").
   * @param {string} [data.description] - Optional description explaining the integration's purpose.
   * @param {Object} data.config - Type-specific configuration including credentials.
   * @param {string} [data.sync_frequency='daily'] - How often to automatically sync (hourly, daily, weekly, manual).
   * @param {boolean} [data.auto_create_evidence=true] - Whether to automatically create evidence from synced data.
   *
   * @returns {Promise<{success: boolean, integration: any, message: string}>} Created integration.
   *   - `success` - Whether creation succeeded
   *   - `integration` - The newly created integration object (config masked)
   *   - `message` - Success message
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403) - requires admin role
   * @throws {Error} Throws if validation fails (400) - invalid type, missing required config fields
   * @throws {Error} Throws if duplicate name exists (409)
   *
   * @example
   * // Create AWS integration
   * async function createAWSIntegration(formData) {
   *   const { integration } = await api.createIntegration({
   *     type: 'aws',
   *     name: 'Production AWS Account',
   *     description: 'Main production infrastructure',
   *     config: {
   *       access_key_id: formData.accessKeyId,
   *       secret_access_key: formData.secretAccessKey,
   *       region: 'us-east-1',
   *       services: ['cloudtrail', 'config']
   *     },
   *     sync_frequency: 'hourly',
   *     auto_create_evidence: true
   *   });
   *
   *   // Test the connection immediately
   *   await api.testIntegration(integration.id);
   *   return integration;
   * }
   *
   * @example
   * // Create integration form component
   * function CreateIntegrationForm({ integrationType, onSuccess }) {
   *   const [formData, setFormData] = useState({
   *     name: '',
   *     description: '',
   *     config: {},
   *     sync_frequency: 'daily',
   *     auto_create_evidence: true
   *   });
   *   const [submitting, setSubmitting] = useState(false);
   *   const [error, setError] = useState(null);
   *
   *   const handleSubmit = async (e) => {
   *     e.preventDefault();
   *     setSubmitting(true);
   *     setError(null);
   *
   *     try {
   *       const { integration, message } = await api.createIntegration({
   *         type: integrationType,
   *         ...formData
   *       });
   *       toast.success(message);
   *       onSuccess(integration);
   *     } catch (err) {
   *       setError(err.message);
   *     } finally {
   *       setSubmitting(false);
   *     }
   *   };
   *
   *   // Returns JSX form with input fields and submit button
   *   // See full implementation in the integration components
   * }
   *
   * @example
   * // Create GitHub integration with specific repositories
   * async function createGitHubIntegration(token, org, repos) {
   *   return api.createIntegration({
   *     type: 'github',
   *     name: `GitHub - ${org}`,
   *     config: {
   *       access_token: token,
   *       organization: org,
   *       repositories: repos.length > 0 ? repos : ['*']
   *     },
   *     sync_frequency: 'daily'
   *   });
   * }
   *
   * @see {@link getIntegrations} for listing integrations
   * @see {@link testIntegration} for validating connection
   * @see {@link syncIntegration} for triggering first sync
   * @see {@link updateIntegration} for modifying settings
   */
  async createIntegration(data: {
    type: string
    name: string
    description?: string
    config: any
    sync_frequency?: string
    auto_create_evidence?: boolean
  }) {
    return this.request<{
      success: boolean
      integration: any
      message: string
    }>('/api/integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing integration's configuration or settings.
   *
   * Modifies integration properties such as name, description, sync frequency,
   * or configuration. Partial updates are supported - only include fields that
   * need to change. Credential updates require full re-validation.
   *
   * **Updatable Fields:**
   * - `name` - Display name
   * - `description` - Description text
   * - `config` - Type-specific configuration (merges with existing)
   * - `sync_frequency` - Sync schedule (hourly, daily, weekly, manual)
   * - `auto_create_evidence` - Auto-evidence toggle
   * - `status` - Enable/disable (set to 'active' or 'disabled')
   *
   * **Config Update Behavior:**
   * When updating `config`, provided fields are merged with existing config.
   * To update credentials, include the full credential set.
   *
   * @param {string} id - The unique identifier of the integration to update.
   * @param {Object} data - Fields to update (partial update supported).
   * @param {string} [data.name] - New display name.
   * @param {string} [data.description] - New description.
   * @param {Object} [data.config] - Configuration fields to update/merge.
   * @param {string} [data.sync_frequency] - New sync frequency.
   * @param {boolean} [data.auto_create_evidence] - New auto-evidence setting.
   * @param {string} [data.status] - New status ('active' or 'disabled').
   *
   * @returns {Promise<{success: boolean, message: string}>} Update result.
   *   - `success` - Whether the update succeeded
   *   - `message` - Success message
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403)
   * @throws {Error} Throws if integration not found (404)
   * @throws {Error} Throws if validation fails (400)
   *
   * @example
   * // Update sync frequency
   * async function updateSyncFrequency(integrationId, frequency) {
   *   await api.updateIntegration(integrationId, {
   *     sync_frequency: frequency
   *   });
   * }
   *
   * @example
   * // Toggle integration status
   * async function toggleIntegration(integrationId, enabled) {
   *   await api.updateIntegration(integrationId, {
   *     status: enabled ? 'active' : 'disabled'
   *   });
   * }
   *
   * @example
   * // Integration settings form
   * function IntegrationSettingsForm({ integration, onSave }) {
   *   const [settings, setSettings] = useState({
   *     name: integration.name,
   *     description: integration.description || '',
   *     sync_frequency: integration.sync_frequency,
   *     auto_create_evidence: integration.auto_create_evidence
   *   });
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSave = async () => {
   *     setSaving(true);
   *     try {
   *       await api.updateIntegration(integration.id, settings);
   *       toast.success('Integration updated');
   *       onSave();
   *     } catch (error) {
   *       toast.error('Failed to update: ' + error.message);
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <form>
   *       <input
   *         value={settings.name}
   *         onChange={e => setSettings(s => ({ ...s, name: e.target.value }))}
   *       />
   *       <select
   *         value={settings.sync_frequency}
   *         onChange={e => setSettings(s => ({ ...s, sync_frequency: e.target.value }))}
   *       >
   *         <option value="hourly">Hourly</option>
   *         <option value="daily">Daily</option>
   *         <option value="weekly">Weekly</option>
   *         <option value="manual">Manual only</option>
   *       </select>
   *       <label>
   *         <input
   *           type="checkbox"
   *           checked={settings.auto_create_evidence}
   *           onChange={e => setSettings(s => ({ ...s, auto_create_evidence: e.target.checked }))}
   *         />
   *         Auto-create evidence
   *       </label>
   *       <button onClick={handleSave} disabled={saving}>
   *         {saving ? 'Saving...' : 'Save Settings'}
   *       </button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Update AWS config to add new services
   * async function addAWSService(integrationId, newService) {
   *   const { integration } = await api.getIntegration(integrationId);
   *   const currentServices = integration.config.services || [];
   *
   *   await api.updateIntegration(integrationId, {
   *     config: {
   *       services: [...currentServices, newService]
   *     }
   *   });
   * }
   *
   * @see {@link getIntegration} for fetching current settings
   * @see {@link testIntegration} for validating after config changes
   * @see {@link deleteIntegration} for removing integrations
   */
  async updateIntegration(id: string, data: any) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/integrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes an integration and all associated sync history.
   *
   * Removes the integration configuration, credentials, and sync records. Evidence
   * that was created by this integration is NOT deleted and remains available.
   * This action cannot be undone - the integration must be recreated from scratch.
   *
   * **What Gets Deleted:**
   * - Integration configuration
   * - Stored credentials (securely wiped)
   * - All sync history records
   * - Scheduled sync jobs
   *
   * **What's Preserved:**
   * - Evidence items created by the integration
   * - Control compliance status
   * - Audit log entries referencing the integration
   *
   * @param {string} id - The unique identifier of the integration to delete.
   *
   * @returns {Promise<{success: boolean, message: string}>} Deletion result.
   *   - `success` - Whether the deletion succeeded
   *   - `message` - Confirmation message
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403) - requires admin role
   * @throws {Error} Throws if integration not found (404)
   * @throws {Error} Throws if sync is in progress (409) - wait for sync to complete
   *
   * @example
   * // Delete integration with confirmation
   * async function deleteIntegrationWithConfirm(integration) {
   *   const confirmed = window.confirm(
   *     `Delete "${integration.name}"? This will remove all sync history but keep existing evidence.`
   *   );
   *
   *   if (confirmed) {
   *     await api.deleteIntegration(integration.id);
   *     toast.success('Integration deleted');
   *     refreshIntegrations();
   *   }
   * }
   *
   * @example
   * // Delete integration button component
   * function DeleteIntegrationButton({ integration, onDeleted }) {
   *   const [deleting, setDeleting] = useState(false);
   *   const [showConfirm, setShowConfirm] = useState(false);
   *
   *   const handleDelete = async () => {
   *     setDeleting(true);
   *     try {
   *       await api.deleteIntegration(integration.id);
   *       onDeleted();
   *     } catch (error) {
   *       if (error.message.includes('409')) {
   *         toast.error('Cannot delete while sync is in progress');
   *       } else {
   *         toast.error('Failed to delete: ' + error.message);
   *       }
   *     } finally {
   *       setDeleting(false);
   *       setShowConfirm(false);
   *     }
   *   };
   *
   *   return (
   *     <>
   *       <button
   *         className="danger"
   *         onClick={() => setShowConfirm(true)}
   *         disabled={deleting}
   *       >
   *         Delete
   *       </button>
   *       {showConfirm && (
   *         <ConfirmDialog
   *           title="Delete Integration"
   *           message={`Are you sure you want to delete "${integration.name}"?`}
   *           confirmLabel="Delete"
   *           onConfirm={handleDelete}
   *           onCancel={() => setShowConfirm(false)}
   *           loading={deleting}
   *         />
   *       )}
   *     </>
   *   );
   * }
   *
   * @see {@link getIntegrations} for listing integrations
   * @see {@link updateIntegration} for disabling instead of deleting
   */
  async deleteIntegration(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/integrations/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Triggers an immediate sync for an integration.
   *
   * Initiates a manual synchronization with the connected service, pulling the latest
   * data regardless of the scheduled sync frequency. Useful for testing, onboarding,
   * or when you need real-time data. The sync runs asynchronously - this method returns
   * once the sync is queued, not when it completes.
   *
   * **Sync Process:**
   * 1. Validates credentials with external service
   * 2. Fetches new/updated records since last sync
   * 3. Processes and normalizes data
   * 4. Creates evidence items (if auto_create_evidence enabled)
   * 5. Updates control compliance based on findings
   * 6. Records sync in history
   *
   * **Sync Status in Response:**
   * - If sync completes quickly, returns full results
   * - For long-running syncs, returns initial status with sync_id
   * - Poll getIntegration() to check sync completion
   *
   * @param {string} id - The unique identifier of the integration to sync.
   *
   * @returns {Promise<{success: boolean, sync_id: string, records_synced: number, evidence_created: number, error?: string}>} Sync result.
   *   - `success` - Whether the sync completed successfully
   *   - `sync_id` - Unique identifier for this sync operation
   *   - `records_synced` - Number of records fetched and processed
   *   - `evidence_created` - Number of evidence items created
   *   - `error` - Error message if sync failed (only present on failure)
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403)
   * @throws {Error} Throws if integration not found (404)
   * @throws {Error} Throws if integration is disabled (400)
   * @throws {Error} Throws if sync already in progress (409)
   *
   * @example
   * // Manual sync button
   * async function handleSyncClick(integrationId) {
   *   try {
   *     const result = await api.syncIntegration(integrationId);
   *     toast.success(
   *       `Synced ${result.records_synced} records, created ${result.evidence_created} evidence items`
   *     );
   *   } catch (error) {
   *     if (error.message.includes('409')) {
   *       toast.info('Sync already in progress');
   *     } else {
   *       toast.error('Sync failed: ' + error.message);
   *     }
   *   }
   * }
   *
   * @example
   * // Sync button with loading state
   * function SyncButton({ integrationId }) {
   *   const [syncing, setSyncing] = useState(false);
   *   const [lastResult, setLastResult] = useState(null);
   *
   *   const handleSync = async () => {
   *     setSyncing(true);
   *     setLastResult(null);
   *
   *     try {
   *       const result = await api.syncIntegration(integrationId);
   *       setLastResult(result);
   *
   *       if (result.error) {
   *         toast.error('Sync completed with errors');
   *       } else {
   *         toast.success(`Synced successfully`);
   *       }
   *     } catch (error) {
   *       toast.error('Failed to start sync');
   *     } finally {
   *       setSyncing(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <button onClick={handleSync} disabled={syncing}>
   *         {syncing ? <Spinner /> : 'Sync Now'}
   *       </button>
   *       {lastResult && (
   *         <div className="sync-result">
   *           <span>{lastResult.records_synced} records</span>
   *           <span>{lastResult.evidence_created} evidence</span>
   *         </div>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Sync all active integrations
   * async function syncAllIntegrations() {
   *   const { integrations } = await api.getIntegrations();
   *   const active = integrations.filter(i => i.status === 'active');
   *
   *   const results = await Promise.allSettled(
   *     active.map(i => api.syncIntegration(i.id))
   *   );
   *
   *   const succeeded = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   toast.info(`Synced ${succeeded} integrations, ${failed} failed`);
   * }
   *
   * @see {@link getIntegration} for sync history
   * @see {@link testIntegration} for connection validation without syncing data
   * @see {@link getIntegrations} to check which integrations need syncing
   */
  async syncIntegration(id: string) {
    return this.request<{
      success: boolean
      sync_id: string
      records_synced: number
      evidence_created: number
      error?: string
    }>(`/api/integrations/${id}/sync`, {
      method: 'POST',
    })
  }

  /**
   * Tests an integration's connection without syncing data.
   *
   * Validates that the stored credentials are valid and the external service is
   * reachable. Use this after creating or updating an integration to verify
   * the configuration before relying on it for compliance data. This is a
   * lightweight operation that doesn't fetch or process any actual data.
   *
   * **What Gets Tested:**
   * - Credential authentication with the service
   * - API connectivity and permissions
   * - Required scopes/roles are available
   * - Service-specific configuration validity
   *
   * **Test Results:**
   * - `success: true` - Connection verified, ready to sync
   * - `success: false` - Problem detected, check `details` for specifics
   *
   * **Common Failure Reasons:**
   * - Invalid or expired credentials
   * - Insufficient permissions/scopes
   * - Network connectivity issues
   * - Service-side configuration (wrong region, missing project, etc.)
   *
   * @param {string} id - The unique identifier of the integration to test.
   *
   * @returns {Promise<{success: boolean, message: string, details?: any}>} Test result.
   *   - `success` - Whether the connection test passed
   *   - `message` - Human-readable result message
   *   - `details` - Additional diagnostic information (on failure):
   *     - `error_code` - Service-specific error code
   *     - `error_type` - Category (auth, network, permission, config)
   *     - `suggestions` - Array of fix suggestions
   *
   * @throws {Error} Throws if not authenticated (401)
   * @throws {Error} Throws if not authorized (403)
   * @throws {Error} Throws if integration not found (404)
   *
   * @example
   * // Test connection after creating integration
   * async function createAndTestIntegration(data) {
   *   const { integration } = await api.createIntegration(data);
   *
   *   const testResult = await api.testIntegration(integration.id);
   *
   *   if (testResult.success) {
   *     toast.success('Integration connected successfully!');
   *     return integration;
   *   } else {
   *     toast.error(`Connection failed: ${testResult.message}`);
   *     // Optionally delete the broken integration
   *     await api.deleteIntegration(integration.id);
   *     throw new Error(testResult.message);
   *   }
   * }
   *
   * @example
   * // Test connection button
   * function TestConnectionButton({ integrationId }) {
   *   const [testing, setTesting] = useState(false);
   *   const [result, setResult] = useState(null);
   *
   *   const handleTest = async () => {
   *     setTesting(true);
   *     setResult(null);
   *
   *     try {
   *       const testResult = await api.testIntegration(integrationId);
   *       setResult(testResult);
   *     } catch (error) {
   *       setResult({
   *         success: false,
   *         message: error.message
   *       });
   *     } finally {
   *       setTesting(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <button onClick={handleTest} disabled={testing}>
   *         {testing ? 'Testing...' : 'Test Connection'}
   *       </button>
   *       {result && (
   *         <div className={`test-result ${result.success ? 'success' : 'error'}`}>
   *           {result.success ? '✓ ' : '✗ '}{result.message}
   *           {result.details?.suggestions && (
   *             <ul>
   *               {result.details.suggestions.map((s, i) => (
   *                 <li key={i}>{s}</li>
   *               ))}
   *             </ul>
   *           )}
   *         </div>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Batch test all integrations for health check
   * async function healthCheckIntegrations() {
   *   const { integrations } = await api.getIntegrations();
   *
   *   const results = await Promise.all(
   *     integrations.map(async (integration) => {
   *       try {
   *         const test = await api.testIntegration(integration.id);
   *         return {
   *           id: integration.id,
   *           name: integration.name,
   *           healthy: test.success,
   *           message: test.message
   *         };
   *       } catch (error) {
   *         return {
   *           id: integration.id,
   *           name: integration.name,
   *           healthy: false,
   *           message: error.message
   *         };
   *       }
   *     })
   *   );
   *
   *   const unhealthy = results.filter(r => !r.healthy);
   *   if (unhealthy.length > 0) {
   *     console.warn('Unhealthy integrations:', unhealthy);
   *   }
   *
   *   return results;
   * }
   *
   * @see {@link createIntegration} for setting up new integrations
   * @see {@link updateIntegration} for fixing configuration issues
   * @see {@link syncIntegration} for actually pulling data after testing
   */
  async testIntegration(id: string) {
    return this.request<{
      success: boolean
      message: string
      details?: any
    }>(`/api/integrations/${id}/test`, {
      method: 'POST',
    })
  }

  // Integration Automated Check endpoints (Issue #86)

  /**
   * Gets available check types for each integration type.
   */
  async getIntegrationCheckTypes() {
    return this.request<{
      success: boolean
      check_types: Record<string, Array<{ type: string; name: string; description: string }>>
    }>('/api/integrations/check-types')
  }

  /**
   * Gets all automated checks configured for an integration.
   */
  async getIntegrationChecks(integrationId: string) {
    return this.request<{
      success: boolean
      checks: Array<{
        id: string
        control_id: string
        control_name: string
        control_number: string
        control_status: string
        check_type: string
        check_name: string
        check_description?: string
        check_config?: any
        is_enabled: boolean
        last_result: 'pass' | 'fail' | 'error' | 'pending' | 'unknown'
        last_result_details?: any
        last_checked_at?: number
        evidence_on_pass: boolean
        evidence_on_fail: boolean
        created_at: number
      }>
    }>(`/api/integrations/${integrationId}/checks`)
  }

  /**
   * Adds a new automated check to an integration.
   */
  async createIntegrationCheck(integrationId: string, data: {
    control_id: string
    check_type: string
    check_name: string
    check_description?: string
    check_config?: any
    evidence_on_pass?: boolean
    evidence_on_fail?: boolean
  }) {
    return this.request<{
      success: boolean
      check: { id: string; check_type: string; check_name: string; last_result: string }
    }>(`/api/integrations/${integrationId}/checks`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing automated check.
   */
  async updateIntegrationCheck(checkId: string, data: {
    check_name?: string
    check_description?: string
    check_config?: any
    is_enabled?: boolean
    evidence_on_pass?: boolean
    evidence_on_fail?: boolean
  }) {
    return this.request<{ success: boolean }>(`/api/integrations/checks/${checkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Deletes an automated check.
   */
  async deleteIntegrationCheck(checkId: string) {
    return this.request<{ success: boolean }>(`/api/integrations/checks/${checkId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Runs a single automated check and returns the result.
   */
  async runIntegrationCheck(checkId: string) {
    return this.request<{
      success: boolean
      result: 'pass' | 'fail' | 'error'
      details: any
      evidence_created: boolean
    }>(`/api/integrations/checks/${checkId}/run`, {
      method: 'POST',
    })
  }

  /**
   * Gets the result history for a specific check.
   */
  async getCheckResults(checkId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : ''
    return this.request<{
      success: boolean
      results: Array<{
        id: string
        result: 'pass' | 'fail' | 'error'
        result_details?: any
        evidence_created_id?: string
        evidence_title?: string
        checked_at: number
      }>
    }>(`/api/integrations/checks/${checkId}/results${params}`)
  }

  /**
   * Gets sync history for an integration.
   */
  async getIntegrationSyncHistory(integrationId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : ''
    return this.request<{
      success: boolean
      history: Array<{
        id: string
        sync_type: 'manual' | 'scheduled' | 'webhook'
        status: 'running' | 'completed' | 'failed'
        started_at: number
        completed_at?: number
        checks_run: number
        checks_passed: number
        checks_failed: number
        error_message?: string
      }>
    }>(`/api/integrations/${integrationId}/sync-history${params}`)
  }

  /**
   * Gets all automated checks configured for a specific control.
   */
  async getControlAutomatedChecks(controlId: string) {
    return this.request<{
      success: boolean
      checks: Array<{
        id: string
        integration_id: string
        integration_name: string
        integration_type: string
        check_type: string
        check_name: string
        check_description?: string
        is_enabled: boolean
        last_result: 'pass' | 'fail' | 'error' | 'pending' | 'unknown'
        last_result_details?: any
        last_checked_at?: number
      }>
    }>(`/api/integrations/controls/${controlId}/automated-checks`)
  }

  // Assessments endpoints

  /**
   * Retrieves all assessment campaigns for the organization.
   *
   * Assessment campaigns are structured compliance review cycles that organize
   * the evaluation of controls against framework requirements. Each campaign
   * targets specific controls and assigns reviewers to evaluate their
   * implementation status.
   *
   * **Campaign Lifecycle:**
   * 1. **Draft** - Campaign is created but not yet started
   * 2. **Active** - Campaign has been started, assessments are in progress
   * 3. **Completed** - All assessments have been reviewed and finalized
   * 4. **Cancelled** - Campaign was terminated before completion
   *
   * **Campaign Properties:**
   * - `name` - Campaign title for identification
   * - `description` - Detailed purpose and scope
   * - `framework_id` - Optional framework to assess against
   * - `due_date` - Deadline for completing all assessments (Unix timestamp)
   * - `controls` - Specific control IDs to include, or all if not specified
   * - `status` - Current lifecycle stage
   * - `progress` - Percentage of completed assessments
   *
   * **Use Cases:**
   * - Annual SOC 2 readiness assessments
   * - Quarterly internal control reviews
   * - Pre-audit preparation campaigns
   * - Continuous monitoring programs
   *
   * @returns {Promise<{success: boolean, campaigns: any[]}>} Campaign list.
   *   - `success` - Whether the request was successful
   *   - `campaigns` - Array of campaign objects with status and progress
   *
   * @throws {Error} If not authenticated - token required
   * @throws {Error} If tenant context is not set
   *
   * @example
   * // Display all campaigns in a dashboard
   * function CampaignDashboard() {
   *   const [campaigns, setCampaigns] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getAssessmentCampaigns()
   *       .then(({ campaigns }) => {
   *         setCampaigns(campaigns);
   *       })
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   const active = campaigns.filter(c => c.status === 'active');
   *   const draft = campaigns.filter(c => c.status === 'draft');
   *   const completed = campaigns.filter(c => c.status === 'completed');
   *
   *   return (
   *     <div className="campaign-overview">
   *       <StatCard title="Active Campaigns" value={active.length} />
   *       <StatCard title="Draft" value={draft.length} />
   *       <StatCard title="Completed" value={completed.length} />
   *
   *       <CampaignList campaigns={campaigns} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Find campaigns nearing their due date
   * async function getUpcomingDeadlines() {
   *   const { campaigns } = await api.getAssessmentCampaigns();
   *   const oneWeek = Date.now() + (7 * 24 * 60 * 60 * 1000);
   *
   *   const upcoming = campaigns
   *     .filter(c => c.status === 'active')
   *     .filter(c => c.due_date < oneWeek)
   *     .sort((a, b) => a.due_date - b.due_date);
   *
   *   return upcoming;
   * }
   *
   * @see {@link createAssessmentCampaign} for creating new campaigns
   * @see {@link startCampaign} for activating draft campaigns
   * @see {@link getAssessments} for individual assessment items
   */
  async getAssessmentCampaigns() {
    return this.request<{
      success: boolean
      campaigns: any[]
    }>('/api/assessments/campaigns')
  }

  /**
   * Creates a new assessment campaign in draft status.
   *
   * Assessment campaigns organize the structured evaluation of controls
   * against compliance framework requirements. A campaign defines which
   * controls to assess, the timeline for completion, and optionally which
   * framework to assess against.
   *
   * **Campaign Creation Flow:**
   * 1. Create campaign with this method (starts in "draft" status)
   * 2. Review and adjust campaign settings if needed
   * 3. Start the campaign with `startCampaign()` to generate assessments
   * 4. Assessors complete individual control assessments
   * 5. Reviewers approve/reject assessment results
   *
   * **Scope Options:**
   * - **All Controls** - Omit `controls` array to assess every control
   * - **Specific Controls** - Provide control IDs to assess only those
   * - **Framework-Specific** - Set `framework_id` to focus on one framework
   *
   * **Due Date Recommendations:**
   * - Allow 2-4 weeks for comprehensive assessments
   * - Factor in reviewer availability
   * - Account for evidence collection time
   * - Set reminders at 75%, 50%, 25% remaining time
   *
   * @param {Object} data - Campaign configuration.
   * @param {string} data.name - Campaign title for identification.
   *   Should be descriptive (e.g., "Q4 2024 SOC 2 Assessment").
   * @param {string} [data.description] - Detailed purpose, scope, and
   *   special instructions for assessors.
   * @param {string} [data.framework_id] - Optional framework ID to scope
   *   assessments. If omitted, all enabled framework controls are included.
   * @param {number} data.due_date - Unix timestamp (milliseconds) for
   *   the campaign deadline. Assessments should be completed by this date.
   * @param {string[]} [data.controls] - Optional array of specific control
   *   IDs to include. If omitted, all applicable controls are included.
   *
   * @returns {Promise<{success: boolean, campaign: any}>} Created campaign.
   *   - `success` - Whether the campaign was created
   *   - `campaign` - The new campaign object with generated ID
   *
   * @throws {Error} If not authenticated - admin or compliance_manager role required
   * @throws {Error} If required fields are missing (name, due_date)
   * @throws {Error} If due_date is in the past
   * @throws {Error} If referenced framework_id doesn't exist
   * @throws {Error} If any control IDs in the array are invalid
   *
   * @example
   * // Create a comprehensive quarterly assessment
   * async function createQuarterlyAssessment() {
   *   const dueDate = new Date();
   *   dueDate.setDate(dueDate.getDate() + 30); // 30 days from now
   *
   *   const { campaign } = await api.createAssessmentCampaign({
   *     name: 'Q4 2024 Control Assessment',
   *     description: `
   *       Quarterly assessment of all SOC 2 controls.
   *       Please ensure all evidence is current (within 90 days).
   *       Contact compliance@company.com with questions.
   *     `.trim(),
   *     framework_id: 'soc2-type2',
   *     due_date: dueDate.getTime()
   *   });
   *
   *   console.log('Created campaign:', campaign.id);
   *   return campaign;
   * }
   *
   * @example
   * // Campaign creation form handler
   * function CampaignForm({ onSuccess }) {
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleSubmit = async (formData) => {
   *     setLoading(true);
   *     try {
   *       const { campaign } = await api.createAssessmentCampaign({
   *         name: formData.name,
   *         description: formData.description,
   *         framework_id: formData.frameworkId || undefined,
   *         due_date: new Date(formData.dueDate).getTime(),
   *         controls: formData.selectedControls.length > 0
   *           ? formData.selectedControls
   *           : undefined
   *       });
   *
   *       toast.success('Campaign created! Ready to start.');
   *       onSuccess(campaign);
   *     } catch (error) {
   *       toast.error(error.message);
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return <CampaignFormFields onSubmit={handleSubmit} loading={loading} />;
   * }
   *
   * @example
   * // Create targeted assessment for specific controls
   * async function assessHighRiskControls() {
   *   // Get controls linked to high-risk items
   *   const { risks } = await api.getRisks({ level: 'critical' });
   *   const controlIds = [...new Set(risks.flatMap(r => r.control_ids))];
   *
   *   const dueDate = new Date();
   *   dueDate.setDate(dueDate.getDate() + 14); // 2 weeks
   *
   *   const { campaign } = await api.createAssessmentCampaign({
   *     name: 'High-Risk Control Assessment',
   *     description: 'Urgent assessment of controls linked to critical risks.',
   *     due_date: dueDate.getTime(),
   *     controls: controlIds
   *   });
   *
   *   // Immediately start the campaign
   *   await api.startCampaign(campaign.id);
   *   return campaign;
   * }
   *
   * @see {@link getAssessmentCampaigns} for listing all campaigns
   * @see {@link startCampaign} for activating this campaign
   * @see {@link getControls} for available control IDs
   * @see {@link getFrameworks} for available framework IDs
   */
  async createAssessmentCampaign(data: {
    name: string
    description?: string
    framework_id?: string
    due_date: number
    controls?: string[]
  }) {
    return this.request<{
      success: boolean
      campaign: any
    }>('/api/assessments/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Starts an assessment campaign, creating individual assessments.
   *
   * Transitions a campaign from "draft" to "active" status and generates
   * individual assessment records for each control in the campaign scope.
   * Once started, assessors can begin evaluating controls and submitting
   * their findings.
   *
   * **What Happens When Started:**
   * 1. Campaign status changes from "draft" to "active"
   * 2. Individual assessment records are created for each control
   * 3. Assigned assessors receive notifications
   * 4. Due date countdown begins
   * 5. Progress tracking is initialized
   *
   * **Pre-Start Checklist:**
   * - Verify campaign scope includes intended controls
   * - Confirm due date is appropriate
   * - Ensure assessors have been assigned (if applicable)
   * - Review campaign description for clarity
   *
   * **Important Notes:**
   * - This action is irreversible - campaigns cannot be "unstarted"
   * - All controls in scope get assessments, even if previously assessed
   * - Starting a campaign triggers notification emails to assessors
   *
   * @param {string} id - The unique identifier of the campaign to start.
   *
   * @returns {Promise<{success: boolean, message: string, assessments_created: number}>}
   *   Start confirmation with creation count.
   *   - `success` - Whether the campaign was started
   *   - `message` - Human-readable confirmation message
   *   - `assessments_created` - Number of individual assessments generated
   *
   * @throws {Error} If not authenticated - admin or compliance_manager role required
   * @throws {Error} If campaign doesn't exist
   * @throws {Error} If campaign is not in "draft" status
   * @throws {Error} If campaign has no controls in scope
   *
   * @example
   * // Start campaign with confirmation dialog
   * function StartCampaignButton({ campaign, onStarted }) {
   *   const [starting, setStarting] = useState(false);
   *
   *   const handleStart = async () => {
   *     const confirmed = await showConfirmDialog({
   *       title: 'Start Campaign?',
   *       message: `This will create assessments for all ${campaign.control_count} controls. This action cannot be undone.`
   *     });
   *
   *     if (!confirmed) return;
   *
   *     setStarting(true);
   *     try {
   *       const result = await api.startCampaign(campaign.id);
   *       toast.success(`Campaign started! ${result.assessments_created} assessments created.`);
   *       onStarted();
   *     } catch (error) {
   *       toast.error(`Failed to start: ${error.message}`);
   *     } finally {
   *       setStarting(false);
   *     }
   *   };
   *
   *   return (
   *     <button
   *       onClick={handleStart}
   *       disabled={starting || campaign.status !== 'draft'}
   *     >
   *       {starting ? 'Starting...' : 'Start Campaign'}
   *     </button>
   *   );
   * }
   *
   * @example
   * // Create and immediately start a campaign
   * async function launchQuickAssessment(controlIds: string[]) {
   *   // Create the campaign
   *   const { campaign } = await api.createAssessmentCampaign({
   *     name: 'Quick Assessment - ' + new Date().toLocaleDateString(),
   *     due_date: Date.now() + (7 * 24 * 60 * 60 * 1000), // 1 week
   *     controls: controlIds
   *   });
   *
   *   // Start it immediately
   *   const { assessments_created } = await api.startCampaign(campaign.id);
   *
   *   console.log(`Created ${assessments_created} assessments for campaign ${campaign.id}`);
   *   return campaign;
   * }
   *
   * @see {@link createAssessmentCampaign} for creating campaigns
   * @see {@link getAssessmentCampaigns} for campaign status
   * @see {@link getAssessments} for viewing generated assessments
   */
  async startCampaign(id: string) {
    return this.request<{
      success: boolean
      message: string
      assessments_created: number
    }>(`/api/assessments/campaigns/${id}/start`, {
      method: 'POST',
    })
  }

  /**
   * Retrieves assessments with optional filtering.
   *
   * Assessments are individual control evaluation records within a campaign.
   * Each assessment represents a specific control being reviewed, with results
   * indicating the control's implementation status (compliant, non-compliant,
   * partially compliant, or not applicable).
   *
   * **Assessment Statuses:**
   * - `pending` - Not yet started, awaiting assessor action
   * - `in_progress` - Assessor has begun evaluation
   * - `submitted` - Assessor submitted findings, awaiting review
   * - `approved` - Reviewer approved the assessment
   * - `rejected` - Reviewer rejected, needs re-evaluation
   *
   * **Assessment Results (when submitted):**
   * - `compliant` - Control is fully implemented and effective
   * - `partially_compliant` - Control is implemented but has gaps
   * - `non_compliant` - Control is not implemented or ineffective
   * - `not_applicable` - Control doesn't apply to this organization
   *
   * **Filtering Options:**
   * - `campaign_id` - Show assessments from a specific campaign
   * - `status` - Filter by assessment status
   * - `my` - Show only assessments assigned to the current user
   *
   * @param {Object} [params={}] - Filter options.
   * @param {string} [params.campaign_id] - Filter by campaign ID.
   * @param {string} [params.status] - Filter by status: 'pending', 'in_progress',
   *   'submitted', 'approved', 'rejected'.
   * @param {boolean} [params.my] - If true, only return assessments assigned
   *   to the current authenticated user.
   *
   * @returns {Promise<{success: boolean, assessments: any[]}>} Assessment list.
   *   - `success` - Whether the request was successful
   *   - `assessments` - Array of assessment objects with control details
   *
   * @throws {Error} If not authenticated - token required
   * @throws {Error} If tenant context is not set
   * @throws {Error} If campaign_id doesn't exist (when specified)
   *
   * @example
   * // Show my pending assessments
   * function MyAssessments() {
   *   const [assessments, setAssessments] = useState([]);
   *
   *   useEffect(() => {
   *     api.getAssessments({ my: true, status: 'pending' })
   *       .then(({ assessments }) => setAssessments(assessments));
   *   }, []);
   *
   *   return (
   *     <div className="my-assessments">
   *       <h2>My Pending Assessments ({assessments.length})</h2>
   *       {assessments.map(assessment => (
   *         <AssessmentCard
   *           key={assessment.id}
   *           assessment={assessment}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Campaign detail view with assessments
   * function CampaignDetail({ campaignId }) {
   *   const [assessments, setAssessments] = useState([]);
   *   const [filter, setFilter] = useState('all');
   *
   *   useEffect(() => {
   *     const params = { campaign_id: campaignId };
   *     if (filter !== 'all') params.status = filter;
   *
   *     api.getAssessments(params)
   *       .then(({ assessments }) => setAssessments(assessments));
   *   }, [campaignId, filter]);
   *
   *   const stats = {
   *     pending: assessments.filter(a => a.status === 'pending').length,
   *     submitted: assessments.filter(a => a.status === 'submitted').length,
   *     approved: assessments.filter(a => a.status === 'approved').length
   *   };
   *
   *   return (
   *     <div>
   *       <ProgressBar {...stats} total={assessments.length} />
   *       <StatusFilter value={filter} onChange={setFilter} />
   *       <AssessmentTable assessments={assessments} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Get assessments needing review (submitted but not yet approved/rejected)
   * async function getPendingReviews() {
   *   const { assessments } = await api.getAssessments({
   *     status: 'submitted'
   *   });
   *
   *   // Sort by submission date (oldest first)
   *   return assessments.sort(
   *     (a, b) => new Date(a.submitted_at) - new Date(b.submitted_at)
   *   );
   * }
   *
   * @see {@link submitAssessment} for submitting assessment results
   * @see {@link approveAssessment} for approving submissions
   * @see {@link rejectAssessment} for rejecting submissions
   * @see {@link getAssessmentCampaigns} for parent campaign data
   */
  async getAssessments(params: { campaign_id?: string; status?: string; my?: boolean } = {}) {
    const searchParams = new URLSearchParams()
    if (params.campaign_id) searchParams.set('campaign_id', params.campaign_id)
    if (params.status) searchParams.set('status', params.status)
    if (params.my) searchParams.set('my', 'true')

    return this.request<{
      success: boolean
      assessments: any[]
    }>(`/api/assessments?${searchParams}`)
  }

  /**
   * Submits an assessment with findings and supporting evidence.
   *
   * Moves an assessment from "pending" or "in_progress" to "submitted" status,
   * recording the assessor's determination of the control's implementation
   * status. Submitted assessments await review before being approved or rejected.
   *
   * **Assessment Results:**
   * - `compliant` - Control is fully implemented and operating effectively.
   *   Evidence demonstrates the control meets all requirements.
   * - `partially_compliant` - Control is implemented but has gaps or weaknesses.
   *   Some requirements are met, others need improvement.
   * - `non_compliant` - Control is not implemented or is ineffective.
   *   Significant remediation is required.
   * - `not_applicable` - Control doesn't apply to this organization's
   *   operations, environment, or scope.
   *
   * **Best Practices for Submissions:**
   * - Always attach supporting evidence when claiming compliance
   * - Provide clear, specific notes explaining the determination
   * - Reference specific control requirements in notes
   * - For non-compliance, describe what's missing or ineffective
   * - For partial compliance, list specific gaps
   *
   * **Evidence Linking:**
   * Evidence IDs reference previously uploaded evidence items. The assessment
   * will be linked to these items, creating an audit trail from control
   * requirement to supporting documentation.
   *
   * @param {string} id - The unique identifier of the assessment to submit.
   * @param {Object} data - Submission data.
   * @param {string} data.result - Assessment result: 'compliant',
   *   'partially_compliant', 'non_compliant', or 'not_applicable'.
   * @param {string} [data.notes] - Assessor notes explaining the determination.
   *   Should include specific observations and reasoning.
   * @param {string[]} [data.evidence_ids] - Array of evidence IDs that support
   *   this assessment. Evidence should be current and relevant.
   *
   * @returns {Promise<{success: boolean, message: string}>} Submission confirmation.
   *   - `success` - Whether the submission was recorded
   *   - `message` - Human-readable confirmation
   *
   * @throws {Error} If not authenticated - assessor must be assigned to this assessment
   * @throws {Error} If assessment doesn't exist
   * @throws {Error} If assessment is not in 'pending' or 'in_progress' status
   * @throws {Error} If result value is invalid
   * @throws {Error} If any evidence_id doesn't exist
   *
   * @example
   * // Assessment form submission
   * function AssessmentForm({ assessment, onSubmitted }) {
   *   const [result, setResult] = useState('');
   *   const [notes, setNotes] = useState('');
   *   const [selectedEvidence, setSelectedEvidence] = useState([]);
   *   const [submitting, setSubmitting] = useState(false);
   *
   *   const handleSubmit = async (e) => {
   *     e.preventDefault();
   *     setSubmitting(true);
   *
   *     try {
   *       await api.submitAssessment(assessment.id, {
   *         result,
   *         notes,
   *         evidence_ids: selectedEvidence.map(e => e.id)
   *       });
   *
   *       toast.success('Assessment submitted for review');
   *       onSubmitted();
   *     } catch (error) {
   *       toast.error(error.message);
   *     } finally {
   *       setSubmitting(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <ResultSelector value={result} onChange={setResult} />
   *       <EvidenceSelector
   *         selected={selectedEvidence}
   *         onChange={setSelectedEvidence}
   *       />
   *       <textarea
   *         value={notes}
   *         onChange={e => setNotes(e.target.value)}
   *         placeholder="Explain your assessment findings..."
   *       />
   *       <button type="submit" disabled={!result || submitting}>
   *         Submit Assessment
   *       </button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Quick assessment with minimal data
   * async function markControlCompliant(assessmentId: string) {
   *   await api.submitAssessment(assessmentId, {
   *     result: 'compliant',
   *     notes: 'Control verified during quarterly review. All requirements met.'
   *   });
   * }
   *
   * @example
   * // Submit with evidence attachment
   * async function submitWithEvidence(assessmentId: string, evidenceIds: string[]) {
   *   await api.submitAssessment(assessmentId, {
   *     result: 'compliant',
   *     notes: `Assessment complete. Attached ${evidenceIds.length} evidence items.`,
   *     evidence_ids: evidenceIds
   *   });
   * }
   *
   * @see {@link getAssessments} for retrieving assessments to submit
   * @see {@link approveAssessment} for approving submitted assessments
   * @see {@link rejectAssessment} for rejecting submitted assessments
   * @see {@link getEvidence} for finding evidence to attach
   */
  async submitAssessment(id: string, data: { result: string; notes?: string; evidence_ids?: string[] }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assessments/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Approves a submitted assessment.
   *
   * Moves an assessment from "submitted" to "approved" status, indicating
   * that a reviewer has verified the assessor's findings and accepts the
   * documented control status. Approved assessments contribute to the
   * campaign's completion progress and the organization's compliance posture.
   *
   * **Review Responsibilities:**
   * Reviewers should verify:
   * - The result matches the attached evidence
   * - Notes adequately explain the determination
   * - Evidence is current (not stale or outdated)
   * - Assessment methodology was appropriate
   *
   * **Approval Impact:**
   * - Assessment status changes to "approved"
   * - Campaign progress is updated
   * - Control's compliance status may be updated
   * - Audit trail records the approval
   *
   * **Who Can Approve:**
   * - Compliance managers and admins can approve any assessment
   * - Assessors cannot approve their own submissions
   * - Some organizations require secondary review for critical controls
   *
   * @param {string} id - The unique identifier of the assessment to approve.
   * @param {string} [notes] - Optional reviewer notes. Can include additional
   *   context, conditions, or observations about the approval.
   *
   * @returns {Promise<{success: boolean, message: string}>} Approval confirmation.
   *   - `success` - Whether the approval was recorded
   *   - `message` - Human-readable confirmation
   *
   * @throws {Error} If not authenticated - reviewer role required
   * @throws {Error} If assessment doesn't exist
   * @throws {Error} If assessment is not in 'submitted' status
   * @throws {Error} If user is the same as the assessor (self-approval not allowed)
   *
   * @example
   * // Quick approval with no notes
   * async function approveQuickly(assessmentId: string) {
   *   await api.approveAssessment(assessmentId);
   *   toast.success('Assessment approved');
   * }
   *
   * @example
   * // Approval with reviewer notes
   * function ApprovalButton({ assessment, onApproved }) {
   *   const [approving, setApproving] = useState(false);
   *
   *   const handleApprove = async () => {
   *     const notes = await showPrompt({
   *       title: 'Approval Notes',
   *       placeholder: 'Optional reviewer comments...',
   *       optional: true
   *     });
   *
   *     setApproving(true);
   *     try {
   *       await api.approveAssessment(assessment.id, notes);
   *       toast.success('Assessment approved');
   *       onApproved();
   *     } catch (error) {
   *       toast.error(error.message);
   *     } finally {
   *       setApproving(false);
   *     }
   *   };
   *
   *   return (
   *     <button
   *       onClick={handleApprove}
   *       disabled={approving || assessment.status !== 'submitted'}
   *       className="btn-approve"
   *     >
   *       {approving ? 'Approving...' : 'Approve'}
   *     </button>
   *   );
   * }
   *
   * @example
   * // Bulk approve multiple assessments
   * async function bulkApprove(assessmentIds: string[]) {
   *   const results = await Promise.allSettled(
   *     assessmentIds.map(id =>
   *       api.approveAssessment(id, 'Bulk approval - batch review completed')
   *     )
   *   );
   *
   *   const approved = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   toast.info(`Approved ${approved}, failed ${failed}`);
   * }
   *
   * @see {@link getAssessments} with status='submitted' for pending reviews
   * @see {@link rejectAssessment} for rejecting assessments
   * @see {@link submitAssessment} for the assessor submission process
   */
  async approveAssessment(id: string, notes?: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assessments/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approval_notes: notes }),
    })
  }

  /**
   * Rejects a submitted assessment, requiring re-evaluation.
   *
   * Moves an assessment from "submitted" back to "rejected" status,
   * indicating that the reviewer found issues with the submission that
   * require the assessor to address. The assessor must revise their
   * findings and resubmit.
   *
   * **Common Rejection Reasons:**
   * - Insufficient or outdated evidence
   * - Result doesn't match the evidence provided
   * - Missing required documentation
   * - Incomplete assessment notes
   * - Incorrect interpretation of control requirements
   * - Evidence doesn't address all control criteria
   *
   * **Rejection Workflow:**
   * 1. Reviewer rejects with clear reason
   * 2. Assessor receives notification
   * 3. Assessor addresses feedback
   * 4. Assessor resubmits with updated evidence/notes
   * 5. Reviewer re-reviews the submission
   *
   * **Best Practices for Rejection:**
   * - Provide specific, actionable feedback
   * - Reference what evidence is missing or insufficient
   * - Explain which control criteria weren't addressed
   * - Suggest how to improve the submission
   * - Be constructive, not punitive
   *
   * @param {string} id - The unique identifier of the assessment to reject.
   * @param {string} reason - Required explanation of why the assessment was
   *   rejected. Should be specific and actionable so the assessor can
   *   address the issues.
   *
   * @returns {Promise<{success: boolean, message: string}>} Rejection confirmation.
   *   - `success` - Whether the rejection was recorded
   *   - `message` - Human-readable confirmation
   *
   * @throws {Error} If not authenticated - reviewer role required
   * @throws {Error} If assessment doesn't exist
   * @throws {Error} If assessment is not in 'submitted' status
   * @throws {Error} If reason is empty - rejection requires explanation
   *
   * @example
   * // Reject with reason dialog
   * function RejectButton({ assessment, onRejected }) {
   *   const [rejecting, setRejecting] = useState(false);
   *
   *   const handleReject = async () => {
   *     const reason = await showPrompt({
   *       title: 'Rejection Reason',
   *       placeholder: 'Explain what needs to be addressed...',
   *       required: true
   *     });
   *
   *     if (!reason) return; // User cancelled
   *
   *     setRejecting(true);
   *     try {
   *       await api.rejectAssessment(assessment.id, reason);
   *       toast.info('Assessment rejected - assessor notified');
   *       onRejected();
   *     } catch (error) {
   *       toast.error(error.message);
   *     } finally {
   *       setRejecting(false);
   *     }
   *   };
   *
   *   return (
   *     <button
   *       onClick={handleReject}
   *       disabled={rejecting || assessment.status !== 'submitted'}
   *       className="btn-reject"
   *     >
   *       {rejecting ? 'Rejecting...' : 'Reject'}
   *     </button>
   *   );
   * }
   *
   * @example
   * // Review panel with approve/reject actions
   * function ReviewPanel({ assessment, onAction }) {
   *   const handleApprove = async () => {
   *     await api.approveAssessment(assessment.id);
   *     onAction('approved');
   *   };
   *
   *   const handleReject = async (reason: string) => {
   *     await api.rejectAssessment(assessment.id, reason);
   *     onAction('rejected');
   *   };
   *
   *   return (
   *     <div className="review-panel">
   *       <AssessmentDetails assessment={assessment} />
   *       <EvidenceViewer evidenceIds={assessment.evidence_ids} />
   *
   *       <div className="actions">
   *         <ApproveButton onClick={handleApprove} />
   *         <RejectButton onReject={handleReject} />
   *       </div>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Reject with specific feedback
   * async function rejectWithFeedback(assessmentId: string) {
   *   await api.rejectAssessment(assessmentId,
   *     'Evidence is from 2022 and no longer current. ' +
   *     'Please provide documentation dated within the last 90 days ' +
   *     'showing current implementation of this control.'
   *   );
   * }
   *
   * @see {@link getAssessments} with status='submitted' for pending reviews
   * @see {@link approveAssessment} for approving valid assessments
   * @see {@link submitAssessment} for the assessor resubmission process
   */
  async rejectAssessment(id: string, reason: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assessments/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: reason }),
    })
  }

  // Documents endpoints

  /**
   * Retrieves a list of policy and procedure documents with optional filtering.
   *
   * Documents in the GRC platform represent formal policies, procedures, standards,
   * and guidelines that support compliance controls. This method returns all
   * documents accessible to the current tenant, filtered by status, category,
   * or associated framework. Documents can be manually created or AI-generated,
   * and support versioning for change tracking.
   *
   * Common document categories include:
   * - **policy** - High-level organizational policies (e.g., Information Security Policy)
   * - **procedure** - Step-by-step operational procedures
   * - **standard** - Technical standards and baselines
   * - **guideline** - Best practice recommendations
   * - **plan** - Business continuity, disaster recovery, incident response plans
   * - **record** - Documented records for audit purposes
   *
   * @param {Object} params - Optional filter parameters.
   * @param {string} [params.status] - Filter by document status.
   *   Values: 'draft', 'review', 'approved', 'published', 'archived'.
   *   - `draft` - Document is being authored, not yet ready for review
   *   - `review` - Document is under stakeholder review
   *   - `approved` - Document has been approved but not published
   *   - `published` - Document is active and in effect
   *   - `archived` - Document is no longer in use but retained for records
   * @param {string} [params.category] - Filter by document category/type
   *   (e.g., 'policy', 'procedure', 'standard', 'guideline', 'plan').
   * @param {string} [params.framework_id] - Filter documents linked to a specific
   *   compliance framework. Returns documents that support controls in that framework.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `documents` {Array<Object>} - Array of document objects, each containing:
   *     - `id` {string} - Unique document identifier
   *     - `title` {string} - Document title
   *     - `category` {string} - Document category/type
   *     - `status` {string} - Current document status
   *     - `description` {string|null} - Document description/summary
   *     - `version` {number} - Current version number
   *     - `owner_id` {string|null} - Document owner's user ID
   *     - `framework_id` {string|null} - Associated framework ID
   *     - `review_frequency` {string|null} - How often document should be reviewed
   *     - `last_review_date` {number|null} - Unix timestamp of last review
   *     - `next_review_date` {number|null} - Unix timestamp of next scheduled review
   *     - `created_at` {number} - Unix timestamp of creation
   *     - `updated_at` {number} - Unix timestamp of last update
   *   - `count` {number} - Total number of documents matching the filters
   *
   * @throws {Error} Throws if the user is not authenticated or lacks permission
   *   to view documents for the current tenant.
   *
   * @example
   * // Fetch all published policies
   * const { documents, count } = await api.getDocuments({
   *   status: 'published',
   *   category: 'policy'
   * });
   *
   * console.log(`Found ${count} published policies`);
   * documents.forEach(doc => {
   *   console.log(`${doc.title} (v${doc.version})`);
   * });
   *
   * @example
   * // Fetch documents for a specific framework
   * const { documents } = await api.getDocuments({
   *   framework_id: soc2FrameworkId
   * });
   *
   * // Group by category for display
   * const byCategory = documents.reduce((acc, doc) => {
   *   if (!acc[doc.category]) acc[doc.category] = [];
   *   acc[doc.category].push(doc);
   *   return acc;
   * }, {});
   *
   * @example
   * // Document library component
   * function DocumentLibrary() {
   *   const [documents, setDocuments] = useState([]);
   *   const [filter, setFilter] = useState({ status: 'published' });
   *
   *   useEffect(() => {
   *     api.getDocuments(filter).then(({ documents }) => {
   *       setDocuments(documents);
   *     });
   *   }, [filter]);
   *
   *   return (
   *     <DocumentGrid
   *       documents={documents}
   *       onFilterChange={setFilter}
   *     />
   *   );
   * }
   *
   * @example
   * // Check for documents needing review
   * async function getDocumentsNeedingReview() {
   *   const { documents } = await api.getDocuments({ status: 'published' });
   *   const now = Date.now();
   *   return documents.filter(doc =>
   *     doc.next_review_date && doc.next_review_date < now
   *   );
   * }
   *
   * @see {@link getDocument} for fetching a single document with version history
   * @see {@link createDocument} for creating new documents
   * @see {@link generateDocument} for AI-generated documents
   * @see {@link getControlPolicies} for documents linked to a specific control
   */
  async getDocuments(params: { status?: string; category?: string; category_id?: string; framework_id?: string; uncategorized?: boolean } = {}) {
    const searchParams = new URLSearchParams()
    if (params.status) searchParams.set('status', params.status)
    if (params.category) searchParams.set('category', params.category)
    if (params.category_id) searchParams.set('category_id', params.category_id)
    if (params.framework_id) searchParams.set('framework_id', params.framework_id)
    if (params.uncategorized) searchParams.set('uncategorized', 'true')

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      documents: any[]
      count: number
    }>(`/api/documents${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a single document by its unique identifier, including version history.
   *
   * Fetches the complete document record including content, metadata, and all
   * historical versions. This method is typically used when viewing a document
   * detail page, editing a document, or comparing versions. The versions array
   * provides a complete audit trail of all changes made to the document.
   *
   * @param {string} id - The unique identifier of the document to retrieve.
   *   This is the UUID assigned when the document was created.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `document` {Object} - The complete document object containing:
   *     - `id` {string} - Unique document identifier
   *     - `title` {string} - Document title
   *     - `category` {string} - Document category (policy, procedure, etc.)
   *     - `status` {string} - Current status (draft, review, approved, published, archived)
   *     - `description` {string|null} - Document description/summary
   *     - `content` {string} - Full document content (may be Markdown or HTML)
   *     - `version` {number} - Current version number
   *     - `owner_id` {string|null} - Document owner's user ID
   *     - `owner` {Object|null} - Owner user object with name and email
   *     - `framework_id` {string|null} - Associated framework ID
   *     - `framework` {Object|null} - Associated framework details
   *     - `review_frequency` {string|null} - How often document should be reviewed
   *       (e.g., 'quarterly', 'annually')
   *     - `last_review_date` {number|null} - Unix timestamp of last review
   *     - `next_review_date` {number|null} - Unix timestamp of next scheduled review
   *     - `generated` {boolean} - Whether document was AI-generated
   *     - `created_at` {number} - Unix timestamp of creation
   *     - `updated_at` {number} - Unix timestamp of last update
   *   - `versions` {Array<Object>} - Array of version history entries:
   *     - `version` {number} - Version number (1, 2, 3, ...)
   *     - `change_summary` {string|null} - Description of changes in this version
   *     - `change_type` {string} - Type of change ('major', 'minor', 'patch')
   *     - `created_by` {string} - User ID who created this version
   *     - `created_at` {number} - Unix timestamp when version was created
   *
   * @throws {Error} Throws if the document is not found (404) or the user lacks
   *   permission to view it.
   *
   * @example
   * // Fetch document for display
   * async function loadDocument(documentId: string) {
   *   try {
   *     const { document, versions } = await api.getDocument(documentId);
   *     setDocumentData(document);
   *     setVersionHistory(versions);
   *     setPageTitle(document.title);
   *   } catch (error) {
   *     if (error.message.includes('404')) {
   *       navigate('/documents'); // Document not found
   *     }
   *     throw error;
   *   }
   * }
   *
   * @example
   * // Document viewer component with version selector
   * function DocumentViewer({ documentId }) {
   *   const [document, setDocument] = useState(null);
   *   const [versions, setVersions] = useState([]);
   *   const [selectedVersion, setSelectedVersion] = useState(null);
   *
   *   useEffect(() => {
   *     api.getDocument(documentId).then(({ document, versions }) => {
   *       setDocument(document);
   *       setVersions(versions);
   *     });
   *   }, [documentId]);
   *
   *   return (
   *     <div>
   *       <DocumentHeader document={document} />
   *       <VersionSelector
   *         versions={versions}
   *         currentVersion={document?.version}
   *         onSelect={setSelectedVersion}
   *       />
   *       <DocumentContent content={document?.content} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Prepare document for editing
   * const { document } = await api.getDocument(id);
   * setFormValues({
   *   title: document.title,
   *   category: document.category,
   *   description: document.description,
   *   content: document.content,
   *   review_frequency: document.review_frequency
   * });
   *
   * @see {@link getDocuments} for listing multiple documents
   * @see {@link updateDocument} for modifying a document
   * @see {@link getDocumentVersion} for fetching a specific version's content
   * @see {@link createDocumentVersion} for creating new versions
   */
  async getDocument(id: string) {
    return this.request<{
      success: boolean
      document: any
      versions: any[]
    }>(`/api/documents/${id}`)
  }

  /**
   * Creates a new policy or procedure document.
   *
   * This method allows users to create manual documents within the GRC platform.
   * Documents can be policies, procedures, standards, guidelines, or other types
   * that support compliance controls. After creation, the document can be edited,
   * versioned, and linked to controls as supporting evidence.
   *
   * For AI-generated documents based on framework requirements, use
   * {@link generateDocument} instead. This method is for manually-authored content.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role
   * for the tenant.
   *
   * @param {Object} data - The document data to create.
   * @param {string} data.title - Document title. Should be descriptive and unique
   *   within the organization (e.g., "Information Security Policy",
   *   "Incident Response Procedure").
   * @param {string} data.category - Document category/type. Common values:
   *   - `policy` - High-level organizational policies
   *   - `procedure` - Step-by-step operational procedures
   *   - `standard` - Technical standards and baselines
   *   - `guideline` - Best practice recommendations
   *   - `plan` - Business continuity, disaster recovery, incident response plans
   *   - `record` - Documented records for audit purposes
   * @param {string} [data.description] - Brief description or abstract of the
   *   document's purpose and scope. Displayed in document listings.
   * @param {string} [data.framework_id] - ID of the compliance framework this
   *   document supports (e.g., SOC 2, ISO 27001). Links the document to the
   *   framework for easy association with controls.
   * @param {string} [data.content] - Initial document content. Can be plain text,
   *   Markdown, or HTML depending on the platform configuration. If omitted,
   *   document is created with empty content for later editing.
   * @param {string} [data.review_frequency] - How often the document should be
   *   reviewed. Values: 'monthly', 'quarterly', 'semi-annually', 'annually'.
   *   The system will calculate the next_review_date based on this.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the document was created successfully
   *   - `document` {Object} - The created document object:
   *     - `id` {string} - Unique document identifier (UUID)
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - User lacks permission to create documents
   *   - Required fields (title, category) are missing
   *   - Framework ID is invalid (if provided)
   *   - Document title already exists (if uniqueness is enforced)
   *
   * @example
   * // Create a new policy document
   * const { document } = await api.createDocument({
   *   title: 'Access Control Policy',
   *   category: 'policy',
   *   description: 'Defines access control requirements for all systems and data.',
   *   framework_id: soc2FrameworkId,
   *   review_frequency: 'annually'
   * });
   *
   * console.log(`Created document: ${document.id}`);
   * navigate(`/documents/${document.id}/edit`);
   *
   * @example
   * // Create document from form submission
   * async function handleDocumentSubmit(formData: DocumentFormData) {
   *   try {
   *     const { document, message } = await api.createDocument({
   *       title: formData.title,
   *       category: formData.category,
   *       description: formData.description,
   *       framework_id: formData.frameworkId || undefined,
   *       content: formData.content,
   *       review_frequency: formData.reviewFrequency
   *     });
   *
   *     toast.success(message);
   *     navigate(`/documents/${document.id}`);
   *   } catch (error) {
   *     toast.error(`Failed to create document: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Create document and immediately link to control
   * async function createAndLinkDocument(controlId: string, docData: DocumentInput) {
   *   // Create the document
   *   const { document } = await api.createDocument(docData);
   *
   *   // Link it to the control
   *   await api.linkPolicyToControl(controlId, document.id);
   *
   *   toast.success('Document created and linked to control');
   *   return document;
   * }
   *
   * @see {@link getDocument} for retrieving the created document
   * @see {@link updateDocument} for modifying document after creation
   * @see {@link generateDocument} for AI-generated documents
   * @see {@link linkPolicyToControl} for linking documents to controls
   * @see {@link createDocumentVersion} for versioning document changes
   */
  async createDocument(data: {
    title: string
    category: string
    description?: string
    framework_id?: string
    content?: string
    review_frequency?: string
    expires_at?: number | null
    expiration_reason?: string
    successor_document_id?: string
  }) {
    return this.request<{
      success: boolean
      document: { id: string }
      message: string
    }>('/api/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get documents that are expired or expiring soon.
   * Useful for dashboard alerts and compliance monitoring.
   *
   * @param days - Number of days ahead to check for expiring documents (default: 30)
   */
  async getExpiringDocuments(days: number = 30) {
    return this.request<{
      success: boolean
      expired: Array<{
        id: string
        title: string
        category: string
        status: string
        expires_at: number
        expiration_reason: string | null
        successor_document_id: string | null
        successor_title: string | null
        owner_username: string | null
        expiration_status: 'expired' | 'expiring_soon'
        days_until_expiration: number
      }>
      expiring_soon: Array<{
        id: string
        title: string
        category: string
        status: string
        expires_at: number
        expiration_reason: string | null
        successor_document_id: string | null
        successor_title: string | null
        owner_username: string | null
        expiration_status: 'expired' | 'expiring_soon'
        days_until_expiration: number
      }>
      expired_count: number
      expiring_soon_count: number
      total: number
    }>(`/api/documents/expiring?days=${days}`)
  }

  /**
   * Updates an existing document with partial data.
   *
   * This method uses PATCH semantics, meaning only the fields provided in the
   * data object will be updated. Omitted fields will retain their current values.
   * This is ideal for updating document metadata, status transitions, or content
   * edits without needing to send the complete document object.
   *
   * **Important:** For content changes that should be tracked as a new version,
   * use {@link createDocumentVersion} instead. This method updates the document
   * in place without creating version history for content changes.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role,
   * or be the document owner.
   *
   * @param {string} id - The unique identifier of the document to update.
   * @param {Object} data - Partial document data to update. Only include fields
   *   that should change.
   * @param {string} [data.title] - Updated document title.
   * @param {string} [data.category] - Updated document category.
   *   Values: 'policy', 'procedure', 'standard', 'guideline', 'plan', 'record'.
   * @param {string} [data.description] - Updated document description/summary.
   * @param {string} [data.content] - Updated document content. For formal versioning,
   *   use {@link createDocumentVersion} instead.
   * @param {string} [data.status] - Updated document status.
   *   Values: 'draft', 'review', 'approved', 'published', 'archived'.
   *   Common transitions:
   *   - draft → review (submit for review)
   *   - review → approved (approve after review)
   *   - approved → published (make active)
   *   - published → archived (retire document)
   * @param {string} [data.framework_id] - Updated associated framework ID.
   *   Set to null to disassociate from any framework.
   * @param {string} [data.owner_id] - Updated owner user ID. Set to null to unassign.
   * @param {string} [data.review_frequency] - Updated review frequency.
   *   Values: 'monthly', 'quarterly', 'semi-annually', 'annually'.
   * @param {number} [data.next_review_date] - Manually set next review date
   *   (Unix timestamp). Overrides calculated date from review_frequency.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the update was applied successfully
   *   - `message` {string} - Human-readable confirmation message
   *   Note: The updated document object is not returned. Use {@link getDocument}
   *   to fetch the updated record if needed.
   *
   * @throws {Error} Throws if:
   *   - Document is not found (404)
   *   - User lacks permission to update the document
   *   - Validation fails (e.g., invalid status transition)
   *
   * @example
   * // Update document status to published
   * await api.updateDocument(documentId, {
   *   status: 'published'
   * });
   * toast.success('Document published');
   *
   * @example
   * // Update document metadata from edit form
   * async function handleSave(documentId: string, formData: Partial<Document>) {
   *   try {
   *     await api.updateDocument(documentId, {
   *       title: formData.title,
   *       description: formData.description,
   *       category: formData.category,
   *       framework_id: formData.frameworkId
   *     });
   *     toast.success('Document updated');
   *     // Refresh to get updated data
   *     const { document } = await api.getDocument(documentId);
   *     setDocument(document);
   *   } catch (error) {
   *     toast.error(`Failed to update document: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Archive a document
   * async function archiveDocument(documentId: string) {
   *   const confirmed = await confirm(
   *     'Are you sure you want to archive this document? ' +
   *     'It will no longer appear in active document lists.'
   *   );
   *   if (!confirmed) return;
   *
   *   await api.updateDocument(documentId, { status: 'archived' });
   *   toast.success('Document archived');
   * }
   *
   * @example
   * // Reassign document ownership
   * await api.updateDocument(documentId, {
   *   owner_id: newOwnerId
   * });
   *
   * @see {@link getDocument} for fetching the updated document
   * @see {@link createDocumentVersion} for versioned content updates
   * @see {@link deleteDocument} for removing documents
   */
  async updateDocument(id: string, data: any) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a document and all its version history.
   *
   * This is a destructive operation that cannot be undone. The document and all
   * associated versions are permanently removed from the system. Any links
   * between this document and controls will be automatically removed.
   *
   * **Recommendation:** For audit trail purposes, consider using
   * {@link updateDocument} to set status to 'archived' instead of deleting.
   * Archived documents remain in the system but are hidden from active views,
   * preserving the historical record.
   *
   * **Required permissions:** User must have 'admin' role for the tenant.
   * Compliance managers can only archive documents, not delete them.
   *
   * @param {string} id - The unique identifier of the document to delete.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the deletion was successful
   *   - `message` {string} - Human-readable confirmation message
   *
   * @throws {Error} Throws if:
   *   - Document is not found (404)
   *   - User lacks admin permission to delete documents
   *   - Document is protected and cannot be deleted (e.g., system-generated
   *     framework templates)
   *   - Document is linked to active compliance assessments
   *
   * @example
   * // Delete with confirmation dialog
   * async function handleDeleteDocument(documentId: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete Document?',
   *     message:
   *       'This will permanently delete this document and all its versions. ' +
   *       'This action cannot be undone. Consider archiving instead.',
   *     confirmLabel: 'Delete Permanently',
   *     destructive: true
   *   });
   *
   *   if (!confirmed) return;
   *
   *   try {
   *     await api.deleteDocument(documentId);
   *     toast.success('Document deleted');
   *     navigate('/documents');
   *   } catch (error) {
   *     toast.error(`Failed to delete document: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Delete or archive based on user preference
   * async function handleRemoveDocument(documentId: string, permanent: boolean) {
   *   if (permanent) {
   *     // Hard delete
   *     await api.deleteDocument(documentId);
   *   } else {
   *     // Soft delete (archive)
   *     await api.updateDocument(documentId, { status: 'archived' });
   *   }
   *   toast.success(permanent ? 'Document deleted' : 'Document archived');
   * }
   *
   * @example
   * // Bulk delete multiple documents
   * async function deleteSelectedDocuments(documentIds: string[]) {
   *   const results = await Promise.allSettled(
   *     documentIds.map(id => api.deleteDocument(id))
   *   );
   *
   *   const succeeded = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   if (failed > 0) {
   *     toast.warning(`${succeeded} deleted, ${failed} failed`);
   *   } else {
   *     toast.success(`${succeeded} documents deleted`);
   *   }
   * }
   *
   * @see {@link updateDocument} with status='archived' for soft deletion
   * @see {@link getDocument} for checking document before deletion
   * @see {@link getDocuments} for listing remaining documents after deletion
   */
  async deleteDocument(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${id}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // DOCUMENT APPROVAL WORKFLOW (Issue #50)
  // ============================================================================

  /**
   * Request approval for a document.
   * Creates a pending approval request for the specified approver.
   * @param id - Document ID
   * @param data - Approval request data including approver_id
   * @returns Approval request details
   */
  async requestDocumentApproval(id: string, data: { approver_id: string; comments?: string }) {
    return this.request<{
      success: boolean
      approval_id: string
    }>(`/api/documents/${id}/request-approval`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Approve a document (approver only).
   * Marks the document as approved and updates status.
   * @param id - Document ID
   * @param data - Optional approval comments
   * @returns Success status
   */
  async approveDocument(id: string, data?: { comments?: string }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  }

  /**
   * Reject a document (approver only).
   * Marks the document as rejected with required comments.
   * @param id - Document ID
   * @param data - Rejection comments (required)
   * @returns Success status
   */
  async rejectDocument(id: string, data: { comments: string }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Recall a document approval request (author only).
   * Cancels a pending approval request.
   * @param id - Document ID
   * @returns Success status
   */
  async recallDocumentApproval(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${id}/recall-approval`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  /**
   * Get approval history for a document.
   * Returns all approval requests and their statuses.
   * @param id - Document ID
   * @returns List of approvals
   */
  async getDocumentApprovals(id: string) {
    return this.request<{
      success: boolean
      approvals: Array<{
        id: string
        version: number
        approver_id: string
        approver_name?: string
        status: 'pending' | 'approved' | 'rejected' | 'recalled'
        requested_by: string
        requester_name?: string
        requested_at: number
        responded_at: number | null
        comments: string | null
      }>
    }>(`/api/documents/${id}/approvals`)
  }

  // ============================================================================
  // DOCUMENT CATEGORIES / FOLDERS (Issue #161)
  // ============================================================================

  /**
   * Get all document categories with hierarchical structure.
   * Returns both a hierarchical tree and flat list for different use cases.
   * @returns Categories as hierarchical tree and flat list
   */
  async getDocumentCategories() {
    return this.request<{
      success: boolean
      categories: any[] // Hierarchical structure
      flat: any[] // Flat list for dropdowns
      count: number
    }>('/api/documents/categories')
  }

  /**
   * Get a single document category with details.
   * Includes child categories and documents in this category.
   * @param id - Category ID
   * @returns Category details with children and documents
   */
  async getDocumentCategory(id: string) {
    return this.request<{
      success: boolean
      category: any
      children: any[]
      documents: any[]
    }>(`/api/documents/categories/${id}`)
  }

  /**
   * Create a new document category (folder).
   * @param data - Category data including name, description, and optional parent_id
   * @returns Created category
   */
  async createDocumentCategory(data: {
    name: string
    description?: string
    parent_id?: string
    color?: string
    icon?: string
  }) {
    return this.request<{
      success: boolean
      category: { id: string; name: string; parent_id: string | null }
      message: string
    }>('/api/documents/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a document category.
   * @param id - Category ID
   * @param data - Updated category data
   * @returns Success message
   */
  async updateDocumentCategory(id: string, data: {
    name?: string
    description?: string
    parent_id?: string | null
    sort_order?: number
    color?: string
    icon?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a document category.
   * Documents in this category become uncategorized, children move to root.
   * @param id - Category ID
   * @returns Affected count info
   */
  async deleteDocumentCategory(id: string) {
    return this.request<{
      success: boolean
      message: string
      affected: { documents_uncategorized: number; children_moved_to_root: number }
    }>(`/api/documents/categories/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Reorder document categories.
   * @param orders - Array of { id, sort_order }
   * @returns Success message
   */
  async reorderDocumentCategories(orders: Array<{ id: string; sort_order: number }>) {
    return this.request<{
      success: boolean
      message: string
    }>('/api/documents/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ orders }),
    })
  }

  // ============================================================================
  // DOCUMENT TEMPLATES (Issue #92)
  // ============================================================================

  /**
   * Get all document templates.
   * @param params - Optional filters
   * @returns List of templates sorted by use count and update time
   */
  async getDocumentTemplates(params: { category?: string } = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.set('category', params.category)
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      templates: Array<{
        id: string
        name: string
        description: string | null
        content: string
        category: string
        tags: string[]
        preview: string | null
        use_count: number
        created_by: string
        created_by_username: string | null
        created_by_first_name: string | null
        created_by_last_name: string | null
        created_at: number
        updated_at: number
      }>
      count: number
    }>(`/api/documents/templates${query ? `?${query}` : ''}`)
  }

  /**
   * Get a single document template by ID.
   * @param id - Template ID
   * @returns Template with full content
   */
  async getDocumentTemplate(id: string) {
    return this.request<{
      success: boolean
      template: {
        id: string
        name: string
        description: string | null
        content: string
        category: string
        tags: string[]
        preview: string | null
        use_count: number
        created_by: string
        created_by_username: string | null
        created_by_first_name: string | null
        created_by_last_name: string | null
        created_at: number
        updated_at: number
      }
    }>(`/api/documents/templates/${id}`)
  }

  /**
   * Create a new document template.
   * @param data - Template data
   * @returns Created template info
   */
  async createDocumentTemplate(data: {
    name: string
    description?: string
    content: string
    category?: string
    tags?: string[]
  }) {
    return this.request<{
      success: boolean
      template: { id: string; name: string }
      message: string
    }>('/api/documents/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a document template.
   * @param id - Template ID
   * @param data - Fields to update
   * @returns Success message
   */
  async updateDocumentTemplate(id: string, data: {
    name?: string
    description?: string
    content?: string
    category?: string
    tags?: string[]
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a document template.
   * @param id - Template ID
   * @returns Success message
   */
  async deleteDocumentTemplate(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/templates/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Create a new document from a template.
   * @param templateId - Template ID to use
   * @param data - Document creation data
   * @returns Created document info
   */
  async createDocumentFromTemplate(templateId: string, data: {
    title: string
    description?: string
    category_id?: string
    framework_id?: string
    framework_ids?: string[]
    review_frequency?: string
    variables?: Record<string, string>
  }) {
    return this.request<{
      success: boolean
      document: { id: string; title: string }
      message: string
    }>(`/api/documents/from-template/${templateId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get available template variables.
   * @returns Available variables with descriptions
   */
  async getTemplateVariables() {
    return this.request<{
      success: boolean
      variables: Array<{
        name: string
        description: string
        example: string
      }>
      syntax: string
      example: string
    }>('/api/documents/templates/variables/available')
  }

  // ============================================================================
  // DOCUMENT-CONTROL LINKING (Issue #212)
  // ============================================================================

  /**
   * Get controls linked to a document.
   * @param documentId - Document ID
   * @returns List of linked controls with framework info
   */
  async getDocumentControls(documentId: string) {
    return this.request<{
      success: boolean
      controls: any[]
      count: number
    }>(`/api/documents/${documentId}/controls`)
  }

  /**
   * Link controls to a document.
   * @param documentId - Document ID
   * @param data - Object with control_ids array
   * @returns Number of controls linked
   */
  async linkDocumentControls(documentId: string, data: { control_ids: string[] }) {
    return this.request<{
      success: boolean
      linked: number
      message: string
    }>(`/api/documents/${documentId}/controls`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Unlink a control from a document.
   * @param documentId - Document ID
   * @param controlId - Control ID to unlink
   */
  async unlinkDocumentControl(documentId: string, controlId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${documentId}/controls/${controlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Replace all control links for a document.
   * @param documentId - Document ID
   * @param data - Object with control_ids array
   */
  async replaceDocumentControls(documentId: string, data: { control_ids: string[] }) {
    return this.request<{
      success: boolean
      linked: number
      message: string
    }>(`/api/documents/${documentId}/controls`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // DOCUMENT-INTERNAL CONTROL LINKING (#311 Common Control Framework)
  // ============================================================================

  /**
   * Get internal controls linked to a document.
   * @param documentId - Document ID
   * @returns List of linked internal controls
   */
  async getDocumentInternalControls(documentId: string) {
    return this.request<{
      success: boolean
      internal_controls: any[]
      count: number
    }>(`/api/documents/${documentId}/internal-controls`)
  }

  /**
   * Link internal controls to a document (policy).
   * This is the recommended way to link policies - satisfies all frameworks via common controls.
   * @param documentId - Document ID
   * @param data - Object with internal_control_ids array
   * @returns Number of internal controls linked
   */
  async linkDocumentInternalControls(documentId: string, data: { internal_control_ids: string[] }) {
    return this.request<{
      success: boolean
      linked: number
      message: string
    }>(`/api/documents/${documentId}/internal-controls`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Unlink an internal control from a document.
   * @param documentId - Document ID
   * @param internalControlId - Internal control ID to unlink
   */
  async unlinkDocumentInternalControl(documentId: string, internalControlId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${documentId}/internal-controls/${internalControlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Generates a new document using AI based on the specified title, category, and framework.
   *
   * This method leverages AI to automatically create policy/procedure documents
   * with appropriate content based on compliance framework requirements. The AI
   * generates comprehensive, industry-standard content tailored to the document
   * type and the selected framework's control requirements.
   *
   * Use this method when you want AI-generated content. For manually-authored
   * documents, use {@link createDocument} instead.
   *
   * **Generation process:**
   * 1. Analyzes the title and category to understand document purpose
   * 2. If framework_id is provided, incorporates relevant control requirements
   * 3. Generates comprehensive content following industry best practices
   * 4. Creates the document with version 1 and 'draft' status
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role
   * for the tenant.
   *
   * @param {Object} data - The document generation parameters.
   * @param {string} data.title - Document title describing the policy/procedure.
   *   Be specific to get better AI-generated content (e.g., "Data Classification
   *   and Handling Policy" rather than just "Data Policy").
   * @param {string} data.category - Document category/type. Common values:
   *   - `policy` - High-level organizational policies
   *   - `procedure` - Step-by-step operational procedures
   *   - `standard` - Technical standards and baselines
   *   - `guideline` - Best practice recommendations
   *   - `plan` - Business continuity, disaster recovery, incident response plans
   * @param {string} [data.framework_id] - ID of the compliance framework to
   *   incorporate requirements from. The AI will tailor content to address
   *   relevant controls from this framework (e.g., SOC 2, ISO 27001, HIPAA).
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether generation was successful
   *   - `document` {Object} - The generated document:
   *     - `id` {string} - Unique document identifier (UUID)
   *     - `title` {string} - Document title
   *     - `category` {string} - Document category
   *     - `content` {string} - AI-generated document content
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - User lacks permission to create documents
   *   - Required fields (title, category) are missing
   *   - Framework ID is invalid (if provided)
   *   - AI generation service is unavailable
   *
   * @example
   * // Generate a policy document for SOC 2
   * const { document } = await api.generateDocument({
   *   title: 'Access Control Policy',
   *   category: 'policy',
   *   framework_id: soc2FrameworkId
   * });
   *
   * console.log(`Generated document with ${document.content.length} characters`);
   * navigate(`/documents/${document.id}/edit`);
   *
   * @example
   * // Generate procedure from UI form
   * async function handleGenerateDocument(formData: GenerateDocumentForm) {
   *   try {
   *     setGenerating(true);
   *     const { document, message } = await api.generateDocument({
   *       title: formData.title,
   *       category: formData.category,
   *       framework_id: formData.frameworkId || undefined
   *     });
   *
   *     toast.success(message);
   *     // Navigate to review/edit the generated content
   *     navigate(`/documents/${document.id}/edit`);
   *   } catch (error) {
   *     toast.error(`Generation failed: ${error.message}`);
   *   } finally {
   *     setGenerating(false);
   *   }
   * }
   *
   * @example
   * // Quick generation from template selection
   * async function generateFromTemplate(template: DocumentTemplate) {
   *   const { document } = await api.generateDocument({
   *     title: template.title,
   *     category: template.category,
   *     framework_id: selectedFramework
   *   });
   *   return document;
   * }
   *
   * @see {@link createDocument} for manually-authored documents
   * @see {@link getDocumentTemplates} for available document templates by framework
   * @see {@link generateAllDocuments} for bulk generation of all framework documents
   * @see {@link regenerateDocument} for regenerating existing document content
   */
  async generateDocument(data: {
    title: string
    category: string
    framework_id?: string
  }) {
    return this.request<{
      success: boolean
      document: {
        id: string
        title: string
        category: string
        content: string
      }
      message: string
      generation?: {
        source: 'ai' | 'fallback' | 'cache'
        cached: boolean
        isFallback: boolean
        fallbackReason?: string
        retryCount?: number
      }
    }>('/api/documents/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Creates a new version of an existing document with updated content.
   *
   * This method implements semantic versioning for document management, allowing
   * organizations to track changes over time while maintaining a complete audit
   * trail. Each version is immutable once created, supporting compliance
   * requirements for document control.
   *
   * **Versioning semantics:**
   * - `major` - Significant policy changes, restructuring, or scope changes (1.0 → 2.0)
   * - `minor` - New sections, expanded coverage, or feature additions (1.0 → 1.1)
   * - `patch` - Typo fixes, clarifications, formatting changes (1.0 → 1.0.1)
   *
   * **Version control workflow:**
   * 1. Retrieve current document with {@link getDocument}
   * 2. Edit content in the UI
   * 3. Create new version with this method
   * 4. Previous versions remain accessible via {@link getDocumentVersion}
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role
   * for the tenant.
   *
   * @param {string} id - The unique identifier (UUID) of the document to version.
   * @param {Object} data - The version data.
   * @param {string} data.content - The complete updated document content. This
   *   replaces the entire content - partial updates are not supported.
   * @param {string} [data.change_summary] - Description of what changed in this
   *   version. Displayed in version history and audit logs. Should be concise
   *   but descriptive (e.g., "Added remote work policy section").
   * @param {'major' | 'minor' | 'patch'} [data.change_type='minor'] - The type
   *   of change for semantic versioning:
   *   - `major` - Breaking or significant changes
   *   - `minor` - Backward-compatible additions (default)
   *   - `patch` - Backward-compatible fixes
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the version was created successfully
   *   - `version` {number} - The new version number
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - User lacks permission to edit documents
   *   - Content is empty or invalid
   *   - Document is archived (cannot create new versions)
   *
   * @example
   * // Create a minor version with content changes
   * const { version } = await api.createDocumentVersion(documentId, {
   *   content: updatedContent,
   *   change_summary: 'Added section on password requirements',
   *   change_type: 'minor'
   * });
   *
   * toast.success(`Created version ${version}`);
   *
   * @example
   * // Document editor save handler
   * async function handleSaveDocument(documentId: string, editor: Editor) {
   *   const content = editor.getContent();
   *   const changeType = determineChangeType(originalContent, content);
   *
   *   try {
   *     const { version, message } = await api.createDocumentVersion(documentId, {
   *       content,
   *       change_summary: changeSummaryInput.value,
   *       change_type: changeType
   *     });
   *
   *     toast.success(message);
   *     setCurrentVersion(version);
   *     setHasUnsavedChanges(false);
   *   } catch (error) {
   *     toast.error(`Failed to save: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Version history comparison
   * async function compareVersions(docId: string, v1: number, v2: number) {
   *   const [version1, version2] = await Promise.all([
   *     api.getDocumentVersion(docId, v1),
   *     api.getDocumentVersion(docId, v2)
   *   ]);
   *   return diffContent(version1.version.content, version2.version.content);
   * }
   *
   * @see {@link getDocument} for retrieving current document with version history
   * @see {@link getDocumentVersion} for fetching a specific version's content
   * @see {@link updateDocument} for updating metadata without creating a version
   */
  async createDocumentVersion(id: string, data: {
    content: string
    change_summary?: string
    change_type?: 'major' | 'minor' | 'patch'
    expected_version?: number  // For conflict detection (Issue #177)
    force?: boolean           // Force save even with conflicts
  }) {
    return this.request<{
      success: boolean
      version: number
      updated_at: number
      message: string
      // Conflict response fields (status 409)
      error?: string
      conflict_type?: 'locked' | 'version_conflict'
      locked_by?: string
      lock_expires_at?: number
      expected_version?: number
      current_version?: number
      current_content?: string
      conflicting_edit?: {
        version: number
        created_by: string
        created_by_name: string
        created_at: number
        change_summary: string | null
      }
    }>(`/api/documents/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Retrieves a specific version of a document by its version number.
   *
   * This method fetches the complete content and metadata of a specific
   * document version from the version history. Use this to view previous
   * versions, compare changes between versions, or restore old content.
   *
   * **Use cases:**
   * - Viewing historical versions of a document
   * - Comparing content between versions (diff view)
   * - Restoring a previous version by copying its content
   * - Audit trail review for compliance purposes
   *
   * @param {string} id - The unique identifier (UUID) of the document.
   * @param {number} version - The version number to retrieve. Version numbers
   *   start at 1 and increment with each new version. Get the list of available
   *   versions from {@link getDocument}.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the version was retrieved successfully
   *   - `version` {Object} - The version data:
   *     - `version` {number} - Version number
   *     - `content` {string} - Document content at this version
   *     - `change_summary` {string} - Description of changes made in this version
   *     - `change_type` {string} - Type of change ('major', 'minor', 'patch')
   *     - `created_by` {string} - User ID who created this version
   *     - `created_at` {number} - Unix timestamp when version was created
   *     - `file_key` {string} - Storage key if this version has an uploaded file
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - Version number doesn't exist
   *   - User lacks permission to view the document
   *
   * @example
   * // View a specific historical version
   * const { version } = await api.getDocumentVersion(documentId, 3);
   * console.log(`Version 3 content: ${version.content.substring(0, 100)}...`);
   * console.log(`Changed by: ${version.created_by} on ${new Date(version.created_at)}`);
   *
   * @example
   * // Version comparison component
   * function VersionComparison({ documentId, v1, v2 }: Props) {
   *   const [version1, setVersion1] = useState<DocumentVersion | null>(null);
   *   const [version2, setVersion2] = useState<DocumentVersion | null>(null);
   *
   *   useEffect(() => {
   *     Promise.all([
   *       api.getDocumentVersion(documentId, v1),
   *       api.getDocumentVersion(documentId, v2)
   *     ]).then(([res1, res2]) => {
   *       setVersion1(res1.version);
   *       setVersion2(res2.version);
   *     });
   *   }, [documentId, v1, v2]);
   *
   *   if (!version1 || !version2) return <Loading />;
   *
   *   return (
   *     <DiffViewer
   *       oldContent={version1.content}
   *       newContent={version2.content}
   *       oldTitle={`Version ${v1}`}
   *       newTitle={`Version ${v2}`}
   *     />
   *   );
   * }
   *
   * @example
   * // Restore a previous version by creating a new version with old content
   * async function restoreVersion(documentId: string, versionToRestore: number) {
   *   const { version } = await api.getDocumentVersion(documentId, versionToRestore);
   *
   *   await api.createDocumentVersion(documentId, {
   *     content: version.content,
   *     change_summary: `Restored from version ${versionToRestore}`,
   *     change_type: 'major'
   *   });
   *
   *   toast.success(`Restored to version ${versionToRestore}`);
   * }
   *
   * @see {@link getDocument} for getting document with all version metadata
   * @see {@link createDocumentVersion} for creating new versions
   * @see {@link getDocumentDownloadUrl} for downloading versioned file attachments
   */
  async getDocumentVersion(id: string, version: number) {
    return this.request<{
      success: boolean
      version: any
    }>(`/api/documents/${id}/versions/${version}`)
  }

  /**
   * Compare two versions of a document (Issue #63)
   * Returns a line-by-line diff showing additions, removals, and modifications.
   *
   * @param id - Document ID
   * @param v1 - First version number (older)
   * @param v2 - Second version number (newer)
   * @returns Diff result with lines and summary
   */
  async getDocumentDiff(id: string, v1: number, v2: number) {
    return this.request<{
      success: boolean
      version1: number
      version2: number
      diff: {
        lines: Array<{
          type: 'unchanged' | 'added' | 'removed' | 'modified'
          line1?: string
          line2?: string
          lineNum1?: number
          lineNum2?: number
        }>
        summary: {
          added: number
          removed: number
          modified: number
          unchanged: number
          total: number
        }
      }
    }>(`/api/documents/${id}/diff?v1=${v1}&v2=${v2}`)
  }

  /**
   * Uploads a file attachment for a document, creating a new version.
   *
   * This method allows uploading binary files (PDFs, Word documents, etc.) as
   * document attachments. Each upload creates a new version of the document,
   * maintaining a complete history of file changes for compliance purposes.
   *
   * **Supported file types:**
   * - PDF documents (.pdf)
   * - Microsoft Word (.doc, .docx)
   * - Plain text (.txt)
   * - Rich text format (.rtf)
   * - Markdown (.md)
   *
   * **Upload process:**
   * 1. File is uploaded to secure storage (S3/cloud storage)
   * 2. A new version is created with the file_key reference
   * 3. The version number is incremented
   * 4. Original filename and metadata are preserved
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role
   * for the tenant.
   *
   * @param {string} id - The unique identifier (UUID) of the document.
   * @param {File} file - The file to upload. This is a browser File object,
   *   typically from an `<input type="file">` element or drag-and-drop.
   *   Maximum file size is typically 10MB (configurable server-side).
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the upload was successful
   *   - `version` {number} - The new version number created by this upload
   *   - `file_key` {string} - Storage key for the uploaded file (used for downloads)
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - User lacks permission to edit documents
   *   - File is too large (exceeds server limit)
   *   - File type is not allowed
   *   - Storage service is unavailable
   *
   * @example
   * // Basic file upload from input element
   * const fileInput = document.getElementById('file-input') as HTMLInputElement;
   * const file = fileInput.files?.[0];
   *
   * if (file) {
   *   const { version, file_key } = await api.uploadDocumentFile(documentId, file);
   *   toast.success(`Uploaded as version ${version}`);
   * }
   *
   * @example
   * // React file upload component
   * function DocumentUploader({ documentId }: { documentId: string }) {
   *   const [uploading, setUploading] = useState(false);
   *
   *   const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
   *     const file = e.target.files?.[0];
   *     if (!file) return;
   *
   *     try {
   *       setUploading(true);
   *       const { version, message } = await api.uploadDocumentFile(documentId, file);
   *       toast.success(message);
   *       onVersionCreated?.(version);
   *     } catch (error) {
   *       toast.error(`Upload failed: ${error.message}`);
   *     } finally {
   *       setUploading(false);
   *     }
   *   };
   *
   *   return (
   *     <input
   *       type="file"
   *       accept=".pdf,.doc,.docx,.txt,.md"
   *       onChange={handleFileChange}
   *       disabled={uploading}
   *     />
   *   );
   * }
   *
   * @example
   * // Drag and drop upload
   * async function handleDrop(e: DragEvent, documentId: string) {
   *   e.preventDefault();
   *   const file = e.dataTransfer?.files[0];
   *
   *   if (file && file.type === 'application/pdf') {
   *     const { version } = await api.uploadDocumentFile(documentId, file);
   *     toast.success(`PDF uploaded as version ${version}`);
   *   }
   * }
   *
   * @see {@link getDocumentDownloadUrl} for downloading uploaded files
   * @see {@link createDocumentVersion} for text-based version updates
   * @see {@link getDocumentVersion} for retrieving version with file_key
   */
  async uploadDocumentFile(id: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<{
      success: boolean
      version: number
      file_key: string
      message: string
    }>(`/api/documents/${id}/upload`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    })
  }

  /**
   * Generates the download URL for a specific document version's file attachment.
   *
   * This is a synchronous method that constructs the URL for downloading a
   * document file. Unlike other API methods, this does not make a network
   * request - it simply returns the URL string that can be used directly
   * in anchor tags, window.open(), or fetch requests.
   *
   * **Important:** The returned URL requires authentication. The browser will
   * automatically include cookies for same-origin requests. For cross-origin
   * or programmatic downloads, you may need to handle authentication headers.
   *
   * **Use cases:**
   * - Direct download via anchor tag with download attribute
   * - Opening file in new tab for preview
   * - Programmatic downloads with fetch/XMLHttpRequest
   *
   * @param {string} id - The unique identifier (UUID) of the document.
   * @param {number} version - The version number to download. Get available
   *   versions from {@link getDocument} or {@link getDocumentVersion}.
   *
   * @returns {string} The full URL to download the document file. The URL
   *   includes the API base URL and tenant context. The server will respond
   *   with appropriate Content-Disposition headers for download.
   *
   * @example
   * // Download button with anchor tag
   * function DownloadButton({ documentId, version }: Props) {
   *   const downloadUrl = api.getDocumentDownloadUrl(documentId, version);
   *
   *   return (
   *     <a
   *       href={downloadUrl}
   *       download
   *       className="btn btn-primary"
   *     >
   *       Download Version {version}
   *     </a>
   *   );
   * }
   *
   * @example
   * // Open in new tab for preview
   * function handlePreview(documentId: string, version: number) {
   *   const url = api.getDocumentDownloadUrl(documentId, version);
   *   window.open(url, '_blank');
   * }
   *
   * @example
   * // Programmatic download with progress tracking
   * async function downloadWithProgress(documentId: string, version: number) {
   *   const url = api.getDocumentDownloadUrl(documentId, version);
   *
   *   const response = await fetch(url, { credentials: 'include' });
   *   const blob = await response.blob();
   *
   *   // Create download link
   *   const downloadLink = document.createElement('a');
   *   downloadLink.href = URL.createObjectURL(blob);
   *   downloadLink.download = `document-v${version}.pdf`;
   *   downloadLink.click();
   *
   *   URL.revokeObjectURL(downloadLink.href);
   * }
   *
   * @example
   * // Version list with download links
   * function VersionList({ documentId, versions }: Props) {
   *   return (
   *     <ul>
   *       {versions.map(v => (
   *         <li key={v.version}>
   *           Version {v.version} - {formatDate(v.created_at)}
   *           <a href={api.getDocumentDownloadUrl(documentId, v.version)} download>
   *             Download
   *           </a>
   *         </li>
   *       ))}
   *     </ul>
   *   );
   * }
   *
   * @see {@link uploadDocumentFile} for uploading document files
   * @see {@link getDocumentVersion} for retrieving version metadata
   * @see {@link getEvidenceDownloadUrl} for similar functionality with evidence files
   */
  getDocumentDownloadUrl(id: string, version: number) {
    return `${getApiBase()}/api/documents/${id}/download/${version}`
  }

  /**
   * Retrieves available document templates for a specific compliance framework.
   *
   * This method returns a list of recommended policy and procedure templates
   * based on the selected framework's requirements. Templates provide starting
   * points for creating compliant documents and can be used with
   * {@link generateDocument} to create AI-generated content.
   *
   * **Template structure:**
   * Each template defines a document that addresses specific framework controls.
   * Templates are curated based on industry best practices and common compliance
   * requirements.
   *
   * **Supported frameworks:**
   * - `soc2` - SOC 2 Type II (Security, Availability, Processing Integrity, Confidentiality, Privacy)
   * - `iso27001` - ISO/IEC 27001 Information Security Management
   * - `hipaa` - Health Insurance Portability and Accountability Act
   * - `pci-dss` - Payment Card Industry Data Security Standard
   * - `gdpr` - General Data Protection Regulation
   * - `nist` - NIST Cybersecurity Framework
   *
   * @param {string} framework - The framework identifier (e.g., 'soc2', 'iso27001').
   *   Use {@link getFrameworks} to get the list of available framework IDs.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request was successful
   *   - `framework` {string} - The framework ID that was queried
   *   - `templates` {Array<Object>} - List of available templates:
   *     - `title` {string} - Recommended document title
   *     - `category` {string} - Document category (policy, procedure, etc.)
   *     - `description` {string} - Brief description of what the document covers
   *   - `count` {number} - Total number of templates available
   *
   * @throws {Error} Throws if:
   *   - Framework ID is invalid or not found
   *   - User is not authenticated
   *
   * @example
   * // Load templates for SOC 2 framework
   * const { templates, count } = await api.getDocumentTemplates('soc2');
   * console.log(`Found ${count} templates for SOC 2`);
   *
   * templates.forEach(t => {
   *   console.log(`${t.title} (${t.category}): ${t.description}`);
   * });
   *
   * @example
   * // Template selector component
   * function TemplateSelector({ frameworkId, onSelect }: Props) {
   *   const [templates, setTemplates] = useState<Template[]>([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getDocumentTemplates(frameworkId)
   *       .then(({ templates }) => setTemplates(templates))
   *       .finally(() => setLoading(false));
   *   }, [frameworkId]);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div className="template-grid">
   *       {templates.map(template => (
   *         <TemplateCard
   *           key={template.title}
   *           template={template}
   *           onClick={() => onSelect(template)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Generate document from template
   * async function createFromTemplate(template: Template, frameworkId: string) {
   *   const { document } = await api.generateDocument({
   *     title: template.title,
   *     category: template.category,
   *     framework_id: frameworkId
   *   });
   *
   *   navigate(`/documents/${document.id}/edit`);
   * }
   *
   * @example
   * // Show template count in framework selection
   * async function loadFrameworksWithCounts() {
   *   const { frameworks } = await api.getFrameworks();
   *
   *   const withCounts = await Promise.all(
   *     frameworks.filter(f => f.enabled).map(async framework => {
   *       const { count } = await api.getDocumentTemplates(framework.id);
   *       return { ...framework, templateCount: count };
   *     })
   *   );
   *
   *   return withCounts;
   * }
   *
   * @see {@link getFrameworks} for listing available frameworks
   * @see {@link generateDocument} for creating documents from templates
   * @see {@link generateAllDocuments} for bulk generation of all framework templates
   */
  async getFrameworkDocumentTemplates(framework: string) {
    return this.request<{
      success: boolean
      framework: string
      templates: Array<{
        title: string
        category: string
        description: string
      }>
      count: number
    }>(`/api/documents/templates/${framework}`)
  }

  /**
   * Bulk generates all required documents for a compliance framework using AI.
   *
   * This method automatically generates the complete set of policies and
   * procedures recommended for a specific compliance framework. It uses
   * the framework's template list and creates AI-generated content for each
   * document that doesn't already exist.
   *
   * **Generation process:**
   * 1. Fetches all templates for the specified framework
   * 2. Checks each template against existing documents
   * 3. For new documents: generates AI content and creates document
   * 4. For existing documents: skips or maps based on title matching
   * 5. Returns detailed results for each template
   *
   * **Result statuses:**
   * - `created` - New document was generated successfully
   * - `mapped` - Existing document found with matching title (linked to framework)
   * - `skipped` - Document already exists for this framework
   * - `error` - Generation failed for this template
   *
   * **Performance note:** This operation can take several minutes depending on
   * the number of templates. Consider showing a progress indicator to users.
   *
   * **Required permissions:** User must have 'admin' role for the tenant.
   *
   * @param {string} frameworkId - The unique identifier (UUID) of the framework
   *   to generate documents for. Use {@link getFrameworks} to get available IDs.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the operation completed (may still have errors)
   *   - `message` {string} - Overall status message
   *   - `results` {Array<Object>} - Detailed result for each template:
   *     - `title` {string} - Document title from template
   *     - `id` {string} - Document ID (for created/mapped documents)
   *     - `status` {'created' | 'mapped' | 'skipped' | 'error'} - Result status
   *     - `message` {string} - Status message (for success cases)
   *     - `error` {string} - Error message (for error cases)
   *   - `summary` {Object} - Aggregated counts:
   *     - `total` {number} - Total templates processed
   *     - `created` {number} - New documents generated
   *     - `mapped` {number} - Existing documents linked
   *     - `skipped` {number} - Already existing, skipped
   *     - `error` {number} - Failed to generate
   *
   * @throws {Error} Throws if:
   *   - User lacks admin permission
   *   - Framework ID is invalid
   *   - AI generation service is unavailable
   *
   * @example
   * // Basic bulk generation with summary
   * async function generateFrameworkDocs(frameworkId: string) {
   *   toast.info('Generating documents... This may take a few minutes.');
   *
   *   const { summary, results } = await api.generateAllDocuments(frameworkId);
   *
   *   toast.success(
   *     `Generated ${summary.created} documents, ` +
   *     `mapped ${summary.mapped} existing, ` +
   *     `${summary.error} errors`
   *   );
   *
   *   return results;
   * }
   *
   * @example
   * // Bulk generation with progress tracking
   * function BulkGenerationModal({ frameworkId, onComplete }: Props) {
   *   const [generating, setGenerating] = useState(false);
   *   const [results, setResults] = useState<GenerationResult[] | null>(null);
   *
   *   const handleGenerate = async () => {
   *     setGenerating(true);
   *     try {
   *       const response = await api.generateAllDocuments(frameworkId);
   *       setResults(response.results);
   *
   *       if (response.summary.error === 0) {
   *         toast.success('All documents generated successfully!');
   *       } else {
   *         toast.warning(`${response.summary.error} documents failed to generate`);
   *       }
   *
   *       onComplete?.(response);
   *     } catch (error) {
   *       toast.error(`Generation failed: ${error.message}`);
   *     } finally {
   *       setGenerating(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <h2>Generate All Framework Documents</h2>
   *       {results ? (
   *         <ResultsList results={results} />
   *       ) : (
   *         <Button onClick={handleGenerate} loading={generating}>
   *           {generating ? 'Generating...' : 'Generate All Documents'}
   *         </Button>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Handle errors and retry failed documents
   * async function generateWithRetry(frameworkId: string) {
   *   const { results, summary } = await api.generateAllDocuments(frameworkId);
   *
   *   // Log errors for debugging
   *   const errors = results.filter(r => r.status === 'error');
   *   errors.forEach(e => console.error(`Failed: ${e.title} - ${e.error}`));
   *
   *   // Retry individual documents that failed
   *   for (const failed of errors) {
   *     try {
   *       await api.generateDocument({
   *         title: failed.title,
   *         category: 'policy', // May need to determine from template
   *         framework_id: frameworkId
   *       });
   *       console.log(`Retry succeeded: ${failed.title}`);
   *     } catch (e) {
   *       console.error(`Retry failed: ${failed.title}`);
   *     }
   *   }
   * }
   *
   * @see {@link getDocumentTemplates} for viewing available templates before generation
   * @see {@link generateDocument} for generating individual documents
   * @see {@link getDocuments} for listing generated documents
   * @see {@link getFrameworks} for available frameworks
   */
  async generateAllDocuments(frameworkId: string) {
    return this.request<{
      success: boolean
      message: string
      results: Array<{
        title: string
        id: string
        status: 'created' | 'mapped' | 'skipped' | 'error'
        message?: string
        error?: string
        isFallback?: boolean
      }>
      summary: {
        total: number
        created: number
        mapped: number
        skipped: number
        error: number
        fallback?: number
      }
    }>('/api/documents/generate-all', {
      method: 'POST',
      body: JSON.stringify({ framework_id: frameworkId }),
    })
  }

  /**
   * Regenerates an existing document's content using AI.
   *
   * This method uses AI to create fresh content for an existing document based
   * on its title, category, and framework association. The new content replaces
   * the current content by creating a new version, preserving the document's
   * history and metadata.
   *
   * **Use cases:**
   * - Refresh outdated document content with current best practices
   * - Get a new AI interpretation when the original wasn't satisfactory
   * - Update content after framework requirements change
   * - Reset a heavily-edited document to AI baseline
   *
   * **Regeneration process:**
   * 1. Retrieves the document's title, category, and framework
   * 2. Generates new AI content based on these parameters
   * 3. Creates a new version with the regenerated content
   * 4. Previous version remains accessible via version history
   *
   * **Important:** This creates a new version - it does not overwrite history.
   * Users can compare or restore previous versions if needed.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role
   * for the tenant.
   *
   * @param {string} id - The unique identifier (UUID) of the document to regenerate.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether regeneration was successful
   *   - `message` {string} - Human-readable success message
   *   - `version` {number} - The new version number containing regenerated content
   *   - `content` {string} - The newly generated document content
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - User lacks permission to edit documents
   *   - Document is archived (cannot regenerate archived documents)
   *   - AI generation service is unavailable
   *
   * @example
   * // Simple regeneration with confirmation
   * async function handleRegenerate(documentId: string) {
   *   const confirmed = await confirm(
   *     'This will generate new content based on AI. ' +
   *     'Current content will be preserved in version history. Continue?'
   *   );
   *
   *   if (!confirmed) return;
   *
   *   try {
   *     const { version, content, message } = await api.regenerateDocument(documentId);
   *     toast.success(message);
   *     setDocumentContent(content);
   *     setCurrentVersion(version);
   *   } catch (error) {
   *     toast.error(`Regeneration failed: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Regenerate button component
   * function RegenerateButton({ documentId, onRegenerated }: Props) {
   *   const [regenerating, setRegenerating] = useState(false);
   *
   *   const handleClick = async () => {
   *     setRegenerating(true);
   *     try {
   *       const result = await api.regenerateDocument(documentId);
   *       toast.success(`Regenerated as version ${result.version}`);
   *       onRegenerated?.(result);
   *     } catch (error) {
   *       toast.error('Failed to regenerate document');
   *     } finally {
   *       setRegenerating(false);
   *     }
   *   };
   *
   *   return (
   *     <Button
   *       onClick={handleClick}
   *       loading={regenerating}
   *       icon={<RefreshIcon />}
   *     >
   *       {regenerating ? 'Regenerating...' : 'Regenerate with AI'}
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Compare current vs regenerated content before saving
   * async function previewRegeneration(documentId: string, currentContent: string) {
   *   // Get regenerated content without committing
   *   const { content: newContent } = await api.regenerateDocument(documentId);
   *
   *   // Show diff view to user
   *   showDiffModal({
   *     title: 'Review Regenerated Content',
   *     oldContent: currentContent,
   *     newContent: newContent,
   *     oldLabel: 'Current',
   *     newLabel: 'AI Regenerated'
   *   });
   * }
   *
   * @see {@link generateDocument} for creating new AI-generated documents
   * @see {@link createDocumentVersion} for manual content updates
   * @see {@link getDocumentVersion} for viewing previous versions after regeneration
   * @see {@link updateDocument} for updating metadata without changing content
   */
  async regenerateDocument(id: string) {
    return this.request<{
      success: boolean
      message: string
      version: number
      content: string
      generation?: {
        source: 'ai' | 'fallback' | 'cache'
        isFallback: boolean
        fallbackReason?: string
        retryCount?: number
      }
    }>(`/api/documents/${id}/regenerate`, {
      method: 'POST',
    })
  }

  // Document Control Suggestions (Policy Auto-Linking)

  /**
   * Retrieves AI-generated control-to-document mapping suggestions for a document.
   *
   * This method returns suggestions where the AI has identified potential relationships
   * between a document (typically a policy or procedure) and compliance framework controls.
   * The AI analyzes the document's content and matches it against control requirements
   * to suggest which controls the document helps satisfy.
   *
   * **How AI suggestions work:**
   * 1. When a document is created or analyzed, AI scans its content
   * 2. AI compares content against all enabled framework controls
   * 3. Matches are scored by confidence (0.0-1.0) with reasoning
   * 4. Suggestions remain "pending" until a user reviews them
   * 5. Users can accept (creates link) or reject (dismisses) each suggestion
   *
   * **Confidence scoring:**
   * - **0.9-1.0**: Very high confidence - strong keyword and context match
   * - **0.7-0.9**: High confidence - good semantic alignment
   * - **0.5-0.7**: Medium confidence - partial match, review recommended
   * - **Below 0.5**: Low confidence - weak correlation, likely noise
   *
   * **Suggestion statuses:**
   * - `pending`: Not yet reviewed by a user
   * - `accepted`: User confirmed the link (control is now linked to document)
   * - `rejected`: User dismissed the suggestion (no link created)
   *
   * **Required permissions:** User must have read access to the document.
   *
   * @param {string} documentId - The unique identifier (UUID) of the document.
   * @param {'pending' | 'accepted' | 'rejected'} [status] - Optional filter to return
   *   only suggestions with a specific review status. If omitted, returns all suggestions.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request was successful
   *   - `suggestions` {Array<Object>} - Array of suggestion objects:
   *     - `id` {string} - Unique suggestion identifier (UUID)
   *     - `document_id` {string} - The document this suggestion belongs to
   *     - `control_id` {string} - The suggested control to link
   *     - `confidence_score` {number} - AI confidence (0.0-1.0) in the match
   *     - `match_reason` {string} - AI explanation of why this control matches
   *     - `status` {'pending' | 'accepted' | 'rejected'} - Review status
   *     - `control_number` {string} - Control identifier (e.g., "CC6.1", "A.9.1.1")
   *     - `control_name` {string} - Human-readable control name
   *     - `control_description` {string} - Full control description
   *     - `framework_name` {string} - Framework the control belongs to (e.g., "SOC 2")
   *     - `reviewed_by_username` {string} [optional] - Who reviewed this suggestion
   *     - `reviewed_at` {number} [optional] - Unix timestamp of review
   *   - `count` {number} - Total number of suggestions matching the filter
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - User lacks permission to view the document
   *   - Network or server error occurs
   *
   * @example
   * // Load pending suggestions for review
   * async function loadPendingSuggestions(documentId: string) {
   *   const { suggestions, count } = await api.getDocumentSuggestions(
   *     documentId,
   *     'pending'
   *   );
   *
   *   console.log(`${count} suggestions awaiting review`);
   *
   *   // Sort by confidence for prioritized review
   *   const sorted = suggestions.sort(
   *     (a, b) => b.confidence_score - a.confidence_score
   *   );
   *
   *   return sorted;
   * }
   *
   * @example
   * // Suggestion review component
   * function SuggestionReview({ documentId }: Props) {
   *   const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getDocumentSuggestions(documentId, 'pending')
   *       .then(({ suggestions }) => setSuggestions(suggestions))
   *       .finally(() => setLoading(false));
   *   }, [documentId]);
   *
   *   const handleAccept = async (suggestionId: string) => {
   *     await api.acceptDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestionId]
   *     });
   *     setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
   *     toast.success('Control linked to document');
   *   };
   *
   *   const handleReject = async (suggestionId: string) => {
   *     await api.rejectDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestionId]
   *     });
   *     setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
   *   };
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div>
   *       <h3>AI Suggested Controls ({suggestions.length})</h3>
   *       {suggestions.map(suggestion => (
   *         <SuggestionCard
   *           key={suggestion.id}
   *           suggestion={suggestion}
   *           onAccept={() => handleAccept(suggestion.id)}
   *           onReject={() => handleReject(suggestion.id)}
   *         />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Show suggestion history (all statuses)
   * async function getSuggestionHistory(documentId: string) {
   *   const { suggestions } = await api.getDocumentSuggestions(documentId);
   *
   *   const pending = suggestions.filter(s => s.status === 'pending');
   *   const accepted = suggestions.filter(s => s.status === 'accepted');
   *   const rejected = suggestions.filter(s => s.status === 'rejected');
   *
   *   return {
   *     pending: pending.length,
   *     accepted: accepted.length,
   *     rejected: rejected.length,
   *     total: suggestions.length
   *   };
   * }
   *
   * @see {@link analyzeDocumentControls} for triggering AI to generate new suggestions
   * @see {@link acceptDocumentSuggestions} for accepting suggested control links
   * @see {@link rejectDocumentSuggestions} for rejecting suggested control links
   * @see {@link getControlPolicies} for viewing existing control-document links
   */
  async getDocumentSuggestions(documentId: string, status?: 'pending' | 'accepted' | 'rejected') {
    const params = status ? `?status=${status}` : ''
    return this.request<{
      success: boolean
      suggestions: Array<{
        id: string
        document_id: string
        control_id: string
        confidence_score: number
        match_reason: string
        status: 'pending' | 'accepted' | 'rejected'
        control_number: string
        control_name: string
        control_description: string
        framework_name: string
        reviewed_by_username?: string
        reviewed_at?: number
      }>
      count: number
    }>(`/api/documents/${documentId}/suggested-controls${params}`)
  }

  /**
   * Accepts AI-generated control suggestions, creating links between the document and controls.
   *
   * This method confirms one or more AI suggestions and establishes actual control-document
   * relationships. Once accepted, the linked controls will appear in the document's control
   * mappings, and the document will contribute to those controls' compliance status.
   *
   * **Accepting suggestions:**
   * You can accept suggestions in two ways:
   * - By `suggestion_ids`: Accept specific suggestions by their unique IDs
   * - By `control_ids`: Accept all pending suggestions for specific controls
   *
   * Either `suggestion_ids` or `control_ids` must be provided, but not both.
   *
   * **What happens when accepted:**
   * 1. The suggestion status changes from 'pending' to 'accepted'
   * 2. A document-control link is created in the control mappings
   * 3. The document now counts toward that control's evidence/policy coverage
   * 4. Suggestion is marked with reviewer username and timestamp
   *
   * **Bulk operations:**
   * For efficiency, you can accept multiple suggestions in a single API call.
   * This is useful for "Accept All" functionality or batch processing.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {string} documentId - The unique identifier (UUID) of the document.
   * @param {Object} data - The acceptance criteria:
   *   - `suggestion_ids` {string[]} [optional] - Array of suggestion UUIDs to accept
   *   - `control_ids` {string[]} [optional] - Array of control UUIDs (accepts all pending
   *     suggestions for these controls)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the operation was successful
   *   - `accepted` {number} - Count of suggestions that were accepted
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - Neither `suggestion_ids` nor `control_ids` provided
   *   - User lacks permission to manage control mappings
   *   - Some suggestion IDs are invalid or already processed
   *
   * @example
   * // Accept a single suggestion
   * async function handleAcceptSuggestion(documentId: string, suggestionId: string) {
   *   try {
   *     const { accepted, message } = await api.acceptDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestionId]
   *     });
   *     toast.success(message);
   *     refreshSuggestions();
   *   } catch (error) {
   *     toast.error(`Failed to accept: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Accept multiple suggestions (batch operation)
   * async function handleBulkAccept(documentId: string, selectedIds: string[]) {
   *   const { accepted } = await api.acceptDocumentSuggestions(documentId, {
   *     suggestion_ids: selectedIds
   *   });
   *   toast.success(`Accepted ${accepted} control suggestions`);
   * }
   *
   * @example
   * // Accept all high-confidence suggestions automatically
   * async function autoAcceptHighConfidence(documentId: string) {
   *   const { suggestions } = await api.getDocumentSuggestions(documentId, 'pending');
   *
   *   // Filter suggestions with confidence >= 0.9
   *   const highConfidence = suggestions
   *     .filter(s => s.confidence_score >= 0.9)
   *     .map(s => s.id);
   *
   *   if (highConfidence.length === 0) {
   *     console.log('No high-confidence suggestions to auto-accept');
   *     return;
   *   }
   *
   *   const { accepted } = await api.acceptDocumentSuggestions(documentId, {
   *     suggestion_ids: highConfidence
   *   });
   *
   *   console.log(`Auto-accepted ${accepted} high-confidence suggestions`);
   * }
   *
   * @example
   * // "Accept All" button with confirmation
   * function AcceptAllButton({ documentId, pendingCount }: Props) {
   *   const handleAcceptAll = async () => {
   *     const confirmed = await confirm(
   *       `Accept all ${pendingCount} control suggestions? ` +
   *       'This will link all suggested controls to this document.'
   *     );
   *
   *     if (!confirmed) return;
   *
   *     // Get all pending suggestions and accept them
   *     const { suggestions } = await api.getDocumentSuggestions(documentId, 'pending');
   *     const ids = suggestions.map(s => s.id);
   *
   *     const { accepted } = await api.acceptDocumentSuggestions(documentId, {
   *       suggestion_ids: ids
   *     });
   *
   *     toast.success(`Linked ${accepted} controls to document`);
   *   };
   *
   *   return (
   *     <Button onClick={handleAcceptAll} disabled={pendingCount === 0}>
   *       Accept All ({pendingCount})
   *     </Button>
   *   );
   * }
   *
   * @see {@link getDocumentSuggestions} for retrieving suggestions to review
   * @see {@link rejectDocumentSuggestions} for rejecting suggestions
   * @see {@link analyzeDocumentControls} for generating new suggestions
   * @see {@link linkPolicyToControl} for manually linking controls without suggestions
   */
  async acceptDocumentSuggestions(documentId: string, data: { suggestion_ids?: string[]; control_ids?: string[] }) {
    return this.request<{
      success: boolean
      accepted: number
      message: string
    }>(`/api/documents/${documentId}/suggested-controls/accept`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Rejects AI-generated control suggestions, dismissing them without creating links.
   *
   * This method marks one or more AI suggestions as rejected, indicating that the suggested
   * control-document relationship is not valid or appropriate. Rejected suggestions are
   * preserved for audit purposes but do not create any control mappings.
   *
   * **When to reject suggestions:**
   * - AI made an incorrect match (false positive)
   * - Control doesn't actually apply to this document
   * - Document covers a different aspect than what AI detected
   * - Duplicate of an already-accepted control
   * - Organization-specific policy overrides
   *
   * **Rejecting suggestions:**
   * You can reject suggestions in two ways:
   * - By `suggestion_ids`: Reject specific suggestions by their unique IDs
   * - By `control_ids`: Reject all pending suggestions for specific controls
   *
   * Either `suggestion_ids` or `control_ids` must be provided, but not both.
   *
   * **What happens when rejected:**
   * 1. The suggestion status changes from 'pending' to 'rejected'
   * 2. No control-document link is created
   * 3. Suggestion is marked with reviewer username and timestamp
   * 4. The suggestion remains visible in history for audit/review purposes
   *
   * **Note:** Rejected suggestions are not deleted. They remain in the system to:
   * - Provide an audit trail of review decisions
   * - Allow future reviewers to see what was rejected and why
   * - Help improve AI suggestion quality over time
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {string} documentId - The unique identifier (UUID) of the document.
   * @param {Object} data - The rejection criteria:
   *   - `suggestion_ids` {string[]} [optional] - Array of suggestion UUIDs to reject
   *   - `control_ids` {string[]} [optional] - Array of control UUIDs (rejects all pending
   *     suggestions for these controls)
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the operation was successful
   *   - `rejected` {number} - Count of suggestions that were rejected
   *   - `message` {string} - Human-readable success message
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - Neither `suggestion_ids` nor `control_ids` provided
   *   - User lacks permission to manage control mappings
   *   - Some suggestion IDs are invalid or already processed
   *
   * @example
   * // Reject a single suggestion
   * async function handleRejectSuggestion(documentId: string, suggestionId: string) {
   *   try {
   *     await api.rejectDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestionId]
   *     });
   *     toast.info('Suggestion dismissed');
   *     refreshSuggestions();
   *   } catch (error) {
   *     toast.error(`Failed to reject: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Reject multiple suggestions (batch operation)
   * async function handleBulkReject(documentId: string, selectedIds: string[]) {
   *   const { rejected } = await api.rejectDocumentSuggestions(documentId, {
   *     suggestion_ids: selectedIds
   *   });
   *   toast.info(`Dismissed ${rejected} suggestions`);
   * }
   *
   * @example
   * // Reject all low-confidence suggestions
   * async function dismissLowConfidence(documentId: string, threshold = 0.5) {
   *   const { suggestions } = await api.getDocumentSuggestions(documentId, 'pending');
   *
   *   // Filter suggestions with confidence below threshold
   *   const lowConfidence = suggestions
   *     .filter(s => s.confidence_score < threshold)
   *     .map(s => s.id);
   *
   *   if (lowConfidence.length === 0) {
   *     console.log('No low-confidence suggestions to dismiss');
   *     return;
   *   }
   *
   *   const { rejected } = await api.rejectDocumentSuggestions(documentId, {
   *     suggestion_ids: lowConfidence
   *   });
   *
   *   console.log(`Dismissed ${rejected} low-confidence suggestions`);
   * }
   *
   * @example
   * // Suggestion card with accept/reject actions
   * function SuggestionCard({ suggestion, documentId, onUpdate }: Props) {
   *   const handleAccept = async () => {
   *     await api.acceptDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestion.id]
   *     });
   *     onUpdate();
   *   };
   *
   *   const handleReject = async () => {
   *     await api.rejectDocumentSuggestions(documentId, {
   *       suggestion_ids: [suggestion.id]
   *     });
   *     onUpdate();
   *   };
   *
   *   return (
   *     <Card>
   *       <h4>{suggestion.control_name}</h4>
   *       <p>{suggestion.match_reason}</p>
   *       <ConfidenceBadge score={suggestion.confidence_score} />
   *       <div className="actions">
   *         <Button onClick={handleAccept} variant="success">
   *           <CheckIcon /> Accept
   *         </Button>
   *         <Button onClick={handleReject} variant="ghost">
   *           <XIcon /> Dismiss
   *         </Button>
   *       </div>
   *     </Card>
   *   );
   * }
   *
   * @see {@link getDocumentSuggestions} for retrieving suggestions to review
   * @see {@link acceptDocumentSuggestions} for accepting suggestions
   * @see {@link analyzeDocumentControls} for generating new suggestions
   */
  async rejectDocumentSuggestions(documentId: string, data: { suggestion_ids?: string[]; control_ids?: string[] }) {
    return this.request<{
      success: boolean
      rejected: number
      message: string
    }>(`/api/documents/${documentId}/suggested-controls/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Triggers AI analysis to generate control mapping suggestions for a document.
   *
   * This method initiates an AI-powered analysis of the document's content to identify
   * which compliance framework controls the document helps satisfy. The AI examines
   * the document text, compares it against all enabled framework controls, and generates
   * suggestions with confidence scores and explanations.
   *
   * **When to use this method:**
   * - After uploading or creating a new document
   * - After significantly editing a document's content
   * - When frameworks have been updated with new controls
   * - To refresh suggestions if initial analysis missed relevant controls
   * - When the user clicks an "Analyze" or "Find Controls" button
   *
   * **Analysis process:**
   * 1. AI retrieves the document's current content
   * 2. Content is analyzed for compliance-relevant keywords, concepts, and practices
   * 3. AI matches against controls from all enabled frameworks
   * 4. Each match is scored (0.0-1.0) with an explanation
   * 5. New suggestions are created with 'pending' status for review
   *
   * **Note:** This method creates new suggestions but does not remove existing ones.
   * Previous suggestions (pending, accepted, or rejected) remain in the system.
   * If a control was already suggested, it won't create duplicate suggestions.
   *
   * **Performance:** Analysis may take several seconds depending on document length
   * and number of enabled frameworks/controls. Consider showing a loading indicator.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {string} documentId - The unique identifier (UUID) of the document to analyze.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the analysis completed successfully
   *   - `message` {string} - Human-readable result message
   *   - `suggestions_count` {number} - Number of new suggestions generated
   *   - `suggestions` {Array<Object>} - Preview of generated suggestions:
   *     - `control_id` {string} - The matched control's UUID
   *     - `confidence` {number} - AI confidence score (0.0-1.0)
   *     - `reason` {string} - Why this control was matched
   *
   * @throws {Error} Throws if:
   *   - Document ID is invalid or document not found
   *   - Document has no content to analyze
   *   - User lacks permission to analyze documents
   *   - No frameworks are enabled for the tenant
   *   - AI analysis service is unavailable
   *
   * @example
   * // Basic analysis with loading state
   * async function handleAnalyze(documentId: string) {
   *   setAnalyzing(true);
   *   try {
   *     const { suggestions_count, message } = await api.analyzeDocumentControls(documentId);
   *
   *     if (suggestions_count > 0) {
   *       toast.success(`Found ${suggestions_count} potential control matches`);
   *       refreshSuggestions(); // Reload suggestions list
   *     } else {
   *       toast.info('No new control matches found');
   *     }
   *   } catch (error) {
   *     toast.error(`Analysis failed: ${error.message}`);
   *   } finally {
   *     setAnalyzing(false);
   *   }
   * }
   *
   * @example
   * // Analyze button component
   * function AnalyzeControlsButton({ documentId, onComplete }: Props) {
   *   const [analyzing, setAnalyzing] = useState(false);
   *
   *   const handleClick = async () => {
   *     setAnalyzing(true);
   *     try {
   *       const result = await api.analyzeDocumentControls(documentId);
   *       toast.success(result.message);
   *       onComplete?.(result.suggestions);
   *     } catch (error) {
   *       toast.error('Failed to analyze document');
   *     } finally {
   *       setAnalyzing(false);
   *     }
   *   };
   *
   *   return (
   *     <Button
   *       onClick={handleClick}
   *       loading={analyzing}
   *       icon={<ScanIcon />}
   *     >
   *       {analyzing ? 'Analyzing...' : 'Find Related Controls'}
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Auto-analyze after document creation
   * async function createAndAnalyze(documentData: CreateDocumentInput) {
   *   // Create the document
   *   const { document } = await api.createDocument(documentData);
   *
   *   // Automatically trigger analysis
   *   const { suggestions_count, suggestions } = await api.analyzeDocumentControls(
   *     document.id
   *   );
   *
   *   console.log(`Document created with ${suggestions_count} suggested controls`);
   *
   *   // Show high-confidence matches immediately
   *   const highConfidence = suggestions.filter(s => s.confidence >= 0.85);
   *   if (highConfidence.length > 0) {
   *     showNotification(
   *       `AI found ${highConfidence.length} strong control matches. ` +
   *       'Review them in the Suggestions tab.'
   *     );
   *   }
   *
   *   return document;
   * }
   *
   * @example
   * // Batch analyze multiple documents
   * async function analyzeAllDocuments(documentIds: string[]) {
   *   const results = await Promise.allSettled(
   *     documentIds.map(id => api.analyzeDocumentControls(id))
   *   );
   *
   *   const successful = results.filter(r => r.status === 'fulfilled');
   *   const failed = results.filter(r => r.status === 'rejected');
   *
   *   const totalSuggestions = successful.reduce(
   *     (sum, r) => sum + (r as PromiseFulfilledResult<any>).value.suggestions_count,
   *     0
   *   );
   *
   *   console.log(`Analyzed ${successful.length}/${documentIds.length} documents`);
   *   console.log(`Generated ${totalSuggestions} total suggestions`);
   *   if (failed.length > 0) {
   *     console.warn(`${failed.length} documents failed analysis`);
   *   }
   * }
   *
   * @see {@link getDocumentSuggestions} for retrieving generated suggestions
   * @see {@link acceptDocumentSuggestions} for accepting suggestions
   * @see {@link rejectDocumentSuggestions} for rejecting suggestions
   * @see {@link createDocument} for creating documents to analyze
   * @see {@link getFrameworks} for viewing enabled frameworks
   */
  async analyzeDocumentControls(documentId: string) {
    return this.request<{
      success: boolean
      message: string
      suggestions_count: number
      suggestions: Array<{
        control_id: string
        confidence: number
        reason: string
      }>
    }>(`/api/documents/${documentId}/analyze-controls`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // TPRM (Third Party Risk Management) endpoints
  // ============================================================================

  /**
   * Retrieves a paginated list of vendors with optional filtering.
   *
   * This method returns all third-party vendors in the organization's TPRM
   * (Third Party Risk Management) registry. Vendors represent external companies,
   * contractors, or service providers that have access to organizational data,
   * systems, or facilities and require ongoing risk assessment.
   *
   * Common vendor categories include:
   * - `cloud_provider` - Cloud infrastructure and SaaS providers (AWS, Azure, Salesforce)
   * - `software` - Software vendors and license providers
   * - `consulting` - Consulting and professional services firms
   * - `it_services` - Managed IT services and support providers
   * - `data_processor` - Companies that process data on your behalf
   * - `subcontractor` - Subcontractors and outsourcing partners
   * - `other` - Miscellaneous vendors
   *
   * Risk levels indicate the inherent risk based on vendor access and criticality:
   * - `critical` - Vendors with access to sensitive data or critical systems
   * - `high` - Significant access or business dependency
   * - `medium` - Moderate access or limited business impact
   * - `low` - Minimal access with negligible risk exposure
   *
   * Vendor statuses track the lifecycle state:
   * - `active` - Currently engaged vendor with valid contract
   * - `pending` - Vendor onboarding in progress
   * - `inactive` - Temporarily inactive but may resume
   * - `offboarded` - Vendor relationship terminated
   *
   * @param {Object} [params] - Optional filter and pagination parameters.
   * @param {string} [params.status] - Filter by vendor status.
   *   Values: 'active', 'pending', 'inactive', 'offboarded'.
   * @param {string} [params.risk_level] - Filter by risk level.
   *   Values: 'critical', 'high', 'medium', 'low'.
   * @param {string} [params.category] - Filter by vendor category.
   *   Values: 'cloud_provider', 'software', 'consulting', 'it_services', 'data_processor', 'subcontractor', 'other'.
   * @param {string} [params.search] - Search term to filter by vendor name or description.
   *   Performs case-insensitive partial matching.
   * @param {number} [params.limit=50] - Maximum number of vendors to return.
   *   Default is 50, maximum is typically 100.
   * @param {number} [params.offset=0] - Number of vendors to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `vendors` {Array<Object>} - Array of vendor objects with:
   *     - `id` {string} - Unique vendor identifier
   *     - `name` {string} - Vendor company name
   *     - `description` {string|null} - Vendor description
   *     - `category` {string} - Vendor category
   *     - `risk_level` {string} - Current risk level
   *     - `status` {string} - Current vendor status
   *     - `contact_name` {string|null} - Primary contact name
   *     - `contact_email` {string|null} - Primary contact email
   *     - `website` {string|null} - Vendor website URL
   *     - `contract_start` {number|null} - Contract start date (Unix timestamp)
   *     - `contract_end` {number|null} - Contract end date (Unix timestamp)
   *     - `last_assessment` {Object|null} - Most recent risk assessment summary
   *     - `created_at` {number} - Creation timestamp
   *     - `updated_at` {number} - Last update timestamp
   *   - `pagination` {Object} - Pagination metadata:
   *     - `total` {number} - Total vendors matching filters
   *     - `limit` {number} - Current page size
   *     - `offset` {number} - Current offset
   *     - `hasMore` {boolean} - Whether more results exist
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view vendors
   *   - Invalid filter parameters are provided
   *
   * @example
   * // Load all active vendors for the vendor list page
   * const { vendors, pagination } = await api.getVendors({
   *   status: 'active',
   *   limit: 25
   * });
   *
   * @example
   * // Filter high-risk cloud providers for review
   * const { vendors } = await api.getVendors({
   *   risk_level: 'high',
   *   category: 'cloud_provider'
   * });
   *
   * @example
   * // Search for vendors with pagination
   * function VendorList() {
   *   const [vendors, setVendors] = useState([]);
   *   const [page, setPage] = useState(0);
   *   const [search, setSearch] = useState('');
   *   const pageSize = 25;
   *
   *   useEffect(() => {
   *     api.getVendors({
   *       search,
   *       limit: pageSize,
   *       offset: page * pageSize
   *     }).then(({ vendors, pagination }) => {
   *       setVendors(vendors);
   *       setHasMore(pagination.hasMore);
   *     });
   *   }, [page, search]);
   *
   *   return (
   *     <VendorTable
   *       vendors={vendors}
   *       onSearch={setSearch}
   *       onNextPage={() => setPage(p => p + 1)}
   *     />
   *   );
   * }
   *
   * @example
   * // Dashboard widget showing critical vendor count
   * async function loadCriticalVendorCount() {
   *   const { pagination } = await api.getVendors({
   *     risk_level: 'critical',
   *     status: 'active',
   *     limit: 1  // Just need the count
   *   });
   *   return pagination.total;
   * }
   *
   * @see {@link getVendor} for retrieving a single vendor with full details
   * @see {@link createVendor} for adding a new vendor
   * @see {@link getRiskAssessments} for vendor risk assessment history
   * @see {@link getTPRMStats} for vendor portfolio analytics
   */
  async getVendors(params?: {
    status?: string
    risk_level?: string
    category?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.risk_level) searchParams.set('risk_level', params.risk_level)
    if (params?.category) searchParams.set('category', params.category)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      vendors: any[]
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/tprm/vendors${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves detailed information for a single vendor.
   *
   * This method returns comprehensive vendor details including contact information,
   * contract dates, risk assessment history, and associated questionnaires. Use this
   * for the vendor detail page where users need full visibility into the vendor
   * relationship and its current risk posture.
   *
   * The response includes all questionnaires associated with this vendor, both
   * outgoing questionnaires (security assessments sent to the vendor) and their
   * responses. This provides a complete picture of the vendor's compliance status.
   *
   * @param {string} id - The unique identifier of the vendor to retrieve.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `vendor` {Object} - Complete vendor object with:
   *     - `id` {string} - Unique vendor identifier
   *     - `name` {string} - Vendor company name
   *     - `description` {string|null} - Detailed vendor description
   *     - `category` {string} - Vendor category (cloud_provider, software, etc.)
   *     - `risk_level` {string} - Current risk level (critical, high, medium, low)
   *     - `status` {string} - Lifecycle status (active, pending, inactive, offboarded)
   *     - `contact_name` {string|null} - Primary contact full name
   *     - `contact_email` {string|null} - Primary contact email address
   *     - `contact_phone` {string|null} - Primary contact phone number
   *     - `website` {string|null} - Vendor website URL
   *     - `contract_start` {number|null} - Contract start date (Unix timestamp)
   *     - `contract_end` {number|null} - Contract end date (Unix timestamp)
   *     - `notes` {string|null} - Internal notes about the vendor
   *     - `last_assessment` {Object|null} - Most recent risk assessment with scores
   *     - `created_at` {number} - Creation timestamp
   *     - `updated_at` {number} - Last update timestamp
   *     - `created_by` {Object} - User who created the vendor record
   *   - `questionnaires` {Array<Object>} - Associated questionnaires with:
   *     - `id` {string} - Questionnaire identifier
   *     - `title` {string} - Questionnaire name
   *     - `status` {string} - Questionnaire status (draft, sent, completed, etc.)
   *     - `sent_at` {number|null} - When questionnaire was sent
   *     - `completed_at` {number|null} - When vendor completed it
   *     - `response_count` {number} - Number of responses received
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view vendors
   *   - Vendor with the specified ID does not exist
   *   - Vendor belongs to a different tenant
   *
   * @example
   * // Load vendor details for the detail page
   * function VendorDetailPage({ vendorId }: { vendorId: string }) {
   *   const [vendor, setVendor] = useState(null);
   *   const [questionnaires, setQuestionnaires] = useState([]);
   *
   *   useEffect(() => {
   *     api.getVendor(vendorId).then(({ vendor, questionnaires }) => {
   *       setVendor(vendor);
   *       setQuestionnaires(questionnaires);
   *     });
   *   }, [vendorId]);
   *
   *   if (!vendor) return <Loading />;
   *
   *   return (
   *     <div>
   *       <VendorHeader vendor={vendor} />
   *       <RiskSummary assessment={vendor.last_assessment} />
   *       <QuestionnaireList questionnaires={questionnaires} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Check if vendor contract is expiring soon
   * async function checkContractExpiration(vendorId: string) {
   *   const { vendor } = await api.getVendor(vendorId);
   *   if (vendor.contract_end) {
   *     const daysUntilExpiry = (vendor.contract_end - Date.now()) / (1000 * 60 * 60 * 24);
   *     if (daysUntilExpiry < 90) {
   *       return { expiring: true, days: Math.floor(daysUntilExpiry) };
   *     }
   *   }
   *   return { expiring: false };
   * }
   *
   * @see {@link getVendors} for listing all vendors
   * @see {@link updateVendor} for modifying vendor information
   * @see {@link getQuestionnaires} for vendor questionnaire management
   * @see {@link getRiskAssessment} for detailed risk assessment data
   */
  async getVendor(id: string) {
    return this.request<{
      success: boolean
      vendor: any
      questionnaires: any[]
    }>(`/api/tprm/vendors/${id}`)
  }

  /**
   * Creates a new vendor in the TPRM registry.
   *
   * This method adds a new third-party vendor to the organization's vendor
   * management system. Once created, the vendor can be assigned risk assessments,
   * receive security questionnaires, and be monitored for ongoing compliance.
   *
   * The vendor creation process typically follows this workflow:
   * 1. Create vendor with basic information (this method)
   * 2. Run initial risk assessment to determine risk level
   * 3. Send security questionnaire if required by risk level
   * 4. Set up ongoing monitoring and periodic re-assessment schedule
   *
   * **Contract dates:** Use Unix timestamps (milliseconds since epoch) for
   * `contract_start` and `contract_end` dates. These are used for contract
   * expiration alerts and renewal tracking.
   *
   * **Initial status:** New vendors default to 'pending' status until their
   * initial risk assessment is completed. Update to 'active' once onboarding
   * is complete.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {Object} data - The vendor data to create.
   * @param {string} data.name - Required. The vendor's company name.
   *   Must be unique within the tenant.
   * @param {string} [data.description] - Detailed description of the vendor
   *   relationship, services provided, or data access.
   * @param {string} [data.category] - Vendor category for classification.
   *   Values: 'cloud_provider', 'software', 'consulting', 'it_services',
   *   'data_processor', 'subcontractor', 'other'. Defaults to 'other'.
   * @param {string} [data.risk_level] - Initial risk level assignment.
   *   Values: 'critical', 'high', 'medium', 'low'. Often set after initial
   *   risk assessment rather than at creation.
   * @param {string} [data.status] - Initial vendor status.
   *   Values: 'active', 'pending', 'inactive', 'offboarded'.
   *   Defaults to 'pending'.
   * @param {string} [data.contact_name] - Primary contact's full name.
   * @param {string} [data.contact_email] - Primary contact's email address.
   *   Used for sending questionnaires and communications.
   * @param {string} [data.contact_phone] - Primary contact's phone number.
   * @param {string} [data.website] - Vendor's website URL.
   * @param {number} [data.contract_start] - Contract start date as Unix
   *   timestamp (milliseconds since epoch).
   * @param {number} [data.contract_end] - Contract end date as Unix timestamp.
   *   Used for expiration alerts.
   * @param {string} [data.notes] - Internal notes about the vendor relationship.
   *   Not visible to the vendor.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the vendor was created successfully
   *   - `vendor` {Object} - The created vendor summary:
   *     - `id` {string} - Generated unique identifier for the new vendor
   *     - `name` {string} - The vendor's name as provided
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to create vendors
   *   - Required name field is missing
   *   - Vendor with same name already exists in tenant
   *   - Invalid category, risk_level, or status value provided
   *
   * @example
   * // Create a new cloud provider vendor
   * const { vendor } = await api.createVendor({
   *   name: 'Amazon Web Services',
   *   description: 'Primary cloud infrastructure provider for production workloads',
   *   category: 'cloud_provider',
   *   risk_level: 'critical',
   *   contact_name: 'AWS Account Team',
   *   contact_email: 'enterprise-support@aws.amazon.com',
   *   website: 'https://aws.amazon.com',
   *   contract_start: Date.now(),
   *   contract_end: Date.now() + (365 * 24 * 60 * 60 * 1000) // 1 year
   * });
   * console.log(`Created vendor: ${vendor.id}`);
   *
   * @example
   * // Create vendor from form submission
   * async function handleVendorForm(formData: FormData) {
   *   try {
   *     const { vendor } = await api.createVendor({
   *       name: formData.get('name') as string,
   *       description: formData.get('description') as string,
   *       category: formData.get('category') as string,
   *       contact_name: formData.get('contactName') as string,
   *       contact_email: formData.get('contactEmail') as string,
   *       website: formData.get('website') as string
   *     });
   *
   *     toast.success(`Vendor "${vendor.name}" created successfully`);
   *     router.push(`/vendors/${vendor.id}`);
   *   } catch (error) {
   *     if (error.message.includes('already exists')) {
   *       toast.error('A vendor with this name already exists');
   *     } else {
   *       toast.error(`Failed to create vendor: ${error.message}`);
   *     }
   *   }
   * }
   *
   * @example
   * // Bulk import vendors from CSV
   * async function importVendors(vendors: VendorImport[]) {
   *   const results = { created: 0, failed: 0, errors: [] };
   *
   *   for (const data of vendors) {
   *     try {
   *       await api.createVendor({
   *         name: data.name,
   *         category: data.category,
   *         contact_email: data.email,
   *         website: data.website
   *       });
   *       results.created++;
   *     } catch (error) {
   *       results.failed++;
   *       results.errors.push({ name: data.name, error: error.message });
   *     }
   *   }
   *
   *   return results;
   * }
   *
   * @see {@link getVendors} for listing all vendors
   * @see {@link getVendor} for retrieving the full vendor details after creation
   * @see {@link updateVendor} for modifying vendor information
   * @see {@link runRiskAssessment} for running initial risk assessment
   * @see {@link createQuestionnaire} for sending security questionnaire
   */
  async createVendor(data: {
    name: string
    description?: string
    category?: string
    risk_level?: string
    status?: string
    contact_name?: string
    contact_email?: string
    contact_phone?: string
    website?: string
    contract_start?: number
    contract_end?: number
    notes?: string
  }) {
    return this.request<{
      success: boolean
      vendor: { id: string; name: string }
    }>('/api/tprm/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing vendor's information.
   *
   * This method allows partial updates to vendor records. Only the fields
   * provided in the data object will be modified; other fields remain unchanged.
   * This is useful for updating contact information, changing status during
   * the vendor lifecycle, or adjusting risk levels after reassessment.
   *
   * Common update scenarios include:
   * - Updating contact information when vendor personnel changes
   * - Changing status from 'pending' to 'active' after onboarding
   * - Adjusting risk_level after a risk assessment is completed
   * - Extending contract_end date upon renewal
   * - Setting status to 'offboarded' when relationship ends
   *
   * **Audit trail:** All changes are logged in the audit trail with the
   * previous and new values for compliance tracking.
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {string} id - The unique identifier of the vendor to update.
   * @param {Object} data - Partial vendor data to update. All fields are optional.
   * @param {string} [data.name] - Updated vendor company name.
   * @param {string} [data.description] - Updated vendor description.
   * @param {string} [data.category] - Updated category classification.
   *   Values: 'cloud_provider', 'software', 'consulting', 'it_services',
   *   'data_processor', 'subcontractor', 'other'.
   * @param {string} [data.risk_level] - Updated risk level.
   *   Values: 'critical', 'high', 'medium', 'low'.
   * @param {string} [data.status] - Updated vendor status.
   *   Values: 'active', 'pending', 'inactive', 'offboarded'.
   * @param {string} [data.contact_name] - Updated primary contact name.
   * @param {string} [data.contact_email] - Updated primary contact email.
   * @param {string} [data.contact_phone] - Updated primary contact phone.
   * @param {string} [data.website] - Updated vendor website URL.
   * @param {number} [data.contract_start] - Updated contract start date
   *   (Unix timestamp in milliseconds).
   * @param {number} [data.contract_end] - Updated contract end date
   *   (Unix timestamp in milliseconds).
   * @param {string} [data.notes] - Updated internal notes.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the update was successful
   *   - `message` {string} - Confirmation message (e.g., "Vendor updated successfully")
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to update vendors
   *   - Vendor with the specified ID does not exist
   *   - Vendor belongs to a different tenant
   *   - Invalid category, risk_level, or status value provided
   *   - Name conflicts with another existing vendor
   *
   * @example
   * // Update vendor status after completing onboarding
   * await api.updateVendor(vendorId, {
   *   status: 'active',
   *   notes: 'Onboarding completed. Initial security assessment passed.'
   * });
   *
   * @example
   * // Update risk level after assessment
   * async function handleRiskLevelUpdate(vendorId: string, newLevel: string) {
   *   try {
   *     await api.updateVendor(vendorId, {
   *       risk_level: newLevel
   *     });
   *     toast.success(`Risk level updated to ${newLevel}`);
   *     refreshVendorDetails();
   *   } catch (error) {
   *     toast.error(`Failed to update risk level: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Handle vendor edit form submission
   * async function handleVendorEditForm(vendorId: string, formData: VendorFormData) {
   *   try {
   *     await api.updateVendor(vendorId, {
   *       name: formData.name,
   *       description: formData.description,
   *       contact_name: formData.contactName,
   *       contact_email: formData.contactEmail,
   *       contact_phone: formData.contactPhone,
   *       website: formData.website,
   *       contract_end: formData.contractEnd?.getTime()
   *     });
   *     toast.success('Vendor updated successfully');
   *     router.push(`/vendors/${vendorId}`);
   *   } catch (error) {
   *     toast.error(`Failed to update vendor: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Offboard a vendor
   * async function offboardVendor(vendorId: string, reason: string) {
   *   await api.updateVendor(vendorId, {
   *     status: 'offboarded',
   *     notes: `Offboarded on ${new Date().toISOString()}: ${reason}`
   *   });
   * }
   *
   * @see {@link getVendor} for retrieving current vendor information
   * @see {@link createVendor} for creating new vendors
   * @see {@link deleteVendor} for permanently removing vendors
   * @see {@link runRiskAssessment} for updating risk level based on assessment
   */
  async updateVendor(id: string, data: any) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/vendors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a vendor from the TPRM registry.
   *
   * This method removes a vendor and all associated data including risk assessments,
   * questionnaire records, and audit history. This action is irreversible and should
   * be used with caution.
   *
   * **Important considerations:**
   * - All associated questionnaires (both sent and received) will be deleted
   * - Risk assessment history will be permanently removed
   * - Audit trail entries referencing this vendor remain for compliance
   * - Consider using 'offboarded' status instead for historical record-keeping
   *
   * **When to delete vs offboard:**
   * - **Delete:** Use for test vendors, duplicates, or vendors created in error
   * - **Offboard:** Use for legitimate vendor relationships that have ended;
   *   preserves history for audit and compliance purposes
   *
   * **Required permissions:** User must have 'admin' role. Compliance managers
   * can offboard but not delete vendors to maintain audit trail integrity.
   *
   * @param {string} id - The unique identifier of the vendor to delete.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the deletion was successful
   *   - `message` {string} - Confirmation message (e.g., "Vendor deleted successfully")
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks admin permission to delete vendors
   *   - Vendor with the specified ID does not exist
   *   - Vendor belongs to a different tenant
   *   - Vendor has active questionnaires in progress (must complete or cancel first)
   *
   * @example
   * // Delete a test vendor with confirmation
   * async function handleDeleteVendor(vendorId: string, vendorName: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete Vendor',
   *     message: `Are you sure you want to permanently delete "${vendorName}"? ` +
   *              'This will remove all associated assessments and questionnaires.',
   *     confirmText: 'Delete',
   *     destructive: true
   *   });
   *
   *   if (!confirmed) return;
   *
   *   try {
   *     await api.deleteVendor(vendorId);
   *     toast.success(`Vendor "${vendorName}" has been deleted`);
   *     router.push('/vendors');
   *   } catch (error) {
   *     if (error.message.includes('active questionnaires')) {
   *       toast.error('Cannot delete vendor with active questionnaires. ' +
   *                   'Please complete or cancel them first.');
   *     } else {
   *       toast.error(`Failed to delete vendor: ${error.message}`);
   *     }
   *   }
   * }
   *
   * @example
   * // Delete button component with permission check
   * function VendorDeleteButton({ vendorId, vendorName, userRole }) {
   *   if (userRole !== 'admin') {
   *     return null; // Only admins can delete
   *   }
   *
   *   return (
   *     <Button
   *       variant="destructive"
   *       onClick={() => handleDeleteVendor(vendorId, vendorName)}
   *     >
   *       Delete Vendor
   *     </Button>
   *   );
   * }
   *
   * @see {@link getVendor} for checking vendor details before deletion
   * @see {@link updateVendor} for offboarding vendors instead of deleting
   * @see {@link getVendors} for refreshing vendor list after deletion
   */
  async deleteVendor(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/vendors/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves a paginated list of outgoing security questionnaires sent to vendors.
   *
   * This method fetches questionnaires that your organization has sent to third-party
   * vendors as part of the TPRM (Third Party Risk Management) due diligence process.
   * Questionnaires are used to assess vendor security posture, compliance status,
   * and risk factors before or during the vendor relationship.
   *
   * Outgoing questionnaires differ from incoming questionnaires:
   * - **Outgoing** (this method): Questionnaires YOUR organization sends TO vendors
   * - **Incoming**: Questionnaires other organizations send TO you
   *
   * Questionnaire statuses track the assessment lifecycle:
   * - `draft` - Questionnaire created but not yet sent to vendor
   * - `sent` - Questionnaire sent to vendor, awaiting response
   * - `in_progress` - Vendor has started responding
   * - `submitted` - Vendor has completed and submitted responses
   * - `under_review` - Your team is reviewing vendor responses
   * - `completed` - Review complete, questionnaire closed
   * - `expired` - Due date passed without submission
   *
   * @param {Object} [params] - Optional filter and pagination parameters.
   * @param {string} [params.vendor_id] - Filter questionnaires for a specific vendor.
   *   Returns only questionnaires sent to the specified vendor ID.
   * @param {string} [params.status] - Filter by questionnaire status.
   *   Values: 'draft', 'sent', 'in_progress', 'submitted', 'under_review', 'completed', 'expired'.
   * @param {number} [params.limit=50] - Maximum number of questionnaires to return.
   *   Default is 50, maximum is typically 100.
   * @param {number} [params.offset=0] - Number of questionnaires to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `questionnaires` {Array<Object>} - Array of questionnaire objects with:
   *     - `id` {string} - Unique questionnaire identifier
   *     - `vendor_id` {string} - Associated vendor ID
   *     - `vendor_name` {string} - Vendor company name
   *     - `title` {string} - Questionnaire title
   *     - `description` {string|null} - Questionnaire description
   *     - `status` {string} - Current status
   *     - `due_date` {number|null} - Due date as Unix timestamp
   *     - `submitted_at` {number|null} - Submission timestamp
   *     - `question_count` {number} - Total number of questions
   *     - `answered_count` {number} - Number of questions answered
   *     - `created_at` {number} - Creation timestamp
   *     - `updated_at` {number} - Last update timestamp
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view TPRM questionnaires
   *   - Invalid status or vendor_id parameter provided
   *
   * @example
   * // Load all pending questionnaires for the dashboard
   * const { questionnaires } = await api.getQuestionnaires({
   *   status: 'submitted',
   *   limit: 10
   * });
   *
   * @example
   * // Get all questionnaires for a specific vendor
   * const { questionnaires } = await api.getQuestionnaires({
   *   vendor_id: 'vendor-123'
   * });
   *
   * @example
   * // Questionnaire list component with filtering
   * function QuestionnaireList() {
   *   const [questionnaires, setQuestionnaires] = useState([]);
   *   const [statusFilter, setStatusFilter] = useState('all');
   *   const [page, setPage] = useState(0);
   *   const pageSize = 25;
   *
   *   useEffect(() => {
   *     const params = {
   *       limit: pageSize,
   *       offset: page * pageSize
   *     };
   *     if (statusFilter !== 'all') {
   *       params.status = statusFilter;
   *     }
   *
   *     api.getQuestionnaires(params).then(({ questionnaires }) => {
   *       setQuestionnaires(questionnaires);
   *     });
   *   }, [statusFilter, page]);
   *
   *   return (
   *     <QuestionnaireTable
   *       questionnaires={questionnaires}
   *       onStatusChange={setStatusFilter}
   *       onNextPage={() => setPage(p => p + 1)}
   *     />
   *   );
   * }
   *
   * @example
   * // Dashboard widget showing overdue questionnaires
   * async function loadOverdueQuestionnaires() {
   *   const { questionnaires } = await api.getQuestionnaires({
   *     status: 'sent'
   *   });
   *   const now = Date.now();
   *   return questionnaires.filter(q => q.due_date && q.due_date < now);
   * }
   *
   * @see {@link getQuestionnaire} for retrieving a single questionnaire with all questions
   * @see {@link createQuestionnaire} for sending a new questionnaire to a vendor
   * @see {@link getVendor} for vendor details and associated questionnaires
   * @see {@link getIncomingQuestionnaires} for questionnaires received FROM other organizations
   */
  async getQuestionnaires(params?: {
    vendor_id?: string
    status?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.vendor_id) searchParams.set('vendor_id', params.vendor_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      questionnaires: any[]
    }>(`/api/tprm/questionnaires${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a single outgoing questionnaire with all its questions and responses.
   *
   * This method fetches the complete details of a security questionnaire sent to
   * a vendor, including all individual question items and any vendor responses.
   * Use this to display the full questionnaire for review, editing, or analysis.
   *
   * The questionnaire object contains metadata about the assessment, while the
   * items array contains the individual questions organized by category. Each
   * item may include the vendor's response if they have begun answering.
   *
   * Common question categories include:
   * - `security` - Information security policies and practices
   * - `privacy` - Data privacy and protection measures
   * - `compliance` - Regulatory compliance (SOC 2, ISO 27001, etc.)
   * - `business_continuity` - Disaster recovery and business continuity
   * - `access_control` - Access management and authentication
   * - `incident_response` - Security incident handling procedures
   * - `vendor_management` - Sub-processor and fourth-party risk
   *
   * @param {string} id - The unique identifier of the questionnaire to retrieve.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `questionnaire` {Object} - The questionnaire metadata:
   *     - `id` {string} - Unique questionnaire identifier
   *     - `vendor_id` {string} - Associated vendor ID
   *     - `vendor_name` {string} - Vendor company name
   *     - `title` {string} - Questionnaire title
   *     - `description` {string|null} - Questionnaire description
   *     - `status` {string} - Current status
   *     - `due_date` {number|null} - Due date as Unix timestamp
   *     - `submitted_at` {number|null} - Submission timestamp
   *     - `reviewed_at` {number|null} - Review completion timestamp
   *     - `created_at` {number} - Creation timestamp
   *     - `updated_at` {number} - Last update timestamp
   *   - `items` {Array<Object>} - Array of question items:
   *     - `id` {string} - Question item ID
   *     - `category` {string|null} - Question category for grouping
   *     - `question` {string} - The question text
   *     - `answer` {string|null} - Vendor's response (if answered)
   *     - `notes` {string|null} - Additional notes or evidence references
   *     - `status` {string} - Item status: 'pending', 'answered', 'flagged'
   *     - `order` {number} - Display order within category
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view TPRM questionnaires
   *   - Questionnaire with specified ID does not exist
   *   - Questionnaire belongs to a different tenant
   *
   * @example
   * // Load questionnaire for detail view
   * const { questionnaire, items } = await api.getQuestionnaire('quest-123');
   * console.log(`${questionnaire.title}: ${items.length} questions`);
   *
   * @example
   * // Display questionnaire with grouped questions
   * function QuestionnaireDetail({ questionnaireId }: { questionnaireId: string }) {
   *   const [questionnaire, setQuestionnaire] = useState(null);
   *   const [items, setItems] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getQuestionnaire(questionnaireId)
   *       .then(({ questionnaire, items }) => {
   *         setQuestionnaire(questionnaire);
   *         setItems(items);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [questionnaireId]);
   *
   *   if (loading) return <Spinner />;
   *
   *   // Group items by category
   *   const grouped = items.reduce((acc, item) => {
   *     const cat = item.category || 'General';
   *     acc[cat] = acc[cat] || [];
   *     acc[cat].push(item);
   *     return acc;
   *   }, {});
   *
   *   return (
   *     <div>
   *       <h1>{questionnaire.title}</h1>
   *       <VendorInfo vendorId={questionnaire.vendor_id} />
   *       <StatusBadge status={questionnaire.status} />
   *       {Object.entries(grouped).map(([category, questions]) => (
   *         <QuestionCategory key={category} name={category} questions={questions} />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate completion percentage
   * async function getQuestionnaireProgress(questionnaireId: string) {
   *   const { items } = await api.getQuestionnaire(questionnaireId);
   *   const answered = items.filter(item => item.status === 'answered').length;
   *   return {
   *     total: items.length,
   *     answered,
   *     percentage: Math.round((answered / items.length) * 100)
   *   };
   * }
   *
   * @see {@link getQuestionnaires} for listing all questionnaires
   * @see {@link createQuestionnaire} for creating new questionnaires
   * @see {@link getVendor} for vendor details
   */
  async getQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      questionnaire: any
      items: any[]
    }>(`/api/tprm/questionnaires/${id}`)
  }

  /**
   * Creates and sends a new security questionnaire to a vendor.
   *
   * This method creates a new outgoing questionnaire to assess a vendor's
   * security posture, compliance status, or risk factors. Questionnaires are
   * a core component of TPRM due diligence, enabling systematic collection
   * of security information from third-party vendors.
   *
   * The questionnaire creation workflow:
   * 1. Create questionnaire with questions (this method)
   * 2. Questionnaire is sent to vendor contact email
   * 3. Vendor receives unique link to complete questionnaire
   * 4. Vendor submits responses
   * 5. Your team reviews and assesses responses
   *
   * **Question categories:** Organize questions by category for better structure.
   * Common categories include: 'security', 'privacy', 'compliance',
   * 'business_continuity', 'access_control', 'incident_response'.
   *
   * **Due dates:** Set a due_date to establish response expectations. The system
   * can send reminder notifications as the deadline approaches. Use Unix
   * timestamps (milliseconds since epoch).
   *
   * **Required permissions:** User must have 'admin' or 'compliance_manager' role.
   *
   * @param {Object} data - The questionnaire data to create.
   * @param {string} data.vendor_id - Required. The ID of the vendor to receive
   *   the questionnaire. Vendor must exist and be in 'active' or 'pending' status.
   * @param {string} data.title - Required. The questionnaire title displayed
   *   to the vendor. Be clear about the assessment purpose.
   * @param {string} [data.description] - Optional description providing context
   *   about the questionnaire purpose and instructions for the vendor.
   * @param {number} [data.due_date] - Optional due date as Unix timestamp
   *   (milliseconds since epoch). If not set, no deadline is enforced.
   * @param {Array<Object>} [data.questions] - Array of question objects to include.
   *   If not provided, questionnaire is created as a draft for later editing.
   * @param {string} [data.questions[].category] - Optional category for grouping
   *   questions (e.g., 'security', 'privacy', 'compliance').
   * @param {string} data.questions[].question - Required. The question text.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the questionnaire was created successfully
   *   - `questionnaire` {Object} - The created questionnaire summary:
   *     - `id` {string} - Generated unique identifier
   *     - `title` {string} - The questionnaire title as provided
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to create questionnaires
   *   - Required vendor_id or title field is missing
   *   - Vendor with specified ID does not exist
   *   - Vendor is offboarded or cannot receive questionnaires
   *
   * @example
   * // Create a basic security questionnaire
   * const { questionnaire } = await api.createQuestionnaire({
   *   vendor_id: 'vendor-123',
   *   title: 'Annual Security Assessment 2024',
   *   description: 'Please complete this questionnaire to help us assess your security posture.',
   *   due_date: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days from now
   *   questions: [
   *     { category: 'security', question: 'Do you have a documented information security policy?' },
   *     { category: 'security', question: 'Are your systems regularly patched and updated?' },
   *     { category: 'compliance', question: 'Do you hold any security certifications (SOC 2, ISO 27001)?' },
   *     { category: 'incident_response', question: 'Do you have an incident response plan?' }
   *   ]
   * });
   *
   * @example
   * // Create questionnaire from form submission
   * async function handleQuestionnaireForm(formData: QuestionnaireFormData) {
   *   try {
   *     const { questionnaire } = await api.createQuestionnaire({
   *       vendor_id: formData.vendorId,
   *       title: formData.title,
   *       description: formData.description,
   *       due_date: formData.dueDate?.getTime(),
   *       questions: formData.questions.map(q => ({
   *         category: q.category,
   *         question: q.text
   *       }))
   *     });
   *
   *     toast.success(`Questionnaire "${questionnaire.title}" sent to vendor`);
   *     router.push(`/questionnaires/${questionnaire.id}`);
   *   } catch (error) {
   *     toast.error(`Failed to create questionnaire: ${error.message}`);
   *   }
   * }
   *
   * @example
   * // Create questionnaire from a template
   * const securityTemplate = [
   *   { category: 'access_control', question: 'How do you manage user access controls?' },
   *   { category: 'access_control', question: 'Do you use multi-factor authentication?' },
   *   { category: 'encryption', question: 'Is data encrypted at rest and in transit?' },
   *   { category: 'monitoring', question: 'Do you have security monitoring and logging?' }
   * ];
   *
   * const { questionnaire } = await api.createQuestionnaire({
   *   vendor_id: vendorId,
   *   title: 'Security Controls Assessment',
   *   questions: securityTemplate
   * });
   *
   * @example
   * // Vendor onboarding with questionnaire
   * async function onboardVendor(vendorData: VendorData) {
   *   // Create the vendor first
   *   const { vendor } = await api.createVendor(vendorData);
   *
   *   // Send initial security questionnaire
   *   await api.createQuestionnaire({
   *     vendor_id: vendor.id,
   *     title: 'Initial Security Assessment',
   *     description: 'Please complete this assessment as part of our vendor onboarding process.',
   *     due_date: Date.now() + (14 * 24 * 60 * 60 * 1000), // 2 weeks
   *     questions: getOnboardingQuestions()
   *   });
   *
   *   return vendor;
   * }
   *
   * @see {@link getQuestionnaires} for listing all questionnaires
   * @see {@link getQuestionnaire} for viewing questionnaire details and responses
   * @see {@link getVendor} for vendor information
   * @see {@link createVendor} for creating new vendors before sending questionnaires
   */
  async createQuestionnaire(data: {
    vendor_id: string
    title: string
    description?: string
    due_date?: number
    questions?: Array<{ category?: string; question: string }>
  }) {
    return this.request<{
      success: boolean
      questionnaire: { id: string; title: string }
    }>('/api/tprm/questionnaires', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Sends questionnaire to vendor with external link
  async sendQuestionnaire(questionnaireId: string, data: { vendor_email: string; vendor_contact_name?: string; due_date?: number; message?: string }) {
    return this.request<{ success: boolean; message: string; questionnaire_url: string; access_token: string; expires_at: number }>(`/api/tprm/questionnaires/${questionnaireId}/send`, { method: 'POST', body: JSON.stringify(data) })
  }

  // Sends a reminder to vendor
  async sendQuestionnaireReminder(questionnaireId: string, data?: { message?: string }) {
    return this.request<{ success: boolean; message: string; reminder_count: number }>(`/api/tprm/questionnaires/${questionnaireId}/remind`, { method: 'POST', body: JSON.stringify(data || {}) })
  }

  // Gets questionnaire activity log
  async getQuestionnaireActivity(questionnaireId: string) {
    return this.request<{ success: boolean; activity: Array<{ id: string; activity_type: string; ip_address?: string; user_agent?: string; details?: string; created_at: number }> }>(`/api/tprm/questionnaires/${questionnaireId}/activity`)
  }

  // Generates questionnaire questions using AI
  async generateQuestionnaireAI(data: { vendor_type?: string; risk_level?: string; frameworks?: string[]; question_count?: number }) {
    return this.request<{ success: boolean; questions: Array<{ category: string; question: string; question_type: string; required: boolean; help_text?: string; weight?: number; choices?: string[] }>; ai_prompt: string }>('/api/tprm/questionnaires/generate-ai', { method: 'POST', body: JSON.stringify(data) })
  }

  // Gets questionnaire templates
  async getQuestionnaireTemplates() {
    return this.request<{ success: boolean; templates: Array<{ id: string; name: string; description?: string; type: string; vendor_tier?: string; question_count: number; use_count: number; created_at: number }> }>('/api/tprm/questionnaires/templates')
  }

  // Creates questionnaire template
  async createQuestionnaireTemplate(data: { name: string; description?: string; type?: string; vendor_tier?: string; ai_generated?: boolean; frameworks?: string[]; questions?: Array<{ category?: string; question: string; question_type?: string; required?: boolean; help_text?: string; choices?: string[]; weight?: number }> }) {
    return this.request<{ success: boolean; template: { id: string; name: string } }>('/api/tprm/questionnaires/templates', { method: 'POST', body: JSON.stringify(data) })
  }

  // Creates questionnaire from template
  async createQuestionnaireFromTemplate(templateId: string, data: { vendor_id: string; title?: string; description?: string }) {
    return this.request<{ success: boolean; questionnaire: { id: string; title: string } }>(`/api/tprm/questionnaires/from-template/${templateId}`, { method: 'POST', body: JSON.stringify(data) })
  }

  // Gets external questionnaire by access token (no auth required - for vendors)
  async getExternalQuestionnaire(token: string) {
    return this.request<{
      success: boolean
      error?: string
      error_code?: string
      completed?: boolean
      questionnaire: {
        id: string
        title: string
        description?: string | null
        due_date?: number | null
        sender_company?: string | null
        vendor_contact_name?: string | null
        vendor_name?: string | null
      }
      items: Array<{
        id: string
        question_number: number
        question: string
        question_type: string
        required: boolean
        help_text?: string | null
        choices?: string[] | null
        response?: string | null
        answered_at?: number | null
        category?: string | null
      }>
      progress: { answered: number; total: number; percentage: number }
    }>(`/api/questionnaire/${token}`)
  }

  // Saves progress on external questionnaire (no auth required - for vendors)
  async submitExternalQuestionnaire(token: string, responses: Array<{ item_id: string; response: string }>, submit: boolean = false) {
    // Map item_id to question_id for backend compatibility
    const mappedResponses = responses.map(r => ({ question_id: r.item_id, response: r.response }))

    // Use save endpoint for auto-save, submit endpoint for final submission
    const endpoint = submit ? `/api/questionnaire/${token}/submit` : `/api/questionnaire/${token}/save`
    return this.request<{
      success: boolean
      error?: string
      message?: string
      saved_at?: number
      score?: number
      submitted_at?: number
    }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ responses: mappedResponses })
    })
  }

  // Saves a single item response on external questionnaire (auto-save via save endpoint)
  async saveExternalQuestionnaireItem(token: string, itemId: string, response: string) {
    // Use the save endpoint with a single response
    return this.request<{ success: boolean; error?: string; message?: string; saved_at?: number }>(`/api/questionnaire/${token}/save`, {
      method: 'POST',
      body: JSON.stringify({ responses: [{ question_id: itemId, response }] })
    })
  }

  /**
   * Retrieves a paginated list of incoming questionnaires received from third parties.
   *
   * This method returns questionnaires that YOUR organization has received from
   * other companies (customers, partners, auditors) requesting information about
   * your security practices. This is the inverse of outgoing questionnaires which
   * you send to your vendors.
   *
   * Incoming questionnaires flow:
   * 1. **Upload** - Third party sends you a questionnaire (Excel, CSV, PDF)
   * 2. **Parse** - System extracts questions from the uploaded file
   * 3. **Autofill** - AI attempts to answer questions based on your existing controls/policies
   * 4. **Review** - Your team reviews and finalizes answers
   * 5. **Export** - Generate completed questionnaire for return to sender
   *
   * Questionnaire statuses:
   * - `uploaded` - File uploaded, awaiting parsing
   * - `parsing` - System is extracting questions
   * - `parsed` - Questions extracted, ready for autofill or manual answering
   * - `autofilling` - AI is generating draft answers
   * - `reviewing` - Draft answers generated, under human review
   * - `completed` - All answers finalized, ready for export
   *
   * @param {Object} [params] - Optional filter and pagination parameters.
   * @param {string} [params.status] - Filter by questionnaire status.
   *   Values: 'uploaded', 'parsing', 'parsed', 'autofilling', 'reviewing', 'completed'.
   * @param {number} [params.limit=50] - Maximum number of questionnaires to return.
   *   Default is 50, maximum is typically 100.
   * @param {number} [params.offset=0] - Number of questionnaires to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `questionnaires` {Array<Object>} - Array of incoming questionnaire objects:
   *     - `id` {string} - Unique questionnaire identifier
   *     - `title` {string} - Questionnaire title (from filename or metadata)
   *     - `sender_org` {string} - Organization that sent the questionnaire
   *     - `sender_contact` {string|null} - Contact person name
   *     - `sender_email` {string|null} - Contact email address
   *     - `status` {string} - Current processing status
   *     - `due_date` {number|null} - Due date as Unix timestamp
   *     - `questions_count` {number} - Total number of extracted questions
   *     - `answered_count` {number} - Number of questions with answers
   *     - `file_name` {string} - Original uploaded filename
   *     - `file_type` {string} - File MIME type or extension
   *     - `created_at` {number} - Upload timestamp
   *     - `updated_at` {number} - Last update timestamp
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view incoming questionnaires
   *   - Invalid status parameter provided
   *
   * @example
   * // Load all incoming questionnaires awaiting review
   * const { questionnaires } = await api.getIncomingQuestionnaires({
   *   status: 'reviewing',
   *   limit: 20
   * });
   *
   * @example
   * // Dashboard showing pending questionnaires count
   * async function getPendingCount() {
   *   const { questionnaires } = await api.getIncomingQuestionnaires({
   *     status: 'reviewing'
   *   });
   *   return questionnaires.length;
   * }
   *
   * @example
   * // Incoming questionnaire inbox component
   * function IncomingQuestionnaireInbox() {
   *   const [questionnaires, setQuestionnaires] = useState([]);
   *   const [statusFilter, setStatusFilter] = useState('all');
   *   const [page, setPage] = useState(0);
   *   const pageSize = 25;
   *
   *   useEffect(() => {
   *     const params = {
   *       limit: pageSize,
   *       offset: page * pageSize
   *     };
   *     if (statusFilter !== 'all') {
   *       params.status = statusFilter;
   *     }
   *
   *     api.getIncomingQuestionnaires(params).then(({ questionnaires }) => {
   *       setQuestionnaires(questionnaires);
   *     });
   *   }, [statusFilter, page]);
   *
   *   return (
   *     <InboxTable
   *       questionnaires={questionnaires}
   *       onStatusFilter={setStatusFilter}
   *       onPageChange={setPage}
   *     />
   *   );
   * }
   *
   * @example
   * // Show overdue incoming questionnaires
   * async function loadOverdueIncoming() {
   *   const { questionnaires } = await api.getIncomingQuestionnaires({
   *     status: 'reviewing'
   *   });
   *   const now = Date.now();
   *   return questionnaires.filter(q => q.due_date && q.due_date < now);
   * }
   *
   * @see {@link getIncomingQuestionnaire} for retrieving a single questionnaire with all questions
   * @see {@link uploadIncomingQuestionnaire} for uploading a new questionnaire file
   * @see {@link getQuestionnaires} for outgoing questionnaires sent TO vendors
   * @see {@link getTPRMStats} for overall TPRM statistics
   */
  async getIncomingQuestionnaires(params?: {
    status?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      questionnaires: any[]
    }>(`/api/tprm/incoming${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a single incoming questionnaire with all its extracted questions and answers.
   *
   * This method fetches the complete details of an incoming questionnaire received
   * from a third party, including all extracted question items and any draft or
   * finalized answers. Use this to display the questionnaire for review, editing,
   * or to check progress on AI autofill results.
   *
   * The questionnaire object contains metadata about the sender and file, while
   * the items array contains the individual questions extracted from the document.
   * Each item may have an AI-generated draft answer and/or a human-reviewed final answer.
   *
   * Question item statuses:
   * - `pending` - Question extracted, no answer yet
   * - `draft` - AI has generated a draft answer, awaiting review
   * - `approved` - Human reviewer has approved the answer
   * - `needs_revision` - Answer flagged for revision
   * - `skipped` - Question marked as not applicable
   *
   * @param {string} id - The unique identifier of the incoming questionnaire to retrieve.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `questionnaire` {Object} - The questionnaire metadata:
   *     - `id` {string} - Unique questionnaire identifier
   *     - `title` {string} - Questionnaire title
   *     - `sender_org` {string} - Organization that sent the questionnaire
   *     - `sender_contact` {string|null} - Contact person name
   *     - `sender_email` {string|null} - Contact email address
   *     - `status` {string} - Current processing status
   *     - `due_date` {number|null} - Due date as Unix timestamp
   *     - `file_name` {string} - Original uploaded filename
   *     - `file_type` {string} - File MIME type
   *     - `questions_count` {number} - Total extracted questions
   *     - `answered_count` {number} - Questions with answers
   *     - `created_at` {number} - Upload timestamp
   *     - `updated_at` {number} - Last update timestamp
   *   - `items` {Array<Object>} - Array of question items:
   *     - `id` {string} - Question item ID
   *     - `question_number` {string|null} - Original question number from document
   *     - `question_text` {string} - The extracted question text
   *     - `category` {string|null} - Inferred question category
   *     - `draft_answer` {string|null} - AI-generated draft answer
   *     - `final_answer` {string|null} - Human-approved final answer
   *     - `confidence` {number|null} - AI confidence score (0.0-1.0)
   *     - `source_controls` {Array<string>|null} - Control IDs used for answer
   *     - `status` {string} - Item status
   *     - `reviewer_notes` {string|null} - Notes from human reviewer
   *     - `order` {number} - Display order
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view incoming questionnaires
   *   - Questionnaire with specified ID does not exist
   *   - Questionnaire belongs to a different tenant
   *
   * @example
   * // Load questionnaire for detail view
   * const { questionnaire, items } = await api.getIncomingQuestionnaire('iq-123');
   * console.log(`${questionnaire.title} from ${questionnaire.sender_org}`);
   * console.log(`Progress: ${questionnaire.answered_count}/${questionnaire.questions_count}`);
   *
   * @example
   * // Display questionnaire review interface
   * function IncomingQuestionnaireReview({ id }: { id: string }) {
   *   const [questionnaire, setQuestionnaire] = useState(null);
   *   const [items, setItems] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getIncomingQuestionnaire(id)
   *       .then(({ questionnaire, items }) => {
   *         setQuestionnaire(questionnaire);
   *         setItems(items);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [id]);
   *
   *   if (loading) return <Spinner />;
   *
   *   const needsReview = items.filter(i => i.status === 'draft');
   *   const approved = items.filter(i => i.status === 'approved');
   *
   *   return (
   *     <div>
   *       <h1>{questionnaire.title}</h1>
   *       <SenderInfo org={questionnaire.sender_org} />
   *       <ProgressBar completed={approved.length} total={items.length} />
   *       <QuestionList items={items} onItemUpdate={handleUpdate} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Show items with high-confidence AI answers
   * const { items } = await api.getIncomingQuestionnaire(id);
   * const highConfidence = items.filter(
   *   item => item.confidence && item.confidence >= 0.9 && item.draft_answer
   * );
   *
   * @see {@link getIncomingQuestionnaires} for listing all incoming questionnaires
   * @see {@link updateIncomingQuestionnaireItem} for updating individual answers
   * @see {@link autofillIncomingQuestionnaire} for AI answer generation
   * @see {@link exportIncomingQuestionnaire} for exporting completed questionnaire
   */
  async getIncomingQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      questionnaire: any
      items: any[]
    }>(`/api/tprm/incoming/${id}`)
  }

  /**
   * Uploads a new incoming questionnaire file for processing.
   *
   * This method accepts a questionnaire document (Excel, CSV, or PDF) from a third
   * party and stores it for subsequent parsing and answering. The file is analyzed
   * to extract metadata, and the sender information is recorded for tracking.
   *
   * Supported file formats:
   * - **Excel** (.xlsx, .xls) - Most common format, parsed row-by-row
   * - **CSV** (.csv) - Simple tabular format with questions in rows
   * - **PDF** (.pdf) - Parsed using OCR/text extraction for questions
   *
   * After upload, the questionnaire has status `uploaded` and needs to be parsed
   * using {@link parseIncomingQuestionnaire} to extract individual questions.
   *
   * Typical workflow:
   * 1. Upload file with this method
   * 2. Parse to extract questions with {@link parseIncomingQuestionnaire}
   * 3. Autofill with AI using {@link autofillIncomingQuestionnaire}
   * 4. Review and finalize answers with {@link updateIncomingQuestionnaireItem}
   * 5. Export completed questionnaire with {@link exportIncomingQuestionnaire}
   *
   * @param {Object} data - Upload data containing file and sender information.
   * @param {File} data.file - The questionnaire file to upload.
   *   Accepted formats: .xlsx, .xls, .csv, .pdf. Maximum size typically 10MB.
   * @param {string} data.sender_org - The name of the organization that sent the questionnaire.
   *   This is required for tracking and reporting purposes.
   * @param {string} [data.sender_contact] - The name of the contact person who sent the questionnaire.
   * @param {string} [data.sender_email] - The email address of the sender contact.
   * @param {string} [data.title] - Custom title for the questionnaire.
   *   If not provided, the filename will be used.
   * @param {number} [data.due_date] - Due date as Unix timestamp (milliseconds).
   *   When the completed questionnaire must be returned to the sender.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the upload succeeded
   *   - `message` {string} - Success or error message
   *   - `questionnaire` {Object} - The created questionnaire record:
   *     - `id` {string} - Unique questionnaire identifier
   *     - `title` {string} - Questionnaire title
   *     - `sender_org` {string} - Sender organization name
   *     - `status` {string} - Initial status ('uploaded')
   *     - `file_name` {string} - Original filename
   *     - `file_type` {string} - Detected file MIME type
   *     - `created_at` {number} - Upload timestamp
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to upload questionnaires
   *   - File type is not supported
   *   - File exceeds maximum size limit
   *   - sender_org is not provided
   *
   * @example
   * // Basic file upload
   * const file = fileInput.files[0];
   * const result = await api.uploadIncomingQuestionnaire({
   *   file: file,
   *   sender_org: 'Acme Corp',
   *   sender_contact: 'John Smith',
   *   sender_email: 'john@acme.com'
   * });
   * console.log(`Uploaded: ${result.questionnaire.id}`);
   *
   * @example
   * // Upload with due date tracking
   * const dueDateMs = new Date('2024-03-15').getTime();
   * await api.uploadIncomingQuestionnaire({
   *   file: questionnaire,
   *   sender_org: 'Enterprise Client',
   *   title: 'Q1 2024 Security Assessment',
   *   due_date: dueDateMs
   * });
   *
   * @example
   * // React file upload component
   * function QuestionnaireUpload() {
   *   const [uploading, setUploading] = useState(false);
   *   const [error, setError] = useState(null);
   *
   *   const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
   *     e.preventDefault();
   *     const formData = new FormData(e.currentTarget);
   *     const file = formData.get('file') as File;
   *
   *     if (!file) return;
   *
   *     setUploading(true);
   *     setError(null);
   *
   *     try {
   *       const result = await api.uploadIncomingQuestionnaire({
   *         file,
   *         sender_org: formData.get('sender_org') as string,
   *         sender_contact: formData.get('sender_contact') as string,
   *         sender_email: formData.get('sender_email') as string,
   *         due_date: formData.get('due_date')
   *           ? new Date(formData.get('due_date') as string).getTime()
   *           : undefined
   *       });
   *
   *       // Navigate to parse step
   *       navigate(`/tprm/incoming/${result.questionnaire.id}/parse`);
   *     } catch (err) {
   *       setError(err.message);
   *     } finally {
   *       setUploading(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleUpload}>
   *       <FileInput name="file" accept=".xlsx,.xls,.csv,.pdf" />
   *       <Input name="sender_org" label="Sender Organization" required />
   *       <Input name="sender_contact" label="Contact Name" />
   *       <Input name="sender_email" label="Contact Email" type="email" />
   *       <DatePicker name="due_date" label="Due Date" />
   *       <Button type="submit" loading={uploading}>Upload</Button>
   *     </form>
   *   );
   * }
   *
   * @see {@link parseIncomingQuestionnaire} for extracting questions from uploaded file
   * @see {@link getIncomingQuestionnaires} for listing all incoming questionnaires
   * @see {@link deleteIncomingQuestionnaire} for removing uploaded questionnaires
   */
  async uploadIncomingQuestionnaire(data: {
    file: File
    sender_org: string
    sender_contact?: string
    sender_email?: string
    title?: string
    due_date?: number
  }) {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('sender_org', data.sender_org)
    if (data.sender_contact) formData.append('sender_contact', data.sender_contact)
    if (data.sender_email) formData.append('sender_email', data.sender_email)
    if (data.title) formData.append('title', data.title)
    if (data.due_date) formData.append('due_date', String(data.due_date))

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/tprm/incoming/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    })
    return response.json()
  }

  /**
   * Parses an uploaded incoming questionnaire to extract individual questions.
   *
   * This method analyzes the uploaded questionnaire file and extracts individual
   * questions from it, creating question items that can then be answered. The
   * parsing process uses pattern recognition and AI to identify question boundaries,
   * question numbers, and categorize questions by topic.
   *
   * Parsing behavior by file type:
   * - **Excel** - Each row with question-like content becomes an item
   * - **CSV** - Similar to Excel, parses row-by-row
   * - **PDF** - Uses text extraction and AI to identify question boundaries
   *
   * After parsing, the questionnaire status changes from `uploaded` to `parsed`
   * and the questions are available in the items array. You can then proceed
   * to autofill answers using {@link autofillIncomingQuestionnaire}.
   *
   * This is an asynchronous operation. For large questionnaires, the parsing
   * may take several seconds. The method returns when parsing is complete.
   *
   * @param {string} id - The unique identifier of the incoming questionnaire to parse.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the parsing succeeded
   *   - `message` {string} - Status message (e.g., "Successfully parsed questionnaire")
   *   - `questions_count` {number} - Number of questions extracted from the file
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to process questionnaires
   *   - Questionnaire with specified ID does not exist
   *   - Questionnaire is not in 'uploaded' status
   *   - File format cannot be parsed
   *   - File is corrupted or password-protected
   *
   * @example
   * // Parse after upload
   * const uploadResult = await api.uploadIncomingQuestionnaire({
   *   file: file,
   *   sender_org: 'Acme Corp'
   * });
   *
   * const parseResult = await api.parseIncomingQuestionnaire(
   *   uploadResult.questionnaire.id
   * );
   * console.log(`Extracted ${parseResult.questions_count} questions`);
   *
   * @example
   * // Multi-step processing flow
   * async function processQuestionnaire(file, senderOrg) {
   *   // Step 1: Upload
   *   const { questionnaire } = await api.uploadIncomingQuestionnaire({
   *     file,
   *     sender_org: senderOrg
   *   });
   *
   *   // Step 2: Parse
   *   const parseResult = await api.parseIncomingQuestionnaire(questionnaire.id);
   *
   *   // Step 3: Autofill
   *   const autofillResult = await api.autofillIncomingQuestionnaire(questionnaire.id);
   *
   *   return {
   *     id: questionnaire.id,
   *     questionsExtracted: parseResult.questions_count,
   *     questionsAnswered: autofillResult.answered_count
   *   };
   * }
   *
   * @example
   * // React processing component with progress
   * function QuestionnaireParser({ questionnaireId }: { questionnaireId: string }) {
   *   const [parsing, setParsing] = useState(false);
   *   const [result, setResult] = useState(null);
   *   const [error, setError] = useState(null);
   *
   *   const handleParse = async () => {
   *     setParsing(true);
   *     setError(null);
   *
   *     try {
   *       const result = await api.parseIncomingQuestionnaire(questionnaireId);
   *       setResult(result);
   *       // Navigate to autofill step
   *       navigate(`/tprm/incoming/${questionnaireId}/autofill`);
   *     } catch (err) {
   *       setError(err.message);
   *     } finally {
   *       setParsing(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <Button onClick={handleParse} loading={parsing}>
   *         Extract Questions
   *       </Button>
   *       {result && <p>Extracted {result.questions_count} questions</p>}
   *       {error && <ErrorMessage>{error}</ErrorMessage>}
   *     </div>
   *   );
   * }
   *
   * @see {@link uploadIncomingQuestionnaire} for uploading the questionnaire file
   * @see {@link autofillIncomingQuestionnaire} for AI-powered answer generation
   * @see {@link getIncomingQuestionnaire} for viewing extracted questions
   */
  async parseIncomingQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      message: string
      questions_count: number
    }>(`/api/tprm/incoming/${id}/parse`, {
      method: 'POST',
    })
  }

  /**
   * Triggers AI-powered autofill to generate draft answers for questionnaire questions.
   *
   * This method uses artificial intelligence to analyze each extracted question
   * and generate draft answers based on your organization's existing controls,
   * policies, and evidence. The AI matches questions to relevant documentation
   * and synthesizes appropriate responses.
   *
   * The autofill process:
   * 1. Analyzes each question to understand what information is requested
   * 2. Searches your controls, policies, and evidence for relevant content
   * 3. Generates a draft answer with appropriate language and detail
   * 4. Assigns a confidence score (0.0-1.0) based on match quality
   * 5. Links source controls/documents for audit trail
   *
   * Confidence score interpretation:
   * - **0.9-1.0** - High confidence, likely accurate answer
   * - **0.7-0.9** - Medium confidence, review recommended
   * - **0.5-0.7** - Low confidence, needs human revision
   * - **<0.5** - Very low confidence, manual answer likely needed
   *
   * After autofill, questions have status `draft` and include AI-generated
   * answers that should be reviewed by a human before finalizing.
   *
   * @param {string} id - The unique identifier of the incoming questionnaire to autofill.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the autofill succeeded
   *   - `message` {string} - Status message
   *   - `answered_count` {number} - Number of questions that received draft answers
   *   - `total_count` {number} - Total number of questions in the questionnaire
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to process questionnaires
   *   - Questionnaire with specified ID does not exist
   *   - Questionnaire is not in 'parsed' status
   *   - AI service is temporarily unavailable
   *
   * @example
   * // Basic autofill after parsing
   * const result = await api.autofillIncomingQuestionnaire('iq-123');
   * console.log(`Answered ${result.answered_count} of ${result.total_count} questions`);
   *
   * @example
   * // Full processing pipeline
   * async function processAndAutofill(questionnaireId) {
   *   // Parse questions
   *   await api.parseIncomingQuestionnaire(questionnaireId);
   *
   *   // Generate AI answers
   *   const autofillResult = await api.autofillIncomingQuestionnaire(questionnaireId);
   *
   *   // Load for review
   *   const { items } = await api.getIncomingQuestionnaire(questionnaireId);
   *
   *   const highConfidence = items.filter(i => i.confidence >= 0.9);
   *   const needsReview = items.filter(i => i.confidence < 0.9);
   *
   *   return {
   *     autoApproved: highConfidence.length,
   *     needsReview: needsReview.length,
   *     coverage: `${autofillResult.answered_count}/${autofillResult.total_count}`
   *   };
   * }
   *
   * @example
   * // React autofill component with progress
   * function QuestionnaireAutofill({ questionnaireId }: { questionnaireId: string }) {
   *   const [processing, setProcessing] = useState(false);
   *   const [result, setResult] = useState(null);
   *
   *   const handleAutofill = async () => {
   *     setProcessing(true);
   *     try {
   *       const result = await api.autofillIncomingQuestionnaire(questionnaireId);
   *       setResult(result);
   *
   *       // Auto-navigate to review when complete
   *       navigate(`/tprm/incoming/${questionnaireId}/review`);
   *     } finally {
   *       setProcessing(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       <p>
   *         AI will analyze your controls and policies to generate
   *         draft answers for each question.
   *       </p>
   *       <Button onClick={handleAutofill} loading={processing}>
   *         {processing ? 'Generating Answers...' : 'Start Autofill'}
   *       </Button>
   *       {result && (
   *         <ProgressSummary
   *           answered={result.answered_count}
   *           total={result.total_count}
   *         />
   *       )}
   *     </div>
   *   );
   * }
   *
   * @see {@link parseIncomingQuestionnaire} for extracting questions first
   * @see {@link updateIncomingQuestionnaireItem} for reviewing/finalizing answers
   * @see {@link getIncomingQuestionnaire} for viewing autofill results
   */
  async autofillIncomingQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      message: string
      answered_count: number
      total_count: number
    }>(`/api/tprm/incoming/${id}/autofill`, {
      method: 'POST',
    })
  }

  /**
   * Updates a single question item in an incoming questionnaire.
   *
   * This method allows reviewers to update individual question items, including
   * approving AI-generated draft answers, editing answers, adding reviewer notes,
   * and changing item status. This is the primary method for the human review
   * workflow after AI autofill has generated draft answers.
   *
   * Item statuses:
   * - `pending` - Question extracted, no answer yet
   * - `draft` - AI has generated a draft answer, awaiting review
   * - `approved` - Human reviewer has approved the answer as final
   * - `needs_revision` - Answer flagged for additional work
   * - `skipped` - Question marked as not applicable
   *
   * When setting a final_answer, the answer is saved and will be included in
   * the exported questionnaire. The draft_answer is preserved for reference.
   *
   * @param {string} questionnaireId - The unique identifier of the incoming questionnaire.
   * @param {string} itemId - The unique identifier of the question item to update.
   * @param {Object} data - The update data.
   * @param {string} [data.final_answer] - The finalized answer text for this question.
   *   This is the answer that will be exported in the completed questionnaire.
   * @param {string} [data.status] - The new status for this item.
   *   Values: 'pending', 'draft', 'approved', 'needs_revision', 'skipped'.
   * @param {string} [data.reviewer_notes] - Internal notes about this question.
   *   These notes are not exported but are visible to your team.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the update succeeded
   *   - `message` {string} - Success or error message
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to edit questionnaires
   *   - Questionnaire or item with specified ID does not exist
   *   - Invalid status value provided
   *
   * @example
   * // Approve an AI-generated answer as-is
   * await api.updateIncomingQuestionnaireItem('iq-123', 'item-456', {
   *   final_answer: item.draft_answer,
   *   status: 'approved'
   * });
   *
   * @example
   * // Edit and approve an answer
   * await api.updateIncomingQuestionnaireItem('iq-123', 'item-456', {
   *   final_answer: 'Our organization implements MFA using...',
   *   status: 'approved',
   *   reviewer_notes: 'Updated to include recent MFA rollout'
   * });
   *
   * @example
   * // Mark a question as not applicable
   * await api.updateIncomingQuestionnaireItem('iq-123', 'item-456', {
   *   status: 'skipped',
   *   reviewer_notes: 'Not applicable - we do not process credit card data'
   * });
   *
   * @example
   * // React question review component
   * function QuestionReview({ questionnaire, item, onUpdate }) {
   *   const [answer, setAnswer] = useState(item.draft_answer || '');
   *   const [notes, setNotes] = useState(item.reviewer_notes || '');
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleApprove = async () => {
   *     setSaving(true);
   *     try {
   *       await api.updateIncomingQuestionnaireItem(
   *         questionnaire.id,
   *         item.id,
   *         {
   *           final_answer: answer,
   *           status: 'approved',
   *           reviewer_notes: notes
   *         }
   *       );
   *       onUpdate({ ...item, status: 'approved', final_answer: answer });
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   const handleSkip = async () => {
   *     await api.updateIncomingQuestionnaireItem(
   *       questionnaire.id,
   *       item.id,
   *       { status: 'skipped', reviewer_notes: notes }
   *     );
   *     onUpdate({ ...item, status: 'skipped' });
   *   };
   *
   *   return (
   *     <div>
   *       <h3>Q{item.question_number}: {item.question_text}</h3>
   *       <ConfidenceBadge score={item.confidence} />
   *       <Textarea value={answer} onChange={setAnswer} />
   *       <Input
   *         label="Reviewer Notes (internal)"
   *         value={notes}
   *         onChange={setNotes}
   *       />
   *       <ButtonGroup>
   *         <Button onClick={handleApprove} loading={saving}>Approve</Button>
   *         <Button variant="secondary" onClick={handleSkip}>Skip</Button>
   *       </ButtonGroup>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Bulk approve high-confidence answers
   * async function bulkApproveHighConfidence(questionnaireId) {
   *   const { items } = await api.getIncomingQuestionnaire(questionnaireId);
   *
   *   const highConfidence = items.filter(
   *     i => i.confidence >= 0.95 && i.status === 'draft'
   *   );
   *
   *   for (const item of highConfidence) {
   *     await api.updateIncomingQuestionnaireItem(questionnaireId, item.id, {
   *       final_answer: item.draft_answer,
   *       status: 'approved',
   *       reviewer_notes: 'Auto-approved: high confidence score'
   *     });
   *   }
   *
   *   return highConfidence.length;
   * }
   *
   * @see {@link getIncomingQuestionnaire} for loading all items
   * @see {@link autofillIncomingQuestionnaire} for generating draft answers
   * @see {@link exportIncomingQuestionnaire} for exporting approved answers
   */
  async updateIncomingQuestionnaireItem(questionnaireId: string, itemId: string, data: {
    final_answer?: string
    status?: string
    reviewer_notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/incoming/${questionnaireId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes an incoming questionnaire and all its associated data.
   *
   * This method permanently removes an incoming questionnaire, including:
   * - The uploaded file
   * - All extracted question items
   * - All draft and final answers
   * - All reviewer notes
   *
   * **Warning**: This action cannot be undone. For audit compliance, consider
   * exporting the completed questionnaire before deletion if it contains
   * valuable responses that may be needed for future reference.
   *
   * Use cases for deletion:
   * - Duplicate uploads
   * - Test/demo questionnaires
   * - Questionnaires uploaded in error
   * - Obsolete questionnaires that are no longer needed
   *
   * @param {string} id - The unique identifier of the incoming questionnaire to delete.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the deletion succeeded
   *   - `message` {string} - Success or error message
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to delete questionnaires (typically requires admin role)
   *   - Questionnaire with specified ID does not exist
   *   - Questionnaire belongs to a different tenant
   *
   * @example
   * // Delete a questionnaire
   * await api.deleteIncomingQuestionnaire('iq-123');
   *
   * @example
   * // Delete with confirmation dialog
   * function DeleteQuestionnaireButton({ questionnaire, onDelete }) {
   *   const [confirming, setConfirming] = useState(false);
   *   const [deleting, setDeleting] = useState(false);
   *
   *   const handleDelete = async () => {
   *     setDeleting(true);
   *     try {
   *       await api.deleteIncomingQuestionnaire(questionnaire.id);
   *       onDelete(questionnaire.id);
   *     } finally {
   *       setDeleting(false);
   *       setConfirming(false);
   *     }
   *   };
   *
   *   if (confirming) {
   *     return (
   *       <ConfirmDialog
   *         title="Delete Questionnaire?"
   *         message={`Permanently delete "${questionnaire.title}"? This cannot be undone.`}
   *         onConfirm={handleDelete}
   *         onCancel={() => setConfirming(false)}
   *         loading={deleting}
   *       />
   *     );
   *   }
   *
   *   return (
   *     <Button variant="danger" onClick={() => setConfirming(true)}>
   *       Delete
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Export before delete for archival
   * async function archiveAndDelete(questionnaireId) {
   *   // Export first
   *   const blob = await api.exportIncomingQuestionnaire(questionnaireId);
   *   await saveToArchive(blob);
   *
   *   // Then delete
   *   await api.deleteIncomingQuestionnaire(questionnaireId);
   * }
   *
   * @see {@link getIncomingQuestionnaires} for listing questionnaires
   * @see {@link exportIncomingQuestionnaire} for exporting before deletion
   */
  async deleteIncomingQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/incoming/${id}`, {
      method: 'DELETE',
    })
  }

  async deleteQuestionnaire(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/questionnaires/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Exports a completed incoming questionnaire as a downloadable file.
   *
   * This method generates an export of the incoming questionnaire with all
   * finalized answers, suitable for sending back to the requesting organization.
   * The export format matches the original file format when possible (Excel, CSV)
   * and includes all approved answers along with relevant metadata.
   *
   * The export includes:
   * - Original question text and numbering
   * - Final approved answers for each question
   * - Any questions marked as skipped (with indication)
   * - Questionnaire metadata (title, date, your organization info)
   *
   * The returned Blob can be downloaded using standard browser file download
   * techniques. This is a synchronous operation that returns a Blob directly
   * rather than a JSON response.
   *
   * **Tip**: Ensure all questions are either approved or skipped before exporting.
   * Questions in draft or pending status will show incomplete answers.
   *
   * @param {string} id - The unique identifier of the incoming questionnaire to export.
   *
   * @returns {Promise<Blob>} A Blob containing the exported questionnaire file.
   *   The content type is typically 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
   *   for Excel or 'text/csv' for CSV exports.
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to export questionnaires
   *   - Questionnaire with specified ID does not exist
   *   - Server fails to generate the export file
   *
   * @example
   * // Basic export and download
   * const blob = await api.exportIncomingQuestionnaire('iq-123');
   * const url = URL.createObjectURL(blob);
   * const a = document.createElement('a');
   * a.href = url;
   * a.download = 'completed-questionnaire.xlsx';
   * a.click();
   * URL.revokeObjectURL(url);
   *
   * @example
   * // React export button component
   * function ExportQuestionnaireButton({ questionnaire }) {
   *   const [exporting, setExporting] = useState(false);
   *
   *   const handleExport = async () => {
   *     setExporting(true);
   *     try {
   *       const blob = await api.exportIncomingQuestionnaire(questionnaire.id);
   *
   *       // Create download link
   *       const url = URL.createObjectURL(blob);
   *       const link = document.createElement('a');
   *       link.href = url;
   *       link.download = `${questionnaire.title}-completed.xlsx`;
   *       document.body.appendChild(link);
   *       link.click();
   *       document.body.removeChild(link);
   *       URL.revokeObjectURL(url);
   *     } finally {
   *       setExporting(false);
   *     }
   *   };
   *
   *   return (
   *     <Button onClick={handleExport} loading={exporting}>
   *       Export Completed Questionnaire
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Check completion before export
   * async function exportIfComplete(questionnaireId) {
   *   const { items } = await api.getIncomingQuestionnaire(questionnaireId);
   *
   *   const incomplete = items.filter(
   *     i => i.status !== 'approved' && i.status !== 'skipped'
   *   );
   *
   *   if (incomplete.length > 0) {
   *     throw new Error(
   *       `Cannot export: ${incomplete.length} questions still need review`
   *     );
   *   }
   *
   *   return api.exportIncomingQuestionnaire(questionnaireId);
   * }
   *
   * @see {@link getIncomingQuestionnaire} for checking completion status
   * @see {@link updateIncomingQuestionnaireItem} for finalizing answers
   * @see {@link deleteIncomingQuestionnaire} for cleanup after export
   */
  async exportIncomingQuestionnaire(id: string): Promise<Blob> {
    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/tprm/incoming/${id}/export`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to export questionnaire')
    }
    return response.blob()
  }

  /**
   * Retrieves comprehensive statistics for the Third Party Risk Management (TPRM) module.
   *
   * This method returns aggregated statistics across all TPRM activities including
   * vendor risk distribution, questionnaire status breakdown, risk assessment results,
   * assessment queue status, and dark web monitoring alerts. Use this data to power
   * TPRM dashboards and executive reporting.
   *
   * Statistics categories:
   * - **Vendors** - Total vendors, active count, and risk level distribution
   * - **Outgoing Questionnaires** - Questionnaires sent to vendors by status
   * - **Incoming Questionnaires** - Questionnaires received from third parties
   * - **Risk Assessments** - Completed assessments with risk score analysis
   * - **Assessment Queue** - Pending and in-progress assessments
   * - **Dark Web Alerts** - Security alerts by severity and status
   *
   * Vendor risk levels (from risk assessments):
   * - `critical` - Risk score 80-100, requires immediate attention
   * - `high` - Risk score 60-79, significant concerns
   * - `medium` - Risk score 40-59, moderate risk
   * - `low` - Risk score 20-39, acceptable risk
   * - `minimal` - Risk score 0-19, very low risk
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `stats` {Object} - Aggregated TPRM statistics:
   *     - `vendors` {Object} - Vendor statistics:
   *       - `total_vendors` {number} - Total number of vendors
   *       - `active_vendors` {number} - Vendors with active status
   *       - `critical_risk` {number} - Vendors at critical risk level
   *       - `high_risk` {number} - Vendors at high risk level
   *       - `medium_risk` {number} - Vendors at medium risk level
   *       - `low_risk` {number} - Vendors at low or minimal risk level
   *     - `outgoing_questionnaires` {Object} - Outgoing questionnaire counts:
   *       - `total` {number} - Total questionnaires sent
   *       - `draft` {number} - Questionnaires in draft
   *       - `sent` {number} - Sent, awaiting vendor response
   *       - `in_progress` {number} - Vendor is responding
   *       - `completed` {number} - Fully completed
   *     - `incoming_questionnaires` {Object} - Incoming questionnaire counts:
   *       - `total` {number} - Total questionnaires received
   *       - `pending` {number} - Awaiting processing
   *       - `reviewing` {number} - Under review
   *       - `completed` {number} - Fully completed
   *     - `risk_assessments` {Object} - Assessment statistics:
   *       - `total_assessments` {number} - Total assessments run
   *       - `critical_risk` {number} - Assessments with critical risk
   *       - `high_risk` {number} - Assessments with high risk
   *       - `medium_risk` {number} - Assessments with medium risk
   *       - `low_risk` {number} - Assessments with low risk
   *       - `minimal_risk` {number} - Assessments with minimal risk
   *       - `avg_risk_score` {number} - Average risk score (0-100)
   *     - `assessment_queue` {Object} - Queue statistics:
   *       - `total` {number} - Total items in queue
   *       - `queued` {number} - Waiting to be processed
   *       - `processing` {number} - Currently being assessed
   *     - `dark_web_alerts` {Object} - Alert statistics:
   *       - `total_alerts` {number} - Total dark web alerts
   *       - `new_alerts` {number} - New, unacknowledged alerts
   *       - `investigating` {number} - Under investigation
   *       - `critical_alerts` {number} - Critical severity
   *       - `high_alerts` {number} - High severity
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view TPRM statistics
   *
   * @example
   * // Load TPRM dashboard data
   * const { stats } = await api.getTPRMStats();
   * console.log(`Managing ${stats.vendors.total_vendors} vendors`);
   * console.log(`${stats.vendors.critical_risk} vendors at critical risk`);
   *
   * @example
   * // TPRM dashboard component
   * function TPRMDashboard() {
   *   const [stats, setStats] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getTPRMStats()
   *       .then(({ stats }) => setStats(stats))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div className="grid grid-cols-3 gap-4">
   *       <StatCard
   *         title="Vendor Portfolio"
   *         value={stats.vendors.total_vendors}
   *         breakdown={{
   *           'Active': stats.vendors.active_vendors,
   *           'Critical Risk': stats.vendors.critical_risk,
   *           'High Risk': stats.vendors.high_risk
   *         }}
   *       />
   *       <StatCard
   *         title="Outgoing Questionnaires"
   *         value={stats.outgoing_questionnaires.total}
   *         breakdown={{
   *           'Awaiting Response': stats.outgoing_questionnaires.sent,
   *           'In Progress': stats.outgoing_questionnaires.in_progress,
   *           'Completed': stats.outgoing_questionnaires.completed
   *         }}
   *       />
   *       <StatCard
   *         title="Incoming Questionnaires"
   *         value={stats.incoming_questionnaires.total}
   *         breakdown={{
   *           'Pending Review': stats.incoming_questionnaires.pending,
   *           'Under Review': stats.incoming_questionnaires.reviewing,
   *           'Completed': stats.incoming_questionnaires.completed
   *         }}
   *       />
   *       <RiskDistributionChart assessments={stats.risk_assessments} />
   *       <QueueStatus queue={stats.assessment_queue} />
   *       <AlertsWidget alerts={stats.dark_web_alerts} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Executive risk summary
   * async function getExecutiveSummary() {
   *   const { stats } = await api.getTPRMStats();
   *
   *   const highRiskVendorPercent = (
   *     (stats.vendors.critical_risk + stats.vendors.high_risk) /
   *     stats.vendors.total_vendors * 100
   *   ).toFixed(1);
   *
   *   return {
   *     totalVendors: stats.vendors.total_vendors,
   *     highRiskVendorPercent: `${highRiskVendorPercent}%`,
   *     averageRiskScore: stats.risk_assessments.avg_risk_score,
   *     openAlerts: stats.dark_web_alerts.new_alerts + stats.dark_web_alerts.investigating,
   *     pendingQuestionnaires:
   *       stats.incoming_questionnaires.pending +
   *       stats.incoming_questionnaires.reviewing
   *   };
   * }
   *
   * @see {@link getVendors} for detailed vendor listing
   * @see {@link getQuestionnaires} for outgoing questionnaire details
   * @see {@link getIncomingQuestionnaires} for incoming questionnaire details
   * @see {@link getRiskAssessments} for assessment details
   * @see {@link getDarkWebAlerts} for alert details
   */
  async getTPRMStats() {
    return this.request<{
      success: boolean
      stats: {
        vendors: {
          total_vendors: number
          active_vendors: number
          critical_risk: number
          high_risk: number
          medium_risk: number
          low_risk: number
        }
        outgoing_questionnaires: {
          total: number
          draft: number
          sent: number
          in_progress: number
          completed: number
        }
        incoming_questionnaires: {
          total: number
          pending: number
          reviewing: number
          completed: number
        }
        risk_assessments: {
          total_assessments: number
          critical_risk: number
          high_risk: number
          medium_risk: number
          low_risk: number
          minimal_risk: number
          avg_risk_score: number
        }
        assessment_queue: {
          total: number
          queued: number
          processing: number
        }
        dark_web_alerts: {
          total_alerts: number
          new_alerts: number
          investigating: number
          critical_alerts: number
          high_alerts: number
        }
      }
    }>('/api/tprm/stats')
  }

  // Risk Assessments endpoints

  /**
   * Retrieves a list of vendor risk assessments with optional filtering.
   *
   * This method fetches risk assessments that have been performed on third-party
   * vendors in your TPRM (Third Party Risk Management) program. Each assessment
   * provides a comprehensive risk profile based on multiple scoring categories
   * that evaluate different aspects of vendor risk.
   *
   * Risk assessments evaluate vendors across four key categories:
   * - **Security Score** - Technical security posture (SSL/TLS, headers, vulnerabilities)
   * - **Reputation Score** - Business reputation and trustworthiness indicators
   * - **Compliance Score** - Regulatory compliance and certification status
   * - **Dark Web Score** - Presence of credentials/data on dark web marketplaces
   *
   * Each category is scored 0-100, with higher scores indicating lower risk.
   * The overall risk score is a weighted average of these categories.
   *
   * Risk levels based on overall score:
   * - `critical` - Score 0-25: Immediate attention required
   * - `high` - Score 26-50: Significant risk, remediation needed
   * - `medium` - Score 51-75: Moderate risk, monitor closely
   * - `low` - Score 76-90: Acceptable risk level
   * - `minimal` - Score 91-100: Low risk vendor
   *
   * @param {Object} [params] - Optional filter parameters.
   * @param {string} [params.vendor_id] - Filter assessments for a specific vendor.
   *   Returns all assessments (including historical) for that vendor.
   * @param {string} [params.risk_level] - Filter by risk level.
   *   Values: 'critical', 'high', 'medium', 'low', 'minimal'.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `assessments` {Array<Object>} - Array of assessment objects:
   *     - `id` {string} - Unique assessment identifier
   *     - `vendor_id` {string} - Associated vendor ID
   *     - `vendor_name` {string} - Vendor display name
   *     - `overall_risk_score` {number} - Combined risk score (0-100)
   *     - `overall_risk_level` {string} - Risk level classification
   *     - `security_score` {number} - Security category score (0-100)
   *     - `reputation_score` {number} - Reputation category score (0-100)
   *     - `compliance_score` {number} - Compliance category score (0-100)
   *     - `dark_web_score` {number} - Dark web exposure score (0-100)
   *     - `findings_count` {number} - Number of findings/issues identified
   *     - `assessed_at` {number} - Assessment timestamp (Unix epoch)
   *     - `expires_at` {number|null} - When reassessment is recommended
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view vendor assessments
   *   - Invalid risk_level parameter provided
   *
   * @example
   * // Load all vendor risk assessments
   * const { assessments } = await api.getRiskAssessments();
   * console.log(`${assessments.length} vendors assessed`);
   *
   * @example
   * // Filter to show only high-risk vendors
   * const { assessments } = await api.getRiskAssessments({
   *   risk_level: 'high'
   * });
   * assessments.forEach(a => {
   *   console.log(`${a.vendor_name}: Score ${a.overall_risk_score}`);
   * });
   *
   * @example
   * // Load assessment history for a specific vendor
   * async function getVendorAssessmentHistory(vendorId: string) {
   *   const { assessments } = await api.getRiskAssessments({
   *     vendor_id: vendorId
   *   });
   *   // Sort by date to show trend
   *   return assessments.sort((a, b) => b.assessed_at - a.assessed_at);
   * }
   *
   * @example
   * // Vendor risk dashboard component
   * function VendorRiskDashboard() {
   *   const [assessments, setAssessments] = useState([]);
   *   const [riskFilter, setRiskFilter] = useState('all');
   *
   *   useEffect(() => {
   *     const params = riskFilter !== 'all'
   *       ? { risk_level: riskFilter }
   *       : undefined;
   *
   *     api.getRiskAssessments(params).then(({ assessments }) => {
   *       setAssessments(assessments);
   *     });
   *   }, [riskFilter]);
   *
   *   return (
   *     <RiskAssessmentTable
   *       assessments={assessments}
   *       onFilterChange={setRiskFilter}
   *     />
   *   );
   * }
   *
   * @example
   * // Group assessments by risk level for summary
   * async function getRiskDistribution() {
   *   const { assessments } = await api.getRiskAssessments();
   *   return assessments.reduce((acc, a) => {
   *     acc[a.overall_risk_level] = (acc[a.overall_risk_level] || 0) + 1;
   *     return acc;
   *   }, {} as Record<string, number>);
   * }
   *
   * @see {@link getRiskAssessment} for retrieving a single assessment with full findings
   * @see {@link runRiskAssessment} for triggering a new vendor assessment
   * @see {@link getVendors} for vendor listing and management
   * @see {@link getTPRMStats} for overall TPRM statistics
   */
  async getRiskAssessments(params?: { vendor_id?: string; risk_level?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.vendor_id) searchParams.set('vendor_id', params.vendor_id)
    if (params?.risk_level) searchParams.set('risk_level', params.risk_level)
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      assessments: any[]
    }>(`/api/tprm/assessments${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a single vendor risk assessment with detailed findings.
   *
   * This method fetches the complete details of a specific risk assessment,
   * including all individual findings and recommendations. Use this to display
   * a detailed assessment report, investigate specific risk factors, or review
   * the evidence behind each score category.
   *
   * The assessment includes scores across four risk categories:
   * - **Security** - Technical vulnerabilities, SSL configuration, security headers
   * - **Reputation** - Business reputation indicators, reviews, news sentiment
   * - **Compliance** - Certifications, regulatory compliance evidence
   * - **Dark Web** - Credential leaks, data exposure on underground forums
   *
   * Each finding represents a specific issue or observation that contributed
   * to the category scores. Findings include severity levels and remediation
   * recommendations that can be shared with the vendor.
   *
   * @param {string} id - The unique identifier of the assessment to retrieve.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `assessment` {Object} - The assessment details:
   *     - `id` {string} - Unique assessment identifier
   *     - `vendor_id` {string} - Associated vendor ID
   *     - `vendor_name` {string} - Vendor display name
   *     - `vendor_website` {string} - Vendor website URL assessed
   *     - `overall_risk_score` {number} - Combined risk score (0-100)
   *     - `overall_risk_level` {string} - Risk level classification
   *     - `security_score` {number} - Security category score (0-100)
   *     - `reputation_score` {number} - Reputation category score (0-100)
   *     - `compliance_score` {number} - Compliance category score (0-100)
   *     - `dark_web_score` {number} - Dark web exposure score (0-100)
   *     - `assessed_at` {number} - Assessment timestamp
   *     - `assessed_by` {string|null} - User who triggered assessment
   *     - `expires_at` {number|null} - Recommended reassessment date
   *   - `findings` {Array<Object>} - Array of finding objects:
   *     - `id` {string} - Finding identifier
   *     - `category` {string} - 'security' | 'reputation' | 'compliance' | 'dark_web'
   *     - `severity` {string} - 'critical' | 'high' | 'medium' | 'low' | 'info'
   *     - `title` {string} - Brief finding title
   *     - `description` {string} - Detailed finding description
   *     - `recommendation` {string|null} - Suggested remediation steps
   *     - `evidence` {Object|null} - Supporting evidence data
   *     - `detected_at` {number} - When finding was detected
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view vendor assessments
   *   - Assessment with specified ID does not exist
   *   - Assessment belongs to a different tenant
   *
   * @example
   * // Load assessment for detail view
   * const { assessment, findings } = await api.getRiskAssessment('ra-123');
   * console.log(`${assessment.vendor_name}: ${assessment.overall_risk_level}`);
   * console.log(`${findings.length} findings identified`);
   *
   * @example
   * // Display assessment detail page
   * function AssessmentDetailPage({ id }: { id: string }) {
   *   const [assessment, setAssessment] = useState(null);
   *   const [findings, setFindings] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getRiskAssessment(id)
   *       .then(({ assessment, findings }) => {
   *         setAssessment(assessment);
   *         setFindings(findings);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [id]);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div>
   *       <ScoreCard
   *         overall={assessment.overall_risk_score}
   *         security={assessment.security_score}
   *         reputation={assessment.reputation_score}
   *         compliance={assessment.compliance_score}
   *         darkWeb={assessment.dark_web_score}
   *       />
   *       <FindingsTable findings={findings} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Filter findings by severity
   * async function getCriticalFindings(assessmentId: string) {
   *   const { findings } = await api.getRiskAssessment(assessmentId);
   *   return findings.filter(f =>
   *     f.severity === 'critical' || f.severity === 'high'
   *   );
   * }
   *
   * @example
   * // Group findings by category for display
   * async function getFindingsByCategory(assessmentId: string) {
   *   const { findings } = await api.getRiskAssessment(assessmentId);
   *   return findings.reduce((acc, f) => {
   *     if (!acc[f.category]) acc[f.category] = [];
   *     acc[f.category].push(f);
   *     return acc;
   *   }, {} as Record<string, typeof findings>);
   * }
   *
   * @see {@link getRiskAssessments} for listing all assessments
   * @see {@link runRiskAssessment} for triggering a new assessment
   * @see {@link getVendor} for vendor details
   * @see {@link getDarkWebAlerts} for dark web monitoring alerts
   */
  async getRiskAssessment(id: string) {
    return this.request<{
      success: boolean
      assessment: any
      findings: any[]
    }>(`/api/tprm/assessments/${id}`)
  }

  /**
   * Triggers a new risk assessment for a third-party vendor.
   *
   * This method initiates an automated risk assessment that analyzes a vendor's
   * publicly available information to generate risk scores across multiple
   * categories. The assessment runs asynchronously but returns initial results
   * immediately, with detailed findings populated shortly after.
   *
   * The assessment engine evaluates the vendor across four key categories:
   *
   * **Security Score (0-100):**
   * - SSL/TLS certificate validity and configuration
   * - HTTP security headers (CSP, HSTS, X-Frame-Options, etc.)
   * - Known vulnerability exposure
   * - DNS security (DNSSEC, SPF, DKIM, DMARC)
   *
   * **Reputation Score (0-100):**
   * - Business registration and legitimacy
   * - Online reviews and ratings
   * - News sentiment analysis
   * - Domain age and history
   *
   * **Compliance Score (0-100):**
   * - Published certifications (SOC 2, ISO 27001, etc.)
   * - Privacy policy presence and quality
   * - Terms of service analysis
   * - GDPR/regulatory compliance indicators
   *
   * **Dark Web Score (0-100):**
   * - Credential leaks associated with vendor domain
   * - Data breaches involving the vendor
   * - Mentions on underground forums
   * - Paste sites and data dumps
   *
   * Higher scores indicate lower risk. The overall score is a weighted
   * average of all categories.
   *
   * @param {string} vendorId - The unique identifier of the vendor to assess.
   *   The vendor must exist and have a valid website URL configured.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the assessment was initiated
   *   - `error` {string} [optional] - Error message if assessment failed
   *   - `assessment` {Object} - The assessment results:
   *     - `id` {string} - Unique assessment identifier
   *     - `vendor_id` {string} - The assessed vendor's ID
   *     - `vendor_name` {string} - Vendor display name
   *     - `overall_risk_score` {number} - Combined risk score (0-100)
   *     - `overall_risk_level` {string} - Risk classification
   *       ('critical' | 'high' | 'medium' | 'low' | 'minimal')
   *     - `security_score` {number} - Security category score (0-100)
   *     - `reputation_score` {number} - Reputation category score (0-100)
   *     - `compliance_score` {number} - Compliance category score (0-100)
   *     - `dark_web_score` {number} - Dark web exposure score (0-100)
   *     - `findings_count` {number} - Number of findings identified
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to run vendor assessments
   *   - Vendor with specified ID does not exist
   *   - Vendor has no website URL configured
   *   - Assessment is already in progress for this vendor
   *   - Rate limit exceeded for assessments
   *
   * @example
   * // Run assessment and display results
   * const { assessment } = await api.runRiskAssessment('vendor-123');
   * console.log(`Risk Level: ${assessment.overall_risk_level}`);
   * console.log(`Overall Score: ${assessment.overall_risk_score}/100`);
   *
   * @example
   * // Assessment button with loading state
   * function RunAssessmentButton({ vendorId }: { vendorId: string }) {
   *   const [loading, setLoading] = useState(false);
   *   const [result, setResult] = useState(null);
   *
   *   const handleRun = async () => {
   *     setLoading(true);
   *     try {
   *       const { assessment, error } = await api.runRiskAssessment(vendorId);
   *       if (error) {
   *         toast.error(error);
   *       } else {
   *         setResult(assessment);
   *         toast.success(`Assessment complete: ${assessment.overall_risk_level}`);
   *       }
   *     } catch (e) {
   *       toast.error('Failed to run assessment');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <Button onClick={handleRun} disabled={loading}>
   *       {loading ? 'Assessing...' : 'Run Assessment'}
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Bulk assessment for multiple vendors
   * async function assessMultipleVendors(vendorIds: string[]) {
   *   const results = [];
   *   for (const id of vendorIds) {
   *     try {
   *       const { assessment } = await api.runRiskAssessment(id);
   *       results.push({ vendorId: id, success: true, assessment });
   *     } catch (error) {
   *       results.push({ vendorId: id, success: false, error });
   *     }
   *     // Small delay to avoid rate limiting
   *     await new Promise(r => setTimeout(r, 1000));
   *   }
   *   return results;
   * }
   *
   * @example
   * // Vendor detail page with assessment action
   * function VendorDetail({ vendorId }: { vendorId: string }) {
   *   const [vendor, setVendor] = useState(null);
   *   const [assessment, setAssessment] = useState(null);
   *
   *   const runNewAssessment = async () => {
   *     const { assessment } = await api.runRiskAssessment(vendorId);
   *     setAssessment(assessment);
   *     // Navigate to assessment detail
   *     router.push(`/tprm/assessments/${assessment.id}`);
   *   };
   *
   *   return (
   *     <VendorCard
   *       vendor={vendor}
   *       onRunAssessment={runNewAssessment}
   *     />
   *   );
   * }
   *
   * @see {@link getRiskAssessments} for listing all assessments
   * @see {@link getRiskAssessment} for detailed assessment with findings
   * @see {@link queueAssessment} for scheduling assessments
   * @see {@link getVendor} for vendor details
   */
  async runRiskAssessment(vendorId: string) {
    return this.request<{
      success: boolean
      error?: string
      assessment: {
        id: string
        vendor_id: string
        vendor_name: string
        overall_risk_score: number
        overall_risk_level: string
        security_score: number
        reputation_score: number
        compliance_score: number
        dark_web_score: number
        findings_count: number
      }
    }>(`/api/tprm/assessments/${vendorId}/run`, {
      method: 'POST',
    })
  }

  // Self-Assessment (organization's own risk assessment)

  /**
   * Runs a risk assessment on your own organization.
   *
   * This method initiates a self-assessment that evaluates your organization
   * using the same methodology applied to third-party vendors. This allows you
   * to see how your organization would appear to external partners conducting
   * their own TPRM due diligence on you.
   *
   * Self-assessments are valuable for:
   * - **Benchmarking** - Compare your security posture against vendor standards
   * - **Due diligence preparation** - Know what customers will find when assessing you
   * - **Gap identification** - Discover issues before external parties do
   * - **Continuous improvement** - Track progress over time with periodic assessments
   *
   * The assessment evaluates the same four categories as vendor assessments:
   *
   * **Security Score (0-100):**
   * - SSL/TLS configuration on your domains
   * - Security headers implementation
   * - Known vulnerability exposure
   * - Email security (SPF, DKIM, DMARC)
   *
   * **Reputation Score (0-100):**
   * - Online presence and reviews
   * - News and media sentiment
   * - Domain history and trust signals
   *
   * **Compliance Score (0-100):**
   * - Published certifications and attestations
   * - Privacy policy quality
   * - Regulatory compliance indicators
   *
   * **Dark Web Score (0-100):**
   * - Employee credential exposures
   * - Company data in breaches
   * - Mentions on underground forums
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the assessment was initiated
   *   - `error` {string} [optional] - Error message if assessment failed
   *   - `assessment` {Object} - The assessment results:
   *     - `id` {string} - Unique assessment identifier
   *     - `type` {string} - Always 'self' for self-assessments
   *     - `organization_name` {string} - Your organization's name
   *     - `website` {string} - Your organization's website assessed
   *     - `overall_risk_score` {number} - Combined risk score (0-100)
   *     - `overall_risk_level` {string} - Risk classification
   *       ('critical' | 'high' | 'medium' | 'low' | 'minimal')
   *     - `security_score` {number} - Security category score (0-100)
   *     - `reputation_score` {number} - Reputation category score (0-100)
   *     - `compliance_score` {number} - Compliance category score (0-100)
   *     - `dark_web_score` {number} - Dark web exposure score (0-100)
   *     - `findings_count` {number} - Number of findings identified
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to run self-assessments (typically admin only)
   *   - Organization has no website URL configured
   *   - Self-assessment is already in progress
   *   - Rate limit exceeded for assessments
   *
   * @example
   * // Run self-assessment and display results
   * const { assessment, error } = await api.runSelfAssessment();
   * if (error) {
   *   console.error('Assessment failed:', error);
   * } else {
   *   console.log(`Your organization: ${assessment.organization_name}`);
   *   console.log(`Risk Level: ${assessment.overall_risk_level}`);
   *   console.log(`Overall Score: ${assessment.overall_risk_score}/100`);
   * }
   *
   * @example
   * // Self-assessment dashboard card
   * function SelfAssessmentCard() {
   *   const [loading, setLoading] = useState(false);
   *   const [result, setResult] = useState(null);
   *
   *   const handleRunAssessment = async () => {
   *     setLoading(true);
   *     try {
   *       const { assessment, error } = await api.runSelfAssessment();
   *       if (error) {
   *         toast.error(error);
   *       } else {
   *         setResult(assessment);
   *         toast.success('Self-assessment complete!');
   *       }
   *     } catch (e) {
   *       toast.error('Failed to run self-assessment');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <Card>
   *       <CardHeader>
   *         <CardTitle>Your Security Posture</CardTitle>
   *         <CardDescription>
   *           See how your organization appears to external assessors
   *         </CardDescription>
   *       </CardHeader>
   *       <CardContent>
   *         {result ? (
   *           <ScoreDisplay
   *             overall={result.overall_risk_score}
   *             level={result.overall_risk_level}
   *           />
   *         ) : (
   *           <Button onClick={handleRunAssessment} disabled={loading}>
   *             {loading ? 'Analyzing...' : 'Run Self-Assessment'}
   *           </Button>
   *         )}
   *       </CardContent>
   *     </Card>
   *   );
   * }
   *
   * @example
   * // Compare self-assessment to vendor baseline
   * async function compareToVendorBaseline() {
   *   const { assessment } = await api.runSelfAssessment();
   *   const { assessments } = await api.getRiskAssessments();
   *
   *   const avgVendorScore = assessments.reduce(
   *     (sum, a) => sum + a.overall_risk_score, 0
   *   ) / assessments.length;
   *
   *   return {
   *     yourScore: assessment.overall_risk_score,
   *     vendorAverage: avgVendorScore,
   *     difference: assessment.overall_risk_score - avgVendorScore
   *   };
   * }
   *
   * @see {@link getSelfAssessmentHistory} for historical self-assessments
   * @see {@link runRiskAssessment} for assessing third-party vendors
   * @see {@link getOrganization} for organization settings
   */
  async runSelfAssessment() {
    return this.request<{
      success: boolean
      error?: string
      assessment: {
        id: string
        type: 'self'
        organization_name: string
        website: string
        overall_risk_score: number
        overall_risk_level: string
        security_score: number
        reputation_score: number
        compliance_score: number
        dark_web_score: number
        findings_count: number
      }
    }>('/api/tprm/self-assessment/run', {
      method: 'POST',
    })
  }

  /**
   * Retrieves the history of self-assessments for your organization.
   *
   * This method fetches all historical self-assessments, allowing you to track
   * your organization's security posture over time. Use this to build trend
   * charts, monitor improvement after remediation efforts, and demonstrate
   * security progress to stakeholders or customers.
   *
   * Self-assessment history is valuable for:
   * - **Trend analysis** - Track score improvements over time
   * - **Remediation validation** - Verify that security fixes improved scores
   * - **Compliance reporting** - Show continuous security monitoring
   * - **Board/executive reporting** - Demonstrate security investment ROI
   *
   * Assessments are returned in reverse chronological order (newest first),
   * with each entry containing the same scoring breakdown as individual
   * assessments (security, reputation, compliance, dark web).
   *
   * @param {Object} [params] - Optional pagination parameters.
   * @param {number} [params.limit=50] - Maximum number of assessments to return.
   *   Default is 50, maximum is typically 100.
   * @param {number} [params.offset=0] - Number of assessments to skip for pagination.
   *
   * @returns {Promise<Object>} Response object containing:
   *   - `success` {boolean} - Whether the request succeeded
   *   - `organization` {Object} - Your organization details:
   *     - `company_name` {string} - Organization name
   *     - `website` {string|null} - Website URL assessed
   *   - `assessments` {Array<Object>} - Array of historical assessments:
   *     - `id` {string} - Unique assessment identifier
   *     - `overall_risk_score` {number} - Combined risk score (0-100)
   *     - `overall_risk_level` {string} - Risk level classification
   *     - `security_score` {number} - Security category score (0-100)
   *     - `reputation_score` {number} - Reputation category score (0-100)
   *     - `compliance_score` {number} - Compliance category score (0-100)
   *     - `dark_web_score` {number} - Dark web exposure score (0-100)
   *     - `findings_count` {number} - Number of findings identified
   *     - `assessed_at` {number} - Assessment timestamp (Unix epoch)
   *     - `assessed_by` {string|null} - User who ran the assessment
   *   - `pagination` {Object} - Pagination metadata:
   *     - `limit` {number} - Requested page size
   *     - `offset` {number} - Current offset
   *
   * @throws {Error} Throws if:
   *   - User is not authenticated
   *   - User lacks permission to view self-assessment history
   *
   * @example
   * // Load recent self-assessment history
   * const { organization, assessments } = await api.getSelfAssessmentHistory();
   * console.log(`${organization.company_name} - ${assessments.length} assessments`);
   *
   * @example
   * // Build trend chart data
   * async function getScoreTrendData() {
   *   const { assessments } = await api.getSelfAssessmentHistory({
   *     limit: 12  // Last 12 assessments
   *   });
   *
   *   return assessments
   *     .reverse()  // Oldest first for chart
   *     .map(a => ({
   *       date: new Date(a.assessed_at),
   *       overall: a.overall_risk_score,
   *       security: a.security_score,
   *       reputation: a.reputation_score,
   *       compliance: a.compliance_score,
   *       darkWeb: a.dark_web_score
   *     }));
   * }
   *
   * @example
   * // Self-assessment history component with trend chart
   * function SelfAssessmentHistory() {
   *   const [organization, setOrganization] = useState(null);
   *   const [assessments, setAssessments] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getSelfAssessmentHistory({ limit: 20 })
   *       .then(({ organization, assessments }) => {
   *         setOrganization(organization);
   *         setAssessments(assessments);
   *       })
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div>
   *       <h2>{organization.company_name} Security Trend</h2>
   *       <TrendChart
   *         data={assessments.map(a => ({
   *           x: a.assessed_at,
   *           y: a.overall_risk_score
   *         }))}
   *       />
   *       <AssessmentTable assessments={assessments} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate score improvement since first assessment
   * async function getOverallImprovement() {
   *   const { assessments } = await api.getSelfAssessmentHistory();
   *   if (assessments.length < 2) return null;
   *
   *   const latest = assessments[0];
   *   const oldest = assessments[assessments.length - 1];
   *
   *   return {
   *     current: latest.overall_risk_score,
   *     initial: oldest.overall_risk_score,
   *     improvement: latest.overall_risk_score - oldest.overall_risk_score,
   *     percentChange: ((latest.overall_risk_score - oldest.overall_risk_score)
   *       / oldest.overall_risk_score * 100).toFixed(1)
   *   };
   * }
   *
   * @example
   * // Paginated history viewer
   * function PaginatedHistory({ pageSize = 10 }: { pageSize?: number }) {
   *   const [page, setPage] = useState(0);
   *   const [assessments, setAssessments] = useState([]);
   *
   *   useEffect(() => {
   *     api.getSelfAssessmentHistory({
   *       limit: pageSize,
   *       offset: page * pageSize
   *     }).then(({ assessments }) => {
   *       setAssessments(assessments);
   *     });
   *   }, [page, pageSize]);
   *
   *   return (
   *     <div>
   *       <HistoryTable assessments={assessments} />
   *       <Pagination
   *         page={page}
   *         onPageChange={setPage}
   *         hasMore={assessments.length === pageSize}
   *       />
   *     </div>
   *   );
   * }
   *
   * @see {@link runSelfAssessment} for running a new self-assessment
   * @see {@link getRiskAssessments} for vendor assessment history
   * @see {@link getOrganization} for organization settings
   */
  async getSelfAssessmentHistory(params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      organization: { company_name: string; website: string | null }
      assessments: any[]
      pagination: { limit: number; offset: number }
    }>(`/api/tprm/self-assessment/history${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves the vendor risk assessment queue.
   *
   * The assessment queue manages scheduled and in-progress vendor risk assessments.
   * Items move through the queue lifecycle: queued → processing → completed/failed.
   * Use this to monitor assessment progress and manage the assessment workload.
   *
   * **Queue Item Statuses:**
   * - `queued` - Waiting to be processed
   * - `processing` - Currently being assessed
   * - `completed` - Assessment finished successfully
   * - `failed` - Assessment encountered an error
   *
   * @param params - Optional filter parameters
   * @param params.status - Filter by queue item status ('queued' | 'processing' | 'completed' | 'failed')
   *
   * @returns Promise resolving to queue items and status counts
   * @returns response.success - Whether the request succeeded
   * @returns response.queue - Array of queue items with vendor info, status, priority, and timestamps
   * @returns response.counts - Breakdown of queue items by status
   * @returns response.counts.total - Total items in queue
   * @returns response.counts.queued - Items waiting to be processed
   * @returns response.counts.processing - Items currently being assessed
   * @returns response.counts.completed - Items that finished successfully
   * @returns response.counts.failed - Items that encountered errors
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If tenant context is missing (400)
   *
   * @example
   * // Load the full assessment queue
   * const { queue, counts } = await api.getAssessmentQueue();
   * console.log(`${counts.queued} pending, ${counts.processing} in progress`);
   *
   * @example
   * // View only queued (pending) assessments
   * const { queue } = await api.getAssessmentQueue({ status: 'queued' });
   * queue.forEach(item => {
   *   console.log(`${item.vendor_name} - Priority: ${item.priority}`);
   * });
   *
   * @example
   * // Queue management dashboard component
   * function QueueDashboard() {
   *   const [queue, setQueue] = useState([]);
   *   const [counts, setCounts] = useState({});
   *   const [statusFilter, setStatusFilter] = useState<string>();
   *
   *   useEffect(() => {
   *     api.getAssessmentQueue({ status: statusFilter })
   *       .then(({ queue, counts }) => {
   *         setQueue(queue);
   *         setCounts(counts);
   *       });
   *   }, [statusFilter]);
   *
   *   return (
   *     <div>
   *       <QueueStats counts={counts} />
   *       <StatusFilter value={statusFilter} onChange={setStatusFilter} />
   *       <QueueTable items={queue} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Auto-refresh queue status (polling every 30 seconds)
   * useEffect(() => {
   *   const interval = setInterval(async () => {
   *     const { counts } = await api.getAssessmentQueue();
   *     setProcessingCount(counts.processing);
   *   }, 30000);
   *   return () => clearInterval(interval);
   * }, []);
   *
   * @see {@link queueAssessment} for scheduling a single vendor assessment
   * @see {@link bulkQueueAssessments} for scheduling multiple assessments
   * @see {@link removeFromQueue} for canceling a queued assessment
   * @see {@link getRiskAssessments} for viewing completed assessment results
   */
  async getAssessmentQueue(params?: { status?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      queue: any[]
      counts: {
        total: number
        queued: number
        processing: number
        completed: number
        failed: number
      }
    }>(`/api/tprm/assessments/queue${query ? `?${query}` : ''}`)
  }

  /**
   * Schedules a single vendor for risk assessment.
   *
   * Adds a vendor to the assessment queue for automated risk evaluation. The assessment
   * will analyze the vendor's security posture, compliance status, reputation, and
   * dark web exposure. Higher priority items are processed first.
   *
   * **Priority Levels:**
   * - `1` (Highest) - Critical vendors, urgent reassessment needed
   * - `2` - High priority, important vendors
   * - `3` (Default) - Normal priority
   * - `4` - Low priority, routine assessment
   * - `5` (Lowest) - Background assessment, no urgency
   *
   * @param data - Assessment queue request parameters
   * @param data.vendor_id - The ID of the vendor to assess (required)
   * @param data.priority - Processing priority 1-5, lower = higher priority (default: 3)
   * @param data.reason - Reason for scheduling the assessment (e.g., 'Annual review', 'Contract renewal')
   * @param data.scheduled_for - Unix timestamp for when to run the assessment (optional, runs immediately if not set)
   *
   * @returns Promise resolving to the created queue item
   * @returns response.success - Whether the request succeeded
   * @returns response.queue_item - The created queue entry
   * @returns response.queue_item.id - Unique ID of the queue item (use for removeFromQueue)
   * @returns response.queue_item.vendor_id - The vendor being assessed
   * @returns response.queue_item.status - Initial status (typically 'queued')
   * @returns response.error - Error message if vendor is already queued or other issue
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If vendor not found (404)
   * @throws {Error} If vendor is already in the queue (409)
   *
   * @example
   * // Queue a vendor for immediate assessment
   * const { queue_item } = await api.queueAssessment({
   *   vendor_id: 'vendor_123',
   *   reason: 'Annual security review'
   * });
   * console.log(`Queued as ${queue_item.id}`);
   *
   * @example
   * // Queue high-priority assessment for critical vendor
   * await api.queueAssessment({
   *   vendor_id: 'critical_vendor_456',
   *   priority: 1,
   *   reason: 'Security incident reported'
   * });
   *
   * @example
   * // Schedule assessment for next week
   * const nextWeek = Math.floor(Date.now() / 1000) + (7 * 24 * 60 * 60);
   * await api.queueAssessment({
   *   vendor_id: 'vendor_789',
   *   scheduled_for: nextWeek,
   *   reason: 'Contract renewal due'
   * });
   *
   * @example
   * // Queue assessment button component
   * function QueueAssessmentButton({ vendorId, vendorName }: { vendorId: string; vendorName: string }) {
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleQueue = async () => {
   *     setLoading(true);
   *     try {
   *       const { success, error } = await api.queueAssessment({
   *         vendor_id: vendorId,
   *         reason: 'Manual assessment request'
   *       });
   *       if (success) {
   *         toast.success(`${vendorName} queued for assessment`);
   *       } else {
   *         toast.error(error || 'Failed to queue assessment');
   *       }
   *     } catch (err) {
   *       toast.error('Vendor may already be in queue');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <Button onClick={handleQueue} disabled={loading}>
   *       {loading ? 'Queueing...' : 'Run Assessment'}
   *     </Button>
   *   );
   * }
   *
   * @see {@link getAssessmentQueue} for viewing queued assessments
   * @see {@link bulkQueueAssessments} for scheduling multiple vendors
   * @see {@link removeFromQueue} for canceling a queued assessment
   * @see {@link getRiskAssessment} for viewing completed assessment results
   */
  async queueAssessment(data: { vendor_id: string; priority?: number; reason?: string; scheduled_for?: number }) {
    return this.request<{
      success: boolean
      queue_item: { id: string; vendor_id: string; status: string }
      error?: string
    }>('/api/tprm/assessments/queue', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Schedules multiple vendors for risk assessment in bulk.
   *
   * Efficiently queues many vendors for assessment using filter criteria or explicit
   * vendor IDs. Vendors already in the queue are automatically skipped. Use this for
   * periodic bulk reassessments (e.g., quarterly reviews of all high-risk vendors).
   *
   * **Selection Methods (use one):**
   * - `vendor_ids` - Explicit list of vendor IDs to queue
   * - `risk_level` - Queue all vendors at a specific risk level
   * - `category` - Queue all vendors in a category
   *
   * **Common Use Cases:**
   * - Quarterly reassessment of all critical/high-risk vendors
   * - Annual review of vendors in a specific category
   * - Re-run assessments after security policy changes
   *
   * @param data - Bulk queue request parameters
   * @param data.vendor_ids - Array of specific vendor IDs to queue (optional)
   * @param data.risk_level - Queue all vendors at this risk level: 'critical' | 'high' | 'medium' | 'low' | 'minimal' (optional)
   * @param data.category - Queue all vendors in this category (optional)
   * @param data.priority - Processing priority 1-5 for all queued items (default: 3)
   * @param data.reason - Reason for the bulk assessment (e.g., 'Q1 2024 review')
   *
   * @returns Promise resolving to bulk operation results
   * @returns response.success - Whether the request succeeded
   * @returns response.message - Human-readable result summary
   * @returns response.queued_count - Number of vendors successfully queued
   * @returns response.skipped_count - Number of vendors skipped (already queued or recently assessed)
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If no selection criteria provided (400)
   * @throws {Error} If invalid risk_level or category (400)
   *
   * @example
   * // Queue all high-risk vendors for reassessment
   * const { queued_count, skipped_count } = await api.bulkQueueAssessments({
   *   risk_level: 'high',
   *   priority: 2,
   *   reason: 'Quarterly high-risk vendor review'
   * });
   * console.log(`Queued ${queued_count} vendors, skipped ${skipped_count}`);
   *
   * @example
   * // Queue all vendors in a specific category
   * await api.bulkQueueAssessments({
   *   category: 'cloud_infrastructure',
   *   reason: 'Annual cloud vendor security review'
   * });
   *
   * @example
   * // Queue specific vendors by ID
   * const selectedVendorIds = ['vendor_1', 'vendor_2', 'vendor_3'];
   * await api.bulkQueueAssessments({
   *   vendor_ids: selectedVendorIds,
   *   priority: 1,
   *   reason: 'Contract renewal assessments'
   * });
   *
   * @example
   * // Bulk assessment form component
   * function BulkAssessmentForm() {
   *   const [riskLevel, setRiskLevel] = useState('');
   *   const [reason, setReason] = useState('');
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleSubmit = async (e: FormEvent) => {
   *     e.preventDefault();
   *     setLoading(true);
   *
   *     try {
   *       const { queued_count, skipped_count, message } = await api.bulkQueueAssessments({
   *         risk_level: riskLevel || undefined,
   *         reason
   *       });
   *
   *       toast.success(message);
   *       console.log(`Queued: ${queued_count}, Skipped: ${skipped_count}`);
   *     } catch (err) {
   *       toast.error('Bulk queue operation failed');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <RiskLevelSelect value={riskLevel} onChange={setRiskLevel} />
   *       <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for assessment" />
   *       <Button type="submit" disabled={loading}>
   *         {loading ? 'Queueing...' : 'Queue Assessments'}
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @see {@link getAssessmentQueue} for viewing queued assessments
   * @see {@link queueAssessment} for scheduling a single vendor
   * @see {@link getVendors} for listing vendors with filtering
   * @see {@link getRiskAssessments} for viewing completed assessment results
   */
  async bulkQueueAssessments(data: { risk_level?: string; category?: string; vendor_ids?: string[]; priority?: number; reason?: string }) {
    return this.request<{
      success: boolean
      message: string
      queued_count: number
      skipped_count: number
    }>('/api/tprm/assessments/queue/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Removes a vendor from the assessment queue.
   *
   * Cancels a pending or queued assessment before it starts processing.
   * Cannot remove assessments that are already in 'processing' status.
   * Use this to cancel mistakenly queued assessments or when a vendor
   * relationship is terminated.
   *
   * **Note:** This removes the queue item, not the vendor itself. To delete
   * a vendor entirely, use {@link deleteVendor}.
   *
   * @param id - The queue item ID (from getAssessmentQueue response), NOT the vendor ID
   *
   * @returns Promise resolving to the removal result
   * @returns response.success - Whether the removal succeeded
   * @returns response.message - Human-readable confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If queue item not found (404)
   * @throws {Error} If assessment is already processing (409)
   *
   * @example
   * // Remove a queued assessment
   * const { success, message } = await api.removeFromQueue('queue_item_123');
   * if (success) {
   *   console.log(message); // "Assessment removed from queue"
   * }
   *
   * @example
   * // Queue item row with cancel button
   * function QueueItemRow({ item }: { item: QueueItem }) {
   *   const [removing, setRemoving] = useState(false);
   *
   *   const handleRemove = async () => {
   *     if (!confirm(`Cancel assessment for ${item.vendor_name}?`)) return;
   *
   *     setRemoving(true);
   *     try {
   *       const { success } = await api.removeFromQueue(item.id);
   *       if (success) {
   *         toast.success('Assessment cancelled');
   *         refreshQueue(); // Reload the queue list
   *       }
   *     } catch (err) {
   *       toast.error('Cannot cancel - assessment may already be processing');
   *     } finally {
   *       setRemoving(false);
   *     }
   *   };
   *
   *   return (
   *     <tr>
   *       <td>{item.vendor_name}</td>
   *       <td>{item.status}</td>
   *       <td>{item.priority}</td>
   *       <td>
   *         <Button
   *           onClick={handleRemove}
   *           disabled={removing || item.status === 'processing'}
   *           variant="destructive"
   *         >
   *           {removing ? 'Cancelling...' : 'Cancel'}
   *         </Button>
   *       </td>
   *     </tr>
   *   );
   * }
   *
   * @example
   * // Bulk cancel with optimistic updates
   * async function cancelSelectedItems(itemIds: string[]) {
   *   const results = await Promise.allSettled(
   *     itemIds.map(id => api.removeFromQueue(id))
   *   );
   *
   *   const successful = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   toast.info(`Cancelled ${successful} items, ${failed} failed`);
   * }
   *
   * @see {@link getAssessmentQueue} for listing queue items
   * @see {@link queueAssessment} for scheduling assessments
   * @see {@link bulkQueueAssessments} for bulk scheduling
   * @see {@link deleteVendor} for removing a vendor entirely
   */
  async removeFromQueue(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/assessments/queue/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves dark web monitoring alerts for third-party vendors.
   *
   * Dark web monitoring is a critical component of Third Party Risk Management (TPRM),
   * alerting organizations when their vendors' data, credentials, or mentions appear
   * on dark web forums, paste sites, or breach databases. These alerts help identify
   * vendor security incidents early, enabling proactive risk mitigation.
   *
   * **Alert Severity Levels:**
   * - `critical` - Verified breach with sensitive data (credentials, PII, financial)
   * - `high` - Confirmed exposure with potential business impact
   * - `medium` - Suspicious mentions or unverified exposures
   * - `low` - Minor mentions or historical references
   *
   * **Alert Statuses:**
   * - `new` - Freshly detected, awaiting review
   * - `investigating` - Under active investigation by security team
   * - `resolved` - Investigated and remediation confirmed with vendor
   * - `dismissed` - Determined to be false positive or not applicable
   *
   * **Investigation Workflow:**
   * 1. New alerts are auto-generated by dark web monitoring service
   * 2. Security team triages and sets status to 'investigating'
   * 3. Team contacts vendor for verification and remediation plan
   * 4. Alert is resolved with notes or dismissed if false positive
   *
   * @param params - Optional filter parameters
   * @param params.vendor_id - Filter alerts for a specific vendor (show all if omitted)
   * @param params.status - Filter by alert status: 'new' | 'investigating' | 'resolved' | 'dismissed'
   * @param params.severity - Filter by severity level: 'critical' | 'high' | 'medium' | 'low'
   *
   * @returns Promise resolving to dark web alerts with aggregate counts
   * @returns response.success - Whether the request succeeded
   * @returns response.alerts - Array of dark web alert objects
   * @returns response.alerts[].id - Unique alert identifier
   * @returns response.alerts[].vendor_id - Associated vendor ID
   * @returns response.alerts[].vendor_name - Vendor display name
   * @returns response.alerts[].severity - Alert severity: 'critical' | 'high' | 'medium' | 'low'
   * @returns response.alerts[].status - Current status: 'new' | 'investigating' | 'resolved' | 'dismissed'
   * @returns response.alerts[].source - Where the alert originated (e.g., 'breach_database', 'dark_forum', 'paste_site')
   * @returns response.alerts[].description - Human-readable summary of the exposure
   * @returns response.alerts[].detected_at - ISO timestamp when alert was detected
   * @returns response.alerts[].resolution_notes - Notes from investigation (if resolved/dismissed)
   * @returns response.counts - Aggregate counts for dashboard metrics
   * @returns response.counts.total - Total number of alerts matching filters
   * @returns response.counts.new_alerts - Count of alerts in 'new' status
   * @returns response.counts.investigating - Count of alerts being investigated
   * @returns response.counts.critical - Count of critical severity alerts
   * @returns response.counts.high - Count of high severity alerts
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If tenant context is missing (400)
   *
   * @example
   * // Load all dark web alerts
   * const { alerts, counts } = await api.getDarkWebAlerts();
   * console.log(`${counts.new_alerts} new alerts, ${counts.critical} critical`);
   *
   * @example
   * // Get alerts for a specific vendor
   * const { alerts } = await api.getDarkWebAlerts({ vendor_id: 'vendor_123' });
   * alerts.forEach(alert => {
   *   console.log(`[${alert.severity}] ${alert.description}`);
   * });
   *
   * @example
   * // Filter to only critical/high alerts requiring attention
   * const criticalAlerts = await api.getDarkWebAlerts({ severity: 'critical' });
   * const highAlerts = await api.getDarkWebAlerts({ severity: 'high' });
   *
   * @example
   * // Dark web alerts dashboard component
   * function DarkWebAlertsDashboard() {
   *   const [alerts, setAlerts] = useState([]);
   *   const [counts, setCounts] = useState({});
   *   const [statusFilter, setStatusFilter] = useState<string>('new');
   *
   *   useEffect(() => {
   *     api.getDarkWebAlerts({ status: statusFilter })
   *       .then(({ alerts, counts }) => {
   *         setAlerts(alerts);
   *         setCounts(counts);
   *       });
   *   }, [statusFilter]);
   *
   *   return (
   *     <div>
   *       <AlertStats counts={counts} />
   *       <StatusFilter value={statusFilter} onChange={setStatusFilter} />
   *       <AlertTable alerts={alerts} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Vendor detail page with dark web alert section
   * function VendorDarkWebAlerts({ vendorId }: { vendorId: string }) {
   *   const [alerts, setAlerts] = useState([]);
   *
   *   useEffect(() => {
   *     api.getDarkWebAlerts({ vendor_id: vendorId })
   *       .then(({ alerts }) => setAlerts(alerts));
   *   }, [vendorId]);
   *
   *   if (alerts.length === 0) {
   *     return <p>No dark web alerts detected for this vendor.</p>;
   *   }
   *
   *   return (
   *     <div className="space-y-4">
   *       <h3>Dark Web Monitoring</h3>
   *       {alerts.map(alert => (
   *         <AlertCard key={alert.id} alert={alert} />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Notification badge showing critical dark web alerts
   * function DarkWebAlertBadge() {
   *   const [criticalCount, setCriticalCount] = useState(0);
   *
   *   useEffect(() => {
   *     api.getDarkWebAlerts({ status: 'new' })
   *       .then(({ counts }) => setCriticalCount(counts.critical));
   *   }, []);
   *
   *   if (criticalCount === 0) return null;
   *
   *   return (
   *     <Badge variant="destructive">
   *       {criticalCount} critical alerts
   *     </Badge>
   *   );
   * }
   *
   * @see {@link updateDarkWebAlert} for updating alert status and resolution
   * @see {@link getRiskAssessment} for full vendor risk assessment including dark web score
   * @see {@link getTPRMStats} for aggregate TPRM statistics including dark web metrics
   * @see {@link getVendor} for vendor details
   */
  async getDarkWebAlerts(params?: { vendor_id?: string; status?: string; severity?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.vendor_id) searchParams.set('vendor_id', params.vendor_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.severity) searchParams.set('severity', params.severity)
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      alerts: any[]
      counts: {
        total: number
        new_alerts: number
        investigating: number
        critical: number
        high: number
      }
    }>(`/api/tprm/dark-web-alerts${query ? `?${query}` : ''}`)
  }

  /**
   * Updates the status and resolution notes for a dark web alert.
   *
   * This method is used to progress dark web alerts through the investigation workflow.
   * Security teams use it to mark alerts as being investigated, record their findings,
   * and ultimately resolve or dismiss alerts with appropriate documentation.
   *
   * **Status Transitions:**
   * - `new` → `investigating` - Begin investigation
   * - `investigating` → `resolved` - Confirmed and remediated with vendor
   * - `investigating` → `dismissed` - False positive or not applicable
   * - Any status → `new` - Reopen for re-investigation
   *
   * **Best Practices:**
   * - Always add resolution_notes when resolving or dismissing an alert
   * - Document the vendor's response and remediation actions taken
   * - Include dates, ticket numbers, or references for audit trail
   * - For dismissed alerts, explain why it's a false positive
   *
   * @param id - The unique identifier of the dark web alert to update
   * @param data - Update payload with status and/or notes
   * @param data.status - New status: 'new' | 'investigating' | 'resolved' | 'dismissed'
   * @param data.resolution_notes - Free-text notes documenting the investigation outcome,
   *   vendor response, remediation actions, or dismissal reason. Recommended for
   *   'resolved' and 'dismissed' statuses.
   *
   * @returns Promise resolving to the update result
   * @returns response.success - Whether the update succeeded
   * @returns response.message - Human-readable confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If alert not found (404)
   * @throws {Error} If invalid status value provided (400)
   *
   * @example
   * // Start investigating an alert
   * await api.updateDarkWebAlert('alert_123', {
   *   status: 'investigating'
   * });
   *
   * @example
   * // Resolve an alert with vendor remediation notes
   * await api.updateDarkWebAlert('alert_123', {
   *   status: 'resolved',
   *   resolution_notes: 'Contacted vendor on 2024-01-15. They confirmed the breach ' +
   *     'affected a deprecated system. Password reset enforced for all users. ' +
   *     'Vendor ticket: VND-2024-0042'
   * });
   *
   * @example
   * // Dismiss a false positive alert
   * await api.updateDarkWebAlert('alert_456', {
   *   status: 'dismissed',
   *   resolution_notes: 'Alert references a different company with similar name. ' +
   *     'Domain does not match our vendor records.'
   * });
   *
   * @example
   * // Alert investigation form component
   * function AlertInvestigationForm({ alert, onComplete }: Props) {
   *   const [status, setStatus] = useState(alert.status);
   *   const [notes, setNotes] = useState(alert.resolution_notes || '');
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSubmit = async (e: FormEvent) => {
   *     e.preventDefault();
   *     setSaving(true);
   *
   *     try {
   *       const { success } = await api.updateDarkWebAlert(alert.id, {
   *         status,
   *         resolution_notes: notes
   *       });
   *
   *       if (success) {
   *         toast.success('Alert updated');
   *         onComplete();
   *       }
   *     } catch (err) {
   *       toast.error('Failed to update alert');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <select value={status} onChange={e => setStatus(e.target.value)}>
   *         <option value="new">New</option>
   *         <option value="investigating">Investigating</option>
   *         <option value="resolved">Resolved</option>
   *         <option value="dismissed">Dismissed</option>
   *       </select>
   *
   *       <textarea
   *         value={notes}
   *         onChange={e => setNotes(e.target.value)}
   *         placeholder="Investigation notes, vendor response, remediation steps..."
   *         rows={5}
   *       />
   *
   *       <Button type="submit" disabled={saving}>
   *         {saving ? 'Saving...' : 'Update Alert'}
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Quick action buttons on alert row
   * function AlertQuickActions({ alert, onUpdate }: Props) {
   *   const handleInvestigate = async () => {
   *     await api.updateDarkWebAlert(alert.id, { status: 'investigating' });
   *     onUpdate();
   *   };
   *
   *   const handleDismiss = async () => {
   *     const reason = prompt('Reason for dismissal:');
   *     if (!reason) return;
   *
   *     await api.updateDarkWebAlert(alert.id, {
   *       status: 'dismissed',
   *       resolution_notes: reason
   *     });
   *     onUpdate();
   *   };
   *
   *   if (alert.status === 'new') {
   *     return (
   *       <div>
   *         <Button onClick={handleInvestigate}>Investigate</Button>
   *         <Button variant="ghost" onClick={handleDismiss}>Dismiss</Button>
   *       </div>
   *     );
   *   }
   *
   *   return null;
   * }
   *
   * @see {@link getDarkWebAlerts} for retrieving dark web alerts
   * @see {@link getRiskAssessment} for full vendor risk assessment
   * @see {@link getVendor} for vendor details
   * @see {@link getAuditLogs} for tracking alert status changes
   */
  async updateDarkWebAlert(id: string, data: { status?: string; resolution_notes?: string }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tprm/dark-web-alerts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Scans all organization and vendor domains for info stealer exposure.
   * Uses Hudson Rock API to detect compromised credentials from info stealer malware.
   *
   * Domains scanned:
   * - Organization primary website and additional domains from Settings
   * - All active vendor websites and additional domains
   *
   * @param options.force - If true, deletes existing alerts and rescans fresh
   * @returns Scan summary with domains scanned, exposures found, and alerts created
   */
  async scanDarkWebAlerts(options?: { force?: boolean }) {
    return this.request<{
      success: boolean
      message: string
      summary: {
        domainsScanned: number
        orgDomains: number
        vendorDomains: number
        alertsCreated: number
        alertsUpdated: number
        alertsDeleted: number
        forceMode: boolean
        exposuresFound: number
        scanResults: Array<{
          domain: string
          source: 'organization' | 'vendor'
          vendorName?: string
          success: boolean
          findings: number
          data: any
        }>
      }
    }>('/api/tprm/dark-web-alerts/scan', {
      method: 'POST',
      body: JSON.stringify({ force: options?.force || false }),
    })
  }

  // ============================================================================
  // Dark Web Module endpoints (standalone module, not TPRM)
  // ============================================================================

  /**
   * Get dark web monitoring dashboard data.
   * Returns aggregated stats, timeline, recent alerts, and exposure breakdown.
   */
  async getDarkWebDashboard(params?: { timeframe?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.timeframe && params.timeframe !== 'all') {
      searchParams.set('timeframe', params.timeframe)
    }
    const queryString = searchParams.toString()
    return this.request<{
      success: boolean
      dashboard: {
        stats: {
          totalAlerts: number
          newAlerts: number
          investigating: number
          resolved: number
          critical: number
          high: number
          medium: number
          low: number
          totalCredentials: number
        }
        breakdown: {
          hudsonRockCredentials: number
          whiteIntelCredentials?: number
          infoStealerTotal?: number
          hibpBreachedEmails: number
          hibpStealerLogs: number
          uniqueBreaches: number
          stealerFamilies: number
        }
        aggregates: {
          topStealerFamilies: Array<{ name: string; count: number }>
          topBreaches: string[]
          dataClassesExposed: Array<{
            dataClass: string
            breaches: Array<{ name: string; date: string }>
          }>
        }
        timeline: Array<{
          date: string
          credentials_exposed: number
          event_names: string
          event_types: string
          data_classes: string
        }>
        recentAlerts: any[]
        exposureBySource: Array<{
          source_name: string
          alert_count: number
          total_credentials: number
        }>
        // #401: last manual or scheduled scan; sourceErrors maps domain -> source -> error
        lastScan?: {
          finishedAt: number
          trigger: 'manual' | 'scheduled'
          domainsScanned: number
          sourceErrors: Record<string, Record<string, string>>
          credentialSync: { inserted: number; updated: number; reopened: number; unchanged: number }
        } | null
        threatFeedAvailable?: boolean
      }
    }>(`/api/dark-web/dashboard${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Get dark web alerts with filtering for the standalone module.
   */
  async getDarkWebModuleAlerts(params?: {
    vendor_id?: string
    status?: string
    severity?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.vendor_id) searchParams.set('vendor_id', params.vendor_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.severity) searchParams.set('severity', params.severity)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      alerts: any[]
      counts: {
        total: number
        new_alerts: number
        investigating: number
        critical: number
        high: number
      }
      pagination: { limit: number; offset: number }
    }>(`/api/dark-web/alerts${query ? `?${query}` : ''}`)
  }

  /**
   * Get a single dark web alert with full detail.
   */
  async getDarkWebAlert(id: string) {
    return this.request<{
      success: boolean
      alert: any
    }>(`/api/dark-web/alerts/${id}`)
  }

  /**
   * Update a dark web alert status.
   */
  async updateDarkWebModuleAlert(id: string, data: { status?: string; resolution_notes?: string }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/dark-web/alerts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Trigger a dark web scan for all monitored domains.
   */
  async runDarkWebScan(options?: { force?: boolean }) {
    return this.request<{
      success: boolean
      message: string
      summary: {
        domainsScanned: number
        orgDomains: number
        vendorDomains: number
        alertsCreated: number
        alertsUpdated: number
        alertsDeleted: number
        forceMode: boolean
        hibpEnabled: boolean
        hibpVerifiedDomains: number
        hibpMatchingDomains: number
        exposuresFound: number
        scanResults: any[]
      }
    }>('/api/dark-web/scan', {
      method: 'POST',
      body: JSON.stringify({ force: options?.force || false }),
    })
  }

  /**
   * Get list of monitored domains.
   */
  async getDarkWebDomains() {
    return this.request<{
      success: boolean
      domains: Array<{
        domain: string
        source: 'organization' | 'vendor'
        vendorId?: string
        vendorName?: string
      }>
      total: number
      orgDomains: number
      vendorDomains: number
    }>('/api/dark-web/domains')
  }

  // ============================================================================
  // Dark Web Credential-Level Tracking (Issue #317)
  // ============================================================================

  /**
   * Get credential statistics with delta counts.
   * Returns total credentials tracked, new counts by timeframe, and latest scan delta.
   */
  async getDarkWebCredentialStats() {
    return this.request<{
      success: boolean
      stats: {
        total: number
        new_count: number
        new_7d: number
        new_30d: number
        new_90d: number
        resolved: number
        passwords_exposed: number
        // #401: distinct people with open (new/reviewed) exposures
        exposed_identities?: number
        exposed_employees?: number
        exposed_customers?: number
        exposed_with_password?: number
        by_source: {
          hibp: number
          hudsonrock: number
          psbdmp: number
          github: number
          telegram: number
        }
      }
      latestScan: {
        new_credential_count: number
        returning_credential_count: number
        total_credential_count: number
        scanned_at: number
      } | null
    }>('/api/dark-web/credentials/stats')
  }

  /**
   * Get list of individual credentials with filtering.
   * Enables delta view: see NEW vs previously-seen exposures.
   */
  async getDarkWebCredentials(params?: {
    status?: 'new' | 'reviewed' | 'resolved' | 'false_positive'
    source?: 'hibp' | 'hudsonrock' | 'psbdmp' | 'github' | 'telegram'
    timeframe?: '7' | '30' | '90' | 'all'
    search?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.source) searchParams.set('source', params.source)
    if (params?.timeframe) searchParams.set('timeframe', params.timeframe)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      credentials: Array<{
        id: string
        fingerprint: string
        email: string
        breach_name: string | null
        breach_date: number | null
        source: string
        source_type: string | null
        data_types: string[]
        has_password: boolean
        has_personal_info: boolean
        first_seen_at: number
        last_seen_at: number
        seen_count: number
        status: string
        notes: string | null
      }>
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/dark-web/credentials${query ? `?${query}` : ''}`)
  }

  /**
   * Resolve a single credential exposure.
   */
  async resolveDarkWebCredential(id: string, notes?: string) {
    return this.request<{ success: boolean; message?: string }>(`/api/dark-web/credentials/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    })
  }

  /**
   * Bulk resolve multiple credentials.
   */
  async bulkResolveDarkWebCredentials(ids: string[]) {
    return this.request<{ success: boolean; resolved: number; message?: string }>('/api/dark-web/credentials/bulk-resolve', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
  }

  /**
   * Update credential status (mark as reviewed, false_positive, etc.)
   */
  async updateDarkWebCredential(id: string, updates: { status?: string; notes?: string }) {
    return this.request<{ success: boolean; message?: string }>(`/api/dark-web/credentials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Set status (and optionally a note) on many credentials at once (#401).
   */
  async bulkUpdateDarkWebCredentials(ids: string[], status: 'new' | 'reviewed' | 'resolved' | 'false_positive', notes?: string) {
    return this.request<{ success: boolean; updated: number; message?: string }>('/api/dark-web/credentials/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ ids, status, notes }),
    })
  }

  /**
   * Set status and/or owner on many credentials (#397). ownerUserId null unassigns.
   */
  async updateDarkWebCredentials(ids: string[], updates: { status?: DarkWebCredentialStatus; ownerUserId?: string | null; notes?: string }) {
    return this.request<{ success: boolean; updated: number; message?: string; error?: string }>('/api/dark-web/credentials/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ ids, ...updates }),
    })
  }

  /**
   * People with dark web exposure, one row per person (#397).
   */
  async getDarkWebPeople(params: {
    category: 'employee' | 'customer'
    status?: string
    source?: string
    sourceType?: string
    search?: string
    owner?: string
    sort?: 'last_seen' | 'latest_leak' | 'email' | 'exposures' | 'status'
    direction?: 'asc' | 'desc'
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') searchParams.set(key, String(value))
    }
    return this.request<{
      success: boolean
      people: DarkWebPerson[]
      total: number
      pagination: { limit: number; offset: number }
      progress: { new: number; reset_requested: number; reviewed: number; resolved: number; false_positive: number; total: number; handled: number }
      error?: string
    }>(`/api/dark-web/people?${searchParams}`)
  }

  /**
   * Every exposure for one person (#397).
   */
  async getDarkWebPersonDetail(email: string) {
    return this.request<{
      success: boolean
      email: string
      exposures: Array<{
        id: string
        category: string
        source: string
        sourceType: string
        leaks: string[]
        dataClasses: string[]
        hasPassword: boolean
        latestLeakAt: number | null
        status: string
        notes: string | null
        ownerUserId: string | null
        taskId: string | null
        firstSeenAt: number
        lastSeenAt: number
        seenCount: number
        resolvedAt: number | null
      }>
      error?: string
    }>(`/api/dark-web/people/detail?email=${encodeURIComponent(email)}`)
  }

  /**
   * Create one remediation task for the selected people (#397).
   */
  async createDarkWebPeopleTask(body: { emails: string[]; category: 'employee' | 'customer'; assignedTo?: string | null; dueDate?: number | null; priority?: string; note?: string }) {
    return this.request<{ success: boolean; taskId: string; linked: number; message?: string; error?: string }>('/api/dark-web/people/tasks', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * People newly exposed per month by leak type, plus open people per leak type (#397).
   */
  async getDarkWebExposureTrend(params: { category?: 'employee' | 'customer'; months?: number } = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.set('category', params.category)
    if (params.months) searchParams.set('months', String(params.months))
    return this.request<{
      success: boolean
      months: Array<{ month: string; breach: number; stealer_log: number; combolist: number }>
      bySourceType: Array<{ source_type: string; people: number; records: number }>
      error?: string
    }>(`/api/dark-web/exposure-trend?${searchParams}`)
  }

  /**
   * Download credential exposures as CSV (#401). Never includes password values.
   */
  async exportDarkWebCredentials(params?: { category?: 'employee' | 'customer'; status?: 'open' | 'new' | 'reviewed' | 'resolved' | 'false_positive' }) {
    const searchParams = new URLSearchParams()
    if (params?.category) searchParams.set('category', params.category)
    if (params?.status) searchParams.set('status', params.status)

    const token = this.token || localStorage.getItem('grc_token')
    const apiBase = getApiBase()
    const response = await fetch(`${apiBase}/api/dark-web/credentials/export?${searchParams}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new Error('Failed to export credentials')
    }

    return response.blob()
  }

  /**
   * Dark web settings type (#303)
   */
  // Type defined inline for API methods

  /**
   * Get dark web module settings.
   */
  async getDarkWebSettings() {
    return this.request<{
      success: boolean
      settings: {
        enableDailyScans: boolean
        emailAlertsEnabled: boolean
        emailRecipients: string[]
        webhookAlertsEnabled: boolean
        notificationChannelIds: string[]
        lastScanAt: number | null
      }
    }>('/api/dark-web/settings')
  }

  /**
   * Update dark web module settings.
   */
  async updateDarkWebSettings(settings: {
    enableDailyScans?: boolean
    emailAlertsEnabled?: boolean
    emailRecipients?: string[]
    webhookAlertsEnabled?: boolean
    notificationChannelIds?: string[]
  }) {
    return this.request<{
      success: boolean
      settings: {
        enableDailyScans: boolean
        emailAlertsEnabled: boolean
        emailRecipients: string[]
        webhookAlertsEnabled: boolean
        notificationChannelIds: string[]
        lastScanAt: number | null
      }
      message?: string
    }>('/api/dark-web/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  /**
   * Get Hudson Rock stealer data with filtering.
   * Returns infected machines (stealers) with their credentials.
   */
  async getDarkWebStealers(params?: {
    type?: 'employee' | 'user' | 'third_party'
    source?: 'stealer' | 'combolist'  // stealer = malware, combolist = breach
    search?: string
    sort?: 'date_uploaded' | 'date_compromised' | 'stealer_family'
    limit?: number
    offset?: number
    hideResolved?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (params?.hideResolved) searchParams.set('hideResolved', 'true')
    if (params?.type) searchParams.set('type', params.type)
    if (params?.source) searchParams.set('source', params.source)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.sort) searchParams.set('sort', params.sort)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      stealers: Array<{
        stealer_id: string
        stealer_family?: string
        hostname?: string
        ip_address?: string
        malware_path?: string
        date_compromised?: string
        date_uploaded: string
        computer_name?: string
        operating_system?: string
        anti_virus?: string
        credentials: Array<{
          url: string
          login: string
          password?: string
          password_strength?: string
          domain?: string
        }>
        category: 'employee' | 'user' | 'third_party'
        source_type?: 'stealer' | 'combolist'  // stealer = malware, combolist = breach
        data_source?: 'hudsonrock' | 'whiteintel'
        isNew: boolean
        credentialCount: number
        hasPassword: boolean
        // #401: remediation status of this record's identity (WhiteIntel records only)
        credential?: { id: string; status: string; notes: string | null } | null
      }>
      total: number
      pagination: { limit: number; offset: number; hasMore: boolean }
      summary: {
        employees: number
        users: number
        thirdParties: number
        totalStealers: number
        totalCredentials: number
        stealerFamilies: string[]
        stealerSourceCount?: number   // From malware
        combolistSourceCount?: number // From breaches
      }
    }>(`/api/dark-web/stealers${query ? `?${query}` : ''}`)
  }

  /**
   * Get Hudson Rock assets discovery data.
   * Returns URLs where credentials were found with occurrence counts.
   */
  async getDarkWebAssets(params?: {
    search?: string
    limit?: number
    offset?: number
    group_by?: 'domain' | 'url'
  }) {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    if (params?.group_by) searchParams.set('group_by', params.group_by)

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      groupBy?: 'domain' | 'url'
      domains: Array<{
        domain: string
        urlCount: number
        userCount: number
        types: string[]
        sourceTypes: string[]
        latestDate: string
        urls: Array<{ url: string; userCount: number; users: string[] }>
      }>
      assets: Array<{
        url: string
        domain: string
        userCount: number
        users: string[]
        types: string[]
        sourceTypes: string[]
        latestDate: string
      }>
      total: number
      pagination: { limit: number; offset: number; hasMore: boolean }
    }>(`/api/dark-web/assets${query ? `?${query}` : ''}`)
  }

  /**
   * Get HIBP breach data with data types, severity flags, and affected emails.
   */
  async getDarkWebBreaches(params?: {
    search?: string
    severity?: 'critical' | 'high' | 'medium' | 'low'
    hasPasswords?: boolean
    sort?: 'added_date' | 'breach_date' | 'pwn_count' | 'name' | 'severity'
    limit?: number
    offset?: number
    hideResolved?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (params?.hideResolved) searchParams.set('hideResolved', 'true')
    if (params?.search) searchParams.set('search', params.search)
    if (params?.severity) searchParams.set('severity', params.severity)
    if (params?.hasPasswords) searchParams.set('hasPasswords', 'true')
    if (params?.sort) searchParams.set('sort', params.sort)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      breaches: Array<{
        name: string
        title: string
        domain: string
        breachDate: string
        addedDate: string
        modifiedDate?: string
        pwnCount: number
        description: string
        logoPath?: string
        dataClasses: string[]
        criticalTypes: string[]
        highRiskTypes: string[]
        hasPassword: boolean
        severity: 'critical' | 'high' | 'medium' | 'low'
        affectedEmails: string[]
        affectedEmailCount: number
        // #401: email -> remediation status of that employee's breach exposure
        affectedEmailStatus?: Record<string, { id: string; status: string; notes: string | null } | null>
        isNew: boolean
        isVerified: boolean
        isSensitive: boolean
        isMalware: boolean
      }>
      total: number
      pagination: { limit: number; offset: number; hasMore: boolean }
      summary: {
        totalBreaches: number
        criticalBreaches: number
        highBreaches: number
        breachesWithPasswords: number
        newBreaches: number
        totalAffectedEmails: number
        totalStealerLogEmails: number
        uniqueDataClasses: string[]
      }
    }>(`/api/dark-web/breaches${query ? `?${query}` : ''}`)
  }

  /**
   * Get HIBP stealer log data - emails found in stealer malware logs.
   */
  async getDarkWebStealerLogs(params?: {
    search?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      stealerLogs: Array<{
        email: string
        websites: string[]
        websiteCount: number
        isCritical: boolean
      }>
      total: number
      pagination: { limit: number; offset: number; hasMore: boolean }
      summary: {
        totalEmails: number
        criticalCount: number
        uniqueWebsites: string[]
      }
    }>(`/api/dark-web/stealer-logs${query ? `?${query}` : ''}`)
  }

  /**
   * Get dark web threat feed (dark web chatter monitoring)
   * Searches for company/domain mentions on dark web forums, Telegram, etc.
   */
  async getDarkWebThreatFeed(params?: {
    search?: string
    category?: string
    industry?: string
    network?: string
    start_date?: string
    end_date?: string
    page?: number
    limit?: number
  }) {
    const query = params ? new URLSearchParams(
      Object.entries(params)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString() : ''

    return this.request<{
      success: boolean
      items: Array<{
        id: string
        title: string
        category: string
        network: string
        published_at: string
        victim_domain?: string
        victim_industry?: string
        source?: string
        summary?: string
        link?: string
      }>
      total: number
      pagination: { page: number; limit: number }
      remainingCalls?: number
      // #401: set when the WhiteIntel subscription lacks the Threat Feed add-on
      unavailable?: 'addon_required'
      message?: string
    }>(`/api/dark-web/threat-feed${query ? `?${query}` : ''}`)
  }

  /**
   * Get threat feed taxonomy (categories, industries, networks for filtering)
   */
  async getDarkWebThreatFeedTaxonomy() {
    return this.request<{
      success: boolean
      taxonomy: {
        categories: string[]
        industries: string[]
        networks: string[]
      }
    }>('/api/dark-web/threat-feed/taxonomy')
  }

  /**
   * Get lookalike domains (brand protection / phishing detection)
   */
  async getDarkWebLookalikeDomains(params?: {
    domain?: string
    page?: number
    limit?: number
  }) {
    const query = params ? new URLSearchParams(
      Object.entries(params)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString() : ''

    return this.request<{
      success: boolean
      // Mapped by the worker from WhiteIntel's detected_domain* fields (#397: type matched to the API)
      domains: Array<{
        domain: string
        source_domain?: string
        ip_addresses: string[]
        nameservers: string[]
        discovered_at: string
        registration_date?: string
        risk_level: 'high' | 'medium' | 'low' | string
        whois?: any
      }>
      total: number
      pagination: { page: number; limit: number }
      summary: {
        totalDetected: number
        highRisk: number
        mediumRisk: number
        lowRisk: number
        recentlyRegistered: number
        domainsMonitored?: number
      }
      monitoredDomains?: string[]
      cached?: boolean
      remainingCalls?: number
    }>(`/api/dark-web/lookalike-domains${query ? `?${query}` : ''}`)
  }

  // ============================================================================
  // Control Mappings endpoints
  // ============================================================================

  /**
   * Retrieves cross-framework control mappings with filtering and pagination.
   *
   * Control mappings define relationships between controls from different compliance
   * frameworks (e.g., SOC 2 CC6.1 → ISO 27001 A.9.1.1). This enables organizations
   * to satisfy multiple framework requirements with a single control implementation,
   * reducing compliance overhead.
   *
   * **Mapping Sources:**
   * - AI-generated suggestions (via analyzeMappings) with confidence scores
   * - Manual mappings created by compliance officers
   * - Imported from standard mapping libraries
   *
   * **Mapping Statuses:**
   * - `pending_review` - AI-suggested mapping awaiting human review
   * - `approved` - Confirmed mapping, active for evidence linking
   * - `rejected` - Mapping determined to be incorrect or inapplicable
   *
   * **Mapping Types:**
   * - `equivalent` - Controls have identical or nearly identical requirements
   * - `partial` - Source control partially satisfies target control
   * - `related` - Controls address similar concerns but aren't equivalent
   *
   * @param params - Optional filtering and pagination parameters
   * @param params.status - Filter by mapping status: 'pending_review' | 'approved' | 'rejected'
   * @param params.control_id - Filter to mappings involving a specific control
   * @param params.framework_id - Filter to mappings involving controls from a specific framework
   * @param params.min_confidence - Filter AI suggestions by minimum confidence score (0.0-1.0)
   * @param params.limit - Maximum number of results to return (default: 50)
   * @param params.offset - Number of results to skip for pagination
   *
   * @returns Promise resolving to paginated mapping results
   * @returns response.success - Whether the request succeeded
   * @returns response.mappings - Array of control mapping objects
   * @returns response.mappings[].id - Unique mapping identifier
   * @returns response.mappings[].source_control_id - ID of the source control
   * @returns response.mappings[].target_control_id - ID of the target control
   * @returns response.mappings[].mapping_type - Relationship type (equivalent, partial, related)
   * @returns response.mappings[].status - Current mapping status
   * @returns response.mappings[].confidence - AI confidence score (0.0-1.0) if AI-generated
   * @returns response.mappings[].notes - Optional explanation or justification
   * @returns response.pagination - Pagination metadata
   * @returns response.pagination.total - Total mappings matching filters
   * @returns response.pagination.limit - Page size used
   * @returns response.pagination.offset - Current offset
   * @returns response.pagination.hasMore - Whether more results exist
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If tenant context is missing (400)
   *
   * @example
   * // Load all approved mappings
   * const { mappings, pagination } = await api.getMappings({ status: 'approved' });
   * console.log(`${pagination.total} approved cross-framework mappings`);
   *
   * @example
   * // Get mappings pending review with high confidence
   * const { mappings } = await api.getMappings({
   *   status: 'pending_review',
   *   min_confidence: 0.8
   * });
   * mappings.forEach(m => {
   *   console.log(`${m.source_control_id} → ${m.target_control_id} (${m.confidence})`);
   * });
   *
   * @example
   * // Find all mappings for a specific control
   * const { mappings } = await api.getMappings({ control_id: 'ctrl_123' });
   * const inbound = mappings.filter(m => m.target_control_id === 'ctrl_123');
   * const outbound = mappings.filter(m => m.source_control_id === 'ctrl_123');
   *
   * @example
   * // Paginated mappings browser component
   * function MappingsBrowser() {
   *   const [mappings, setMappings] = useState([]);
   *   const [pagination, setPagination] = useState({ offset: 0, limit: 20 });
   *   const [filter, setFilter] = useState({ status: 'approved' });
   *
   *   useEffect(() => {
   *     api.getMappings({ ...filter, ...pagination })
   *       .then(({ mappings, pagination }) => {
   *         setMappings(mappings);
   *         setPagination(pagination);
   *       });
   *   }, [filter, pagination.offset]);
   *
   *   return (
   *     <div>
   *       <StatusFilter value={filter.status} onChange={s => setFilter({ ...filter, status: s })} />
   *       <MappingsTable mappings={mappings} />
   *       <Pagination
   *         total={pagination.total}
   *         offset={pagination.offset}
   *         limit={pagination.limit}
   *         onChange={offset => setPagination({ ...pagination, offset })}
   *       />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Mapping review dashboard with filtering
   * function MappingReviewDashboard() {
   *   const [pendingMappings, setPendingMappings] = useState([]);
   *   const [minConfidence, setMinConfidence] = useState(0.7);
   *
   *   useEffect(() => {
   *     api.getMappings({
   *       status: 'pending_review',
   *       min_confidence: minConfidence
   *     }).then(({ mappings }) => setPendingMappings(mappings));
   *   }, [minConfidence]);
   *
   *   return (
   *     <div>
   *       <h2>AI Mapping Suggestions</h2>
   *       <ConfidenceSlider value={minConfidence} onChange={setMinConfidence} />
   *       <p>{pendingMappings.length} mappings above {minConfidence * 100}% confidence</p>
   *       {pendingMappings.map(m => (
   *         <MappingReviewCard key={m.id} mapping={m} />
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @see {@link getMapping} for retrieving a single mapping by ID
   * @see {@link analyzeMappings} for triggering AI analysis to generate suggestions
   * @see {@link createMapping} for creating manual mappings
   * @see {@link updateMapping} for approving/rejecting mappings
   * @see {@link getMappingStats} for aggregate mapping statistics
   */
  async getMappings(params?: {
    status?: string
    control_id?: string
    framework_id?: string
    min_confidence?: number
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.control_id) searchParams.set('control_id', params.control_id)
    if (params?.framework_id) searchParams.set('framework_id', params.framework_id)
    if (params?.min_confidence) searchParams.set('min_confidence', String(params.min_confidence))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      mappings: any[]
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/mappings${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves detailed information about a single cross-framework control mapping.
   *
   * This method fetches the complete mapping record including both source and target
   * control details, confidence scoring, review history, and any associated notes.
   * Use this when viewing mapping details or preparing to update/review a mapping.
   *
   * **Mapping Details Include:**
   * - Source and target control information (IDs, titles, frameworks)
   * - Mapping type classification (equivalent, partial, related)
   * - AI confidence score (if AI-generated)
   * - Current status and review history
   * - Notes and justification
   *
   * @param id - The unique identifier of the mapping to retrieve
   *
   * @returns Promise resolving to the mapping details
   * @returns response.success - Whether the request succeeded
   * @returns response.mapping - The complete mapping object
   * @returns response.mapping.id - Unique mapping identifier
   * @returns response.mapping.source_control_id - ID of the source control
   * @returns response.mapping.source_control - Populated source control details
   * @returns response.mapping.target_control_id - ID of the target control
   * @returns response.mapping.target_control - Populated target control details
   * @returns response.mapping.mapping_type - Relationship type (equivalent, partial, related)
   * @returns response.mapping.status - Current status (pending_review, approved, rejected)
   * @returns response.mapping.confidence - AI confidence score (0.0-1.0) if applicable
   * @returns response.mapping.notes - Mapping notes or justification
   * @returns response.mapping.reviewed_by - User who last reviewed (if reviewed)
   * @returns response.mapping.reviewed_at - Timestamp of last review (if reviewed)
   * @returns response.mapping.created_at - When the mapping was created
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If mapping not found (404)
   *
   * @example
   * // Load mapping details for display
   * const { mapping } = await api.getMapping('mapping_123');
   * console.log(`${mapping.source_control.title} → ${mapping.target_control.title}`);
   * console.log(`Type: ${mapping.mapping_type}, Confidence: ${mapping.confidence}`);
   *
   * @example
   * // Mapping detail view component
   * function MappingDetailView({ mappingId }: { mappingId: string }) {
   *   const [mapping, setMapping] = useState(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getMapping(mappingId)
   *       .then(({ mapping }) => setMapping(mapping))
   *       .finally(() => setLoading(false));
   *   }, [mappingId]);
   *
   *   if (loading) return <Spinner />;
   *   if (!mapping) return <NotFound />;
   *
   *   return (
   *     <div className="space-y-4">
   *       <div className="flex items-center gap-4">
   *         <ControlCard control={mapping.source_control} />
   *         <ArrowRight />
   *         <ControlCard control={mapping.target_control} />
   *       </div>
   *       <Badge>{mapping.mapping_type}</Badge>
   *       <Badge variant={mapping.status === 'approved' ? 'success' : 'warning'}>
   *         {mapping.status}
   *       </Badge>
   *       {mapping.confidence && (
   *         <span>AI Confidence: {(mapping.confidence * 100).toFixed(0)}%</span>
   *       )}
   *       {mapping.notes && <p>{mapping.notes}</p>}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Load mapping for review modal
   * async function openMappingReview(mappingId: string) {
   *   const { mapping } = await api.getMapping(mappingId);
   *
   *   showModal({
   *     title: 'Review Mapping',
   *     content: <MappingReviewForm mapping={mapping} />,
   *     onApprove: () => api.updateMapping(mappingId, { status: 'approved' }),
   *     onReject: () => api.updateMapping(mappingId, { status: 'rejected' })
   *   });
   * }
   *
   * @see {@link getMappings} for listing mappings with filters
   * @see {@link updateMapping} for approving/rejecting mappings
   * @see {@link deleteMapping} for removing mappings
   * @see {@link getControlMappings} for mappings related to a specific control
   */
  async getMapping(id: string) {
    return this.request<{
      success: boolean
      mapping: any
    }>(`/api/mappings/${id}`)
  }

  /**
   * Triggers AI-powered analysis to discover cross-framework control mappings.
   *
   * This method uses AI to analyze control descriptions and identify potential mappings
   * between controls from different frameworks. The AI compares control objectives,
   * implementation requirements, and terminology to suggest mappings with confidence scores.
   *
   * **How It Works:**
   * 1. AI analyzes source control descriptions and requirements
   * 2. Compares against target framework controls
   * 3. Identifies semantic similarities and relationships
   * 4. Generates mapping suggestions with confidence scores
   * 5. Suggestions are stored with `pending_review` status for human review
   *
   * **Analysis Scope:**
   * - If `source_framework_id` + `target_framework_id` provided: Analyzes between two frameworks
   * - If `control_ids` provided: Only analyzes specific controls
   * - Default: Analyzes all enabled frameworks for cross-mappings
   *
   * **Performance Notes:**
   * - Analysis is asynchronous and may take 10-60 seconds depending on scope
   * - Use `min_confidence` to filter out low-quality suggestions at generation time
   * - Consider running during off-peak hours for large-scale analysis
   *
   * @param params - Analysis parameters
   * @param params.source_framework_id - Source framework to map from (e.g., 'soc2')
   * @param params.target_framework_id - Target framework to map to (e.g., 'iso27001')
   * @param params.control_ids - Specific control IDs to analyze (optional subset)
   * @param params.min_confidence - Minimum confidence threshold (0.0-1.0, default: 0.6)
   *
   * @returns Promise resolving to analysis results
   * @returns response.success - Whether the analysis completed successfully
   * @returns response.message - Human-readable result summary
   * @returns response.suggestions_count - Number of new mapping suggestions generated
   * @returns response.analyzed_count - Number of controls analyzed
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If framework not found (404)
   * @throws {Error} If invalid parameters provided (400)
   * @throws {Error} If analysis times out (504)
   *
   * @example
   * // Analyze SOC 2 to ISO 27001 mappings
   * const result = await api.analyzeMappings({
   *   source_framework_id: 'soc2',
   *   target_framework_id: 'iso27001',
   *   min_confidence: 0.7
   * });
   * console.log(`Generated ${result.suggestions_count} mapping suggestions`);
   *
   * @example
   * // Analyze specific controls with high confidence threshold
   * const result = await api.analyzeMappings({
   *   control_ids: ['ctrl_123', 'ctrl_456', 'ctrl_789'],
   *   min_confidence: 0.85
   * });
   *
   * @example
   * // Framework mapping analyzer component
   * function FrameworkMappingAnalyzer() {
   *   const [source, setSource] = useState('');
   *   const [target, setTarget] = useState('');
   *   const [analyzing, setAnalyzing] = useState(false);
   *   const [result, setResult] = useState(null);
   *
   *   const handleAnalyze = async () => {
   *     setAnalyzing(true);
   *     try {
   *       const result = await api.analyzeMappings({
   *         source_framework_id: source,
   *         target_framework_id: target,
   *         min_confidence: 0.7
   *       });
   *       setResult(result);
   *       toast.success(`Found ${result.suggestions_count} potential mappings`);
   *     } catch (err) {
   *       toast.error('Analysis failed');
   *     } finally {
   *       setAnalyzing(false);
   *     }
   *   };
   *
   *   return (
   *     <div className="space-y-4">
   *       <FrameworkSelect label="Source" value={source} onChange={setSource} />
   *       <FrameworkSelect label="Target" value={target} onChange={setTarget} />
   *       <Button onClick={handleAnalyze} disabled={analyzing || !source || !target}>
   *         {analyzing ? 'Analyzing...' : 'Discover Mappings'}
   *       </Button>
   *       {result && (
   *         <Alert>
   *           Analyzed {result.analyzed_count} controls,
   *           found {result.suggestions_count} mapping suggestions.
   *           <Link to="/mappings?status=pending_review">Review now</Link>
   *         </Alert>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Batch analysis of all enabled frameworks
   * async function runFullMappingAnalysis() {
   *   const { frameworks } = await api.getFrameworks();
   *   const enabled = frameworks.filter(f => f.enabled);
   *
   *   let totalSuggestions = 0;
   *   for (let i = 0; i < enabled.length; i++) {
   *     for (let j = i + 1; j < enabled.length; j++) {
   *       const result = await api.analyzeMappings({
   *         source_framework_id: enabled[i].id,
   *         target_framework_id: enabled[j].id
   *       });
   *       totalSuggestions += result.suggestions_count;
   *     }
   *   }
   *
   *   return totalSuggestions;
   * }
   *
   * @see {@link getMappings} for retrieving generated suggestions
   * @see {@link updateMapping} for approving/rejecting suggestions
   * @see {@link getFrameworks} for available frameworks
   * @see {@link getMappingStats} for mapping coverage statistics
   */
  async analyzeMappings(params: {
    source_framework_id?: string
    target_framework_id?: string
    control_ids?: string[]
    min_confidence?: number
  }) {
    return this.request<{
      success: boolean
      message: string
      suggestions_count: number
      analyzed_count: number
    }>('/api/mappings/analyze', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Creates a new manual cross-framework control mapping.
   *
   * Use this method to manually define relationships between controls from different
   * compliance frameworks. Manual mappings are immediately set to 'approved' status
   * (unlike AI-generated suggestions which start as 'pending_review').
   *
   * **Common Use Cases:**
   * - Creating mappings the AI didn't detect
   * - Linking controls based on organizational context
   * - Importing mappings from external mapping libraries
   * - Documenting custom framework-to-framework relationships
   *
   * **Mapping Types:**
   * - `equivalent` - Controls have identical or nearly identical requirements
   *   (e.g., both require annual penetration testing)
   * - `partial` - Source control partially satisfies target control requirements
   *   (e.g., covers encryption but not key management)
   * - `related` - Controls address similar concerns but aren't direct equivalents
   *   (e.g., both address access control from different angles)
   *
   * @param data - Mapping creation payload
   * @param data.source_control_id - ID of the source control in the mapping
   * @param data.target_control_id - ID of the target control in the mapping
   * @param data.mapping_type - Relationship type: 'equivalent' | 'partial' | 'related'
   *   (default: 'equivalent')
   * @param data.notes - Optional justification or notes explaining the mapping relationship
   *
   * @returns Promise resolving to the created mapping
   * @returns response.success - Whether the mapping was created successfully
   * @returns response.mapping - The created mapping object
   * @returns response.mapping.id - Unique identifier of the new mapping
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If source or target control not found (404)
   * @throws {Error} If mapping already exists between these controls (409)
   * @throws {Error} If controls are from the same framework (400)
   *
   * @example
   * // Create a simple equivalent mapping
   * const { mapping } = await api.createMapping({
   *   source_control_id: 'soc2_cc6_1',
   *   target_control_id: 'iso27001_a9_1_1'
   * });
   *
   * @example
   * // Create a partial mapping with notes
   * const { mapping } = await api.createMapping({
   *   source_control_id: 'ctrl_123',
   *   target_control_id: 'ctrl_456',
   *   mapping_type: 'partial',
   *   notes: 'Source control covers encryption requirements but not key rotation. ' +
   *     'Additional evidence needed for key management aspects.'
   * });
   *
   * @example
   * // Manual mapping creation form
   * function CreateMappingForm({ sourceControl }: { sourceControl: Control }) {
   *   const [targetId, setTargetId] = useState('');
   *   const [mappingType, setMappingType] = useState('equivalent');
   *   const [notes, setNotes] = useState('');
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleSubmit = async (e: FormEvent) => {
   *     e.preventDefault();
   *     setSaving(true);
   *
   *     try {
   *       const { mapping } = await api.createMapping({
   *         source_control_id: sourceControl.id,
   *         target_control_id: targetId,
   *         mapping_type: mappingType,
   *         notes
   *       });
   *
   *       toast.success('Mapping created');
   *       navigate(`/mappings/${mapping.id}`);
   *     } catch (err: any) {
   *       if (err.status === 409) {
   *         toast.error('Mapping already exists between these controls');
   *       } else {
   *         toast.error('Failed to create mapping');
   *       }
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <ControlSelector
   *         excludeFramework={sourceControl.framework_id}
   *         value={targetId}
   *         onChange={setTargetId}
   *       />
   *       <select value={mappingType} onChange={e => setMappingType(e.target.value)}>
   *         <option value="equivalent">Equivalent</option>
   *         <option value="partial">Partial</option>
   *         <option value="related">Related</option>
   *       </select>
   *       <textarea
   *         value={notes}
   *         onChange={e => setNotes(e.target.value)}
   *         placeholder="Explain the relationship..."
   *       />
   *       <Button type="submit" disabled={saving || !targetId}>
   *         {saving ? 'Creating...' : 'Create Mapping'}
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Bulk import mappings from external source
   * async function importMappings(mappings: ExternalMapping[]) {
   *   const results = { success: 0, failed: 0 };
   *
   *   for (const m of mappings) {
   *     try {
   *       await api.createMapping({
   *         source_control_id: m.sourceId,
   *         target_control_id: m.targetId,
   *         mapping_type: m.type,
   *         notes: `Imported from ${m.source}`
   *       });
   *       results.success++;
   *     } catch {
   *       results.failed++;
   *     }
   *   }
   *
   *   return results;
   * }
   *
   * @see {@link getMappings} for listing existing mappings
   * @see {@link analyzeMappings} for AI-generated mapping suggestions
   * @see {@link updateMapping} for modifying mapping properties
   * @see {@link deleteMapping} for removing mappings
   */
  async createMapping(data: {
    source_control_id: string
    target_control_id: string
    mapping_type?: string
    notes?: string
  }) {
    return this.request<{
      success: boolean
      mapping: { id: string }
    }>('/api/mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing control mapping's status, type, or review notes.
   *
   * This method is primarily used for the mapping review workflow where compliance
   * officers approve or reject AI-generated mapping suggestions. It can also be used
   * to modify mapping type or add review notes to existing mappings.
   *
   * **Review Workflow:**
   * 1. AI generates mapping suggestions with `pending_review` status
   * 2. Compliance officer reviews mapping via UI
   * 3. Officer approves (activates for evidence linking) or rejects (marks as incorrect)
   * 4. Review notes document the decision rationale for audit trail
   *
   * **Status Transitions:**
   * - `pending_review` → `approved` - Confirm mapping is correct
   * - `pending_review` → `rejected` - Mark mapping as incorrect/inapplicable
   * - `approved` → `rejected` - Revoke previously approved mapping
   * - `rejected` → `approved` - Reinstate previously rejected mapping
   *
   * @param id - The unique identifier of the mapping to update
   * @param data - Update payload
   * @param data.status - New status: 'approved' | 'rejected' | 'pending_review'
   * @param data.mapping_type - Updated relationship type: 'equivalent' | 'partial' | 'related'
   * @param data.review_notes - Notes documenting the review decision or changes
   *
   * @returns Promise resolving to the update result
   * @returns response.success - Whether the update succeeded
   * @returns response.message - Human-readable confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If mapping not found (404)
   * @throws {Error} If invalid status or type value (400)
   *
   * @example
   * // Approve a mapping suggestion
   * await api.updateMapping('mapping_123', {
   *   status: 'approved',
   *   review_notes: 'Verified - both controls require annual pen testing'
   * });
   *
   * @example
   * // Reject a mapping with explanation
   * await api.updateMapping('mapping_456', {
   *   status: 'rejected',
   *   review_notes: 'Controls appear similar but address different risk areas. ' +
   *     'Source is about access control, target is about encryption.'
   * });
   *
   * @example
   * // Change mapping type after review
   * await api.updateMapping('mapping_789', {
   *   mapping_type: 'partial',
   *   review_notes: 'Downgraded from equivalent to partial - source only covers ' +
   *     'half of the target requirements.'
   * });
   *
   * @example
   * // Mapping review component with approve/reject actions
   * function MappingReviewCard({ mapping, onUpdate }: Props) {
   *   const [notes, setNotes] = useState('');
   *   const [processing, setProcessing] = useState(false);
   *
   *   const handleReview = async (status: 'approved' | 'rejected') => {
   *     setProcessing(true);
   *     try {
   *       await api.updateMapping(mapping.id, {
   *         status,
   *         review_notes: notes
   *       });
   *       toast.success(`Mapping ${status}`);
   *       onUpdate();
   *     } catch (err) {
   *       toast.error('Review failed');
   *     } finally {
   *       setProcessing(false);
   *     }
   *   };
   *
   *   return (
   *     <Card>
   *       <MappingVisualization mapping={mapping} />
   *       <Badge>Confidence: {(mapping.confidence * 100).toFixed(0)}%</Badge>
   *
   *       <textarea
   *         value={notes}
   *         onChange={e => setNotes(e.target.value)}
   *         placeholder="Review notes (optional)..."
   *       />
   *
   *       <div className="flex gap-2">
   *         <Button
   *           variant="success"
   *           onClick={() => handleReview('approved')}
   *           disabled={processing}
   *         >
   *           Approve
   *         </Button>
   *         <Button
   *           variant="destructive"
   *           onClick={() => handleReview('rejected')}
   *           disabled={processing}
   *         >
   *           Reject
   *         </Button>
   *       </div>
   *     </Card>
   *   );
   * }
   *
   * @example
   * // Bulk approve high-confidence mappings
   * async function bulkApproveHighConfidence(minConfidence = 0.9) {
   *   const { mappings } = await api.getMappings({
   *     status: 'pending_review',
   *     min_confidence: minConfidence
   *   });
   *
   *   let approved = 0;
   *   for (const mapping of mappings) {
   *     try {
   *       await api.updateMapping(mapping.id, {
   *         status: 'approved',
   *         review_notes: `Bulk approved - confidence ${(mapping.confidence * 100).toFixed(0)}%`
   *       });
   *       approved++;
   *     } catch {
   *       // Continue on individual failures
   *     }
   *   }
   *
   *   return { approved, total: mappings.length };
   * }
   *
   * @see {@link getMapping} for retrieving mapping details before update
   * @see {@link getMappings} for listing pending review mappings
   * @see {@link deleteMapping} for permanently removing mappings
   * @see {@link getAuditLogs} for tracking mapping review history
   */
  async updateMapping(id: string, data: {
    status?: 'approved' | 'rejected' | 'pending_review'
    mapping_type?: string
    review_notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/mappings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a cross-framework control mapping.
   *
   * This method removes a mapping relationship between two controls. The deletion
   * is permanent and cannot be undone. Consider using `updateMapping` to reject
   * a mapping instead if you want to preserve the record for audit purposes.
   *
   * **When to Delete vs Reject:**
   * - **Delete**: Remove erroneous mappings, clean up duplicates, imported incorrectly
   * - **Reject**: AI-suggested mapping that's incorrect - preserves audit trail
   *
   * **Impact of Deletion:**
   * - Evidence linked via this mapping will NOT be automatically unlinked from controls
   * - Mapping will be removed from all statistics and reports
   * - Action is logged in audit trail
   * - Related evidence link suggestions may change
   *
   * @param id - The unique identifier of the mapping to delete
   *
   * @returns Promise resolving to the deletion result
   * @returns response.success - Whether the deletion succeeded
   * @returns response.message - Human-readable confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If mapping not found (404)
   * @throws {Error} If insufficient permissions (403)
   *
   * @example
   * // Delete a mapping
   * await api.deleteMapping('mapping_123');
   *
   * @example
   * // Delete with confirmation
   * async function confirmAndDeleteMapping(mappingId: string) {
   *   const confirmed = await confirm(
   *     'Are you sure you want to permanently delete this mapping? ' +
   *     'Consider rejecting it instead to preserve the audit trail.'
   *   );
   *
   *   if (confirmed) {
   *     await api.deleteMapping(mappingId);
   *     toast.success('Mapping deleted');
   *   }
   * }
   *
   * @example
   * // Mapping management component with delete action
   * function MappingActions({ mapping, onDelete }: Props) {
   *   const [deleting, setDeleting] = useState(false);
   *
   *   const handleDelete = async () => {
   *     if (!confirm('Permanently delete this mapping?')) return;
   *
   *     setDeleting(true);
   *     try {
   *       await api.deleteMapping(mapping.id);
   *       toast.success('Mapping deleted');
   *       onDelete();
   *     } catch (err) {
   *       toast.error('Failed to delete mapping');
   *     } finally {
   *       setDeleting(false);
   *     }
   *   };
   *
   *   return (
   *     <DropdownMenu>
   *       <DropdownMenuItem onClick={() => navigate(`/mappings/${mapping.id}`)}>
   *         View Details
   *       </DropdownMenuItem>
   *       <DropdownMenuItem
   *         onClick={() => api.updateMapping(mapping.id, { status: 'rejected' })}
   *       >
   *         Reject (Keep Record)
   *       </DropdownMenuItem>
   *       <DropdownMenuItem
   *         onClick={handleDelete}
   *         className="text-destructive"
   *         disabled={deleting}
   *       >
   *         {deleting ? 'Deleting...' : 'Delete Permanently'}
   *       </DropdownMenuItem>
   *     </DropdownMenu>
   *   );
   * }
   *
   * @example
   * // Cleanup duplicate mappings
   * async function removeDuplicateMappings() {
   *   const { mappings } = await api.getMappings();
   *
   *   const seen = new Set<string>();
   *   const duplicates: string[] = [];
   *
   *   for (const mapping of mappings) {
   *     const key = `${mapping.source_control_id}:${mapping.target_control_id}`;
   *     if (seen.has(key)) {
   *       duplicates.push(mapping.id);
   *     } else {
   *       seen.add(key);
   *     }
   *   }
   *
   *   for (const id of duplicates) {
   *     await api.deleteMapping(id);
   *   }
   *
   *   return duplicates.length;
   * }
   *
   * @see {@link updateMapping} for rejecting mappings while preserving records
   * @see {@link getMappings} for listing mappings
   * @see {@link getMapping} for viewing mapping details before deletion
   * @see {@link getAuditLogs} for tracking deletion events
   */
  async deleteMapping(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/mappings/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves all cross-framework mappings associated with a specific control.
   *
   * This method returns all mappings where the specified control is either the
   * source or target of the mapping relationship. Use this to understand how a
   * control maps to other controls across different compliance frameworks.
   *
   * **Mapping Directions:**
   * - **Source mappings**: This control maps TO other controls (outgoing)
   * - **Target mappings**: Other controls map TO this control (incoming)
   *
   * **Use Cases:**
   * - Display control equivalencies in control detail views
   * - Calculate cross-framework compliance coverage
   * - Find controls that can satisfy multiple framework requirements
   * - Identify evidence that could apply to mapped controls
   *
   * @param controlId - The unique identifier of the control
   *
   * @returns Promise resolving to the control's mappings
   * @returns response.success - Whether the request succeeded
   * @returns response.control_id - The control ID that was queried
   * @returns response.mappings - Array of mapping objects containing:
   *   - `id` - Unique mapping identifier
   *   - `source_control_id` - The source control in the mapping
   *   - `target_control_id` - The target control in the mapping
   *   - `source_control` - Source control details (id, code, title, framework)
   *   - `target_control` - Target control details (id, code, title, framework)
   *   - `mapping_type` - Relationship type: 'equivalent', 'partial', or 'related'
   *   - `status` - Review status: 'pending_review', 'approved', or 'rejected'
   *   - `confidence` - AI confidence score (0.0-1.0) if AI-generated
   *   - `direction` - Whether this control is 'source' or 'target' in the mapping
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If control not found (404)
   *
   * @example
   * // Get all mappings for a control
   * const { mappings } = await api.getControlMappings('control_123');
   * console.log(`Found ${mappings.length} mappings`);
   *
   * @example
   * // Display mapped controls in a control detail view
   * function MappedControlsList({ controlId }: { controlId: string }) {
   *   const [mappings, setMappings] = useState<any[]>([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getControlMappings(controlId)
   *       .then(res => setMappings(res.mappings))
   *       .finally(() => setLoading(false));
   *   }, [controlId]);
   *
   *   if (loading) return <Spinner />;
   *
   *   const equivalent = mappings.filter(m => m.mapping_type === 'equivalent');
   *   const partial = mappings.filter(m => m.mapping_type === 'partial');
   *
   *   return (
   *     <div>
   *       <h3>Cross-Framework Mappings</h3>
   *
   *       {equivalent.length > 0 && (
   *         <Section title="Equivalent Controls">
   *           {equivalent.map(m => {
   *             const other = m.direction === 'source'
   *               ? m.target_control
   *               : m.source_control;
   *             return (
   *               <ControlCard
   *                 key={m.id}
   *                 control={other}
   *                 badge={<Badge variant="success">Equivalent</Badge>}
   *               />
   *             );
   *           })}
   *         </Section>
   *       )}
   *
   *       {partial.length > 0 && (
   *         <Section title="Partially Mapped">
   *           {partial.map(m => (
   *             <MappingCard key={m.id} mapping={m} />
   *           ))}
   *         </Section>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate cross-framework coverage
   * async function calculateCrossFrameworkCoverage(controlIds: string[]) {
   *   const coverage: Record<string, Set<string>> = {};
   *
   *   for (const controlId of controlIds) {
   *     const { mappings } = await api.getControlMappings(controlId);
   *
   *     for (const mapping of mappings) {
   *       if (mapping.status !== 'approved') continue;
   *
   *       const otherControl = mapping.direction === 'source'
   *         ? mapping.target_control
   *         : mapping.source_control;
   *
   *       if (!coverage[otherControl.framework]) {
   *         coverage[otherControl.framework] = new Set();
   *       }
   *       coverage[otherControl.framework].add(otherControl.id);
   *     }
   *   }
   *
   *   return Object.entries(coverage).map(([framework, controls]) => ({
   *     framework,
   *     controlCount: controls.size
   *   }));
   * }
   *
   * @see {@link getMappings} for listing all mappings with filtering
   * @see {@link getMapping} for detailed information about a specific mapping
   * @see {@link getEvidenceLinkSuggestions} for evidence linking across mappings
   */
  async getControlMappings(controlId: string) {
    return this.request<{
      success: boolean
      control_id: string
      mappings: any[]
    }>(`/api/mappings/controls/${controlId}`)
  }

  /**
   * Retrieves AI-suggested controls that evidence could be linked to based on
   * cross-framework control mappings.
   *
   * When evidence is linked to a control, this method identifies other controls
   * that have approved mappings to that control, suggesting they could also be
   * satisfied by the same evidence. This enables "link once, satisfy many"
   * compliance efficiency across frameworks.
   *
   * **How Suggestions Work:**
   * 1. Evidence is linked to its original/primary control
   * 2. System finds all approved mappings to/from that control
   * 3. Mapped controls are suggested as additional link targets
   * 4. Confidence scores indicate mapping strength
   *
   * **Suggestion Quality:**
   * - Only approved mappings generate suggestions
   * - Equivalent mappings have higher confidence than partial mappings
   * - Already-linked controls are excluded from suggestions
   *
   * @param evidenceId - The unique identifier of the evidence item
   *
   * @returns Promise resolving to evidence link suggestions
   * @returns response.success - Whether the request succeeded
   * @returns response.evidence_id - The evidence ID that was queried
   * @returns response.original_control_id - The primary control this evidence is linked to
   * @returns response.suggestions - Array of suggested controls containing:
   *   - `control_id` - The suggested control's identifier
   *   - `control_code` - Control code (e.g., 'CC1.1', 'A.8.2.1')
   *   - `control_title` - Human-readable control title
   *   - `framework` - The control's compliance framework
   *   - `mapping_id` - The mapping that created this suggestion
   *   - `mapping_type` - Type of mapping: 'equivalent', 'partial', or 'related'
   *   - `confidence` - Suggestion confidence (0.0-1.0), derived from mapping confidence
   *   - `reason` - Human-readable explanation of why this is suggested
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If evidence not found (404)
   * @throws {Error} If evidence not linked to any control (400)
   *
   * @example
   * // Get suggestions for an evidence item
   * const { suggestions, original_control_id } = await api.getEvidenceLinkSuggestions('ev_123');
   * console.log(`Evidence linked to ${original_control_id}, found ${suggestions.length} suggestions`);
   *
   * @example
   * // Evidence cross-linking suggestion panel
   * function EvidenceLinkSuggestions({ evidenceId }: { evidenceId: string }) {
   *   const [suggestions, setSuggestions] = useState<any[]>([]);
   *   const [loading, setLoading] = useState(true);
   *   const [linking, setLinking] = useState<string[]>([]);
   *
   *   useEffect(() => {
   *     api.getEvidenceLinkSuggestions(evidenceId)
   *       .then(res => setSuggestions(res.suggestions))
   *       .catch(() => setSuggestions([]))
   *       .finally(() => setLoading(false));
   *   }, [evidenceId]);
   *
   *   const handleLink = async (suggestion: any) => {
   *     setLinking(prev => [...prev, suggestion.control_id]);
   *     try {
   *       await api.linkEvidenceToControls(
   *         evidenceId,
   *         [suggestion.control_id],
   *         suggestion.mapping_id
   *       );
   *       setSuggestions(prev =>
   *         prev.filter(s => s.control_id !== suggestion.control_id)
   *       );
   *       toast.success(`Linked to ${suggestion.control_code}`);
   *     } catch (err) {
   *       toast.error('Failed to link evidence');
   *     } finally {
   *       setLinking(prev => prev.filter(id => id !== suggestion.control_id));
   *     }
   *   };
   *
   *   const handleLinkAll = async () => {
   *     const controlIds = suggestions.map(s => s.control_id);
   *     await api.linkEvidenceToControls(evidenceId, controlIds);
   *     setSuggestions([]);
   *     toast.success(`Linked to ${controlIds.length} controls`);
   *   };
   *
   *   if (loading) return <Spinner />;
   *   if (suggestions.length === 0) return null;
   *
   *   return (
   *     <Card>
   *       <CardHeader>
   *         <h3>Suggested Control Links</h3>
   *         <p className="text-muted">
   *           Based on cross-framework mappings, this evidence may satisfy additional controls
   *         </p>
   *       </CardHeader>
   *
   *       <CardContent>
   *         {suggestions.map(suggestion => (
   *           <div key={suggestion.control_id} className="flex justify-between items-center">
   *             <div>
   *               <Badge variant={suggestion.mapping_type === 'equivalent' ? 'success' : 'warning'}>
   *                 {suggestion.mapping_type}
   *               </Badge>
   *               <span className="font-mono">{suggestion.control_code}</span>
   *               <span className="text-muted">{suggestion.framework}</span>
   *             </div>
   *             <Button
   *               onClick={() => handleLink(suggestion)}
   *               loading={linking.includes(suggestion.control_id)}
   *               size="sm"
   *             >
   *               Link
   *             </Button>
   *           </div>
   *         ))}
   *       </CardContent>
   *
   *       <CardFooter>
   *         <Button onClick={handleLinkAll} variant="outline">
   *           Link All ({suggestions.length})
   *         </Button>
   *       </CardFooter>
   *     </Card>
   *   );
   * }
   *
   * @example
   * // Filter high-confidence suggestions
   * async function getHighConfidenceSuggestions(evidenceId: string) {
   *   const { suggestions } = await api.getEvidenceLinkSuggestions(evidenceId);
   *
   *   return suggestions
   *     .filter(s => s.confidence >= 0.8 && s.mapping_type === 'equivalent')
   *     .sort((a, b) => b.confidence - a.confidence);
   * }
   *
   * @see {@link linkEvidenceToControls} for linking evidence to suggested controls
   * @see {@link getControlMappings} for understanding control relationships
   * @see {@link getMappings} for managing cross-framework mappings
   */
  async getEvidenceLinkSuggestions(evidenceId: string) {
    return this.request<{
      success: boolean
      evidence_id: string
      original_control_id: string
      suggestions: any[]
    }>(`/api/mappings/evidence/${evidenceId}/suggestions`)
  }

  /**
   * Links evidence to one or more controls, optionally through a mapping relationship.
   *
   * This method creates associations between evidence and controls. When linked via
   * a mapping, the relationship is tracked to show how evidence satisfies multiple
   * framework requirements through control equivalencies.
   *
   * **Linking Modes:**
   * - **Direct link**: Evidence is linked directly to controls without a mapping context
   * - **Mapping-based link**: Evidence is linked through a specific mapping, enabling
   *   cross-framework compliance tracking
   *
   * **Link Behavior:**
   * - Duplicate links are ignored (idempotent operation)
   * - Already-linked controls are skipped in the count
   * - Links are audited for compliance tracking
   *
   * **Use Cases:**
   * - Link uploaded evidence to its primary control
   * - Apply evidence across mapped controls (cross-framework)
   * - Bulk-link evidence to multiple controls from suggestions
   *
   * @param evidenceId - The unique identifier of the evidence to link
   * @param controlIds - Array of control IDs to link the evidence to
   * @param mappingId - Optional mapping ID that justifies cross-framework linking
   *
   * @returns Promise resolving to the link result
   * @returns response.success - Whether the operation succeeded
   * @returns response.message - Human-readable result message
   * @returns response.linked_count - Number of new links created (excludes duplicates)
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If evidence not found (404)
   * @throws {Error} If any control not found (404)
   * @throws {Error} If mapping not found when mappingId provided (404)
   * @throws {Error} If insufficient permissions (403)
   *
   * @example
   * // Link evidence to a single control
   * await api.linkEvidenceToControls('ev_123', ['control_456']);
   *
   * @example
   * // Link evidence to multiple controls
   * const { linked_count } = await api.linkEvidenceToControls(
   *   'ev_123',
   *   ['control_1', 'control_2', 'control_3']
   * );
   * console.log(`Created ${linked_count} new links`);
   *
   * @example
   * // Link through a mapping for cross-framework tracking
   * await api.linkEvidenceToControls(
   *   'ev_123',
   *   ['control_789'],
   *   'mapping_456' // The mapping that shows control equivalency
   * );
   *
   * @example
   * // Link all suggested controls at once
   * async function linkAllSuggestions(evidenceId: string) {
   *   const { suggestions } = await api.getEvidenceLinkSuggestions(evidenceId);
   *   const controlIds = suggestions.map(s => s.control_id);
   *
   *   if (controlIds.length === 0) {
   *     return { linked: 0 };
   *   }
   *
   *   const { linked_count } = await api.linkEvidenceToControls(evidenceId, controlIds);
   *   return { linked: linked_count };
   * }
   *
   * @example
   * // Evidence upload with multi-control linking
   * async function uploadAndLink(
   *   file: File,
   *   primaryControlId: string,
   *   additionalControlIds: string[]
   * ) {
   *   // Upload the evidence
   *   const formData = new FormData();
   *   formData.append('file', file);
   *   formData.append('control_id', primaryControlId);
   *
   *   const { evidence } = await api.uploadEvidence(formData);
   *
   *   // Link to additional controls if any
   *   if (additionalControlIds.length > 0) {
   *     await api.linkEvidenceToControls(evidence.id, additionalControlIds);
   *   }
   *
   *   return evidence;
   * }
   *
   * @example
   * // React component for linking evidence
   * function LinkEvidenceButton({ evidenceId, controlId, mappingId }: Props) {
   *   const [linking, setLinking] = useState(false);
   *
   *   const handleLink = async () => {
   *     setLinking(true);
   *     try {
   *       await api.linkEvidenceToControls(evidenceId, [controlId], mappingId);
   *       toast.success('Evidence linked successfully');
   *     } catch (err) {
   *       toast.error('Failed to link evidence');
   *     } finally {
   *       setLinking(false);
   *     }
   *   };
   *
   *   return (
   *     <Button onClick={handleLink} loading={linking} size="sm">
   *       <LinkIcon className="w-4 h-4 mr-1" />
   *       Link Evidence
   *     </Button>
   *   );
   * }
   *
   * @see {@link getEvidenceLinkSuggestions} for finding controls to link via mappings
   * @see {@link unlinkEvidenceFromControl} for removing evidence-control links
   * @see {@link getControlMappings} for understanding control relationships
   * @see {@link uploadEvidence} for uploading evidence with initial control link
   */
  async linkEvidenceToControls(evidenceId: string, controlIds: string[], mappingId?: string) {
    return this.request<{
      success: boolean
      message: string
      linked_count: number
    }>(`/api/mappings/evidence/${evidenceId}/link`, {
      method: 'POST',
      body: JSON.stringify({ control_ids: controlIds, mapping_id: mappingId }),
    })
  }

  /**
   * Removes a link between evidence and a specific control.
   *
   * This method removes the association between an evidence item and a control,
   * indicating the evidence no longer satisfies or supports that control. The
   * unlink operation is audited for compliance tracking.
   *
   * **Unlink Behavior:**
   * - Only removes the link, does NOT delete the evidence itself
   * - The evidence remains linked to other controls
   * - If this was the last link, evidence becomes "orphaned" but still exists
   * - Action is recorded in audit logs
   *
   * **When to Unlink:**
   * - Evidence no longer applies to a control (policy change)
   * - Link was created in error
   * - Control requirements changed
   * - Evidence expired or became outdated for that specific control
   *
   * **Important Considerations:**
   * - Cannot unlink evidence from its primary/original control through this method
   *   (use deleteEvidence instead to completely remove)
   * - Cross-framework links created via mappings can be individually removed
   *
   * @param evidenceId - The unique identifier of the evidence
   * @param controlId - The control ID to unlink the evidence from
   *
   * @returns Promise resolving to the unlink result
   * @returns response.success - Whether the unlink succeeded
   * @returns response.message - Human-readable confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If evidence not found (404)
   * @throws {Error} If control not found (404)
   * @throws {Error} If link doesn't exist (400)
   * @throws {Error} If insufficient permissions (403)
   * @throws {Error} If attempting to unlink from primary control (400)
   *
   * @example
   * // Unlink evidence from a control
   * await api.unlinkEvidenceFromControl('ev_123', 'control_456');
   *
   * @example
   * // Unlink with confirmation
   * async function confirmAndUnlink(evidenceId: string, controlId: string) {
   *   const confirmed = await confirm(
   *     'Remove this evidence link? The evidence will remain linked to other controls.'
   *   );
   *
   *   if (confirmed) {
   *     await api.unlinkEvidenceFromControl(evidenceId, controlId);
   *     toast.success('Evidence unlinked');
   *   }
   * }
   *
   * @example
   * // Unlink button in evidence list
   * function UnlinkButton({ evidenceId, controlId, onUnlink }: Props) {
   *   const [unlinking, setUnlinking] = useState(false);
   *
   *   const handleUnlink = async () => {
   *     setUnlinking(true);
   *     try {
   *       await api.unlinkEvidenceFromControl(evidenceId, controlId);
   *       toast.success('Evidence unlinked from control');
   *       onUnlink();
   *     } catch (err: any) {
   *       if (err.message.includes('primary control')) {
   *         toast.error('Cannot unlink from primary control. Delete the evidence instead.');
   *       } else {
   *         toast.error('Failed to unlink evidence');
   *       }
   *     } finally {
   *       setUnlinking(false);
   *     }
   *   };
   *
   *   return (
   *     <Button
   *       variant="ghost"
   *       size="sm"
   *       onClick={handleUnlink}
   *       loading={unlinking}
   *     >
   *       <UnlinkIcon className="w-4 h-4" />
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Remove all cross-framework links for evidence
   * async function removeAllCrossFrameworkLinks(evidenceId: string) {
   *   // Get all evidence links
   *   const evidence = await api.getEvidenceItem(evidenceId);
   *   const primaryControlId = evidence.control_id;
   *
   *   // Unlink all except primary
   *   const linkedControls = evidence.linked_controls || [];
   *   for (const link of linkedControls) {
   *     if (link.control_id !== primaryControlId) {
   *       await api.unlinkEvidenceFromControl(evidenceId, link.control_id);
   *     }
   *   }
   * }
   *
   * @see {@link linkEvidenceToControls} for linking evidence to controls
   * @see {@link getEvidenceLinkSuggestions} for understanding link sources
   * @see {@link deleteEvidence} for completely removing evidence
   * @see {@link getAuditLogs} for tracking unlink events
   */
  async unlinkEvidenceFromControl(evidenceId: string, controlId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/mappings/evidence/${evidenceId}/link/${controlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Retrieves aggregate statistics for cross-framework control mappings.
   *
   * This method provides a high-level overview of the mapping system, including
   * counts by status, average AI confidence, and cross-framework evidence linking
   * metrics. Use these stats for dashboards and monitoring mapping health.
   *
   * **Stat Categories:**
   * - **Mapping counts**: Total, by review status (suggested/approved/rejected)
   * - **Quality metrics**: Average AI confidence score
   * - **Evidence utilization**: Count of cross-framework evidence links
   *
   * **Interpreting Stats:**
   * - High `suggested` with low `approved`: Review backlog exists
   * - Low `avg_confidence`: AI may need more training data or mappings are weak
   * - High `evidence_links`: Good cross-framework evidence reuse
   * - High `rejected`: Potential framework mismatch or over-aggressive AI
   *
   * @returns Promise resolving to mapping statistics
   * @returns response.success - Whether the request succeeded
   * @returns response.stats - Statistics object containing:
   * @returns response.stats.total_mappings - Total number of mappings across all statuses
   * @returns response.stats.suggested - Mappings pending review (AI-generated, not yet reviewed)
   * @returns response.stats.approved - Mappings that have been approved by reviewers
   * @returns response.stats.rejected - Mappings that have been rejected by reviewers
   * @returns response.stats.avg_confidence - Average AI confidence score (0.0-1.0) across all mappings
   * @returns response.stats.evidence_links - Total evidence-control links created via mappings
   *
   * @throws {Error} If not authenticated (401)
   *
   * @example
   * // Get mapping stats
   * const { stats } = await api.getMappingStats();
   * console.log(`${stats.total_mappings} total mappings, ${stats.approved} approved`);
   *
   * @example
   * // Mapping dashboard stats widget
   * function MappingStatsWidget() {
   *   const [stats, setStats] = useState<any>(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getMappingStats()
   *       .then(res => setStats(res.stats))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Skeleton />;
   *
   *   const reviewRate = stats.total_mappings > 0
   *     ? ((stats.approved + stats.rejected) / stats.total_mappings * 100).toFixed(1)
   *     : 0;
   *
   *   const approvalRate = (stats.approved + stats.rejected) > 0
   *     ? (stats.approved / (stats.approved + stats.rejected) * 100).toFixed(1)
   *     : 0;
   *
   *   return (
   *     <Card>
   *       <CardHeader>
   *         <h3>Cross-Framework Mappings</h3>
   *       </CardHeader>
   *       <CardContent>
   *         <div className="grid grid-cols-3 gap-4">
   *           <Stat label="Total Mappings" value={stats.total_mappings} />
   *           <Stat label="Pending Review" value={stats.suggested} variant="warning" />
   *           <Stat label="Approved" value={stats.approved} variant="success" />
   *         </div>
   *
   *         <div className="mt-4">
   *           <Progress
   *             value={parseFloat(reviewRate)}
   *             label={`${reviewRate}% reviewed`}
   *           />
   *         </div>
   *
   *         <div className="flex justify-between mt-4 text-sm text-muted">
   *           <span>Approval rate: {approvalRate}%</span>
   *           <span>Avg confidence: {(stats.avg_confidence * 100).toFixed(0)}%</span>
   *         </div>
   *       </CardContent>
   *     </Card>
   *   );
   * }
   *
   * @example
   * // Cross-framework efficiency metric
   * async function calculateCrossFrameworkEfficiency() {
   *   const { stats } = await api.getMappingStats();
   *
   *   // Evidence reuse efficiency: links per approved mapping
   *   const efficiency = stats.approved > 0
   *     ? stats.evidence_links / stats.approved
   *     : 0;
   *
   *   return {
   *     efficiency: efficiency.toFixed(2),
   *     interpretation: efficiency > 1.5
   *       ? 'High evidence reuse across frameworks'
   *       : efficiency > 0.5
   *         ? 'Moderate cross-framework linking'
   *         : 'Low cross-framework evidence utilization'
   *   };
   * }
   *
   * @example
   * // Mapping health alert
   * async function checkMappingHealth() {
   *   const { stats } = await api.getMappingStats();
   *   const alerts: string[] = [];
   *
   *   // Check review backlog
   *   if (stats.suggested > 50) {
   *     alerts.push(`${stats.suggested} mappings pending review`);
   *   }
   *
   *   // Check confidence quality
   *   if (stats.avg_confidence < 0.6) {
   *     alerts.push(`Low average confidence (${(stats.avg_confidence * 100).toFixed(0)}%)`);
   *   }
   *
   *   // Check rejection rate
   *   const totalReviewed = stats.approved + stats.rejected;
   *   if (totalReviewed > 10 && stats.rejected / totalReviewed > 0.5) {
   *     alerts.push('High rejection rate - review mapping criteria');
   *   }
   *
   *   return alerts;
   * }
   *
   * @see {@link getMappings} for detailed mapping list
   * @see {@link analyzeMappings} for triggering new AI mapping analysis
   * @see {@link getEvidenceLinkSuggestions} for evidence linking via mappings
   * @see {@link getDashboardOverview} for overall compliance dashboard
   */
  async getMappingStats() {
    return this.request<{
      success: boolean
      stats: {
        total_mappings: number
        suggested: number
        approved: number
        rejected: number
        avg_confidence: number
        evidence_links: number
      }
    }>('/api/mappings/stats')
  }

  // ============================================================================
  // Scanner endpoints
  // ============================================================================

  /**
   * Retrieves a paginated list of scan configurations for vulnerability scanning.
   *
   * Scan configurations define automated vulnerability scans that can run on schedules
   * or be triggered manually. Each config specifies the scan type, target systems,
   * frequency, and scan-specific options.
   *
   * **Scan Types:**
   * - `web`: Web application vulnerability scanning (OWASP Top 10, XSS, SQL injection)
   * - `network`: Network infrastructure scanning (open ports, services, misconfigurations)
   * - `cloud`: Cloud configuration scanning (AWS, Azure, GCP security posture)
   * - `container`: Container image vulnerability scanning (Docker, Kubernetes)
   * - `code`: Static code analysis (SAST) for security vulnerabilities
   *
   * **Scan Frequencies:**
   * - `manual`: Only runs when manually triggered via {@link runScan}
   * - `hourly`: Runs every hour (for critical systems)
   * - `daily`: Runs once per day (recommended for most configs)
   * - `weekly`: Runs once per week (for low-change environments)
   * - `monthly`: Runs once per month (for stable production systems)
   *
   * @param params - Optional filter and pagination parameters
   * @param params.limit - Maximum number of configs to return (default: 20)
   * @param params.offset - Number of configs to skip for pagination (default: 0)
   *
   * @returns Promise resolving to paginated scan configurations
   * @returns response.success - Whether the request succeeded
   * @returns response.configs - Array of scan configuration objects
   * @returns response.configs[].id - Unique configuration identifier
   * @returns response.configs[].name - Human-readable configuration name
   * @returns response.configs[].description - Optional description of the scan scope
   * @returns response.configs[].scan_type - Type of vulnerability scan (web, network, cloud, etc.)
   * @returns response.configs[].targets - Comma-separated list of scan targets (URLs, IPs, cloud resources)
   * @returns response.configs[].frequency - How often the scan runs (manual, hourly, daily, weekly, monthly)
   * @returns response.configs[].enabled - Whether the scheduled scan is active (1=enabled, 0=disabled)
   * @returns response.configs[].scan_options - JSON string with scan-specific configuration options
   * @returns response.configs[].last_run_at - Unix timestamp of last scan execution (null if never run)
   * @returns response.configs[].next_run_at - Unix timestamp of next scheduled scan (null if manual/disabled)
   * @returns response.configs[].total_scans - Total number of scans executed with this config
   * @returns response.configs[].last_scan_status - Status of most recent scan (completed, failed, running, null)
   * @returns response.configs[].created_at - Unix timestamp when config was created
   * @returns response.configs[].updated_at - Unix timestamp when config was last modified
   * @returns response.count - Total number of configs matching criteria
   * @returns response.pagination - Pagination metadata
   * @returns response.pagination.total - Total number of scan configurations
   * @returns response.pagination.limit - Current page size
   * @returns response.pagination.offset - Current offset
   * @returns response.pagination.hasMore - Whether more results exist beyond current page
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to view scanner configs (403)
   *
   * @example
   * // Load all scan configurations
   * const { configs, pagination } = await api.getScanConfigs();
   * console.log(`${pagination.total} scan configs configured`);
   *
   * @example
   * // Paginated scan config list
   * function ScanConfigList() {
   *   const [configs, setConfigs] = useState([]);
   *   const [page, setPage] = useState(0);
   *   const [hasMore, setHasMore] = useState(true);
   *   const limit = 10;
   *
   *   useEffect(() => {
   *     api.getScanConfigs({ limit, offset: page * limit })
   *       .then(res => {
   *         setConfigs(res.configs);
   *         setHasMore(res.pagination.hasMore);
   *       });
   *   }, [page]);
   *
   *   return (
   *     <div>
   *       <Table>
   *         <TableHeader>
   *           <TableRow>
   *             <TableHead>Name</TableHead>
   *             <TableHead>Type</TableHead>
   *             <TableHead>Frequency</TableHead>
   *             <TableHead>Last Run</TableHead>
   *             <TableHead>Status</TableHead>
   *           </TableRow>
   *         </TableHeader>
   *         <TableBody>
   *           {configs.map(config => (
   *             <TableRow key={config.id}>
   *               <TableCell>{config.name}</TableCell>
   *               <TableCell><Badge>{config.scan_type}</Badge></TableCell>
   *               <TableCell>{config.frequency}</TableCell>
   *               <TableCell>
   *                 {config.last_run_at
   *                   ? formatDate(config.last_run_at * 1000)
   *                   : 'Never'}
   *               </TableCell>
   *               <TableCell>
   *                 <StatusBadge status={config.last_scan_status || 'pending'} />
   *               </TableCell>
   *             </TableRow>
   *           ))}
   *         </TableBody>
   *       </Table>
   *
   *       <div className="flex gap-2 mt-4">
   *         <Button disabled={page === 0} onClick={() => setPage(p => p - 1)}>
   *           Previous
   *         </Button>
   *         <Button disabled={!hasMore} onClick={() => setPage(p => p + 1)}>
   *           Next
   *         </Button>
   *       </div>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Get enabled configs for scheduler dashboard
   * async function getActiveConfigs() {
   *   const { configs } = await api.getScanConfigs({ limit: 100 });
   *   return configs.filter(c => c.enabled && c.frequency !== 'manual');
   * }
   *
   * @example
   * // Find configs that haven't run recently
   * async function findStaleConfigs(daysSinceLastRun: number) {
   *   const { configs } = await api.getScanConfigs({ limit: 100 });
   *   const cutoff = Date.now() / 1000 - (daysSinceLastRun * 24 * 60 * 60);
   *
   *   return configs.filter(c =>
   *     c.enabled &&
   *     (c.last_run_at === null || c.last_run_at < cutoff)
   *   );
   * }
   *
   * @see {@link getScanConfig} for detailed config with recent results
   * @see {@link createScanConfig} for creating new configurations
   * @see {@link runScan} for manually triggering a scan
   * @see {@link getScanResults} for viewing scan results
   */
  async getScanConfigs(params?: {
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      configs: Array<{
        id: string
        name: string
        description: string | null
        scan_type: string
        targets: string
        frequency: string
        enabled: number
        scan_options: string
        last_run_at: number | null
        next_run_at: number | null
        total_scans: number
        last_scan_status: string | null
        created_at: number
        updated_at: number
      }>
      count: number
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/scanners/configs${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves a single scan configuration with its recent scan results.
   *
   * This method returns detailed information about a specific scan configuration,
   * including the full configuration settings and a summary of recent scan results
   * with vulnerability counts by severity level.
   *
   * **Vulnerability Severity Levels:**
   * - `critical`: CVSS 9.0-10.0 - Immediate action required, active exploitation possible
   * - `high`: CVSS 7.0-8.9 - Significant risk, should be addressed quickly
   * - `medium`: CVSS 4.0-6.9 - Moderate risk, should be scheduled for remediation
   * - `low`: CVSS 0.1-3.9 - Minor risk, address as resources allow
   * - `info`: CVSS 0 - Informational finding, no direct security impact
   *
   * **Scan Result Statuses:**
   * - `queued`: Scan is scheduled and waiting to run
   * - `running`: Scan is currently in progress
   * - `completed`: Scan finished successfully
   * - `failed`: Scan encountered an error and could not complete
   * - `cancelled`: Scan was manually cancelled before completion
   *
   * @param id - Unique identifier of the scan configuration to retrieve
   *
   * @returns Promise resolving to scan configuration with recent results
   * @returns response.success - Whether the request succeeded
   * @returns response.config - Full scan configuration object (see {@link getScanConfigs} for fields)
   * @returns response.recent_results - Array of recent scan results (most recent first)
   * @returns response.recent_results[].id - Unique scan result identifier
   * @returns response.recent_results[].started_at - Unix timestamp when scan started
   * @returns response.recent_results[].completed_at - Unix timestamp when scan completed (null if still running)
   * @returns response.recent_results[].status - Current scan status (queued, running, completed, failed, cancelled)
   * @returns response.recent_results[].vulnerabilities_found - Total vulnerabilities discovered
   * @returns response.recent_results[].critical_count - Number of critical severity vulnerabilities
   * @returns response.recent_results[].high_count - Number of high severity vulnerabilities
   * @returns response.recent_results[].medium_count - Number of medium severity vulnerabilities
   * @returns response.recent_results[].low_count - Number of low severity vulnerabilities
   * @returns response.recent_results[].info_count - Number of informational findings
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to view scanner configs (403)
   * @throws {Error} If scan configuration not found (404)
   *
   * @example
   * // Load scan configuration details
   * const { config, recent_results } = await api.getScanConfig('config_123');
   * console.log(`${config.name}: ${recent_results.length} recent scans`);
   *
   * @example
   * // Scan configuration detail page
   * function ScanConfigDetail({ configId }: { configId: string }) {
   *   const [config, setConfig] = useState<any>(null);
   *   const [results, setResults] = useState([]);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getScanConfig(configId)
   *       .then(res => {
   *         setConfig(res.config);
   *         setResults(res.recent_results);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [configId]);
   *
   *   if (loading) return <Skeleton />;
   *   if (!config) return <NotFound />;
   *
   *   const latestResult = results[0];
   *
   *   return (
   *     <div className="space-y-6">
   *       <Card>
   *         <CardHeader>
   *           <h2>{config.name}</h2>
   *           <Badge>{config.scan_type}</Badge>
   *           <Badge variant={config.enabled ? 'success' : 'secondary'}>
   *             {config.enabled ? 'Enabled' : 'Disabled'}
   *           </Badge>
   *         </CardHeader>
   *         <CardContent>
   *           <p>{config.description}</p>
   *           <div className="grid grid-cols-2 gap-4 mt-4">
   *             <Stat label="Frequency" value={config.frequency} />
   *             <Stat label="Total Scans" value={config.total_scans} />
   *           </div>
   *         </CardContent>
   *       </Card>
   *
   *       {latestResult && (
   *         <Card>
   *           <CardHeader>
   *             <h3>Latest Scan</h3>
   *             <StatusBadge status={latestResult.status} />
   *           </CardHeader>
   *           <CardContent>
   *             <VulnerabilitySummary
   *               critical={latestResult.critical_count}
   *               high={latestResult.high_count}
   *               medium={latestResult.medium_count}
   *               low={latestResult.low_count}
   *               info={latestResult.info_count}
   *             />
   *           </CardContent>
   *         </Card>
   *       )}
   *
   *       <ScanHistoryTable results={results} />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate vulnerability trend from recent results
   * async function calculateVulnerabilityTrend(configId: string) {
   *   const { recent_results } = await api.getScanConfig(configId);
   *
   *   if (recent_results.length < 2) return 'insufficient_data';
   *
   *   const latest = recent_results[0];
   *   const previous = recent_results[1];
   *
   *   const latestTotal = latest.vulnerabilities_found;
   *   const previousTotal = previous.vulnerabilities_found;
   *
   *   if (latestTotal > previousTotal) return 'increasing';
   *   if (latestTotal < previousTotal) return 'decreasing';
   *   return 'stable';
   * }
   *
   * @see {@link getScanConfigs} for listing all configurations
   * @see {@link updateScanConfig} for modifying configuration settings
   * @see {@link runScan} for manually triggering a scan
   * @see {@link getScanResult} for detailed vulnerability findings
   */
  async getScanConfig(id: string) {
    return this.request<{
      success: boolean
      config: any
      recent_results: Array<{
        id: string
        started_at: number
        completed_at: number | null
        status: string
        vulnerabilities_found: number
        critical_count: number
        high_count: number
        medium_count: number
        low_count: number
        info_count: number
      }>
    }>(`/api/scanners/configs/${id}`)
  }

  /**
   * Creates a new scan configuration for automated vulnerability scanning.
   *
   * This method creates a new scanner configuration that defines what systems to scan,
   * what type of vulnerabilities to look for, and how often to scan. Once created,
   * configurations can run on a schedule or be triggered manually via {@link runScan}.
   *
   * **Scan Types Available:**
   * - `web` (default): Web application scanning - tests for OWASP Top 10, XSS, SQLi, CSRF
   * - `network`: Infrastructure scanning - port scans, service detection, misconfigurations
   * - `cloud`: Cloud security posture - AWS/Azure/GCP configuration assessment
   * - `container`: Container scanning - Docker image vulnerabilities, Kubernetes configs
   * - `code`: Static analysis - source code security scanning (SAST)
   *
   * **Target Format by Scan Type:**
   * - `web`: Full URLs (e.g., `["https://app.example.com", "https://api.example.com"]`)
   * - `network`: IP addresses or CIDR ranges (e.g., `["192.168.1.0/24", "10.0.0.5"]`)
   * - `cloud`: Cloud resource ARNs or account IDs
   * - `container`: Image names (e.g., `["myapp:latest", "nginx:1.24"]`)
   * - `code`: Repository paths or URLs
   *
   * **Frequency Options:**
   * - `manual` (default): Only runs when triggered via {@link runScan}
   * - `hourly`: Runs every hour (use for critical production systems)
   * - `daily`: Runs once per day at midnight UTC (recommended default)
   * - `weekly`: Runs every Sunday at midnight UTC
   * - `monthly`: Runs on the 1st of each month at midnight UTC
   *
   * @param data - Scan configuration settings
   * @param data.name - Human-readable name for the configuration (required)
   * @param data.description - Optional description explaining scan scope and purpose
   * @param data.scan_type - Type of vulnerability scan to perform (default: 'web')
   * @param data.targets - Array of scan targets (URLs, IPs, resources) - format depends on scan_type (required)
   * @param data.frequency - How often to run automated scans (default: 'manual')
   * @param data.enabled - Whether scheduled scanning is active (default: true)
   * @param data.scan_options - Type-specific scan options (e.g., depth, authentication, exclusions)
   *
   * @returns Promise resolving to created configuration
   * @returns response.success - Whether the configuration was created successfully
   * @returns response.config - Created configuration object
   * @returns response.config.id - Unique identifier for the new configuration
   * @returns response.message - Success message confirming creation
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to create scanner configs (403)
   * @throws {Error} If validation fails (invalid targets, unsupported scan type, etc.) (400)
   *
   * @example
   * // Create a basic web application scan
   * const { config } = await api.createScanConfig({
   *   name: 'Production Web App Scan',
   *   description: 'Daily scan of main production web application',
   *   scan_type: 'web',
   *   targets: ['https://app.example.com', 'https://api.example.com'],
   *   frequency: 'daily',
   *   enabled: true
   * });
   * console.log(`Created scan config: ${config.id}`);
   *
   * @example
   * // Create scan config form handler
   * function CreateScanConfigForm() {
   *   const [formData, setFormData] = useState({
   *     name: '',
   *     description: '',
   *     scan_type: 'web',
   *     targets: [''],
   *     frequency: 'daily',
   *     enabled: true
   *   });
   *   const [submitting, setSubmitting] = useState(false);
   *   const navigate = useNavigate();
   *
   *   async function handleSubmit(e: FormEvent) {
   *     e.preventDefault();
   *     setSubmitting(true);
   *
   *     try {
   *       // Filter out empty targets
   *       const cleanTargets = formData.targets.filter(t => t.trim());
   *
   *       const { config, message } = await api.createScanConfig({
   *         ...formData,
   *         targets: cleanTargets
   *       });
   *
   *       toast.success(message);
   *       navigate(`/scanners/configs/${config.id}`);
   *     } catch (err) {
   *       toast.error(err.message);
   *     } finally {
   *       setSubmitting(false);
   *     }
   *   }
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <Input
   *         label="Configuration Name"
   *         value={formData.name}
   *         onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
   *         required
   *       />
   *
   *       <TextArea
   *         label="Description"
   *         value={formData.description}
   *         onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
   *       />
   *
   *       <Select
   *         label="Scan Type"
   *         value={formData.scan_type}
   *         onChange={e => setFormData(prev => ({ ...prev, scan_type: e.target.value }))}
   *       >
   *         <option value="web">Web Application</option>
   *         <option value="network">Network Infrastructure</option>
   *         <option value="cloud">Cloud Security</option>
   *         <option value="container">Container Images</option>
   *       </Select>
   *
   *       <TargetListInput
   *         label="Scan Targets"
   *         values={formData.targets}
   *         onChange={targets => setFormData(prev => ({ ...prev, targets }))}
   *         placeholder={
   *           formData.scan_type === 'web' ? 'https://example.com' :
   *           formData.scan_type === 'network' ? '192.168.1.0/24' :
   *           'Target identifier'
   *         }
   *       />
   *
   *       <Select
   *         label="Scan Frequency"
   *         value={formData.frequency}
   *         onChange={e => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
   *       >
   *         <option value="manual">Manual Only</option>
   *         <option value="hourly">Hourly</option>
   *         <option value="daily">Daily</option>
   *         <option value="weekly">Weekly</option>
   *         <option value="monthly">Monthly</option>
   *       </Select>
   *
   *       <Switch
   *         label="Enable Scheduled Scanning"
   *         checked={formData.enabled}
   *         onChange={enabled => setFormData(prev => ({ ...prev, enabled }))}
   *       />
   *
   *       <Button type="submit" loading={submitting}>
   *         Create Scan Configuration
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Create network scan with custom options
   * await api.createScanConfig({
   *   name: 'Internal Network Scan',
   *   scan_type: 'network',
   *   targets: ['10.0.0.0/8', '172.16.0.0/12'],
   *   frequency: 'weekly',
   *   scan_options: {
   *     port_range: '1-65535',
   *     scan_speed: 'normal',
   *     exclude_hosts: ['10.0.0.1', '10.0.0.254']
   *   }
   * });
   *
   * @see {@link getScanConfigs} for listing all configurations
   * @see {@link updateScanConfig} for modifying configurations
   * @see {@link runScan} for manually triggering a scan
   * @see {@link deleteScanConfig} for removing configurations
   */
  async createScanConfig(data: {
    name: string
    description?: string
    scan_type?: string
    targets: string[]
    frequency?: string
    enabled?: boolean
    scan_options?: Record<string, any>
    schedule_hour?: number
    schedule_day?: number
  }) {
    return this.request<{
      success: boolean
      config: { id: string }
      message: string
    }>('/api/scanners/configs', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Updates an existing scan configuration with new settings.
   *
   * This method allows partial updates to a scan configuration. Only the fields
   * provided in the data object will be updated; omitted fields retain their
   * current values. Use this to modify scan targets, change schedules, or
   * enable/disable configurations.
   *
   * **Common Update Scenarios:**
   * - **Add/remove targets**: Update the targets array to include new systems
   * - **Change frequency**: Adjust scan schedule (e.g., daily to weekly)
   * - **Enable/disable**: Toggle automated scanning without deleting the config
   * - **Update options**: Modify scan-specific settings (port ranges, exclusions, etc.)
   *
   * **Important Notes:**
   * - Changes to targets or scan_type take effect on the next scan execution
   * - Changing frequency immediately reschedules the next automated scan
   * - Disabling a config (enabled: false) cancels any pending scheduled scans
   * - Changes are logged in the audit trail for compliance tracking
   *
   * @param id - Unique identifier of the scan configuration to update
   * @param data - Partial configuration data with fields to update
   * @param data.name - New human-readable name for the configuration
   * @param data.description - Updated description of the scan scope
   * @param data.scan_type - New type of vulnerability scan (web, network, cloud, container, code)
   * @param data.targets - Updated array of scan targets (replaces existing targets)
   * @param data.frequency - New scan frequency (manual, hourly, daily, weekly, monthly)
   * @param data.enabled - Whether scheduled scanning should be active
   * @param data.scan_options - Updated type-specific scan options
   *
   * @returns Promise resolving to update confirmation
   * @returns response.success - Whether the update was applied successfully
   * @returns response.message - Success message confirming the update
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to modify scanner configs (403)
   * @throws {Error} If scan configuration not found (404)
   * @throws {Error} If validation fails (invalid targets, unsupported options, etc.) (400)
   *
   * @example
   * // Disable a scan configuration
   * await api.updateScanConfig('config_123', { enabled: false });
   *
   * @example
   * // Change scan frequency
   * await api.updateScanConfig('config_123', { frequency: 'weekly' });
   *
   * @example
   * // Add new targets to a scan
   * const { config } = await api.getScanConfig('config_123');
   * const existingTargets = config.targets.split(',');
   * await api.updateScanConfig('config_123', {
   *   targets: [...existingTargets, 'https://new-app.example.com']
   * });
   *
   * @example
   * // Edit scan config form
   * function EditScanConfigForm({ configId }: { configId: string }) {
   *   const [config, setConfig] = useState<any>(null);
   *   const [formData, setFormData] = useState<any>(null);
   *   const [submitting, setSubmitting] = useState(false);
   *
   *   useEffect(() => {
   *     api.getScanConfig(configId).then(res => {
   *       setConfig(res.config);
   *       // Parse targets from comma-separated string
   *       setFormData({
   *         ...res.config,
   *         targets: res.config.targets.split(',').map((t: string) => t.trim())
   *       });
   *     });
   *   }, [configId]);
   *
   *   async function handleSubmit(e: FormEvent) {
   *     e.preventDefault();
   *     setSubmitting(true);
   *
   *     try {
   *       const { message } = await api.updateScanConfig(configId, formData);
   *       toast.success(message);
   *     } catch (err) {
   *       toast.error(err.message);
   *     } finally {
   *       setSubmitting(false);
   *     }
   *   }
   *
   *   if (!formData) return <Skeleton />;
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <Input
   *         label="Name"
   *         value={formData.name}
   *         onChange={e => setFormData((prev: any) => ({ ...prev, name: e.target.value }))}
   *       />
   *
   *       <Select
   *         label="Frequency"
   *         value={formData.frequency}
   *         onChange={e => setFormData((prev: any) => ({ ...prev, frequency: e.target.value }))}
   *       >
   *         <option value="manual">Manual Only</option>
   *         <option value="hourly">Hourly</option>
   *         <option value="daily">Daily</option>
   *         <option value="weekly">Weekly</option>
   *         <option value="monthly">Monthly</option>
   *       </Select>
   *
   *       <Switch
   *         label="Enabled"
   *         checked={Boolean(formData.enabled)}
   *         onChange={enabled => setFormData((prev: any) => ({ ...prev, enabled }))}
   *       />
   *
   *       <Button type="submit" loading={submitting}>
   *         Save Changes
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Bulk enable/disable configs
   * async function setConfigsEnabled(configIds: string[], enabled: boolean) {
   *   await Promise.all(
   *     configIds.map(id => api.updateScanConfig(id, { enabled }))
   *   );
   * }
   *
   * @see {@link getScanConfig} for retrieving current configuration
   * @see {@link createScanConfig} for creating new configurations
   * @see {@link deleteScanConfig} for removing configurations
   * @see {@link runScan} for manually triggering a scan
   */
  async updateScanConfig(id: string, data: Partial<{
    name: string
    description: string
    scan_type: string
    targets: string[]
    frequency: string
    enabled: boolean
    scan_options: Record<string, any>
    schedule_hour: number
    schedule_day: number
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/configs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Permanently deletes a scan configuration.
   *
   * This method removes a scan configuration and cancels any pending scheduled scans.
   * Historical scan results associated with this configuration are **preserved** for
   * audit and compliance purposes, but no new scans can be run.
   *
   * **Important Considerations:**
   * - This action is **permanent and cannot be undone**
   * - Historical scan results remain accessible via {@link getScanResults}
   * - Consider using {@link updateScanConfig} with `enabled: false` to disable instead of delete
   * - Deletion is logged in the audit trail for compliance tracking
   * - If a scan is currently running, it will complete but the config will be removed afterward
   *
   * **When to Delete vs Disable:**
   * - **Delete**: When the targets no longer exist or config was created in error
   * - **Disable**: When temporarily pausing scans or preserving config for future use
   *
   * @param id - Unique identifier of the scan configuration to delete
   *
   * @returns Promise resolving to deletion confirmation
   * @returns response.success - Whether the configuration was deleted successfully
   * @returns response.message - Success message confirming deletion
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to delete scanner configs (403)
   * @throws {Error} If scan configuration not found (404)
   *
   * @example
   * // Delete a scan configuration
   * await api.deleteScanConfig('config_123');
   *
   * @example
   * // Delete with confirmation dialog
   * function DeleteConfigButton({ configId, configName }: { configId: string; configName: string }) {
   *   const [deleting, setDeleting] = useState(false);
   *   const navigate = useNavigate();
   *
   *   async function handleDelete() {
   *     const confirmed = await confirm(
   *       `Are you sure you want to delete "${configName}"? This action cannot be undone.`
   *     );
   *
   *     if (!confirmed) return;
   *
   *     setDeleting(true);
   *     try {
   *       const { message } = await api.deleteScanConfig(configId);
   *       toast.success(message);
   *       navigate('/scanners/configs');
   *     } catch (err) {
   *       toast.error(err.message);
   *     } finally {
   *       setDeleting(false);
   *     }
   *   }
   *
   *   return (
   *     <Button
   *       variant="destructive"
   *       loading={deleting}
   *       onClick={handleDelete}
   *     >
   *       Delete Configuration
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Cleanup unused configurations
   * async function cleanupUnusedConfigs(daysInactive: number) {
   *   const { configs } = await api.getScanConfigs({ limit: 100 });
   *   const cutoff = Date.now() / 1000 - (daysInactive * 24 * 60 * 60);
   *
   *   const unusedConfigs = configs.filter(c =>
   *     !c.enabled &&
   *     c.total_scans === 0 &&
   *     c.created_at < cutoff
   *   );
   *
   *   for (const config of unusedConfigs) {
   *     console.log(`Deleting unused config: ${config.name}`);
   *     await api.deleteScanConfig(config.id);
   *   }
   *
   *   return unusedConfigs.length;
   * }
   *
   * @see {@link getScanConfigs} for listing configurations
   * @see {@link updateScanConfig} for disabling instead of deleting
   * @see {@link getScanResults} for accessing historical scan results
   */
  async deleteScanConfig(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/configs/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Manually triggers a vulnerability scan execution for a configuration.
   *
   * This method immediately queues a scan for the specified configuration, regardless
   * of the configured schedule. The scan runs asynchronously in the background;
   * use {@link getScanResult} to poll for completion and retrieve results.
   *
   * **Scan Execution Flow:**
   * 1. Scan is queued (returns `scan_result_id`)
   * 2. Scanner picks up the job and begins execution
   * 3. Targets are scanned based on scan_type configuration
   * 4. Results are stored with vulnerability counts by severity
   * 5. Scan completes (or fails with error message)
   *
   * **Important Notes:**
   * - Only one scan per configuration can run at a time
   * - If a scan is already running for this config, the request will fail
   * - The returned `scan_result_id` can be used to track progress via {@link getScanResult}
   * - Scans can take seconds (small targets) to hours (large network ranges)
   * - The configuration must be enabled (or this is overridden for manual runs)
   *
   * **Typical Scan Durations:**
   * - `web` (single app): 5-30 minutes depending on site size
   * - `network` (small range): 10-60 minutes depending on port range
   * - `cloud`: 2-10 minutes per cloud account
   * - `container`: 1-5 minutes per image
   *
   * @param configId - Unique identifier of the scan configuration to execute
   *
   * @returns Promise resolving to scan execution details
   * @returns response.success - Whether the scan was queued successfully
   * @returns response.scan_result_id - Unique identifier for tracking this scan execution
   * @returns response.message - Confirmation message with scan details
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to run scans (403)
   * @throws {Error} If scan configuration not found (404)
   * @throws {Error} If a scan is already running for this configuration (409)
   *
   * @example
   * // Trigger a scan and get the result ID
   * const { scan_result_id } = await api.runScan('config_123');
   * console.log(`Scan started: ${scan_result_id}`);
   *
   * @example
   * // Manual scan trigger button
   * function RunScanButton({ configId }: { configId: string }) {
   *   const [running, setRunning] = useState(false);
   *   const navigate = useNavigate();
   *
   *   async function handleRunScan() {
   *     setRunning(true);
   *     try {
   *       const { scan_result_id, message } = await api.runScan(configId);
   *       toast.success(message);
   *       navigate(`/scanners/results/${scan_result_id}`);
   *     } catch (err) {
   *       if (err.message.includes('already running')) {
   *         toast.warning('A scan is already in progress for this configuration');
   *       } else {
   *         toast.error(err.message);
   *       }
   *     } finally {
   *       setRunning(false);
   *     }
   *   }
   *
   *   return (
   *     <Button onClick={handleRunScan} loading={running}>
   *       Run Scan Now
   *     </Button>
   *   );
   * }
   *
   * @example
   * // Run scan and poll for completion
   * async function runScanAndWait(configId: string, maxWaitMs = 300000) {
   *   const { scan_result_id } = await api.runScan(configId);
   *
   *   const startTime = Date.now();
   *   while (Date.now() - startTime < maxWaitMs) {
   *     const { result } = await api.getScanResult(scan_result_id);
   *
   *     if (result.status === 'completed') {
   *       return result;
   *     }
   *
   *     if (result.status === 'failed') {
   *       throw new Error(`Scan failed: ${result.error_message}`);
   *     }
   *
   *     // Wait 5 seconds before polling again
   *     await new Promise(resolve => setTimeout(resolve, 5000));
   *   }
   *
   *   throw new Error('Scan timed out');
   * }
   *
   * @example
   * // Scan progress tracker component
   * function ScanProgressTracker({ scanResultId }: { scanResultId: string }) {
   *   const [result, setResult] = useState<any>(null);
   *
   *   useEffect(() => {
   *     let cancelled = false;
   *
   *     async function pollStatus() {
   *       while (!cancelled) {
   *         const { result } = await api.getScanResult(scanResultId);
   *         setResult(result);
   *
   *         if (['completed', 'failed', 'cancelled'].includes(result.status)) {
   *           break;
   *         }
   *
   *         await new Promise(r => setTimeout(r, 3000));
   *       }
   *     }
   *
   *     pollStatus();
   *     return () => { cancelled = true; };
   *   }, [scanResultId]);
   *
   *   if (!result) return <Spinner />;
   *
   *   return (
   *     <Card>
   *       <CardHeader>
   *         <StatusBadge status={result.status} />
   *       </CardHeader>
   *       <CardContent>
   *         {result.status === 'running' && (
   *           <Progress indeterminate label="Scan in progress..." />
   *         )}
   *         {result.status === 'completed' && (
   *           <VulnerabilitySummary
   *             total={result.vulnerabilities_found}
   *             critical={result.critical_count}
   *             high={result.high_count}
   *             medium={result.medium_count}
   *             low={result.low_count}
   *           />
   *         )}
   *       </CardContent>
   *     </Card>
   *   );
   * }
   *
   * @see {@link getScanConfigs} for listing scan configurations
   * @see {@link getScanConfig} for configuration details
   * @see {@link getScanResult} for retrieving scan results and progress
   * @see {@link getScanResults} for listing all scan results
   */
  async runScan(configId: string, options?: {
    scan_mode?: 'quick' | 'full' | 'stealth'
    engine?: 'auto' | 'pd' | 'nmap' | 'nuclei'
  }) {
    return this.request<{
      success: boolean
      scan_result_id: string
      message: string
    }>(`/api/scanners/configs/${configId}/run`, {
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined,
    })
  }

  // Scan Results

  /**
   * Retrieves a paginated list of scan results with filtering options.
   *
   * This method returns scan execution history including summary statistics
   * for each scan run. Use this to display scan history, monitor scan health,
   * and identify scans that need attention based on vulnerability counts.
   *
   * **Scan Result Statuses:**
   * - `queued`: Scan is scheduled and waiting to run
   * - `running`: Scan is currently in progress
   * - `completed`: Scan finished successfully with results
   * - `failed`: Scan encountered an error (see error_message for details)
   * - `cancelled`: Scan was manually cancelled before completion
   *
   * **Vulnerability Counts:**
   * Each result includes counts by severity level to quickly identify
   * high-risk scans without fetching full vulnerability details:
   * - `critical_count`: CVSS 9.0-10.0 - Requires immediate attention
   * - `high_count`: CVSS 7.0-8.9 - Should be prioritized
   * - `medium_count`: CVSS 4.0-6.9 - Schedule for remediation
   * - `low_count`: CVSS 0.1-3.9 - Address as resources allow
   * - `info_count`: CVSS 0 - Informational only
   *
   * @param params - Optional filter and pagination parameters
   * @param params.config_id - Filter results to a specific scan configuration
   * @param params.status - Filter by scan status (queued, running, completed, failed, cancelled)
   * @param params.limit - Maximum number of results to return (default: 20)
   * @param params.offset - Number of results to skip for pagination (default: 0)
   *
   * @returns Promise resolving to paginated scan results
   * @returns response.success - Whether the request succeeded
   * @returns response.results - Array of scan result summaries
   * @returns response.results[].id - Unique scan result identifier
   * @returns response.results[].scan_config_id - ID of the configuration that ran this scan
   * @returns response.results[].config_name - Human-readable name of the scan configuration
   * @returns response.results[].scan_type - Type of scan (web, network, cloud, container, code)
   * @returns response.results[].started_at - Unix timestamp when scan started
   * @returns response.results[].completed_at - Unix timestamp when scan completed (null if still running)
   * @returns response.results[].duration - Scan duration in seconds (null if still running)
   * @returns response.results[].status - Current scan status
   * @returns response.results[].hosts_scanned - Number of hosts/targets scanned
   * @returns response.results[].vulnerabilities_found - Total vulnerabilities discovered
   * @returns response.results[].critical_count - Number of critical severity vulnerabilities
   * @returns response.results[].high_count - Number of high severity vulnerabilities
   * @returns response.results[].medium_count - Number of medium severity vulnerabilities
   * @returns response.results[].low_count - Number of low severity vulnerabilities
   * @returns response.results[].info_count - Number of informational findings
   * @returns response.results[].error_message - Error details if scan failed (null otherwise)
   * @returns response.pagination - Pagination metadata
   * @returns response.pagination.total - Total number of scan results
   * @returns response.pagination.limit - Current page size
   * @returns response.pagination.offset - Current offset
   * @returns response.pagination.hasMore - Whether more results exist
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to view scanner results (403)
   *
   * @example
   * // Load all scan results with pagination
   * const { results, pagination } = await api.getScanResults({
   *   limit: 20,
   *   offset: 0
   * });
   *
   * @example
   * // Filter results for a specific scan configuration
   * const { results } = await api.getScanResults({
   *   config_id: 'config_123',
   *   limit: 10
   * });
   *
   * @example
   * // Find failed scans that need investigation
   * const { results } = await api.getScanResults({
   *   status: 'failed'
   * });
   * results.forEach(r => console.log(`${r.config_name}: ${r.error_message}`));
   *
   * @example
   * // Scan results list page with filtering
   * function ScanResultsList() {
   *   const [results, setResults] = useState([]);
   *   const [pagination, setPagination] = useState(null);
   *   const [statusFilter, setStatusFilter] = useState<string>();
   *   const [loading, setLoading] = useState(true);
   *
   *   const loadResults = async (offset = 0) => {
   *     setLoading(true);
   *     try {
   *       const res = await api.getScanResults({
   *         status: statusFilter,
   *         limit: 20,
   *         offset
   *       });
   *       setResults(res.results);
   *       setPagination(res.pagination);
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   useEffect(() => {
   *     loadResults();
   *   }, [statusFilter]);
   *
   *   return (
   *     <div className="space-y-4">
   *       <Select value={statusFilter} onValueChange={setStatusFilter}>
   *         <SelectItem value="">All Statuses</SelectItem>
   *         <SelectItem value="completed">Completed</SelectItem>
   *         <SelectItem value="running">Running</SelectItem>
   *         <SelectItem value="failed">Failed</SelectItem>
   *       </Select>
   *
   *       <Table>
   *         <TableHeader>
   *           <TableRow>
   *             <TableHead>Config</TableHead>
   *             <TableHead>Status</TableHead>
   *             <TableHead>Started</TableHead>
   *             <TableHead>Vulnerabilities</TableHead>
   *           </TableRow>
   *         </TableHeader>
   *         <TableBody>
   *           {results.map(result => (
   *             <TableRow key={result.id}>
   *               <TableCell>{result.config_name}</TableCell>
   *               <TableCell>
   *                 <StatusBadge status={result.status} />
   *               </TableCell>
   *               <TableCell>{formatDate(result.started_at)}</TableCell>
   *               <TableCell>
   *                 <VulnerabilityCounts
   *                   critical={result.critical_count}
   *                   high={result.high_count}
   *                   medium={result.medium_count}
   *                   low={result.low_count}
   *                 />
   *               </TableCell>
   *             </TableRow>
   *           ))}
   *         </TableBody>
   *       </Table>
   *
   *       <Pagination
   *         total={pagination?.total}
   *         limit={pagination?.limit}
   *         offset={pagination?.offset}
   *         onPageChange={loadResults}
   *       />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate aggregate vulnerability stats across all scans
   * async function getAggregateVulnerabilityStats() {
   *   const allResults = [];
   *   let offset = 0;
   *   let hasMore = true;
   *
   *   // Fetch all completed scan results
   *   while (hasMore) {
   *     const { results, pagination } = await api.getScanResults({
   *       status: 'completed',
   *       limit: 100,
   *       offset
   *     });
   *     allResults.push(...results);
   *     hasMore = pagination.hasMore;
   *     offset += 100;
   *   }
   *
   *   // Aggregate counts from most recent scan per config
   *   const latestByConfig = new Map();
   *   allResults.forEach(r => {
   *     if (!latestByConfig.has(r.scan_config_id) ||
   *         r.started_at > latestByConfig.get(r.scan_config_id).started_at) {
   *       latestByConfig.set(r.scan_config_id, r);
   *     }
   *   });
   *
   *   return Array.from(latestByConfig.values()).reduce(
   *     (acc, r) => ({
   *       critical: acc.critical + r.critical_count,
   *       high: acc.high + r.high_count,
   *       medium: acc.medium + r.medium_count,
   *       low: acc.low + r.low_count
   *     }),
   *     { critical: 0, high: 0, medium: 0, low: 0 }
   *   );
   * }
   *
   * @see {@link getScanResult} for detailed vulnerability findings from a specific scan
   * @see {@link getScanConfigs} for listing scan configurations
   * @see {@link runScan} for manually triggering a new scan
   * @see {@link deleteScanResult} for removing old scan results
   * @see {@link cleanupScanResults} for bulk result cleanup
   */
  async getScanResults(params?: {
    config_id?: string
    status?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.config_id) searchParams.set('config_id', params.config_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      results: Array<{
        id: string
        scan_config_id: string
        config_name: string
        scan_type: string
        started_at: number
        completed_at: number | null
        duration: number | null
        status: string
        hosts_scanned: number
        vulnerabilities_found: number
        critical_count: number
        high_count: number
        medium_count: number
        low_count: number
        info_count: number
        error_message: string | null
      }>
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/scanners/results${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves detailed scan result including all discovered vulnerabilities.
   *
   * This method returns the full scan result with a complete list of
   * vulnerabilities discovered during the scan. Use this for detailed
   * vulnerability analysis, remediation planning, and compliance reporting.
   *
   * **Vulnerability Severity Levels (CVSS-based):**
   * - `critical`: CVSS 9.0-10.0 - Immediate action required, may be actively exploited
   * - `high`: CVSS 7.0-8.9 - Significant risk, should be addressed quickly
   * - `medium`: CVSS 4.0-6.9 - Moderate risk, schedule for remediation
   * - `low`: CVSS 0.1-3.9 - Minor risk, address as resources allow
   * - `info`: CVSS 0 - Informational finding, no direct security impact
   *
   * **False Positive Management:**
   * Vulnerabilities can be marked as false positives to exclude them from
   * future reports. The `is_false_positive` field indicates this status,
   * with audit trail fields showing who marked it and why.
   *
   * **Evidence Linking:**
   * Vulnerabilities with `evidence_id` have been linked to compliance evidence,
   * demonstrating remediation or acceptance for audit purposes.
   *
   * @param id - Unique identifier of the scan result to retrieve
   *
   * @returns Promise resolving to scan result with vulnerabilities
   * @returns response.success - Whether the request succeeded
   * @returns response.result - Full scan result object (summary data)
   * @returns response.vulnerabilities - Array of discovered vulnerabilities
   * @returns response.vulnerabilities[].id - Unique vulnerability finding identifier
   * @returns response.vulnerabilities[].host - Target host where vulnerability was found
   * @returns response.vulnerabilities[].port - Network port if applicable (null for non-network vulns)
   * @returns response.vulnerabilities[].service - Service name running on port (e.g., "ssh", "http")
   * @returns response.vulnerabilities[].protocol - Network protocol (tcp, udp, null)
   * @returns response.vulnerabilities[].vulnerability_id - CVE or vendor vulnerability ID (e.g., "CVE-2024-1234")
   * @returns response.vulnerabilities[].vulnerability_name - Human-readable vulnerability name
   * @returns response.vulnerabilities[].description - Detailed vulnerability description
   * @returns response.vulnerabilities[].severity - Severity level (critical, high, medium, low, info)
   * @returns response.vulnerabilities[].cvss_score - CVSS v3.x score (0.0-10.0)
   * @returns response.vulnerabilities[].cvss_vector - Full CVSS vector string
   * @returns response.vulnerabilities[].solution - Recommended remediation steps
   * @returns response.vulnerabilities[].reference_urls - JSON string of reference URLs (CVE links, vendor advisories)
   * @returns response.vulnerabilities[].is_false_positive - Whether marked as false positive (0 or 1)
   * @returns response.vulnerabilities[].marked_false_positive_by - User ID who marked as false positive
   * @returns response.vulnerabilities[].marked_false_positive_at - Unix timestamp when marked
   * @returns response.vulnerabilities[].false_positive_reason - Explanation for false positive determination
   * @returns response.vulnerabilities[].evidence_id - Linked evidence ID for compliance (null if unlinked)
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to view scanner results (403)
   * @throws {Error} If scan result not found (404)
   *
   * @example
   * // Load scan result details
   * const { result, vulnerabilities } = await api.getScanResult('result_123');
   * console.log(`Found ${vulnerabilities.length} vulnerabilities`);
   *
   * @example
   * // Filter vulnerabilities by severity
   * const { vulnerabilities } = await api.getScanResult('result_123');
   * const criticalAndHigh = vulnerabilities.filter(
   *   v => v.severity === 'critical' || v.severity === 'high'
   * );
   *
   * @example
   * // Poll for scan completion (after runScan)
   * async function waitForScanCompletion(scanResultId: string) {
   *   let attempts = 0;
   *   const maxAttempts = 60; // 5 minutes with 5s interval
   *
   *   while (attempts < maxAttempts) {
   *     const { result } = await api.getScanResult(scanResultId);
   *
   *     if (result.status === 'completed') {
   *       return result;
   *     }
   *
   *     if (result.status === 'failed') {
   *       throw new Error(`Scan failed: ${result.error_message}`);
   *     }
   *
   *     await new Promise(resolve => setTimeout(resolve, 5000));
   *     attempts++;
   *   }
   *
   *   throw new Error('Scan timed out');
   * }
   *
   * @example
   * // Scan result detail page with vulnerability table
   * function ScanResultDetail({ resultId }: { resultId: string }) {
   *   const [result, setResult] = useState<any>(null);
   *   const [vulnerabilities, setVulnerabilities] = useState([]);
   *   const [severityFilter, setSeverityFilter] = useState<string>();
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getScanResult(resultId)
   *       .then(res => {
   *         setResult(res.result);
   *         setVulnerabilities(res.vulnerabilities);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [resultId]);
   *
   *   const filteredVulns = vulnerabilities.filter(v =>
   *     !severityFilter || v.severity === severityFilter
   *   );
   *
   *   if (loading) return <Skeleton />;
   *   if (!result) return <NotFound />;
   *
   *   return (
   *     <div className="space-y-6">
   *       <Card>
   *         <CardHeader>
   *           <h2>{result.config_name}</h2>
   *           <StatusBadge status={result.status} />
   *         </CardHeader>
   *         <CardContent>
   *           <div className="grid grid-cols-4 gap-4">
   *             <Stat label="Critical" value={result.critical_count} variant="critical" />
   *             <Stat label="High" value={result.high_count} variant="high" />
   *             <Stat label="Medium" value={result.medium_count} variant="medium" />
   *             <Stat label="Low" value={result.low_count} variant="low" />
   *           </div>
   *         </CardContent>
   *       </Card>
   *
   *       <div className="flex items-center gap-4">
   *         <Select value={severityFilter} onValueChange={setSeverityFilter}>
   *           <SelectItem value="">All Severities</SelectItem>
   *           <SelectItem value="critical">Critical</SelectItem>
   *           <SelectItem value="high">High</SelectItem>
   *           <SelectItem value="medium">Medium</SelectItem>
   *           <SelectItem value="low">Low</SelectItem>
   *         </Select>
   *       </div>
   *
   *       <Table>
   *         <TableHeader>
   *           <TableRow>
   *             <TableHead>Vulnerability</TableHead>
   *             <TableHead>Host</TableHead>
   *             <TableHead>Severity</TableHead>
   *             <TableHead>CVSS</TableHead>
   *             <TableHead>Actions</TableHead>
   *           </TableRow>
   *         </TableHeader>
   *         <TableBody>
   *           {filteredVulns.map(vuln => (
   *             <TableRow
   *               key={vuln.id}
   *               className={vuln.is_false_positive ? 'opacity-50' : ''}
   *             >
   *               <TableCell>
   *                 <div>
   *                   <span className="font-medium">{vuln.vulnerability_name}</span>
   *                   {vuln.vulnerability_id && (
   *                     <Badge variant="outline" className="ml-2">
   *                       {vuln.vulnerability_id}
   *                     </Badge>
   *                   )}
   *                 </div>
   *               </TableCell>
   *               <TableCell>
   *                 {vuln.host}{vuln.port ? `:${vuln.port}` : ''}
   *               </TableCell>
   *               <TableCell>
   *                 <SeverityBadge severity={vuln.severity} />
   *               </TableCell>
   *               <TableCell>{vuln.cvss_score?.toFixed(1) || '-'}</TableCell>
   *               <TableCell>
   *                 <VulnerabilityActions vulnerability={vuln} />
   *               </TableCell>
   *             </TableRow>
   *           ))}
   *         </TableBody>
   *       </Table>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Generate remediation report grouped by severity
   * async function generateRemediationReport(resultId: string) {
   *   const { result, vulnerabilities } = await api.getScanResult(resultId);
   *
   *   // Exclude false positives
   *   const activeVulns = vulnerabilities.filter(v => !v.is_false_positive);
   *
   *   // Group by severity
   *   const bySeverity = activeVulns.reduce((acc, v) => {
   *     acc[v.severity] = acc[v.severity] || [];
   *     acc[v.severity].push(v);
   *     return acc;
   *   }, {} as Record<string, typeof vulnerabilities>);
   *
   *   return {
   *     scanDate: new Date(result.completed_at * 1000),
   *     configName: result.config_name,
   *     totalActive: activeVulns.length,
   *     bySeverity,
   *     actionItems: Object.entries(bySeverity)
   *       .flatMap(([severity, vulns]) =>
   *         vulns.map(v => ({
   *           priority: severity,
   *           host: v.host,
   *           issue: v.vulnerability_name,
   *           solution: v.solution
   *         }))
   *       )
   *   };
   * }
   *
   * @see {@link getScanResults} for listing all scan results
   * @see {@link runScan} for triggering a new scan
   * @see {@link markVulnerabilityFalsePositive} for managing false positives
   * @see {@link deleteScanResult} for removing old scan results
   */
  async getScanResult(id: string) {
    return this.request<{
      success: boolean
      result: any
      vulnerabilities: Array<{
        id: string
        host: string
        port: number | null
        service: string | null
        protocol: string | null
        vulnerability_id: string | null
        vulnerability_name: string
        description: string | null
        severity: string
        cvss_score: number | null
        cvss_vector: string | null
        solution: string | null
        reference_urls: string | null
        is_false_positive: number
        marked_false_positive_by: string | null
        marked_false_positive_at: number | null
        false_positive_reason: string | null
        evidence_id: string | null
      }>
    }>(`/api/scanners/results/${id}`)
  }

  /**
   * Gets AI-powered security analysis for a scan result.
   * Uses Cloudflare Workers AI to analyze vulnerabilities and provide insights.
   *
   * @param id - Scan result ID to analyze
   * @returns Promise with AI analysis including executive summary, key findings, and remediation priorities
   */
  async getScanAIAnalysis(id: string) {
    return this.request<{
      success: boolean
      analysis: {
        executive_summary: string
        risk_assessment: 'Critical' | 'High' | 'Medium' | 'Low' | 'Minimal'
        key_findings: string[]
        attack_vectors: string[]
        priority_remediation: Array<{
          issue: string
          action: string
          priority: 'Critical' | 'High' | 'Medium' | 'Low'
        }>
        security_posture: {
          strengths: string[]
          weaknesses: string[]
        }
        compliance_impact: string
      }
      metadata: {
        scan_id: string
        analyzed_at: string
        model: string
        vulns_analyzed: number
        hosts_analyzed: number
      }
    }>(`/api/scanners/results/${id}/ai-analysis`)
  }

  /**
   * Permanently deletes a single scan result and all associated vulnerabilities.
   *
   * This is a destructive operation that removes:
   * - The scan result record
   * - All discovered vulnerabilities from that scan
   * - Any false positive markings for those vulnerabilities
   *
   * **Use Cases:**
   * - Removing test scans from production data
   * - Cleaning up accidental duplicate scans
   * - Deleting sensitive scan data per data retention policies
   *
   * **Warning:** This operation cannot be undone. For bulk cleanup based on
   * age or status, use {@link cleanupScanResults} instead.
   *
   * **Alternative: Retention vs Deletion**
   * Consider whether you need to retain scan history for:
   * - Compliance audits requiring historical vulnerability data
   * - Trend analysis and remediation tracking
   * - Security posture reporting over time
   *
   * @param id - Unique identifier of the scan result to delete
   *
   * @returns Promise resolving to deletion confirmation
   * @returns response.success - Whether the deletion succeeded
   * @returns response.message - Confirmation message or error description
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to delete scanner results (403)
   * @throws {Error} If scan result not found (404)
   * @throws {Error} If scan is currently running and cannot be deleted (409)
   *
   * @example
   * // Delete a single scan result
   * await api.deleteScanResult('result_123');
   *
   * @example
   * // Delete with confirmation dialog
   * async function handleDeleteScanResult(resultId: string) {
   *   const confirmed = await showConfirmDialog({
   *     title: 'Delete Scan Result?',
   *     message: 'This will permanently delete the scan result and all discovered vulnerabilities.',
   *     confirmLabel: 'Delete',
   *     variant: 'destructive'
   *   });
   *
   *   if (confirmed) {
   *     try {
   *       await api.deleteScanResult(resultId);
   *       toast.success('Scan result deleted');
   *       router.push('/scanner/results');
   *     } catch (error) {
   *       toast.error('Failed to delete scan result');
   *     }
   *   }
   * }
   *
   * @example
   * // Delete button in scan results table
   * function ScanResultActions({ result }: { result: ScanResult }) {
   *   const [deleting, setDeleting] = useState(false);
   *
   *   const handleDelete = async () => {
   *     setDeleting(true);
   *     try {
   *       await api.deleteScanResult(result.id);
   *       onDelete?.(result.id); // Refresh parent list
   *     } catch (error) {
   *       toast.error('Failed to delete');
   *     } finally {
   *       setDeleting(false);
   *     }
   *   };
   *
   *   return (
   *     <DropdownMenu>
   *       <DropdownMenuTrigger asChild>
   *         <Button variant="ghost" size="icon">
   *           <MoreVertical className="h-4 w-4" />
   *         </Button>
   *       </DropdownMenuTrigger>
   *       <DropdownMenuContent>
   *         <DropdownMenuItem asChild>
   *           <Link href={`/scanner/results/${result.id}`}>
   *             View Details
   *           </Link>
   *         </DropdownMenuItem>
   *         <DropdownMenuSeparator />
   *         <DropdownMenuItem
   *           className="text-destructive"
   *           onClick={handleDelete}
   *           disabled={deleting || result.status === 'running'}
   *         >
   *           {deleting ? 'Deleting...' : 'Delete'}
   *         </DropdownMenuItem>
   *       </DropdownMenuContent>
   *     </DropdownMenu>
   *   );
   * }
   *
   * @see {@link cleanupScanResults} for bulk deletion based on criteria
   * @see {@link getScanResults} for listing all scan results
   * @see {@link getScanResult} for viewing scan details before deletion
   */
  async deleteScanResult(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/results/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Cancels a running or queued scan.
   *
   * This method requests cancellation of a scan that is currently running or
   * waiting in the queue. Cancelled scans will have their status updated to
   * 'cancelled' and any partial results may be preserved.
   *
   * @param id - The scan result ID to cancel
   *
   * @returns Promise resolving to cancellation result
   * @returns response.success - Whether the cancellation was successful
   * @returns response.message - Status message
   * @returns response.error - Error message if cancellation failed
   *
   * @example
   * const result = await api.cancelScan('scan_123');
   * if (result.success) {
   *   console.log('Scan cancelled successfully');
   * }
   */
  async cancelScan(id: string) {
    return this.request<{
      success: boolean
      message?: string
      error?: string
    }>(`/api/scanners/results/${id}/cancel`, {
      method: 'POST',
    })
  }

  /**
   * Bulk deletes scan results based on age, status, or configuration criteria.
   *
   * This method provides intelligent cleanup of old scan data to manage storage
   * and improve performance. It's particularly useful for implementing data
   * retention policies and removing obsolete vulnerability data.
   *
   * **Cleanup Criteria (all filters are combined with AND logic):**
   * - `older_than_days`: Delete results older than N days
   * - `status`: Delete only results with specific status (e.g., "failed", "cancelled")
   * - `config_id`: Delete results from a specific scan configuration
   *
   * **Data Retention Best Practices:**
   * - Keep successful scans for 90+ days for trend analysis
   * - Failed/cancelled scans can often be cleaned up after 30 days
   * - Consider compliance requirements (some regulations require 1+ year retention)
   * - Run cleanup during off-peak hours for large datasets
   *
   * **Warning:** This is a destructive bulk operation. Always preview the affected
   * results using {@link getScanResults} with the same filters before cleanup.
   *
   * @param params - Cleanup criteria (at least one filter recommended)
   * @param params.status - Only delete results with this status (queued, running, completed, failed, cancelled)
   * @param params.older_than_days - Only delete results older than this many days
   * @param params.config_id - Only delete results from this scan configuration
   *
   * @returns Promise resolving to cleanup summary
   * @returns response.success - Whether the cleanup operation succeeded
   * @returns response.deleted - Number of scan results deleted
   * @returns response.message - Summary message describing the cleanup
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to delete scanner results (403)
   * @throws {Error} If no filter criteria provided (400) - prevents accidental full wipe
   *
   * @example
   * // Clean up failed scans older than 30 days
   * const { deleted } = await api.cleanupScanResults({
   *   status: 'failed',
   *   older_than_days: 30
   * });
   * console.log(`Cleaned up ${deleted} failed scan results`);
   *
   * @example
   * // Clean up all results from a specific configuration
   * await api.cleanupScanResults({
   *   config_id: 'config_123',
   *   older_than_days: 90
   * });
   *
   * @example
   * // Implement data retention policy
   * async function enforceRetentionPolicy() {
   *   // Keep successful scans for 1 year
   *   await api.cleanupScanResults({
   *     status: 'completed',
   *     older_than_days: 365
   *   });
   *
   *   // Keep failed scans for 90 days
   *   await api.cleanupScanResults({
   *     status: 'failed',
   *     older_than_days: 90
   *   });
   *
   *   // Remove cancelled scans after 30 days
   *   await api.cleanupScanResults({
   *     status: 'cancelled',
   *     older_than_days: 30
   *   });
   * }
   *
   * @example
   * // Admin cleanup UI with preview
   * function ScanResultsCleanup() {
   *   const [status, setStatus] = useState<string>('failed');
   *   const [olderThanDays, setOlderThanDays] = useState(30);
   *   const [preview, setPreview] = useState<any[]>([]);
   *   const [cleaning, setCleaning] = useState(false);
   *
   *   // Preview what would be deleted
   *   const loadPreview = async () => {
   *     const cutoffDate = Date.now() / 1000 - (olderThanDays * 24 * 60 * 60);
   *     const { results } = await api.getScanResults({
   *       status,
   *       limit: 100
   *     });
   *     // Client-side filter by date (server cleanup will do this too)
   *     setPreview(results.filter(r => r.started_at < cutoffDate));
   *   };
   *
   *   const handleCleanup = async () => {
   *     const confirmed = await showConfirmDialog({
   *       title: 'Cleanup Scan Results?',
   *       message: `This will permanently delete ${preview.length}+ scan results.`,
   *       confirmLabel: 'Delete',
   *       variant: 'destructive'
   *     });
   *
   *     if (confirmed) {
   *       setCleaning(true);
   *       try {
   *         const { deleted } = await api.cleanupScanResults({
   *           status,
   *           older_than_days: olderThanDays
   *         });
   *         toast.success(`Deleted ${deleted} scan results`);
   *         setPreview([]);
   *       } catch (error) {
   *         toast.error('Cleanup failed');
   *       } finally {
   *         setCleaning(false);
   *       }
   *     }
   *   };
   *
   *   return (
   *     <Card>
   *       <CardHeader>
   *         <h3>Cleanup Scan Results</h3>
   *       </CardHeader>
   *       <CardContent className="space-y-4">
   *         <div className="grid grid-cols-2 gap-4">
   *           <div>
   *             <Label>Status</Label>
   *             <Select value={status} onValueChange={setStatus}>
   *               <SelectItem value="failed">Failed</SelectItem>
   *               <SelectItem value="cancelled">Cancelled</SelectItem>
   *               <SelectItem value="completed">Completed</SelectItem>
   *             </Select>
   *           </div>
   *           <div>
   *             <Label>Older than (days)</Label>
   *             <Input
   *               type="number"
   *               value={olderThanDays}
   *               onChange={e => setOlderThanDays(Number(e.target.value))}
   *             />
   *           </div>
   *         </div>
   *
   *         <div className="flex gap-2">
   *           <Button variant="outline" onClick={loadPreview}>
   *             Preview ({preview.length} results)
   *           </Button>
   *           <Button
   *             variant="destructive"
   *             onClick={handleCleanup}
   *             disabled={cleaning || preview.length === 0}
   *           >
   *             {cleaning ? 'Cleaning...' : 'Cleanup Now'}
   *           </Button>
   *         </div>
   *
   *         {preview.length > 0 && (
   *           <Alert>
   *             <AlertDescription>
   *               {preview.length} scan results will be deleted.
   *             </AlertDescription>
   *           </Alert>
   *         )}
   *       </CardContent>
   *     </Card>
   *   );
   * }
   *
   * @example
   * // Scheduled cleanup job (server-side or cron)
   * async function scheduledCleanup() {
   *   const results = await Promise.all([
   *     // Clean up stale running scans (likely zombie processes)
   *     api.cleanupScanResults({
   *       status: 'running',
   *       older_than_days: 1
   *     }),
   *     // Clean up old failed scans
   *     api.cleanupScanResults({
   *       status: 'failed',
   *       older_than_days: 30
   *     }),
   *     // Clean up very old completed scans
   *     api.cleanupScanResults({
   *       status: 'completed',
   *       older_than_days: 180
   *     })
   *   ]);
   *
   *   const totalDeleted = results.reduce((sum, r) => sum + r.deleted, 0);
   *   console.log(`Scheduled cleanup deleted ${totalDeleted} scan results`);
   * }
   *
   * @see {@link deleteScanResult} for single result deletion
   * @see {@link getScanResults} for previewing results before cleanup
   * @see {@link getScannerStats} for monitoring overall scan result volume
   */
  async cleanupScanResults(params: {
    status?: string
    older_than_days?: number
    config_id?: string
  }) {
    return this.request<{
      success: boolean
      deleted: number
      message: string
    }>('/api/scanners/results/cleanup', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  // Vulnerabilities
  /**
   * Marks or unmarks a vulnerability as a false positive.
   *
   * False positives are vulnerability detections that, upon investigation, are
   * determined not to be actual security issues. Common reasons include:
   * - **Configuration variance**: The detected issue doesn't apply to the deployment
   * - **Compensating controls**: Other security measures mitigate the vulnerability
   * - **Scanner error**: The scanner misidentified a pattern as a vulnerability
   * - **Accepted risk**: The vulnerability is acknowledged but accepted by the organization
   *
   * **False Positive Workflow:**
   * 1. Security analyst reviews vulnerability in {@link getScanResult} details
   * 2. Analyst investigates the finding (code review, manual testing, etc.)
   * 3. If determined to be false positive, mark it with this method and provide reason
   * 4. The vulnerability remains in history but is excluded from active counts
   * 5. Future scans may still detect it, but the false positive flag persists
   *
   * **Best Practices:**
   * - Always provide a detailed reason for documentation and audit purposes
   * - Review false positives periodically - scanner rules may improve
   * - Consider if the issue could become valid with configuration changes
   * - Document compensating controls if marking as false positive for that reason
   *
   * **Audit Trail:** All false positive decisions are logged in the audit trail,
   * including who made the decision, when, and the provided reason. This is
   * essential for compliance audits (SOC 2, ISO 27001, etc.).
   *
   * @param id - The unique identifier of the vulnerability to update
   * @param data - False positive status and optional reason
   * @param data.is_false_positive - Set to true to mark as false positive, false to unmark
   * @param data.false_positive_reason - Explanation for why this is a false positive (required when is_false_positive=true)
   *
   * @returns Promise resolving to operation result
   * @returns response.success - Whether the update succeeded
   * @returns response.message - Confirmation message
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to manage vulnerabilities (403)
   * @throws {Error} If vulnerability not found (404)
   * @throws {Error} If reason not provided when marking as false positive (400)
   *
   * @example
   * // Mark a vulnerability as a false positive
   * await api.markVulnerabilityFalsePositive('vuln_abc123', {
   *   is_false_positive: true,
   *   false_positive_reason: 'This endpoint is internal-only and protected by VPN access control'
   * });
   *
   * @example
   * // Unmark a false positive (e.g., after scanner rule improvement)
   * await api.markVulnerabilityFalsePositive('vuln_abc123', {
   *   is_false_positive: false
   * });
   *
   * @example
   * // React component for false positive toggle
   * function VulnerabilityFalsePositiveToggle({
   *   vulnerability,
   *   onUpdate
   * }: {
   *   vulnerability: { id: string; is_false_positive: boolean };
   *   onUpdate: () => void;
   * }) {
   *   const [reason, setReason] = useState('');
   *   const [showReasonInput, setShowReasonInput] = useState(false);
   *   const [saving, setSaving] = useState(false);
   *
   *   const handleToggle = async () => {
   *     if (!vulnerability.is_false_positive && !showReasonInput) {
   *       // Show reason input before marking as false positive
   *       setShowReasonInput(true);
   *       return;
   *     }
   *
   *     setSaving(true);
   *     try {
   *       await api.markVulnerabilityFalsePositive(vulnerability.id, {
   *         is_false_positive: !vulnerability.is_false_positive,
   *         false_positive_reason: reason || undefined
   *       });
   *       onUpdate();
   *       setShowReasonInput(false);
   *       setReason('');
   *     } finally {
   *       setSaving(false);
   *     }
   *   };
   *
   *   return (
   *     <div>
   *       {showReasonInput && (
   *         <textarea
   *           value={reason}
   *           onChange={e => setReason(e.target.value)}
   *           placeholder="Explain why this is a false positive..."
   *           required
   *         />
   *       )}
   *       <button onClick={handleToggle} disabled={saving}>
   *         {vulnerability.is_false_positive ? 'Unmark False Positive' : 'Mark as False Positive'}
   *       </button>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Bulk false positive management with filtering
   * async function bulkMarkFalsePositives(
   *   vulnerabilityIds: string[],
   *   reason: string
   * ) {
   *   const results = await Promise.allSettled(
   *     vulnerabilityIds.map(id =>
   *       api.markVulnerabilityFalsePositive(id, {
   *         is_false_positive: true,
   *         false_positive_reason: reason
   *       })
   *     )
   *   );
   *
   *   const succeeded = results.filter(r => r.status === 'fulfilled').length;
   *   const failed = results.filter(r => r.status === 'rejected').length;
   *
   *   return { succeeded, failed };
   * }
   *
   * @see {@link getScanResult} for viewing vulnerability details and false positive status
   * @see {@link getScanResults} for listing scan results with vulnerability counts
   * @see {@link getScannerStats} for aggregate statistics including false positive counts
   */
  async markVulnerabilityFalsePositive(id: string, data: {
    is_false_positive: boolean
    false_positive_reason?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/vulnerabilities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Scanner Stats
  /**
   * Retrieves aggregate statistics for the vulnerability scanning system.
   *
   * This method provides a comprehensive overview of the organization's scanning
   * activity and vulnerability landscape. It's essential for security dashboards,
   * executive reporting, and monitoring scanner health.
   *
   * **Statistics Returned:**
   *
   * **Configuration Overview:**
   * - `configs_count`: Total number of active scan configurations
   *
   * **Recent Scan Activity:**
   * - `total`: Total scans executed (typically last 30 days)
   * - `completed`: Successfully completed scans
   * - `failed`: Scans that encountered errors
   * - `total_vulnerabilities`: Sum of all vulnerabilities found
   * - `total_critical`: Critical severity vulnerabilities (CVSS 9.0-10.0)
   * - `total_high`: High severity vulnerabilities (CVSS 7.0-8.9)
   *
   * **Vulnerability Breakdown by Severity:**
   * Detailed counts for each severity level:
   * - `severity`: Severity level (critical, high, medium, low, info)
   * - `count`: Total vulnerabilities at this severity
   * - `false_positives`: Number marked as false positives (excluded from active counts)
   *
   * **Severity Level CVSS Ranges:**
   * - **Critical** (9.0-10.0): Immediate action required
   * - **High** (7.0-8.9): Address within days
   * - **Medium** (4.0-6.9): Address within weeks
   * - **Low** (0.1-3.9): Address when convenient
   * - **Info** (0.0): Informational findings
   *
   * **Common Use Cases:**
   * - Security posture dashboards
   * - Executive KPI reporting
   * - Trend analysis (compare with historical data)
   * - SLA compliance monitoring
   * - Resource allocation planning
   *
   * @returns Promise resolving to scanner statistics
   * @returns response.success - Whether the request succeeded
   * @returns response.stats - The aggregate statistics object
   * @returns response.stats.configs_count - Number of scan configurations
   * @returns response.stats.recent_scans - Recent scan activity metrics
   * @returns response.stats.recent_scans.total - Total scans in period
   * @returns response.stats.recent_scans.completed - Successfully completed scans
   * @returns response.stats.recent_scans.failed - Failed scans
   * @returns response.stats.recent_scans.total_vulnerabilities - Total vulnerabilities found
   * @returns response.stats.recent_scans.total_critical - Critical severity count
   * @returns response.stats.recent_scans.total_high - High severity count
   * @returns response.stats.vulnerability_breakdown - Array of severity breakdown objects
   * @returns response.stats.vulnerability_breakdown[].severity - Severity level name
   * @returns response.stats.vulnerability_breakdown[].count - Total count at this severity
   * @returns response.stats.vulnerability_breakdown[].false_positives - False positive count
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient permissions to view scanner statistics (403)
   *
   * @example
   * // Basic statistics retrieval
   * const { stats } = await api.getScannerStats();
   * console.log(`Active scan configs: ${stats.configs_count}`);
   * console.log(`Total vulnerabilities: ${stats.recent_scans.total_vulnerabilities}`);
   * console.log(`Critical issues: ${stats.recent_scans.total_critical}`);
   *
   * @example
   * // Security dashboard widget
   * function VulnerabilityOverview() {
   *   const [stats, setStats] = useState<any>(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getScannerStats()
   *       .then(({ stats }) => setStats(stats))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   const { recent_scans } = stats;
   *   const successRate = recent_scans.total > 0
   *     ? ((recent_scans.completed / recent_scans.total) * 100).toFixed(1)
   *     : 'N/A';
   *
   *   return (
   *     <div className="grid grid-cols-3 gap-4">
   *       <StatCard
   *         label="Scan Success Rate"
   *         value={`${successRate}%`}
   *         trend={recent_scans.failed > 5 ? 'warning' : 'good'}
   *       />
   *       <StatCard
   *         label="Critical Vulnerabilities"
   *         value={recent_scans.total_critical}
   *         trend={recent_scans.total_critical > 0 ? 'critical' : 'good'}
   *       />
   *       <StatCard
   *         label="High Vulnerabilities"
   *         value={recent_scans.total_high}
   *         trend={recent_scans.total_high > 10 ? 'warning' : 'good'}
   *       />
   *     </div>
   *   );
   * }
   *
   * @example
   * // Vulnerability severity distribution chart
   * function VulnerabilitySeverityChart() {
   *   const [breakdown, setBreakdown] = useState<any[]>([]);
   *
   *   useEffect(() => {
   *     api.getScannerStats().then(({ stats }) => {
   *       setBreakdown(stats.vulnerability_breakdown);
   *     });
   *   }, []);
   *
   *   // Calculate active (non-false-positive) counts
   *   const chartData = breakdown.map(item => ({
   *     severity: item.severity,
   *     active: item.count - item.false_positives,
   *     falsePositives: item.false_positives,
   *     total: item.count
   *   }));
   *
   *   return (
   *     <BarChart data={chartData}>
   *       <XAxis dataKey="severity" />
   *       <YAxis />
   *       <Bar dataKey="active" fill="#ef4444" name="Active" />
   *       <Bar dataKey="falsePositives" fill="#9ca3af" name="False Positives" />
   *     </BarChart>
   *   );
   * }
   *
   * @example
   * // Executive security report generation
   * async function generateSecurityReport() {
   *   const { stats } = await api.getScannerStats();
   *   const { recent_scans, vulnerability_breakdown } = stats;
   *
   *   // Calculate false positive rate
   *   const totalFP = vulnerability_breakdown.reduce(
   *     (sum, item) => sum + item.false_positives,
   *     0
   *   );
   *   const fpRate = recent_scans.total_vulnerabilities > 0
   *     ? (totalFP / recent_scans.total_vulnerabilities * 100).toFixed(1)
   *     : 0;
   *
   *   return {
   *     summary: {
   *       totalScans: recent_scans.total,
   *       scanSuccessRate: `${((recent_scans.completed / recent_scans.total) * 100).toFixed(1)}%`,
   *       totalVulnerabilities: recent_scans.total_vulnerabilities,
   *       criticalAndHigh: recent_scans.total_critical + recent_scans.total_high,
   *       falsePositiveRate: `${fpRate}%`
   *     },
   *     breakdown: vulnerability_breakdown.map(item => ({
   *       severity: item.severity,
   *       count: item.count - item.false_positives, // Active only
   *       falsePositives: item.false_positives
   *     }))
   *   };
   * }
   *
   * @example
   * // Scanner health monitoring
   * function ScannerHealthWidget() {
   *   const [stats, setStats] = useState<any>(null);
   *
   *   useEffect(() => {
   *     const loadStats = () => api.getScannerStats()
   *       .then(({ stats }) => setStats(stats));
   *
   *     loadStats();
   *     // Refresh every 5 minutes
   *     const interval = setInterval(loadStats, 5 * 60 * 1000);
   *     return () => clearInterval(interval);
   *   }, []);
   *
   *   if (!stats) return null;
   *
   *   const failureRate = stats.recent_scans.total > 0
   *     ? (stats.recent_scans.failed / stats.recent_scans.total) * 100
   *     : 0;
   *
   *   // Alert if >20% failure rate
   *   const healthStatus = failureRate > 20 ? 'unhealthy' : 'healthy';
   *
   *   return (
   *     <div className={`health-indicator ${healthStatus}`}>
   *       <span>Scanner Health: {healthStatus}</span>
   *       <span>{stats.configs_count} active configs</span>
   *       <span>{stats.recent_scans.completed}/{stats.recent_scans.total} scans succeeded</span>
   *     </div>
   *   );
   * }
   *
   * @see {@link getScanConfigs} for viewing individual scan configuration details
   * @see {@link getScanResults} for detailed scan result history
   * @see {@link markVulnerabilityFalsePositive} for managing false positive classifications
   * @see {@link runScan} for triggering new vulnerability scans
   */
  async getScannerStats() {
    return this.request<{
      success: boolean
      stats: {
        configs_count: number
        recent_scans: {
          total: number
          completed: number
          failed: number
          total_vulnerabilities: number
          total_critical: number
          total_high: number
          total_medium: number
        }
        vulnerability_breakdown: Array<{
          severity: string
          count: number
          false_positives: number
        }>
      }
    }>('/api/scanners/stats')
  }

  // ============================================================================
  // ASSET DISCOVERY AND INVENTORY MANAGEMENT (#128)
  // ============================================================================

  /**
   * Get list of assets with filtering and pagination.
   */
  async getAssets(params?: {
    search?: string
    asset_type?: string
    status?: string
    criticality?: string
    environment?: string
    owner_id?: string
    has_vulnerabilities?: 'true' | 'false'
    stale_days?: number
    sort_by?: string
    sort_order?: 'ASC' | 'DESC'
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value))
        }
      })
    }
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      assets: Asset[]
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/assets${query ? `?${query}` : ''}`)
  }

  /**
   * Get asset statistics for dashboard.
   */
  async getAssetStats() {
    return this.request<{
      success: boolean
      stats: {
        total: number
        by_type: Record<string, number>
        by_status: Record<string, number>
        by_criticality: Record<string, number>
        by_environment: Record<string, number>
        stale_count: number
        with_vulnerabilities: number
      }
    }>('/api/assets/stats')
  }

  /**
   * Get a single asset with full details.
   */
  async getAsset(id: string) {
    return this.request<{
      success: boolean
      asset: Asset
      vulnerabilities: Array<{
        id: string
        vulnerability_id: string
        vulnerability_name: string
        severity: string
        cvss_score: number | null
        host: string
        port: number | null
        service: string | null
        description: string | null
        first_found: number
        last_found: number
        times_found: number
        is_resolved: number
      }>
      scan_history: Array<{
        id: string
        scan_result_id: string
        ports_snapshot: Array<{ port: number; protocol: string; service: string }>
        os_snapshot: string | null
        new_ports: Array<{ port: number; protocol: string; service: string }>
        closed_ports: Array<{ port: number; protocol: string; service: string }>
        scanned_at: number
        scan_status: string
      }>
    }>(`/api/assets/${id}`)
  }

  /**
   * Create a new asset manually.
   */
  async createAsset(data: {
    hostname?: string
    ip_address?: string
    mac_address?: string
    asset_type?: string
    os?: string
    os_family?: string
    environment?: string
    ports?: Array<{ port: number; protocol: string; service: string }>
    criticality?: string
    business_unit?: string
    owner_id?: string
    tags?: string[]
    notes?: string
    custom_fields?: Record<string, any>
    status?: string
  }) {
    return this.request<{
      success: boolean
      asset: { id: string; hostname: string | null; ip_address: string | null }
      message: string
    }>('/api/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an asset.
   */
  async updateAsset(id: string, data: Partial<{
    hostname: string
    ip_address: string
    mac_address: string
    asset_type: string
    os: string
    os_family: string
    environment: string
    criticality: string
    business_unit: string
    owner_id: string
    notes: string
    status: string
    tags: string[]
    custom_fields: Record<string, any>
    ports: Array<{ port: number; protocol: string; service: string }>
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete an asset.
   */
  async deleteAsset(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assets/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Add tags to an asset.
   */
  async addAssetTags(id: string, tags: string[]) {
    return this.request<{
      success: boolean
      tags: string[]
    }>(`/api/assets/${id}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tags }),
    })
  }

  /**
   * Remove a tag from an asset.
   */
  async removeAssetTag(id: string, tag: string) {
    return this.request<{
      success: boolean
      tags: string[]
    }>(`/api/assets/${id}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get vulnerabilities for an asset.
   */
  async getAssetVulnerabilities(id: string, params?: {
    show_resolved?: boolean
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.show_resolved) searchParams.append('show_resolved', 'true')
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.offset) searchParams.append('offset', String(params.offset))
    const query = searchParams.toString()
    return this.request<{
      success: boolean
      vulnerabilities: Array<{
        id: string
        vulnerability_id: string
        vulnerability_name: string
        severity: string
        cvss_score: number | null
        host: string
        port: number | null
        service: string | null
        description: string | null
        first_found: number
        last_found: number
        times_found: number
        is_resolved: number
        resolved_at: number | null
        resolved_by: string | null
      }>
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/assets/${id}/vulnerabilities${query ? `?${query}` : ''}`)
  }

  /**
   * Mark a vulnerability as resolved on an asset.
   */
  async resolveAssetVulnerability(assetId: string, vulnerabilityId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assets/${assetId}/vulnerabilities/${vulnerabilityId}/resolve`, {
      method: 'POST',
    })
  }

  /**
   * Mark a vulnerability as unresolved on an asset.
   */
  async unresolveAssetVulnerability(assetId: string, vulnerabilityId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/assets/${assetId}/vulnerabilities/${vulnerabilityId}/unresolve`, {
      method: 'POST',
    })
  }

  // ============================================================================
  // Vulnerability-Control Linking (#240)
  // ============================================================================

  /**
   * Get controls linked to a vulnerability
   */
  async getVulnerabilityLinkedControls(vulnerabilityId: string) {
    return this.request<{
      success: boolean
      vulnerability_id: string
      linked_controls: Array<{
        control_id: string
        linked_at: number
        notes: string | null
        control_number: string
        control_name: string
        control_description: string | null
        control_status: string
        framework_name: string
        linked_by_username: string | null
      }>
    }>(`/api/scanners/vulnerabilities/${vulnerabilityId}/controls`)
  }

  /**
   * Link a control to a vulnerability
   */
  async linkVulnerabilityControl(vulnerabilityId: string, data: {
    control_id: string
    notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      link?: {
        vulnerability_id: string
        control_id: string
        linked_at: number
      }
    }>(`/api/scanners/vulnerabilities/${vulnerabilityId}/controls`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Unlink a control from a vulnerability
   */
  async unlinkVulnerabilityControl(vulnerabilityId: string, controlId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/vulnerabilities/${vulnerabilityId}/controls/${controlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get suggested controls for a vulnerability based on its type/tags
   */
  async getVulnerabilitySuggestedControls(vulnerabilityId: string, limit?: number) {
    const params = new URLSearchParams()
    if (limit) params.set('limit', limit.toString())
    const queryString = params.toString()
    return this.request<{
      success: boolean
      vulnerability_id: string
      vulnerability_name: string
      suggested_controls: Array<{
        id: string
        control_number: string
        name: string
        description: string | null
        status: string
        frequency: string
        framework_name: string
        relevance_score: number
      }>
      patterns_matched: string[]
    }>(`/api/scanners/vulnerabilities/${vulnerabilityId}/suggested-controls${queryString ? '?' + queryString : ''}`)
  }

  /**
   * Bulk link a control to multiple vulnerabilities
   */
  async bulkLinkVulnerabilitiesControl(data: {
    vulnerability_ids: string[]
    control_id: string
    notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      linked: number
      skipped: number
    }>('/api/scanners/vulnerabilities/bulk-link-controls', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get vulnerabilities linked to a control
   */
  async getControlLinkedVulnerabilities(controlId: string, params?: {
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    const queryString = searchParams.toString()
    return this.request<{
      success: boolean
      control: {
        id: string
        control_number: string
        name: string
      }
      vulnerabilities: Array<{
        vulnerability_id: string
        linked_at: number
        link_notes: string | null
        host: string
        port: number | null
        service: string | null
        vuln_cve_id: string | null
        vulnerability_name: string
        description: string | null
        severity: string
        cvss_score: number | null
        is_false_positive: number
        solution: string | null
        source: string | null
        scan_result_id: string
        scan_config_name: string
        scan_date: number
        linked_by_username: string | null
      }>
      severity_breakdown: Record<string, number>
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/controls/${controlId}/vulnerabilities${queryString ? '?' + queryString : ''}`)
  }

  // ============================================================================
  // POC Validation endpoints
  // ============================================================================

  // Credits
  /**
   * Retrieves the current POC (Proof of Concept) validation credit balance.
   *
   * POC validation is a premium feature that allows organizations to automatically
   * test whether detected vulnerabilities are actually exploitable. Each validation
   * consumes credits from the organization's balance.
   *
   * **Credit System Overview:**
   * - Credits are purchased in packages via {@link getPocPackages}
   * - Each vulnerability validation consumes 1 credit
   * - Credits do not expire and carry over month-to-month
   * - Balance is shared across all users in the organization
   *
   * **Use Cases:**
   * - Display remaining credits in the UI header or dashboard
   * - Check balance before initiating validations
   * - Trigger low-balance warnings to prompt purchases
   * - Track credit consumption for budgeting purposes
   *
   * @returns Promise resolving to current credit balance
   * @returns {boolean} success - Whether the request was successful
   * @returns {number} balance - Current credit balance (non-negative integer)
   * @returns {number} updated_at - Unix timestamp of last balance change
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If POC validation feature is not enabled for the organization (403)
   *
   * @example
   * // Basic credit balance retrieval
   * const { balance, updated_at } = await api.getPocCredits();
   * console.log(`Current balance: ${balance} credits`);
   * console.log(`Last updated: ${new Date(updated_at * 1000).toLocaleString()}`);
   *
   * @example
   * // Credit balance header indicator
   * function CreditBalanceIndicator() {
   *   const [credits, setCredits] = useState<number | null>(null);
   *
   *   useEffect(() => {
   *     api.getPocCredits()
   *       .then(({ balance }) => setCredits(balance))
   *       .catch(() => setCredits(null)); // Feature may not be enabled
   *   }, []);
   *
   *   if (credits === null) return null;
   *
   *   return (
   *     <div className={`credit-badge ${credits < 10 ? 'low' : ''}`}>
   *       <CreditIcon />
   *       <span>{credits} credits</span>
   *       {credits < 10 && <span className="warning">Low balance</span>}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Pre-validation balance check
   * async function validateWithBalanceCheck(vulnerabilityId: string) {
   *   const { balance } = await api.getPocCredits();
   *
   *   if (balance === 0) {
   *     throw new Error('No credits remaining. Please purchase more credits.');
   *   }
   *
   *   if (balance < 5) {
   *     console.warn('Low credit balance. Consider purchasing more credits.');
   *   }
   *
   *   return api.validatePoc(vulnerabilityId);
   * }
   *
   * @example
   * // Dashboard credit widget with purchase CTA
   * function CreditWidget() {
   *   const [data, setData] = useState<{ balance: number; updated_at: number } | null>(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getPocCredits()
   *       .then(setData)
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *   if (!data) return <div>POC Validation not available</div>;
   *
   *   const lowBalance = data.balance < 10;
   *
   *   return (
   *     <Card className={lowBalance ? 'border-warning' : ''}>
   *       <h3>POC Validation Credits</h3>
   *       <div className="balance-display">
   *         <span className="amount">{data.balance}</span>
   *         <span className="label">credits remaining</span>
   *       </div>
   *       {lowBalance && (
   *         <Link to="/settings/credits" className="btn btn-primary">
   *           Purchase Credits
   *         </Link>
   *       )}
   *     </Card>
   *   );
   * }
   *
   * @see {@link getPocPackages} for available credit packages to purchase
   * @see {@link addPocCredits} for adding credits to the balance
   * @see {@link getPocTransactions} for credit transaction history
   * @see {@link validatePoc} for consuming credits to validate vulnerabilities
   */
  async getPocCredits() {
    return this.request<{
      success: boolean
      balance: number
      updated_at: number
    }>('/api/poc/credits')
  }

  /**
   * Retrieves available POC validation credit packages for purchase.
   *
   * Credit packages allow organizations to purchase bulk POC validation credits
   * at discounted rates. Larger packages typically offer better per-credit pricing
   * to encourage bulk purchases.
   *
   * **Package Structure:**
   * Each package includes:
   * - `id`: Unique package identifier for purchase transactions
   * - `name`: Human-readable package name (e.g., "Starter", "Professional", "Enterprise")
   * - `credits`: Number of credits included in the package
   * - `price`: Total price in dollars (for display)
   * - `price_cents`: Total price in cents (for payment processing)
   * - `per_credit`: Formatted per-credit cost (e.g., "$0.50/credit")
   *
   * **Typical Package Tiers:**
   * - **Starter** (~10 credits): For testing the feature
   * - **Professional** (~50 credits): For regular validation needs
   * - **Enterprise** (~200+ credits): For high-volume security operations
   *
   * **Usage Notes:**
   * - Package availability may vary by subscription tier
   * - Prices are subject to change; always fetch fresh data
   * - Use `price_cents` for payment processing to avoid floating-point issues
   *
   * @returns Promise resolving to available credit packages
   * @returns {boolean} success - Whether the request was successful
   * @returns {Array} packages - Array of available credit packages
   * @returns {string} packages[].id - Unique package identifier
   * @returns {string} packages[].name - Package display name
   * @returns {number} packages[].credits - Number of credits in the package
   * @returns {number} packages[].price - Price in dollars (for display)
   * @returns {number} packages[].price_cents - Price in cents (for payment)
   * @returns {string} packages[].per_credit - Formatted per-credit cost
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If POC validation feature is not enabled for the organization (403)
   *
   * @example
   * // Basic package listing
   * const { packages } = await api.getPocPackages();
   * packages.forEach(pkg => {
   *   console.log(`${pkg.name}: ${pkg.credits} credits for $${pkg.price} (${pkg.per_credit})`);
   * });
   *
   * @example
   * // Credit package purchase page
   * function CreditPackagePurchase() {
   *   const [packages, setPackages] = useState<any[]>([]);
   *   const [selectedId, setSelectedId] = useState<string | null>(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getPocPackages()
   *       .then(({ packages }) => setPackages(packages))
   *       .finally(() => setLoading(false));
   *   }, []);
   *
   *   if (loading) return <Spinner />;
   *
   *   return (
   *     <div className="package-grid">
   *       {packages.map(pkg => (
   *         <Card
   *           key={pkg.id}
   *           className={selectedId === pkg.id ? 'selected' : ''}
   *           onClick={() => setSelectedId(pkg.id)}
   *         >
   *           <h3>{pkg.name}</h3>
   *           <div className="credits">{pkg.credits} credits</div>
   *           <div className="price">${pkg.price}</div>
   *           <div className="per-credit">{pkg.per_credit}</div>
   *         </Card>
   *       ))}
   *       <Button
   *         disabled={!selectedId}
   *         onClick={() => handlePurchase(selectedId)}
   *       >
   *         Purchase Credits
   *       </Button>
   *     </div>
   *   );
   * }
   *
   * @example
   * // Find best value package
   * async function getBestValuePackage() {
   *   const { packages } = await api.getPocPackages();
   *
   *   // Sort by per-credit cost (parse from string like "$0.50/credit")
   *   const sorted = packages.sort((a, b) => {
   *     const costA = parseFloat(a.per_credit.replace(/[^0-9.]/g, ''));
   *     const costB = parseFloat(b.per_credit.replace(/[^0-9.]/g, ''));
   *     return costA - costB;
   *   });
   *
   *   return sorted[0]; // Best value (lowest per-credit cost)
   * }
   *
   * @example
   * // Package comparison with savings calculation
   * function PackageComparison() {
   *   const [packages, setPackages] = useState<any[]>([]);
   *
   *   useEffect(() => {
   *     api.getPocPackages().then(({ packages }) => setPackages(packages));
   *   }, []);
   *
   *   if (packages.length === 0) return null;
   *
   *   // Calculate savings relative to the smallest package
   *   const baseRate = packages[0]?.price / packages[0]?.credits || 0;
   *
   *   return (
   *     <table>
   *       <thead>
   *         <tr>
   *           <th>Package</th>
   *           <th>Credits</th>
   *           <th>Price</th>
   *           <th>Per Credit</th>
   *           <th>Savings</th>
   *         </tr>
   *       </thead>
   *       <tbody>
   *         {packages.map(pkg => {
   *           const effectiveRate = pkg.price / pkg.credits;
   *           const savings = ((baseRate - effectiveRate) / baseRate * 100).toFixed(0);
   *           return (
   *             <tr key={pkg.id}>
   *               <td>{pkg.name}</td>
   *               <td>{pkg.credits}</td>
   *               <td>${pkg.price}</td>
   *               <td>{pkg.per_credit}</td>
   *               <td>{savings > 0 ? `${savings}% off` : '-'}</td>
   *             </tr>
   *           );
   *         })}
   *       </tbody>
   *     </table>
   *   );
   * }
   *
   * @see {@link getPocCredits} for checking current credit balance
   * @see {@link addPocCredits} for adding credits after purchase
   * @see {@link getPocTransactions} for credit transaction history
   */
  async getPocPackages() {
    return this.request<{
      success: boolean
      packages: Array<{
        id: string
        name: string
        credits: number
        price: number
        price_cents: number
        per_credit: string
      }>
    }>('/api/poc/packages')
  }

  /**
   * Adds POC validation credits to the organization's balance.
   *
   * This method is typically called after a successful payment transaction to
   * fulfill a credit package purchase. It adds the specified number of credits
   * to the organization's balance and creates an auditable transaction record.
   *
   * **Transaction Tracking:**
   * Each credit addition generates a transaction record with:
   * - `transaction_id`: Unique identifier for the transaction
   * - `amount_added`: Credits added in this transaction
   * - `new_balance`: Updated total balance after addition
   * - Timestamp and optional reference for reconciliation
   *
   * **Common Use Cases:**
   * - Fulfill credit package purchases after payment
   * - Add promotional or trial credits
   * - Apply credits from support cases or refunds
   * - Bulk credit allocation for enterprise agreements
   *
   * **Security Considerations:**
   * - This endpoint requires administrative privileges
   * - All credit additions are logged in the audit trail
   * - The reference_id should link to payment records for reconciliation
   *
   * @param data - Credit addition parameters
   * @param data.amount - Number of credits to add (must be positive integer)
   * @param data.description - Optional human-readable description (e.g., "Professional Package Purchase")
   * @param data.reference_id - Optional external reference (e.g., Stripe payment_intent ID, invoice number)
   *
   * @returns Promise resolving to credit addition result
   * @returns {boolean} success - Whether the credits were added successfully
   * @returns {string} transaction_id - Unique transaction identifier for this addition
   * @returns {number} amount_added - Number of credits added
   * @returns {number} new_balance - Updated credit balance after addition
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If user lacks permission to add credits (403)
   * @throws {Error} If amount is invalid (negative or non-integer) (400)
   *
   * @example
   * // Basic credit addition
   * const { transaction_id, new_balance } = await api.addPocCredits({
   *   amount: 50,
   *   description: 'Professional Package Purchase',
   *   reference_id: 'pi_1234567890'
   * });
   * console.log(`Added credits. Transaction: ${transaction_id}, New balance: ${new_balance}`);
   *
   * @example
   * // Post-payment credit fulfillment
   * async function fulfillCreditPurchase(paymentIntent: any, packageDetails: any) {
   *   try {
   *     const result = await api.addPocCredits({
   *       amount: packageDetails.credits,
   *       description: `${packageDetails.name} Purchase`,
   *       reference_id: paymentIntent.id
   *     });
   *
   *     // Update UI to show new balance
   *     showSuccess(`Successfully added ${result.amount_added} credits!`);
   *     updateCreditBalance(result.new_balance);
   *
   *     // Log for reconciliation
   *     console.log('Credit fulfillment:', {
   *       transaction: result.transaction_id,
   *       payment: paymentIntent.id,
   *       credits: result.amount_added
   *     });
   *
   *     return result;
   *   } catch (error) {
   *     // Handle failed fulfillment - may need support ticket
   *     console.error('Credit fulfillment failed:', error);
   *     showError('Failed to add credits. Please contact support.');
   *     throw error;
   *   }
   * }
   *
   * @example
   * // Admin promotional credits form
   * function AddPromotionalCredits({ onSuccess }: { onSuccess: () => void }) {
   *   const [amount, setAmount] = useState<number>(10);
   *   const [description, setDescription] = useState<string>('');
   *   const [loading, setLoading] = useState(false);
   *
   *   const handleSubmit = async (e: React.FormEvent) => {
   *     e.preventDefault();
   *     setLoading(true);
   *
   *     try {
   *       const result = await api.addPocCredits({
   *         amount,
   *         description: description || 'Promotional credits',
   *         reference_id: `promo-${Date.now()}`
   *       });
   *
   *       toast.success(`Added ${result.amount_added} credits. New balance: ${result.new_balance}`);
   *       onSuccess();
   *     } catch (error) {
   *       toast.error('Failed to add credits');
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   return (
   *     <form onSubmit={handleSubmit}>
   *       <label>
   *         Credits to Add:
   *         <input
   *           type="number"
   *           min="1"
   *           value={amount}
   *           onChange={e => setAmount(parseInt(e.target.value))}
   *           required
   *         />
   *       </label>
   *       <label>
   *         Description:
   *         <input
   *           type="text"
   *           value={description}
   *           onChange={e => setDescription(e.target.value)}
   *           placeholder="Promotional credits"
   *         />
   *       </label>
   *       <Button type="submit" disabled={loading}>
   *         {loading ? 'Adding...' : 'Add Credits'}
   *       </Button>
   *     </form>
   *   );
   * }
   *
   * @example
   * // Stripe integration webhook handler (server-side context)
   * async function handleStripeWebhook(event: any) {
   *   if (event.type === 'payment_intent.succeeded') {
   *     const paymentIntent = event.data.object;
   *     const metadata = paymentIntent.metadata;
   *
   *     if (metadata.type === 'poc_credits') {
   *       await api.addPocCredits({
   *         amount: parseInt(metadata.credits),
   *         description: `Package purchase: ${metadata.package_name}`,
   *         reference_id: paymentIntent.id
   *       });
   *     }
   *   }
   * }
   *
   * @see {@link getPocCredits} for checking current credit balance
   * @see {@link getPocPackages} for available credit packages
   * @see {@link getPocTransactions} for viewing transaction history
   */
  async addPocCredits(data: {
    amount: number
    description?: string
    reference_id?: string
  }) {
    return this.request<{
      success: boolean
      transaction_id: string
      amount_added: number
      new_balance: number
    }>('/api/poc/credits/add', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Retrieves the paginated transaction history for POC validation credits.
   *
   * This method provides a complete audit trail of all credit-related transactions
   * including purchases, consumption (validations), adjustments, and refunds. It's
   * essential for billing reconciliation, usage monitoring, and audit compliance.
   *
   * **Transaction Types:**
   * - **purchase**: Credits added via package purchase
   * - **validation**: Credits consumed for vulnerability validation
   * - **adjustment**: Manual credit adjustments (admin action)
   * - **refund**: Credits returned for failed validations
   * - **promotional**: Free credits from promotions or trials
   *
   * **Transaction Fields:**
   * Each transaction typically includes:
   * - `id`: Unique transaction identifier
   * - `type`: Transaction type (purchase, validation, adjustment, etc.)
   * - `amount`: Credit change (positive for additions, negative for consumption)
   * - `balance_after`: Running balance after this transaction
   * - `description`: Human-readable description
   * - `reference_id`: External reference (payment ID, validation ID, etc.)
   * - `created_at`: Transaction timestamp
   * - `created_by`: User who initiated the transaction (for manual adjustments)
   *
   * **Pagination:**
   * Uses limit/offset pagination for efficient retrieval of large transaction histories.
   * Default limit is typically 20-50 items.
   *
   * @param params - Optional query parameters
   * @param params.limit - Maximum number of transactions to return (default: 20)
   * @param params.offset - Number of transactions to skip for pagination (default: 0)
   *
   * @returns Promise resolving to paginated transaction list
   * @returns {boolean} success - Whether the request was successful
   * @returns {Array} transactions - Array of credit transactions
   * @returns {number} total - Total number of transactions (for pagination)
   * @returns {number} limit - Applied limit value
   * @returns {number} offset - Applied offset value
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If POC validation feature is not enabled for the organization (403)
   *
   * @example
   * // Basic transaction listing
   * const { transactions, total } = await api.getPocTransactions();
   * console.log(`Showing ${transactions.length} of ${total} transactions`);
   * transactions.forEach(tx => {
   *   const sign = tx.amount >= 0 ? '+' : '';
   *   console.log(`${tx.created_at}: ${sign}${tx.amount} - ${tx.description}`);
   * });
   *
   * @example
   * // Paginated transaction history
   * async function loadTransactionPage(page: number, pageSize: number = 20) {
   *   const { transactions, total } = await api.getPocTransactions({
   *     limit: pageSize,
   *     offset: (page - 1) * pageSize
   *   });
   *
   *   return {
   *     transactions,
   *     totalPages: Math.ceil(total / pageSize),
   *     currentPage: page
   *   };
   * }
   *
   * @example
   * // Transaction history table component
   * function TransactionHistory() {
   *   const [transactions, setTransactions] = useState<any[]>([]);
   *   const [total, setTotal] = useState(0);
   *   const [page, setPage] = useState(1);
   *   const [loading, setLoading] = useState(true);
   *   const pageSize = 20;
   *
   *   useEffect(() => {
   *     setLoading(true);
   *     api.getPocTransactions({
   *       limit: pageSize,
   *       offset: (page - 1) * pageSize
   *     })
   *       .then(({ transactions, total }) => {
   *         setTransactions(transactions);
   *         setTotal(total);
   *       })
   *       .finally(() => setLoading(false));
   *   }, [page]);
   *
   *   const totalPages = Math.ceil(total / pageSize);
   *
   *   return (
   *     <div>
   *       <h2>Credit Transaction History</h2>
   *       {loading ? <Spinner /> : (
   *         <>
   *           <table>
   *             <thead>
   *               <tr>
   *                 <th>Date</th>
   *                 <th>Type</th>
   *                 <th>Amount</th>
   *                 <th>Balance</th>
   *                 <th>Description</th>
   *               </tr>
   *             </thead>
   *             <tbody>
   *               {transactions.map(tx => (
   *                 <tr key={tx.id}>
   *                   <td>{new Date(tx.created_at).toLocaleString()}</td>
   *                   <td>{tx.type}</td>
   *                   <td className={tx.amount >= 0 ? 'credit' : 'debit'}>
   *                     {tx.amount >= 0 ? '+' : ''}{tx.amount}
   *                   </td>
   *                   <td>{tx.balance_after}</td>
   *                   <td>{tx.description}</td>
   *                 </tr>
   *               ))}
   *             </tbody>
   *           </table>
   *           <Pagination
   *             currentPage={page}
   *             totalPages={totalPages}
   *             onPageChange={setPage}
   *           />
   *         </>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Calculate credit consumption over time
   * async function getUsageStats(days: number = 30) {
   *   // Load all transactions (with appropriate pagination)
   *   const { transactions } = await api.getPocTransactions({
   *     limit: 1000, // Adjust based on expected volume
   *     offset: 0
   *   });
   *
   *   const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
   *
   *   const recentTransactions = transactions.filter(
   *     tx => new Date(tx.created_at).getTime() > cutoff
   *   );
   *
   *   const usage = {
   *     purchased: recentTransactions
   *       .filter(tx => tx.type === 'purchase')
   *       .reduce((sum, tx) => sum + tx.amount, 0),
   *     consumed: recentTransactions
   *       .filter(tx => tx.type === 'validation')
   *       .reduce((sum, tx) => sum + Math.abs(tx.amount), 0),
   *     refunded: recentTransactions
   *       .filter(tx => tx.type === 'refund')
   *       .reduce((sum, tx) => sum + tx.amount, 0)
   *   };
   *
   *   return usage;
   * }
   *
   * @example
   * // Export transactions for billing reconciliation
   * async function exportTransactionsCSV() {
   *   const allTransactions: any[] = [];
   *   let offset = 0;
   *   const limit = 100;
   *
   *   // Paginate through all transactions
   *   while (true) {
   *     const { transactions, total } = await api.getPocTransactions({ limit, offset });
   *     allTransactions.push(...transactions);
   *
   *     if (allTransactions.length >= total) break;
   *     offset += limit;
   *   }
   *
   *   // Convert to CSV
   *   const headers = ['Date', 'Type', 'Amount', 'Balance', 'Description', 'Reference'];
   *   const rows = allTransactions.map(tx => [
   *     new Date(tx.created_at).toISOString(),
   *     tx.type,
   *     tx.amount,
   *     tx.balance_after,
   *     tx.description,
   *     tx.reference_id || ''
   *   ]);
   *
   *   const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
   *   downloadFile(csv, 'credit-transactions.csv', 'text/csv');
   * }
   *
   * @see {@link getPocCredits} for current credit balance
   * @see {@link addPocCredits} for credit addition records
   * @see {@link validatePoc} for validation-related transactions
   */
  async getPocTransactions(params?: {
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      transactions: any[]
      total: number
      limit: number
      offset: number
    }>(`/api/poc/transactions${query ? `?${query}` : ''}`)
  }

  // POC Validation
  /**
   * Initiates a Proof of Concept (POC) validation for a detected vulnerability.
   *
   * POC validation automatically tests whether a detected vulnerability is actually
   * exploitable in your specific environment. This helps distinguish true positives
   * from false positives by attempting safe, controlled exploitation techniques.
   *
   * **Validation Process:**
   * 1. Submits vulnerability for validation (consumes 1 credit immediately)
   * 2. Backend queues the validation job
   * 3. Automated POC execution runs against the target
   * 4. Results are stored with confidence scoring
   * 5. Evidence is captured for compliance documentation
   *
   * **Credit Consumption:**
   * - Each validation consumes exactly 1 credit
   * - Credits are deducted immediately upon submission
   * - Failed validations (network errors, timeouts) still consume credits
   * - Validation of the same vulnerability again consumes another credit
   *
   * **Validation Results (retrieved via {@link getPocValidation}):**
   * - `exploitable` - Vulnerability confirmed as exploitable
   * - `not_exploitable` - POC could not successfully exploit the vulnerability
   * - `inconclusive` - Unable to determine (firewall, timeout, etc.)
   * - `error` - Validation encountered a technical error
   *
   * @param vulnerabilityId - Unique identifier of the vulnerability to validate
   *
   * @returns Promise resolving to validation initiation result
   * @returns {boolean} success - Whether validation was successfully initiated
   * @returns {string} validation_id - Unique ID to track validation status
   * @returns {number} credits_remaining - Updated credit balance after deduction
   * @returns {string} message - Human-readable status message
   * @returns {string} [error] - Error message if validation failed to initiate
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If insufficient credits (402 Payment Required)
   * @throws {Error} If vulnerability not found (404)
   * @throws {Error} If POC validation not enabled for organization (403)
   *
   * @example
   * // Basic vulnerability validation
   * const result = await api.validatePoc('vuln_abc123');
   * console.log(`Validation started: ${result.validation_id}`);
   * console.log(`Credits remaining: ${result.credits_remaining}`);
   *
   * @example
   * // Validate button with loading state and error handling
   * function ValidateButton({ vulnerabilityId }: { vulnerabilityId: string }) {
   *   const [loading, setLoading] = useState(false);
   *   const [validationId, setValidationId] = useState<string | null>(null);
   *
   *   const handleValidate = async () => {
   *     setLoading(true);
   *     try {
   *       const { validation_id, credits_remaining, message } = await api.validatePoc(vulnerabilityId);
   *       setValidationId(validation_id);
   *       toast.success(message);
   *
   *       if (credits_remaining < 5) {
   *         toast.warning(`Low credits: ${credits_remaining} remaining`);
   *       }
   *     } catch (error) {
   *       if (error.status === 402) {
   *         toast.error('No credits remaining. Please purchase more.');
   *       } else {
   *         toast.error('Validation failed to start');
   *       }
   *     } finally {
   *       setLoading(false);
   *     }
   *   };
   *
   *   if (validationId) {
   *     return <ValidationStatus validationId={validationId} />;
   *   }
   *
   *   return (
   *     <button onClick={handleValidate} disabled={loading}>
   *       {loading ? 'Starting...' : 'Validate Vulnerability'}
   *     </button>
   *   );
   * }
   *
   * @example
   * // Bulk validation with credit check
   * async function bulkValidate(vulnerabilityIds: string[]) {
   *   const { balance } = await api.getPocCredits();
   *
   *   if (balance < vulnerabilityIds.length) {
   *     throw new Error(
   *       `Insufficient credits. Need ${vulnerabilityIds.length}, have ${balance}.`
   *     );
   *   }
   *
   *   const results = [];
   *   for (const vulnId of vulnerabilityIds) {
   *     const result = await api.validatePoc(vulnId);
   *     results.push({
   *       vulnerabilityId: vulnId,
   *       validationId: result.validation_id,
   *       success: result.success,
   *     });
   *
   *     // Check remaining credits
   *     if (result.credits_remaining === 0) {
   *       console.warn('Credits exhausted, stopping bulk validation');
   *       break;
   *     }
   *   }
   *
   *   return results;
   * }
   *
   * @example
   * // Validation with status polling
   * async function validateAndWaitForResult(vulnerabilityId: string) {
   *   const { validation_id } = await api.validatePoc(vulnerabilityId);
   *
   *   // Poll for completion
   *   let attempts = 0;
   *   const maxAttempts = 30; // 5 minutes with 10s intervals
   *
   *   while (attempts < maxAttempts) {
   *     await new Promise(resolve => setTimeout(resolve, 10000));
   *     const { validation } = await api.getPocValidation(validation_id);
   *
   *     if (validation.status === 'completed' || validation.status === 'failed') {
   *       return validation;
   *     }
   *
   *     attempts++;
   *   }
   *
   *   throw new Error('Validation timed out');
   * }
   *
   * @see {@link getPocCredits} for checking credit balance before validation
   * @see {@link getPocValidation} for retrieving validation results
   * @see {@link getPocValidations} for listing all validation history
   * @see {@link getPocTransactions} for credit consumption records
   */
  async validatePoc(vulnerabilityId: string) {
    return this.request<{
      success: boolean
      validation_id: string
      credits_remaining: number
      message: string
      error?: string
    }>(`/api/poc/validate/${vulnerabilityId}`, {
      method: 'POST',
    })
  }

  /**
   * Retrieves a paginated list of POC (Proof of Concept) validation results.
   *
   * Returns historical validation attempts with their statuses, results, and
   * confidence scores. Use this to display validation history, track validation
   * progress, and analyze exploitability trends across your vulnerability portfolio.
   *
   * **Validation Statuses:**
   * - `queued` - Validation submitted, waiting for processing
   * - `running` - Validation in progress
   * - `completed` - Validation finished (check `result` for outcome)
   * - `failed` - Validation encountered a technical error
   * - `cancelled` - Validation was cancelled before completion
   *
   * **Validation Results (for completed status):**
   * - `exploitable` - POC successfully exploited the vulnerability
   * - `not_exploitable` - POC could not exploit the vulnerability
   * - `inconclusive` - Unable to determine exploitability
   *
   * **Confidence Scoring:**
   * The `confidence_score` (0.0-1.0) indicates how confident the system is
   * in the validation result:
   * - 0.9-1.0: High confidence (clear exploit success/failure)
   * - 0.7-0.9: Medium confidence (some ambiguity in results)
   * - Below 0.7: Low confidence (recommend manual verification)
   *
   * @param params - Optional filtering and pagination parameters
   * @param params.status - Filter by validation status ('queued', 'running', 'completed', 'failed', 'cancelled')
   * @param params.limit - Maximum number of validations to return (default: 50, max: 100)
   * @param params.offset - Number of validations to skip for pagination (default: 0)
   *
   * @returns Promise resolving to paginated validation list
   * @returns {boolean} success - Whether the request was successful
   * @returns {Array} validations - Array of validation records
   * @returns {string} validations[].id - Unique validation identifier
   * @returns {string} validations[].vulnerability_id - ID of validated vulnerability
   * @returns {string} validations[].vulnerability_name - Name/title of vulnerability
   * @returns {string|null} validations[].cve_id - CVE identifier if applicable
   * @returns {string} validations[].host - Target host that was tested
   * @returns {number|null} validations[].port - Target port if applicable
   * @returns {string} validations[].severity - Vulnerability severity (critical/high/medium/low)
   * @returns {string} validations[].status - Current validation status
   * @returns {string|null} validations[].result - Validation result if completed
   * @returns {number|null} validations[].confidence_score - Confidence in result (0.0-1.0)
   * @returns {string|null} validations[].validation_method - Technique used for validation
   * @returns {string|null} validations[].poc_output - Raw output from POC execution
   * @returns {string|null} validations[].evidence_summary - Summary for compliance reports
   * @returns {number} validations[].created_at - Unix timestamp when validation started
   * @returns {number|null} validations[].completed_at - Unix timestamp when validation finished
   * @returns {number} limit - Current page size
   * @returns {number} offset - Current offset position
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If POC validation not enabled for organization (403)
   *
   * @example
   * // Basic validation history retrieval
   * const { validations } = await api.getPocValidations();
   * console.log(`Found ${validations.length} validations`);
   *
   * @example
   * // Filter for completed validations only
   * const { validations } = await api.getPocValidations({ status: 'completed' });
   * const exploitable = validations.filter(v => v.result === 'exploitable');
   * console.log(`${exploitable.length} vulnerabilities confirmed exploitable`);
   *
   * @example
   * // Validation history table with pagination
   * function ValidationHistory() {
   *   const [validations, setValidations] = useState<Validation[]>([]);
   *   const [loading, setLoading] = useState(true);
   *   const [page, setPage] = useState(0);
   *   const pageSize = 20;
   *
   *   useEffect(() => {
   *     setLoading(true);
   *     api.getPocValidations({
   *       limit: pageSize,
   *       offset: page * pageSize,
   *     })
   *       .then(({ validations }) => setValidations(validations))
   *       .finally(() => setLoading(false));
   *   }, [page]);
   *
   *   return (
   *     <Table>
   *       <thead>
   *         <tr>
   *           <th>Vulnerability</th>
   *           <th>Host</th>
   *           <th>Status</th>
   *           <th>Result</th>
   *           <th>Confidence</th>
   *           <th>Date</th>
   *         </tr>
   *       </thead>
   *       <tbody>
   *         {validations.map(v => (
   *           <tr key={v.id}>
   *             <td>
   *               {v.vulnerability_name}
   *               {v.cve_id && <Badge>{v.cve_id}</Badge>}
   *             </td>
   *             <td>{v.host}{v.port && `:${v.port}`}</td>
   *             <td><StatusBadge status={v.status} /></td>
   *             <td>{v.result || '-'}</td>
   *             <td>{v.confidence_score ? `${(v.confidence_score * 100).toFixed(0)}%` : '-'}</td>
   *             <td>{formatDate(v.created_at)}</td>
   *           </tr>
   *         ))}
   *       </tbody>
   *     </Table>
   *   );
   * }
   *
   * @example
   * // In-progress validation tracker
   * function ActiveValidations() {
   *   const [activeValidations, setActiveValidations] = useState<Validation[]>([]);
   *
   *   useEffect(() => {
   *     const fetchActive = () => {
   *       Promise.all([
   *         api.getPocValidations({ status: 'queued' }),
   *         api.getPocValidations({ status: 'running' }),
   *       ]).then(([queued, running]) => {
   *         setActiveValidations([...queued.validations, ...running.validations]);
   *       });
   *     };
   *
   *     fetchActive();
   *     const interval = setInterval(fetchActive, 10000); // Poll every 10s
   *     return () => clearInterval(interval);
   *   }, []);
   *
   *   if (activeValidations.length === 0) return null;
   *
   *   return (
   *     <div className="active-validations">
   *       <h4>{activeValidations.length} validations in progress</h4>
   *       {activeValidations.map(v => (
   *         <div key={v.id} className="validation-item">
   *           <Spinner size="sm" />
   *           <span>{v.vulnerability_name}</span>
   *           <span>{v.status}</span>
   *         </div>
   *       ))}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Exploitability summary report
   * async function generateExploitabilityReport() {
   *   let allValidations: Validation[] = [];
   *   let offset = 0;
   *   const limit = 100;
   *
   *   // Fetch all completed validations
   *   while (true) {
   *     const { validations } = await api.getPocValidations({
   *       status: 'completed',
   *       limit,
   *       offset,
   *     });
   *
   *     allValidations = [...allValidations, ...validations];
   *     if (validations.length < limit) break;
   *     offset += limit;
   *   }
   *
   *   // Analyze results
   *   const summary = {
   *     total: allValidations.length,
   *     exploitable: allValidations.filter(v => v.result === 'exploitable').length,
   *     notExploitable: allValidations.filter(v => v.result === 'not_exploitable').length,
   *     inconclusive: allValidations.filter(v => v.result === 'inconclusive').length,
   *     bySeverity: {
   *       critical: allValidations.filter(v => v.severity === 'critical' && v.result === 'exploitable').length,
   *       high: allValidations.filter(v => v.severity === 'high' && v.result === 'exploitable').length,
   *     },
   *   };
   *
   *   return summary;
   * }
   *
   * @see {@link getPocValidation} for detailed single validation view
   * @see {@link validatePoc} for initiating new validations
   * @see {@link getPocCredits} for credit balance monitoring
   */
  async getPocValidations(params?: {
    status?: string
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request<{
      success: boolean
      validations: Array<{
        id: string
        vulnerability_id: string
        vulnerability_name: string
        cve_id: string | null
        host: string
        port: number | null
        severity: string
        status: string
        result: string | null
        confidence_score: number | null
        validation_method: string | null
        poc_output: string | null
        evidence_summary: string | null
        created_at: number
        completed_at: number | null
      }>
      limit: number
      offset: number
    }>(`/api/poc/validations${query ? `?${query}` : ''}`)
  }

  /**
   * Retrieves detailed information about a specific POC validation.
   *
   * Returns complete validation details including POC output, evidence summary,
   * confidence scoring, and timing information. Use this for detailed validation
   * views, compliance reporting, and debugging validation issues.
   *
   * **Validation Detail Fields:**
   * - `vuln_description` - Full vulnerability description (not in list view)
   * - `poc_output` - Complete raw output from POC execution
   * - `evidence_summary` - Human-readable summary suitable for reports
   * - `duration` - Total validation time in milliseconds
   *
   * **Interpreting Results:**
   * - `exploitable` with high confidence (>0.9): True positive, prioritize remediation
   * - `not_exploitable` with high confidence: Likely false positive, consider suppressing
   * - `inconclusive`: Manual verification recommended
   *
   * **Compliance Use Cases:**
   * The `evidence_summary` field provides pre-formatted text suitable for:
   * - SOC 2 evidence collection
   * - Penetration test reports
   * - Vulnerability management metrics
   * - Risk assessment documentation
   *
   * @param id - Unique validation identifier (from {@link validatePoc} or {@link getPocValidations})
   *
   * @returns Promise resolving to detailed validation information
   * @returns {boolean} success - Whether the request was successful
   * @returns {Object} validation - Complete validation details
   * @returns {string} validation.id - Unique validation identifier
   * @returns {string} validation.vulnerability_id - ID of the validated vulnerability
   * @returns {string} validation.vulnerability_name - Name/title of the vulnerability
   * @returns {string|null} validation.cve_id - CVE identifier if applicable (e.g., "CVE-2021-44228")
   * @returns {string} validation.host - Target host that was tested
   * @returns {number|null} validation.port - Target port if applicable
   * @returns {string} validation.severity - Vulnerability severity (critical/high/medium/low)
   * @returns {string|null} validation.vuln_description - Full vulnerability description
   * @returns {string} validation.status - Current status (queued/running/completed/failed/cancelled)
   * @returns {string|null} validation.result - Validation outcome (exploitable/not_exploitable/inconclusive)
   * @returns {number|null} validation.confidence_score - Confidence in result (0.0-1.0)
   * @returns {string|null} validation.validation_method - Technique used (e.g., "network_probe", "payload_injection")
   * @returns {string|null} validation.poc_output - Raw POC execution output
   * @returns {string|null} validation.evidence_summary - Compliance-ready summary text
   * @returns {number} validation.created_at - Unix timestamp when validation was initiated
   * @returns {number|null} validation.completed_at - Unix timestamp when validation finished
   * @returns {number|null} validation.duration - Total execution time in milliseconds
   *
   * @throws {Error} If not authenticated (401)
   * @throws {Error} If validation not found (404)
   * @throws {Error} If POC validation not enabled for organization (403)
   *
   * @example
   * // Basic validation retrieval
   * const { validation } = await api.getPocValidation('val_abc123');
   * console.log(`Status: ${validation.status}`);
   * if (validation.status === 'completed') {
   *   console.log(`Result: ${validation.result} (${(validation.confidence_score * 100).toFixed(0)}% confidence)`);
   * }
   *
   * @example
   * // Detailed validation view component
   * function ValidationDetail({ validationId }: { validationId: string }) {
   *   const [validation, setValidation] = useState<Validation | null>(null);
   *   const [loading, setLoading] = useState(true);
   *
   *   useEffect(() => {
   *     api.getPocValidation(validationId)
   *       .then(({ validation }) => setValidation(validation))
   *       .finally(() => setLoading(false));
   *   }, [validationId]);
   *
   *   if (loading) return <Spinner />;
   *   if (!validation) return <div>Validation not found</div>;
   *
   *   return (
   *     <div className="validation-detail">
   *       <header>
   *         <h2>{validation.vulnerability_name}</h2>
   *         {validation.cve_id && <Badge variant="info">{validation.cve_id}</Badge>}
   *         <SeverityBadge severity={validation.severity} />
   *       </header>
   *
   *       <dl>
   *         <dt>Target</dt>
   *         <dd>{validation.host}{validation.port && `:${validation.port}`}</dd>
   *
   *         <dt>Status</dt>
   *         <dd><StatusBadge status={validation.status} /></dd>
   *
   *         {validation.result && (
   *           <>
   *             <dt>Result</dt>
   *             <dd>
   *               <ResultBadge result={validation.result} />
   *               {validation.confidence_score && (
   *                 <span>({(validation.confidence_score * 100).toFixed(0)}% confidence)</span>
   *               )}
   *             </dd>
   *           </>
   *         )}
   *
   *         <dt>Validation Method</dt>
   *         <dd>{validation.validation_method || 'N/A'}</dd>
   *
   *         <dt>Duration</dt>
   *         <dd>{validation.duration ? `${(validation.duration / 1000).toFixed(1)}s` : 'In progress'}</dd>
   *       </dl>
   *
   *       {validation.vuln_description && (
   *         <section>
   *           <h3>Vulnerability Description</h3>
   *           <p>{validation.vuln_description}</p>
   *         </section>
   *       )}
   *
   *       {validation.poc_output && (
   *         <section>
   *           <h3>POC Output</h3>
   *           <pre className="code-block">{validation.poc_output}</pre>
   *         </section>
   *       )}
   *
   *       {validation.evidence_summary && (
   *         <section>
   *           <h3>Evidence Summary</h3>
   *           <div className="evidence-box">{validation.evidence_summary}</div>
   *           <CopyButton text={validation.evidence_summary}>
   *             Copy for Report
   *           </CopyButton>
   *         </section>
   *       )}
   *     </div>
   *   );
   * }
   *
   * @example
   * // Polling for validation completion
   * async function waitForValidation(validationId: string, timeoutMs = 300000) {
   *   const startTime = Date.now();
   *
   *   while (Date.now() - startTime < timeoutMs) {
   *     const { validation } = await api.getPocValidation(validationId);
   *
   *     if (validation.status === 'completed') {
   *       return {
   *         success: true,
   *         result: validation.result,
   *         confidence: validation.confidence_score,
   *         evidence: validation.evidence_summary,
   *       };
   *     }
   *
   *     if (validation.status === 'failed' || validation.status === 'cancelled') {
   *       return {
   *         success: false,
   *         status: validation.status,
   *         output: validation.poc_output,
   *       };
   *     }
   *
   *     // Wait before next poll
   *     await new Promise(resolve => setTimeout(resolve, 5000));
   *   }
   *
   *   throw new Error('Validation timeout');
   * }
   *
   * @example
   * // Export validation evidence for compliance report
   * async function exportValidationEvidence(validationId: string) {
   *   const { validation } = await api.getPocValidation(validationId);
   *
   *   if (validation.status !== 'completed') {
   *     throw new Error('Validation not yet completed');
   *   }
   *
   *   return {
   *     vulnerability: {
   *       name: validation.vulnerability_name,
   *       cve: validation.cve_id,
   *       severity: validation.severity,
   *       target: `${validation.host}${validation.port ? `:${validation.port}` : ''}`,
   *     },
   *     validation: {
   *       result: validation.result,
   *       confidence: validation.confidence_score,
   *       method: validation.validation_method,
   *       duration: validation.duration,
   *       timestamp: new Date(validation.completed_at! * 1000).toISOString(),
   *     },
   *     evidence: validation.evidence_summary,
   *   };
   * }
   *
   * @see {@link getPocValidations} for listing all validations
   * @see {@link validatePoc} for initiating new validations
   * @see {@link getPocCredits} for credit balance monitoring
   */
  async getPocValidation(id: string) {
    return this.request<{
      success: boolean
      validation: {
        id: string
        vulnerability_id: string
        vulnerability_name: string
        cve_id: string | null
        host: string
        port: number | null
        severity: string
        vuln_description: string | null
        status: string
        result: string | null
        confidence_score: number | null
        validation_method: string | null
        poc_output: string | null
        evidence_summary: string | null
        created_at: number
        completed_at: number | null
        duration: number | null
      }
    }>(`/api/poc/validations/${id}`)
  }

  // ============================================================================
  // TASK MANAGEMENT ENDPOINTS
  // ============================================================================

  // Task type is defined at the top of the file as TaskType interface

  /**
   * List all tasks with optional filtering.
   */
  async getTasksList(params: {
    status?: 'open' | 'in-progress' | 'completed' | 'cancelled'
    type?: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
    priority?: 'critical' | 'high' | 'medium' | 'low'
    assigned_to?: string
    control_id?: string
    source?: 'manual' | 'auto-frequency' | 'auto-reminder'
    search?: string
    limit?: number
    offset?: number
  } = {}) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request<{
      success: boolean
      tasks: Array<TaskType>
      counts: {
        total: number
        open: number
        in_progress: number
        completed: number
        cancelled: number
        overdue: number
      }
      pagination: { limit: number; offset: number; hasMore: boolean }
    }>(`/api/tasks?${searchParams}`)
  }

  /**
   * Get a single task by ID.
   */
  async getTask(id: string) {
    return this.request<{
      success: boolean
      task: TaskType
      linked_evidence: Array<{
        link_id: string
        linked_at: number
        link_notes?: string
        evidence_id: string
        note?: string
        file_name?: string
        timestamp: number
        uploaded_by_username?: string
      }>
    }>(`/api/tasks/${id}`)
  }

  /**
   * Create a new task.
   */
  async createTask(task: {
    title: string
    description?: string
    type?: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
    assigned_to?: string
    priority?: 'critical' | 'high' | 'medium' | 'low'
    due_date?: number
    related_control_id?: string
    related_document_id?: string
    related_risk_id?: string
    related_vendor_id?: string
    // Approval workflow fields (Issue #150)
    approval_required?: boolean
    approver_id?: string
    approver_role?: 'admin' | 'contributor'
  }) {
    return this.request<{
      success: boolean
      task: TaskType
    }>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    })
  }

  /**
   * Update an existing task.
   */
  async updateTask(id: string, updates: {
    title?: string
    description?: string
    type?: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
    assigned_to?: string | null
    priority?: 'critical' | 'high' | 'medium' | 'low'
    status?: 'open' | 'in-progress' | 'pending-approval' | 'approved' | 'rejected' | 'completed' | 'cancelled'
    due_date?: number | null
    related_control_id?: string | null
    related_document_id?: string | null
    related_risk_id?: string | null
    related_vendor_id?: string | null
    // Approval workflow fields (Issue #150)
    approval_required?: boolean
    approver_id?: string | null
    approver_role?: 'admin' | 'contributor' | null
  }) {
    return this.request<{
      success: boolean
      task: TaskType
    }>(`/api/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Delete a task.
   */
  async deleteTask(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Link evidence to a task.
   */
  async linkTaskEvidence(taskId: string, evidenceId: string, notes?: string) {
    return this.request<{
      success: boolean
      link_id: string
      message: string
    }>(`/api/tasks/${taskId}/evidence`, {
      method: 'POST',
      body: JSON.stringify({ evidence_id: evidenceId, notes }),
    })
  }

  /**
   * Unlink evidence from a task.
   */
  async unlinkTaskEvidence(taskId: string, evidenceId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${taskId}/evidence/${evidenceId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get all evidence linked to a task.
   */
  async getTaskEvidence(taskId: string) {
    return this.request<{
      success: boolean
      evidence: Array<{
        link_id: string
        linked_at: number
        link_notes?: string
        evidence_id: string
        note?: string
        file_name?: string
        file_key?: string
        file_type?: string
        timestamp: number
        source?: string
        linked_by_username?: string
        uploaded_by_username?: string
        control_number?: string
        control_name?: string
      }>
      count: number
    }>(`/api/tasks/${taskId}/evidence`)
  }

  // ============================================================================
  // TASK-CONTROL LINKING (Many-to-Many)
  // ============================================================================

  /**
   * Link a control to a task.
   */
  async linkTaskControl(taskId: string, controlId: string, notes?: string) {
    return this.request<{
      success: boolean
      link_id: string
      message: string
      control: {
        id: string
        control_number: string
        name: string
      }
    }>(`/api/tasks/${taskId}/controls`, {
      method: 'POST',
      body: JSON.stringify({ control_id: controlId, notes }),
    })
  }

  /**
   * Unlink a control from a task.
   */
  async unlinkTaskControl(taskId: string, controlId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${taskId}/controls/${controlId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get all controls linked to a task.
   */
  async getTaskControls(taskId: string) {
    return this.request<{
      success: boolean
      controls: Array<{
        link_id: string
        linked_at: number
        link_notes?: string
        control_id: string
        control_number: string
        name: string
        description?: string
        status: string
        frequency: string
        next_due_date?: number
        linked_by_username?: string
        framework_name?: string
      }>
      count: number
    }>(`/api/tasks/${taskId}/controls`)
  }

  // ============================================================================
  // TASK APPROVAL WORKFLOW (Issue #150)
  // ============================================================================

  /**
   * Get tasks pending approval that the current user can approve.
   *
   * Returns tasks with status 'pending-approval' where the user is
   * either the designated approver or has the required role.
   *
   * @example
   * const { tasks, count } = await api.getTasksPendingApproval()
   */
  async getTasksPendingApproval() {
    return this.request<{
      success: boolean
      tasks: Array<{
        id: string
        title: string
        description?: string
        type: string
        assigned_to?: string
        assigned_to_username?: string
        assigned_to_email?: string
        created_by: string
        created_by_username?: string
        priority: string
        status: string
        due_date?: number
        approval_required: number
        approver_id?: string
        approver_username?: string
        approver_role?: string
        control_number?: string
        control_name?: string
        approval_history_count: number
        created_at: number
        updated_at: number
      }>
      count: number
    }>('/api/tasks/pending-approval')
  }

  /**
   * Submit a task for approval.
   *
   * Changes the task status from 'in-progress' or 'rejected' to 'pending-approval'.
   * Only works for tasks with approval_required = true.
   *
   * @param {string} taskId - ID of the task to submit
   *
   * @example
   * await api.submitTaskForApproval('task-123')
   */
  async submitTaskForApproval(taskId: string) {
    return this.request<{
      success: boolean
      message: string
      task: {
        id: string
        title: string
        status: string
        approval_required: number
        approver_id?: string
        approver_username?: string
        updated_at: number
      }
    }>(`/api/tasks/${taskId}/submit-for-approval`, {
      method: 'PATCH',
    })
  }

  /**
   * Approve a task.
   *
   * Changes the task status from 'pending-approval' to 'approved'.
   * Optionally can auto-complete the task in the same operation.
   *
   * @param {string} taskId - ID of the task to approve
   * @param {Object} options - Approval options
   * @param {string} [options.comments] - Optional approval comments
   * @param {boolean} [options.auto_complete] - If true, mark task as completed after approval
   *
   * @example
   * await api.approveTask('task-123', { comments: 'Looks good!', auto_complete: true })
   */
  async approveTask(
    taskId: string,
    options?: { comments?: string; auto_complete?: boolean }
  ) {
    return this.request<{
      success: boolean
      message: string
      task: {
        id: string
        title: string
        status: string
        completed_at?: number
        updated_at: number
      }
    }>(`/api/tasks/${taskId}/approve`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    })
  }

  /**
   * Reject a task.
   *
   * Changes the task status from 'pending-approval' to 'rejected'.
   * Comments are required to explain the rejection reason.
   *
   * @param {string} taskId - ID of the task to reject
   * @param {string} comments - Reason for rejection (required)
   *
   * @example
   * await api.rejectTask('task-123', 'Missing required documentation. Please attach the security policy.')
   */
  async rejectTask(taskId: string, comments: string) {
    return this.request<{
      success: boolean
      message: string
      task: {
        id: string
        title: string
        status: string
        updated_at: number
      }
      rejection_comments: string
    }>(`/api/tasks/${taskId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ comments }),
    })
  }

  /**
   * Get approval history for a task.
   *
   * Returns all approval actions taken on a task including
   * submissions, approvals, and rejections with timestamps and comments.
   *
   * @param {string} taskId - ID of the task
   *
   * @example
   * const { approvals } = await api.getTaskApprovals('task-123')
   * approvals.forEach(a => console.log(`${a.action} by ${a.approver_username} at ${a.created_at}`))
   */
  async getTaskApprovals(taskId: string) {
    return this.request<{
      success: boolean
      approvals: Array<{
        id: string
        task_id: string
        approver_id: string
        approver_username?: string
        approver_email?: string
        action: 'submitted' | 'approved' | 'rejected' | 'reverted'
        comments?: string
        created_at: number
      }>
      count: number
    }>(`/api/tasks/${taskId}/approvals`)
  }

  // ============================================================================
  // TASK COMMENTS (Issue #90)
  // ============================================================================

  /**
   * Get comments for a task.
   *
   * Returns all comments for the specified task, ordered by creation date (oldest first).
   * Each comment includes author information.
   *
   * @param {string} taskId - The task ID
   *
   * @example
   * const { comments } = await api.getTaskComments('task-123')
   * comments.forEach(c => console.log(`${c.author_username}: ${c.content}`))
   */
  async getTaskComments(taskId: string) {
    return this.request<{
      success: boolean
      comments: TaskComment[]
      count: number
      warning?: string
    }>(`/api/tasks/${taskId}/comments`)
  }

  /**
   * Add a comment to a task.
   *
   * @param {string} taskId - The task ID
   * @param {string} content - The comment text
   *
   * @example
   * const { comment } = await api.addTaskComment('task-123', 'This is looking good!')
   */
  async addTaskComment(taskId: string, content: string) {
    return this.request<{
      success: boolean
      comment: TaskComment
    }>(`/api/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    })
  }

  /**
   * Edit a task comment.
   *
   * Only the comment author or admin can edit a comment.
   *
   * @param {string} taskId - The task ID
   * @param {string} commentId - The comment ID
   * @param {string} content - The updated comment text
   *
   * @example
   * await api.updateTaskComment('task-123', 'comment-456', 'Updated comment text')
   */
  async updateTaskComment(taskId: string, commentId: string, content: string) {
    return this.request<{
      success: boolean
      comment: TaskComment
    }>(`/api/tasks/${taskId}/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    })
  }

  /**
   * Delete a task comment.
   *
   * Only the comment author or admin can delete a comment.
   *
   * @param {string} taskId - The task ID
   * @param {string} commentId - The comment ID
   *
   * @example
   * await api.deleteTaskComment('task-123', 'comment-456')
   */
  async deleteTaskComment(taskId: string, commentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${taskId}/comments/${commentId}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // TASK ATTACHMENTS (Issue #98)
  // ============================================================================

  /**
   * Get all attachments for a task.
   *
   * Returns a list of file attachments uploaded to the specified task,
   * ordered by creation date (newest first).
   *
   * @param {string} taskId - The task ID
   * @returns {Promise<Object>} Response containing attachments array
   *
   * @example
   * const { attachments } = await api.getTaskAttachments('task-123')
   * attachments.forEach(a => console.log(a.filename, a.file_size))
   */
  async getTaskAttachments(taskId: string) {
    return this.request<{
      success: boolean
      attachments: Array<{
        id: string
        task_id: string
        filename: string
        file_key: string
        file_size: number
        mime_type: string | null
        uploaded_by: string
        uploaded_by_username?: string
        created_at: number
      }>
    }>(`/api/tasks/${taskId}/attachments`)
  }

  /**
   * Upload a file attachment to a task.
   *
   * Accepts a FormData object with a 'file' field containing the file to upload.
   * Maximum file size is 50MB.
   *
   * @param {string} taskId - The task ID
   * @param {FormData} formData - FormData with 'file' field
   * @returns {Promise<Object>} Response containing the created attachment
   *
   * @example
   * const formData = new FormData()
   * formData.append('file', selectedFile)
   * const { attachment } = await api.uploadTaskAttachment('task-123', formData)
   * console.log('Uploaded:', attachment.filename)
   */
  async uploadTaskAttachment(taskId: string, formData: FormData) {
    return this.request<{
      success: boolean
      attachment: {
        id: string
        task_id: string
        filename: string
        file_key: string
        file_size: number
        mime_type: string | null
        uploaded_by: string
        uploaded_by_username?: string
        created_at: number
      }
    }>(`/api/tasks/${taskId}/attachments`, {
      method: 'POST',
      body: formData,
    })
  }

  /**
   * Get the download URL for a task attachment.
   *
   * This returns the API endpoint that will serve the file download.
   * The URL includes authentication through the normal API flow.
   *
   * @param {string} taskId - The task ID
   * @param {string} attachmentId - The attachment ID
   * @returns {string} The download URL
   *
   * @example
   * const url = api.getTaskAttachmentDownloadUrl('task-123', 'att-456')
   * window.open(url, '_blank')
   */
  getTaskAttachmentDownloadUrl(taskId: string, attachmentId: string): string {
    return `${getApiBase()}/api/tasks/${taskId}/attachments/${attachmentId}`
  }

  /**
   * Download a task attachment.
   *
   * Fetches the attachment file and triggers a browser download.
   *
   * @param {string} taskId - The task ID
   * @param {string} attachmentId - The attachment ID
   * @param {string} filename - The filename for the download
   *
   * @example
   * await api.downloadTaskAttachment('task-123', 'att-456', 'report.pdf')
   */
  async downloadTaskAttachment(taskId: string, attachmentId: string, filename: string) {
    const response = await fetch(
      `${getApiBase()}/api/tasks/${taskId}/attachments/${attachmentId}`,
      {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error('Failed to download attachment')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  /**
   * Delete a task attachment.
   *
   * Permanently removes the attachment from storage. Only the uploader
   * or an admin can delete an attachment.
   *
   * @param {string} taskId - The task ID
   * @param {string} attachmentId - The attachment ID
   *
   * @example
   * await api.deleteTaskAttachment('task-123', 'att-456')
   */
  async deleteTaskAttachment(taskId: string, attachmentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${taskId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // BULK TASK OPERATIONS
  // ============================================================================

  /**
   * Bulk update multiple tasks at once.
   *
   * Allows updating status, assigned_to, or priority for multiple tasks
   * in a single atomic operation.
   *
   * @param {string[]} taskIds - Array of task IDs to update (max 100)
   * @param {Object} updates - Fields to update
   * @param {string} [updates.status] - New status ('open' | 'in-progress' | 'completed' | 'cancelled')
   * @param {string | null} [updates.assigned_to] - User ID to assign to, or null to unassign
   * @param {string} [updates.priority] - New priority ('critical' | 'high' | 'medium' | 'low')
   *
   * @example
   * // Mark multiple tasks as completed
   * await api.bulkUpdateTasks(['task1', 'task2', 'task3'], { status: 'completed' })
   *
   * @example
   * // Assign multiple tasks to a user
   * await api.bulkUpdateTasks(['task1', 'task2'], { assigned_to: 'user-123' })
   *
   * @example
   * // Unassign multiple tasks
   * await api.bulkUpdateTasks(['task1', 'task2'], { assigned_to: null })
   */
  async bulkUpdateTasks(
    taskIds: string[],
    updates: {
      status?: 'open' | 'in-progress' | 'pending-approval' | 'approved' | 'rejected' | 'completed' | 'cancelled'
      assigned_to?: string | null
      priority?: 'critical' | 'high' | 'medium' | 'low'
    }
  ) {
    return this.request<{
      success: boolean
      message: string
      updated_count: number
      updates_applied: typeof updates
    }>('/api/tasks/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ task_ids: taskIds, updates }),
    })
  }

  /**
   * Bulk delete multiple tasks at once.
   *
   * Only admins or task creators can delete tasks. Returns partial success
   * if some tasks couldn't be deleted due to permissions.
   *
   * @param {string[]} taskIds - Array of task IDs to delete (max 100)
   *
   * @example
   * // Delete multiple tasks
   * const result = await api.bulkDeleteTasks(['task1', 'task2', 'task3'])
   * console.log(`Deleted ${result.deleted_count} tasks`)
   * if (result.warnings) {
   *   console.log('Some tasks could not be deleted:', result.warnings)
   * }
   */
  async bulkDeleteTasks(taskIds: string[]) {
    return this.request<{
      success: boolean
      message: string
      deleted_count: number
      deleted_ids: string[]
      warnings?: {
        not_found?: string[]
        unauthorized?: string[]
      }
    }>('/api/tasks/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ task_ids: taskIds }),
    })
  }

  /**
   * Get all tasks linked to a control.
   */
  async getControlTasks(controlId: string, params?: { status?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    const queryString = searchParams.toString()
    return this.request<{
      success: boolean
      control: {
        id: string
        control_number: string
        name: string
      }
      tasks: Array<{
        id: string
        title: string
        description?: string
        type: string
        priority: string
        status: string
        due_date?: number
        created_at: number
        completed_at?: number
        linked_at: number
        link_notes?: string
        assigned_to_username?: string
        assigned_to_email?: string
        created_by_username?: string
        linked_by_username?: string
      }>
      counts: {
        total: number
        open: number
        in_progress: number
        completed: number
      }
      pagination: {
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/controls/${controlId}/tasks${queryString ? `?${queryString}` : ''}`)
  }

  // ============================================================================
  // WEBHOOKS
  // ============================================================================

  /**
   * Get all available webhook event types
   */
  async getWebhookEvents() {
    return this.request<{
      success: boolean
      events: Array<{
        type: string
        description: string
        category: string
      }>
      count: number
    }>('/api/webhooks/events')
  }

  /**
   * List all webhook configurations
   */
  async getWebhooks() {
    return this.request<{
      success: boolean
      webhooks: Array<{
        id: string
        name: string
        description: string | null
        url: string
        auth_type: 'none' | 'hmac' | 'bearer' | 'basic'
        events: string[]
        filters: Record<string, any>
        enabled: number
        retry_count: number
        retry_delay_seconds: number
        timeout_seconds: number
        http_method: 'POST' | 'PUT'
        content_type: string
        custom_headers: Record<string, string>
        last_triggered_at: number | null
        last_success_at: number | null
        last_failure_at: number | null
        success_count: number
        failure_count: number
        created_at: number
        updated_at: number
      }>
      count: number
    }>('/api/webhooks')
  }

  /**
   * Get a single webhook configuration with delivery history
   */
  async getWebhook(id: string) {
    return this.request<{
      success: boolean
      webhook: {
        id: string
        name: string
        description: string | null
        url: string
        auth_type: 'none' | 'hmac' | 'bearer' | 'basic'
        events: string[]
        filters: Record<string, any>
        enabled: number
        retry_count: number
        retry_delay_seconds: number
        timeout_seconds: number
        http_method: 'POST' | 'PUT'
        content_type: string
        custom_headers: Record<string, string>
        last_triggered_at: number | null
        last_success_at: number | null
        last_failure_at: number | null
        success_count: number
        failure_count: number
        created_at: number
        updated_at: number
      }
      delivery_history: Array<{
        id: string
        event_type: string
        event_id: string | null
        status: 'pending' | 'success' | 'failed' | 'retrying'
        response_status: number | null
        error_message: string | null
        triggered_at: number
        delivered_at: number | null
        duration_ms: number | null
      }>
    }>(`/api/webhooks/${id}`)
  }

  /**
   * Create a new webhook configuration
   */
  async createWebhook(data: {
    name: string
    description?: string
    url: string
    secret?: string
    auth_type?: 'none' | 'hmac' | 'bearer' | 'basic'
    auth_value?: string
    events: string[]
    filters?: Record<string, any>
    enabled?: boolean
    retry_count?: number
    retry_delay_seconds?: number
    timeout_seconds?: number
    http_method?: 'POST' | 'PUT'
    content_type?: string
    custom_headers?: Record<string, string>
  }) {
    return this.request<{
      success: boolean
      webhook: { id: string; name: string; url: string; events: string[]; enabled: boolean }
      message: string
    }>('/api/webhooks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a webhook configuration
   */
  async updateWebhook(id: string, data: Partial<{
    name: string
    description: string | null
    url: string
    secret: string | null
    auth_type: 'none' | 'hmac' | 'bearer' | 'basic'
    auth_value: string | null
    events: string[]
    filters: Record<string, any>
    enabled: boolean
    retry_count: number
    retry_delay_seconds: number
    timeout_seconds: number
    http_method: 'POST' | 'PUT'
    content_type: string
    custom_headers: Record<string, string>
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/webhooks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a webhook configuration
   */
  async deleteWebhook(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/webhooks/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Test a webhook by sending a test event
   */
  async testWebhook(id: string) {
    return this.request<{
      success: boolean
      status_code: number | null
      response_time_ms: number
      message: string
      error: string | null
    }>(`/api/webhooks/${id}/test`, {
      method: 'POST',
    })
  }

  /**
   * Get delivery history for a webhook
   */
  async getWebhookDeliveries(id: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())
    const query = params.toString()

    return this.request<{
      success: boolean
      deliveries: Array<{
        id: string
        event_type: string
        event_id: string | null
        status: 'pending' | 'success' | 'failed' | 'retrying'
        response_status: number | null
        error_message: string | null
        triggered_at: number
        delivered_at: number | null
        duration_ms: number | null
      }>
      count: number
      total: number
    }>(`/api/webhooks/${id}/deliveries${query ? `?${query}` : ''}`)
  }

  // ============================================================================
  // SCAN PROFILES (#181 - Compliance-Specific Scan Profiles)
  // ============================================================================

  /**
   * Get all scan profiles
   */
  async getScanProfiles(options?: { framework_id?: string; type?: 'system' | 'custom' }) {
    const params = new URLSearchParams()
    if (options?.framework_id) params.set('framework_id', options.framework_id)
    if (options?.type) params.set('type', options.type)
    const query = params.toString()

    return this.request<{
      success: boolean
      profiles: Array<{
        id: string
        name: string
        description: string | null
        framework_id: string | null
        framework_name: string | null
        profile_type: 'system' | 'custom'
        scan_settings: Record<string, any>
        nuclei_templates: string[]
        nuclei_severity: string | null
        nmap_scripts: string[]
        port_range: string | null
        enabled: number
        linked_framework_name: string | null
        linked_framework_display_name: string | null
        created_at: number
        updated_at: number
      }>
    }>(`/api/scanners/profiles${query ? `?${query}` : ''}`)
  }

  /**
   * Get a single scan profile by ID
   */
  async getScanProfile(id: string) {
    return this.request<{
      success: boolean
      profile: {
        id: string
        name: string
        description: string | null
        framework_id: string | null
        framework_name: string | null
        profile_type: 'system' | 'custom'
        scan_settings: Record<string, any>
        nuclei_templates: string[]
        nuclei_severity: string | null
        nmap_scripts: string[]
        port_range: string | null
        enabled: number
        linked_framework_name: string | null
        linked_framework_display_name: string | null
        created_at: number
        updated_at: number
      }
    }>(`/api/scanners/profiles/${id}`)
  }

  /**
   * Create a new scan profile
   */
  async createScanProfile(data: {
    name: string
    description?: string
    framework_id?: string
    scan_settings?: Record<string, any>
    nuclei_templates?: string[]
    nuclei_severity?: string
    nmap_scripts?: string[]
    port_range?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      profile: { id: string; name: string }
    }>('/api/scanners/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a scan profile
   */
  async updateScanProfile(id: string, data: Partial<{
    name: string
    description: string
    framework_id: string | null
    scan_settings: Record<string, any>
    nuclei_templates: string[]
    nuclei_severity: string
    nmap_scripts: string[]
    port_range: string
    enabled: boolean
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a scan profile
   */
  async deleteScanProfile(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/scanners/profiles/${id}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // SCAN RESULT EXPORT (#185 - Scan Result Export)
  // ============================================================================

  /**
   * Export scan result in various formats (JSON, CSV, PDF/HTML)
   */
  async exportScanResult(id: string, format: 'json' | 'csv' | 'pdf' = 'json') {
    if (format === 'json') {
      return this.request<{
        success: boolean
        export_format: 'json'
        data: {
          scan: {
            id: string
            config_name: string
            scan_type: string
            status: string
            started_at: string | null
            completed_at: string | null
            duration_seconds: number | null
            hosts_scanned: number
          }
          summary: {
            total_vulnerabilities: number
            critical: number
            high: number
            medium: number
            low: number
            info: number
          }
          discovered_hosts: Array<{
            ip: string
            hostname: string | null
            state: string
            os: string | null
            ports: Array<{
              port: number
              protocol: string
              service: string | null
            }>
          }>
          vulnerabilities: Array<{
            id: string
            host: string
            port: number | null
            protocol: string | null
            service: string | null
            vulnerability_id: string | null
            vulnerability_name: string
            severity: string
            cvss_score: number | null
            description: string | null
            solution: string | null
            reference_urls: string | null
            is_false_positive: boolean
            source: string | null
          }>
        }
      }>(`/api/scanners/results/${id}/export?format=json`)
    }
    // For CSV and PDF, return a download URL
    return {
      success: true,
      downloadUrl: `${getApiBase()}/api/scanners/results/${id}/export?format=${format}`,
    }
  }

  /**
   * Download scan result export (CSV or PDF)
   * This method triggers a file download
   */
  async downloadScanExport(id: string, format: 'csv' | 'pdf'): Promise<void> {
    const url = `${getApiBase()}/api/scanners/results/${id}/export?format=${format}`
    const token = localStorage.getItem('auth_token')

    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new Error('Failed to download export')
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `scan-report-${id}.${format === 'pdf' ? 'html' : format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  // ============================================================================
  // SCAN COMPARISON (#81, #101 - Scan Comparison/Diff)
  // ============================================================================

  /**
   * Compare two scan results to find new, fixed, unchanged, and regression vulnerabilities.
   * Uses path-based endpoint for better RESTful design.
   * @param currentScanId - The more recent scan to analyze
   * @param previousScanId - The earlier scan to compare against
   */
  async compareScanResults(currentScanId: string, previousScanId: string) {
    return this.request<{
      success: boolean
      comparison: {
        current_scan: {
          id: string
          config_name: string
          started_at: number
          vulnerabilities_found: number
        }
        previous_scan: {
          id: string
          config_name: string
          started_at: number
          vulnerabilities_found: number
        }
        summary: {
          new_count: number
          fixed_count: number
          unchanged_count: number
          regression_count: number
          net_change: number
          severity_delta: {
            critical: number
            high: number
            medium: number
            low: number
            info: number
          }
        }
        new_vulnerabilities: Array<{
          id: string
          fingerprint: string
          host: string
          port: number | null
          vulnerability_id: string | null
          vulnerability_name: string
          severity: string
          cvss_score: number | null
          vuln_status: string
          first_seen_at: number
          seen_count: number
        }>
        fixed_vulnerabilities: Array<{
          id: string
          fingerprint: string
          host: string
          port: number | null
          vulnerability_id: string | null
          vulnerability_name: string
          severity: string
          cvss_score: number | null
        }>
        regressions: Array<{
          id: string
          fingerprint: string
          host: string
          port: number | null
          vulnerability_id: string | null
          vulnerability_name: string
          severity: string
          cvss_score: number | null
        }>
        unchanged_vulnerabilities: Array<{
          id: string
          fingerprint: string
          host: string
          port: number | null
          vulnerability_id: string | null
          vulnerability_name: string
          severity: string
          cvss_score: number | null
        }>
      }
    }>(`/api/scanners/results/${currentScanId}/compare/${previousScanId}`)
  }

  // ============================================================================
  // TREND ANALYSIS (#81 - Scan Trend Analysis)
  // ============================================================================

  /**
   * Get vulnerability trend data for a scan configuration over time
   * @param configId - The scan configuration ID
   * @param days - Number of days of history (default: 30, max: 365)
   */
  async getScanTrends(configId: string, days: number = 30) {
    const query = days ? `?days=${days}` : ''
    return this.request<{
      success: boolean
      config: {
        id: string
        name: string
      }
      days_range: number
      trend: 'improving' | 'stable' | 'regressing'
      statistics: {
        total_scans: number
        first_scan: number | null
        last_scan: number | null
        avg_vulnerabilities: number
        min_vulnerabilities: number
        max_vulnerabilities: number
      }
      lifecycle: Array<{
        vuln_status: string
        count: number
        avg_seen_count: number
      }>
      data: Array<{
        scan_id: string
        date: number
        total: number
        critical: number
        high: number
        medium: number
        low: number
        info: number
      }>
    }>(`/api/scanners/configs/${configId}/trends${query}`)
  }

  /**
   * Get the previous scan result for a given scan (for comparison)
   * @param scanId - The scan result ID
   */
  async getPreviousScan(scanId: string) {
    return this.request<{
      success: boolean
      has_previous: boolean
      message?: string
      previous_scan?: {
        id: string
        started_at: number
        completed_at: number | null
        status: string
        vulnerabilities_found: number
        critical_count: number
        high_count: number
        medium_count: number
        low_count: number
        info_count: number
      }
    }>(`/api/scanners/results/${scanId}/previous`)
  }

  /**
   * Get scan delta comparing current scan to previous scan
   * @param scanId - The scan result ID
   */
  async getScanDelta(scanId: string) {
    return this.request<{
      success: boolean
      has_previous: boolean
      message?: string
      delta?: {
        new_count: number
        fixed_count: number
        trend: 'improving' | 'stable' | 'regressing'
      }
    }>(`/api/scanners/results/${scanId}/delta`)
  }

  /**
   * Get vulnerability history for a specific fingerprint
   * @param fingerprint - The vulnerability fingerprint
   * @param configId - Optional: filter to specific scan config
   */
  async getVulnerabilityHistory(fingerprint: string, configId?: string) {
    const params = new URLSearchParams({ fingerprint })
    if (configId) params.append('config_id', configId)
    return this.request<{
      success: boolean
      fingerprint: string
      history: Array<{
        id: string
        scan_result_id: string
        host: string
        port: number | null
        vulnerability_id: string | null
        vulnerability_name: string
        severity: string
        vuln_status: string
        first_seen_at: number
        last_seen_at: number
        seen_count: number
        scan_date: number
        scan_config_id: string
        config_name: string
      }>
      total: number
    }>(`/api/scanners/vulnerabilities/history?${params.toString()}`)
  }

  // ============================================================================
  // Remediation Tracking (#122)
  // ============================================================================

  /**
   * Update remediation status for a vulnerability
   */
  async updateVulnerabilityRemediation(vulnerabilityId: string, data: {
    status?: 'new' | 'acknowledged' | 'in_progress' | 'remediated' | 'accepted_risk' | 'false_positive' | 'deferred'
    assigned_to?: string | null
    due_date?: number | null
    notes?: string | null
    risk_acceptance_reason?: string
    risk_acceptance_expires_at?: number
  }) {
    return this.request<{
      success: boolean
      message: string
      vulnerability: {
        id: string
        remediation_status: string
        assigned_to: string | null
        assigned_to_username: string | null
        remediation_due_date: number | null
        remediation_notes: string | null
        remediated_at: number | null
        remediated_by: string | null
      }
    }>(`/api/scanners/vulnerabilities/${vulnerabilityId}/remediate`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Bulk update remediation status for multiple vulnerabilities
   */
  async bulkUpdateRemediation(data: {
    vulnerability_ids: string[]
    status: 'new' | 'acknowledged' | 'in_progress' | 'remediated' | 'accepted_risk' | 'false_positive' | 'deferred'
    assigned_to?: string
    due_date?: number
    notes?: string
  }) {
    return this.request<{
      success: boolean
      message: string
      updated: number
      skipped: number
    }>('/api/scanners/vulnerabilities/bulk-remediate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get remediation statistics dashboard data
   */
  async getRemediationStats() {
    return this.request<{
      success: boolean
      stats: {
        by_status: Array<{
          status: string
          count: number
          critical: number
          high: number
          medium: number
          low: number
        }>
        overdue: number
        due_soon: number
        by_assignee: Array<{
          assigned_to: string
          assignee_name: string | null
          total: number
          open: number
          remediated: number
          overdue: number
        }>
        by_severity: Array<{
          severity: string
          total: number
          open: number
          remediated: number
        }>
        avg_time_to_remediate: Array<{
          severity: string
          avg_time_ms: number
        }>
        recent_remediated: Array<{
          id: string
          vulnerability_name: string
          severity: string
          remediation_status: string
          remediated_at: number
          host: string
          remediated_by_name: string | null
        }>
      }
    }>('/api/scanners/vulnerabilities/remediation-stats')
  }

  /**
   * Get open vulnerabilities needing remediation
   */
  async getOpenVulnerabilities(params?: {
    severity?: string
    assigned_to?: string
    status?: string
    overdue?: boolean
    sort_by?: 'severity' | 'due_date' | 'host'
    sort_order?: 'asc' | 'desc'
    limit?: number
    offset?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.severity) searchParams.set('severity', params.severity)
    if (params?.assigned_to) searchParams.set('assigned_to', params.assigned_to)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.overdue) searchParams.set('overdue', 'true')
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const queryString = searchParams.toString()
    return this.request<{
      success: boolean
      vulnerabilities: Array<{
        id: string
        vulnerability_name: string
        severity: string
        host: string
        port: number | null
        remediation_status: string | null
        assigned_to: string | null
        assigned_to_username: string | null
        remediation_due_date: number | null
        remediation_notes: string | null
        config_name: string
        scan_result_id: string
        is_overdue: number
      }>
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/scanners/vulnerabilities/open${queryString ? `?${queryString}` : ''}`)
  }

  // ============================================================================
  // API Keys
  // ============================================================================

  /**
   * List all API keys for the current user
   */
  async getApiKeys() {
    return this.request<{
      success: boolean
      api_keys: Array<{
        id: string
        tenant_id: string
        user_id: string
        name: string
        key_prefix: string
        scopes: string[]
        last_used_at: number | null
        use_count: number
        expires_at: number | null
        revoked_at: number | null
        created_at: number
        updated_at: number
        is_expired: boolean
        is_revoked: boolean
      }>
      count: number
    }>('/api/api-keys')
  }

  /**
   * Create a new API key
   * IMPORTANT: The 'key' field is only returned on creation and cannot be retrieved again
   */
  async createApiKey(data: {
    name: string
    scopes?: string[]
    expires_in_days?: number
  }) {
    return this.request<{
      success: boolean
      api_key: {
        id: string
        name: string
        key: string // Only returned on creation!
        key_prefix: string
        scopes: string[]
        expires_at: number | null
        created_at: number
      }
      message: string
    }>('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an API key (name and/or scopes)
   */
  async updateApiKey(id: string, data: {
    name?: string
    scopes?: string[]
  }) {
    return this.request<{
      success: boolean
      api_key: {
        id: string
        name: string
        key_prefix: string
        scopes: string[]
        last_used_at: number | null
        use_count: number
        expires_at: number | null
        revoked_at: number | null
        created_at: number
        updated_at: number
      }
    }>(`/api/api-keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Revoke an API key
   */
  async revokeApiKey(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/api-keys/${id}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // DOCUMENT COLLABORATIVE EDITING (Issue #177)
  // ============================================================================

  /**
   * Get lock status and active editors for a document.
   *
   * Returns information about who currently holds the edit lock (if any)
   * and a list of users actively viewing/editing the document.
   *
   * @param documentId - The document ID to check
   * @returns Lock status, lock holder info, and list of active editors
   *
   * @example
   * const { is_locked, lock, active_editors } = await api.getDocumentLockStatus(docId);
   * if (is_locked) {
   *   console.log(`Locked by ${lock.locked_by_name} until ${new Date(lock.expires_at)}`);
   * }
   */
  async getDocumentLockStatus(documentId: string) {
    return this.request<{
      success: boolean
      is_locked: boolean
      lock: {
        locked_by: string
        locked_by_username: string
        locked_by_name: string
        locked_at: number
        expires_at: number
        expires_in_ms: number
      } | null
      active_editors: Array<{
        session_id: string
        user_id: string
        username: string
        name: string
        started_at: number
        last_heartbeat: number
      }>
    }>(`/api/documents/${documentId}/lock-status`)
  }

  /**
   * Acquire an edit lock on a document.
   *
   * The lock prevents other users from editing the document. Locks auto-expire
   * after 15 minutes of inactivity. Use heartbeat() to extend the lock while editing.
   *
   * @param documentId - The document ID to lock
   * @returns Lock details including expiration time
   *
   * @example
   * try {
   *   const { lock } = await api.lockDocument(docId);
   *   console.log(`Lock acquired, expires at ${new Date(lock.expires_at)}`);
   * } catch (error) {
   *   if (error.status === 409) {
   *     console.log(`Document locked by ${error.locked_by}`);
   *   }
   * }
   */
  async lockDocument(documentId: string) {
    return this.request<{
      success: boolean
      message: string
      lock: {
        locked_by: string
        locked_at: number
        expires_at: number
        expires_in_ms: number
      }
    }>(`/api/documents/${documentId}/lock`, { method: 'POST' })
  }

  /**
   * Release an edit lock on a document.
   *
   * Only the lock holder or an admin can release a lock.
   *
   * @param documentId - The document ID to unlock
   * @returns Success confirmation
   */
  async unlockDocument(documentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${documentId}/unlock`, { method: 'POST' })
  }

  /**
   * Send a heartbeat to extend the lock and keep the session active.
   *
   * Should be called periodically (e.g., every 60 seconds) while editing.
   * If you hold the lock, it will be extended for another 15 minutes.
   *
   * @param documentId - The document ID
   * @returns Whether the lock was extended
   */
  async documentHeartbeat(documentId: string) {
    return this.request<{
      success: boolean
      lock_extended: boolean
      expires_at: number | null
    }>(`/api/documents/${documentId}/heartbeat`, { method: 'POST' })
  }

  /**
   * Force unlock a document (admin only).
   *
   * Use this when a user has abandoned a document without releasing the lock.
   *
   * @param documentId - The document ID to force unlock
   * @returns Success confirmation with previous lock holder info
   */
  async forceUnlockDocument(documentId: string) {
    return this.request<{
      success: boolean
      message: string
      previous_lock_holder: string
    }>(`/api/documents/${documentId}/force-unlock`, { method: 'POST' })
  }

  /**
   * Request edit access to a locked document.
   *
   * Sends a notification to the current lock holder asking them to release the lock.
   *
   * @param documentId - The document ID
   * @returns Success confirmation with notified user info
   */
  async requestDocumentAccess(documentId: string) {
    return this.request<{
      success: boolean
      message: string
      notified_user: string
    }>(`/api/documents/${documentId}/request-access`, { method: 'POST' })
  }

  /**
   * Get list of active editors for a document.
   *
   * @param documentId - The document ID
   * @returns List of users currently editing/viewing the document
   */
  async getDocumentEditors(documentId: string) {
    return this.request<{
      success: boolean
      editors: Array<{
        session_id: string
        user_id: string
        username: string
        name: string
        started_at: number
        last_heartbeat: number
        idle_ms: number
      }>
      count: number
    }>(`/api/documents/${documentId}/editors`)
  }

  // ============================================================================
  // DOCUMENT COMMENTS (Issue #177)
  // ============================================================================

  /**
   * Get comments for a document.
   *
   * @param documentId - The document ID
   * @param status - Filter by status: 'all', 'open', 'resolved', 'accepted', 'rejected'
   * @returns List of comments
   */
  async getDocumentComments(documentId: string, status: 'all' | 'open' | 'resolved' | 'accepted' | 'rejected' = 'all') {
    const params = status !== 'all' ? `?status=${status}` : ''
    return this.request<{
      success: boolean
      comments: Array<{
        id: string
        document_id: string
        version: number
        user_id: string
        author_username: string
        author_name: string
        selection_start: number | null
        selection_end: number | null
        comment_text: string
        is_suggestion: number
        suggested_text: string | null
        status: 'open' | 'resolved' | 'accepted' | 'rejected'
        resolved_at: number | null
        resolved_by: string | null
        resolved_by_username: string | null
        created_at: number
        updated_at: number
      }>
      count: number
    }>(`/api/documents/${documentId}/comments${params}`)
  }

  /**
   * Add a comment to a document.
   *
   * @param documentId - The document ID
   * @param comment - Comment details
   * @returns Created comment ID
   */
  async addDocumentComment(documentId: string, comment: {
    comment_text: string
    selection_start?: number
    selection_end?: number
    is_suggestion?: boolean
    suggested_text?: string
    version?: number
  }) {
    return this.request<{
      success: boolean
      comment: { id: string }
      message: string
    }>(`/api/documents/${documentId}/comments`, {
      method: 'POST',
      body: JSON.stringify(comment),
    })
  }

  /**
   * Update a comment (change status or text).
   *
   * @param documentId - The document ID
   * @param commentId - The comment ID
   * @param updates - Fields to update
   * @returns Success confirmation
   */
  async updateDocumentComment(documentId: string, commentId: string, updates: {
    status?: 'open' | 'resolved' | 'accepted' | 'rejected'
    comment_text?: string
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${documentId}/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Delete a comment.
   *
   * Only the comment author or an admin can delete a comment.
   *
   * @param documentId - The document ID
   * @param commentId - The comment ID
   * @returns Success confirmation
   */
  async deleteDocumentComment(documentId: string, commentId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/documents/${documentId}/comments/${commentId}`, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // EVIDENCE RETENTION POLICY (Issue #182)
  // ============================================================================

  /**
   * Get evidence retention policy settings.
   *
   * Returns the organization's retention policy configuration including
   * statistics about evidence that would be affected.
   *
   * @returns Retention policy and statistics
   */
  async getRetentionPolicy() {
    return this.request<{
      success: boolean
      retention_policy: {
        enabled: boolean
        period_months: number
        action: 'archive' | 'delete'
        exempt_controls: string[]
        exempt_evidence_types: string[]
        notify_before_days: number
        last_run_at: number | null
      }
      stats: {
        total_evidence: number
        archived_count: number
        pending_archival: number
      }
    }>('/api/settings/retention-policy')
  }

  /**
   * Update evidence retention policy settings.
   *
   * Allows administrators to configure automatic evidence archival/deletion
   * based on age and exemption rules.
   *
   * @param data - Retention policy settings to update
   * @returns Updated retention policy
   */
  async updateRetentionPolicy(data: {
    enabled?: boolean
    period_months?: number
    action?: 'archive' | 'delete'
    exempt_controls?: string[]
    exempt_evidence_types?: string[]
    notify_before_days?: number
  }) {
    return this.request<{
      success: boolean
      message: string
      retention_policy: any
    }>('/api/settings/retention-policy', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Preview retention policy impact.
   *
   * Returns a preview of evidence that would be affected by the retention
   * policy with the specified settings.
   *
   * @param periodMonths - Retention period in months
   * @param limit - Maximum number of records to return
   * @returns Preview of affected evidence
   */
  async previewRetentionPolicy(periodMonths: number, limit?: number) {
    const params = new URLSearchParams({
      period_months: periodMonths.toString(),
      ...(limit && { limit: limit.toString() }),
    })
    return this.request<{
      success: boolean
      preview: {
        period_months: number
        cutoff_date: number
        total_affected: number
        sample_evidence: any[]
      }
    }>(`/api/settings/retention-policy/preview?${params}`)
  }

  // ============================================================================
  // EVIDENCE ARCHIVAL (Issue #182)
  // ============================================================================

  /**
   * Archive a single evidence item.
   *
   * @param id - Evidence ID to archive
   * @param reason - Optional reason for archiving
   * @returns Success confirmation
   */
  async archiveEvidence(id: string, reason?: string) {
    return this.request<{
      success: boolean
      message: string
      archived_at: number
    }>(`/api/evidence/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'manual' }),
    })
  }

  /**
   * Restore an archived evidence item.
   *
   * @param id - Evidence ID to restore
   * @returns Success confirmation
   */
  async restoreEvidence(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/evidence/${id}/restore`, {
      method: 'POST',
    })
  }

  /**
   * Bulk archive multiple evidence items.
   *
   * @param evidenceIds - Array of evidence IDs to archive
   * @param reason - Optional reason for archiving
   * @returns Results of bulk operation
   */
  async bulkArchiveEvidence(evidenceIds: string[], reason?: string) {
    return this.request<{
      success: boolean
      message: string
      archived: string[]
      failed: Array<{ id: string; error: string }>
    }>('/api/evidence/archive/bulk', {
      method: 'POST',
      body: JSON.stringify({
        evidence_ids: evidenceIds,
        reason: reason || 'bulk_archive',
      }),
    })
  }

  /**
   * Get evidence archive logs.
   *
   * Returns audit logs for evidence archival/restoration operations.
   *
   * @param options - Filter options
   * @returns Archive logs with pagination
   */
  async getEvidenceArchiveLogs(options?: {
    evidence_id?: string
    action?: 'archived' | 'restored' | 'deleted'
    limit?: number
    offset?: number
  }) {
    const params = new URLSearchParams()
    if (options?.evidence_id) params.append('evidence_id', options.evidence_id)
    if (options?.action) params.append('action', options.action)
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())

    return this.request<{
      success: boolean
      logs: any[]
      pagination: {
        total: number
        limit: number
        offset: number
        hasMore: boolean
      }
    }>(`/api/evidence/archive/logs?${params}`)
  }

  // ============================================================================
  // CUSTOM FIELDS
  // ============================================================================

  /**
   * Get all custom field definitions for a tenant.
   * @param entityType - Filter by entity type (risk, control, vendor, evidence, document)
   * @param activeOnly - Only return active definitions (default: true)
   */
  async getCustomFieldDefinitions(entityType?: string, activeOnly: boolean = true) {
    const params = new URLSearchParams()
    if (entityType) params.append('entity_type', entityType)
    if (!activeOnly) params.append('active', 'false')
    return this.request<{
      success: boolean
      definitions: CustomFieldDefinition[]
      count: number
    }>(`/api/custom-fields/definitions?${params}`)
  }

  /**
   * Create a custom field definition.
   */
  async createCustomFieldDefinition(data: CreateCustomFieldInput) {
    return this.request<{
      success: boolean
      definition: CustomFieldDefinition
    }>('/api/custom-fields/definitions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a custom field definition.
   */
  async updateCustomFieldDefinition(id: string, data: UpdateCustomFieldInput) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/custom-fields/definitions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a custom field definition.
   */
  async deleteCustomFieldDefinition(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/custom-fields/definitions/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get custom field values for an entity.
   */
  async getCustomFieldValues(entityType: string, entityId: string) {
    return this.request<{
      success: boolean
      entity_type: string
      entity_id: string
      fields: Array<{
        definition: CustomFieldDefinition
        value: any
      }>
    }>(`/api/custom-fields/values/${entityType}/${entityId}`)
  }

  /**
   * Update custom field values for an entity.
   */
  async updateCustomFieldValues(entityType: string, entityId: string, values: Record<string, any>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/custom-fields/values/${entityType}/${entityId}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    })
  }

  /**
   * Reorder custom field definitions.
   */
  async reorderCustomFields(entityType: string, order: Array<{ id: string; display_order: number }>) {
    return this.request<{
      success: boolean
      message: string
    }>('/api/custom-fields/reorder', {
      method: 'POST',
      body: JSON.stringify({ entity_type: entityType, order }),
    })
  }

  // ============================================================================
  // TASK TIME TRACKING (Issue #140)
  // ============================================================================

  /**
   * Log time spent on a task (manual entry).
   *
   * @param taskId - The ID of the task
   * @param entry - Time entry details
   * @param entry.date - The date of work (YYYY-MM-DD)
   * @param entry.hours - Number of hours worked
   * @param entry.minutes - Number of minutes worked
   * @param entry.description - Optional description of work done
   */
  async createTaskTimeEntry(
    taskId: string,
    entry: {
      date: string
      hours?: number
      minutes?: number
      description?: string
    }
  ) {
    return this.request<{
      success: boolean
      time_entry: TaskTimeEntry
    }>(`/api/tasks/${taskId}/time-entries`, {
      method: 'POST',
      body: JSON.stringify(entry),
    })
  }

  /**
   * Get all time entries for a task.
   */
  async getTaskTimeEntries(taskId: string) {
    return this.request<{
      success: boolean
      time_entries: TaskTimeEntry[]
      summary: TaskTimeSummary
    }>(`/api/tasks/${taskId}/time-entries`)
  }

  /**
   * Delete a time entry.
   */
  async deleteTaskTimeEntry(taskId: string, entryId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tasks/${taskId}/time-entries/${entryId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Start a timer for a task.
   */
  async startTaskTimer(taskId: string) {
    return this.request<{
      success: boolean
      message: string
      timer: {
        entry_id: string
        task_id: string
        started_at: number
      }
    }>(`/api/tasks/${taskId}/timer/start`, {
      method: 'POST',
    })
  }

  /**
   * Stop a running timer for a task.
   */
  async stopTaskTimer(taskId: string, description?: string) {
    return this.request<{
      success: boolean
      message: string
      time_entry: TaskTimeEntry
    }>(`/api/tasks/${taskId}/timer/stop`, {
      method: 'POST',
      body: JSON.stringify({ description }),
    })
  }

  /**
   * Get running timer for a task (if any).
   */
  async getTaskTimer(taskId: string) {
    return this.request<{
      success: boolean
      timer: TaskTimeEntry | null
      running: boolean
    }>(`/api/tasks/${taskId}/timer`)
  }

  /**
   * Get the current user's running timer (across all tasks).
   */
  async getMyTimer() {
    return this.request<{
      success: boolean
      timer: (TaskTimeEntry & { task_title: string; task_status: string }) | null
      running: boolean
    }>('/api/tasks/my-timer')
  }

  /**
   * Get time report across all tasks with filters.
   */
  async getTimeReport(filters?: {
    start?: string
    end?: string
    user_id?: string
    task_id?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.start) params.set('start', filters.start)
    if (filters?.end) params.set('end', filters.end)
    if (filters?.user_id) params.set('user_id', filters.user_id)
    if (filters?.task_id) params.set('task_id', filters.task_id)
    const queryString = params.toString()
    return this.request<{
      success: boolean
      entries: TaskTimeEntry[]
      summary: TimeReportSummary
      filters: typeof filters
    }>(`/api/tasks/time-report${queryString ? `?${queryString}` : ''}`)
  }

  // ============================================================================
  // KEY RISK INDICATORS (KRI) ENDPOINTS (Issue #127)
  // ============================================================================

  /**
   * Get all KRIs with optional filtering.
   */
  async getKRIs(filters?: {
    category?: string
    status?: 'green' | 'yellow' | 'red'
    is_active?: boolean
    owner_id?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.category) params.append('category', filters.category)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active))
    if (filters?.owner_id) params.append('owner_id', filters.owner_id)
    const queryString = params.toString()
    return this.request<{
      success: boolean
      kris: KRI[]
      count: number
    }>(`/api/kri${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Get KRI dashboard with summary and current status.
   */
  async getKRIDashboard() {
    return this.request<{
      success: boolean
      dashboard: {
        summary: {
          total: number
          green: number
          yellow: number
          red: number
          no_data: number
        }
        unacknowledged_alerts: number
        kris: KRI[]
        by_category: Record<string, KRI[]>
      }
    }>('/api/kri/dashboard')
  }

  /**
   * Seed example KRIs for the tenant.
   * Only works if no KRIs exist yet.
   */
  async seedKRIs() {
    return this.request<{
      success: boolean
      message: string
      kris?: KRI[]
      error?: string
      count?: number
    }>('/api/kri/seed', {
      method: 'POST',
    })
  }

  /**
   * Get KRI alerts.
   */
  async getKRIAlerts(acknowledged?: boolean, limit = 50) {
    const params = new URLSearchParams()
    if (acknowledged !== undefined) params.append('acknowledged', String(acknowledged))
    params.append('limit', String(limit))
    return this.request<{
      success: boolean
      alerts: KRIAlert[]
      count: number
    }>(`/api/kri/alerts?${params.toString()}`)
  }

  /**
   * Acknowledge a KRI alert.
   */
  async acknowledgeKRIAlert(alertId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/kri/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    })
  }

  /**
   * Dismiss a KRI alert.
   */
  async dismissKRIAlert(alertId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/kri/alerts/${alertId}/dismiss`, {
      method: 'POST',
    })
  }

  /**
   * Get a single KRI with details, linked risks, and recent values.
   */
  async getKRI(id: string) {
    return this.request<{
      success: boolean
      kri: KRI & {
        linked_risks: KRIRiskLink[]
        recent_values: KRIValue[]
      }
    }>(`/api/kri/${id}`)
  }

  /**
   * Create a new KRI.
   */
  async createKRI(data: CreateKRIInput) {
    return this.request<{
      success: boolean
      kri: KRI
    }>('/api/kri', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a KRI.
   */
  async updateKRI(id: string, data: UpdateKRIInput) {
    return this.request<{
      success: boolean
      kri: KRI
    }>(`/api/kri/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a KRI (soft delete by default).
   */
  async deleteKRI(id: string, hardDelete = false) {
    const params = hardDelete ? '?hard=true' : ''
    return this.request<{
      success: boolean
      message: string
    }>(`/api/kri/${id}${params}`, {
      method: 'DELETE',
    })
  }

  /**
   * Record a new value for a KRI.
   */
  async recordKRIValue(kriId: string, data: { value: number; notes?: string; source?: string }) {
    return this.request<{
      success: boolean
      value: KRIValue
      status_changed: boolean
      previous_status?: string
    }>(`/api/kri/${kriId}/values`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get value history for a KRI.
   */
  async getKRIHistory(kriId: string, options?: { limit?: number; start_date?: number; end_date?: number }) {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', String(options.limit))
    if (options?.start_date) params.append('start_date', String(options.start_date))
    if (options?.end_date) params.append('end_date', String(options.end_date))
    const queryString = params.toString()
    return this.request<{
      success: boolean
      kri: Pick<KRI, 'name' | 'green_threshold' | 'yellow_threshold' | 'red_threshold' | 'direction' | 'unit' | 'metric_type'>
      history: KRIValue[]
      count: number
    }>(`/api/kri/${kriId}/history${queryString ? `?${queryString}` : ''}`)
  }

  /**
   * Link a risk to a KRI.
   */
  async linkRiskToKRI(kriId: string, riskId: string, data?: { relationship_type?: string; notes?: string }) {
    return this.request<{
      success: boolean
      link: KRIRiskLink
    }>(`/api/kri/${kriId}/risks`, {
      method: 'POST',
      body: JSON.stringify({ risk_id: riskId, ...data }),
    })
  }

  /**
   * Unlink a risk from a KRI.
   */
  async unlinkRiskFromKRI(kriId: string, riskId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/kri/${kriId}/risks/${riskId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get KRIs linked to a specific risk.
   */
  async getKRIsForRisk(riskId: string) {
    return this.request<{
      success: boolean
      kris: Array<KRI & { relationship_type: string; link_notes?: string; linked_at: number }>
      count: number
    }>(`/api/kri/by-risk/${riskId}`)
  }

  // ============================================================================
  // RBAC PERMISSIONS ENDPOINTS (Issue #138)
  // ============================================================================

  /**
   * Get all permissions for the tenant.
   */
  async getPermissions() {
    return this.request<{
      success: boolean
      permissions: Permission[]
      grouped: Record<string, Permission[]>
      count: number
    }>('/api/permissions')
  }

  /**
   * Create a custom permission (admin only).
   */
  async createPermission(data: { resource: string; action: string; description?: string }) {
    return this.request<{
      success: boolean
      permission: Permission
    }>('/api/permissions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a custom permission (admin only).
   */
  async deletePermission(permissionId: string) {
    return this.request<{ success: boolean }>(`/api/permissions/${permissionId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get all roles with their permissions.
   */
  async getRoles() {
    return this.request<{
      success: boolean
      roles: Role[]
      count: number
    }>('/api/permissions/roles')
  }

  /**
   * Create a custom role (admin only).
   */
  async createRole(data: { name: string; description?: string; permission_ids?: string[] }) {
    return this.request<{
      success: boolean
      role: Role
    }>('/api/permissions/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a role (admin only).
   */
  async updateRole(
    roleId: string,
    data: { name?: string; description?: string; is_active?: boolean; permission_ids?: string[] }
  ) {
    return this.request<{ success: boolean }>(`/api/permissions/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a custom role (admin only).
   */
  async deleteRole(roleId: string) {
    return this.request<{ success: boolean }>(`/api/permissions/roles/${roleId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get a user's effective permissions and overrides.
   */
  async getUserPermissions(userId: string) {
    return this.request<{
      success: boolean
    } & UserPermissionDetails>(`/api/permissions/users/${userId}`)
  }

  /**
   * Set a permission override for a user (admin only).
   */
  async setUserPermissionOverride(
    userId: string,
    data: { permission_id: string; grant_type: 'grant' | 'deny'; expires_at?: number; reason?: string }
  ) {
    return this.request<{ success: boolean }>(`/api/permissions/users/${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Remove a permission override from a user (admin only).
   */
  async removeUserPermissionOverride(userId: string, permissionId: string) {
    return this.request<{ success: boolean }>(`/api/permissions/users/${userId}/${permissionId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Assign a role to a user (admin only).
   */
  async assignUserRole(userId: string, roleId: string, expiresAt?: number) {
    return this.request<{ success: boolean }>(`/api/permissions/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId, expires_at: expiresAt }),
    })
  }

  /**
   * Remove a role from a user (admin only).
   */
  async removeUserRole(userId: string, roleId: string) {
    return this.request<{ success: boolean }>(`/api/permissions/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Seed default permissions and roles (admin only).
   */
  async seedPermissions() {
    return this.request<{
      success: boolean
      message: string
      permissions_created: number
      roles_created: number
    }>('/api/permissions/seed', {
      method: 'POST',
    })
  }

  // ============================================================================
  // DATA EXPORT ENDPOINTS (Issue #91)
  // ============================================================================

  /** Get a summary of available data for export. */
  async getExportSummary() {
    return this.request<{
      success: boolean
      summary: {
        controls: number
        risks: number
        evidence: number
        documents: number
        vendors: number
        tasks: number
      }
      available_formats: string[]
      available_exports: Array<{ key: string; name: string; description: string }>
    }>('/api/export/summary')
  }

  /** Export controls to CSV or JSON format. */
  async exportControls(options: { format?: 'csv' | 'json'; include_related?: boolean; framework_id?: string } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    if (options.include_related) params.append('include_related', 'true')
    if (options.framework_id) params.append('framework_id', options.framework_id)
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/controls${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/controls${queryString ? `?${queryString}` : ''}`)
  }

  /** Export risks to CSV or JSON format. */
  async exportRisks(options: { format?: 'csv' | 'json'; include_related?: boolean } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    if (options.include_related) params.append('include_related', 'true')
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/risks${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/risks${queryString ? `?${queryString}` : ''}`)
  }

  /** Export evidence metadata to CSV or JSON format. */
  async exportEvidence(options: { format?: 'csv' | 'json'; control_id?: string; date_from?: number; date_to?: number } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    if (options.control_id) params.append('control_id', options.control_id)
    if (options.date_from) params.append('date_from', String(options.date_from))
    if (options.date_to) params.append('date_to', String(options.date_to))
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/evidence${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/evidence${queryString ? `?${queryString}` : ''}`)
  }

  /** Export documents (policies) metadata to CSV or JSON format. */
  async exportDocuments(options: { format?: 'csv' | 'json' } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/documents${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/documents${queryString ? `?${queryString}` : ''}`)
  }

  /** Export vendors to CSV or JSON format. */
  async exportVendors(options: { format?: 'csv' | 'json'; include_related?: boolean } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    if (options.include_related) params.append('include_related', 'true')
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/vendors${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/vendors${queryString ? `?${queryString}` : ''}`)
  }

  /** Export tasks to CSV or JSON format. */
  async exportTasks(options: { format?: 'csv' | 'json'; status?: string } = {}) {
    const params = new URLSearchParams()
    if (options.format) params.append('format', options.format)
    if (options.status) params.append('status', options.status)
    const queryString = params.toString()
    if (options.format === 'csv') {
      const apiBase = this.getApiBaseForDownload()
      const response = await fetch(`${apiBase}/api/export/tasks${queryString ? `?${queryString}` : ''}`, { headers: this.getAuthHeaders() })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Export failed') }
      return response.blob()
    }
    return this.request<{ success: boolean; export_date: string; count: number; data: any[] }>(`/api/export/tasks${queryString ? `?${queryString}` : ''}`)
  }

  /** Export all data as a full backup (JSON only, admin required). */
  async exportAll() {
    const apiBase = this.getApiBaseForDownload()
    const response = await fetch(`${apiBase}/api/export/all`, { headers: this.getAuthHeaders() })
    if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Full export failed') }
    return response.blob()
  }

  /** Helper to get API base URL for download requests. */
  private getApiBaseForDownload(): string {
    if (!import.meta.env.PROD) return ''
    const selectedTenant = getSelectedTenant()
    if (selectedTenant?.slug) return `https://${selectedTenant.slug}.app.clarantis.com`
    const hostname = window.location.hostname
    const clarantisMatch = hostname.match(/^([^.]+)\.app\.clarantis\.com$/)
    if (clarantisMatch) return `https://${clarantisMatch[1]}.app.clarantis.com`
    if (hostname.endsWith('.pages.dev')) return import.meta.env.VITE_API_BASE_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'
    return import.meta.env.VITE_API_BASE_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'
  }

  /** Helper to get authorization headers. */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}
    const token = this.token || localStorage.getItem('grc_token')
    if (token) headers['Authorization'] = `Bearer ${token}`
    return headers
  }

  // ============================================================================
  // JIRA INTEGRATION ENDPOINTS (Issue #125)
  // ============================================================================

  /** Get Jira integration configuration (admin only). */
  async getJiraConfig() {
    return this.request<{
      success: boolean
      configured: boolean
      config: JiraConfig | null
    }>('/api/jira/config')
  }

  /** Create or update Jira integration configuration (admin only). */
  async saveJiraConfig(config: JiraConfigInput) {
    return this.request<{
      success: boolean
      message: string
      config_id?: string
    }>('/api/jira/config', {
      method: 'POST',
      body: JSON.stringify(config),
    })
  }

  /** Delete Jira integration configuration (admin only). */
  async deleteJiraConfig() {
    return this.request<{ success: boolean; message: string }>('/api/jira/config', {
      method: 'DELETE',
    })
  }

  /** Test Jira API connection (admin only). */
  async testJiraConnection() {
    return this.request<{
      success: boolean
      message: string
      jira_user?: string
      project?: string
      error?: string
    }>('/api/jira/test-connection', {
      method: 'POST',
    })
  }

  /** Get available Jira projects (admin only). */
  async getJiraProjects() {
    return this.request<{
      success: boolean
      projects: Array<{ key: string; name: string; id: string }>
    }>('/api/jira/projects')
  }

  /** Get available issue types for a Jira project (admin only). */
  async getJiraIssueTypes(projectKey: string) {
    return this.request<{
      success: boolean
      issue_types: Array<{
        id: string
        name: string
        description?: string
        subtask: boolean
      }>
    }>(`/api/jira/issue-types/${projectKey}`)
  }

  /** Create a Jira ticket for a vulnerability. */
  async createJiraTicket(
    vulnerabilityId: string,
    options?: { project_key?: string; issue_type?: string; priority?: string }
  ) {
    return this.request<{
      success: boolean
      message: string
      ticket?: {
        key: string
        url: string
        self: string
      }
      existing_ticket?: {
        key: string
        url: string
      }
      error?: string
    }>(`/api/jira/vulnerabilities/${vulnerabilityId}/create-ticket`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    })
  }

  /** Sync Jira ticket status for a vulnerability. */
  async syncJiraTicket(vulnerabilityId: string) {
    return this.request<{
      success: boolean
      message: string
      ticket?: {
        key: string
        url: string
        status: string
        resolution: string | null
        statusCategory?: string
        synced_at: number
      }
      error?: string
    }>(`/api/jira/vulnerabilities/${vulnerabilityId}/sync-ticket`, {
      method: 'POST',
    })
  }

  /** Unlink Jira ticket from a vulnerability (doesn't delete the ticket). */
  async unlinkJiraTicket(vulnerabilityId: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/jira/vulnerabilities/${vulnerabilityId}/unlink-ticket`, {
      method: 'POST',
    })
  }

  /** Get Jira ticket history for a vulnerability. */
  async getJiraTicketHistory(vulnerabilityId: string) {
    return this.request<{
      success: boolean
      history: JiraTicketHistoryEntry[]
    }>(`/api/jira/vulnerabilities/${vulnerabilityId}/ticket-history`)
  }

  /** Sync all vulnerabilities with Jira tickets (admin only). */
  async syncAllJiraTickets() {
    return this.request<{
      success: boolean
      message: string
      synced: number
      failed: number
      errors?: Array<{ vuln_id: string; ticket: string; error: string }>
    }>('/api/jira/sync-all', {
      method: 'POST',
    })
  }

  // ============================================================================
  // NOTIFICATION CHANNELS (Issue #157)
  // Slack, Email, and Custom Webhook notification integrations
  // ============================================================================

  /** Get available notification event types. */
  async getNotificationEventTypes() {
    return this.request<{
      success: boolean
      events: Array<{
        type: string
        label: string
        description: string
        category: string
        default_severity: 'info' | 'warning' | 'critical'
      }>
      count: number
    }>('/api/notification-channels/events')
  }

  /** List all notification channels (Slack, Email, Webhook, Custom). */
  async getNotificationChannels() {
    return this.request<{
      success: boolean
      channels: NotificationChannel[]
      count: number
    }>('/api/notification-channels')
  }

  /** Get a single notification channel by ID. */
  async getNotificationChannel(id: string) {
    return this.request<{
      success: boolean
      channel: NotificationChannel
    }>(`/api/notification-channels/${id}`)
  }

  /** Create a new notification channel. */
  async createNotificationChannel(data: {
    channel_type: 'slack' | 'email' | 'webhook' | 'custom'
    name: string
    config: Record<string, any>
    is_enabled?: boolean
    event_types?: string[]
  }) {
    return this.request<{
      success: boolean
      channel: NotificationChannel
      message: string
    }>('/api/notification-channels', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Update a notification channel. */
  async updateNotificationChannel(id: string, data: {
    name?: string
    config?: Record<string, any>
    is_enabled?: boolean
    event_types?: string[]
  }) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/notification-channels/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /** Delete a notification channel. */
  async deleteNotificationChannel(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/notification-channels/${id}`, {
      method: 'DELETE',
    })
  }

  /** Test a notification channel by sending a test message. */
  async testNotificationChannel(id: string) {
    return this.request<{
      success: boolean
      status_code?: number
      duration_ms: number
      message: string
      error?: string
    }>(`/api/notification-channels/${id}/test`, {
      method: 'POST',
    })
  }

  /** Test a notification channel configuration without saving. */
  async testNotificationChannelConfig(data: { type: string; config: Record<string, any> }) {
    return this.request<{
      success: boolean
      status_code?: number
      duration_ms: number
      message: string
      error?: string
    }>('/api/notification-channels/test', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Send a notification to all enabled channels (admin only). */
  async sendNotification(data: {
    event: string
    title: string
    message: string
    severity?: 'info' | 'warning' | 'critical'
    data?: Record<string, any>
    link?: string
  }) {
    return this.request<{
      success: boolean
      channels_notified: number
      channels_failed: number
      results: Array<{
        channel_id: string
        channel_name: string
        channel_type: string
        success: boolean
        status_code?: number
        error?: string
        duration_ms: number
      }>
      message: string
    }>('/api/notification-channels/send', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ============================================================================
  // ATLASSIAN INTEGRATION (Jira/Confluence)
  // ============================================================================

  /** Generic GET request */
  async get<T = any>(path: string): Promise<T> {
    return this.request<T>(path)
  }

  /** Generic POST request */
  async post<T = any>(path: string, data?: any): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /** Generic PATCH request */
  async patch<T = any>(path: string, data?: any): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /** Generic DELETE request */
  async delete<T = any>(path: string): Promise<T> {
    return this.request<T>(path, {
      method: 'DELETE',
    })
  }

  // ============================================================================
  // VIRTUAL CISO CHAT (Issue #299)
  // ============================================================================

  /**
   * Send a message to the Virtual CISO assistant
   */
  async sendChatMessage(params: {
    message: string
    conversationId?: string
    currentPage?: string
  }): Promise<{
    success: boolean
    data?: {
      message: string
      conversationId: string
      source: 'ai' | 'fallback'
      fallbackReason?: string
    }
    error?: string
  }> {
    return this.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * List user's chat conversations
   */
  async getChatConversations(limit = 10): Promise<{
    success: boolean
    data?: Array<{
      id: string
      preview: string
      updatedAt: number
    }>
    error?: string
  }> {
    return this.request(`/api/chat/conversations?limit=${limit}`)
  }

  /**
   * Get a specific chat conversation
   */
  async getChatConversation(conversationId: string): Promise<{
    success: boolean
    data?: {
      id: string
      tenantId: string
      userId: string
      messages: Array<{
        role: 'user' | 'assistant' | 'system'
        content: string
        timestamp: number
      }>
      currentPage?: string
      createdAt: number
      updatedAt: number
    }
    error?: string
  }> {
    return this.request(`/api/chat/conversations/${conversationId}`)
  }

  /**
   * Delete a chat conversation
   */
  async deleteChatConversation(conversationId: string): Promise<{
    success: boolean
    message?: string
    error?: string
  }> {
    return this.request(`/api/chat/conversations/${conversationId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Clear all chat conversations
   */
  async clearAllChatConversations(): Promise<{
    success: boolean
    message?: string
    error?: string
  }> {
    return this.request('/api/chat/conversations', {
      method: 'DELETE',
    })
  }

  /**
   * Get AI-generated insight for a page context
   * Cross-references data for actionable insights
   */
  async getPageInsight(page: string): Promise<{
    success: boolean
    insight: string | null
    context?: {
      summary: string
      criticalCount: number
    }
    generatedAt?: number
    message?: string
    error?: string
  }> {
    return this.request(`/api/chat/insight?page=${encodeURIComponent(page)}`)
  }

  // ============================================================================
  // INTERNAL CONTROLS (Issue #311: Common Control Framework)
  // ============================================================================

  /**
   * Get list of internal controls with filtering and pagination.
   * Internal controls are the master catalog where evidence attaches.
   */
  async getInternalControls(params: {
    category?: string
    subcategory?: string
    status?: string
    owner_id?: string
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: InternalControl[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/internal-controls?${searchParams}`)
  }

  /**
   * Get internal control statistics.
   */
  async getInternalControlStats(): Promise<{
    success: boolean
    data: {
      by_status: Array<{ status: string; count: number }>
      by_category: Array<{ category: string; count: number }>
      total_evidence_links: number
      coverage: { total_controls: number; with_evidence: number; without_evidence: number }
    }
  }> {
    return this.request('/api/internal-controls/stats')
  }

  /**
   * Get a single internal control with evidence and framework mapping details.
   */
  async getInternalControl(id: string): Promise<{
    success: boolean
    data: InternalControl & {
      evidence: Array<{
        id: string
        file_name: string
        file_type: string
        file_size: number
        uploaded_at: number
        uploaded_by: string
        source: string
        uploaded_by_username?: string
      }>
      evidence_count: number
      framework_controls: Array<{
        id: string
        control_number: string
        name: string
        framework_name: string
        framework_code: string
      }>
      framework_control_count: number
      policies: Array<{
        id: string
        name: string
        version: string
        status: string
        approved_at: number
        approved_by_username?: string
      }>
      policy_count: number
    }
  }> {
    return this.request(`/api/internal-controls/${id}`)
  }

  /**
   * Update an internal control.
   */
  async updateInternalControl(id: string, updates: {
    owner_id?: string | null
    frequency?: string
    implementation_guidance?: string
    evidence_guidance?: string
    testing_procedures?: string
  }): Promise<{
    success: boolean
    data: InternalControl
  }> {
    return this.request(`/api/internal-controls/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Get framework controls satisfied by an internal control.
   */
  async getInternalControlFrameworkControls(id: string, params: {
    framework_id?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: Array<{
      id: string
      control_number: string
      name: string
      description: string
      frequency: string
      status: string
      is_not_applicable: boolean
      framework_id: string
      framework_name: string
      framework_code: string
    }>
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/internal-controls/${id}/framework-controls?${searchParams}`)
  }

  // ==========================================================================
  // COMPLIANCE TRAINING API METHODS (Issue #285)
  // ==========================================================================

  /**
   * Get all training categories.
   */
  async getTrainingCategories(params: {
    include_inactive?: boolean
  } = {}): Promise<{
    success: boolean
    data: TrainingCategory[]
  }> {
    const searchParams = new URLSearchParams()
    if (params.include_inactive) searchParams.set('include_inactive', 'true')
    return this.request(`/api/training/categories?${searchParams}`)
  }

  /**
   * Create a new training category.
   */
  async createTrainingCategory(data: {
    name: string
    description?: string
    icon?: string
    color?: string
  }): Promise<{
    success: boolean
    data: TrainingCategory
  }> {
    return this.request('/api/training/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a training category.
   */
  async updateTrainingCategory(id: string, data: {
    name?: string
    description?: string
    icon?: string
    color?: string
    is_active?: boolean
    display_order?: number
  }): Promise<{
    success: boolean
    data: TrainingCategory
  }> {
    return this.request(`/api/training/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a training category.
   */
  async deleteTrainingCategory(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/training/categories/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get training modules with optional filters.
   */
  async getTrainingModules(params: {
    category_id?: string
    status?: TrainingModuleStatus
    content_type?: TrainingContentType
    is_mandatory?: boolean
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: TrainingModule[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/training/modules?${searchParams}`)
  }

  /**
   * Get a single training module by ID.
   */
  async getTrainingModule(id: string): Promise<{
    success: boolean
    data: TrainingModule
  }> {
    return this.request(`/api/training/modules/${id}`)
  }

  /**
   * Create a new training module.
   */
  async createTrainingModule(data: {
    title: string
    description?: string
    category_id?: string
    content_type: TrainingContentType
    video_url?: string
    video_duration_seconds?: number
    slides_content?: any
    quiz_questions?: TrainingQuizQuestion[]
    quiz_passing_score?: number
    quiz_time_limit_minutes?: number
    document_content?: string
    estimated_minutes?: number
    is_mandatory?: boolean
    tags?: string[]
    prerequisites?: string[]
  }): Promise<{
    success: boolean
    data: TrainingModule
  }> {
    return this.request('/api/training/modules', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a training module.
   */
  async updateTrainingModule(id: string, data: {
    title?: string
    description?: string
    category_id?: string | null
    content_type?: TrainingContentType
    video_url?: string
    video_duration_seconds?: number
    slides_content?: any
    quiz_questions?: TrainingQuizQuestion[]
    quiz_passing_score?: number
    quiz_time_limit_minutes?: number
    document_content?: string
    estimated_minutes?: number
    is_mandatory?: boolean
    tags?: string[]
    prerequisites?: string[]
  }): Promise<{
    success: boolean
    data: TrainingModule
  }> {
    return this.request(`/api/training/modules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Publish a training module.
   */
  async publishTrainingModule(id: string): Promise<{
    success: boolean
    data: TrainingModule
  }> {
    return this.request(`/api/training/modules/${id}/publish`, {
      method: 'POST',
    })
  }

  /**
   * Archive a training module.
   */
  async archiveTrainingModule(id: string): Promise<{
    success: boolean
    data: TrainingModule
  }> {
    return this.request(`/api/training/modules/${id}/archive`, {
      method: 'POST',
    })
  }

  /**
   * Delete a training module.
   */
  async deleteTrainingModule(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/training/modules/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get training campaigns with optional filters.
   */
  async getTrainingCampaigns(params: {
    status?: TrainingCampaignStatus
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: TrainingCampaign[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/training/campaigns?${searchParams}`)
  }

  /**
   * Get a single training campaign by ID with full details.
   */
  async getTrainingCampaign(id: string): Promise<{
    success: boolean
    data: TrainingCampaign
  }> {
    return this.request(`/api/training/campaigns/${id}`)
  }

  /**
   * Create a new training campaign.
   */
  async createTrainingCampaign(data: {
    name: string
    description?: string
    module_ids: string[]
    assignment_type: TrainingAssignmentType
    assignment_criteria?: any
    schedule_type: TrainingScheduleType
    scheduled_start?: number
    recurrence_pattern?: string
    due_days_from_assignment?: number
    allow_late_completion?: boolean
    send_reminders?: boolean
    reminder_days_before?: number[]
  }): Promise<{
    success: boolean
    data: TrainingCampaign
  }> {
    return this.request('/api/training/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a training campaign.
   */
  async updateTrainingCampaign(id: string, data: {
    name?: string
    description?: string
    module_ids?: string[]
    assignment_type?: TrainingAssignmentType
    assignment_criteria?: any
    schedule_type?: TrainingScheduleType
    scheduled_start?: number
    recurrence_pattern?: string
    due_days_from_assignment?: number
    allow_late_completion?: boolean
    send_reminders?: boolean
    reminder_days_before?: number[]
  }): Promise<{
    success: boolean
    data: TrainingCampaign
  }> {
    return this.request(`/api/training/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Launch a training campaign.
   */
  async launchTrainingCampaign(id: string): Promise<{
    success: boolean
    data: TrainingCampaign
    assignments_created: number
  }> {
    return this.request(`/api/training/campaigns/${id}/launch`, {
      method: 'POST',
    })
  }

  /**
   * Cancel a training campaign.
   */
  async cancelTrainingCampaign(id: string): Promise<{
    success: boolean
    data: TrainingCampaign
  }> {
    return this.request(`/api/training/campaigns/${id}/cancel`, {
      method: 'POST',
    })
  }

  /**
   * Delete a training campaign.
   */
  async deleteTrainingCampaign(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/training/campaigns/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get campaign assignments.
   */
  async getTrainingCampaignAssignments(campaignId: string, params: {
    status?: TrainingAssignmentStatus
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: TrainingAssignment[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/training/campaigns/${campaignId}/assignments?${searchParams}`)
  }

  /**
   * Get a single assignment with progress details.
   */
  async getTrainingAssignment(id: string): Promise<{
    success: boolean
    data: TrainingAssignment & {
      progress: TrainingProgress[]
    }
  }> {
    return this.request(`/api/training/assignments/${id}`)
  }

  /**
   * Update module progress for an assignment.
   */
  async updateTrainingProgress(assignmentId: string, moduleId: string, data: {
    progress_percent?: number
    last_position?: any
    time_spent_minutes?: number
  }): Promise<{
    success: boolean
    data: TrainingProgress
  }> {
    return this.request(`/api/training/assignments/${assignmentId}/modules/${moduleId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Start a module in an assignment.
   */
  async startTrainingModule(assignmentId: string, moduleId: string): Promise<{
    success: boolean
    data: TrainingProgress
  }> {
    return this.request(`/api/training/assignments/${assignmentId}/modules/${moduleId}/start`, {
      method: 'POST',
    })
  }

  /**
   * Complete a module (non-quiz).
   */
  async completeTrainingModule(assignmentId: string, moduleId: string): Promise<{
    success: boolean
    data: TrainingProgress
  }> {
    return this.request(`/api/training/assignments/${assignmentId}/modules/${moduleId}/complete`, {
      method: 'POST',
    })
  }

  /**
   * Submit quiz answers for a module.
   */
  async submitTrainingQuiz(assignmentId: string, moduleId: string, answers: TrainingQuizSubmission[]): Promise<{
    success: boolean
    data: TrainingProgress
    results: {
      score: number
      passed: boolean
      passing_score: number
      correct_answers: number
      total_questions: number
      feedback: Array<{
        question_id: string
        correct: boolean
        correct_answer: string | string[]
        explanation: string | null
      }>
    }
  }> {
    return this.request(`/api/training/assignments/${assignmentId}/modules/${moduleId}/quiz`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    })
  }

  /**
   * Excuse a user from an assignment.
   */
  async excuseTrainingAssignment(id: string, reason: string): Promise<{
    success: boolean
    data: TrainingAssignment
  }> {
    return this.request(`/api/training/assignments/${id}/excuse`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  /**
   * Get training dashboard statistics.
   */
  async getTrainingDashboard(): Promise<{
    success: boolean
    data: TrainingDashboardStats
  }> {
    return this.request('/api/training/dashboard')
  }

  // ==========================================================================
  // PHISHING SIMULATION API METHODS (Issue #285)
  // ==========================================================================

  /**
   * Get phishing templates with optional filters.
   */
  async getPhishingTemplates(params: {
    category?: string
    difficulty?: PhishingDifficulty
    is_system?: boolean
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: PhishingTemplate[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/phishing/templates?${searchParams}`)
  }

  /**
   * Get a single phishing template by ID.
   */
  async getPhishingTemplate(id: string): Promise<{
    success: boolean
    data: PhishingTemplate
  }> {
    return this.request(`/api/phishing/templates/${id}`)
  }

  /**
   * Create a new phishing template.
   */
  async createPhishingTemplate(data: {
    name: string
    subject: string
    body_html: string
    body_text?: string
    sender_name: string
    sender_email: string
    category: string
    difficulty: PhishingDifficulty
    indicators?: string[]
    landing_page_html?: string
    capture_credentials?: boolean
  }): Promise<{
    success: boolean
    data: PhishingTemplate
  }> {
    return this.request('/api/phishing/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a phishing template.
   */
  async updatePhishingTemplate(id: string, data: {
    name?: string
    subject?: string
    body_html?: string
    body_text?: string
    sender_name?: string
    sender_email?: string
    category?: string
    difficulty?: PhishingDifficulty
    indicators?: string[]
    landing_page_html?: string
    capture_credentials?: boolean
    is_active?: boolean
  }): Promise<{
    success: boolean
    data: PhishingTemplate
  }> {
    return this.request(`/api/phishing/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a phishing template.
   */
  async deletePhishingTemplate(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/phishing/templates/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Clone a phishing template.
   */
  async clonePhishingTemplate(id: string, name?: string): Promise<{
    success: boolean
    data: PhishingTemplate
  }> {
    return this.request(`/api/phishing/templates/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  /**
   * Get sender profiles.
   */
  async getPhishingSenderProfiles(): Promise<{
    success: boolean
    data: PhishingSenderProfile[]
  }> {
    return this.request('/api/phishing/senders')
  }

  /**
   * Create a sender profile.
   */
  async createPhishingSenderProfile(data: {
    name: string
    from_name: string
    from_email: string
    reply_to_email?: string
  }): Promise<{
    success: boolean
    data: PhishingSenderProfile
  }> {
    return this.request('/api/phishing/senders', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a sender profile.
   */
  async deletePhishingSenderProfile(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/phishing/senders/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get phishing campaigns with optional filters.
   */
  async getPhishingCampaigns(params: {
    status?: PhishingCampaignStatus
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: PhishingCampaign[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/phishing/campaigns?${searchParams}`)
  }

  /**
   * Get a single phishing campaign by ID.
   */
  async getPhishingCampaign(id: string): Promise<{
    success: boolean
    data: PhishingCampaign
  }> {
    return this.request(`/api/phishing/campaigns/${id}`)
  }

  /**
   * Create a new phishing campaign.
   */
  async createPhishingCampaign(data: {
    name: string
    description?: string
    template_id: string
    sender_profile_id?: string
    target_type: 'all_users' | 'specific_users' | 'by_role' | 'by_department'
    target_criteria?: any
    scheduled_at?: number
    send_window_start?: string
    send_window_end?: string
    randomize_send_time?: boolean
    track_opens?: boolean
    track_clicks?: boolean
    capture_credentials?: boolean
    training_module_id?: string
    auto_enroll_on_fail?: boolean
    requires_approval?: boolean
  }): Promise<{
    success: boolean
    data: PhishingCampaign
  }> {
    return this.request('/api/phishing/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a phishing campaign.
   */
  async updatePhishingCampaign(id: string, data: {
    name?: string
    description?: string
    template_id?: string
    sender_profile_id?: string
    target_type?: 'all_users' | 'specific_users' | 'by_role' | 'by_department'
    target_criteria?: any
    scheduled_at?: number
    send_window_start?: string
    send_window_end?: string
    randomize_send_time?: boolean
    track_opens?: boolean
    track_clicks?: boolean
    capture_credentials?: boolean
    training_module_id?: string
    auto_enroll_on_fail?: boolean
  }): Promise<{
    success: boolean
    data: PhishingCampaign
  }> {
    return this.request(`/api/phishing/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Approve a phishing campaign (admin only).
   */
  async approvePhishingCampaign(id: string): Promise<{
    success: boolean
    data: PhishingCampaign
  }> {
    return this.request(`/api/phishing/campaigns/${id}/approve`, {
      method: 'POST',
    })
  }

  /**
   * Start/launch a phishing campaign.
   */
  async startPhishingCampaign(id: string): Promise<{
    success: boolean
    data: PhishingCampaign
    emails_queued: number
  }> {
    return this.request(`/api/phishing/campaigns/${id}/start`, {
      method: 'POST',
    })
  }

  /**
   * Cancel a phishing campaign.
   */
  async cancelPhishingCampaign(id: string): Promise<{
    success: boolean
    data: PhishingCampaign
  }> {
    return this.request(`/api/phishing/campaigns/${id}/cancel`, {
      method: 'POST',
    })
  }

  /**
   * Delete a phishing campaign.
   */
  async deletePhishingCampaign(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/phishing/campaigns/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get phishing campaign results.
   */
  async getPhishingCampaignResults(campaignId: string, params: {
    outcome?: PhishingResultOutcome
    search?: string
    limit?: number
    offset?: number
  } = {}): Promise<{
    success: boolean
    data: PhishingResult[]
    pagination: { total: number; limit: number; offset: number }
    stats: {
      total: number
      sent: number
      opened: number
      clicked: number
      compromised: number
      reported: number
    }
  }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })
    return this.request(`/api/phishing/campaigns/${campaignId}/results?${searchParams}`)
  }

  /**
   * Export phishing campaign results as CSV.
   */
  async exportPhishingCampaignResults(campaignId: string): Promise<Blob> {
    const response = await fetch(`${getApiBase()}/api/phishing/campaigns/${campaignId}/results/export`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to export results')
    }
    return response.blob()
  }

  /**
   * Get phishing dashboard statistics.
   */
  async getPhishingDashboard(): Promise<{
    success: boolean
    data: PhishingDashboardStats
  }> {
    return this.request('/api/phishing/dashboard')
  }

  /**
   * Preview a phishing template with sample data.
   */
  async previewPhishingTemplate(id: string, sampleData?: {
    first_name?: string
    last_name?: string
    email?: string
    department?: string
    company?: string
  }): Promise<{
    success: boolean
    html: string
    text: string
    subject: string
  }> {
    return this.request(`/api/phishing/templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ sample_data: sampleData }),
    })
  }

  // ==========================================================================
  // ADVANCED TRAINING ANALYTICS (Phase 4)
  // ==========================================================================

  /**
   * Get training completion trends over time.
   */
  async getTrainingTrends(months: number = 6): Promise<{
    success: boolean
    data: Array<{
      month: string
      completions: number
      avg_score: number
    }>
  }> {
    return this.request(`/api/training/analytics/trends?months=${months}`)
  }

  /**
   * Get top performers by training completions.
   */
  async getTrainingTopPerformers(limit: number = 10): Promise<{
    success: boolean
    data: Array<{
      user_id: string
      username: string
      email: string
      first_name: string | null
      last_name: string | null
      modules_completed: number
      avg_score: number | null
      quizzes_passed: number
      first_completion: number
      last_completion: number
    }>
  }> {
    return this.request(`/api/training/analytics/top-performers?limit=${limit}`)
  }

  /**
   * Get module effectiveness metrics.
   */
  async getTrainingModuleEffectiveness(): Promise<{
    success: boolean
    data: Array<{
      module_id: string
      title: string
      content_type: TrainingContentType
      estimated_minutes: number
      total_attempts: number
      completions: number
      completion_rate: number
      avg_score: number | null
      pass_rate: number | null
      avg_completion_minutes: number | null
    }>
  }> {
    return this.request('/api/training/analytics/module-effectiveness')
  }

  /**
   * Get training completion breakdown by department/role.
   */
  async getTrainingByDepartment(): Promise<{
    success: boolean
    data: Array<{
      role: string
      total_assignments: number
      completed: number
      overdue: number
      completion_rate: number
      avg_days_to_complete: number | null
    }>
  }> {
    return this.request('/api/training/analytics/by-department')
  }

  /**
   * Generate quiz questions using AI.
   */
  async generateTrainingQuiz(params: {
    topic: string
    num_questions?: number
    difficulty?: 'easy' | 'medium' | 'hard'
  }): Promise<{
    success: boolean
    data: {
      topic: string
      difficulty: string
      questions: Array<{
        id: string
        question_text: string
        question_type: string
        options: Array<{
          id: string
          text: string
          is_correct: boolean
        }>
        explanation: string | null
        points: number
        sort_order: number
      }>
    }
  }> {
    return this.request('/api/training/ai/generate-quiz', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Generate module description using AI.
   */
  async generateTrainingDescription(params: {
    title: string
    topic?: string
    content_type?: TrainingContentType
  }): Promise<{
    success: boolean
    data: {
      description: string
    }
  }> {
    return this.request('/api/training/ai/generate-description', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Export training report.
   */
  async exportTrainingReport(params: {
    format?: 'csv' | 'json'
    type?: 'assignments' | 'progress'
    start_date?: number
    end_date?: number
    campaign_id?: string
  }): Promise<Blob | { success: boolean; data: any }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })

    if (params.format === 'csv') {
      const response = await fetch(`${getApiBase()}/api/training/reports/export?${searchParams}`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      })
      if (!response.ok) {
        throw new Error('Failed to export report')
      }
      return response.blob()
    }

    return this.request(`/api/training/reports/export?${searchParams}`)
  }

  /**
   * Get training summary report.
   */
  async getTrainingSummaryReport(params?: {
    start_date?: number
    end_date?: number
  }): Promise<{
    success: boolean
    data: {
      total_assignments: number
      completed_assignments: number
      overdue_assignments: number
      completion_rate: number
      unique_users: number
      avg_quiz_score: number | null
      certificates_issued: number
      period: { start: number | null; end: number | null }
    }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', String(params.start_date))
    if (params?.end_date) searchParams.set('end_date', String(params.end_date))
    return this.request(`/api/training/reports/summary?${searchParams}`)
  }

  // ==========================================================================
  // ADVANCED PHISHING ANALYTICS (Phase 4)
  // ==========================================================================

  /**
   * Get phishing click rate trends over time.
   */
  async getPhishingTrends(months: number = 6): Promise<{
    success: boolean
    data: Array<{
      month: string
      emails_sent: number
      clicks: number
      submissions: number
      reports: number
      click_rate: number
      compromise_rate: number
      report_rate: number
    }>
  }> {
    return this.request(`/api/phishing/analytics/trends?months=${months}`)
  }

  /**
   * Get most effective phishing templates.
   */
  async getPhishingEffectiveTemplates(limit: number = 10): Promise<{
    success: boolean
    data: Array<{
      template_id: string
      name: string
      category: string
      difficulty: PhishingDifficulty
      campaigns_used: number
      total_sent: number
      total_clicks: number
      click_rate: number
      compromise_rate: number
    }>
  }> {
    return this.request(`/api/phishing/analytics/effective-templates?limit=${limit}`)
  }

  /**
   * Get phishing vulnerability breakdown by department/role.
   */
  async getPhishingByDepartment(): Promise<{
    success: boolean
    data: Array<{
      role: string
      total_targets: number
      clicked: number
      compromised: number
      reported: number
      click_rate: number
      compromise_rate: number
      report_rate: number
    }>
  }> {
    return this.request('/api/phishing/analytics/by-department')
  }

  /**
   * Get repeat offenders (users who repeatedly click).
   */
  async getPhishingRepeatOffenders(params?: {
    limit?: number
    min_clicks?: number
  }): Promise<{
    success: boolean
    data: Array<{
      user_id: string
      email: string
      first_name: string | null
      last_name: string | null
      role: string | null
      total_campaigns: number
      times_clicked: number
      times_compromised: number
      times_reported: number
      click_rate: number
      first_campaign: number
      last_campaign: number
    }>
  }> {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.min_clicks) searchParams.set('min_clicks', String(params.min_clicks))
    return this.request(`/api/phishing/analytics/repeat-offenders?${searchParams}`)
  }

  /**
   * Get user improvement tracking over campaigns.
   */
  async getPhishingUserImprovement(userId: string): Promise<{
    success: boolean
    data: {
      user_id: string
      total_campaigns: number
      history: Array<{
        campaign_id: string
        campaign_name: string
        difficulty: PhishingDifficulty
        outcome: PhishingResultOutcome
        date: number
      }>
      improvement: {
        first_half_click_rate: number
        second_half_click_rate: number
        improvement_percent: number
        is_improving: boolean
      } | null
    }
  }> {
    return this.request(`/api/phishing/analytics/user-improvement?user_id=${userId}`)
  }

  /**
   * Suggest phishing indicators using AI.
   */
  async suggestPhishingIndicators(params: {
    subject: string
    sender_name?: string
    sender_email?: string
    body_snippet?: string
  }): Promise<{
    success: boolean
    data: {
      indicators: string[]
    }
  }> {
    return this.request('/api/phishing/ai/suggest-indicators', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Export phishing report.
   */
  async exportPhishingReport(params: {
    format?: 'csv' | 'json'
    type?: 'results' | 'campaigns'
    campaign_id?: string
    start_date?: number
    end_date?: number
  }): Promise<Blob | { success: boolean; data: any }> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.set(key, String(value))
    })

    if (params.format === 'csv') {
      const response = await fetch(`${getApiBase()}/api/phishing/reports/export?${searchParams}`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      })
      if (!response.ok) {
        throw new Error('Failed to export report')
      }
      return response.blob()
    }

    return this.request(`/api/phishing/reports/export?${searchParams}`)
  }

  /**
   * Get phishing summary report.
   */
  async getPhishingSummaryReport(params?: {
    start_date?: number
    end_date?: number
  }): Promise<{
    success: boolean
    data: {
      total_campaigns: number
      total_targets: number
      total_clicks: number
      total_submissions: number
      total_reports: number
      unique_users_tested: number
      click_rate: number
      compromise_rate: number
      report_rate: number
      period: { start: number | null; end: number | null }
    }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', String(params.start_date))
    if (params?.end_date) searchParams.set('end_date', String(params.end_date))
    return this.request(`/api/phishing/reports/summary?${searchParams}`)
  }

  // ==========================================================================
  // USER DIRECTORY INTEGRATION (Issue #336)
  // ==========================================================================

  /**
   * Get all directory connections for the tenant.
   */
  async getDirectoryConnections(): Promise<{
    success: boolean
    data: DirectoryConnection[]
  }> {
    return this.request('/api/directory/connections')
  }

  /**
   * Create a new directory connection.
   * For OAuth providers (google, entra), include client_id and client_secret.
   * Returns an oauth_url for authorization.
   */
  async createDirectoryConnection(data: {
    name: string
    provider: DirectoryProvider
    sync_interval_hours?: number
    client_id?: string
    client_secret?: string
    config?: Record<string, any>
  }): Promise<{
    success: boolean
    data: DirectoryConnection
    oauth_url?: string
  }> {
    return this.request('/api/directory/connections', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a directory connection.
   */
  async updateDirectoryConnection(id: string, data: {
    name?: string
    sync_enabled?: boolean
    sync_interval_hours?: number
    status?: DirectoryConnectionStatus
  }): Promise<{
    success: boolean
    data: DirectoryConnection
  }> {
    return this.request(`/api/directory/connections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a directory connection.
   */
  async deleteDirectoryConnection(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/directory/connections/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Trigger a manual sync for a directory connection.
   */
  async triggerDirectorySync(id: string): Promise<{
    success: boolean
    data?: DirectorySyncLog
    message?: string
  }> {
    return this.request(`/api/directory/connections/${id}/sync`, {
      method: 'POST',
    })
  }

  /**
   * Get sync logs for directory connections.
   */
  async getDirectorySyncLogs(params?: {
    connection_id?: string
    limit?: number
    offset?: number
  }): Promise<{
    success: boolean
    data: DirectorySyncLog[]
    total?: number
  }> {
    const searchParams = new URLSearchParams()
    if (params?.connection_id) searchParams.set('connection_id', params.connection_id)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    return this.request(`/api/directory/sync-logs?${searchParams}`)
  }

  /**
   * Preview a CSV file for user import.
   * Returns validation results and a preview of the import.
   */
  async previewCsvImport(file: File): Promise<{
    success: boolean
    data: CsvPreviewResult
  }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${getApiBase()}/api/directory/csv/preview`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to preview CSV')
    }
    return data
  }

  /**
   * Confirm and execute a CSV user import.
   */
  async confirmCsvImport(file: File, options?: {
    update_existing?: boolean
    send_welcome_email?: boolean
    default_role?: string
  }): Promise<{
    success: boolean
    data: DirectorySyncLog
  }> {
    const formData = new FormData()
    formData.append('file', file)
    if (options) {
      formData.append('options', JSON.stringify(options))
    }

    const response = await fetch(`${getApiBase()}/api/directory/csv/import`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to import CSV')
    }
    return data
  }

  /**
   * Get user groups synced from directories.
   */
  async getUserGroups(): Promise<{
    success: boolean
    data: UserGroup[]
  }> {
    return this.request('/api/directory/groups')
  }

  /**
   * Get all unique departments from users.
   */
  async getUserDepartments(): Promise<{
    success: boolean
    data: string[]
  }> {
    return this.request('/api/users/departments')
  }

  /**
   * Get users with extended filtering.
   */
  async getUsersFiltered(params?: {
    source?: DirectoryProvider | 'manual'
    department?: string
    search?: string
    active?: boolean
    limit?: number
    offset?: number
  }): Promise<{
    success: boolean
    users: DirectoryUser[]
    count: number
    total: number
  }> {
    const searchParams = new URLSearchParams()
    if (params?.source) searchParams.set('source', params.source)
    if (params?.department) searchParams.set('department', params.department)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.active !== undefined) searchParams.set('active', String(params.active))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    return this.request(`/api/users?${searchParams}`)
  }

  /**
   * Bulk update user status (enable/disable).
   */
  async bulkUpdateUserStatus(userIds: string[], active: boolean): Promise<{
    success: boolean
    updated: number
  }> {
    return this.request('/api/users/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, active }),
    })
  }

  // ============================================================================
  // DIRECTORY PEOPLE (Issue #338)
  // Directory people are training/phishing targets from external identity providers.
  // They do NOT have GRC platform login access.
  // ============================================================================

  /**
   * Get all directory people for training/phishing targeting.
   */
  async getDirectoryPeople(params?: {
    status?: 'active' | 'inactive'
    department?: string
    source?: 'manual' | 'csv' | 'google' | 'entra'
    search?: string
    limit?: number
    offset?: number
  }): Promise<{
    success: boolean
    data: DirectoryPerson[]
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.department) searchParams.set('department', params.department)
    if (params?.source) searchParams.set('source', params.source)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    return this.request(`/api/directory/people?${searchParams}`)
  }

  /**
   * Get a single directory person.
   */
  async getDirectoryPerson(id: string): Promise<{
    success: boolean
    data: DirectoryPerson
  }> {
    return this.request(`/api/directory/people/${id}`)
  }

  /**
   * Create a directory person manually.
   */
  async createDirectoryPerson(data: {
    email: string
    first_name?: string
    last_name?: string
    department?: string
    title?: string
  }): Promise<{
    success: boolean
    data: DirectoryPerson
  }> {
    return this.request('/api/directory/people', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a directory person.
   */
  async updateDirectoryPerson(id: string, data: {
    first_name?: string
    last_name?: string
    department?: string
    title?: string
    status?: 'active' | 'inactive'
  }): Promise<{
    success: boolean
  }> {
    return this.request(`/api/directory/people/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a directory person (only manual people).
   */
  async deleteDirectoryPerson(id: string): Promise<{
    success: boolean
  }> {
    return this.request(`/api/directory/people/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Promote a directory person to a GRC user.
   */
  async promoteDirectoryPerson(id: string, role: 'admin' | 'contributor' | 'viewer'): Promise<{
    success: boolean
    data: { userId: string; email: string }
    message: string
  }> {
    return this.request(`/api/directory/people/${id}/promote`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    })
  }

  /**
   * Get directory people stats.
   */
  async getDirectoryPeopleStats(): Promise<{
    success: boolean
    stats: {
      by_status: Array<{ status: string; count: number }>
      by_source: Array<{ directory_source: string; count: number }>
      promoted: number
    }
  }> {
    return this.request('/api/directory/people/stats')
  }

  /**
   * Get departments from directory people.
   */
  async getDirectoryPeopleDepartments(): Promise<{
    success: boolean
    departments: Array<{ department: string; person_count: number }>
  }> {
    return this.request('/api/directory/people/departments')
  }

  // ============================================================================
  // TRAINING TARGET SELECTION (Issue #338)
  // ============================================================================

  /**
   * Get directory people available for training targeting.
   */
  async getTrainingTargetPeople(params?: {
    department?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{
    success: boolean
    data: Array<{
      id: string
      email: string
      first_name?: string
      last_name?: string
      department?: string
      title?: string
      status: string
    }>
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.department) searchParams.set('department', params.department)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    return this.request(`/api/training/targets/people?${searchParams}`)
  }

  /**
   * Get departments available for training targeting.
   */
  async getTrainingTargetDepartments(): Promise<{
    success: boolean
    departments: Array<{ department: string; person_count: number }>
  }> {
    return this.request('/api/training/targets/departments')
  }

  // ============================================================================
  // PHISHING TARGET SELECTION (Issue #338)
  // ============================================================================

  /**
   * Get directory people available for phishing targeting.
   */
  async getPhishingTargetPeople(params?: {
    department?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{
    success: boolean
    data: Array<{
      id: string
      email: string
      first_name?: string
      last_name?: string
      department?: string
      title?: string
      status: string
    }>
    pagination: { total: number; limit: number; offset: number }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.department) searchParams.set('department', params.department)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))
    return this.request(`/api/phishing/targets/people?${searchParams}`)
  }

  /**
   * Get departments available for phishing targeting.
   */
  async getPhishingTargetDepartments(): Promise<{
    success: boolean
    departments: Array<{ department: string; person_count: number }>
  }> {
    return this.request('/api/phishing/targets/departments')
  }

  // ============================================================================
  // NETWORK DIAGRAMS (Issue #341)
  // ============================================================================

  /**
   * Get all network diagrams for the current tenant.
   */
  async getDiagrams(): Promise<{
    success: boolean
    diagrams: Array<{
      id: string
      name: string
      description?: string
      source_type: string
      source_integration_id?: string
      status: string
      created_at: string
      updated_at: string
    }>
  }> {
    return this.request('/api/diagrams')
  }

  /**
   * Get a single diagram by ID with full layout data.
   */
  async getDiagram(id: string): Promise<{
    success: boolean
    diagram: {
      id: string
      name: string
      description?: string
      source_type: string
      source_integration_id?: string
      layout_data?: string
      custom_nodes?: string
      status: string
      created_at: string
      updated_at: string
    }
  }> {
    return this.request(`/api/diagrams/${id}`)
  }

  /**
   * Create a new network diagram.
   */
  async createDiagram(data: {
    name: string
    description?: string
    source_type?: string
    source_integration_id?: string
    layout_data?: string
    custom_nodes?: string
  }): Promise<{
    success: boolean
    diagram: { id: string; name: string }
    message: string
  }> {
    return this.request('/api/diagrams', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Update an existing network diagram.
   */
  async updateDiagram(id: string, data: {
    name?: string
    description?: string
    layout_data?: string
    custom_nodes?: string
    status?: string
  }): Promise<{
    success: boolean
    message: string
  }> {
    return this.request(`/api/diagrams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a network diagram.
   */
  async deleteDiagram(id: string): Promise<{
    success: boolean
    message: string
  }> {
    return this.request(`/api/diagrams/${id}`, {
      method: 'DELETE',
    })
  }
}

// Task comment types (Issue #90)
export interface TaskComment {
  id: string
  tenant_id: string
  task_id: string
  user_id: string
  content: string
  created_at: number
  updated_at: number
  author_username?: string
  author_email?: string
  author_first_name?: string
  author_last_name?: string
}

// Task attachment types (Issue #98)
export interface TaskAttachment {
  id: string
  task_id: string
  filename: string
  file_key: string
  file_size: number
  mime_type: string | null
  uploaded_by: string
  uploaded_by_username?: string
  created_at: number
}

// Task time tracking types (Issue #140)
export interface TaskTimeEntry {
  id: string
  tenant_id: string
  task_id: string
  user_id: string
  started_at: number
  ended_at?: number
  duration_minutes?: number
  description?: string
  entry_type: 'timer' | 'manual'
  created_at: number
  updated_at: number
  user_username?: string
  user_email?: string
}

export interface TaskTimeSummary {
  total_minutes: number
  total_hours: number
  estimated_hours: number | null
  by_user: Array<{
    user_id: string
    username: string
    email: string
    total_minutes: number
    total_hours: number
  }>
}

export interface TimeReportSummary {
  total_minutes: number
  total_hours: number
  entry_count: number
  by_task: Array<{
    task_id: string
    task_title: string
    task_status: string
    estimated_hours: number | null
    total_minutes: number
    total_hours: number
    entry_count: number
    variance_hours: number | null
  }>
  by_user: Array<{
    user_id: string
    username: string
    email: string
    total_minutes: number
    total_hours: number
    entry_count: number
  }>
  by_date: Array<{
    date: string
    total_minutes: number
    total_hours: number
    entry_count: number
  }>
}

// Custom field types
export interface CustomFieldDefinition {
  id: string
  tenant_id: string
  entity_type: 'risk' | 'control' | 'vendor' | 'evidence' | 'document'
  field_name: string
  field_label: string
  field_type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea' | 'email' | 'url'
  options?: Array<{ value: string; label: string }> | null
  required: boolean
  default_value?: string | null
  min_value?: number | null
  max_value?: number | null
  max_length?: number | null
  placeholder?: string | null
  help_text?: string | null
  display_order: number
  show_in_list: boolean
  active: boolean
  created_at: number
  updated_at: number
  created_by?: string | null
  created_by_username?: string | null
}

export interface CreateCustomFieldInput {
  entity_type: 'risk' | 'control' | 'vendor' | 'evidence' | 'document'
  field_name: string
  field_label: string
  field_type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea' | 'email' | 'url'
  options?: Array<{ value: string; label: string }>
  required?: boolean
  default_value?: string
  min_value?: number
  max_value?: number
  max_length?: number
  placeholder?: string
  help_text?: string
  display_order?: number
  show_in_list?: boolean
}

export interface UpdateCustomFieldInput {
  field_label?: string
  options?: Array<{ value: string; label: string }>
  required?: boolean
  default_value?: string | null
  min_value?: number | null
  max_value?: number | null
  max_length?: number | null
  placeholder?: string | null
  help_text?: string | null
  display_order?: number
  show_in_list?: boolean
  active?: boolean
}

// Risk Category types (Issue #100)
export interface RiskCategory {
  id: string
  tenant_id: string
  name: string
  description: string | null
  parent_id: string | null
  color: string
  icon: string
  sort_order: number
  is_system: number // 1 = predefined/system category, 0 = user-created
  is_active: number // 1 = active, 0 = inactive
  created_at: number
  updated_at: number
  created_by: string | null
  children?: RiskCategory[] // Present in hierarchical responses
}

// Risk Appetite types (Issue #139)
export interface RiskAppetite {
  id: string
  tenant_id: string
  category: string
  display_name: string | null
  appetite_level: number // 1-5: Very Low to Very High
  tolerance_threshold: number // Risk score threshold (1-25)
  max_acceptable_score: number // Maximum acceptable score before escalation
  description: string | null
  appetite_statement: string | null
  is_active: number
  created_at: number
  updated_at: number
  created_by: string | null
  updated_by: string | null
  created_by_username?: string
  updated_by_username?: string
  // Enriched fields from API
  appetite_level_label?: string
  appetite_level_description?: string
  appetite_level_color?: string
}

export interface RiskAppetiteHistory {
  id: string
  tenant_id: string
  risk_appetite_id: string
  category: string
  appetite_level: number
  tolerance_threshold: number
  max_acceptable_score: number
  description: string | null
  appetite_statement: string | null
  changed_by: string | null
  changed_by_username?: string
  changed_at: number
  change_reason: string | null
}

export interface ToleranceBreach {
  id: string
  title: string
  description: string | null
  category: string | null
  category_id: string | null
  likelihood: number
  impact: number
  risk_score: number
  residual_likelihood: number | null
  residual_impact: number | null
  residual_risk_score: number | null
  status: string
  owner_id: string | null
  owner_username: string | null
  due_date: number | null
  created_at: number
  tolerance_threshold: number
  max_acceptable_score: number
  appetite_level: number
  appetite_category_name: string
  category_name: string | null
  category_color: string | null
  breach_level: 'critical' | 'above_tolerance' | 'within_tolerance'
  score_above_tolerance: number
  appetite_level_label?: string
  appetite_level_color?: string
}

// Evidence Tag types (Issue #151)
export interface EvidenceTag {
  id: string
  name: string
  slug: string
  category: 'type' | 'source' | 'period' | 'department' | 'general'
  color: string
  is_system: number // 1 = system-defined (auto-applied), 0 = user-created
  use_count: number
  source?: 'manual' | 'auto' | 'ai' // How tag was applied (on evidence-tag link)
  applied_at?: number // When tag was applied (on evidence-tag link)
  created_at?: number
  updated_at?: number
}

// KRI Types (Issue #127)
export type KRIMetricType = 'number' | 'percentage' | 'currency' | 'count' | 'days' | 'ratio'
export type KRIDirection = 'lower_is_better' | 'higher_is_better'
export type KRIStatus = 'green' | 'yellow' | 'red'

export interface KRI {
  id: string
  tenant_id: string
  name: string
  description?: string
  metric_type: KRIMetricType
  unit?: string
  green_threshold: number
  yellow_threshold: number
  red_threshold?: number
  direction: KRIDirection
  data_source?: string
  data_source_config?: string
  alert_on_breach: number
  alert_recipients?: string
  owner_id?: string
  owner_username?: string
  category_id?: string  // FK to risk_categories.id
  category?: string     // Deprecated - kept for backward compatibility
  tags?: string
  is_active: number
  created_by?: string
  created_at: number
  updated_at: number
  // Computed fields
  current_value?: number
  current_status?: KRIStatus
  last_recorded_at?: number
  linked_risks_count?: number
}

export interface KRIValue {
  id: string
  tenant_id: string
  kri_id: string
  value: number
  status: KRIStatus
  notes?: string
  recorded_by?: string
  recorded_by_username?: string
  recorded_at: number
  source: string
  source_reference?: string
}

export interface KRIRiskLink {
  id: string
  tenant_id: string
  kri_id: string
  risk_id: string
  risk_title?: string
  risk_status?: string
  risk_score?: number
  relationship_type: string
  notes?: string
  linked_by?: string
  linked_by_username?: string
  linked_at: number
}

export interface KRIAlert {
  id: string
  tenant_id: string
  kri_id: string
  kri_name?: string
  kri_value_id?: string
  alert_type: string
  previous_status?: string
  new_status: string
  value: number
  message: string
  acknowledged: number
  acknowledged_by?: string
  acknowledged_by_username?: string
  acknowledged_at?: number
  dismissed: number
  dismissed_by?: string
  dismissed_by_username?: string
  dismissed_at?: number
  created_at: number
}

export interface CreateKRIInput {
  name: string
  description?: string
  metric_type: KRIMetricType
  unit?: string
  green_threshold: number
  yellow_threshold: number
  red_threshold?: number
  direction?: KRIDirection
  data_source?: string
  data_source_config?: Record<string, any>
  alert_on_breach?: boolean
  alert_recipients?: string[]
  owner_id?: string
  category_id?: string  // FK to risk_categories.id
  category?: string     // Deprecated - kept for backward compatibility
  tags?: string[]
}

export interface UpdateKRIInput {
  name?: string
  description?: string
  metric_type?: KRIMetricType
  unit?: string
  green_threshold?: number
  yellow_threshold?: number
  red_threshold?: number
  direction?: KRIDirection
  data_source?: string
  data_source_config?: Record<string, any>
  alert_on_breach?: boolean
  alert_recipients?: string[]
  owner_id?: string
  category_id?: string  // FK to risk_categories.id
  category?: string     // Deprecated
  tags?: string[]
  is_active?: boolean
}

// ============================================================================
// CONTROL HISTORY INTERFACES (Issue #94)
// ============================================================================

/**
 * Single entry in the control change history
 */
export interface ControlHistoryEntry {
  id: string
  version: number
  changed_by: string
  changed_by_username?: string
  changed_by_display_name?: string
  changed_by_email?: string
  changed_at: number
  change_type: 'created' | 'updated' | 'status_change' | 'deleted'
  previous_values?: Record<string, any>
  new_values?: Record<string, any>
  change_summary?: string
  notes?: string
}

/**
 * Statistics about a control's change history
 */
export interface ControlHistoryStats {
  total_changes: number
  updates: number
  status_changes: number
  first_change?: number
  last_change?: number
}

/**
 * Response from GET /api/controls/:id/history
 */
export interface ControlHistoryResponse {
  success: boolean
  control: {
    id: string
    control_number: string
    name: string
  }
  history: ControlHistoryEntry[]
  stats: ControlHistoryStats
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

// ============================================================================
// RBAC PERMISSIONS TYPES (Issue #138)
// ============================================================================

export interface Permission {
  id: string
  tenant_id: string
  name: string
  description: string | null
  resource: string
  action: string
  is_system: number
  created_at: number
  updated_at: number
}

export interface Role {
  id: string
  tenant_id: string
  name: string
  description: string | null
  is_system: number
  is_active: number
  created_at: number
  updated_at: number
  created_by: string | null
  permissions?: Permission[]
}

export interface UserPermissionOverride {
  user_id: string
  permission_id: string
  permission_name?: string
  grant_type: 'grant' | 'deny'
  granted_at: number
  granted_by: string | null
  expires_at: number | null
  reason: string | null
}

export interface UserPermissionDetails {
  user: {
    id: string
    username: string
    role: string
  }
  legacy_role: string
  assigned_roles: Role[]
  effective_permissions: string[]
  overrides: UserPermissionOverride[]
}

// ============================================================================
// ASSET INVENTORY TYPES (Issue #128)
// ============================================================================

export interface Asset {
  id: string
  tenant_id: string
  hostname: string | null
  ip_address: string | null
  mac_address: string | null
  asset_type: 'server' | 'workstation' | 'network_device' | 'cloud_instance' | 'container' | 'database' | 'application' | 'iot_device' | 'unknown'
  os: string | null
  os_family: 'linux' | 'windows' | 'macos' | 'bsd' | 'other' | null
  environment: 'production' | 'staging' | 'development' | 'test' | 'unknown' | null
  ports: Array<{ port: number; protocol: string; state?: string; service?: string; product?: string; version?: string }>
  last_scan_ports: string | null
  first_seen: number
  last_seen: number
  scan_count: number
  owner_id: string | null
  owner_username?: string | null
  owner_email?: string | null
  business_unit: string | null
  criticality: 'critical' | 'high' | 'medium' | 'low'
  tags: string[]
  notes: string | null
  custom_fields: Record<string, any>
  status: 'active' | 'inactive' | 'decommissioned' | 'unknown'
  last_scan_result_id: string | null
  last_scan_status?: string | null
  last_scan_completed_at?: number | null
  open_vulnerability_count?: number
  total_vulnerability_count?: number
  created_at: number
  updated_at: number
}

export interface AssetPort {
  port: number
  protocol: string
  state?: string
  service?: string
  product?: string
  version?: string
}

export interface AssetVulnerability {
  id: string
  vulnerability_id: string
  vulnerability_name: string
  severity: string
  cvss_score: number | null
  host: string
  port: number | null
  service: string | null
  description: string | null
  first_found: number
  last_found: number
  times_found: number
  is_resolved: number
  resolved_at?: number | null
  resolved_by?: string | null
}

export interface AssetScanHistory {
  id: string
  scan_result_id: string
  ports_snapshot: AssetPort[]
  os_snapshot: string | null
  new_ports: AssetPort[]
  closed_ports: AssetPort[]
  scanned_at: number
  scan_status: string
}

// ============================================================================
// JIRA INTEGRATION TYPES (Issue #125)
// ============================================================================

export interface JiraConfig {
  id: string
  jira_url: string
  jira_email: string
  default_project_key: string
  default_issue_type: string
  field_mappings: Record<string, any>
  auto_create_tickets: number
  auto_sync_enabled: number
  min_severity_for_auto: string
  enabled: number
  last_sync_at: number | null
  last_sync_error: string | null
  created_at: number
  updated_at: number
}

export interface JiraConfigInput {
  jira_url: string
  jira_email: string
  jira_api_token?: string // Only required for new configuration
  default_project_key: string
  default_issue_type?: string
  field_mappings?: Record<string, any>
  auto_create_tickets?: boolean
  auto_sync_enabled?: boolean
  min_severity_for_auto?: string
  enabled?: boolean
}

export interface JiraTicketHistoryEntry {
  id: string
  tenant_id: string
  vulnerability_id: string
  jira_ticket_key: string
  action: 'created' | 'updated' | 'synced' | 'transitioned' | 'commented' | 'error'
  old_status: string | null
  new_status: string | null
  details: string | null
  error_message: string | null
  performed_by: string | null
  performed_by_username?: string
  created_at: number
}

// ============================================================================
// NOTIFICATION CHANNELS TYPES (Issue #157)
// ============================================================================

export interface NotificationChannel {
  id: string
  tenant_id: string
  name: string
  channel_type: 'slack' | 'email' | 'webhook' | 'custom'
  is_enabled: boolean
  config: Record<string, any>
  event_types: string[]
  last_triggered_at: number | null
  failure_count: number
  created_at: number
  updated_at: number
}

export interface NotificationEvent {
  id: string
  name: string
  description: string
  category: string
}

// ============================================================================
// INTERNAL CONTROLS TYPES (Issue #311: Common Control Framework)
// ============================================================================

export interface InternalControl {
  id: string
  tenant_id: string
  control_number: string
  name: string
  description: string | null
  category: 'Identify' | 'Protect' | 'Detect' | 'Respond' | 'Recover'
  subcategory: string | null
  implementation_guidance: string | null
  evidence_guidance: string | null
  testing_procedures: string | null
  evidence_type: 'evidence' | 'policy' | 'both'
  frequency: string
  owner_id: string | null
  owner_username?: string | null
  last_evidence_date: number | null
  next_due_date: number | null
  status: 'pending' | 'compliant' | 'non-compliant' | 'in-progress'
  status_reason?: string | null
  evidence_count?: number
  policy_count?: number
  framework_control_count?: number
  created_at: number
  updated_at: number
}

// ============================================================================
// COMPLIANCE TRAINING TYPES (Issue #285)
// ============================================================================

export interface TrainingCategory {
  id: string
  tenant_id: string
  name: string
  description: string | null
  icon: string
  color: string
  is_system: boolean
  is_active: boolean
  display_order: number
  module_count?: number
  created_at: number
  updated_at: number
}

export type TrainingContentType = 'video' | 'slides' | 'quiz' | 'document' | 'mixed'
export type TrainingModuleStatus = 'draft' | 'published' | 'archived'

export interface TrainingModule {
  id: string
  tenant_id: string
  title: string
  description: string | null
  category_id: string | null
  category_name?: string
  category_icon?: string
  category_color?: string
  content_type: TrainingContentType
  status: TrainingModuleStatus
  video_url: string | null
  video_duration_seconds: number | null
  slides_content: any | null
  quiz_questions: any | null
  quiz_passing_score: number
  quiz_time_limit_minutes: number | null
  document_content: string | null
  estimated_minutes: number
  version: number
  is_mandatory: boolean
  tags: string[]
  prerequisites: string[]
  created_by: string | null
  created_by_username?: string
  created_at: number
  updated_at: number
  published_at: number | null
}

export type TrainingCampaignStatus = 'draft' | 'active' | 'completed' | 'cancelled'
export type TrainingAssignmentType = 'all_users' | 'specific_users' | 'by_role' | 'by_department'
export type TrainingScheduleType = 'immediate' | 'scheduled' | 'recurring'

export interface TrainingCampaign {
  id: string
  tenant_id: string
  name: string
  description: string | null
  status: TrainingCampaignStatus
  assignment_type: TrainingAssignmentType
  assignment_criteria: any | null
  schedule_type: TrainingScheduleType
  scheduled_start: number | null
  recurrence_pattern: string | null
  due_days_from_assignment: number
  allow_late_completion: boolean
  send_reminders: boolean
  reminder_days_before: number[]
  modules?: TrainingModule[]
  module_ids?: string[]
  stats?: {
    total_assignments: number
    completed: number
    in_progress: number
    not_started: number
    overdue: number
    completion_rate: number
    avg_score: number | null
  }
  created_by: string | null
  created_by_username?: string
  created_at: number
  updated_at: number
  started_at: number | null
  completed_at: number | null
}

export type TrainingAssignmentStatus = 'assigned' | 'in_progress' | 'completed' | 'overdue' | 'excused'

export interface TrainingAssignment {
  id: string
  tenant_id: string
  campaign_id: string
  campaign_name?: string
  user_id: string
  user_username?: string
  user_email?: string
  status: TrainingAssignmentStatus
  assigned_at: number
  due_date: number | null
  started_at: number | null
  completed_at: number | null
  overall_progress_percent: number
  overall_score: number | null
  modules_completed: number
  modules_total: number
  time_spent_minutes: number
  excused_reason: string | null
  excused_by: string | null
  excused_at: number | null
}

export type TrainingProgressStatus = 'not_started' | 'in_progress' | 'completed' | 'failed'

export interface TrainingProgress {
  id: string
  tenant_id: string
  assignment_id: string
  module_id: string
  module_title?: string
  module_content_type?: TrainingContentType
  status: TrainingProgressStatus
  progress_percent: number
  started_at: number | null
  completed_at: number | null
  time_spent_minutes: number
  last_position: any | null
  quiz_score: number | null
  quiz_passed: boolean | null
  quiz_attempts: number
  quiz_answers: any | null
  created_at: number
  updated_at: number
}

export interface TrainingQuizQuestion {
  id: string
  question: string
  question_type: 'multiple_choice' | 'true_false' | 'multi_select'
  options: string[]
  correct_answer: string | string[]
  explanation: string | null
  points: number
}

export interface TrainingQuizSubmission {
  question_id: string
  answer: string | string[]
}

export interface TrainingDashboardStats {
  total_modules: number
  published_modules: number
  active_campaigns: number
  total_assignments: number
  completed_assignments: number
  overdue_assignments: number
  overall_completion_rate: number
  avg_quiz_score: number | null
  by_category: Array<{
    category_id: string
    category_name: string
    module_count: number
    completion_rate: number
  }>
  recent_completions: Array<{
    user_id: string
    user_username: string
    module_id: string
    module_title: string
    completed_at: number
    score: number | null
  }>
}

// ============================================================================
// PHISHING SIMULATION TYPES (Issue #285)
// ============================================================================

export type PhishingDifficulty = 'easy' | 'medium' | 'hard' | 'expert'
export type PhishingCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'active' | 'completed' | 'cancelled'
export type PhishingResultOutcome = 'pending' | 'safe' | 'clicked' | 'compromised' | 'reported'

export interface PhishingTemplate {
  id: string
  tenant_id: string
  name: string
  subject: string
  body_html: string
  body_text: string | null
  sender_name: string
  sender_email: string
  category: string
  difficulty: PhishingDifficulty
  indicators: string[]
  landing_page_html: string | null
  capture_credentials: boolean
  is_system: boolean
  is_active: boolean
  usage_count?: number
  success_rate?: number
  created_by: string | null
  created_by_username?: string
  created_at: number
  updated_at: number
}

export interface PhishingSenderProfile {
  id: string
  tenant_id: string
  name: string
  from_name: string
  from_email: string
  reply_to_email: string | null
  is_verified: boolean
  is_default: boolean
  created_at: number
  updated_at: number
}

export interface PhishingCampaign {
  id: string
  tenant_id: string
  name: string
  description: string | null
  status: PhishingCampaignStatus
  template_id: string
  template_name?: string
  sender_profile_id: string | null
  sender_name?: string
  sender_email?: string
  target_type: 'all_users' | 'specific_users' | 'by_role' | 'by_department'
  target_criteria: any | null
  scheduled_at: number | null
  send_window_start: string | null
  send_window_end: string | null
  randomize_send_time: boolean
  track_opens: boolean
  track_clicks: boolean
  capture_credentials: boolean
  training_module_id: string | null
  training_module_title?: string
  auto_enroll_on_fail: boolean
  requires_approval: boolean
  approved_by: string | null
  approved_by_username?: string
  approved_at: number | null
  total_targets: number
  emails_sent: number
  emails_opened: number
  links_clicked: number
  credentials_submitted: number
  reported_count: number
  created_by: string | null
  created_by_username?: string
  created_at: number
  updated_at: number
  started_at: number | null
  completed_at: number | null
}

export interface PhishingResult {
  id: string
  tenant_id: string
  campaign_id: string
  campaign_name?: string
  user_id: string
  user_username?: string
  user_email?: string
  user_department?: string
  email: string
  tracking_id: string
  outcome: PhishingResultOutcome
  sent_at: number | null
  delivered_at: number | null
  opened_at: number | null
  clicked_at: number | null
  submitted_at: number | null
  reported_at: number | null
  user_agent: string | null
  ip_address: string | null
  submitted_data: any | null
  created_at: number
  updated_at: number
}

export interface PhishingDashboardStats {
  total_campaigns: number
  active_campaigns: number
  completed_campaigns: number
  total_emails_sent: number
  total_opened: number
  total_clicked: number
  total_compromised: number
  total_reported: number
  overall_click_rate: number
  overall_compromise_rate: number
  overall_report_rate: number
  by_difficulty: Array<{
    difficulty: PhishingDifficulty
    sent: number
    clicked: number
    click_rate: number
  }>
  by_department: Array<{
    department: string
    sent: number
    clicked: number
    click_rate: number
  }>
  trend: Array<{
    month: string
    sent: number
    clicked: number
    click_rate: number
  }>
  recent_results: Array<{
    campaign_name: string
    user_email: string
    outcome: PhishingResultOutcome
    timestamp: number
  }>
}

// ============================================================================
// USER DIRECTORY INTEGRATION TYPES (Issue #336)
// ============================================================================

export type DirectoryProvider = 'csv' | 'google' | 'entra'
export type DirectoryConnectionStatus = 'active' | 'paused' | 'error'
export type DirectorySyncType = 'manual' | 'scheduled' | 'csv_import'
export type DirectorySyncStatus = 'started' | 'completed' | 'failed'

export interface DirectoryConnection {
  id: string
  tenant_id: string
  name: string
  provider: DirectoryProvider
  status: DirectoryConnectionStatus
  sync_enabled: boolean
  sync_interval_hours: number
  last_sync_at: number | null
  last_sync_status: string | null
  last_sync_error: string | null
  created_at: number
  updated_at: number
}

export interface DirectorySyncLog {
  id: string
  connection_id: string | null
  sync_type: DirectorySyncType
  status: DirectorySyncStatus
  users_created: number
  users_updated: number
  users_disabled: number
  users_unchanged: number
  errors: string[]
  started_at: number
  completed_at: number | null
}

export interface UserGroup {
  id: string
  name: string
  description: string | null
  source: DirectoryProvider | 'manual'
  member_count: number
}

export interface CsvPreviewResult {
  total_rows: number
  valid_rows: number
  to_create: number
  to_update: number
  errors: Array<{ row: number; error: string }>
  preview: Array<{
    email: string
    first_name: string
    last_name: string
    department: string
    action: 'create' | 'update'
  }>
}

export interface DirectoryUser {
  id: string
  username: string
  email: string
  role: string
  active: number
  first_name?: string
  last_name?: string
  department?: string
  source?: DirectoryProvider | 'manual'
  external_id?: string
  last_synced_at?: number
  created_at: number
}

/**
 * Directory Person - Training/Phishing target imported from external identity providers.
 * These people do NOT have GRC platform login access.
 * (Issue #338)
 */
export interface DirectoryPerson {
  id: string
  tenant_id: string
  email: string
  first_name?: string
  last_name?: string
  department?: string
  title?: string
  external_id?: string
  directory_source: 'manual' | 'csv' | 'google' | 'entra'
  directory_connection_id?: string
  manager_id?: string
  status: 'active' | 'inactive'
  last_synced_at?: number
  promoted_to_user_id?: string
  promoted_at?: number
  created_at: number
  updated_at: number
}

export const api = new ApiClient()
export default api

// Dark Web People view (#397)
export type DarkWebCredentialStatus = 'new' | 'reviewed' | 'reset_requested' | 'resolved' | 'false_positive'

export interface DarkWebPerson {
  email: string
  status: DarkWebCredentialStatus | string
  credentialIds: string[]
  exposureCount: number
  sources: string[]
  sourceTypes: string[]
  foundIn: string[]
  foundInCount: number
  hasPassword: boolean
  latestLeakAt: number | null
  lastSeenAt: number
  firstSeenAt: number
  ownerUserId: string | null
  ownerName: string | null
  taskId: string | null
}
